import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import allSlots from './slotsData';

// ===== Ambient backdrop (home + leaderboard) =====
// The effects layer is registered to the ARTWORK's coordinate space, not the
// viewport: a box replicating background-size:cover geometry (image aspect
// 1920/1073) holds every effect at the image-percentage position of the thing
// it animates — so twinkles flare exactly on the painted stars, breath overlays
// sit exactly on the painted clouds, at any viewport size.
const BG_AR = 1920 / 1073;
// Painted stars in the artwork (image-%), flared by the bgTwinkle animation
const AMBIENT_STARS = [
  { x: 9.8, y: 6.9, size: 13, dur: 4.2, delay: 0, color: '#d8b4fe' },
  { x: 14.7, y: 8.2, size: 9, dur: 3.5, delay: 1.7, color: '#ffffff' },
  { x: 5.8, y: 26.3, size: 11, dur: 5.0, delay: 2.9, color: '#fde68a' },
  { x: 17.1, y: 30.6, size: 10, dur: 4.6, delay: 0.8, color: '#d8b4fe' },
  { x: 21.8, y: 46.5, size: 7, dur: 3.8, delay: 3.3, color: '#ffffff' },
  { x: 27.7, y: 54.3, size: 7, dur: 5.4, delay: 1.2, color: '#ffffff' },
  { x: 52.3, y: 37.0, size: 7, dur: 4.9, delay: 2.2, color: '#ffffff' },
  { x: 68.9, y: 4.6, size: 13, dur: 3.9, delay: 0.4, color: '#fde68a' },
  { x: 75.1, y: 14.5, size: 10, dur: 4.4, delay: 2.6, color: '#d8b4fe' },
  { x: 78.8, y: 7.9, size: 7, dur: 5.2, delay: 3.8, color: '#ffffff' },
  { x: 81.8, y: 27.6, size: 11, dur: 4.1, delay: 1.5, color: '#fde68a' },
  { x: 76.3, y: 35.9, size: 7, dur: 5.6, delay: 0.2, color: '#ffffff' },
  { x: 81.4, y: 41.9, size: 7, dur: 4.7, delay: 3.0, color: '#ffffff' },
  { x: 69.7, y: 63.8, size: 7, dur: 5.1, delay: 1.9, color: '#ffffff' }
];
// Painted cloud clusters (image-%), softly brightened by bgCloudBreath
const AMBIENT_CLOUDS = [
  { x: 6, y: 14, w: 24, h: 20, dur: 13, delay: 0 },
  { x: 21, y: 15, w: 13, h: 9, dur: 16, delay: 4 },
  { x: 93, y: 8, w: 24, h: 18, dur: 14, delay: 2 },
  { x: 92, y: 30, w: 20, h: 15, dur: 17, delay: 7 },
  { x: 15, y: 52, w: 28, h: 20, dur: 15, delay: 5 },
  { x: 80, y: 62, w: 26, h: 17, dur: 18, delay: 9 },
  { x: 50, y: 68, w: 42, h: 15, dur: 20, delay: 3 }
];
// Glints on the gold props: trophy (star emblem, cup rim, base) + chest coins
const AMBIENT_GLINTS = [
  { x: 92.3, y: 48.5, size: 14, dur: 5.5, delay: 0.6, color: '#fff7cc' },
  { x: 88.8, y: 38.5, size: 9, dur: 6.5, delay: 3.2, color: '#ffffff' },
  { x: 93.2, y: 63.0, size: 8, dur: 7.0, delay: 5.1, color: '#fde68a' },
  { x: 7.5, y: 68.0, size: 10, dur: 6.0, delay: 1.8, color: '#fff7cc' },
  { x: 17.0, y: 82.0, size: 8, dur: 6.8, delay: 4.4, color: '#fde68a' }
];

// ===== Seasonal: Halloween theme (temporary) =====
// site_settings.halloween_theme: 'on' | 'off' | unset/'auto' = on Oct 24 → Nov 1
// (Central), so it switches itself off after Halloween with no redeploy.
function isHalloweenActive(setting) {
  if (setting === 'on') return true;
  if (setting === 'off') return false;
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', month: 'numeric', day: 'numeric' }).formatToParts(new Date());
  const month = Number(parts.find(p => p.type === 'month').value);
  const day = Number(parts.find(p => p.type === 'day').value);
  return (month === 10 && day >= 24) || (month === 11 && day === 1);
}
// Bats crossing behind the content: vertical band, size, flight time, start offset, direction
const SPOOKY_BATS = [
  { top: 14, size: 52, dur: 26, delay: -4, dir: 1 },
  { top: 24, size: 34, dur: 34, delay: -19, dir: 1 },
  { top: 42, size: 40, dur: 30, delay: -11, dir: -1 },
  { top: 9, size: 28, dur: 41, delay: -28, dir: -1 }
];
function SpookyBat({ size }) {
  return (
    <svg className="spooky-bat-wings" width={size} height={size * 0.5} viewBox="0 0 64 32" fill="#0b0712">
      <path d="M32 11L30.2 7L29.6 11.2C25 9 18 5 3 6C7 9 9 13 8 17C11 15.5 14 16 16 18.5C18 16 22 16.5 24 19C26 17 28.5 17.5 29.5 20C30.5 22 31.3 23 32 23C32.7 23 33.5 22 34.5 20C35.5 17.5 38 17 40 19C42 16.5 46 16 48 18.5C50 16 53 15.5 56 17C55 13 57 9 61 6C46 5 39 9 34.4 11.2L33.8 7Z" />
    </svg>
  );
}

function AmbientBackdrop({ spooky = false }) {
  // Cursor sparkle trail: tiny stars spawned along the mouse path, faded out by
  // CSS and removed. Direct DOM writes — no React state per mousemove.
  const trailRef = useRef(null);
  useEffect(() => {
    const host = trailRef.current;
    if (!host) return;
    const colors = spooky
      ? ['#fb923c', '#fdba74', '#c084fc', '#a3e635']
      : ['#fde68a', '#ffffff', '#d8b4fe', '#a5f3fc'];
    let last = { x: -1e4, y: -1e4, t: 0 };
    const onMove = (e) => {
      const now = performance.now();
      const dx = e.clientX - last.x;
      const dy = e.clientY - last.y;
      if (now - last.t < 55 || (dx * dx + dy * dy) < 1100) return; // throttle by time AND distance
      last = { x: e.clientX, y: e.clientY, t: now };
      const s = document.createElement('div');
      const size = 5 + Math.random() * 5;
      const color = colors[(Math.random() * colors.length) | 0];
      s.className = 'cursor-sparkle';
      s.style.cssText = `left:${e.clientX + (Math.random() * 18 - 9)}px;top:${e.clientY + (Math.random() * 18 - 9)}px;width:${size}px;height:${size}px;background:${color};color:${color};`;
      host.appendChild(s);
      setTimeout(() => s.remove(), 800);
      if (host.childElementCount > 24) host.firstChild.remove();
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [spooky]);

  // Cover-box: same size/position the browser gives the cover-fitted image
  const coverBox = {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: `max(100vw, ${(BG_AR * 100).toFixed(2)}vh)`,
    height: `max(100vh, ${(100 / BG_AR).toFixed(2)}vw)`
  };
  return (
    <>
    <div aria-hidden style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: -1, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.55 }} />
      <div style={coverBox}>
        {AMBIENT_CLOUDS.map((c, i) => (
          <div key={`c${i}`} style={{
            position: 'absolute',
            left: `${c.x - c.w / 2}%`,
            top: `${c.y - c.h / 2}%`,
            width: `${c.w}%`,
            height: `${c.h}%`,
            background: 'radial-gradient(closest-side, rgba(148, 163, 255, 0.11), transparent 72%)',
            animation: `bgCloudBreath ${c.dur}s ease-in-out infinite`,
            animationDelay: `${c.delay}s`
          }} />
        ))}
        {AMBIENT_STARS.concat(AMBIENT_GLINTS).map((s, i) => (
          <div key={`s${i}`} className="bg-twinkle" style={{
            left: `calc(${s.x}% - ${s.size / 2}px)`,
            top: `calc(${s.y}% - ${s.size / 2}px)`,
            width: s.size,
            height: s.size,
            background: s.color,
            filter: `drop-shadow(0 0 ${Math.ceil(s.size / 2)}px ${s.color})`,
            animationDuration: `${s.dur}s`,
            animationDelay: `${s.delay}s`
          }} />
        ))}
      </div>
      {/* breathing glow pools over the gold props */}
      <div style={{ position: 'absolute', right: '-6%', top: '28%', width: '38%', height: '55%', background: 'radial-gradient(closest-side, rgba(251, 191, 36, 0.13), transparent 70%)', animation: 'bgGlowBreath 7s ease-in-out infinite' }} />
      <div style={{ position: 'absolute', left: '-8%', bottom: '-6%', width: '34%', height: '50%', background: 'radial-gradient(closest-side, rgba(251, 191, 36, 0.10), transparent 70%)', animation: 'bgGlowBreath 9s ease-in-out infinite 2.5s' }} />
      {spooky && (
        <>
          {/* recolor the artwork: violet sky fading to ember orange */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #5b21b6 0%, #3b0764 45%, #9a3412 100%)', mixBlendMode: 'color', opacity: 0.55 }} />
        </>
      )}
      {/* readability scrim, darkening toward the lower sections */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(10, 10, 15, 0.2) 0%, rgba(10, 10, 15, 0.52) 45%, rgba(10, 10, 15, 0.82) 100%)' }} />
      {spooky && (
        <>
          <div className="spooky-moon" />
          {SPOOKY_BATS.map((b, i) => (
            <div key={`b${i}`} className={`spooky-bat ${b.dir < 0 ? 'rtl' : ''}`} style={{ top: `${b.top}%`, animationDuration: `${b.dur}s`, animationDelay: `${b.delay}s` }}>
              <SpookyBat size={b.size} />
            </div>
          ))}
          <div className="spooky-fog" />
          <div className="spooky-fog back" />
        </>
      )}
    </div>
    {/* cursor sparkle trail host — sibling of the -1 layer so it renders above content, never intercepts input */}
    <div ref={trailRef} aria-hidden style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9999, overflow: 'hidden' }} />
    </>
  );
}

// ===== Rainbet .com / .us accounts =====
// Code CAM works on both rainbet.com and rainbet.us. They're separate casinos
// (the same name on both is two different accounts), so a user links either or
// both, and the backend tags every leaderboard/raffle row with its `site`.
const hasRainbetLink = (u) => !!(u?.rainbetUsername || u?.rainbetUsUsername);
// One string for places that show "the" Rainbet account: "name", "name (Rainbet.us)" or both
const rainbetDisplay = (u) =>
  [u?.rainbetUsername, u?.rainbetUsUsername && `${u.rainbetUsUsername} (Rainbet.us)`].filter(Boolean).join(' / ');
// The single account a prize gets paid to: .com when linked, else the .us one (labelled)
const rainbetPayoutName = (u) =>
  u?.rainbetUsername || (u?.rainbetUsUsername ? `${u.rainbetUsUsername} (Rainbet.us)` : '');

// Small "US" chip on rows that came from rainbet.us (.com rows show nothing)
function UsSiteChip({ site, style }) {
  if (site !== 'us') return null;
  return (
    <span title="Rainbet.us player" style={{ display: 'inline-block', marginLeft: '0.35rem', padding: '0.05rem 0.32rem', fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.06em', lineHeight: 1.4, borderRadius: '4px', background: 'rgba(59, 130, 246, 0.16)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', verticalAlign: 'middle', ...style }}>US</span>
  );
}

// First-time linking (welcome popup + checklist): pick the site — .com, .us or
// both — then enter the username(s). `onSave(site, name)` does the API call and
// throws with a user-facing message; `onDone(linkedAny)` runs after.
function RainbetLinkForm({ onSave, onDone, compact = false, allowSkip = false }) {
  const [choice, setChoice] = useState(null); // 'com' | 'us' | 'both'
  const [names, setNames] = useState({ com: '', us: '' });
  const [saved, setSaved] = useState({}); // sites already saved — a retry after a partial failure skips them (2h change cooldown)
  const [busy, setBusy] = useState(false);
  const sites = choice === 'both' ? ['com', 'us'] : choice ? [choice] : [];
  const pending = sites.filter(s => names[s].trim() && !saved[s]);
  const anySaved = Object.keys(saved).length > 0;

  const submit = async () => {
    if (!pending.length) {
      if (anySaved || allowSkip) onDone?.(anySaved);
      return;
    }
    setBusy(true);
    try {
      for (const s of pending) {
        await onSave(s, names[s].trim());
        setSaved(prev => ({ ...prev, [s]: true }));
      }
      onDone?.(true);
    } catch (e) {
      alert(e.message || 'Could not link that Rainbet username');
    } finally {
      setBusy(false);
    }
  };

  const pill = (key, label) => {
    const on = choice === key;
    return (
      <button key={key} type="button" onClick={() => setChoice(key)} style={{
        flex: 1, padding: compact ? '0.35rem 0.4rem' : '0.55rem 0.5rem',
        background: on ? 'rgba(34, 211, 238, 0.14)' : 'rgba(255, 255, 255, 0.04)',
        color: on ? '#22d3ee' : 'var(--text-dim)',
        border: `1px solid ${on ? 'rgba(34, 211, 238, 0.5)' : 'rgba(255, 255, 255, 0.12)'}`,
        borderRadius: '8px', fontSize: compact ? '0.72rem' : '0.82rem', fontWeight: 700,
        cursor: 'pointer', fontFamily: 'inherit',
        transition: 'background 0.15s, color 0.15s, border-color 0.15s'
      }}>{label}</button>
    );
  };
  const inputStyle = compact
    ? { width: '100%', padding: '0.35rem 0.6rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.78rem', fontFamily: 'inherit', boxSizing: 'border-box' }
    : undefined;
  const siteLabel = { com: 'Rainbet.com', us: 'Rainbet.us' };
  const buttonLabel = busy ? 'Linking…'
    : pending.length ? (compact ? 'Link ✓' : 'Link Username & Continue')
    : (anySaved || allowSkip) ? (compact ? 'Done' : 'Skip — Continue') : 'Link ✓';

  return (
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontSize: compact ? '0.72rem' : '0.8rem', fontWeight: 700, color: 'var(--text-dim)', marginBottom: '0.4rem' }}>
        Which Rainbet site do you play on?
      </div>
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.6rem' }}>
        {pill('com', 'Rainbet.com')}
        {pill('us', 'Rainbet.us')}
        {pill('both', 'Both')}
      </div>
      {sites.map(s => (
        <div key={s} className={compact ? undefined : 'welcome-input-group'} style={compact ? { marginBottom: '0.45rem' } : undefined}>
          {!compact && <label>{siteLabel[s]} username (under code CAM)</label>}
          {saved[s] ? (
            <div style={{ fontSize: '0.78rem', color: '#22c55e', fontWeight: 600, padding: '0.3rem 0' }}>✅ {siteLabel[s]}: {names[s].trim()}</div>
          ) : (
            <input
              className={compact ? undefined : 'welcome-input'}
              style={inputStyle}
              type="text"
              value={names[s]}
              onChange={(e) => setNames(prev => ({ ...prev, [s]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              placeholder={`${siteLabel[s]} username (under code CAM)`}
            />
          )}
        </div>
      ))}
      {(choice || allowSkip) && (
        <button
          type="button"
          className={compact ? undefined : 'welcome-btn-primary'}
          disabled={busy || (!allowSkip && !anySaved && !pending.length)}
          onClick={submit}
          style={compact ? { padding: '0.35rem 0.85rem', background: 'rgba(34, 211, 238, 0.12)', color: '#22d3ee', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700, border: '1px solid rgba(34, 211, 238, 0.25)', cursor: 'pointer', fontFamily: 'inherit', opacity: (!pending.length && !anySaved) ? 0.5 : 1 } : undefined}
        >
          {buttonLabel}
        </button>
      )}
    </div>
  );
}

export default function CamLeaderboard() {
  // Track if we need to redirect www → non-www
  const isWww = typeof window !== 'undefined' && window.location.hostname === 'www.camneverwins.com';

  const getPageFromPath = () => {
    const path = window.location.pathname.toLowerCase();
    if (path.includes('reward')) return 'rewards';
    if (path.includes('instruction') || path === '/how-to-join') return 'instructions';
    if (path.includes('challenge')) return 'challenges';
    if (path === '/points' || path === '/points-shop' || path === '/shop') return 'pointsshop';
    if (path === '/socials' || path === '/social' || path === '/instagram') return 'socials';
    if (path === '/prizes' || path === '/prize-portal' || path === '/claim-prize' || path === '/claim') return 'prizes';
    if (path === '/raffle' || path === '/wager-raffle') return 'wagerraffle';
    if (path === '/battles' || path === '/battle' || path === '/pvp') return 'battles';
    if (path === '/raffle-bot' || path === '/rafflebot' || path === '/raffles') return 'rafflebot';
    if (path === '/bingo' || path === '/slot-bingo') return 'bingo';
    if (path === '/terms' || path === '/tos' || path === '/terms-of-service') return 'terms';
    if (path === '/privacy' || path === '/privacy-policy') return 'privacy';
    if (path.includes('content') || path.includes('video')) return 'content';
    if (path.includes('prediction-results') || path.includes('predictionresults')) return 'predictions';
    if (path.includes('my-predictions') || path.includes('mypredictions')) return 'mypredictions';
    if (path.includes('predictions')) return 'predictions';
    if (path === '/bonus-hunts' || path === '/bonushunts' || path.includes('bonus-hunt') || path.includes('bonushunt')) return 'bonushunts';
    if (path.includes('community-hunt') || path.includes('communityhunt')) return 'communityhunts';
    if (path.includes('tournament')) return 'tournaments';
    if (path.includes('slot-picks') || path.includes('slotpicks')) return 'slotpicks';
    if (path.includes('slot-request') || path.includes('slotrequest')) return 'slotrequests';
    if (path.includes('profile')) return 'profile';
    if (path.includes('admin')) return 'admin';
    if (path.includes('game') || path.includes('spin')) return { page: 'games', game: null };
    if (path.includes('slot')) return { page: 'games', game: 'slots' };
    if (path.includes('rejuicer') || path.includes('wheel') || path.includes('gauntlet')) return { page: 'games', game: 'rejuicer' };
    if (path.includes('signup') || path.includes('join')) return 'signup';
    if (path === '/leaderboard') return 'leaderboard';
    if (path === '/' || path === '/home') return 'home';
    return 'home';
  };

  const initialPage = getPageFromPath();
  const [activePage, setActivePage] = useState(typeof initialPage === 'object' ? initialPage.page : initialPage);
  const [selectedGame, setSelectedGame] = useState(typeof initialPage === 'object' ? initialPage.game : null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [weeklyLeaderboard, setWeeklyLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null); // { weekly: {rank, wagered}|null, monthly: {rank, wagered}|null } from /api/my-rank
  const [leaderboardType, setLeaderboardType] = useState('weekly'); // 'monthly' | 'weekly'
  const [showLbRules, setShowLbRules] = useState(false);
  const [showLossbackRules, setShowLossbackRules] = useState(false);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Live-status pill: user can collapse it to a small dot so it never covers content
  const [livePillCollapsed, setLivePillCollapsed] = useState(() => {
    try { return localStorage.getItem('cam_live_pill_collapsed') === '1'; } catch { return false; }
  });
  const toggleLivePill = () => setLivePillCollapsed(v => {
    const next = !v;
    try { localStorage.setItem('cam_live_pill_collapsed', next ? '1' : '0'); } catch {}
    return next;
  });
  // "Updated X ago" stamps for polled wager data (leaderboard + raffle tickets).
  // Rendered text stays fresh because the countdown ticker re-renders every second.
  const [lbUpdatedAt, setLbUpdatedAt] = useState(null);
  const [raffleUpdatedAt, setRaffleUpdatedAt] = useState(null);
  const fmtUpdatedAgo = (ts) => {
    if (!ts) return null;
    const mins = Math.floor((Date.now() - ts) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m ago`;
  };
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const [wheelSpinning, setWheelSpinning] = useState(false);
  const [wheelRotation, setWheelRotation] = useState(0);
  const [wheelResult, setWheelResult] = useState(null);
  const wheelResultRef = useRef(null);
  const audioContextRef = useRef(null);
  const tickIntervalRef = useRef(null);
  // Phase 3: sound mute toggle, persisted in localStorage. soundsMutedRef mirrors
  // the state so audio callbacks can read it synchronously without re-binding.
  const soundsMutedRef = useRef(false);
  const [soundsMuted, setSoundsMuted] = useState(() => {
    try { return localStorage.getItem('cam_sounds_muted') === 'true'; } catch { return false; }
  });
  useEffect(() => {
    soundsMutedRef.current = soundsMuted;
    try { localStorage.setItem('cam_sounds_muted', String(soundsMuted)); } catch {}
  }, [soundsMuted]);
  // Phase 3: raffle reel animation state
  // null = closed; { strip, target, winner, stage } where stage in 'spinning'|'revealed'
  const [raffleReel, setRaffleReel] = useState(null);

  const [slotSpinning, setSlotSpinning] = useState(false);
  const [slotResult, setSlotResult] = useState(null);
  const [slotResultPopup, setSlotResultPopup] = useState(false);
  const [slotRotation, setSlotRotation] = useState(-54000);
  const [slotIsResetting, setSlotIsResetting] = useState(false);
  const [shuffleSeed, setShuffleSeed] = useState(0);
  const [liveStatus, setLiveStatus] = useState({ isLive: false, streamer: null, url: null });
  const [bonusHunts, setBonusHunts] = useState({ activeHunt: null, recentHunts: [], total: 0, highestMulti: null, hotSlot: null, coldSlot: null });
  const [huntPage, setHuntPage] = useState(0);
  const [youtubeShorts, setYoutubeShorts] = useState([]);
  const [instagramReels, setInstagramReels] = useState([]);
  const [tiktokVideos, setTiktokVideos] = useState([]);
  const [winnerPicking, setWinnerPicking] = useState(null);
  const [winner, setWinner] = useState(null);
  const [winnerVideoId, setWinnerVideoId] = useState(null);
  const [winnerPlatform, setWinnerPlatform] = useState(null);
  const [totalPaidOut, setTotalPaidOut] = useState(0);

  // Phase A: dynamic reward tiers (fetched from backend)
  const [rewardTiersAll, setRewardTiersAll] = useState([]);          // both modes, active only
  const [siteSettings, setSiteSettings] = useState({ reward_mode: 'lifetime', total_given_away: '0' });
  const [animatedGiveaway, setAnimatedGiveaway] = useState(0);
  const giveawayAnimatedOnceRef = useRef(false);
  const [rewardProgress, setRewardProgress] = useState(null);         // { lifetime_wagered, monthly_wagered, tiers: [...] }
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [meStats, setMeStats] = useState(null);                       // { weekly: {rank, wagered, total}, raffle: {tickets, ...} }

  // Phase A: admin reward-tier management + wager lookup state
  const [adminTiers, setAdminTiers] = useState([]);
  const [adminTiersLoading, setAdminTiersLoading] = useState(false);
  const [editingTier, setEditingTier] = useState(null);   // { id?, mode, rank_order, name, reward_amount, wager_requirement, color, active }
  const [wagerLookup, setWagerLookup] = useState({ username: '', start: '', end: '', result: null, loading: false, error: null });
  // Admin: monthly wager analytics (Rainbet totals for a chosen month)
  const [monthlyWagers, setMonthlyWagers] = useState({ month: '', data: null, loading: false, error: null, search: '' });

  // Phase B: leaderboard periods + site links, both DB-backed with admin CRUD
  const [dbPeriods, setDbPeriods] = useState(null);    // null = not yet loaded; array once loaded (may be empty)
  const [dbLinks, setDbLinks] = useState(null);        // same pattern
  const [adminPeriods, setAdminPeriods] = useState([]);
  const [adminLinks, setAdminLinks] = useState([]);
  const [editingPeriod, setEditingPeriod] = useState(null); // full period object or null
  const [editingLink, setEditingLink] = useState(null);

  // Phase C: admin wager stats (dashboard)
  const [wagerStats, setWagerStats] = useState(null);
  const [wagerStatsLoading, setWagerStatsLoading] = useState(false);
  const [wagerStatsError, setWagerStatsError] = useState(null);
  const [topListMode, setTopListMode] = useState('period'); // 'period' | 'allTime'

  // Phase C: slot challenges
  const [challenges, setChallenges] = useState({ active: [], completed: [] });
  const [challengesLoading, setChallengesLoading] = useState(false);
  const [claimingChallenge, setClaimingChallenge] = useState(null);
  const [claimForm, setClaimForm] = useState({ discord_username: '', rainbet_username: '', rainbet_id: '', bet_link: '', confirm: false });
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [completingChallenge, setCompletingChallenge] = useState(null);
  const [completeForm, setCompleteForm] = useState({ completed_by: '', completed_multiplier: '', completed_bet: '' });
  const [adminChallenges, setAdminChallenges] = useState([]);
  const [adminChallengeClaims, setAdminChallengeClaims] = useState([]);
  const [adminChallengeClaimFilter, setAdminChallengeClaimFilter] = useState('pending');
  const [adminChallengeClaimSearch, setAdminChallengeClaimSearch] = useState('');
  const [adminChallengeClaimSelected, setAdminChallengeClaimSelected] = useState(new Set());

  // Phase D: raffle bot
  const [botHealth, setBotHealth] = useState(null);
  const [botRaffles, setBotRaffles] = useState([]);
  const [botActiveRaffle, setBotActiveRaffle] = useState(null);
  const [botEntries, setBotEntries] = useState([]);
  const botEntriesRef = useRef([]);
  useEffect(() => { botEntriesRef.current = botEntries; }, [botEntries]);
  // Phase 4: winner history across all raffles
  const [winnerHistory, setWinnerHistory] = useState([]);
  const [winnerHistoryFilter, setWinnerHistoryFilter] = useState('all');
  const [selectedWinners, setSelectedWinners] = useState(new Set());
  const [winnerHistorySearch, setWinnerHistorySearch] = useState('');
  const [botLoading, setBotLoading] = useState(false);
  const [botError, setBotError] = useState(null);
  // Bingo (3x3 stream game on the bot service)
  const [bingoState, setBingoState] = useState(null); // { status, sessionId, entryCount, board, activePos, recentEntries }
  const [bingoBusy, setBingoBusy] = useState(false);
  // ---- PVP Battles ----
  const [battleState, setBattleState] = useState(null); // public /api/battles/state payload
  const [battleAdmin, setBattleAdmin] = useState(null); // admin /api/admin/battles/active payload
  const [battleHistory, setBattleHistory] = useState({ sessions: [], total: 0 });
  const [battleHistoryPage, setBattleHistoryPage] = useState(1);
  const [battleHistoryOpen, setBattleHistoryOpen] = useState(null); // expanded past-session id
  const [battleHistoryDetail, setBattleHistoryDetail] = useState({}); // session id -> full detail
  const [battleForm, setBattleForm] = useState(null); // create/edit session form values, null = closed
  const [battleBusy, setBattleBusy] = useState(false);
  const [battleError, setBattleError] = useState(null);
  const [battleDraw, setBattleDraw] = useState({ a: 't1', b: 't2' }); // which teams face off next
  const [battleShowWidget, setBattleShowWidget] = useState(false); // OBS link hidden behind a button (avoid leaking it on stream)
  const [battleShowEntrants, setBattleShowEntrants] = useState(false); // live entrant list w/ detected sub/affiliate flags
  const [battleEntrantSearch, setBattleEntrantSearch] = useState('');
  // One inline slot-call editor at a time (battles + bingo): { key, value }
  const [slotEdit, setSlotEdit] = useState(null);
  // <datalist> options for the slot editors — built once, only mounted while editing
  const slotNameOptions = useMemo(() => allSlots.map((s, i) => <option key={i} value={s.name} />), []);
  const [battlePrize, setBattlePrize] = useState(''); // prize text for the final crown
  const [battleResults, setBattleResults] = useState({}); // per-matchup draft buy/payout inputs, keyed by matchup id
  const [battleAddWinner, setBattleAddWinner] = useState(null); // { side, platform, username, tickets } manual add form
  const [bingoError, setBingoError] = useState(null);
  const [bingoSelectedPos, setBingoSelectedPos] = useState(null);
  const [bingoShowWidget, setBingoShowWidget] = useState(false); // OBS link behind a button (don't leak it on stream)
  // Admin entries panel (remove trolls, hand-pick a replacement)
  const [bingoShowEntries, setBingoShowEntries] = useState(false);
  const [bingoEntries, setBingoEntries] = useState([]);
  const [bingoEntrySearch, setBingoEntrySearch] = useState('');
  // Bingo "Pick Random" roulette animation + sound (driven by rollNonce)
  const [bingoRollPos, setBingoRollPos] = useState(null);
  const [bingoRolling, setBingoRolling] = useState(false);
  const bingoRollTimerRef = useRef(null);
  const bingoAudioRef = useRef(null);
  const bingoLastRollRef = useRef(null);
  const [startRaffleForm, setStartRaffleForm] = useState({
    keyword: 'join',
    platforms: { kick: true, twitch: true },
    followers_only: false,
    affiliate_bonus: false,
    affiliate_multiplier: 1.25,
    on_code_only: false
  });
  const [drawCount, setDrawCount] = useState(1);
  const [viewingRaffleEntries, setViewingRaffleEntries] = useState(null); // raffle id
  // Winner stats modal
  const [winnerStatsModal, setWinnerStatsModal] = useState(null); // { username, rainbet_username, platform, stats, loading }

  // Phase E (Snutzy port — Session 1): points system
  const [pointsBalance, setPointsBalance] = useState(null);
  const [pointsShop, setPointsShop] = useState([]);
  const [pointsHistory, setPointsHistory] = useState({ items: [], total: 0, page: 1 });
  const [pointsLeaderboard, setPointsLeaderboard] = useState({ items: [], total: 0, page: 1 });
  const [pointsConfig, setPointsConfig] = useState(null);
  const [pointsRecentRedemptions, setPointsRecentRedemptions] = useState([]);
  const [myRedemptions, setMyRedemptions] = useState([]);
  const [pointsTab, setPointsTab] = useState('shop'); // shop | history | redemptions | leaderboard
  const [redeemingItem, setRedeemingItem] = useState(null);
  const [redeemNotes, setRedeemNotes] = useState('');
  const [redeemSlotChoice, setRedeemSlotChoice] = useState(''); // for items with requires_slot_choice
  // Admin: inspect any user's points from the leaderboard
  const [adminUserInspect, setAdminUserInspect] = useState(null);
  const [adminUserInspectTab, setAdminUserInspectTab] = useState('history');
  const [adminUserHistory, setAdminUserHistory] = useState({ items: [], total: 0, page: 1, loading: false });
  const [adminUserRedemptions, setAdminUserRedemptions] = useState({ items: [], loading: false });

  // Admin points state
  const [adminPointsTab, setAdminPointsTab] = useState('config');
  const [grantUserSuggestions, setGrantUserSuggestions] = useState([]); // config | shop | redemptions | grant | user
  const [pointsLookupSuggestions, setPointsLookupSuggestions] = useState([]);
  const [adminPointsConfig, setAdminPointsConfig] = useState(null);
  const [adminPointsConfigDirty, setAdminPointsConfigDirty] = useState({});
  const [adminPointsStats, setAdminPointsStats] = useState(null);
  const [adminShopItems, setAdminShopItems] = useState([]);
  const [editingShopItem, setEditingShopItem] = useState(null);
  const [adminPointsRedemptions, setAdminPointsRedemptions] = useState([]);
  const [adminPointsRedemptionFilter, setAdminPointsRedemptionFilter] = useState('pending');
  const [grantForm, setGrantForm] = useState({ userId: '', amount: '', reason: '' });
  const [pointsUserLookup, setPointsUserLookup] = useState({ userId: '', result: null });
  // Phase E.2a: wager poller status
  const [wagerPollerStatus, setWagerPollerStatus] = useState(null);
  const [wagerPollerBusy, setWagerPollerBusy] = useState(false);

  // Tournament State
  const [tournamentProviders, setTournamentProviders] = useState(['Pragmatic', 'Hacksaw', 'Nolimit City', 'BGaming', 'NetEnt', 'Peter & Sons', 'ELK Studios', 'Red Tiger']);
  const [tournamentProviderInput, setTournamentProviderInput] = useState('');
  const [tournamentBracket, setTournamentBracket] = useState(null);
  const [tournamentHistory, setTournamentHistory] = useState([]);
  const [activeTournament, setActiveTournament] = useState(null);
  const [tournamentSize, setTournamentSize] = useState(8); // 4, 8, or 16 providers
  const [showTournamentSetup, setShowTournamentSetup] = useState(false);
  const [bracketScrolledOnce, setBracketScrolledOnce] = useState(false);
  const [showBracketFullscreen, setShowBracketFullscreen] = useState(false);
  const bracketContainerRef = React.useRef(null);

  // Auth & Predictions State
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [activeHuntData, setActiveHuntData] = useState(null);
  const [myPrediction, setMyPrediction] = useState(null);
  const [predictionInput, setPredictionInput] = useState('');
  const [predictionSubmitting, setPredictionSubmitting] = useState(false);
  const [predictionError, setPredictionError] = useState(null);
  const [predictionSuccess, setPredictionSuccess] = useState(null);
  const [huntPredictions, setHuntPredictions] = useState([]);
  const [_pe, set_pe] = useState(false); // Hidden permission flag
  const [myWins, setMyWins] = useState([]);
  const [showClaimModal, setShowClaimModal] = useState(null);
  const [claimUsername, setClaimUsername] = useState('');
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [predictionHistory, setPredictionHistory] = useState([]);
  const [allWinners, setAllWinners] = useState([]);
  const [lastCompletedHunt, setLastCompletedHunt] = useState(null);
  const [predictionSortOrder, setPredictionSortOrder] = useState('newest');
  const [predictionPage, setPredictionPage] = useState(1);
  const [showWinModal, setShowWinModal] = useState(false);
  const [winModalData, setWinModalData] = useState(null);
  
  // Hunt Override State (Admin)
  const [huntOverride, setHuntOverride] = useState({ huntId: '', startBalance: '', endBalance: '', status: 'active' });
  const [huntOverrideLoading, setHuntOverrideLoading] = useState(false);
  
  // Profile State
  const [profileStats, setProfileStats] = useState(null);
  // Slot Requests state — driven by /api/slot-requests, refreshed on page visit + 5s interval
  const [slotReqData, setSlotReqData] = useState({ pending: [], completed: [], open: false, loadedAt: null });
  const [slotReqCompletedSort, setSlotReqCompletedSort] = useState('order');
  const [predictionSubTab, setPredictionSubTab] = useState(
    window.location.pathname.includes('prediction-result') ? 'results' : 'predict'
  );
  const [slotReqCompleteModal, setSlotReqCompleteModal] = useState(null); // { request, bet, payout, multiplier, notes }
  const [editingRainbet, setEditingRainbet] = useState(false);
  const [rainbetUsernameInput, setRainbetUsernameInput] = useState('');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showRewardRules, setShowRewardRules] = useState(false);
  const [claimModal, setClaimModal] = useState(null); // { type: 'lossback'|'weekly_monthly'|'milestone' }
  const [claimScreenshot, setClaimScreenshot] = useState(null);
  const [claimNotes, setClaimNotes] = useState('');
  const [myClaims, setMyClaims] = useState([]);
  const [wagerClaimsAdmin, setWagerClaimsAdmin] = useState([]);
  const [wagerClaimsFilter, setWagerClaimsFilter] = useState('pending');
  const [lightboxImg, setLightboxImg] = useState(null);
  const [userNotifications, setUserNotifications] = useState([]);
  const [prizeClaims, setPrizeClaims] = useState([]);
  const [prizeClaimsAdmin, setPrizeClaimsAdmin] = useState([]);
  const [prizeClaimsFilter, setPrizeClaimsFilter] = useState('pending');
  const [prizeClaimModal, setPrizeClaimModal] = useState(false);
  const [adminTierClaims, setAdminTierClaims] = useState([]);
  const [adminTierClaimsFilter, setAdminTierClaimsFilter] = useState('pending');
  const [activeWagerRaffle, setActiveWagerRaffle] = useState(null);
  const [wagerRaffleContenders, setWagerRaffleContenders] = useState([]);
  const [wagerRaffleHistory, setWagerRaffleHistory] = useState([]);
  const [pastRafflePage, setPastRafflePage] = useState(0); // Past Raffles pager — 1 per page, newest first
  const [upcomingWagerRaffle, setUpcomingWagerRaffle] = useState(null);
  const [extendRaffleDate, setExtendRaffleDate] = useState(null); // { raffleId, newEnd }
  const [adminWagerRaffles, setAdminWagerRaffles] = useState([]);
  const [editingWagerRaffle, setEditingWagerRaffle] = useState(null);
  const [adminPeriodHistory, setAdminPeriodHistory] = useState([]);
  const [expandedPeriod, setExpandedPeriod] = useState(null);
  const [slotReqReel, setSlotReqReel] = useState(null); // { strip, target, winner, stage }
  const [showChecklist, setShowChecklist] = useState(false);
  const [checklistData, setChecklistData] = useState(null);
  const [checklistAdminClaims, setChecklistAdminClaims] = useState([]);
  const [checklistInputs, setChecklistInputs] = useState({});
  const [checklistAdminFilter, setChecklistAdminFilter] = useState('claimed');
  const [welcomeStep, setWelcomeStep] = useState(1); // 1 = rainbet username, 2 = link other platform
  
  // Admin State
  const [adminTab, setAdminTab] = useState('dashboard');
  const [adminStats, setAdminStats] = useState(null);
  const [adminHunts, setAdminHunts] = useState([]);
  const [adminClaims, setAdminClaims] = useState([]);
  const [adminPaidClaims, setAdminPaidClaims] = useState([]);
  const [adminGiveaways, setAdminGiveaways] = useState([]);
  const [adminPaidGiveaways, setAdminPaidGiveaways] = useState([]);
  const [adminUnclaimedWins, setAdminUnclaimedWins] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersTotal, setAdminUsersTotal] = useState(0);
  const [adminUserSearch, setAdminUserSearch] = useState('');
  const [adminPaidRewardsSearch, setAdminPaidRewardsSearch] = useState('');
  const [adminPaidGiveawaysSearch, setAdminPaidGiveawaysSearch] = useState('');
  const [selectedAdminHunt, setSelectedAdminHunt] = useState(null);
  const [selectedHuntPredictions, setSelectedHuntPredictions] = useState([]);
  const [newGiveaway, setNewGiveaway] = useState({ recipientType: 'rainbet', rainbetUsername: '', amount: '', description: '' });
  const [adminRewards, setAdminRewards] = useState({ first: '$25 Rainbet Balance', second: '$15 Rainbet Balance', third: '$10 Rainbet Balance' });
  const [showEndHuntModal, setShowEndHuntModal] = useState(false);
  const [endHuntBalance, setEndHuntBalance] = useState('');
  const [adminHuntPage, setAdminHuntPage] = useState(1);
  const [adminClaimsPage, setAdminClaimsPage] = useState(1);
  const [adminUsersPage, setAdminUsersPage] = useState(1);
  const [adminPaidRewardsPage, setAdminPaidRewardsPage] = useState(1);
  const [adminPaidGiveawaysPage, setAdminPaidGiveawaysPage] = useState(1);
  const [adminPaidRewardsTotal, setAdminPaidRewardsTotal] = useState(0);
  const [adminPaidGiveawaysTotal, setAdminPaidGiveawaysTotal] = useState(0);
  const [manualPayoutAmount, setManualPayoutAmount] = useState('');
  const [showManualPayoutModal, setShowManualPayoutModal] = useState(false);
  const [showEditRewardsModal, setShowEditRewardsModal] = useState(null);
  const [editRewardsData, setEditRewardsData] = useState({ first: '', second: '', third: '' });
  
  // Sort states for admin rewards
  const [pendingRewardsSort, setPendingRewardsSort] = useState({ field: 'hunt_id', direction: 'desc' });
  const [unclaimedWinsSort, setUnclaimedWinsSort] = useState({ field: 'hunt_id', direction: 'desc' });
  const [paidRewardsSort, setPaidRewardsSort] = useState({ field: 'hunt_id', direction: 'desc' });
  
  // Prediction Control State
  const [predictionControlLoading, setPredictionControlLoading] = useState(false);
  const [predictionControlStatus, setPredictionControlStatus] = useState(null);
  
  // Eligibility Whitelist State
  const [eligibilityWhitelist, setEligibilityWhitelist] = useState([]);
  
  // Slot Picks State
  const [slotPicksQueue, setSlotPicksQueue] = useState([]);
  const [slotPicksQueueTotal, setSlotPicksQueueTotal] = useState(0);
  const [slotPicksQueuePage, setSlotPicksQueuePage] = useState(1);
  const [slotPicksStats, setSlotPicksStats] = useState({ pending: 0, completed: 0, paid: 0, allTimePaidAmount: 0 });
  const [adminSlotPicks, setAdminSlotPicks] = useState([]);
  const [adminSlotPicksTotal, setAdminSlotPicksTotal] = useState(0);
  const [adminSlotPicksPage, setAdminSlotPicksPage] = useState(1);
  const [adminSlotPicksTab, setAdminSlotPicksTab] = useState('pending');
  const [eventSubStatus, setEventSubStatus] = useState(null);
  const SLOT_PICKS_PER_PAGE = 15;
  
  // Results page state
  const [resultsHistoryPage, setResultsHistoryPage] = useState(1);
  const RESULTS_PER_PAGE = 5;
  const [showPreviousWinners, setShowPreviousWinners] = useState(false);

  // Community Hunts State
  const [communityHunts, setCommunityHunts] = useState([]);
  const [activeCommunityHunt, setActiveCommunityHunt] = useState(null);
  const [communityHuntEntries, setCommunityHuntEntries] = useState([]);
  const [communityHuntAllEntries, setCommunityHuntAllEntries] = useState([]); // All entries when open
  const [previousCommunityHunts, setPreviousCommunityHunts] = useState([]);
  const [huntHistoryPagination, setHuntHistoryPagination] = useState({ page: 1, totalPages: 1, total: 0, hasMore: false });
  const [huntHistoryLoading, setHuntHistoryLoading] = useState(false);
  const [editingHuntPayouts, setEditingHuntPayouts] = useState(null); // hunt id being edited
  const [myEntryStatus, setMyEntryStatus] = useState(null); // null, 'entered', 'selected', 'not_selected'
  const [myEligibility, setMyEligibility] = useState({ affiliate: false, vip: false, wagered: 0 });
  const [communityHuntLoading, setCommunityHuntLoading] = useState(false);

  const BACKEND_API_URL = 'https://camneverwins-backend-production.up.railway.app';
  // Bot API (separate Railway service, same JWT secret). Set to the bot's URL once deployed.
  const BOT_API_URL = 'https://camneverwins-bot-production.up.railway.app';
  const GUILD_CODE = 'cam';
  // affiliateLink is derived from DB (site_links) further down; see FALLBACK + derivation below.
  // MyrtleBot widget guild ID for the embedded bonus hunt iframe.
  // TODO: Set this once Cam's MyrtleBot guild is provisioned. Until then,
  // the iframes are hidden and a "not configured" placeholder shows instead.
  const MYRTLE_GUILD_ID = '';
  
  // Leaderboard periods - add new periods here
  // NOTE: Reset for CamNeverWins. Edit prize structure / dates as needed.
  // Leaderboard periods: DB-backed (phase B). Until fetched, use a single
  // fallback entry so the page renders without a flash of broken content.
  const FALLBACK_PERIODS = [
    {
      id: 'jun-w1-2026',
      name: 'June Week 1 2026',
      start: new Date('2026-04-30T00:00:00Z'),
      end: new Date('2026-06-01T00:00:00Z'),
      totalPrize: 50000,
      prizes: [
        { rank: 1, prize: 7000 }, { rank: 2, prize: 5000 }, { rank: 3, prize: 4000 },
        { rank: 4, prize: 4000 }, { rank: 5, prize: 3500 }, { rank: 6, prize: 3000 },
        { rank: 7, prize: 2500 }, { rank: 8, prize: 2000 }, { rank: 9, prize: 1500 }, { rank: 10, prize: 1000 }
      ]
    }
  ];
  // Normalise DB rows to the shape the rest of the component expects.
  const LEADERBOARD_PERIODS = (dbPeriods && dbPeriods.length)
    ? dbPeriods.map(p => ({
        id: p.slug,
        dbId: p.id,
        type: (p.type || 'monthly'),
        name: p.name,
        start: new Date(p.start_at),
        end: new Date(p.end_at),
        totalPrize: Number(p.total_prize),
        prizes: Array.isArray(p.prizes) ? p.prizes : [],
        winners: Array.isArray(p.winners) ? p.winners : undefined
      }))
    : FALLBACK_PERIODS;

  // Get current active period. This is the MONTHLY-side period — never a weekly
  // one (weeklyPeriod is resolved separately below). Without the type filter it
  // would grab whichever period covers "now" first, which can be a weekly period
  // and make the monthly view show weekly metadata.
  const now = new Date();
  const isMonthlyPeriod = (p) => (p.type || 'monthly') !== 'weekly';
  const currentPeriod = LEADERBOARD_PERIODS.find(p => isMonthlyPeriod(p) && now >= p.start && now < p.end)
    || LEADERBOARD_PERIODS.find(isMonthlyPeriod)
    || LEADERBOARD_PERIODS[0];
  const previousPeriod = LEADERBOARD_PERIODS.find(p => p.end <= now && p.winners) || null;

  const PREDICTIONS_PER_PAGE = 20;

  const prizes = currentPeriod.prizes;

  // Weekly period — look for an active weekly period in DB
  const weeklyPeriods = (dbPeriods || [])
    .filter(p => (p.type || 'monthly') === 'weekly')
    .map(p => ({
      id: p.slug || p.id,
      name: p.name,
      active: p.active !== false,
      start: new Date(p.start_at),
      end: new Date(p.end_at),
      totalPrize: Number(p.total_prize || 0),
      prizes: Array.isArray(p.prizes) ? p.prizes : [],
      winners: Array.isArray(p.winners) ? p.winners : undefined
    }));
  const weeklyPeriod = weeklyPeriods.find(p => now >= p.start && now < p.end) || null;

  // Board availability. A board is "active" once it has started (real
  // standings). A monthly that hasn't started yet is "upcoming" and shown as a
  // PREVIEW — countdown to its start + prize tiers with placeholder names — so
  // we never surface the backend's stale calendar-month fallback wagers.
  const monthlyActive = (dbPeriods || []).some(p => (p.type || 'monthly') !== 'weekly' && p.active !== false && new Date(p.start_at) <= now && new Date(p.end_at) > now);
  const weeklyActive = !!weeklyPeriod && weeklyLeaderboard.length > 0;
  // currentPeriod is the monthly-side period; "upcoming" when it hasn't started.
  const monthlyUpcoming = !monthlyActive && !!currentPeriod && currentPeriod.start > now;
  const monthlyViewable = monthlyActive || monthlyUpcoming;

  // effectiveType = what the viewer actually sees. Honors the toggle choice when
  // both boards are available; otherwise falls through to whichever one IS
  // available, so a missing/ended side never blanks the page.
  let effectiveType = leaderboardType;
  if (effectiveType === 'monthly' && !monthlyViewable && weeklyActive) effectiveType = 'weekly';
  else if (effectiveType === 'weekly' && !weeklyActive && monthlyViewable) effectiveType = 'monthly';

  // Monthly selected but not yet started → render a prize preview, not standings.
  const monthlyPreview = effectiveType === 'monthly' && monthlyUpcoming;

  // Between weekly boards (last week ended, next one not started or not created
  // yet): preview the next week, else show the last week's final results —
  // never the old monthly or the backend's calendar-week fallback wagers.
  const weeklyGap = effectiveType === 'weekly' && !weeklyPeriod;
  const nextWeekly = weeklyGap
    ? weeklyPeriods.filter(p => p.active && p.start > now).sort((a, b) => a.start - b.start)[0] || null
    : null;
  const lastWeekly = weeklyGap && !nextWeekly
    ? weeklyPeriods.filter(p => p.active && p.end <= now).sort((a, b) => b.end - a.end)[0] || null
    : null;
  const weeklyPreview = !!nextWeekly;
  const weeklyEnded = !!lastWeekly;
  const shownWeekly = weeklyPeriod || nextWeekly || lastWeekly;
  const weeklyPrizes = shownWeekly?.prizes || [];
  const boardPreview = monthlyPreview || weeklyPreview;

  const activePrizes = effectiveType === 'weekly' ? weeklyPrizes : prizes;
  const getActivePrizeForRank = (rank) => activePrizes.find(p => p.rank === rank)?.prize || 0;
  const hasWeeklyPrizes = weeklyPrizes.length > 0;

  // Headline "up to $X" for monthly milestones. Tiers stack, so the real ceiling
  // is the SUM of every active monthly tier — not the biggest single tier. Rounded
  // down to a clean thousand so the marketing number stays tidy as tiers change.
  const monthlyMilestoneTotal = Math.floor(
    rewardTiersAll
      .filter(t => t.mode === 'monthly' && t.active !== false)
      .reduce((sum, t) => sum + Number(t.reward_amount || 0), 0) / 1000
  ) * 1000;

  // Synthetic standings for a preview: one row per prize rank, blank names, no
  // wagers — just shows off the prize tiers until the board goes live. Shared by
  // the home podium and the leaderboard page so both preview identically.
  const previewRows = boardPreview
    ? (activePrizes || []).map(pz => ({ rank: pz.rank, username: '', wagers: 0, prize: pz.prize, _preview: true }))
    : null;
  // Ended week's final standings come from its snapshot (empty until it's taken).
  const finalRows = weeklyEnded
    ? (lastWeekly.winners || []).slice(0, 20).map(w => ({ rank: w.rank, username: w.username, wagers: Number(w.wagered || 0), site: w.site, _final: true }))
    : null;
  // Rows for the home podium + leaderboard page.
  const boardRows = previewRows || finalRows || (effectiveType === 'weekly' ? weeklyLeaderboard : leaderboard);

  // Display period — used by hero + leaderboard headers. Tracks effectiveType.
  const displayPeriod = (effectiveType === 'weekly' && shownWeekly) ? shownWeekly : currentPeriod;

  // Countdown targets the START while previewing an upcoming board, else the END.
  const LEADERBOARD_END = displayPeriod.end;
  const leaderboardEndTs = (boardPreview ? displayPeriod.start : LEADERBOARD_END).getTime();

  // socialLinks: now derived from the site_links API. Falls back to a baseline set until fetch resolves.
  const FALLBACK_SOCIALS = [
    { name: 'Kick', url: 'https://kick.com/camneverwins', icon: 'kick' },
    { name: 'Twitch', url: 'https://www.twitch.tv/camneverwins', icon: 'twitch' },
    { name: 'Discord', url: 'https://discord.gg/camneverwins', icon: 'discord' },
    { name: 'YouTube', url: 'https://www.youtube.com/@CamNvrWins', icon: 'youtube' },
    { name: 'Twitter', url: 'https://x.com/camneverwins', icon: 'twitter' },
    { name: 'Instagram', url: 'https://www.instagram.com/camneverwins', icon: 'instagram' },
    { name: 'Instagram (Slots)', url: 'https://www.instagram.com/camneverwinsslots', icon: 'instagram' }
  ];
  const socialLinks = (dbLinks && dbLinks.some(l => l.category === 'social'))
    ? dbLinks.filter(l => l.category === 'social').map(l => ({ name: l.label, url: l.url, icon: l.icon || 'twitch' }))
    : FALLBACK_SOCIALS;

  // Affiliate + partner URLs — derived from DB, fallback hardcoded.
  const affiliateLink = (dbLinks && dbLinks.find(l => l.key === 'affiliate_rainbet'))?.url || 'https://rainbet.com/?r=cam';
  // Rainbet.us (US players) — always the secondary link next to .com, never the main CTA
  const affiliateLinkUs = (dbLinks && dbLinks.find(l => l.key === 'affiliate_rainbet_us'))?.url || 'https://rainbet.us/?r=cam';
  const PARTNER_LINKS = {
    rainbet: affiliateLink,
    nordvpn: (dbLinks && dbLinks.find(l => l.key === 'partner_nordvpn'))?.url || 'https://nordvpn.com/camneverwins'
  };

  const streamers = [
    { name: 'Cam', username: 'camneverwins', url: 'https://kick.com/camneverwins' }
  ];

  const socialIcons = {
    kick: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 3h6v6h3V6h3V3h6v6h-3v3h-3v3h3v3h3v6h-6v-3h-3v-3h-3v6H3z"/></svg>,
    twitch: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/></svg>,
    discord: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>,
    youtube: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
    tiktok: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>,
    twitter: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
    instagram: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.757-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/></svg>
  };

  // Reward tier icons — escalating prestige order. Indexed by rank_order (1-based);
  // wraps if there are more tiers than icons. Each is filled (uses currentColor) so
  // it inherits the tier's configured color via a wrapping element.
  const tierIcons = [
    // 1: Coin stack — entry tier
    <svg viewBox="0 0 24 24" fill="currentColor"><ellipse cx="12" cy="6" rx="8" ry="3"/><path d="M4 6v4c0 1.66 3.58 3 8 3s8-1.34 8-3V6"/><path d="M4 11v4c0 1.66 3.58 3 8 3s8-1.34 8-3v-4"/><path d="M4 16v3c0 1.66 3.58 3 8 3s8-1.34 8-3v-3"/></svg>,
    // 2: Medal with ribbon
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 2l1.5 5h7L17 2H7z" opacity="0.7"/><circle cx="12" cy="14" r="7"/><circle cx="12" cy="14" r="4" fill="rgba(0,0,0,0.3)"/><path d="M9 13l2 2 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    // 3: Trophy cup
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 4v3a6 6 0 0 0 12 0V4H6z"/><path d="M6 5H3a3 3 0 0 0 3 3" opacity="0.7"/><path d="M18 5h3a3 3 0 0 1-3 3" opacity="0.7"/><rect x="9" y="13" width="6" height="3"/><rect x="7" y="16" width="10" height="2" rx="1"/><rect x="6" y="20" width="12" height="2" rx="1"/></svg>,
    // 4: 5-pointed star with shine
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.6 6.4L22 9.3l-5.5 4.8L18 22l-6-3.6L6 22l1.5-7.9L2 9.3l7.4-0.9L12 2z"/><circle cx="9" cy="9" r="1" fill="rgba(255,255,255,0.7)"/></svg>,
    // 5: Diamond gem
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 3h12l4 6-10 12L2 9z"/><path d="M2 9h20" stroke="rgba(0,0,0,0.3)" strokeWidth="0.6" fill="none"/><path d="M12 21l-4-12M12 21l4-12M6 3l2 6M18 3l-2 6" stroke="rgba(0,0,0,0.3)" strokeWidth="0.6" fill="none"/><path d="M9 5h6l-1 2h-4z" fill="rgba(255,255,255,0.6)"/></svg>,
    // 6: Crown
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M2 7l4 11h12l4-11-5 4-5-7-5 7z"/><circle cx="2" cy="7" r="1.5"/><circle cx="12" cy="3" r="1.5"/><circle cx="22" cy="7" r="1.5"/><rect x="6" y="20" width="12" height="2" rx="0.5"/><circle cx="9" cy="13" r="1" fill="rgba(255,255,255,0.7)"/><circle cx="15" cy="13" r="1" fill="rgba(255,255,255,0.7)"/></svg>,
    // 7: Lightning bolt — premium speed
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L3 14h7l-1 8L19 10h-7l1-8z"/><path d="M11 14h-3l5-6" fill="rgba(255,255,255,0.5)"/></svg>,
    // 8: Treasure chest
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 7h18v4H3z" opacity="0.85"/><rect x="3" y="11" width="18" height="9" rx="1"/><circle cx="12" cy="14" r="1.5" fill="rgba(0,0,0,0.5)"/><path d="M11 14v2.5h2V14" stroke="rgba(0,0,0,0.5)" strokeWidth="1" fill="none"/><path d="M3 7c0-2 4-3 9-3s9 1 9 3" fill="none" stroke="currentColor" strokeWidth="1.5"/></svg>,
    // 9: Rocket — beyond
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c3 2 5 5 5 9v6l-2 2h-6l-2-2v-6c0-4 2-7 5-9z"/><circle cx="12" cy="9" r="2" fill="rgba(0,0,0,0.4)"/><path d="M7 14l-3 4 3-1M17 14l3 4-3-1" fill="currentColor"/><path d="M10 19l1 3M14 19l-1 3M12 20v3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>,
    // 10: Galaxy/sparkle — ultimate
    <svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4M5 5l3 3M19 19l-3-3M5 19l3-3M19 5l-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><circle cx="6" cy="6" r="0.8"/><circle cx="18" cy="6" r="0.8"/><circle cx="6" cy="18" r="0.8"/><circle cx="18" cy="18" r="0.8"/></svg>
  ];
  const getTierIcon = (rankOrder) => {
    const idx = ((Number(rankOrder) || 1) - 1) % tierIcons.length;
    return tierIcons[idx];
  };

  // Sidebar navigation icons - emojis (matches snutzy.gg style)
  const navIcons = {
    home: <span className="nav-emoji">🏠</span>,
    leaderboard: <span className="nav-emoji">🏆</span>,
    rewards: <span className="nav-emoji">🎁</span>,
    instructions: <span className="nav-emoji">📋</span>,
    challenges: <span className="nav-emoji">⚔️</span>,
    points: <span className="nav-emoji">💰</span>,
    bonushunts: <span className="nav-emoji">🎯</span>,
    communityhunts: <span className="nav-emoji">👥</span>,
    predictions: <span className="nav-emoji">🔮</span>,
    slotpicks: <span className="nav-emoji">🎰</span>,
    results: <span className="nav-emoji">📊</span>,
    games: <span className="nav-emoji">🎮</span>,
    socials: <span className="nav-emoji">📸</span>,
    slotrequests: <span className="nav-emoji">🎰</span>,
    battles: <span className="nav-emoji">🥊</span>,
    admin: <span className="nav-emoji">⚙️</span>,
  };

  const casinoGames = [
    { name: 'Blackjack', color: '#ef4444' },
    { name: 'Roulette', color: '#22c55e' },
    { name: 'CRAPS', color: '#3b82f6' },
    { name: 'Dice', color: '#f59e0b' },
    { name: 'Baccarat', color: '#8b5cf6' },
    { name: 'GOD MODE', color: '#ec4899' },
    { name: 'Feature Spins', color: '#06b6d4' }
  ];


  // Slot catalog lives in src/slotsData.js (auto-generated from the Rainbet
  // games API — see the header comment there for the refresh pipeline).
  const allProviders = [...new Set(allSlots.map(slot => slot.provider))];
  const [selectedProviders, setSelectedProviders] = useState(allProviders);
  const [slotSearch, setSlotSearch] = useState('');
  const [slotBonusBuyOnly, setSlotBonusBuyOnly] = useState(false);
  const [slotNewOnly, setSlotNewOnly] = useState(false);

  const shuffleArray = useCallback((array, seed) => {
    const shuffled = [...array];
    let currentIndex = shuffled.length;
    const seededRandom = () => {
      const x = Math.sin(seed + currentIndex) * 10000;
      return x - Math.floor(x);
    };
    while (currentIndex !== 0) {
      const randomIndex = Math.floor(seededRandom() * currentIndex);
      currentIndex--;
      [shuffled[currentIndex], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[currentIndex]];
    }
    return shuffled;
  }, []);

  const filteredSlots = React.useMemo(() => {
    const filtered = allSlots.filter(slot =>
      slot.image &&
      selectedProviders.includes(slot.provider) &&
      (!slotBonusBuyOnly || slot.bonusBuy === true) &&
      (!slotNewOnly || slot.isNew === true)
    );
    return shuffleSeed > 0 ? shuffleArray(filtered, shuffleSeed) : filtered;
  }, [selectedProviders, slotBonusBuyOnly, slotNewOnly, shuffleSeed, shuffleArray]);

  const [reelSlots, setReelSlots] = useState([]);
  
  useEffect(() => {
    if (filteredSlots.length === 0) { setReelSlots([]); return; }
    let displaySlots = [...filteredSlots].sort(() => Math.random() - 0.5);
    if (displaySlots.length > 40) displaySlots = displaySlots.slice(0, 40);
    setReelSlots(displaySlots);
  }, [filteredSlots, shuffleSeed]);

  // ============================================
  // AUTH FUNCTIONS
  // ============================================
  
  const getAuthToken = () => {
    try {
      return localStorage.getItem('cam_auth_token');
    } catch (e) {
      console.error('Error reading auth token:', e);
      return null;
    }
  };
  
  const setAuthToken = (token) => {
    try {
      if (token) {
        localStorage.setItem('cam_auth_token', token);
        console.log('Auth token saved to localStorage');
      } else {
        localStorage.removeItem('cam_auth_token');
        console.log('Auth token removed from localStorage');
      }
    } catch (e) {
      console.error('Error saving auth token:', e);
    }
  };

  const fetchWithAuth = async (url, options = {}) => {
    const token = getAuthToken();
    const headers = { ...options.headers, 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(url, { ...options, headers });
  };

  const checkAuth = async () => {
    const token = getAuthToken();
    console.log('Checking auth, token exists:', !!token);
    
    if (!token) {
      setAuthLoading(false);
      return;
    }
    
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/auth/me`);
      console.log('Auth check response status:', res.status);
      
      if (res.ok) {
        const data = await res.json();
        console.log('User authenticated:', data.user?.displayName);
        setUser(data.user);
        
        // Silent check for special permissions
        try {
          const pc = await fetchWithAuth(`${BACKEND_API_URL}/api/predictions/check-edit`);
          if (pc.ok) { const pd = await pc.json(); if (pd.e) set_pe(true); }
        } catch (_) {}
        
        // Check if first time login (no rainbet username) and show welcome modal
        if (data.user && !hasRainbetLink(data.user)) {
          const hasSeenWelcome = localStorage.getItem('cam_welcome_shown');
          if (!hasSeenWelcome) {
            setWelcomeStep(1);
            setShowWelcomeModal(true);
            localStorage.setItem('cam_welcome_shown', 'true');
          }
        }
      } else if (res.status === 401 || res.status === 403) {
        // Only clear token on auth errors — not on 500/503 (server restart, rate limit etc.)
        console.log('Auth rejected (401/403), clearing token');
        setAuthToken(null);
        setUser(null);
      } else {
        console.log(`Auth check returned ${res.status} — keeping token, retrying in 5s`);
        setTimeout(checkAuth, 5000);
      }
    } catch (error) {
      console.error('Auth check error:', error);
    }
    setAuthLoading(false);
  };

  const handleLogin = () => {
    // Clear the welcome shown flag so new logins can see the modal
    localStorage.removeItem('cam_welcome_shown');
    window.location.href = `${BACKEND_API_URL}/auth/twitch`;
  };

  const handleKickLogin = () => {
    localStorage.removeItem('cam_welcome_shown');
    window.location.href = `${BACKEND_API_URL}/auth/kick`;
  };

  const handleLogout = async () => {
    setAuthToken(null);
    setUser(null);
    setMyPrediction(null);
    setMyWins([]);
    setPredictionHistory([]);
    setProfileStats(null);
  };

  // Check for auth token in URL (after Twitch callback) and restore session
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('auth_token');
    const errorFromUrl = urlParams.get('auth_error');
    const linkError = urlParams.get('link_error');
    const linkSuccess = urlParams.get('link_success');
    
    if (errorFromUrl) {
      console.error('Auth error from callback:', errorFromUrl, urlParams.get('message'));
      window.history.replaceState({}, '', window.location.pathname);
      setAuthLoading(false);
      return;
    }

    if (linkError) {
      alert('Account linking failed: ' + linkError);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (linkSuccess) {
      const platform = linkSuccess.includes('kick') ? 'Kick' : 'Twitch';
      if (!linkSuccess.includes('already')) alert(`${platform} account linked successfully!`);
      // Clean URL but keep path (we land on /profile)
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    if (tokenFromUrl) {
      console.log('Received auth token from URL');
      setAuthToken(tokenFromUrl);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      // Check auth with the new token
      checkAuth();
    } else {
      // No token in URL, check if we have one stored
      checkAuth();
    }
  }, []);

  // ============================================
  // PREDICTION FUNCTIONS
  // ============================================

  const fetchActiveHunt = async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/predictions/active-hunt`);
      if (res.ok) {
        const data = await res.json();
        setActiveHuntData(data);
      }
    } catch (error) {
      console.error('Error fetching active hunt:', error);
    }
  };

  // Update hunt settings (admin)
  const updateHuntSettings = async (huntId, settings) => {
    setHuntOverrideLoading(true);
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/hunts/${huntId}/settings`, {
        method: 'PUT',
        body: JSON.stringify(settings)
      });
      const data = await res.json();
      if (res.ok) {
        alert(data.message || 'Hunt settings updated!');
        fetchActiveHunt();
        fetchAdminHunts();
      } else {
        alert(data.error || 'Failed to update hunt settings');
      }
    } catch (error) {
      alert('Error updating hunt settings');
    } finally {
      setHuntOverrideLoading(false);
    }
  };

  // Create manual hunt (admin)
  const createManualHunt = async () => {
    if (!huntOverride.huntId || !huntOverride.startBalance) {
      alert('Hunt ID and Start Balance are required');
      return;
    }
    setHuntOverrideLoading(true);
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/hunts/create`, {
        method: 'POST',
        body: JSON.stringify({
          huntId: huntOverride.huntId,
          startBalance: huntOverride.startBalance,
          status: huntOverride.status || 'active'
        })
      });
      const data = await res.json();
      if (res.ok) {
        alert(data.message || 'Hunt created!');
        setHuntOverride({ huntId: '', startBalance: '', endBalance: '', status: 'active' });
        fetchActiveHunt();
        fetchAdminHunts();
      } else {
        alert(data.error || 'Failed to create hunt');
      }
    } catch (error) {
      alert('Error creating hunt');
    } finally {
      setHuntOverrideLoading(false);
    }
  };

  const fetchMyPrediction = async (huntId) => {
    if (!user || !huntId) return;
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/predictions/my-prediction/${encodeURIComponent(huntId)}`);
      if (res.ok) {
        const data = await res.json();
        setMyPrediction(data.prediction);
        if (data.prediction) {
          setPredictionInput(data.prediction.predicted_balance.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching my prediction:', error);
    }
  };

  const fetchHuntPredictions = async (huntId) => {
    if (!huntId) return;
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/predictions/hunt/${encodeURIComponent(huntId)}`);
      if (res.ok) {
        const data = await res.json();
        setHuntPredictions(data.predictions || []);
      }
    } catch (error) {
      console.error('Error fetching hunt predictions:', error);
    }
  };

  const submitPrediction = async () => {
    if (!user || !activeHuntData?.activeHunt?.huntId) return;
    
    const amount = parseFloat(predictionInput);
    if (isNaN(amount) || amount < 0) {
      setPredictionError('Please enter a valid amount');
      return;
    }
    
    setPredictionSubmitting(true);
    setPredictionError(null);
    setPredictionSuccess(null);
    
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/predictions/submit`, {
        method: 'POST',
        body: JSON.stringify({
          huntId: activeHuntData.activeHunt.huntId,
          predictedBalance: amount
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setPredictionSuccess(data.message || 'Prediction submitted!');
        setMyPrediction({ predicted_balance: amount, created_at: new Date().toISOString() });
        fetchHuntPredictions(activeHuntData.activeHunt.huntId);
      } else {
        setPredictionError(data.error || 'Failed to submit prediction');
      }
    } catch (error) {
      setPredictionError('Network error. Please try again.');
    }
    
    setPredictionSubmitting(false);
  };

  const fetchMyWins = async () => {
    if (!user) return;
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/predictions/my-wins`);
      if (res.ok) {
        const data = await res.json();
        setMyWins(data.wins || []);
      }
    } catch (error) {
      console.error('Error fetching wins:', error);
    }
  };

  const claimWin = async (winnerId) => {
    if (!claimUsername.trim()) {
      alert('Please enter your Rainbet username');
      return;
    }
    
    setClaimSubmitting(true);
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/predictions/claim-win`, {
        method: 'POST',
        body: JSON.stringify({ odinnerId: winnerId, rainbetUsername: claimUsername.trim() })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        alert(data.message || 'Prize claimed!');
        setShowClaimModal(null);
        setClaimUsername('');
        fetchMyWins();
      } else {
        alert(data.error || 'Failed to claim');
      }
    } catch (error) {
      alert('Network error');
    }
    setClaimSubmitting(false);
  };

  const fetchPredictionHistory = async () => {
    if (!user) return;
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/predictions/my-history`);
      if (res.ok) {
        const data = await res.json();
        setPredictionHistory(data.predictions || []);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchAllWinners = async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/predictions/all-winners`);
      if (res.ok) {
        const data = await res.json();
        setAllWinners(data.hunts || []);
      }
    } catch (error) {
      console.error('Error fetching all winners:', error);
    }
  };

  const fetchLastCompleted = async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/predictions/last-completed`);
      if (res.ok) {
        const data = await res.json();
        setLastCompletedHunt(data);
      }
    } catch (error) {
      console.error('Error fetching last completed:', error);
    }
  };

  // Fetch prediction data when on bonus hunts page
  useEffect(() => {
    if (activePage === 'bonushunts' || activePage === 'predictions') {
      fetchActiveHunt();
      fetchAllWinners();
      fetchLastCompleted();
      const interval = setInterval(fetchActiveHunt, 30000);
      return () => clearInterval(interval);
    }
  }, [activePage]);

  // Fetch tournament data when on tournaments page
  useEffect(() => {
    if (activePage === 'tournaments') {
      fetchTournaments();
      const interval = setInterval(fetchTournaments, 10000); // Poll every 10s for live updates
      return () => clearInterval(interval);
    }
  }, [activePage]);

  // Fetch user's prediction when hunt or user changes
  useEffect(() => {
    if (activeHuntData?.activeHunt?.huntId) {
      fetchHuntPredictions(activeHuntData.activeHunt.huntId);
      if (user) fetchMyPrediction(activeHuntData.activeHunt.huntId);
    }
  }, [user, activeHuntData?.activeHunt?.huntId]);

  // Fetch wins when user logs in
  useEffect(() => {
    if (user) {
      fetchMyWins();
      fetchPredictionHistory();
    }
  }, [user]);

  // ============================================
  // ADMIN FUNCTIONS
  // ============================================

  const fetchAdminStats = async () => {
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/stats`);
      if (res.ok) {
        const data = await res.json();
        setAdminStats(data);
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    }
  };

  const fetchAdminHunts = async () => {
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/hunts`);
      if (res.ok) {
        const data = await res.json();
        setAdminHunts(data.hunts || []);
      }
    } catch (error) {
      console.error('Error fetching admin hunts:', error);
    }
  };

  const fetchAdminClaims = async () => {
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/claims/pending`);
      if (res.ok) {
        const data = await res.json();
        setAdminClaims(data.claims || []);
      }
    } catch (error) {
      console.error('Error fetching admin claims:', error);
    }
  };

  const fetchAdminGiveaways = async () => {
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/giveaways`);
      if (res.ok) {
        const data = await res.json();
        setAdminGiveaways(data.giveaways || []);
      }
    } catch (error) {
      console.error('Error fetching admin giveaways:', error);
    }
  };

  const fetchAdminUsers = async (search = '', page = 1) => {
    try {
      const offset = (page - 1) * 50;
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/users?search=${encodeURIComponent(search)}&limit=50&offset=${offset}`);
      if (res.ok) {
        const data = await res.json();
        setAdminUsers(data.users || []);
        setAdminUsersTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
    }
  };

  const openPredictions = async () => {
    if (!activeHuntData?.activeHunt?.huntId) {
      alert('No active hunt found');
      return;
    }
    
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/predictions/open`, {
        method: 'POST',
        body: JSON.stringify({
          huntId: activeHuntData.activeHunt.huntId,
          startBalance: activeHuntData.activeHunt.startBalance,
          rewards: adminRewards
        })
      });
      
      const data = await res.json();
      alert(data.message || 'Predictions opened!');
      fetchActiveHunt();
      fetchAdminHunts();
    } catch (error) {
      alert('Error opening predictions');
    }
  };

  const pausePredictions = async () => {
    if (!activeHuntData?.activeHunt?.huntId) return;
    
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/predictions/pause`, {
        method: 'POST',
        body: JSON.stringify({ huntId: activeHuntData.activeHunt.huntId })
      });
      
      const data = await res.json();
      alert(data.message || 'Predictions paused');
      fetchActiveHunt();
    } catch (error) {
      alert('Error pausing predictions');
    }
  };

  const closePredictions = async () => {
    if (!activeHuntData?.activeHunt?.huntId) return;
    
    const endBalance = prompt('Enter the final balance:');
    if (!endBalance) return;
    
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/predictions/close`, {
        method: 'POST',
        body: JSON.stringify({
          huntId: activeHuntData.activeHunt.huntId,
          endBalance: parseFloat(endBalance)
        })
      });
      
      const data = await res.json();
      alert(data.message || 'Hunt closed and winners calculated!');
      fetchActiveHunt();
      fetchAdminHunts();
      fetchAdminClaims();
      fetchLastCompleted();
      fetchAllWinners();
    } catch (error) {
      alert('Error closing predictions');
    }
  };

  const markClaimPaid = async (claimId) => {
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/claims/${claimId}/pay`, {
        method: 'POST'
      });
      
      if (res.ok) {
        fetchAdminClaims();
        fetchAdminStats();
      }
    } catch (error) {
      alert('Error marking claim as paid');
    }
  };

  const addGiveaway = async () => {
    if (!newGiveaway.rainbetUsername || !newGiveaway.amount) {
      alert('Please fill in username and amount');
      return;
    }
    
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/giveaways`, {
        method: 'POST',
        body: JSON.stringify(newGiveaway)
      });
      
      if (res.ok) {
        setNewGiveaway({ recipientType: 'rainbet', rainbetUsername: '', amount: '', description: '' });
        fetchAdminGiveaways();
        fetchAdminStats();
      }
    } catch (error) {
      alert('Error adding giveaway');
    }
  };

  const markGiveawayPaid = async (giveawayId) => {
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/giveaways/${giveawayId}/pay`, {
        method: 'POST'
      });
      
      if (res.ok) {
        fetchAdminGiveaways();
        fetchAdminPaidGiveaways(adminPaidGiveawaysSearch);
        fetchAdminStats();
      }
    } catch (error) {
      alert('Error marking giveaway as paid');
    }
  };

  const deleteGiveaway = async (giveawayId) => {
    if (!window.confirm('Delete this giveaway?')) return;
    
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/giveaways/${giveawayId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        fetchAdminGiveaways();
      }
    } catch (error) {
      alert('Error deleting giveaway');
    }
  };

  // Manual payout function
  const submitManualPayout = async () => {
    if (!manualPayoutAmount || parseFloat(manualPayoutAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/manual-payout`, {
        method: 'POST',
        body: JSON.stringify({ 
          amount: parseFloat(manualPayoutAmount),
          description: 'Manual payout adjustment'
        })
      });
      
      if (res.ok) {
        setManualPayoutAmount('');
        setShowManualPayoutModal(false);
        fetchAdminStats();
        fetchAdminPaidGiveaways(adminPaidGiveawaysSearch);
        alert(`Added $${manualPayoutAmount} to total paid out`);
      } else {
        alert('Failed to add manual payout');
      }
    } catch (error) {
      alert('Error adding manual payout');
    }
  };

  // Enhanced Admin Functions
  const fetchAdminPaidClaims = async (search = '', page = 1) => {
    try {
      const offset = (page - 1) * 50;
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/claims/paid?search=${encodeURIComponent(search)}&limit=50&offset=${offset}`);
      if (res.ok) {
        const data = await res.json();
        setAdminPaidClaims(data.claims || []);
        setAdminPaidRewardsTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching paid claims:', error);
    }
  };

  const fetchAdminPaidGiveaways = async (search = '', page = 1) => {
    try {
      const offset = (page - 1) * 50;
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/giveaways/paid?search=${encodeURIComponent(search)}&limit=50&offset=${offset}`);
      if (res.ok) {
        const data = await res.json();
        setAdminPaidGiveaways(data.giveaways || []);
        setAdminPaidGiveawaysTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching paid giveaways:', error);
    }
  };

  const fetchAdminUnclaimedWins = async () => {
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/unclaimed-wins`);
      if (res.ok) {
        const data = await res.json();
        setAdminUnclaimedWins(data.wins || []);
      }
    } catch (error) {
      console.error('Error fetching unclaimed wins:', error);
    }
  };

  // Slot Picks Functions
  const DEFAULT_AVATAR = 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png';
  
  const fetchSlotPicksQueue = async (page = 1) => {
    try {
      const [queueRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_API_URL}/api/slot-picks/queue?page=${page}&limit=${SLOT_PICKS_PER_PAGE}`),
        fetch(`${BACKEND_API_URL}/api/slot-picks/stats`)
      ]);
      
      if (queueRes.ok) {
        const data = await queueRes.json();
        // Ensure profileImage has a default value
        const queue = (data.queue || []).map(pick => ({
          ...pick,
          profileImage: pick.profileImage || DEFAULT_AVATAR
        }));
        setSlotPicksQueue(queue);
        setSlotPicksQueueTotal(data.total || 0);
      }
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setSlotPicksStats(data);
      }
    } catch (error) {
      console.error('Error fetching slot picks:', error);
    }
  };

  const fetchAdminSlotPicks = async (status = '', page = 1) => {
    try {
      const statusParam = status ? `&status=${status}` : '';
      const url = `${BACKEND_API_URL}/api/admin/slot-picks?page=${page}&limit=${SLOT_PICKS_PER_PAGE}${statusParam}`;
      const res = await fetchWithAuth(url);
      if (res.ok) {
        const data = await res.json();
        // Ensure profile_image_url has a default value
        const slotPicks = (data.slotPicks || []).map(pick => ({
          ...pick,
          profile_image_url: pick.profile_image_url || DEFAULT_AVATAR
        }));
        setAdminSlotPicks(slotPicks);
        setAdminSlotPicksTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching admin slot picks:', error);
    }
  };

  const fetchEventSubStatus = async () => {
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/eventsub-status`);
      if (res.ok) {
        const data = await res.json();
        setEventSubStatus(data);
      }
    } catch (error) {
      console.error('Error fetching EventSub status:', error);
    }
  };

  const setupEventSub = async () => {
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/setup-eventsub`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        alert('EventSub subscription created successfully!');
        fetchEventSubStatus();
      } else {
        if (data.authUrl) {
          if (window.confirm('Broadcaster not authorized. Open authorization page?')) {
            window.open(data.authUrl, '_blank');
          }
        } else {
          alert(data.error || 'Failed to setup EventSub');
        }
      }
    } catch (error) {
      alert('Error setting up EventSub');
    }
  };

  const handleSlotPickAction = async (pickId, action, amount = null) => {
    try {
      let url = `${BACKEND_API_URL}/api/admin/slot-picks/${pickId}/${action}`;
      let options = { method: 'PUT' };
      
      if (action === 'pay' && amount !== null) {
        options.body = JSON.stringify({ amount: parseFloat(amount) });
      }
      
      if (action === 'delete') {
        url = `${BACKEND_API_URL}/api/admin/slot-picks/${pickId}`;
        options.method = 'DELETE';
      }
      
      const res = await fetchWithAuth(url, options);
      if (res.ok) {
        fetchAdminSlotPicks(adminSlotPicksTab === 'all' ? '' : adminSlotPicksTab, adminSlotPicksPage);
        fetchSlotPicksQueue(slotPicksQueuePage);
      } else {
        alert('Action failed');
      }
    } catch (error) {
      alert('Error performing action');
    }
  };

  const fetchSelectedHuntPredictions = async (huntId) => {
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/hunt/${huntId}/predictions`);
      if (res.ok) {
        const data = await res.json();
        setSelectedHuntPredictions(data.predictions || []);
      }
    } catch (error) {
      console.error('Error fetching hunt predictions:', error);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const updateStartBalance = async (huntId, newBalance) => {
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/hunt/${huntId}/start-balance`, {
        method: 'PUT',
        body: JSON.stringify({ startBalance: parseFloat(newBalance) })
      });
      if (res.ok) {
        fetchAdminHunts();
        fetchActiveHunt();
        alert('Start balance updated!');
      }
    } catch (error) {
      alert('Error updating start balance');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const resumePredictions = async () => {
    if (!activeHuntData?.activeHunt?.huntId) return;
    
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/predictions/resume`, {
        method: 'POST',
        body: JSON.stringify({ huntId: activeHuntData.activeHunt.huntId })
      });
      
      const data = await res.json();
      alert(data.message || 'Predictions resumed');
      fetchActiveHunt();
    } catch (error) {
      alert('Error resuming predictions');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const deletePrediction = async (predictionId) => {
    if (!window.confirm('Delete this prediction?')) return;
    
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/prediction/${predictionId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        fetchSelectedHuntPredictions(selectedAdminHunt?.hunt_id);
        fetchHuntPredictions(activeHuntData?.activeHunt?.huntId);
      }
    } catch (error) {
      alert('Error deleting prediction');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const deleteHunt = async (huntId) => {
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/hunt/${huntId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setSelectedAdminHunt(null);
        fetchAdminHunts();
        fetchActiveHunt();
        alert('Hunt deleted');
      }
    } catch (error) {
      alert('Error deleting hunt');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const manuallyPayUnclaimed = async (winnerId) => {
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/unclaimed/${winnerId}/pay`, {
        method: 'POST'
      });
      if (res.ok) {
        fetchAdminUnclaimedWins();
        fetchAdminStats();
        alert('Marked as paid');
      }
    } catch (error) {
      alert('Error marking as paid');
    }
  };

  const deletePredictionWin = async (winnerId, isPaid = false) => {
    const confirmMsg = isPaid 
      ? 'Are you sure you want to delete this PAID reward? This will remove the payment record but the user will still appear in prediction history.'
      : 'Are you sure you want to delete this pending reward? The user will still appear in prediction history but will not be able to claim.';
    
    if (!window.confirm(confirmMsg)) return;
    
    // Double confirm for paid rewards
    if (isPaid && !window.confirm('⚠️ FINAL WARNING: This will permanently delete the paid reward record. Continue?')) return;
    
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/prediction-win/${winnerId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        fetchAdminClaims();
        fetchAdminUnclaimedWins();
        fetchAdminPaidClaims(adminPaidRewardsSearch, adminPaidRewardsPage);
        fetchAdminStats();
        alert('Reward deleted successfully');
      } else {
        const data = await res.json();
        alert(data.error || 'Error deleting reward');
      }
    } catch (error) {
      alert('Error deleting reward');
    }
  };

  // Tournament Functions
  const PROVIDER_COLORS = {
    'Pragmatic': '#ff6b35',
    'Hacksaw': '#e91e63',
    'Nolimit City': '#9c27b0',
    'BGaming': '#4caf50',
    'Relax Gaming': '#00bcd4',
    'Play\'n GO': '#ff9800',
    'NetEnt': '#8bc34a',
    'Red Tiger': '#f44336',
    'Big Time Gaming': '#673ab7',
    'ELK Studios': '#3f51b5',
    'Thunderkick': '#ffeb3b',
    'Yggdrasil': '#795548',
    'Peter & Sons': '#2196f3',
    'Popiplay': '#00bcd4',
    'Print Studios': '#ff5722',
    'default': '#6b7280'
  };

  const getProviderColor = (provider) => PROVIDER_COLORS[provider] || PROVIDER_COLORS['default'];

  const shuffleTournamentArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Fetch tournaments from API
  const fetchTournaments = async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/tournaments`);
      if (res.ok) {
        const data = await res.json();
        if (data.activeTournament) {
          setActiveTournament(data.activeTournament);
          setTournamentBracket(data.activeTournament);
        } else {
          setActiveTournament(null);
          setTournamentBracket(null);
        }
        setTournamentHistory(data.tournamentHistory || []);
      }
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    }
  };

  const generateBracket = async () => {
    const size = tournamentSize;
    if (tournamentProviders.length < size) {
      alert(`Need exactly ${size} providers for this bracket size. You have ${tournamentProviders.length}.`);
      return;
    }
    
    // Take only the needed providers and shuffle them
    const selectedProviders = tournamentProviders.slice(0, size);
    const shuffled = shuffleTournamentArray(selectedProviders);
    
    // Helper to create a match object
    const createMatch = (id, matchNumber, p1 = null, p2 = null) => ({
      id,
      matchNumber,
      provider1: p1,
      provider2: p2,
      provider1Start: null,
      provider1End: null,
      provider2Start: null,
      provider2End: null,
      winner: null,
      loser: null,
      status: 'pending'
    });
    
    // Create bracket structure with winners and losers brackets (double elimination)
    const bracket = {
      providers: shuffled,
      size: size,
      winnersRounds: [],
      losersRounds: [],
      grandFinal: null,
      winner: null,
      createdAt: new Date().toISOString(),
      status: 'active'
    };
    
    // === WINNERS BRACKET ===
    // First round matchups
    const firstRound = [];
    for (let i = 0; i < shuffled.length; i += 2) {
      firstRound.push(createMatch(`w1-m${i/2}`, i/2 + 1, shuffled[i], shuffled[i + 1]));
    }
    bracket.winnersRounds.push(firstRound);
    
    // Create subsequent winners rounds
    let matchesInRound = firstRound.length / 2;
    let roundNum = 2;
    while (matchesInRound >= 1) {
      const round = [];
      for (let i = 0; i < matchesInRound; i++) {
        round.push(createMatch(`w${roundNum}-m${i}`, i + 1));
      }
      bracket.winnersRounds.push(round);
      matchesInRound = matchesInRound / 2;
      roundNum++;
    }
    
    // === LOSERS BRACKET ===
    if (size === 4) {
      // 4 teams: L1(1 match) -> L2(1 match) -> Grand Final
      bracket.losersRounds.push([createMatch('l1-m0', 1)]);
      bracket.losersRounds.push([createMatch('l2-m0', 1)]);
    } else if (size === 8) {
      // 8 teams double elimination
      bracket.losersRounds.push([createMatch('l1-m0', 1), createMatch('l1-m1', 2)]);
      bracket.losersRounds.push([createMatch('l2-m0', 1), createMatch('l2-m1', 2)]);
      bracket.losersRounds.push([createMatch('l3-m0', 1)]);
      bracket.losersRounds.push([createMatch('l4-m0', 1)]);
    } else if (size === 16) {
      // 16 teams double elimination
      bracket.losersRounds.push([createMatch('l1-m0', 1), createMatch('l1-m1', 2), createMatch('l1-m2', 3), createMatch('l1-m3', 4)]);
      bracket.losersRounds.push([createMatch('l2-m0', 1), createMatch('l2-m1', 2), createMatch('l2-m2', 3), createMatch('l2-m3', 4)]);
      bracket.losersRounds.push([createMatch('l3-m0', 1), createMatch('l3-m1', 2)]);
      bracket.losersRounds.push([createMatch('l4-m0', 1), createMatch('l4-m1', 2)]);
      bracket.losersRounds.push([createMatch('l5-m0', 1)]);
      bracket.losersRounds.push([createMatch('l6-m0', 1)]);
    }
    
    // Grand Final
    bracket.grandFinal = createMatch('gf-m0', 1);
    
    // Save to backend
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/tournaments`, {
        method: 'POST',
        body: JSON.stringify({
          bracket,
          providers: shuffled,
          size,
          startBalance: null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setTournamentBracket(data.tournament);
        setActiveTournament(data.tournament);
        setShowTournamentSetup(false);
      } else {
        alert('Failed to create tournament');
      }
    } catch (error) {
      console.error('Error creating tournament:', error);
      alert('Error creating tournament');
    }
  };

  // Update balance for a specific provider in a match
  // Helper to get the right rounds array based on bracket type (kept for potential future use)
  // const getBracketRounds = (bracket, bracketType) => {
  //   if (bracketType === 'winners') return bracket.winnersRounds;
  //   if (bracketType === 'losers') return bracket.losersRounds;
  //   if (bracketType === 'grand') return [[bracket.grandFinal]];
  //   return null;
  // };

  const updateMatchBalance = async (bracketType, roundIndex, matchIndex, field, value) => {
    if (!tournamentBracket) return;
    
    const newBracket = JSON.parse(JSON.stringify(tournamentBracket));
    let match;
    
    if (bracketType === 'winners') {
      match = newBracket.winnersRounds?.[roundIndex]?.[matchIndex];
    } else if (bracketType === 'losers') {
      match = newBracket.losersRounds?.[roundIndex]?.[matchIndex];
    } else if (bracketType === 'grand') {
      match = newBracket.grandFinal;
    }
    
    if (!match) return;
    match[field] = value ? parseFloat(value) : null;
    
    setTournamentBracket(newBracket);
    setActiveTournament(newBracket);
    
    fetchWithAuth(`${BACKEND_API_URL}/api/admin/tournaments/${tournamentBracket.id}`, {
      method: 'PUT',
      body: JSON.stringify({ bracket: newBracket })
    }).catch(err => console.error('Error saving balance:', err));
  };

  // Swap provider positions within a match
  const swapWithinMatch = async (bracketType, roundIndex, matchIndex) => {
    if (!tournamentBracket) return;
    
    const newBracket = JSON.parse(JSON.stringify(tournamentBracket));
    let match;
    
    if (bracketType === 'winners') {
      match = newBracket.winnersRounds?.[roundIndex]?.[matchIndex];
    } else if (bracketType === 'losers') {
      match = newBracket.losersRounds?.[roundIndex]?.[matchIndex];
    } else if (bracketType === 'grand') {
      match = newBracket.grandFinal;
    }
    
    if (!match || match.winner) return;
    
    [match.provider1, match.provider2] = [match.provider2, match.provider1];
    [match.provider1Start, match.provider2Start] = [match.provider2Start, match.provider1Start];
    [match.provider1End, match.provider2End] = [match.provider2End, match.provider1End];
    
    setTournamentBracket(newBracket);
    setActiveTournament(newBracket);
    
    fetchWithAuth(`${BACKEND_API_URL}/api/admin/tournaments/${tournamentBracket.id}`, {
      method: 'PUT',
      body: JSON.stringify({ bracket: newBracket })
    }).catch(err => console.error('Error swapping:', err));
  };

  // Replace a provider with a different one
  const replaceProvider = async (bracketType, roundIndex, matchIndex, position) => {
    if (!tournamentBracket) return;
    
    let match;
    if (bracketType === 'winners') {
      match = tournamentBracket.winnersRounds?.[roundIndex]?.[matchIndex];
    } else if (bracketType === 'losers') {
      match = tournamentBracket.losersRounds?.[roundIndex]?.[matchIndex];
    } else if (bracketType === 'grand') {
      match = tournamentBracket.grandFinal;
    }
    
    if (!match || match.winner) return;
    
    const currentProvider = position === 1 ? match.provider1 : match.provider2;
    const newProvider = window.prompt(`Replace "${currentProvider}" with:`, '');
    
    if (!newProvider || newProvider.trim() === '' || newProvider === currentProvider) return;
    
    const newBracket = JSON.parse(JSON.stringify(tournamentBracket));
    let m;
    if (bracketType === 'winners') {
      m = newBracket.winnersRounds[roundIndex][matchIndex];
    } else if (bracketType === 'losers') {
      m = newBracket.losersRounds[roundIndex][matchIndex];
    } else if (bracketType === 'grand') {
      m = newBracket.grandFinal;
    }
    
    if (position === 1) {
      m.provider1 = newProvider.trim();
      m.provider1Start = null;
      m.provider1End = null;
    } else {
      m.provider2 = newProvider.trim();
      m.provider2Start = null;
      m.provider2End = null;
    }
    
    setTournamentBracket(newBracket);
    setActiveTournament(newBracket);
    
    fetchWithAuth(`${BACKEND_API_URL}/api/admin/tournaments/${tournamentBracket.id}`, {
      method: 'PUT',
      body: JSON.stringify({ bracket: newBracket })
    }).catch(err => console.error('Error replacing provider:', err));
  };

  // Set winner for a match (double elimination)
  const setMatchWinner = async (bracketType, roundIndex, matchIndex, winnerProvider) => {
    if (!tournamentBracket) return;
    
    const newBracket = JSON.parse(JSON.stringify(tournamentBracket));
    let match;
    
    if (bracketType === 'winners') {
      match = newBracket.winnersRounds?.[roundIndex]?.[matchIndex];
    } else if (bracketType === 'losers') {
      match = newBracket.losersRounds?.[roundIndex]?.[matchIndex];
    } else if (bracketType === 'grand') {
      match = newBracket.grandFinal;
    }
    
    if (!match) return;
    
    const loserProvider = match.provider1 === winnerProvider ? match.provider2 : match.provider1;
    match.winner = winnerProvider;
    match.loser = loserProvider;
    match.status = 'completed';
    
    if (bracketType === 'winners') {
      // Advance winner in winners bracket
      if (roundIndex < newBracket.winnersRounds.length - 1) {
        const nextRound = newBracket.winnersRounds[roundIndex + 1];
        const nextMatchIndex = Math.floor(matchIndex / 2);
        const isFirstInMatch = matchIndex % 2 === 0;
        if (isFirstInMatch) {
          nextRound[nextMatchIndex].provider1 = winnerProvider;
        } else {
          nextRound[nextMatchIndex].provider2 = winnerProvider;
        }
      } else {
        // Winners final winner goes to grand final
        newBracket.grandFinal.provider1 = winnerProvider;
      }
      
      // Send loser to losers bracket - proper double elimination logic
      if (newBracket.losersRounds && newBracket.losersRounds.length > 0) {
        const size = newBracket.size || 8;
        
        // For 8-team double elimination:
        // - Winners R1 losers (4 players) → Losers R1 (play each other: 2 matches)
        // - Winners R2 losers (2 players) → Losers R2 (face L1 winners)
        // - Winners Final loser (1 player) → Losers R4 (faces L3 winner)
        
        // For 16-team double elimination:
        // - Winners R1 losers (8 players) → Losers R1 (play each other: 4 matches)
        // - Winners R2 losers (4 players) → Losers R2 (face L1 winners)
        // - Winners R3 losers (2 players) → Losers R4 (face L3 winners)
        // - Winners Final loser (1 player) → Losers R6 (faces L5 winner)
        
        let losersRoundIdx;
        if (size === 8) {
          // 8-team mapping: W-R1 → L-R1, W-R2 → L-R2, W-Final → L-R4
          if (roundIndex === 0) losersRoundIdx = 0; // W-R1 losers to L-R1
          else if (roundIndex === 1) losersRoundIdx = 1; // W-R2 losers to L-R2
          else losersRoundIdx = 3; // W-Final loser to L-R4
        } else if (size === 16) {
          // 16-team mapping: W-R1 → L-R1, W-R2 → L-R2, W-R3 → L-R4, W-Final → L-R6
          if (roundIndex === 0) losersRoundIdx = 0;
          else if (roundIndex === 1) losersRoundIdx = 1;
          else if (roundIndex === 2) losersRoundIdx = 3;
          else losersRoundIdx = 5;
        } else {
          // 4-team: W-R1 → L-R1, W-Final → L-R2
          if (roundIndex === 0) losersRoundIdx = 0;
          else losersRoundIdx = 1;
        }
        
        if (losersRoundIdx < newBracket.losersRounds.length) {
          // Find first empty slot in that losers round
          const losersRound = newBracket.losersRounds[losersRoundIdx];
          for (let i = 0; i < losersRound.length; i++) {
            if (!losersRound[i].provider1) {
              losersRound[i].provider1 = loserProvider;
              break;
            } else if (!losersRound[i].provider2) {
              losersRound[i].provider2 = loserProvider;
              break;
            }
          }
        }
      }
    } else if (bracketType === 'losers') {
      // Advance winner in losers bracket
      if (roundIndex < newBracket.losersRounds.length - 1) {
        const nextRound = newBracket.losersRounds[roundIndex + 1];
        
        // For losers bracket, fill sequentially
        let placed = false;
        for (let i = 0; i < nextRound.length && !placed; i++) {
          if (!nextRound[i].provider1) {
            nextRound[i].provider1 = winnerProvider;
            placed = true;
          } else if (!nextRound[i].provider2) {
            nextRound[i].provider2 = winnerProvider;
            placed = true;
          }
        }
      } else {
        // Losers final winner goes to grand final
        newBracket.grandFinal.provider2 = winnerProvider;
      }
    } else if (bracketType === 'grand') {
      // Grand final completed - tournament winner!
      newBracket.winner = winnerProvider;
      newBracket.status = 'completed';
    }
    
    setTournamentBracket(newBracket);
    setActiveTournament(newBracket);
    
    try {
      await fetchWithAuth(`${BACKEND_API_URL}/api/admin/tournaments/${tournamentBracket.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          bracket: newBracket,
          winner: newBracket.winner || null
        })
      });
    } catch (error) {
      console.error('Error updating tournament:', error);
    }
  };

  // Undo a match result
  const undoMatchWinner = async (bracketType, roundIndex, matchIndex) => {
    if (!tournamentBracket) return;
    
    const newBracket = JSON.parse(JSON.stringify(tournamentBracket));
    let match;
    
    if (bracketType === 'winners') {
      match = newBracket.winnersRounds?.[roundIndex]?.[matchIndex];
    } else if (bracketType === 'losers') {
      match = newBracket.losersRounds?.[roundIndex]?.[matchIndex];
    } else if (bracketType === 'grand') {
      match = newBracket.grandFinal;
    }
    
    if (!match || !match.winner) return;
    
    // Store for potential future use in more complex undo logic
    // const previousWinner = match.winner;
    // const previousLoser = match.loser;
    
    match.winner = null;
    match.loser = null;
    match.status = 'pending';
    
    // Note: Full undo logic for double elimination is complex
    // For now, just clear the match result - admin should manually fix bracket if needed
    if (bracketType === 'grand') {
      newBracket.winner = null;
      newBracket.status = 'active';
    }
    
    setTournamentBracket(newBracket);
    setActiveTournament(newBracket);
    
    try {
      await fetchWithAuth(`${BACKEND_API_URL}/api/admin/tournaments/${tournamentBracket.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          bracket: newBracket,
          winner: newBracket.winner || null
        })
      });
    } catch (error) {
      console.error('Error updating tournament:', error);
    }
  };

  const finishTournament = async () => {
    if (!tournamentBracket || !tournamentBracket.winner) {
      alert('Tournament not complete yet');
      return;
    }
    
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/tournaments/${tournamentBracket.id}/complete`, {
        method: 'POST',
        body: JSON.stringify({
          winner: tournamentBracket.winner
        })
      });
      
      if (res.ok) {
        // Refresh tournaments
        await fetchTournaments();
        setShowTournamentSetup(false);
      } else {
        alert('Failed to complete tournament');
      }
    } catch (error) {
      console.error('Error completing tournament:', error);
      alert('Error completing tournament');
    }
  };

  const resetTournament = async () => {
    if (!window.confirm('Reset current tournament? This cannot be undone.')) return;
    
    if (tournamentBracket?.id) {
      try {
        await fetchWithAuth(`${BACKEND_API_URL}/api/admin/tournaments/${tournamentBracket.id}`, {
          method: 'DELETE'
        });
      } catch (error) {
        console.error('Error deleting tournament:', error);
      }
    }
    
    setTournamentBracket(null);
    setActiveTournament(null);
  };

  // Fix losers bracket - move providers from wrong round to correct round
  const fixLosersBracket = async () => {
    if (!tournamentBracket || !window.confirm('This will reorganize the losers bracket to fix the structure. Continue?')) return;
    
    const newBracket = JSON.parse(JSON.stringify(tournamentBracket));
    const size = newBracket.size || 8;
    
    if (size === 8 && newBracket.losersRounds?.length >= 4) {
      // For 8-team: Check if L3 has providers that should be in L2
      // L1 should have W-R1 losers playing each other
      // L2 should have L1 winners vs W-R2 losers
      // L3 should have L2 winners playing each other
      // L4 should have L3 winner vs W-Final loser
      
      // Get providers currently in L3 (which might be W-R2 losers placed incorrectly)
      const l3 = newBracket.losersRounds[2];
      const l2 = newBracket.losersRounds[1];
      
      // Check if L3 has providers but L2 doesn't have all spots filled correctly
      // This indicates the old buggy placement
      const l3Providers = [];
      if (l3[0]?.provider1) l3Providers.push(l3[0].provider1);
      if (l3[0]?.provider2) l3Providers.push(l3[0].provider2);
      
      if (l3Providers.length > 0) {
        // Move L3 providers to L2 as provider2 slots (they should face L1 winners)
        let l2Idx = 0;
        for (const provider of l3Providers) {
          if (l2Idx < l2.length) {
            // Put in provider2 slot of L2 matches (to face L1 winners who go to provider1)
            if (!l2[l2Idx].provider2) {
              l2[l2Idx].provider2 = provider;
            } else if (!l2[l2Idx].provider1) {
              l2[l2Idx].provider1 = provider;
            }
            l2Idx++;
          }
        }
        
        // Clear L3
        l3[0].provider1 = null;
        l3[0].provider2 = null;
        l3[0].winner = null;
        l3[0].provider1Start = null;
        l3[0].provider1End = null;
        l3[0].provider2Start = null;
        l3[0].provider2End = null;
        
        // Save to backend
        try {
          const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/tournaments/${tournamentBracket.id}`, {
            method: 'PUT',
            body: JSON.stringify({ bracket: newBracket })
          });
          
          if (res.ok) {
            setTournamentBracket(newBracket);
            setActiveTournament(newBracket);
            alert('Losers bracket fixed! W-R2 losers moved to L2.');
          } else {
            alert('Failed to save fix');
          }
        } catch (error) {
          console.error('Error fixing bracket:', error);
          alert('Error fixing bracket');
        }
      } else {
        alert('Bracket appears to be correct or no fix needed.');
      }
    } else {
      alert('Fix only available for 8-team tournaments currently.');
    }
  };

  const addProvider = () => {
    const provider = tournamentProviderInput.trim();
    if (!provider) return;
    if (tournamentProviders.includes(provider)) {
      alert('Provider already added');
      return;
    }
    setTournamentProviders(prev => [...prev, provider]);
    setTournamentProviderInput('');
  };

  const removeProvider = (provider) => {
    setTournamentProviders(prev => prev.filter(p => p !== provider));
  };

  // Profile Functions
  const fetchProfileStats = async () => {
    if (!user) return;
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/user/profile-stats`);
      if (res.ok) {
        const data = await res.json();
        setProfileStats(data);
      }
    } catch (error) {
      console.error('Error fetching profile stats:', error);
    }
  };

  // Link / change / remove one site's Rainbet username ('com' | 'us') and mirror
  // it into `user`. Throws with the backend's message (cooldown, already linked…).
  const saveRainbetLink = async (site, username, { remove = false } = {}) => {
    const res = await fetchWithAuth(`${BACKEND_API_URL}/api/user/rainbet-username`, {
      method: 'PUT',
      body: JSON.stringify(remove ? { site, remove: true } : { site, rainbetUsername: username.trim() })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not save that Rainbet username');
    setUser(prev => ({ ...prev, [site === 'us' ? 'rainbetUsUsername' : 'rainbetUsername']: data.rainbetUsername || null }));
    return data;
  };

  // Profile page editor — `editingRainbet` holds the site being edited
  const updateRainbetUsername = async (site, { remove = false } = {}) => {
    if (!remove && !rainbetUsernameInput.trim()) return;
    const label = site === 'us' ? 'Rainbet.us' : 'Rainbet';
    if (remove && !confirm(`Remove your ${label} account from your profile?`)) return;
    try {
      await saveRainbetLink(site, rainbetUsernameInput, { remove });
      setEditingRainbet(false);
      fetchProfileStats(); // Refresh to get new leaderboard position
      alert(remove ? `${label} account removed` : `${label} username updated!`);
    } catch (error) {
      alert(error.message || 'Error updating username');
    }
  };

  // Community Hunt Functions
  const fetchActiveCommunityHunt = async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/community-hunts/active`);
      if (res.ok) {
        const data = await res.json();
        setActiveCommunityHunt(data.hunt);
        setCommunityHuntEntries(data.entries || []);
        setCommunityHuntAllEntries(data.allEntries || []);
      }
    } catch (error) {
      console.error('Error fetching community hunt:', error);
    }
  };

  const fetchMyHuntEntry = async () => {
    if (!user) return;
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/community-hunts/my-entry`);
      if (res.ok) {
        const data = await res.json();
        setMyEntryStatus(data.entry?.status || null);
        if (data.eligibility) {
          setMyEligibility(data.eligibility);
        }
      }
    } catch (error) {
      console.error('Error fetching entry status:', error);
    }
  };

  const fetchCommunityHuntHistory = async (page = 1, append = false) => {
    setHuntHistoryLoading(true);
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/community-hunts/history?page=${page}&limit=5`);
      if (res.ok) {
        const data = await res.json();
        if (append) {
          setPreviousCommunityHunts(prev => [...prev, ...(data.hunts || [])]);
        } else {
          setPreviousCommunityHunts(data.hunts || []);
        }
        setHuntHistoryPagination(data.pagination || { page: 1, totalPages: 1, total: 0, hasMore: false });
      }
    } catch (error) {
      console.error('Error fetching hunt history:', error);
    } finally {
      setHuntHistoryLoading(false);
    }
  };

  const enterCommunityHunt = async () => {
    if (!user) {
      alert('Please login to enter');
      return;
    }
    
    setCommunityHuntLoading(true);
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/community-hunts/enter`, {
        method: 'POST'
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setMyEntryStatus('entered');
        fetchActiveCommunityHunt(); // Refresh entry count
        alert('🎉 ' + data.message);
      } else {
        alert(data.error || 'Failed to enter');
      }
    } catch (error) {
      alert('Error entering raffle');
    }
    setCommunityHuntLoading(false);
  };

  // Fetch community hunt on page load and when page changes
  useEffect(() => {
    fetchActiveCommunityHunt();
    fetchCommunityHuntHistory();
  }, []);

  // Fetch eligibility whitelist for admin
  const fetchEligibilityWhitelist = async () => {
    if (!user?.isAdmin) return;
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/eligibility-whitelist`);
      if (res.ok) {
        const data = await res.json();
        setEligibilityWhitelist(data.whitelist || []);
      }
    } catch (e) {
      console.error('Error fetching whitelist:', e);
    }
  };

  useEffect(() => {
    if (activePage === 'communityhunts') {
      fetchActiveCommunityHunt();
      fetchCommunityHuntHistory();
      if (user) fetchMyHuntEntry();
      
      // Faster refresh when hunt is live (every 30 seconds for hunt data, bonus hunts refresh separately)
      const interval = setInterval(() => {
        fetchActiveCommunityHunt();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [activePage, user]);

  // Faster bonus hunts refresh when community hunt is live
  useEffect(() => {
    if (activeCommunityHunt?.status === 'live') {
      const fetchBonusHuntsLive = async () => {
        try {
          const response = await fetch(`${BACKEND_API_URL}/api/bonus-hunts`);
          if (response.ok) {
            const data = await response.json();
            setBonusHunts({
              activeHunt: data.activeHunt,
              recentHunts: data.recentHunts || [],
              total: data.total || 0,
              highestMulti: data.highestMulti || null,
              hotSlot: data.hotSlot || null,
              coldSlot: data.coldSlot || null
            });
          }
        } catch (e) {
          console.log('Bonus hunt fetch error:', e);
        }
      };
      
      // Fetch immediately and then every 10 seconds when live for real-time updates
      fetchBonusHuntsLive();
      const interval = setInterval(fetchBonusHuntsLive, 10000);
      return () => clearInterval(interval);
    }
  }, [activeCommunityHunt?.status]);

  // Check for new wins and show modal
  useEffect(() => {
    if (myWins.length > 0) {
      const unclaimedWin = myWins.find(w => !w.claimed);
      if (unclaimedWin && !showWinModal && !showClaimModal) {
        setWinModalData({
          rank: unclaimedWin.rank,
          predicted: parseFloat(unclaimedWin.predicted_balance),
          actual: parseFloat(unclaimedWin.actual_balance),
          difference: parseFloat(unclaimedWin.difference),
          reward: unclaimedWin.reward,
          winnerId: unclaimedWin.id,
          huntId: unclaimedWin.hunt_id
        });
        setShowWinModal(true);
      }
    }
  }, [myWins]);

  // Fetch profile stats when profile page is active
  useEffect(() => {
    if (activePage === 'profile' && user) {
      fetchProfileStats();
    }
  }, [activePage, user]);

  // Fetch admin data when admin tab changes
  useEffect(() => {
    if (user?.isAdmin && activePage === 'admin') {
      // Always fetch stats and data needed for badges
      fetchAdminStats();
      fetchAdminHunts();
      fetchAdminClaims(); // Needed for badge count
      fetchAdminGiveaways(); // Needed for badge count
      fetchAdminUnclaimedWins(); // Needed for badge count
      fetchActiveHunt(); // For prediction controls

      // Fetch tab-specific data
      if (adminTab === 'paidRewards') fetchAdminPaidClaims(adminPaidRewardsSearch, adminPaidRewardsPage);
      if (adminTab === 'paidGiveaways') fetchAdminPaidGiveaways(adminPaidGiveawaysSearch, adminPaidGiveawaysPage);
      if (adminTab === 'users') fetchAdminUsers(adminUserSearch, adminUsersPage);
      if (adminTab === 'wagerClaims') fetchWagerClaimsAdmin();
      if (adminTab === 'checklistClaims') fetchChecklistAdminClaims();
      if (adminTab === 'prizeClaims') fetchPrizeClaimsAdmin();
      if (adminTab === 'tierClaims' || adminTab === 'paidTierClaims') fetchAdminTierClaims();
      if (adminTab === 'periodHistory') fetchAdminPeriodHistory();

      // Set up interval to refresh active hunt status
      const huntInterval = setInterval(fetchActiveHunt, 30000);
      return () => clearInterval(huntInterval);
    }
  }, [user?.isAdmin, activePage, adminTab]);

  // Fetch public slot picks queue - auto refresh every 5 seconds for live updates
  useEffect(() => {
    if (activePage === 'bonushunts' || activePage === 'predictions' || activePage === 'slotpicks') {
      fetchSlotPicksQueue(slotPicksQueuePage);
      const interval = setInterval(() => fetchSlotPicksQueue(slotPicksQueuePage), 5000);
      return () => clearInterval(interval);
    }
  }, [activePage, slotPicksQueuePage]);

  const SLOT_ITEM_WIDTH = 180;

  const getRainbetUrl = useCallback((slot) => {
    if (!slot) return '#';
    // Catalog entries carry Rainbet's real slug — exact game URL, no guessing
    if (slot.slug) return `https://rainbet.com/casino/slots/${slot.slug}`;
    const providerSlugs = {
      'Pragmatic Play': 'pragmatic-play',
      'Hacksaw': 'hacksaw',
      'Nolimit City': 'nolimit',
      'BGaming': 'bgaming',
      'Play n Go': 'playn-go',
      'Peter & Sons': 'peter-sons',
      'Print Studios': 'print-studios',
      'Relax Gaming': 'relax',
      'Popiplay': 'popiplay',
      'Backseat Gaming': 'backseat',
      'Bullshark Games': 'bullshark',
      'Shady Lady': 'shady-lady'
    };
    const providerSlug = providerSlugs[slot.provider] || slot.provider.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const gameSlug = slot.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    return `https://rainbet.com/casino/slots/${providerSlug}-${gameSlug}`;
  }, []);

  const playTickSound = useCallback(() => {
    if (soundsMutedRef.current) return;
    try {
      if (!audioContextRef.current) audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.frequency.value = 800 + Math.random() * 200;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.03, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.03);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.03);
    } catch (e) {}
  }, []);

  // Phase 3: ascending C-E-G arpeggio (~600ms total) — "winner" fanfare
  const playWinSound = useCallback(() => {
    if (soundsMutedRef.current) return;
    try {
      if (!audioContextRef.current) audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      // Three ascending notes — C5, E5, G5 — major triad ("victory")
      const notes = [523.25, 659.25, 783.99];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'triangle';
        const startAt = ctx.currentTime + i * 0.13;
        gain.gain.setValueAtTime(0.0001, startAt);
        gain.gain.exponentialRampToValueAtTime(0.18, startAt + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.42);
        osc.start(startAt);
        osc.stop(startAt + 0.45);
      });
      // Add a sparkly higher overtone on the last note
      const sparkle = ctx.createOscillator();
      const sparkleGain = ctx.createGain();
      sparkle.connect(sparkleGain);
      sparkleGain.connect(ctx.destination);
      sparkle.frequency.value = 1567.98; // G6
      sparkle.type = 'sine';
      const sparkleStart = ctx.currentTime + 0.26;
      sparkleGain.gain.setValueAtTime(0.0001, sparkleStart);
      sparkleGain.gain.exponentialRampToValueAtTime(0.08, sparkleStart + 0.02);
      sparkleGain.gain.exponentialRampToValueAtTime(0.0001, sparkleStart + 0.6);
      sparkle.start(sparkleStart);
      sparkle.stop(sparkleStart + 0.65);
    } catch (e) {}
  }, []);

  const startSpinSound = useCallback((duration = 4000) => {
    const startTime = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startTime;
      if (elapsed < duration) {
        playTickSound();
        const progress = elapsed / duration;
        const speed = 30 + (progress * progress * progress * 400);
        tickIntervalRef.current = setTimeout(tick, speed);
      }
    };
    tick();
  }, [playTickSound]);

  const stopSpinSound = useCallback(() => {
    if (tickIntervalRef.current) { clearTimeout(tickIntervalRef.current); tickIntervalRef.current = null; }
  }, []);

  const spinWheel = () => {
    if (wheelSpinning) return;
    setWheelResult(null);
    setWheelSpinning(true);
    startSpinSound(5000);
    
    const winningIndex = Math.floor(Math.random() * casinoGames.length);
    wheelResultRef.current = casinoGames[winningIndex].name;
    
    const segmentAngle = 360 / casinoGames.length;
    // Segment centers: segment 0 is at segmentAngle/2, segment 1 at segmentAngle*1.5, etc.
    // The SVG draws segments starting from -90 degrees (top), going clockwise
    // Pointer is at top. When rotation=0, segment 0's center is at top-ish
    // To land on segment i: we need rotation such that segment i's center is at top
    // Segment i center angle (from top, clockwise) = i * segmentAngle + segmentAngle/2
    // To bring it to top (pointer), we rotate: -(i * segmentAngle + segmentAngle/2) or equivalently 360 - that
    
    const segmentCenterFromTop = winningIndex * segmentAngle + (segmentAngle / 2);
    const spins = 5 + Math.floor(Math.random() * 3);
    
    // Current rotation mod 360 tells us where segment 0's center currently is
    const currentMod = ((wheelRotation % 360) + 360) % 360;
    
    // We want final rotation mod 360 to equal (360 - segmentCenterFromTop)
    const targetMod = (360 - segmentCenterFromTop + 360) % 360;
    
    // Calculate how much more we need to rotate from current position
    let additionalRotation = targetMod - currentMod;
    if (additionalRotation <= 0) additionalRotation += 360; // Ensure we go forward
    
    const totalRotation = wheelRotation + (spins * 360) + additionalRotation;
    
    setWheelRotation(totalRotation);
    
    setTimeout(() => {
      stopSpinSound();
      setWheelResult(wheelResultRef.current);
      setWheelSpinning(false);
    }, 5000);
  };

  const spinSlotPicker = () => {
    if (slotSpinning || slotIsResetting || filteredSlots.length === 0) return;
    setSlotResult(null);
    setSlotResultPopup(false);
    
    const winningSlot = filteredSlots[Math.floor(Math.random() * filteredSlots.length)];
    let displayReel = [...reelSlots];
    let winningIndex = displayReel.findIndex(s => s.name === winningSlot.name);
    
    if (winningIndex === -1) {
      winningIndex = Math.floor(Math.random() * displayReel.length);
      displayReel[winningIndex] = winningSlot;
      setReelSlots(displayReel);
    }
    
    const totalItems = displayReel.length;
    const spins = 5;
    const startPosition = 10 * totalItems * SLOT_ITEM_WIDTH;
    const endPosition = startPosition + (spins * totalItems * SLOT_ITEM_WIDTH) + (winningIndex * SLOT_ITEM_WIDTH);
    
    setSlotIsResetting(true);
    setSlotRotation(-startPosition);
    
    setTimeout(() => {
      setSlotIsResetting(false);
      setSlotSpinning(true);
      startSpinSound(4000);
      setSlotRotation(-endPosition);
      
      setTimeout(() => {
        stopSpinSound();
        setSlotResult(winningSlot);
        setSlotSpinning(false);
        setTimeout(() => setSlotResultPopup(true), 300);
      }, 4000);
    }, 50);
  };

  const shuffleSlots = () => {
    if (slotSpinning) return;
    setShuffleSeed(Date.now());
  };

  const toggleProvider = (provider) => {
    if (selectedProviders.includes(provider)) {
      if (selectedProviders.length > 1) setSelectedProviders(selectedProviders.filter(p => p !== provider));
    } else {
      setSelectedProviders([...selectedProviders, provider]);
    }
  };

  const navigateTo = useCallback((page) => {
    setActivePage(page);
    setSelectedGame(null);
    setMobileMenuOpen(false);
    setUserDropdownOpen(false);
    // Update URL without reload
    const pathMap = {
      'home': '/',
      'leaderboard': '/leaderboard',
      'rewards': '/rewards',
      'instructions': '/instructions',
      'challenges': '/challenges',
      'pointsshop': '/points',
      'rafflebot': '/raffle-bot',
      'bingo': '/bingo',
      'socials': '/socials',
      'prizes': '/claim',
      'wagerraffle': '/raffle',
      'battles': '/battles',
      'bonushunts': '/bonus-hunts',
      'communityhunts': '/community-hunts',
      'tournaments': '/tournaments',
      'predictions': '/predictions',
      'slotpicks': '/slot-picks',
      'slotrequests': '/slot-requests',
      'mypredictions': '/my-predictions',
      'profile': '/profile',
      'admin': '/admin',
      'games': '/games',
      'content': '/content',
      'terms': '/terms',
      'privacy': '/privacy'
    };
    const newPath = pathMap[page] || '/';
    window.history.pushState({}, '', newPath);
    window.scrollTo(0, 0);
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const result = getPageFromPath();
      if (typeof result === 'object') {
        setActivePage(result.page);
        setSelectedGame(result.game);
      } else {
        setActivePage(result);
        setSelectedGame(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const calc = () => {
      const now = new Date();
      const diff = leaderboardEndTs - now.getTime();
      if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60)
      };
    };
    setCountdown(calc());
    const timer = setInterval(() => setCountdown(calc()), 1000);
    return () => clearInterval(timer);
  }, [leaderboardEndTs]);

  // Check live status - requires TWITCH_CLIENT_ID in backend
  useEffect(() => {
    const checkLiveStatus = async () => {
      // For now, using a simple check via backend
      // You'll need to set up a backend endpoint that checks Twitch API
      try {
        const response = await fetch(`${BACKEND_API_URL}/api/live-status`);
        if (response.ok) {
          const data = await response.json();
          setLiveStatus(data);
        }
      } catch (e) {
        // Fallback: show Cam's stream link when API not available
        setLiveStatus({ isLive: false, streamer: null, url: 'https://www.twitch.tv/camneverwins' });
      }
    };
    checkLiveStatus();
    const interval = setInterval(checkLiveStatus, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Fetch bonus hunts
  useEffect(() => {
    const fetchBonusHunts = async () => {
      try {
        const response = await fetch(`${BACKEND_API_URL}/api/bonus-hunts`);
        if (response.ok) {
          const data = await response.json();
          setBonusHunts({
            activeHunt: data.activeHunt,
            recentHunts: data.recentHunts || [],
            total: data.total || 0,
            highestMulti: data.highestMulti || null,
            hotSlot: data.hotSlot || null,
            coldSlot: data.coldSlot || null
          });
        }
      } catch (e) {
        console.log('Bonus hunt fetch error:', e);
      }
    };
    fetchBonusHunts();
    const interval = setInterval(fetchBonusHunts, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);

  // Fetch YouTube shorts
  useEffect(() => {
    const fetchYouTubeShorts = async () => {
      try {
        const response = await fetch(`${BACKEND_API_URL}/api/youtube/shorts`);
        if (response.ok) {
          const data = await response.json();
          setYoutubeShorts(data.shorts || []);
        }
      } catch (e) {
        console.log('YouTube shorts fetch error:', e);
      }
    };
    fetchYouTubeShorts();
    const interval = setInterval(fetchYouTubeShorts, 1800000); // Refresh every 30 minutes
    return () => clearInterval(interval);
  }, []);

  const pickWinner = async (videoId, platform = 'youtube') => {
    setWinnerPicking(videoId);
    setWinner(null);
    setWinnerVideoId(null);
    setWinnerPlatform(null);
    
    try {
      let url;
      if (platform === 'youtube') {
        url = `${BACKEND_API_URL}/api/youtube/comments/${videoId}?pick=true`;
      } else if (platform === 'instagram') {
        url = `${BACKEND_API_URL}/api/instagram/comments/${videoId}?pick=true`;
      } else if (platform === 'tiktok') {
        url = `${BACKEND_API_URL}/api/tiktok/comments/${videoId}?pick=true`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        if (data.winner) {
          setWinner(data);
          setWinnerVideoId(videoId);
          setWinnerPlatform(platform);
        }
      }
    } catch (e) {
      console.log('Winner pick error:', e);
    } finally {
      setWinnerPicking(null);
    }
  };

  const getYouTubeCommentLink = (videoId, commentId) => {
    return `https://www.youtube.com/watch?v=${videoId}&lc=${commentId}`;
  };

  // Instagram link helper - currently unused but kept for future use
  // const getInstagramLink = (shortCode) => {
  //   return `https://www.instagram.com/reel/${shortCode}/`;
  // };

  const getTikTokLink = (videoId) => {
    return `https://www.tiktok.com/@camneverwins/video/${videoId}`;
  };

  // Fetch Instagram reels
  useEffect(() => {
    const fetchInstagramReels = async () => {
      try {
        const response = await fetch(`${BACKEND_API_URL}/api/instagram/reels`);
        if (response.ok) {
          const data = await response.json();
          setInstagramReels(data.reels || []);
        }
      } catch (e) {
        console.log('Instagram reels fetch error:', e);
      }
    };
    fetchInstagramReels();
    const interval = setInterval(fetchInstagramReels, 3600000); // Refresh every hour
    return () => clearInterval(interval);
  }, []);

  // Fetch TikTok videos
  useEffect(() => {
    const fetchTikTokVideos = async () => {
      try {
        const response = await fetch(`${BACKEND_API_URL}/api/tiktok/videos`);
        if (response.ok) {
          const data = await response.json();
          setTiktokVideos(data.videos || []);
        }
      } catch (e) {
        console.log('TikTok videos fetch error:', e);
      }
    };
    fetchTikTokVideos();
    const interval = setInterval(fetchTikTokVideos, 3600000); // Refresh every hour
    return () => clearInterval(interval);
  }, []);

  // Fetch total paid out (public stat)
  useEffect(() => {
    const fetchTotalPaidOut = async () => {
      try {
        const response = await fetch(`${BACKEND_API_URL}/api/stats/total-paid`);
        if (response.ok) {
          const data = await response.json();
          setTotalPaidOut(data.totalPaidOut || 0);
        }
      } catch (e) {
        console.log('Total paid out fetch error:', e);
      }
    };
    fetchTotalPaidOut();
    const interval = setInterval(fetchTotalPaidOut, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/leaderboard`);
      if (!response.ok) {
        // Backend returns 503 when cache isn't populated yet — retry in 5s
        if (response.status === 503) {
          setTimeout(() => fetchLeaderboard(), 5000);
        }
        return;
      }
      const data = await response.json();
      if (data?.affiliates) {
        const sorted = data.affiliates
          .filter(p => parseFloat(p.wagered || p.wagered_amount || 0) > 0)
          .sort((a, b) => parseFloat(b.wagered || b.wagered_amount) - parseFloat(a.wagered || a.wagered_amount))
          .slice(0, 20)
          .map((p, i) => ({ rank: i + 1, username: p.name || p.username, wagers: parseFloat(p.wagered || p.wagered_amount), site: p.site }));
        setLeaderboard(sorted);
        setLbUpdatedAt(Date.now());
      }
    } catch (e) {
      // Network error — retry in 5s
      setTimeout(() => fetchLeaderboard(), 5000);
    } finally { setLoading(false); }
  }, []);

  const fetchWeeklyLeaderboard = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/weekly-leaderboard`);
      if (!response.ok) {
        if (response.status === 503) setTimeout(() => fetchWeeklyLeaderboard(), 5000);
        setWeeklyLeaderboard([]);
        return;
      }
      const data = await response.json();
      if (data?.affiliates) {
        const sorted = data.affiliates
          .filter(p => parseFloat(p.wagered || p.wagered_amount || 0) > 0)
          .sort((a, b) => parseFloat(b.wagered || b.wagered_amount) - parseFloat(a.wagered || a.wagered_amount))
          .slice(0, 20)
          .map((p, i) => ({ rank: i + 1, username: p.name || p.username, wagers: parseFloat(p.wagered || p.wagered_amount), site: p.site }));
        setWeeklyLeaderboard(sorted);
        setLbUpdatedAt(Date.now());
      }
    } catch (e) {
      setTimeout(() => fetchWeeklyLeaderboard(), 5000);
    }
  }, []);

  // Fetch the logged-in user's real rank from the backend — board usernames arrive
  // censored, so the user's position can't be derived client-side
  useEffect(() => {
    if (!hasRainbetLink(user)) { setMyRank(null); return; }
    let cancelled = false;
    const fetchMyRank = async () => {
      try {
        const res = await fetchWithAuth(`${BACKEND_API_URL}/api/my-rank`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setMyRank(data);
        }
      } catch (_) {}
    };
    fetchMyRank();
    const interval = setInterval(fetchMyRank, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user?.rainbetUsername, user?.rainbetUsUsername]);

  useEffect(() => { fetchLeaderboard(); fetchWeeklyLeaderboard(); }, [fetchLeaderboard, fetchWeeklyLeaderboard]);

  // Phase A: fetch reward tiers (public) and current user progress (if logged in)
  const fetchRewardTiers = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/reward-tiers?mode=all`);
      if (res.ok) {
        const data = await res.json();
        setRewardTiersAll(data.tiers || []);
      }
    } catch (e) { console.error('fetchRewardTiers:', e); }
  }, [BACKEND_API_URL]);

  const fetchSiteSettings = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/settings`);
      if (res.ok) {
        const data = await res.json();
        const s = data.settings || { reward_mode: 'lifetime' };
        setSiteSettings(s);
        if (s.default_reward_first || s.default_reward_second || s.default_reward_third) {
          setAdminRewards(r => ({
            first:  s.default_reward_first  || r.first,
            second: s.default_reward_second || r.second,
            third:  s.default_reward_third  || r.third,
          }));
        }
      }
    } catch (e) { console.error('fetchSiteSettings:', e); }
  }, [BACKEND_API_URL]);

  // Animate the "Total Given Away" counter from 0 to its target value once on first load.
  // Uses ease-out cubic for a satisfying deceleration. Only runs once per session.
  useEffect(() => {
    const target = Number(siteSettings.total_given_away || 0);
    if (giveawayAnimatedOnceRef.current) {
      // Already animated — just sync any future updates immediately
      setAnimatedGiveaway(target);
      return;
    }
    if (target <= 0) return;
    giveawayAnimatedOnceRef.current = true;
    const duration = 2200; // ms
    const start = performance.now();
    let raf = 0;
    const tick = (now) => {
      const elapsed = Math.min(1, (now - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - elapsed, 3);
      setAnimatedGiveaway(Math.floor(target * eased));
      if (elapsed < 1) raf = requestAnimationFrame(tick);
      else setAnimatedGiveaway(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [siteSettings.total_given_away]);

  const fetchDbPeriods = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/leaderboard-periods`);
      if (res.ok) {
        const data = await res.json();
        setDbPeriods(data.periods || []);
      }
    } catch (e) { console.error('fetchDbPeriods:', e); }
  }, [BACKEND_API_URL]);

  const fetchDbLinks = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/site-links`);
      if (res.ok) {
        const data = await res.json();
        setDbLinks(data.links || []);
      }
    } catch (e) { console.error('fetchDbLinks:', e); }
  }, [BACKEND_API_URL]);

  const fetchAdminPeriods = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const res = await fetch(`${BACKEND_API_URL}/api/admin/leaderboard-periods`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAdminPeriods(d.periods || []); }
    } catch (e) { console.error(e); }
  }, [BACKEND_API_URL]);

  const fetchAdminLinks = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const res = await fetch(`${BACKEND_API_URL}/api/admin/site-links`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAdminLinks(d.links || []); }
    } catch (e) { console.error(e); }
  }, [BACKEND_API_URL]);

  const fetchWagerStats = useCallback(async (fresh = false) => {
    setWagerStatsLoading(true);
    try {
      const token = getAuthToken();
      if (!token) return;
      const url = `${BACKEND_API_URL}/api/admin/wager-stats${fresh ? '?fresh=1' : ''}`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setWagerStats(d);
        setWagerStatsError(null);
      } else {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        setWagerStatsError(err.error || `Request failed (${res.status})`);
      }
    } catch (e) {
      console.error('fetchWagerStats:', e);
      setWagerStatsError(e.message || 'Network error');
    }
    finally { setWagerStatsLoading(false); }
  }, [BACKEND_API_URL]);

  const fetchChallenges = useCallback(async () => {
    setChallengesLoading(true);
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/challenges`);
      if (res.ok) { const d = await res.json(); setChallenges({ active: d.active || [], completed: d.completed || [] }); }
    } catch (e) { console.error('fetchChallenges:', e); }
    finally { setChallengesLoading(false); }
  }, [BACKEND_API_URL]);

  const fetchAdminChallenges = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const res = await fetch(`${BACKEND_API_URL}/api/admin/challenges`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAdminChallenges(d.challenges || []); }
    } catch (e) { console.error('fetchAdminChallenges:', e); }
  }, [BACKEND_API_URL]);

  const fetchAdminChallengeClaims = useCallback(async (status = 'pending') => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const url = status === 'all'
        ? `${BACKEND_API_URL}/api/admin/challenge-claims`
        : `${BACKEND_API_URL}/api/admin/challenge-claims?status=${encodeURIComponent(status)}`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAdminChallengeClaims(d.claims || []); }
    } catch (e) { console.error('fetchAdminChallengeClaims:', e); }
  }, [BACKEND_API_URL]);

  // Phase D: raffle bot helpers
  const botFetch = useCallback(async (path, opts = {}) => {
    const token = getAuthToken();
    return fetch(`${BOT_API_URL}${path}`, {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(opts.headers || {})
      }
    });
  }, [BOT_API_URL]);

  const fetchBotHealth = useCallback(async () => {
    try {
      const res = await fetch(`${BOT_API_URL}/health`);
      if (res.ok) { const d = await res.json(); setBotHealth(d); setBotError(null); }
      else setBotError(`Bot health returned ${res.status}`);
    } catch (e) {
      setBotError(`Bot unreachable: ${e.message}`);
      setBotHealth(null);
    }
  }, [BOT_API_URL]);

  const fetchBotRaffles = useCallback(async () => {
    try {
      const res = await botFetch('/raffles');
      if (res.ok) { const d = await res.json(); setBotRaffles(d.raffles || []); }
    } catch (e) { console.error('fetchBotRaffles:', e); }
  }, [botFetch]);

  // Toggle the secondary Kick (rainbet) chat feed for raffle entries
  const toggleRainbetChat = useCallback(async (enabled) => {
    try {
      const res = await botFetch('/rainbet-chat', { method: 'POST', body: JSON.stringify({ enabled }) });
      if (res.ok) { setBotError(null); await fetchBotHealth(); }
      else { const d = await res.json().catch(() => ({})); setBotError(d.error || 'Failed to toggle Rainbet chat'); }
    } catch (e) { setBotError(`Rainbet toggle failed: ${e.message}`); }
  }, [botFetch, fetchBotHealth]);

  // Phase 4: pull recent confirmed/invalidated/pending winners across all raffles
  const fetchWinnerHistory = useCallback(async () => {
    try {
      const res = await botFetch('/winners/history?limit=50');
      if (res.ok) { const d = await res.json(); setWinnerHistory(d.winners || []); }
    } catch (e) { console.error('fetchWinnerHistory:', e); }
  }, [botFetch]);

  const fetchBotActive = useCallback(async () => {
    try {
      const res = await botFetch('/raffles/active');
      if (res.ok) { const d = await res.json(); setBotActiveRaffle(d.active ? d : null); }
    } catch (e) { console.error('fetchBotActive:', e); }
  }, [botFetch]);

  const fetchBotEntries = useCallback(async (raffleId) => {
    if (!raffleId) { setBotEntries([]); return; }
    try {
      const res = await botFetch(`/raffles/${raffleId}/entries`);
      if (res.ok) { const d = await res.json(); setBotEntries(d.entries || []); }
    } catch (e) { console.error('fetchBotEntries:', e); }
  }, [botFetch]);

  // ---- Bingo (3x3 game) helpers ----
  // The /bingo/active endpoint is public (no auth) so the read works for any visitor.
  const fetchBingoState = useCallback(async () => {
    try {
      const res = await fetch(`${BOT_API_URL}/bingo/active`);
      if (res.ok) { const d = await res.json(); setBingoState(d); }
    } catch (e) { /* keep last state on blips */ }
  }, [BOT_API_URL]);

  // Admin: the session's entries (with board square + card art)
  const fetchBingoEntries = useCallback(async () => {
    try {
      const res = await botFetch('/bingo/entries');
      if (res.ok) { const d = await res.json(); setBingoEntries(Array.isArray(d.entries) ? d.entries : []); }
    } catch (e) { /* keep last list on blips */ }
  }, [botFetch]);

  // Run an admin action then refresh state. Returns true on success.
  const bingoAction = useCallback(async (path, opts = {}) => {
    setBingoBusy(true);
    setBingoError(null);
    try {
      const res = await botFetch(path, { method: 'POST', ...opts });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setBingoError(d.error || `Request failed (${res.status})`); return false; }
      if (d.session) setBingoState(s => ({ ...(s || {}), ...d.session, status: d.session.status, sessionId: d.session.id ?? s?.sessionId }));
      await Promise.all([fetchBingoState(), bingoShowEntries ? fetchBingoEntries() : null]);
      return true;
    } catch (e) {
      setBingoError(e.message || 'Network error');
      return false;
    } finally {
      setBingoBusy(false);
    }
  }, [botFetch, fetchBingoState, fetchBingoEntries, bingoShowEntries]);

  // ---- PVP Battles helpers ----
  // Public state is unauthenticated; admin detail adds unmasked fighters + pool depth.
  const fetchBattleState = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/battles/state`);
      if (res.ok) setBattleState(await res.json());
    } catch (e) { /* keep last state on blips */ }
  }, [BACKEND_API_URL]);

  const fetchBattleAdmin = useCallback(async () => {
    if (!user?.isAdmin) return;
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/admin/battles/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setBattleAdmin(await res.json());
    } catch (e) { /* ignore */ }
  }, [user?.isAdmin, BACKEND_API_URL]);

  const fetchBattleHistory = useCallback(async (page = 1) => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/battles/history?page=${page}&limit=6`);
      if (res.ok) setBattleHistory(await res.json());
    } catch (e) { /* ignore */ }
  }, [BACKEND_API_URL]);

  // Run an admin battle action then refresh both views. Returns true on success.
  const battleAction = useCallback(async (path, body, method = 'POST') => {
    setBattleBusy(true);
    setBattleError(null);
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        ...(body ? { body: JSON.stringify(body) } : {})
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setBattleError(d.error || `Request failed (${res.status})`); return false; }
      await Promise.all([fetchBattleState(), fetchBattleAdmin()]);
      return true;
    } catch (e) {
      setBattleError(e.message || 'Network error');
      return false;
    } finally {
      setBattleBusy(false);
    }
  }, [BACKEND_API_URL, fetchBattleState, fetchBattleAdmin]);

  // Bingo roulette sound — synthesized via Web Audio (no asset needed). Must be
  // kicked off from a user gesture (the Pick Random click) to satisfy autoplay
  // policies, hence ensureBingoAudio() is also called in the button handler.
  const ensureBingoAudio = useCallback(() => {
    try {
      if (!bingoAudioRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        bingoAudioRef.current = new Ctx();
      }
      if (bingoAudioRef.current.state === 'suspended') bingoAudioRef.current.resume();
      return bingoAudioRef.current;
    } catch { return null; }
  }, []);

  const playBingoTick = useCallback((progress) => {
    const ac = ensureBingoAudio();
    if (!ac) return;
    try {
      const t = ac.currentTime;
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = 'square';
      o.frequency.value = 320 + (progress || 0) * 280; // pitch rises as it slows
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.06, t + 0.004);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.055);
      o.connect(g); g.connect(ac.destination);
      o.start(t); o.stop(t + 0.06);
    } catch {}
  }, [ensureBingoAudio]);

  const playBingoDing = useCallback(() => {
    const ac = ensureBingoAudio();
    if (!ac) return;
    try {
      const now = ac.currentTime;
      [784, 1175].forEach((f, idx) => { // G5 + D6 — a quick celebratory chime
        const o = ac.createOscillator();
        const g = ac.createGain();
        o.type = 'sine'; o.frequency.value = f;
        const t = now + idx * 0.05;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.16, t + 0.012);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.6);
        o.connect(g); g.connect(ac.destination);
        o.start(t); o.stop(t + 0.65);
      });
    } catch {}
  }, [ensureBingoAudio]);

  // ---- Points system fetches ----
  const fetchPointsBalance = useCallback(async () => {
    if (!user) { setPointsBalance(null); return; }
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/points/balance`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setPointsBalance(d.balance); }
    } catch (e) { console.error('fetchPointsBalance:', e); }
  }, [BACKEND_API_URL, user]);

  const fetchPointsShop = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/points/shop`);
      if (res.ok) { const d = await res.json(); setPointsShop(d.items || []); }
    } catch (e) { console.error('fetchPointsShop:', e); }
  }, [BACKEND_API_URL]);

  const fetchPointsHistory = useCallback(async (page = 1) => {
    if (!user) return;
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/points/history?page=${page}&limit=20`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setPointsHistory({ items: d.history || [], total: d.total || 0, page }); }
    } catch (e) { console.error('fetchPointsHistory:', e); }
  }, [BACKEND_API_URL, user]);

  const fetchPointsLeaderboard = useCallback(async (page = 1) => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/points/leaderboard?page=${page}&limit=20`);
      if (res.ok) { const d = await res.json(); setPointsLeaderboard({ items: d.leaderboard || [], total: d.total || 0, page }); }
    } catch (e) { console.error('fetchPointsLeaderboard:', e); }
  }, [BACKEND_API_URL]);

  const fetchAdminUserHistory = useCallback(async (userId, page = 1) => {
    if (!userId) return;
    setAdminUserHistory(prev => ({ ...prev, loading: true }));
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/admin/points/user/${userId}/history?page=${page}&limit=25`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const d = await res.json();
        setAdminUserHistory({ items: d.history || [], total: d.total || 0, page, loading: false });
      } else { setAdminUserHistory(prev => ({ ...prev, loading: false })); }
    } catch (e) { console.error('fetchAdminUserHistory:', e); setAdminUserHistory(prev => ({ ...prev, loading: false })); }
  }, [BACKEND_API_URL]);

  const fetchAdminUserRedemptions = useCallback(async (userId) => {
    if (!userId) return;
    setAdminUserRedemptions({ items: [], loading: true });
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/admin/points/user/${userId}/redemptions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const d = await res.json();
        setAdminUserRedemptions({ items: d.redemptions || [], loading: false });
      } else { setAdminUserRedemptions({ items: [], loading: false }); }
    } catch (e) { console.error('fetchAdminUserRedemptions:', e); setAdminUserRedemptions({ items: [], loading: false }); }
  }, [BACKEND_API_URL]);

  const fetchPointsConfig = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/points/config`);
      if (res.ok) { const d = await res.json(); setPointsConfig(d.config || {}); }
    } catch (e) { console.error('fetchPointsConfig:', e); }
  }, [BACKEND_API_URL]);

  const fetchPointsRecent = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/points/recent-redemptions`);
      if (res.ok) { const d = await res.json(); setPointsRecentRedemptions(d.redemptions || []); }
    } catch (e) { console.error('fetchPointsRecent:', e); }
  }, [BACKEND_API_URL]);

  const fetchMyRedemptions = useCallback(async () => {
    if (!user) return;
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/points/my-redemptions`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setMyRedemptions(d.redemptions || []); }
    } catch (e) { console.error('fetchMyRedemptions:', e); }
  }, [BACKEND_API_URL, user]);

  // ---- Admin points fetches ----
  const fetchAdminPointsConfig = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/admin/points/config`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAdminPointsConfig(d.config || {}); setAdminPointsConfigDirty({}); }
    } catch (e) { console.error('fetchAdminPointsConfig:', e); }
  }, [BACKEND_API_URL]);

  const fetchAdminPointsStats = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/admin/points/stats`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAdminPointsStats(d); }
    } catch (e) { console.error('fetchAdminPointsStats:', e); }
  }, [BACKEND_API_URL]);

  const fetchAdminShopItems = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/admin/points/shop`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAdminShopItems(d.items || []); }
    } catch (e) { console.error('fetchAdminShopItems:', e); }
  }, [BACKEND_API_URL]);

  const fetchAdminPointsRedemptions = useCallback(async (status = 'pending') => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/admin/points/redemptions?status=${encodeURIComponent(status)}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAdminPointsRedemptions(d.redemptions || []); }
    } catch (e) { console.error('fetchAdminPointsRedemptions:', e); }
  }, [BACKEND_API_URL]);

  const fetchWagerPollerStatus = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/admin/points/wager-poller-status`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setWagerPollerStatus(d); }
    } catch (e) { console.error('fetchWagerPollerStatus:', e); }
  }, [BACKEND_API_URL]);

  const fetchRewardProgress = useCallback(async () => {
    if (!user) { setRewardProgress(null); return; }
    setRewardsLoading(true);
    try {
      const token = getAuthToken();
      if (!token) { setRewardProgress(null); return; }
      const res = await fetch(`${BACKEND_API_URL}/api/me/reward-progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRewardProgress(data);
      }
    } catch (e) { console.error('fetchRewardProgress:', e); }
    finally { setRewardsLoading(false); }
  }, [BACKEND_API_URL, user]);

  const fetchMeStats = useCallback(async () => {
    if (!user) { setMeStats(null); return; }
    try {
      const token = getAuthToken();
      if (!token) { setMeStats(null); return; }
      const res = await fetch(`${BACKEND_API_URL}/api/me/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setMeStats(await res.json());
    } catch (e) { console.error('fetchMeStats:', e); }
  }, [BACKEND_API_URL, user]);

  useEffect(() => { fetchRewardTiers(); }, [fetchRewardTiers]);
  useEffect(() => { fetchSiteSettings(); }, [fetchSiteSettings]);
  useEffect(() => { fetchDbPeriods(); }, [fetchDbPeriods]);
  useEffect(() => { fetchDbLinks(); }, [fetchDbLinks]);
  useEffect(() => {
    if (activePage === 'challenges') fetchChallenges();
  }, [activePage, fetchChallenges]);

  // Points: fetch shop + balance whenever user lands on points page or logs in
  useEffect(() => {
    if (activePage === 'pointsshop') {
      fetchPointsShop();
      fetchPointsConfig();
      fetchPointsRecent();
      if (user) {
        fetchPointsBalance();
        if (pointsTab === 'history') fetchPointsHistory(1);
        if (pointsTab === 'redemptions') fetchMyRedemptions();
        if (pointsTab === 'leaderboard') fetchPointsLeaderboard(1);
      }
    }
  }, [activePage, user, pointsTab, fetchPointsShop, fetchPointsConfig, fetchPointsRecent, fetchPointsBalance, fetchPointsHistory, fetchMyRedemptions, fetchPointsLeaderboard]);

  // Keep sidebar balance fresh whenever logged in
  useEffect(() => { if (user) fetchPointsBalance(); }, [user, fetchPointsBalance]);
  // Refresh points balance every 30 seconds while logged in
  useEffect(() => {
    if (!user) return;
    const id = setInterval(fetchPointsBalance, 30000);
    return () => clearInterval(id);
  }, [user, fetchPointsBalance]);

  // Admin: load points data when entering admin/points tab
  useEffect(() => {
    if (activePage !== 'admin' || adminTab !== 'points' || !user?.isAdmin) return;
    if (adminPointsTab === 'config') { fetchAdminPointsConfig(); fetchAdminPointsStats(); fetchWagerPollerStatus(); }
    if (adminPointsTab === 'shop') fetchAdminShopItems();
    if (adminPointsTab === 'redemptions') fetchAdminPointsRedemptions(adminPointsRedemptionFilter);
  }, [activePage, adminTab, adminPointsTab, adminPointsRedemptionFilter, user?.isAdmin, fetchAdminPointsConfig, fetchAdminPointsStats, fetchAdminShopItems, fetchAdminPointsRedemptions, fetchWagerPollerStatus]);

  // Ambient admin stats poll — keeps sidebar Admin Panel badge fresh anywhere on the site
  useEffect(() => {
    if (!user?.isAdmin) return;
    fetchAdminStats();
    const tick = setInterval(fetchAdminStats, 60000);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.isAdmin]);

  // Admin: load challenge claims when entering that tab
  useEffect(() => {
    if (activePage !== 'admin' || adminTab !== 'challengeClaims' || !user?.isAdmin) return;
    fetchAdminChallengeClaims(adminChallengeClaimFilter);
  }, [activePage, adminTab, adminChallengeClaimFilter, user?.isAdmin, fetchAdminChallengeClaims]);

  // Refresh wager poller status every 15s while config tab is open
  useEffect(() => {
    if (activePage !== 'admin' || adminTab !== 'points' || adminPointsTab !== 'config' || !user?.isAdmin) return;
    const tick = setInterval(fetchWagerPollerStatus, 15000);
    return () => clearInterval(tick);
  }, [activePage, adminTab, adminPointsTab, user?.isAdmin, fetchWagerPollerStatus]);

  // Poll bot status + active raffle entries when on the dedicated Raffle Bot page
  useEffect(() => {
    if (activePage !== 'rafflebot' || !user?.isAdmin) return;
    fetchBotHealth();
    fetchBotActive();
    fetchBotRaffles();
    fetchWinnerHistory();
    // Immediately fetch entries if there's an active raffle
    if (botActiveRaffle?.id) fetchBotEntries(botActiveRaffle.id);
    const activeId = botActiveRaffle?.id; // capture for interval closure
    const tick = setInterval(() => {
      fetchBotHealth();
      fetchBotActive();
      if (activeId) fetchBotEntries(activeId);
    }, 4000);
    // Less-frequent winner history refresh (it changes only on confirm/invalidate)
    const histTick = setInterval(fetchWinnerHistory, 30000);
    return () => { clearInterval(tick); clearInterval(histTick); };
    // Re-run when botActiveRaffle changes so we start polling the right raffle ID
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, user?.isAdmin, botActiveRaffle?.id, fetchBotHealth, fetchBotActive, fetchBotRaffles, fetchBotEntries, fetchWinnerHistory]);

  // Lightweight ambient poll of the active raffle so the top-right nav button
  // can show a green live-dot even when admin isn't on the bot page
  useEffect(() => {
    if (!user?.isAdmin) return;
    fetchBotActive();
    const tick = setInterval(fetchBotActive, 30000);
    return () => clearInterval(tick);
  }, [user?.isAdmin, fetchBotActive]);

  // Poll bingo state. Fast (2s) while on the bingo page so the board + any
  // spectators stay in sync with the OBS widget; slow (30s) elsewhere just to
  // keep the "LIVE" nav badge fresh. /bingo/active is public + cheap.
  useEffect(() => {
    const onPage = activePage === 'bingo';
    fetchBingoState();
    const tick = setInterval(fetchBingoState, onPage ? 2000 : 30000);
    return () => clearInterval(tick);
  }, [activePage, fetchBingoState]);

  // Admin entries panel: refresh every 4s while it's open on the bingo page
  useEffect(() => {
    if (activePage !== 'bingo' || !bingoShowEntries || !user?.isAdmin) return;
    fetchBingoEntries();
    const tick = setInterval(fetchBingoEntries, 4000);
    return () => clearInterval(tick);
  }, [activePage, bingoShowEntries, user?.isAdmin, fetchBingoEntries]);

  // Poll battle state. Fast (2.5s) on the battles page so the matchup reveal +
  // crowns stay in sync with the OBS widget; slow (30s) elsewhere for the nav
  // LIVE badge. Admin detail only polls while an admin is on the page.
  useEffect(() => {
    const onPage = activePage === 'battles';
    fetchBattleState();
    if (onPage && user?.isAdmin) fetchBattleAdmin();
    const tick = setInterval(() => {
      fetchBattleState();
      if (onPage && user?.isAdmin) fetchBattleAdmin();
    }, onPage ? 2500 : 30000);
    return () => clearInterval(tick);
  }, [activePage, user?.isAdmin, fetchBattleState, fetchBattleAdmin]);

  // Battle history: fetch on page enter + when the pager moves
  useEffect(() => {
    if (activePage !== 'battles') return;
    fetchBattleHistory(battleHistoryPage);
  }, [activePage, battleHistoryPage, fetchBattleHistory]);

  // Keep the draw team selectors valid when the session's teams change
  // (stale ids from a previous session would render blank selects)
  useEffect(() => {
    const ids = (battleAdmin?.session?.teams || []).map(t => t.id);
    if (ids.length < 2) return;
    setBattleDraw(d => {
      const a = ids.includes(d.a) ? d.a : ids[0];
      let b = ids.includes(d.b) && d.b !== a ? d.b : ids.find(i => i !== a);
      if (a === d.a && b === d.b) return d;
      return { a, b };
    });
  }, [battleAdmin?.session?.teams]);

  // Run the on-site roulette (highlight cycle + ticks, landing on the picked
  // square) whenever a fresh Pick Random happens. rollNonce only bumps on
  // pick-random, so manual square-selects don't trigger it. The first observed
  // value just primes the baseline so loading the page mid-game doesn't re-roll.
  useEffect(() => {
    if (activePage !== 'bingo') return;
    const bs = bingoState;
    if (!bs || bs.status !== 'playing') return;
    const roll = bs.rollNonce || 0;
    if (bingoLastRollRef.current === null) { bingoLastRollRef.current = roll; return; }
    if (roll === bingoLastRollRef.current) return;
    bingoLastRollRef.current = roll;

    const board = Array.isArray(bs.board) ? bs.board : [];
    const landSq = board.find(s => s && s.status === 'active');
    const unpaid = board.filter(s => s && s.status !== 'paid').map(s => s.position);
    if (!landSq || !unpaid.length) return;
    const landPos = landSq.position;

    if (bingoRollTimerRef.current) clearTimeout(bingoRollTimerRef.current);
    setBingoRolling(true);
    const TICKS = 18;
    let i = 0;
    const step = () => {
      const p = (i >= TICKS - 2) ? landPos : unpaid[Math.floor(Math.random() * unpaid.length)];
      setBingoRollPos(p);
      playBingoTick(i / TICKS);
      i++;
      if (i < TICKS) {
        bingoRollTimerRef.current = setTimeout(step, 45 + Math.floor(i * i * 0.9));
      } else {
        bingoRollTimerRef.current = setTimeout(() => {
          setBingoRolling(false);
          setBingoRollPos(null);
          playBingoDing();
        }, 150);
      }
    };
    step();
    return () => { if (bingoRollTimerRef.current) clearTimeout(bingoRollTimerRef.current); };
  }, [activePage, bingoState?.rollNonce, bingoState?.status, playBingoTick, playBingoDing]);

  // Reset the roll baseline when leaving the page / session changes
  useEffect(() => {
    if (activePage !== 'bingo') { bingoLastRollRef.current = null; setBingoRolling(false); setBingoRollPos(null); }
  }, [activePage]);
  useEffect(() => { bingoLastRollRef.current = null; }, [bingoState?.sessionId]);
  useEffect(() => {
    if (bingoState?.status !== 'playing' && bingoRolling) { setBingoRolling(false); setBingoRollPos(null); }
  }, [bingoState?.status, bingoRolling]);

  // When the winner-stats modal opens, fetch wager stats from main backend
  useEffect(() => {
    if (!winnerStatsModal || !winnerStatsModal.loading) return;
    const rainbet = winnerStatsModal.rainbet_username;
    if (!rainbet) {
      setWinnerStatsModal(prev => prev ? { ...prev, loading: false, stats: null } : prev);
      return;
    }
    (async () => {
      try {
        const token = getAuthToken();
        const res = await fetch(`${BACKEND_API_URL}/api/admin/rainbet-user-stats?rainbet=${encodeURIComponent(rainbet)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setWinnerStatsModal(prev => prev ? { ...prev, loading: false, stats: data } : prev);
        } else {
          setWinnerStatsModal(prev => prev ? { ...prev, loading: false, stats: null } : prev);
        }
      } catch {
        setWinnerStatsModal(prev => prev ? { ...prev, loading: false, stats: null } : prev);
      }
    })();
  }, [winnerStatsModal, BACKEND_API_URL]);

  // Phase 1: when modal opens, also fetch follow-info from the bot service
  useEffect(() => {
    if (!winnerStatsModal || winnerStatsModal.followInfo !== undefined) return;
    setWinnerStatsModal(prev => prev ? { ...prev, followInfo: { loading: true } } : prev);
    (async () => {
      try {
        const params = new URLSearchParams({
          platform: winnerStatsModal.platform,
          username: winnerStatsModal.username
        });
        if (winnerStatsModal.platform === 'twitch' && winnerStatsModal.twitchUserId) {
          params.set('user_id', winnerStatsModal.twitchUserId);
        }
        const res = await botFetch(`/follow-info?${params.toString()}`);
        const data = await res.json().catch(() => ({}));
        setWinnerStatsModal(prev => prev ? { ...prev, followInfo: { loading: false, ...data } } : prev);
      } catch (err) {
        setWinnerStatsModal(prev => prev ? { ...prev, followInfo: { loading: false, error: err.message } } : prev);
      }
    })();
  }, [winnerStatsModal, botFetch]);

  // Phase 1: response timer — counts up since modal opened, only for unconfirmed unwinners
  const [winnerResponseSeconds, setWinnerResponseSeconds] = useState(0);
  useEffect(() => {
    if (!winnerStatsModal) { setWinnerResponseSeconds(0); return; }
    if (winnerStatsModal.confirmed_at || winnerStatsModal.invalidated_at) return;
    setWinnerResponseSeconds(0);
    const start = Date.now();
    const id = setInterval(() => {
      setWinnerResponseSeconds(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [winnerStatsModal?.entryId, winnerStatsModal?.confirmed_at, winnerStatsModal?.invalidated_at]);

  // Phase 2: live message stream — polls bot every 2.5s while modal is open.
  // Uses incremental "since" param so each poll only returns new messages.
  const [winnerMessages, setWinnerMessages] = useState([]);
  const [winnerMessagesTracked, setWinnerMessagesTracked] = useState(true);
  useEffect(() => {
    if (!winnerStatsModal?.entryId || !winnerStatsModal?.raffleId) {
      setWinnerMessages([]);
      return;
    }
    setWinnerMessages([]);
    setWinnerMessagesTracked(true);
    let cancelled = false;
    let lastTs = 0;

    const poll = async () => {
      try {
        const url = `/raffles/${winnerStatsModal.raffleId}/users/${winnerStatsModal.platform}/${encodeURIComponent(winnerStatsModal.username)}/messages${lastTs ? `?since=${lastTs}` : ''}`;
        const res = await botFetch(url);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (data.tracked === false) setWinnerMessagesTracked(false);
        if (data.tracked === true) setWinnerMessagesTracked(true);
        if (Array.isArray(data.messages) && data.messages.length) {
          setWinnerMessagesTracked(true); // if we got messages, tracking works regardless of flag
          setWinnerMessages(prev => {
            const merged = [...prev, ...data.messages];
            return merged.slice(-30);
          });
          lastTs = Math.max(lastTs, ...data.messages.map(m => m.ts || 0));
        }
      } catch {}
    };
    poll();
    const id = setInterval(poll, 2500);
    return () => { cancelled = true; clearInterval(id); };
  }, [winnerStatsModal?.entryId, winnerStatsModal?.raffleId, winnerStatsModal?.platform, winnerStatsModal?.username, botFetch]);

  useEffect(() => {
    if (activePage === 'rewards' || activePage === 'profile') fetchRewardProgress();
  }, [activePage, user, fetchRewardProgress]);

  useEffect(() => {
    if (activePage === 'profile' && user) fetchMeStats();
  }, [activePage, user, fetchMeStats]);

  // Slot Requests — fetch on page visit, poll every 5s while open
  const fetchSlotRequests = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/slot-requests`);
      if (res.ok) {
        const data = await res.json();
        setSlotReqData({ ...data, loadedAt: new Date() });
      }
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (activePage !== 'slotrequests') return;
    fetchSlotRequests();
    const id = setInterval(fetchSlotRequests, 5000);
    return () => clearInterval(id);
  }, [activePage, fetchSlotRequests]);

  // Wager claims
  const fetchMyClaims = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/claims/mine`);
      if (res.ok) { const d = await res.json(); setMyClaims(d.claims || []); }
    } catch {}
  }, [user]);

  const fetchWagerClaimsAdmin = useCallback(async () => {
    if (!user?.isAdmin) return;
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/admin/claims?status=${wagerClaimsFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { const d = await res.json(); setWagerClaimsAdmin(d.claims || []); }
      else { console.error('[wagerClaims] fetch failed:', res.status, await res.text().catch(() => '')); }
    } catch (e) { console.error('[wagerClaims] fetch error:', e); }
  }, [user?.isAdmin, wagerClaimsFilter]);

  useEffect(() => { if (user) fetchMyClaims(); }, [user, fetchMyClaims]);
  useEffect(() => { if (user?.isAdmin) fetchWagerClaimsAdmin(); }, [user?.isAdmin, fetchWagerClaimsAdmin]);

  // Signup checklist
  const fetchChecklist = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/checklist`);
      if (res.ok) setChecklistData(await res.json());
    } catch {}
  }, [user]);

  useEffect(() => { if (user) fetchChecklist(); }, [user, fetchChecklist]);

  // Canonical domain redirect — localStorage is per-origin, so www vs non-www
  // creates separate sessions. Force everyone to the non-www version.
  useEffect(() => {
    if (isWww) window.location.href = window.location.href.replace('www.camneverwins.com', 'camneverwins.com');
  }, [isWww]);

  const fetchChecklistAdminClaims = useCallback(async () => {
    if (!user?.isAdmin) return;
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/admin/checklist-claims?status=${checklistAdminFilter}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setChecklistAdminClaims(d.claims || []); }
    } catch {}
  }, [user?.isAdmin, checklistAdminFilter]);

  useEffect(() => { if (user?.isAdmin) fetchChecklistAdminClaims(); }, [user?.isAdmin, fetchChecklistAdminClaims]);

  // User notifications (deny responses etc)
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/notifications`);
      if (res.ok) { const d = await res.json(); setUserNotifications(d.notifications || []); }
    } catch {}
  }, [user]);

  useEffect(() => { if (user) fetchNotifications(); }, [user, fetchNotifications]);
  // Poll for new notifications every 60s
  useEffect(() => {
    if (!user) return;
    const id = setInterval(fetchNotifications, 60000);
    return () => clearInterval(id);
  }, [user, fetchNotifications]);

  // Prize claims
  const fetchMyPrizeClaims = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/prize-claims/mine`);
      if (res.ok) { const d = await res.json(); setPrizeClaims(d.claims || []); }
    } catch {}
  }, [user]);

  const fetchPrizeClaimsAdmin = useCallback(async () => {
    if (!user?.isAdmin) return;
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/admin/prize-claims?status=${prizeClaimsFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { const d = await res.json(); setPrizeClaimsAdmin(d.claims || []); }
    } catch {}
  }, [user?.isAdmin, prizeClaimsFilter]);

  useEffect(() => { if (user) fetchMyPrizeClaims(); }, [user, fetchMyPrizeClaims]);

  // Wager tier claims (admin)
  const fetchAdminTierClaims = useCallback(async () => {
    if (!user?.isAdmin) return;
    try {
      const token = getAuthToken();
      // 'flagged' is a cross-status view (pending + paid + denied) of suspicious claims
      const qs = adminTierClaimsFilter === 'flagged' ? 'flagged=1' : `status=${adminTierClaimsFilter}`;
      const res = await fetch(`${BACKEND_API_URL}/api/admin/tier-claims?${qs}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAdminTierClaims(d.claims || []); }
    } catch {}
  }, [user?.isAdmin, adminTierClaimsFilter]);

  useEffect(() => { if (user?.isAdmin) fetchAdminTierClaims(); }, [user?.isAdmin, fetchAdminTierClaims]);

  // Wager raffle
  const fetchActiveWagerRaffle = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/wager-raffles/active`);
      if (res.ok) { const d = await res.json(); setActiveWagerRaffle(d.raffle); setWagerRaffleContenders(d.contenders || []); setUpcomingWagerRaffle(d.upcoming || null); setRaffleUpdatedAt(Date.now()); }
    } catch {}
  }, [BACKEND_API_URL]);

  const fetchWagerRaffleHistory = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/wager-raffles/history`);
      if (res.ok) { const d = await res.json(); setWagerRaffleHistory(d.raffles || []); }
    } catch {}
  }, [BACKEND_API_URL]);

  const fetchAdminWagerRaffles = useCallback(async () => {
    if (!user?.isAdmin) return;
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/admin/wager-raffles`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAdminWagerRaffles(d.raffles || []); }
    } catch {}
  }, [user?.isAdmin, BACKEND_API_URL]);

  useEffect(() => {
    if (activePage === 'wagerraffle') { fetchActiveWagerRaffle(); fetchWagerRaffleHistory(); if (user?.isAdmin) fetchAdminWagerRaffles(); }
    else if (activePage === 'home') { fetchActiveWagerRaffle(); } // home stat rail shows the live raffle prize
  }, [activePage, user?.isAdmin, fetchActiveWagerRaffle, fetchWagerRaffleHistory, fetchAdminWagerRaffles]);

  const fetchAdminPeriodHistory = useCallback(async () => {
    if (!user?.isAdmin) return;
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/admin/periods`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAdminPeriodHistory(d.periods || []); }
    } catch {}
  }, [user?.isAdmin, BACKEND_API_URL]);
  useEffect(() => { if (user?.isAdmin) fetchPrizeClaimsAdmin(); }, [user?.isAdmin, fetchPrizeClaimsAdmin]);

  const submitClaim = async () => {
    if (!claimModal || claimSubmitting) return;
    setClaimSubmitting(true);
    try {
      console.log(`[claim] Submitting ${claimModal.type}, screenshot size: ${claimScreenshot ? Math.round(claimScreenshot.length / 1024) + 'KB' : 'none'}`);
      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/claims`, {
        method: 'POST',
        body: JSON.stringify({ claimType: claimModal.type, screenshot: claimScreenshot, notes: claimNotes })
      });
      console.log(`[claim] Response status: ${res.status}`);
      let data;
      try { data = await res.json(); } catch { data = { error: `Server returned ${res.status}` }; }
      console.log(`[claim] Response body:`, data);
      if (!res.ok) { alert(data.error || `Failed to submit (status ${res.status})`); return; }
      alert('Claim submitted! You\'ll be notified when it\'s reviewed (2-3 business days).');
      setClaimModal(null); setClaimScreenshot(null); setClaimNotes('');
      fetchMyClaims();
    } catch (e) { console.error('[claim] Error:', e); alert('Network error: ' + e.message); }
    finally { setClaimSubmitting(false); }
  };

  // Dynamic leaderboard refresh - more frequent near end of period
  useEffect(() => {
    const getRefreshInterval = () => {
      const now = new Date();
      const timeRemaining = LEADERBOARD_END - now;
      const tenMinutes = 10 * 60 * 1000;
      const oneHour = 60 * 60 * 1000;
      const sixHours = 6 * 60 * 60 * 1000;
      const oneDay = 24 * 60 * 60 * 1000;
      
      if (timeRemaining <= 0) return 300000; // 5 min after ended
      if (timeRemaining <= tenMinutes) return 30000; // 30 sec for last 10 min
      if (timeRemaining <= oneHour) return 60000; // 1 min for last hour
      if (timeRemaining <= sixHours) return 120000; // 2 min for last 6 hours
      if (timeRemaining <= oneDay) return 300000; // 5 min for last day
      return 1800000; // 30 min normally
    };
    
    let intervalId;
    const setupInterval = () => {
      const interval = getRefreshInterval();
      intervalId = setTimeout(() => {
        fetchLeaderboard();
        setupInterval(); // Recursively set up next interval
      }, interval);
    };
    
    setupInterval();
    return () => clearTimeout(intervalId);
  }, [fetchLeaderboard, LEADERBOARD_END]);
  
  // Fetch active hunt on initial load (for all pages including admin)
  useEffect(() => {
    fetchActiveHunt();
    const interval = setInterval(fetchActiveHunt, 30000);
    return () => clearInterval(interval);
  }, []);

  const getInitials = (name) => name?.substring(0, 2).toUpperCase() || '??';
  // Privacy: show enough of the name to be recognizable but not full reveal.
  // Format: first 4 chars + **** + last char (e.g. "Deividas" → "Deiv****s").
  // Short names: first char + **** (e.g. "Bob" → "B****").
  const censorUser = (name) => {
    if (!name) return '****';
    if (name.length <= 2) return name.charAt(0) + '***';
    if (name.length <= 3) return name.substring(0, 2) + '***';
    const hidden = name.length - 3;
    const stars = '*'.repeat(Math.min(hidden, 5));
    return name.substring(0, 2) + stars + name.charAt(name.length - 1);
  };
  // Admins see the full username; everyone else sees the censored version.
  const displayUser = (name) => user?.isAdmin ? (name || '****') : censorUser(name);
  const formatNum = (n) => '$' + (n >= 1000000 ? (n/1000000).toFixed(2) + 'M' : n >= 1000 ? (n/1000).toFixed(1) + 'K' : n.toFixed(0));
  const SITE_TZ = 'America/Chicago';
  const fmtDateCT = (d, opts) => new Date(d).toLocaleDateString('en-US', { timeZone: SITE_TZ, ...opts });
  const fmtTimeCT = (d) => new Date(d).toLocaleString('en-US', { timeZone: SITE_TZ, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const getPrizeForRank = (rank) => prizes.find(p => p.rank === rank)?.prize || 0;

  // Phase D: reusable status pill for the bot admin tab
  const StatusPill = ({ label, ok, neutral, value }) => {
    const color = ok ? '#22c55e' : neutral ? '#f59e0b' : '#ef4444';
    const bg = ok ? 'rgba(34, 197, 94, 0.1)' : neutral ? 'rgba(245, 158, 11, 0.1)' : 'rgba(239, 68, 68, 0.1)';
    const border = ok ? 'rgba(34, 197, 94, 0.3)' : neutral ? 'rgba(245, 158, 11, 0.3)' : 'rgba(239, 68, 68, 0.3)';
    return (
      <div style={{ padding: '0.6rem 0.85rem', background: bg, border: `1px solid ${border}`, borderRadius: '8px' }}>
        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.15rem' }}>{label}</div>
        <div style={{ fontSize: '0.95rem', fontWeight: 700, color }}>
          {value != null ? value : (ok ? '✓ OK' : neutral ? 'Check' : '✗ Down')}
        </div>
      </div>
    );
  };

  // Phase E: row for the points-earning summary on /points
  const EarnRow = ({ active, icon, text }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: active ? 1 : 0.45 }}>
      <span>{icon}</span>
      <span style={{ flex: 1, color: active ? 'var(--text)' : 'var(--text-dim)' }}>{text}</span>
      <span style={{ fontSize: '0.7rem', fontWeight: 700, color: active ? '#22c55e' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {active ? '● Live' : 'Coming soon'}
      </span>
    </div>
  );

  // Sort helper function for rewards
  const sortData = (data, sortConfig) => {
    if (!data || !sortConfig) return data;
    return [...data].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.field) {
        case 'hunt_id':
          aVal = parseInt(a.hunt_id) || 0;
          bVal = parseInt(b.hunt_id) || 0;
          break;
        case 'rank':
          aVal = parseInt(a.rank) || 0;
          bVal = parseInt(b.rank) || 0;
          break;
        case 'reward_amount':
          aVal = parseFloat(a.reward_amount) || parseFloat(a.actual_reward?.replace(/[^0-9.]/g, '')) || parseFloat(a.reward?.replace(/[^0-9.]/g, '')) || 0;
          bVal = parseFloat(b.reward_amount) || parseFloat(b.actual_reward?.replace(/[^0-9.]/g, '')) || parseFloat(b.reward?.replace(/[^0-9.]/g, '')) || 0;
          break;
        case 'paid_at':
          aVal = a.paid_at ? new Date(a.paid_at).getTime() : 0;
          bVal = b.paid_at ? new Date(b.paid_at).getTime() : 0;
          break;
        case 'created_at':
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case 'username':
          aVal = (a.display_name || a.username || '').toLowerCase();
          bVal = (b.display_name || b.username || '').toLowerCase();
          break;
        default:
          aVal = a[sortConfig.field];
          bVal = b[sortConfig.field];
      }
      
      if (sortConfig.direction === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  };

  // Sort button group component
  const SortButtons = ({ sortConfig, setSortConfig, options }) => (
    <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', flexWrap: 'wrap' }}>
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => {
            if (sortConfig.field === opt.value) {
              // Toggle direction if same field
              setSortConfig(s => ({ ...s, direction: s.direction === 'asc' ? 'desc' : 'asc' }));
            } else {
              // Set new field with desc default
              setSortConfig({ field: opt.value, direction: 'desc' });
            }
          }}
          style={{
            padding: '0.4rem 0.7rem',
            background: sortConfig.field === opt.value ? 'rgba(139, 92, 246, 0.3)' : 'rgba(0,0,0,0.3)',
            border: sortConfig.field === opt.value ? '1px solid rgba(139, 92, 246, 0.5)' : '1px solid var(--border)',
            borderRadius: '6px',
            color: sortConfig.field === opt.value ? '#a78bfa' : 'var(--text-dim)',
            fontSize: '0.8rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontWeight: sortConfig.field === opt.value ? 600 : 400,
            display: 'flex',
            alignItems: 'center',
            gap: '0.3rem',
            transition: 'all 0.15s ease'
          }}
        >
          {opt.label}
          {sortConfig.field === opt.value && (
            <span style={{ fontSize: '0.75rem' }}>
              {sortConfig.direction === 'asc' ? '↑' : '↓'}
            </span>
          )}
        </button>
      ))}
    </div>
  );

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Space+Mono:wght@700&family=Lilita+One&display=swap');

    .display-font { font-family: 'Lilita One', 'Inter', sans-serif; font-weight: 400; }
    .gold-3d {
      font-family: 'Lilita One', 'Inter', sans-serif;
      font-weight: 400;
      background: linear-gradient(180deg, #fffbe6 0%, #ffe58a 26%, #fbbf24 48%, #e28c08 64%, #b45309 80%, #7c3f06 100%);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      filter: drop-shadow(0 2px 0 rgba(124, 63, 6, 0.9)) drop-shadow(0 2px 0 rgba(75, 36, 2, 0.85)) drop-shadow(0 5px 8px rgba(0, 0, 0, 0.55)) drop-shadow(0 0 26px rgba(251, 191, 36, 0.25));
    }
    
    :root {
      --bg: #0a0a0f;
      --bg-card: #14141c;
      --bg-dark: #16161f;
      --gold: #e5e7eb;
      --gold-dark: #9ca3af;
      --gold-glow: rgba(229, 231, 235, 0.25);
      --green: #22c55e;
      --purple: #8b5cf6;
      --pink: #ec4899;
      --text: #ffffff;
      --text-dim: #9ca3af;
      --text-muted: #7b8394;
      --border: rgba(255,255,255,0.08);
      --sidebar-width: 260px;
    }

    * { margin: 0; padding: 0; box-sizing: border-box; }

    /* Themed scrollbars — the default gray thumb sticks out against the dark theme */
    html {
      scrollbar-width: thin;
      scrollbar-color: rgba(255,255,255,0.16) transparent;
    }
    ::-webkit-scrollbar { width: 10px; height: 10px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb {
      background: rgba(255,255,255,0.12);
      border-radius: 8px;
      border: 2px solid var(--bg);
    }
    ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.22); }
    ::-webkit-scrollbar-corner { background: transparent; }
    body {
      background: var(--bg);
      position: relative;
      overflow-x: hidden;
    }
    /* Diagonal light rays — subtle blue streaks across the viewport */
    body::before {
      content: '';
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      pointer-events: none;
      z-index: 0;
      background:
        linear-gradient(125deg, transparent 42%, rgba(34, 211, 238, 0.02) 43%, rgba(34, 211, 238, 0.03) 44%, transparent 45%),
        linear-gradient(125deg, transparent 52%, rgba(59, 130, 246, 0.02) 53%, rgba(59, 130, 246, 0.025) 54%, transparent 55%),
        linear-gradient(125deg, transparent 68%, rgba(34, 211, 238, 0.015) 69%, rgba(34, 211, 238, 0.02) 70%, transparent 71%),
        linear-gradient(125deg, transparent 78%, rgba(139, 92, 246, 0.015) 79%, rgba(139, 92, 246, 0.02) 80%, transparent 81%);
    }
    /* Subtle dot grid pattern */
    body::after {
      content: '';
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      pointer-events: none;
      z-index: 0;
      background-image: radial-gradient(circle, rgba(34, 211, 238, 0.035) 1px, transparent 1px);
      background-size: 40px 40px;
    }
    /* Ambient star sparkles */
    @keyframes starTwinkle {
      0%, 100% { opacity: 0.15; transform: scale(0.8); }
      50% { opacity: 0.6; transform: scale(1.2); }
    }
    .bg-stars {
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      pointer-events: none;
      z-index: 0;
      overflow: hidden;
    }
    .bg-star {
      position: absolute;
      width: 2px;
      height: 2px;
      background: rgba(34, 211, 238, 0.4);
      border-radius: 50%;
      animation: starTwinkle var(--dur) ease-in-out infinite;
      animation-delay: var(--delay);
    }
    
    .app {
      min-height: 100vh;
      background: var(--bg);
      color: var(--text);
      font-family: 'Inter', -apple-system, sans-serif;
      position: relative;
      overflow-x: hidden;
    }
    /* Ambient gradient orbs that sit way behind everything — adds atmospheric
       depth without competing with content. Fixed-position so they don't scroll. */
    .app::before {
      content: '';
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: 0;
      background:
        radial-gradient(circle at 12% 18%, rgba(34, 211, 238, 0.12) 0, transparent 35%),
        radial-gradient(circle at 88% 22%, rgba(167, 139, 250, 0.1) 0, transparent 38%),
        radial-gradient(circle at 78% 82%, rgba(244, 114, 182, 0.09) 0, transparent 35%),
        radial-gradient(circle at 18% 78%, rgba(34, 211, 238, 0.08) 0, transparent 32%);
    }
    /* Faint noise overlay — breaks up the flat black, very subtle. SVG data URI
       so no extra HTTP request. */
    .app::after {
      content: '';
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: 0;
      opacity: 0.025;
      background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
    }
    
    .app-layout {
      display: flex;
      min-height: 100vh;
      position: relative;
      z-index: 1;
    }
    
    /* Sidebar */
    .sidebar {
      width: var(--sidebar-width);
      /* heavy dark gradient over a whisper of the ambient artwork ties the
         sidebar into the scene without hurting nav readability */
      background: linear-gradient(180deg, rgba(26, 26, 46, 0.93) 0%, rgba(13, 13, 26, 0.97) 100%), url('/hero-bg.webp') left center / cover no-repeat, #0d0d1a;
      border-right: 1px solid var(--border);
      position: fixed;
      top: 0;
      left: 0;
      bottom: 0;
      display: flex;
      flex-direction: column;
      z-index: 100;
      overflow-y: auto;
    }
    .sidebar-header {
      padding: 0.75rem 1rem;
      border-bottom: 1px solid var(--border);
    }
    .sidebar-logo {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      cursor: pointer;
      text-decoration: none;
      min-width: 0;
    }
    .sidebar-logo-icon {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      overflow: hidden;
      flex-shrink: 0;
      background: #0a0a0f;
      border: 1px solid rgba(229, 231, 235, 0.15);
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .sidebar-logo-icon img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    .sidebar-logo-text {
      font-size: 1.35rem;
      font-weight: 800;
      color: var(--text);
      letter-spacing: 3px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .sidebar-cta {
      padding: 0.5rem 1rem;
    }
    /* Secondary "In the US? Rainbet.us" links — quiet on purpose, .com stays the main CTA */
    .rb-us-link {
      display: block;
      text-align: center;
      font-size: 0.7rem;
      font-weight: 600;
      color: var(--text-muted);
      text-decoration: none;
      transition: color 0.15s;
    }
    .rb-us-link strong { color: #93c5fd; font-weight: 700; }
    .rb-us-link:hover, .rb-us-link:hover strong { color: #bfdbfe; }
    .sidebar-cta-btn {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      width: 100%;
      padding: 0.65rem 0.85rem;
      background: linear-gradient(135deg, #0ea5e9, #22d3ee);
      border: none;
      border-radius: 14px;
      color: #fff;
      font-family: inherit;
      font-size: 0.97rem;
      font-weight: 700;
      cursor: pointer;
      text-decoration: none;
      transition: transform 0.2s, box-shadow 0.3s;
      letter-spacing: 0.03em;
      overflow: hidden;
      isolation: isolate;
      box-shadow:
        0 4px 18px rgba(34, 211, 238, 0.35),
        0 0 0 1px rgba(34, 211, 238, 0.3);
      animation: ctaPulse 2.8s ease-in-out infinite;
    }
    /* The CTA is ALLOWED to glow hard — it's the one thing that should pop.
       Ambient glows elsewhere are kept low so this stands out. */
    @keyframes ctaPulse {
      0%, 100% { box-shadow: 0 4px 18px rgba(34, 211, 238, 0.35), 0 0 0 1px rgba(34, 211, 238, 0.3); }
      50% { box-shadow: 0 6px 32px rgba(34, 211, 238, 0.65), 0 0 0 1px rgba(34, 211, 238, 0.5), 0 0 50px rgba(14, 165, 233, 0.25); }
    }
    /* Animated gradient border using ::before as a background underlay */
    .sidebar-cta-btn::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(45deg, transparent, rgba(255,255,255,0.2), transparent);
      animation: ctaSweep 3.5s ease-in-out infinite;
      pointer-events: none;
    }
    /* Inner panel removed — button is now a visible gradient */
    .sidebar-cta-btn::after {
      display: none;
    }
    @keyframes ctaShine {
      0% { background-position: 0% 50%; }
      100% { background-position: 300% 50%; }
    }
    /* Diagonal sweeping highlight that periodically wipes across the button */
    .sidebar-cta-shine {
      position: absolute;
      top: -50%;
      left: -75%;
      width: 50%;
      height: 200%;
      background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%);
      transform: skewX(-22deg);
      animation: ctaSweep 4.5s ease-in-out infinite;
      pointer-events: none;
    }
    @keyframes ctaSweep {
      0%, 70%, 100% { left: -75%; }
      85% { left: 130%; }
    }
    .sidebar-cta-btn:hover {
      transform: translateY(-2px);
      background: linear-gradient(135deg, #0284c7, #06b6d4);
      box-shadow: 0 12px 36px rgba(34, 211, 238, 0.55), 0 0 0 1px rgba(34, 211, 238, 0.5);
      animation-play-state: paused;
    }
    .sidebar-cta-btn:hover .sidebar-cta-icon {
      transform: rotate(-12deg) scale(1.18);
    }
    .sidebar-cta-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: rgba(255,255,255,0.2);
      transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.2);
      animation: ctaIconFloat 3.2s ease-in-out infinite;
    }
    @keyframes ctaIconFloat {
      0%, 100% { transform: translateY(0) rotate(0deg); }
      50% { transform: translateY(-8px) rotate(2deg); }
    }
    /* Free-drifting paths for the hero slot icons — three variants assigned
       round-robin with different durations so they never move in sync */
    @keyframes slotWanderA {
      0%, 100% { transform: translate(0, 0) rotate(-6deg); }
      25% { transform: translate(16px, -20px) rotate(3deg); }
      50% { transform: translate(-12px, -32px) rotate(-4deg); }
      75% { transform: translate(-20px, -10px) rotate(5deg); }
    }
    /* Ambient backdrop life: scripted star twinkles + breathing glow pools
       (deterministic replacement for AI-video ambience) */
    @keyframes bgTwinkle {
      0%, 100% { opacity: 0; transform: scale(0.45) rotate(0deg); }
      50% { opacity: 1; transform: scale(1) rotate(25deg); }
    }
    @keyframes bgGlowBreath {
      0%, 100% { opacity: 0.3; }
      50% { opacity: 0.75; }
    }
    @keyframes bgCloudBreath {
      0%, 100% { opacity: 0.15; transform: scale(1); }
      50% { opacity: 0.85; transform: scale(1.07); }
    }

    /* ===== Halloween theme (temporary — .app.halloween, see isHalloweenActive) ===== */
    .spooky-moon {
      position: absolute;
      right: 9%;
      top: 13%;
      width: 110px;
      height: 110px;
      border-radius: 50%;
      /* a few soft craters over a warm harvest-moon disc */
      background:
        radial-gradient(circle at 62% 30%, rgba(194, 120, 60, 0.35) 0, transparent 11%),
        radial-gradient(circle at 34% 58%, rgba(194, 120, 60, 0.3) 0, transparent 15%),
        radial-gradient(circle at 66% 70%, rgba(194, 120, 60, 0.25) 0, transparent 8%),
        radial-gradient(circle at 38% 34%, #fff8e7 0, #fde7c2 45%, #f6b878 100%);
      box-shadow: 0 0 40px 8px rgba(253, 186, 116, 0.28), 0 0 120px 40px rgba(251, 146, 60, 0.12);
      opacity: 0.78;
      animation: moonGlow 9s ease-in-out infinite;
    }
    @keyframes moonGlow {
      0%, 100% { opacity: 0.7; }
      50% { opacity: 0.85; }
    }
    @media (max-width: 768px) {
      .spooky-moon { width: 60px; height: 60px; right: 4%; top: 10%; animation: none; opacity: 0.5; }
    }
    .spooky-bat {
      position: absolute;
      left: 0;
      animation: batFly linear infinite;
      filter: drop-shadow(0 0 1px rgba(253, 186, 116, 0.55)) drop-shadow(0 0 6px rgba(167, 139, 250, 0.35));
      opacity: 0.9;
    }
    .spooky-bat.rtl { animation-name: batFlyBack; }
    .spooky-bat-wings {
      display: block;
      transform-origin: 50% 40%;
      animation: batFlap 0.32s ease-in-out infinite alternate;
    }
    @keyframes batFlap {
      from { transform: scaleY(1); }
      to { transform: scaleY(0.6); }
    }
    @keyframes batFly {
      0% { transform: translate(-8vw, 0); }
      25% { transform: translate(22vw, -4vh); }
      50% { transform: translate(52vw, 3vh); }
      75% { transform: translate(80vw, -3vh); }
      100% { transform: translate(110vw, 1vh); }
    }
    @keyframes batFlyBack {
      0% { transform: translate(110vw, 0) scaleX(-1); }
      25% { transform: translate(80vw, 4vh) scaleX(-1); }
      50% { transform: translate(50vw, -2vh) scaleX(-1); }
      75% { transform: translate(20vw, 3vh) scaleX(-1); }
      100% { transform: translate(-8vw, -1vh) scaleX(-1); }
    }
    /* Low fog: a 200%-wide strip of tiled blobs drifting by half its width, so the loop is seamless */
    .spooky-fog {
      position: absolute;
      left: 0;
      bottom: -4vh;
      width: 200%;
      height: 34vh;
      background:
        radial-gradient(ellipse 22% 55% at 18% 70%, rgba(196, 181, 253, 0.22), transparent 70%),
        radial-gradient(ellipse 28% 45% at 52% 80%, rgba(167, 139, 250, 0.18), transparent 70%),
        radial-gradient(ellipse 20% 60% at 84% 72%, rgba(216, 180, 254, 0.2), transparent 70%);
      background-size: 50% 100%;
      background-repeat: repeat-x;
      animation: fogDrift 70s linear infinite;
    }
    .spooky-fog.back {
      bottom: 2vh;
      height: 26vh;
      opacity: 0.6;
      background-position: 25% 0;
      animation-duration: 110s;
      animation-direction: reverse;
    }
    @keyframes fogDrift {
      from { transform: translateX(0); }
      to { transform: translateX(-50%); }
    }
    /* Cobweb + dangling spider in the sidebar header corner */
    .app.halloween .sidebar-header { position: relative; }
    .spooky-web {
      position: absolute;
      top: 0;
      right: 0;
      width: 64px;
      height: 64px;
      pointer-events: none;
      opacity: 0.4;
    }
    .spooky-spider {
      position: absolute;
      top: 0;
      right: 22px;
      width: 15px;
      pointer-events: none;
      transform-origin: 50% 0;
      animation: spiderBob 5s ease-in-out infinite;
    }
    @keyframes spiderBob {
      0%, 100% { transform: translateY(0) rotate(-3deg); }
      50% { transform: translateY(5px) rotate(3deg); }
    }
    /* Witch hat perched on the logo */
    .app.halloween .sidebar-logo { position: relative; }
    .spooky-hat {
      position: absolute;
      left: -5px;
      top: -10px;
      width: 26px;
      height: 20px;
      transform: rotate(-18deg);
      pointer-events: none;
      filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.6));
    }
    /* The CTA stays the one thing that glows hard — just in pumpkin */
    .app.halloween .sidebar-cta-btn {
      background: linear-gradient(135deg, #ea580c, #fb923c);
      box-shadow: 0 4px 18px rgba(251, 146, 60, 0.35), 0 0 0 1px rgba(251, 146, 60, 0.3);
      animation-name: ctaPulseSpooky;
    }
    .app.halloween .sidebar-cta-btn:hover {
      background: linear-gradient(135deg, #c2410c, #f97316);
      box-shadow: 0 12px 36px rgba(251, 146, 60, 0.55), 0 0 0 1px rgba(251, 146, 60, 0.5);
    }
    @keyframes ctaPulseSpooky {
      0%, 100% { box-shadow: 0 4px 18px rgba(251, 146, 60, 0.35), 0 0 0 1px rgba(251, 146, 60, 0.3); }
      50% { box-shadow: 0 6px 32px rgba(251, 146, 60, 0.65), 0 0 0 1px rgba(251, 146, 60, 0.5), 0 0 50px rgba(234, 88, 12, 0.25); }
    }
    .app.halloween .bg-star { background: rgba(251, 146, 60, 0.45); }
    @media (prefers-reduced-motion: reduce) {
      .spooky-bat { display: none; }
      .spooky-fog, .spooky-spider, .spooky-bat-wings { animation: none; }
    }

    .cursor-sparkle {
      position: absolute;
      clip-path: polygon(50% 0%, 61% 39%, 100% 50%, 61% 61%, 50% 100%, 39% 61%, 0% 50%, 39% 39%);
      filter: drop-shadow(0 0 4px currentColor);
      animation: cursorSparkle 0.75s ease-out forwards;
    }
    @keyframes cursorSparkle {
      0% { opacity: 0.75; transform: scale(0.35) rotate(0deg); }
      100% { opacity: 0; transform: scale(1.05) rotate(80deg) translateY(8px); }
    }
    .bg-twinkle {
      position: absolute;
      clip-path: polygon(50% 0%, 61% 39%, 100% 50%, 61% 61%, 50% 100%, 39% 61%, 0% 50%, 39% 39%);
      animation: bgTwinkle 4s ease-in-out infinite;
    }
    @keyframes slotWanderB {
      0%, 100% { transform: translate(0, 0) rotate(7deg); }
      30% { transform: translate(-18px, 14px) rotate(-4deg); }
      55% { transform: translate(14px, 26px) rotate(6deg); }
      80% { transform: translate(22px, -8px) rotate(-3deg); }
    }
    @keyframes slotWanderC {
      0%, 100% { transform: translate(0, 0) rotate(-3deg); }
      20% { transform: translate(12px, 18px) rotate(6deg); }
      45% { transform: translate(26px, -12px) rotate(-5deg); }
      70% { transform: translate(-14px, -22px) rotate(4deg); }
    }
    .sidebar-cta-icon svg { width: 24px; height: 24px; filter: drop-shadow(0 1px 3px rgba(0,0,0,0.4)); }
    .sidebar-cta-text {
      color: #fff;
      font-weight: 800;
      position: relative;
      z-index: 2;
      text-shadow: 0 1px 3px rgba(0,0,0,0.25);
    }
    @keyframes ctaTextShine {
      0% { background-position: 0% center; }
      100% { background-position: 200% center; }
    }

    /* Total Given Away counter card — sits between CTA and nav */
    .sidebar-giveaway {
      position: relative;
      margin: 0.4rem 1rem 0.25rem;
      padding: 0.5rem 0.85rem 0.55rem;
      background: linear-gradient(180deg, rgba(34, 197, 94, 0.08) 0%, rgba(0,0,0,0.35) 100%);
      border: 1px solid rgba(34, 197, 94, 0.25);
      border-radius: 12px;
      text-align: center;
      overflow: hidden;
      box-shadow: 0 4px 18px rgba(34, 197, 94, 0.1), inset 0 0 0 1px rgba(34, 197, 94, 0.05);
    }
    .sidebar-giveaway-label {
      font-size: 0.6rem;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: #86efac;
      margin-bottom: 0.3rem;
      position: relative;
      z-index: 1;
    }
    .sidebar-giveaway-amount {
      font-size: 1.15rem;
      font-weight: 800;
      color: #fff;
      font-feature-settings: 'tnum';
      letter-spacing: -0.01em;
      text-shadow: 0 0 10px rgba(34, 197, 94, 0.22);
      position: relative;
      z-index: 1;
    }
    .sidebar-giveaway-currency {
      color: #22c55e;
      margin-right: 0.05em;
    }
    /* Subtle pulse glow that drifts in the background */
    .sidebar-giveaway-pulse {
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: radial-gradient(ellipse at 50% 100%, rgba(34, 197, 94, 0.10) 0%, transparent 65%);
      animation: giveawayPulse 3.5s ease-in-out infinite;
    }
    @keyframes giveawayPulse {
      0%, 100% { opacity: 0.6; transform: scale(1); }
      50% { opacity: 1; transform: scale(1.05); }
    }

    .sidebar-nav {
      flex: 1;
      padding: 0.35rem 0;
      overflow-y: auto;
    }
    .nav-section {
      padding: 0.25rem 0.85rem;
    }
    .nav-section-title {
      font-size: 0.62rem;
      font-weight: 700;
      color: var(--text-muted);
      text-transform: uppercase;
      letter-spacing: 1.5px;
      margin-bottom: 0.3rem;
      padding-left: 0.5rem;
    }
    .nav-item {
      position: relative;
      width: 100%;
      display: flex;
      align-items: center;
      gap: 0.6rem;
      padding: 0.38rem 0.85rem;
      margin-bottom: 0.1rem;
      background: rgba(255,255,255,0.02);
      border: 1px solid transparent;
      color: var(--text-dim);
      font-family: inherit;
      font-size: 0.82rem;
      font-weight: 500;
      cursor: pointer;
      border-radius: 10px;
      transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
      text-align: left;
      overflow: hidden;
    }
    /* Left-edge accent bar — invisible by default, slides in on hover/active */
    .nav-item::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      width: 3px;
      height: 0;
      background: linear-gradient(180deg, #22d3ee, #0ea5e9);
      border-radius: 0 3px 3px 0;
      transform: translateY(-50%);
      transition: height 0.2s ease;
      box-shadow: 0 0 12px rgba(34, 211, 238, 0.6);
    }
    .nav-item:hover {
      color: var(--text);
      background: rgba(34, 211, 238, 0.06);
      border-color: rgba(34, 211, 238, 0.15);
    }
    .nav-item:hover::before { height: 50%; }
    .nav-item.active {
      color: #67e8f9;
      background: linear-gradient(90deg, rgba(34, 211, 238, 0.15) 0%, rgba(34, 211, 238, 0.04) 100%);
      border-color: rgba(34, 211, 238, 0.35);
      box-shadow: inset 0 0 0 1px rgba(34, 211, 238, 0.08);
    }
    .nav-item.active::before { height: 70%; }
    .nav-item-icon {
      width: 22px;
      height: 22px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: var(--text-dim);
      transition: color 0.18s ease, transform 0.18s ease;
    }
    .nav-item-icon svg {
      width: 20px;
      height: 20px;
    }
    /* Emoji nav icons (matches snutzy.gg style) — slight upscale + suppress
       the cyan color shift since emojis already have their own color. */
    .nav-emoji {
      font-size: 16px;
      line-height: 1;
      filter: saturate(1.05);
      transition: transform 0.18s ease, filter 0.18s ease;
    }
    .nav-item:hover .nav-emoji { transform: scale(1.12); filter: saturate(1.2) drop-shadow(0 0 6px rgba(34, 211, 238, 0.4)); }
    .nav-item.active .nav-emoji { transform: scale(1.1); filter: saturate(1.2) drop-shadow(0 0 8px rgba(34, 211, 238, 0.55)); }
    .nav-item:hover .nav-item-icon { color: #67e8f9; }
    .nav-item.active .nav-item-icon {
      color: #22d3ee;
      filter: drop-shadow(0 0 6px rgba(34, 211, 238, 0.5));
    }
    /* Admin item gets a different accent color so it's distinguishable */
    .nav-item.admin-item:hover {
      background: rgba(244, 114, 182, 0.06);
      border-color: rgba(244, 114, 182, 0.18);
    }
    .nav-item.admin-item:hover::before {
      background: linear-gradient(180deg, #f472b6, #db2777);
      box-shadow: 0 0 12px rgba(244, 114, 182, 0.6);
    }
    .nav-item.admin-item:hover .nav-item-icon { color: #f9a8d4; }
    .nav-item.admin-item.active {
      color: #fbcfe8;
      background: linear-gradient(90deg, rgba(244, 114, 182, 0.15) 0%, rgba(244, 114, 182, 0.04) 100%);
      border-color: rgba(244, 114, 182, 0.35);
      box-shadow: inset 0 0 0 1px rgba(244, 114, 182, 0.1), 0 2px 12px rgba(244, 114, 182, 0.15);
    }
    .nav-item.admin-item.active::before {
      background: linear-gradient(180deg, #f472b6, #db2777);
      box-shadow: 0 0 12px rgba(244, 114, 182, 0.6);
    }
    .nav-item.admin-item.active .nav-item-icon {
      color: #f472b6;
      filter: drop-shadow(0 0 6px rgba(244, 114, 182, 0.5));
    }
    .nav-item-badge {
      margin-left: auto;
      font-size: 0.65rem;
      padding: 0.2rem 0.5rem;
      background: #ef4444;
      color: white;
      border-radius: 10px;
      font-weight: 700;
    }
    .nav-item-badge.live {
      background: #ef4444;
      animation: pulse 2s infinite;
    }
    .nav-item.admin-item {
      color: #ef4444;
      border-color: rgba(239, 68, 68, 0.15);
    }
    .nav-item.admin-item:hover {
      background: rgba(239, 68, 68, 0.1);
      border-color: rgba(239, 68, 68, 0.25);
    }
    .nav-item.admin-item.active {
      background: rgba(239, 68, 68, 0.15);
      border-color: rgba(239, 68, 68, 0.4);
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes bounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-8px); }
    }
    .sidebar-footer {
      padding: 1.25rem 1rem;
      border-top: 1px solid var(--border);
    }
    .sidebar-socials {
      display: flex;
      justify-content: center;
      gap: 0.6rem;
      flex-wrap: wrap;
    }
    .sidebar-social-link {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255,255,255,0.04);
      border: 1px solid rgba(255,255,255,0.06);
      border-radius: 8px;
      color: var(--text-dim);
      transition: all 0.2s ease;
      text-decoration: none;
      position: relative;
      overflow: hidden;
    }
    .sidebar-social-link:hover {
      transform: translateY(-2px);
      color: #fff;
    }
    .sidebar-social-link svg {
      width: 18px;
      height: 18px;
      position: relative;
      z-index: 1;
      transition: transform 0.2s ease;
    }
    .sidebar-social-link:hover svg { transform: scale(1.1); }
    /* Per-platform brand-color hover treatment */
    .sidebar-social-link[data-platform="kick"]:hover {
      background: rgba(83, 252, 24, 0.15);
      border-color: rgba(83, 252, 24, 0.5);
      box-shadow: 0 4px 14px rgba(83, 252, 24, 0.3);
      color: #53fc18;
    }
    .sidebar-social-link[data-platform="twitch"]:hover {
      background: rgba(145, 70, 255, 0.18);
      border-color: rgba(145, 70, 255, 0.5);
      box-shadow: 0 4px 14px rgba(145, 70, 255, 0.35);
      color: #a970ff;
    }
    .sidebar-social-link[data-platform="discord"]:hover {
      background: rgba(88, 101, 242, 0.18);
      border-color: rgba(88, 101, 242, 0.5);
      box-shadow: 0 4px 14px rgba(88, 101, 242, 0.35);
      color: #7289da;
    }
    .sidebar-social-link[data-platform="youtube"]:hover {
      background: rgba(255, 0, 0, 0.15);
      border-color: rgba(255, 0, 0, 0.5);
      box-shadow: 0 4px 14px rgba(255, 0, 0, 0.3);
      color: #ff0000;
    }
    .sidebar-social-link[data-platform="tiktok"]:hover {
      background: rgba(0, 242, 234, 0.15);
      border-color: rgba(0, 242, 234, 0.5);
      box-shadow: 0 4px 14px rgba(255, 0, 80, 0.3);
      color: #fff;
    }
    .sidebar-social-link[data-platform="twitter"]:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.4);
      box-shadow: 0 4px 14px rgba(255, 255, 255, 0.2);
      color: #fff;
    }
    .sidebar-social-link[data-platform="instagram"]:hover {
      background: linear-gradient(45deg, rgba(225, 48, 108, 0.18), rgba(255, 220, 128, 0.12));
      border-color: rgba(225, 48, 108, 0.5);
      box-shadow: 0 4px 14px rgba(225, 48, 108, 0.3);
      color: #e1306c;
    }
    
    /* Main Content Area */
    .main-content {
      flex: 1;
      margin-left: var(--sidebar-width);
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      width: calc(100% - var(--sidebar-width));
    }
    
    /* Top Header */
    .top-header {
      height: 60px;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding: 0 2rem;
      background: rgba(13, 13, 26, 0.9);
      backdrop-filter: blur(10px);
      border-bottom: 1px solid var(--border);
      position: sticky;
      top: 0;
      z-index: 50;
      gap: 1rem;
    }
    .total-paid-counter {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      padding: 0.5rem 1rem;
      background: linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(22, 163, 74, 0.1));
      border: 1px solid rgba(34, 197, 94, 0.3);
      border-radius: 10px;
    }
    .total-paid-label {
      font-size: 0.8rem;
      color: var(--text-dim);
      font-weight: 500;
    }
    .total-paid-amount {
      font-size: 1.1rem;
      font-weight: 700;
      color: #22c55e;
      font-family: 'Space Mono', monospace;
    }
    @media (max-width: 600px) {
      .total-paid-counter {
        padding: 0.4rem 0.75rem;
      }
      .total-paid-label {
        display: none;
      }
      .total-paid-amount {
        font-size: 0.95rem;
      }
    }
    .header-spacer {
      flex: 1;
    }
    /* Raffle Bot top-nav button — admin-only, slick neon pill */
    /* Top header points balance pill — cyan-tinted to match diamond gem */
    .top-points-pill {
      display: inline-flex;
      align-items: baseline;
      gap: 0.4rem;
      padding: 0.5rem 0.9rem 0.5rem 0.65rem;
      margin-right: 0.6rem;
      background: linear-gradient(180deg, rgba(34, 211, 238, 0.1) 0%, rgba(0, 0, 0, 0.4) 100%);
      color: #67e8f9;
      border: 1px solid rgba(34, 211, 238, 0.3);
      border-radius: 999px;
      font-size: 0.83rem;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      transition: all 0.2s ease;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
    }
    .top-points-pill:hover {
      background: linear-gradient(180deg, rgba(34, 211, 238, 0.2) 0%, rgba(0, 0, 0, 0.4) 100%);
      border-color: rgba(34, 211, 238, 0.55);
      box-shadow: 0 4px 18px rgba(34, 211, 238, 0.3);
      color: #bef0ff;
      transform: translateY(-1px);
    }
    .top-points-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      align-self: center;
      filter: drop-shadow(0 0 8px rgba(34, 211, 238, 0.55));
    }
    .top-points-icon svg { width: 20px; height: 20px; }
    .top-points-value {
      font-family: 'Space Mono', monospace;
      font-weight: 800;
      font-size: 0.95rem;
      color: #bef0ff;
      letter-spacing: -0.01em;
    }
    .top-points-label {
      font-size: 0.65rem;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: rgba(103, 232, 249, 0.7);
    }
    @media (max-width: 720px) {
      .top-points-label { display: none; }
      .top-points-pill { padding: 0.45rem 0.7rem 0.45rem 0.55rem; margin-right: 0.4rem; }
    }

    .top-raffle-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.55rem;
      padding: 0.5rem 0.9rem 0.5rem 0.65rem;
      margin-right: 0.75rem;
      background: linear-gradient(180deg, rgba(34, 211, 238, 0.08) 0%, rgba(0, 0, 0, 0.4) 100%);
      color: #67e8f9;
      border: 1px solid rgba(34, 211, 238, 0.25);
      border-radius: 999px;
      font-size: 0.83rem;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      transition: all 0.2s ease;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      position: relative;
      overflow: hidden;
    }
    .top-raffle-btn:hover {
      background: linear-gradient(180deg, rgba(34, 211, 238, 0.18) 0%, rgba(0, 0, 0, 0.4) 100%);
      border-color: rgba(34, 211, 238, 0.5);
      box-shadow: 0 4px 18px rgba(34, 211, 238, 0.3);
      color: #fff;
      transform: translateY(-1px);
    }
    .top-raffle-btn.active {
      background: linear-gradient(135deg, #22d3ee 0%, #0ea5e9 100%);
      border-color: transparent;
      color: #042f3e;
      box-shadow: 0 4px 18px rgba(34, 211, 238, 0.45);
    }
    .top-raffle-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 7px;
      background: linear-gradient(135deg, #22d3ee, #0ea5e9);
      color: #042f3e;
      flex-shrink: 0;
    }
    .top-raffle-btn.active .top-raffle-icon {
      background: rgba(4, 47, 62, 0.25);
      color: #fff;
    }
    .top-raffle-icon svg { width: 14px; height: 14px; }
    .top-raffle-live {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      padding: 0.15rem 0.5rem 0.15rem 0.4rem;
      margin-left: 0.15rem;
      background: rgba(34, 197, 94, 0.18);
      border: 1px solid rgba(34, 197, 94, 0.4);
      border-radius: 999px;
      font-size: 0.6rem;
      font-weight: 800;
      letter-spacing: 0.1em;
      color: #4ade80;
    }
    .top-raffle-btn.active .top-raffle-live {
      background: rgba(34, 197, 94, 0.3);
      border-color: rgba(34, 197, 94, 0.6);
      color: #fff;
    }
    .top-raffle-live-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #22c55e;
      box-shadow: 0 0 8px rgba(34, 197, 94, 0.8);
      animation: liveDotPulse 1.4s ease-in-out infinite;
    }
    @keyframes liveDotPulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(0.85); }
    }
    .user-dropdown {
      position: relative;
    }
    .user-dropdown-trigger {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      padding: 0.5rem 1rem;
      background: rgba(255,255,255,0.05);
      border: 1px solid var(--border);
      border-radius: 10px;
      color: var(--text);
      font-family: inherit;
      font-size: 0.9rem;
      cursor: pointer;
      transition: all 0.15s;
    }
    .user-dropdown-trigger:hover {
      background: rgba(255,255,255,0.08);
      border-color: rgba(255,255,255,0.15);
    }
    .user-dropdown-trigger img {
      width: 30px;
      height: 30px;
      border-radius: 50%;
    }
    .user-dropdown-menu {
      position: absolute;
      top: calc(100% + 0.5rem);
      right: 0;
      background: var(--bg-card);
      border: 1px solid var(--border);
      border-radius: 10px;
      min-width: 200px;
      overflow: hidden;
      box-shadow: 0 10px 40px rgba(0,0,0,0.5);
    }
    .dropdown-item {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem 1rem;
      background: transparent;
      border: none;
      color: var(--text);
      font-family: inherit;
      font-size: 0.9rem;
      cursor: pointer;
      text-align: left;
      transition: background 0.15s;
    }
    .dropdown-item:hover {
      background: rgba(255,255,255,0.05);
    }
    .dropdown-item.admin {
      color: #ef4444;
    }
    .dropdown-divider {
      border: none;
      border-top: 1px solid var(--border);
      margin: 0.25rem 0;
    }
    .twitch-login-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.6rem 1.25rem;
      background: #9147ff;
      border: none;
      border-radius: 8px;
      color: white;
      font-family: inherit;
      font-size: 0.9rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.15s;
    }
    .twitch-login-btn:hover {
      background: #7c3aed;
      transform: translateY(-1px);
    }
    .twitch-login-btn svg {
      width: 18px;
      height: 18px;
    }
    /* Kick login button — Kick-green brand color */
    .kick-login-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.6rem 1.25rem;
      background: #53fc18;
      border: none;
      border-radius: 8px;
      color: #000;
      font-family: inherit;
      font-size: 0.9rem;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.15s;
    }
    .kick-login-btn:hover {
      background: #3fdc00;
      transform: translateY(-1px);
      box-shadow: 0 6px 18px rgba(83, 252, 24, 0.35);
    }
    .kick-login-btn svg {
      width: 18px;
      height: 18px;
    }
    .login-buttons {
      display: flex;
      gap: 0.5rem;
      align-items: center;
    }
    @media (max-width: 720px) {
      .login-buttons { flex-direction: column; gap: 0.4rem; align-items: stretch; }
      .kick-login-btn, .twitch-login-btn { justify-content: center; }
    }
    
    /* Welcome Modal */
    .welcome-modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.85);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    }
    .welcome-modal {
      background: linear-gradient(180deg, #1f1f3a 0%, #151528 100%);
      border: 2px solid var(--gold);
      border-radius: 20px;
      padding: 2.5rem;
      max-width: 450px;
      width: 90%;
      text-align: center;
      animation: modalSlide 0.3s ease-out;
    }
    @keyframes modalSlide {
      from { opacity: 0; transform: translateY(-20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .welcome-modal-icon {
      font-size: 4rem;
      margin-bottom: 1rem;
    }
    .welcome-modal h2 {
      font-size: 1.5rem;
      margin-bottom: 0.5rem;
    }
    .welcome-modal h2 span {
      color: var(--gold);
    }
    .welcome-modal p {
      color: var(--text-dim);
      margin-bottom: 1.5rem;
      line-height: 1.5;
    }
    .welcome-input-group {
      margin-bottom: 1.5rem;
    }
    .welcome-input-group label {
      display: block;
      text-align: left;
      margin-bottom: 0.5rem;
      font-size: 0.9rem;
      color: var(--text-dim);
    }
    .welcome-input {
      width: 100%;
      padding: 0.85rem 1rem;
      background: rgba(0,0,0,0.3);
      border: 2px solid var(--border);
      border-radius: 10px;
      color: var(--text);
      font-family: inherit;
      font-size: 1rem;
      transition: border-color 0.15s;
    }
    .welcome-input:focus {
      outline: none;
      border-color: var(--gold);
    }
    .welcome-btn-primary {
      width: 100%;
      padding: 0.85rem;
      background: linear-gradient(135deg, var(--gold), var(--gold-dark));
      border: none;
      border-radius: 10px;
      color: #000;
      font-family: inherit;
      font-size: 1rem;
      font-weight: 700;
      cursor: pointer;
      margin-bottom: 0.75rem;
      transition: all 0.15s;
    }
    .welcome-btn-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(229, 231, 235, 0.3);
    }
    .welcome-btn-secondary {
      background: transparent;
      border: none;
      color: var(--text-dim);
      font-family: inherit;
      font-size: 0.85rem;
      cursor: pointer;
    }
    .welcome-btn-secondary:hover {
      color: var(--text);
    }
    
    /* Mobile Sidebar Toggle */
    .mobile-sidebar-toggle {
      display: none;
      position: fixed;
      top: 1rem;
      left: 1rem;
      width: 44px;
      height: 44px;
      background: var(--gold);
      border: none;
      border-radius: 10px;
      color: #000;
      font-size: 1.25rem;
      cursor: pointer;
      z-index: 9999;
      box-shadow: 0 4px 15px rgba(229, 231, 235, 0.4);
    }
    .sidebar-overlay {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.7);
      z-index: 9997;
    }
    
    @media (max-width: 900px) {
      .sidebar {
        position: fixed;
        top: 0;
        left: 0;
        bottom: 0;
        transform: translateX(-100%);
        transition: transform 0.3s ease;
        z-index: 9998;
      }
      .sidebar.open {
        transform: translateX(0);
      }
      .sidebar-overlay.open {
        display: block;
      }
      .main-content {
        margin-left: 0;
        width: 100%;
      }
      .main {
        padding: 1.5rem;
      }
      .mobile-sidebar-toggle {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
    
    /* Navigation */
    .nav {
      position: sticky;
      top: 0;
      z-index: 100;
      background: rgba(13, 13, 26, 0.95);
      backdrop-filter: blur(20px);
      border-bottom: 1px solid var(--border);
    }
    .nav-inner {
      max-width: 1200px;
      margin: 0 auto;
      padding: 1rem 2rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .nav-logo {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      cursor: pointer;
    }
    .nav-logo-icon { font-size: 2rem; }
    .nav-logo-text {
      font-size: 1.75rem;
      font-weight: 800;
      background: linear-gradient(135deg, var(--gold), #fcd34d);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      text-transform: uppercase;
      letter-spacing: 2px;
    }
    .nav-links { display: flex; gap: 0.5rem; align-items: center; }
    .nav-link {
      padding: 0.6rem 1rem;
      background: transparent;
      border: none;
      color: var(--text-muted);
      font-family: inherit;
      font-size: 0.85rem;
      font-weight: 600;
      cursor: pointer;
      border-radius: 8px;
      transition: all 0.2s;
    }
    .nav-link:hover { color: var(--text); background: rgba(255,255,255,0.05); }
    .nav-link.active { color: var(--gold); background: rgba(229, 231, 235, 0.1); }
    .nav-cta {
      padding: 0.6rem 1.25rem;
      background: linear-gradient(135deg, var(--gold), var(--gold-dark));
      border: none;
      border-radius: 8px;
      color: #000;
      font-family: inherit;
      font-size: 0.8rem;
      font-weight: 700;
      cursor: pointer;
      text-decoration: none;
      margin-left: 0.5rem;
      transition: all 0.2s;
    }
    .nav-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 25px var(--gold-glow); }
    .mobile-menu-btn { display: none; background: none; border: none; color: var(--text); font-size: 1.5rem; cursor: pointer; }
    .mobile-menu {
      display: none;
      position: fixed;
      top: 70px;
      left: 0;
      right: 0;
      background: rgba(13, 13, 26, 0.98);
      padding: 1rem;
      flex-direction: column;
      gap: 0.5rem;
      z-index: 99;
      border-bottom: 1px solid var(--border);
    }
    .mobile-menu.open { display: flex; }
    @media (max-width: 768px) {
      .nav-links { display: none; }
      .mobile-menu-btn { display: block; }
    }
    
    /* Main Content */
    .main {
      position: relative;
      z-index: 2;
      max-width: 1400px;
      width: 100%;
      margin: 0 auto;
      padding: 2rem 2.5rem;
    }
    
    /* Header */
    .header { text-align: center; margin-bottom: 2rem; position: relative; }
    .header-badge {
      display: inline-block;
      background: rgba(34, 211, 238, 0.08);
      border: 1px solid rgba(34, 211, 238, 0.3);
      padding: 0.4rem 1rem;
      border-radius: 50px;
      font-size: 0.75rem;
      font-weight: 700;
      color: #67e8f9;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      margin-bottom: 1rem;
    }
    .header-title {
      font-family: 'Lilita One', 'Inter', sans-serif;
      font-size: clamp(2rem, 6vw, 3.5rem);
      font-weight: 400;
      margin-bottom: 0.5rem;
      letter-spacing: 0.02em;
      position: relative;
      display: inline-block;
      text-shadow: 0 3px 10px rgba(0, 0, 0, 0.45);
    }
    /* Cyan-tinted underline accent below page titles */
    .header-title::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 3px;
      background: linear-gradient(90deg, transparent, #22d3ee, transparent);
      border-radius: 999px;
      box-shadow: 0 0 12px rgba(34, 211, 238, 0.5);
    }
    .header-title span {
      background: linear-gradient(135deg, #22d3ee 0%, #0ea5e9 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .header-sub { color: var(--text-dim); margin-top: 1.25rem; }
    .header-sub strong {
      background: linear-gradient(135deg, #22d3ee, #0ea5e9);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      font-weight: 700;
    }

    /* Cards & Sections — glassmorphism treatment matching the podium/table */
    .card {
      background: linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(0,0,0,0.35) 100%);
      border: 1px solid var(--border);
      border-radius: 16px;
      padding: 1.5rem;
      margin-bottom: 1.5rem;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
    }
    .card:hover {
      border-color: rgba(34, 211, 238, 0.2);
    }
    .card-header {
      display: flex;
      align-items: center;
      gap: 1rem;
      margin-bottom: 1.25rem;
      padding-bottom: 1rem;
      border-bottom: 1px solid rgba(255,255,255,0.05);
    }
    .card-icon {
      font-size: 1.5rem;
      width: 44px;
      height: 44px;
      background: linear-gradient(135deg, rgba(34, 211, 238, 0.18), rgba(14, 165, 233, 0.08));
      border: 1px solid rgba(34, 211, 238, 0.25);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #67e8f9;
      box-shadow: 0 0 14px rgba(34, 211, 238, 0.15);
      flex-shrink: 0;
    }
    .card-title {
      font-size: 1.15rem;
      font-weight: 700;
      letter-spacing: 0.01em;
    }
    .card-sub { font-size: 0.85rem; color: var(--text-dim); }
    
    /* Prize & Countdown */
    .stats-row {
      display: flex;
      justify-content: center;
      gap: 1.5rem;
      margin-bottom: 2rem;
      flex-wrap: wrap;
    }
    .stat-box {
      background: var(--bg-card);
      border: 2px solid rgba(229, 231, 235, 0.3);
      border-radius: 16px;
      padding: 1.25rem 2rem;
      text-align: center;
    }
    .stat-label { font-size: 0.7rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 1px; }
    .stat-value { font-family: 'Space Mono', monospace; font-size: 2rem; font-weight: 700; color: var(--gold); }
    .countdown-timer { display: flex; gap: 0.5rem; justify-content: center; }
    .countdown-block { background: var(--bg-dark); border-radius: 8px; padding: 0.5rem 0.75rem; min-width: 50px; text-align: center; }
    .countdown-num { font-family: 'Space Mono', monospace; font-size: 1.25rem; font-weight: 700; color: var(--gold); }
    .countdown-unit { font-size: 0.55rem; color: var(--text-muted); text-transform: uppercase; }
    
    /* Podium — neon casino theme. Cyan #1, purple #2, magenta #3.
       Cards size-scaled to rank (1 > 2 > 3) with crown for #1. */
    .podium {
      display: flex;
      justify-content: center;
      align-items: flex-end;
      gap: 1.25rem;
      margin-bottom: 1.5rem;
      padding: 7rem 0 1.5rem;
    }
    .pod-card {
      background: linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 100%);
      border: 1px solid var(--border);
      border-radius: 16px;
      padding: 1.5rem 1.25rem 1.25rem;
      text-align: center;
      position: relative;
      transition: all 0.3s;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      backdrop-filter: blur(8px);
    }

    /* === RIBBON (top) === */
    /* The ribbon hangs off the top of every card. Trapezoid shape via clip-path,
       big enough to read clearly, distinct from the small dots that were here. */
    .pod-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translate(-50%, -55%);
      width: 56px;
      height: 56px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 900;
      font-size: 1.5rem;
      color: #fff;
      border: 4px solid var(--bg);
      z-index: 3;
      font-family: 'Inter', sans-serif;
      text-shadow: 0 2px 4px rgba(0,0,0,0.4);
    }
    /* === RANK SIZE HIERARCHY === */
    .pod-card.first {
      order: 2;
      width: 240px;
      transform: translateY(-32px);
      border-color: #22d3ee;
      box-shadow:
        0 0 0 1px rgba(34, 211, 238, 0.5),
        0 12px 50px rgba(34, 211, 238, 0.35),
        0 0 100px rgba(34, 211, 238, 0.18);
      background: linear-gradient(180deg, rgba(34, 211, 238, 0.12) 0%, rgba(0,0,0,0.4) 80%);
      padding: 1.75rem 1.25rem 1.5rem;
    }
    .pod-card.first::before {
      content: '1';
      width: 64px;
      height: 64px;
      font-size: 1.7rem;
      background: linear-gradient(135deg, #22d3ee, #0ea5e9);
      box-shadow:
        0 6px 20px rgba(34, 211, 238, 0.6),
        0 0 30px rgba(34, 211, 238, 0.3);
    }
    /* === CROWN (only on #1) — real emoji for better fidelity === */
    /* Floats clearly ABOVE the rank badge (#1 circle) so both are visible.
       Rank badge is translated to -55% above card top, so crown needs extra clearance. */
    .pod-crown {
      position: absolute;
      top: -68px;
      left: 50%;
      transform: translateX(-50%) rotate(-8deg);
      font-size: 2.2rem;
      line-height: 1;
      z-index: 5;
      filter:
        drop-shadow(0 0 10px rgba(253, 224, 71, 0.85))
        drop-shadow(0 0 24px rgba(245, 158, 11, 0.6))
        drop-shadow(0 8px 16px rgba(0, 0, 0, 0.5));
      animation: podCrownFloat 3.6s ease-in-out infinite;
      pointer-events: none;
    }
    @keyframes podCrownFloat {
      0%, 100% { transform: translateX(-50%) rotate(-8deg) translateY(0); }
      50% { transform: translateX(-46%) rotate(-4deg) translateY(-5px); }
    }

    /* Halo ring behind the crown — glowing radial gradient */
    .pod-card.first::after {
      content: '';
      position: absolute;
      top: -78px;
      left: 50%;
      transform: translateX(-50%);
      width: 110px;
      height: 65px;
      background: radial-gradient(ellipse at center,
        rgba(253, 224, 71, 0.4) 0%,
        rgba(245, 158, 11, 0.2) 35%,
        transparent 70%);
      z-index: 1;
      pointer-events: none;
      animation: podHaloPulse 2.8s ease-in-out infinite;
    }
    @keyframes podHaloPulse {
      0%, 100% { opacity: 0.75; transform: translateX(-50%) scale(1); }
      50% { opacity: 1; transform: translateX(-50%) scale(1.08); }
    }

    /* Sparkles around the card — three positioned asymmetrically for depth */
    .pod-sparkle {
      position: absolute;
      font-size: 1rem;
      z-index: 4;
      pointer-events: none;
      filter: drop-shadow(0 0 6px rgba(253, 224, 71, 0.8));
    }
    .pod-sparkle-1 {
      top: -28px;
      left: -10px;
      font-size: 0.9rem;
      animation: podSparkleA 2.4s ease-in-out infinite;
    }
    .pod-sparkle-2 {
      top: 20px;
      right: -14px;
      font-size: 0.85rem;
      animation: podSparkleB 3.1s ease-in-out infinite 0.4s;
    }
    .pod-sparkle-3 {
      bottom: 30%;
      left: -16px;
      font-size: 0.75rem;
      animation: podSparkleA 2.7s ease-in-out infinite 0.9s;
    }
    @keyframes podSparkleA {
      0%, 100% { opacity: 0; transform: scale(0.6) rotate(0deg); }
      50% { opacity: 1; transform: scale(1.15) rotate(20deg); }
    }
    @keyframes podSparkleB {
      0%, 100% { opacity: 0.2; transform: scale(0.7) rotate(-15deg); }
      50% { opacity: 1; transform: scale(1.1) rotate(10deg); }
    }

    /* Gold-leaning gradient top border on the #1 card for extra premium feel */
    .pod-card.first {
      position: relative;
    }
    .pod-card.first::after {
      /* upgraded from pure radial-gradient halo to composite — set above */
    }

    .pod-card.second {
      order: 1;
      width: 200px;
      border-color: #a78bfa;
      box-shadow: 0 0 0 1px rgba(167, 139, 250, 0.3), 0 6px 28px rgba(167, 139, 250, 0.2);
      background: linear-gradient(180deg, rgba(167, 139, 250, 0.06) 0%, rgba(0,0,0,0.4) 80%);
    }
    .pod-card.second::before {
      content: '2';
      background: linear-gradient(135deg, #a78bfa, #7c3aed);
      box-shadow: 0 5px 16px rgba(167, 139, 250, 0.5);
    }

    .pod-card.third {
      order: 3;
      width: 180px;
      transform: translateY(8px);
      border-color: #f472b6;
      box-shadow: 0 0 0 1px rgba(244, 114, 182, 0.3), 0 6px 28px rgba(244, 114, 182, 0.2);
      background: linear-gradient(180deg, rgba(244, 114, 182, 0.06) 0%, rgba(0,0,0,0.4) 80%);
      padding: 1.35rem 1.1rem 1.15rem;
    }
    .pod-card.third::before {
      content: '3';
      width: 50px;
      height: 50px;
      font-size: 1.35rem;
      background: linear-gradient(135deg, #f472b6, #db2777);
      box-shadow: 0 5px 16px rgba(244, 114, 182, 0.5);
    }

    /* Old emoji medal — hide entirely, ribbon does the job */
    .pod-medal { display: none; }

    /* === AVATAR SIZE HIERARCHY === */
    .pod-avatar {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background: rgba(0,0,0,0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 1rem;
      margin: 0.85rem auto 0.5rem;
      border: 2px solid var(--border);
      color: var(--text);
      letter-spacing: 0.05em;
    }
    .pod-card.first .pod-avatar {
      width: 88px;
      height: 88px;
      font-size: 1.3rem;
      border-width: 3px;
      border-color: #22d3ee;
      box-shadow: 0 0 24px rgba(34, 211, 238, 0.5), inset 0 0 12px rgba(34, 211, 238, 0.15);
      margin: 1rem auto 0.6rem;
    }
    .pod-card.second .pod-avatar { width: 68px; height: 68px; border-color: #a78bfa; box-shadow: 0 0 16px rgba(167, 139, 250, 0.35); }
    .pod-card.third .pod-avatar { width: 58px; height: 58px; font-size: 0.92rem; border-color: #f472b6; box-shadow: 0 0 14px rgba(244, 114, 182, 0.32); }

    .pod-user {
      font-weight: 600;
      font-size: 0.92rem;
      color: var(--text);
      letter-spacing: 0.02em;
      font-family: 'Space Mono', monospace;
    }
    .pod-card.first .pod-user { font-size: 1.05rem; font-weight: 700; }
    .pod-card.third .pod-user { font-size: 0.85rem; }

    /* Tiny WAGERED label above the amount */
    .pod-wager {
      color: var(--text-muted);
      font-size: 0.62rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      margin-top: 0.25rem;
    }
    .pod-amount {
      color: var(--text);
      font-size: 1.15rem;
      font-weight: 800;
      margin-bottom: 0.5rem;
      font-feature-settings: 'tnum';
    }
    .pod-card.first .pod-amount { font-size: 1.55rem; color: #22d3ee; text-shadow: 0 0 18px rgba(34, 211, 238, 0.5); }
    .pod-card.second .pod-amount { color: #c4b5fd; }
    .pod-card.third .pod-amount { color: #f9a8d4; font-size: 1.05rem; }

    .pod-prize {
      font-size: 0.9rem;
      font-weight: 800;
      padding: 0.55rem 0.95rem;
      border-radius: 999px;
      margin-top: auto;
      letter-spacing: 0.02em;
    }
    .pod-card.first .pod-prize {
      background: linear-gradient(135deg, #22d3ee, #0ea5e9);
      color: #042f3e;
      box-shadow: 0 6px 22px rgba(34, 211, 238, 0.5);
      font-size: 1rem;
      padding: 0.7rem 1.15rem;
    }
    .pod-card.second .pod-prize {
      background: linear-gradient(135deg, rgba(167, 139, 250, 0.25), rgba(124, 58, 237, 0.15));
      color: #c4b5fd;
      border: 1px solid rgba(167, 139, 250, 0.4);
    }
    .pod-card.third .pod-prize {
      background: linear-gradient(135deg, rgba(244, 114, 182, 0.22), rgba(219, 39, 119, 0.12));
      color: #f9a8d4;
      border: 1px solid rgba(244, 114, 182, 0.4);
      font-size: 0.85rem;
      padding: 0.5rem 0.85rem;
    }

    @keyframes shineText {
      0% { background-position: 0% center; }
      100% { background-position: 200% center; }
    }
    /* Mobile podium — flatten to vertical stack so the size hierarchy
       doesn't break in flex-wrap weirdness. Crown stays on #1. */
    @media (max-width: 720px) {
      .podium {
        flex-direction: column;
        align-items: stretch;
        gap: 1.5rem;
        padding: 3.5rem 1rem 1rem;
      }
      .pod-card,
      .pod-card.first,
      .pod-card.second,
      .pod-card.third {
        width: 100%;
        max-width: 360px;
        margin: 0 auto;
        transform: none;
      }
      .pod-card.first { order: 1; }
      .pod-card.second { order: 2; }
      .pod-card.third { order: 3; }
      .pod-card.first .pod-avatar { width: 72px; height: 72px; font-size: 1.15rem; }
      .pod-card.first .pod-amount { font-size: 1.35rem; }
    }
    
    /* Table */
    .lb-table {
      background: linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.3) 100%);
      border: 1px solid var(--border);
      border-radius: 16px;
      overflow: hidden;
      backdrop-filter: blur(8px);
    }
    .table-header {
      display: grid;
      grid-template-columns: 60px 1fr 120px 110px;
      padding: 1rem 1.25rem;
      background: rgba(0,0,0,0.4);
      font-size: 0.7rem;
      color: var(--text-muted);
      text-transform: uppercase;
      letter-spacing: 0.12em;
      font-weight: 700;
      border-bottom: 1px solid var(--border);
    }
    .table-row {
      display: grid;
      grid-template-columns: 60px 1fr 120px 110px;
      padding: 0.85rem 1.25rem;
      border-top: 1px solid rgba(255,255,255,0.04);
      align-items: center;
      transition: all 0.2s ease;
    }
    .table-row:hover {
      background: rgba(34, 211, 238, 0.05);
      box-shadow: inset 3px 0 0 rgba(34, 211, 238, 0.45);
    }
    .table-row.in-prizes { background: rgba(34, 211, 238, 0.02); }
    .table-row.in-prizes:hover { background: rgba(34, 211, 238, 0.06); }
    .table-row.fading-1 { opacity: 0.85; }
    .table-row.fading-2 { opacity: 0.75; }
    .table-row.fading-3 { opacity: 0.65; }
    .table-row.fading-4 { opacity: 0.55; }
    .table-row.fading-5 { opacity: 0.48; }
    .table-row.fading-6 { opacity: 0.42; }
    .table-row.fading-7 { opacity: 0.36; }
    .table-row.fading-8 { opacity: 0.30; }
    .table-row.fading-9 { opacity: 0.25; }
    .table-row.fading-10 { opacity: 0.20; }
    .t-rank {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 0.85rem;
    }
    .t-rank.in-prizes {
      background: linear-gradient(135deg, rgba(34, 211, 238, 0.2), rgba(14, 165, 233, 0.15));
      color: #22d3ee;
      border: 1px solid rgba(34, 211, 238, 0.35);
    }
    .t-rank.out-prizes {
      background: rgba(255,255,255,0.04);
      color: var(--text-muted);
      border: 1px solid rgba(255,255,255,0.08);
    }
    .t-user { display: flex; align-items: center; gap: 0.85rem; }
    .t-avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: linear-gradient(160deg, rgba(34, 211, 238, 0.10), rgba(0,0,0,0.55));
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.75rem;
      font-weight: 700;
      border: 1px solid rgba(34, 211, 238, 0.18);
      color: #9adbe8;
      font-family: 'Space Mono', monospace;
    }
    .t-user span { font-family: 'Space Mono', monospace; font-size: 0.88rem; letter-spacing: 0.02em; }
    /* Wager-vs-#1 progress bar filling the empty space after the player name */
    .t-bar {
      flex: 1;
      height: 7px;
      background: rgba(255,255,255,0.04);
      border: 1px solid rgba(255,255,255,0.06);
      border-radius: 4px;
      overflow: hidden;
      margin: 0 1rem;
      min-width: 40px;
    }
    .t-bar > div {
      height: 100%;
      background: linear-gradient(90deg, #0ea5e9, rgba(34, 211, 238, 0.75) 70%, rgba(34, 211, 238, 0.35));
      border-radius: 4px;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.25);
      transition: width 0.6s ease;
    }
    @media (max-width: 720px) { .t-bar { display: none; } }
    .t-wager {
      font-weight: 700;
      color: var(--text);
      font-feature-settings: 'tnum';
    }
    .t-prize {
      font-weight: 700;
      color: #22d3ee;
      font-feature-settings: 'tnum';
    }
    .t-prize.none { color: var(--text-muted); font-weight: 400; }
    
    /* Rewards */
    .tier-list { display: flex; flex-direction: column; gap: 0.75rem; }
    .tier-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: var(--bg-dark);
      border-radius: 12px;
      padding: 1rem 1.25rem;
      border-left: 4px solid var(--gold);
    }
    .tier-left { display: flex; align-items: center; gap: 1rem; }
    .tier-icon { font-size: 1.5rem; }
    .tier-name { font-weight: 600; }
    .tier-req { font-size: 0.85rem; color: var(--text-dim); }
    .tier-reward { font-family: 'Space Mono', monospace; font-size: 1.1rem; font-weight: 700; color: var(--green); }
    .tier-rewards { text-align: right; }
    .tier-total { font-size: 0.75rem; color: var(--text-muted); margin-top: 0.15rem; }
    
    .vip-card {
      background: linear-gradient(135deg, rgba(229, 231, 235, 0.1), rgba(229, 231, 235, 0.05));
      border: 2px solid rgba(229, 231, 235, 0.3);
      border-radius: 20px;
      padding: 2rem;
      text-align: center;
      margin-top: 1.5rem;
    }
    .vip-badge {
      display: inline-block;
      background: var(--gold);
      color: #000;
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 700;
      margin-bottom: 1rem;
    }
    .vip-title { font-size: 1.5rem; font-weight: 800; margin-bottom: 0.5rem; }
    .vip-perks {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
      gap: 1rem;
      margin: 1.5rem 0;
    }
    .vip-perk {
      background: var(--bg-dark);
      border-radius: 12px;
      padding: 1rem;
    }
    .vip-perk-icon { font-size: 1.5rem; margin-bottom: 0.5rem; }
    .vip-perk-title { font-size: 0.85rem; color: var(--text-dim); }
    
    /* CTA Button */
    .cta-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      background: linear-gradient(135deg, var(--gold), var(--gold-dark));
      color: #000;
      padding: 1rem 2rem;
      border-radius: 12px;
      font-weight: 700;
      text-decoration: none;
      transition: all 0.2s;
    }
    .cta-btn:hover { transform: translateY(-2px); box-shadow: 0 10px 30px var(--gold-glow); }
    
    /* Content */
    .video-row {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 1.25rem;
    }
    @media (max-width: 1200px) {
      .video-row { grid-template-columns: repeat(2, 1fr); }
    }
    @media (max-width: 900px) {
      .video-row { grid-template-columns: repeat(2, 1fr); }
    }
    @media (max-width: 600px) {
      .video-row { grid-template-columns: 1fr; }
    }
    .video-card {
      background: var(--bg-dark);
      border-radius: 12px;
      overflow: hidden;
    }
    .video-embed {
      position: relative;
      width: 100%;
      padding-top: 150%; /* Slightly shorter aspect ratio */
      background: #000;
    }
    .video-embed.youtube-short {
      padding-top: 150%;
    }
    .video-embed iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: none;
    }
    .video-info {
      padding: 0.75rem;
    }
    .video-platform {
      display: inline-block;
      padding: 0.25rem 0.6rem;
      border-radius: 4px;
      font-size: 0.7rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
    }
    .video-platform.tiktok {
      background: rgba(0, 242, 234, 0.2);
      color: #00f2ea;
    }
    .video-platform.youtube {
      background: rgba(255, 0, 0, 0.2);
      color: #ff0000;
    }
    .video-platform.instagram {
      background: linear-gradient(135deg, rgba(131, 58, 180, 0.2), rgba(253, 29, 29, 0.2));
      color: #e1306c;
    }
    .pick-winner-btn.instagram {
      background: linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045);
    }
    .pick-winner-btn.tiktok {
      background: linear-gradient(135deg, #00f2ea, #ff0050);
    }
    .video-title {
      font-weight: 600;
      font-size: 0.85rem;
      line-height: 1.3;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .video-stats {
      font-size: 0.7rem;
      color: var(--text-muted);
      margin-top: 0.25rem;
    }
    .pick-winner-btn {
      width: calc(100% - 1.5rem);
      margin: 0 0.75rem 0.75rem;
      padding: 0.5rem;
      background: linear-gradient(135deg, #9333ea, #ec4899);
      border: none;
      border-radius: 6px;
      color: white;
      font-weight: 600;
      font-size: 0.8rem;
      cursor: pointer;
      transition: all 0.2s;
    }
    .pick-winner-btn:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(147, 51, 234, 0.4);
    }
    .pick-winner-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .pick-winner-btn.picking {
      animation: pulse 1s infinite;
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.6; }
    }
    .winner-result {
      margin: 0 1rem 1rem;
      padding: 1rem;
      background: linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(16, 185, 129, 0.2));
      border: 2px solid rgba(34, 197, 94, 0.5);
      border-radius: 12px;
    }
    .winner-header {
      font-size: 1.25rem;
      font-weight: 700;
      text-align: center;
      margin-bottom: 0.75rem;
      color: #22c55e;
    }
    .winner-info {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      text-decoration: none;
      color: inherit;
      padding: 0.75rem;
      margin: -0.5rem;
      border-radius: 8px;
      transition: background 0.2s;
      cursor: pointer;
    }
    .winner-info:hover {
      background: rgba(255, 255, 255, 0.1);
    }
    .winner-avatar {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      flex-shrink: 0;
    }
    .winner-details {
      flex: 1;
      min-width: 0;
    }
    .winner-name {
      font-weight: 700;
      font-size: 1rem;
      margin-bottom: 0.25rem;
    }
    .winner-comment {
      font-size: 0.85rem;
      color: var(--text-dim);
      word-wrap: break-word;
      max-height: 60px;
      overflow: hidden;
    }
    .winner-link-icon {
      font-size: 1.25rem;
      opacity: 0.5;
      transition: opacity 0.2s;
    }
    .winner-info:hover .winner-link-icon {
      opacity: 1;
    }
    .winner-stats {
      font-size: 0.75rem;
      color: var(--text-muted);
      text-align: center;
      margin-top: 0.75rem;
      padding-top: 0.75rem;
      border-top: 1px solid var(--border);
    }
    .reroll-btn {
      width: 100%;
      margin-top: 0.75rem;
      padding: 0.6rem;
      background: transparent;
      border: 2px solid rgba(34, 197, 94, 0.5);
      border-radius: 8px;
      color: #22c55e;
      font-weight: 600;
      font-size: 0.85rem;
      cursor: pointer;
      transition: all 0.2s;
    }
    .reroll-btn:hover:not(:disabled) {
      background: rgba(34, 197, 94, 0.15);
      border-color: #22c55e;
    }
    .reroll-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .content-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 1rem;
    }
    .content-card {
      background: var(--bg-dark);
      border-radius: 12px;
      overflow: hidden;
    }
    .content-thumb {
      height: 150px;
      background: var(--bg-card);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    .content-thumb-icon { font-size: 2.5rem; margin-bottom: 0.5rem; }
    .content-thumb-link { color: var(--gold); text-decoration: none; font-size: 0.9rem; }
    .content-info { padding: 1rem; }
    .content-platform {
      display: inline-block;
      padding: 0.2rem 0.5rem;
      border-radius: 4px;
      font-size: 0.7rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
      background: rgba(229, 231, 235, 0.2);
      color: var(--gold);
    }
    .content-title { font-weight: 600; }
    
    /* Games */
    .games-container { text-align: center; }
    .games-selection { display: flex; gap: 1.5rem; justify-content: center; flex-wrap: wrap; margin-top: 2rem; }
    .game-select-card {
      background: var(--bg-card);
      border: 2px solid var(--border);
      border-radius: 20px;
      padding: 2rem;
      width: 280px;
      cursor: pointer;
      transition: all 0.3s;
    }
    .game-select-card:hover { border-color: var(--gold); transform: translateY(-5px); }
    .game-select-icon { font-size: 4rem; margin-bottom: 1rem; }
    .game-select-title { font-size: 1.5rem; font-weight: 700; margin-bottom: 0.5rem; }
    .game-select-desc { color: var(--text-dim); }
    .back-btn {
      background: rgba(229, 231, 235, 0.2);
      border: 1px solid var(--gold);
      color: var(--gold);
      padding: 0.5rem 1rem;
      border-radius: 8px;
      cursor: pointer;
      font-family: inherit;
      margin-bottom: 1.5rem;
    }
    
    /* Wheel */
    .wheel-wrapper { position: relative; margin: 2rem auto; width: 300px; height: 300px; }
    .wheel-pointer {
      position: absolute;
      top: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 15px solid transparent;
      border-right: 15px solid transparent;
      border-top: 30px solid var(--gold);
      z-index: 20;
      filter: drop-shadow(0 0 10px var(--gold));
    }
    .wheel-svg {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      transition: transform 5s cubic-bezier(0.17, 0.67, 0.12, 0.99);
      box-shadow: 0 0 30px var(--gold-glow);
    }
    .wheel-center {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--gold), var(--gold-dark));
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.5rem;
      z-index: 10;
      border: 3px solid #fff;
    }
    .spin-btn {
      background: linear-gradient(135deg, var(--gold), var(--gold-dark));
      color: #000;
      border: none;
      padding: 1rem 2.5rem;
      font-size: 1.1rem;
      font-weight: 700;
      border-radius: 50px;
      cursor: pointer;
      font-family: inherit;
      margin-top: 1.5rem;
    }
    .spin-btn:disabled { opacity: 0.6; cursor: not-allowed; }
    .wheel-result {
      margin-top: 2rem;
      padding: 1.5rem;
      background: var(--bg-card);
      border: 2px solid var(--gold);
      border-radius: 16px;
    }
    .result-label { color: var(--text-dim); margin-bottom: 0.5rem; }
    .result-game { font-size: 2rem; font-weight: 800; color: var(--gold); }
    
    /* Slot Picker */
    .slot-reel-wrapper { display: flex; justify-content: center; margin: 2rem 0; }
    /* Item box matches the Rainbet card art aspect (360x481) so nothing gets
       cropped — the art has its title/provider text baked into the bottom.
       Keep width at 160 (+2x10 margin): SLOT_ITEM_WIDTH=180 drives the spin math. */
    .slot-reel-container {
      position: relative;
      width: 100%;
      max-width: 800px;
      height: 238px;
      overflow: hidden;
      border-radius: 16px;
      background: var(--bg-card);
      border: 3px solid var(--gold);
    }
    .slot-reel-viewport { position: absolute; top: 0; bottom: 0; left: 0; right: 0; overflow: hidden; }
    .slot-reel-strip { display: flex; position: absolute; top: 12px; left: calc(50% - 80px); height: 214px; }
    .slot-reel-item {
      width: 160px;
      height: 214px;
      flex-shrink: 0;
      margin: 0 10px;
      border-radius: 12px;
      overflow: hidden;
      background: var(--bg-dark);
    }
    .slot-reel-item img { width: 100%; height: 100%; object-fit: cover; }
    .slot-reel-gradient-left, .slot-reel-gradient-right { position: absolute; top: 0; width: 100px; height: 100%; pointer-events: none; z-index: 2; }
    .slot-reel-gradient-left { left: 0; background: linear-gradient(to right, var(--bg-card), transparent); }
    .slot-reel-gradient-right { right: 0; background: linear-gradient(to left, var(--bg-card), transparent); }
    .slot-reel-pointer { position: absolute; top: 0; left: 50%; transform: translateX(-50%); width: 4px; height: 100%; background: var(--gold); z-index: 3; }
    .slot-buttons { display: flex; gap: 1rem; justify-content: center; align-items: center; margin-top: 1.5rem; flex-wrap: wrap; }
    .slot-buttons .spin-btn { margin-top: 0; }
    .shuffle-btn {
      background: rgba(229, 231, 235, 0.15);
      border: 1px solid var(--gold-dark);
      color: var(--gold);
      padding: 0.35rem 0.6rem;
      font-weight: 500;
      font-size: 0.7rem;
      border-radius: 6px;
      cursor: pointer;
      font-family: inherit;
      transition: all 0.2s;
      height: fit-content;
      align-self: center;
    }
    .shuffle-btn:hover:not(:disabled) {
      background: rgba(229, 231, 235, 0.25);
      border-color: var(--gold);
    }
    .shuffle-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .filter-section {
      background: var(--bg-card);
      border: 1px solid var(--border);
      border-radius: 16px;
      padding: 1.25rem;
      margin-top: 2rem;
    }
    .filter-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
    .filter-label { font-weight: 600; }
    .filter-actions { display: flex; gap: 0.5rem; }
    .filter-action-btn {
      background: rgba(229, 231, 235, 0.15);
      border: 1px solid var(--gold-dark);
      color: var(--gold);
      padding: 0.35rem 0.75rem;
      border-radius: 6px;
      cursor: pointer;
      font-family: inherit;
      font-size: 0.8rem;
    }
    .provider-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 0.5rem; }
    .provider-checkbox {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      padding: 0.6rem 0.8rem;
      background: var(--bg-dark);
      border: 1px solid var(--border);
      border-radius: 8px;
      cursor: pointer;
    }
    .provider-checkbox.checked { background: rgba(229, 231, 235, 0.15); border-color: var(--gold); }
    .provider-checkbox input { display: none; }
    .provider-checkbox .checkmark {
      width: 18px;
      height: 18px;
      border: 2px solid var(--border);
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.7rem;
      color: transparent;
    }
    .provider-checkbox.checked .checkmark { background: var(--gold); border-color: var(--gold); color: #000; }
    .provider-name { font-size: 0.9rem; color: var(--text-dim); }
    .provider-checkbox.checked .provider-name { color: var(--text); }
    .slot-count { text-align: center; color: var(--text-muted); font-size: 0.85rem; margin-top: 1rem; }
    
    /* Slot Popup */
    .slot-popup-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.85);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }
    .slot-popup {
      background: var(--bg-card);
      border: 3px solid var(--gold);
      border-radius: 24px;
      padding: 2rem;
      text-align: center;
      max-width: 90%;
      width: 350px;
      position: relative;
    }
    .slot-popup-close {
      position: absolute;
      top: 0.75rem;
      right: 0.75rem;
      background: rgba(255, 255, 255, 0.1);
      border: none;
      color: var(--text-dim);
      width: 32px;
      height: 32px;
      border-radius: 50%;
      cursor: pointer;
      font-size: 1.1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }
    .slot-popup-close:hover {
      background: rgba(255, 255, 255, 0.2);
      color: var(--text);
    }
    .slot-popup-label { color: var(--text-dim); margin-bottom: 1rem; }
    .slot-popup-image {
      width: 180px;
      height: 240px; /* matches the 360x481 card art aspect — no bottom crop */
      object-fit: cover;
      border-radius: 12px;
      margin: 0 auto 1rem;
      display: block;
      border: 2px solid var(--gold-dark);
    }
    .slot-popup-name { font-size: 1.3rem; font-weight: 700; margin-bottom: 0.25rem; color: var(--text); }
    .slot-popup-provider { color: var(--gold); margin-bottom: 1rem; }
    .slot-popup-game-link { text-decoration: none; color: inherit; }
    .slot-popup-play-btn {
      display: inline-block;
      background: linear-gradient(135deg, var(--gold), var(--gold-dark));
      color: #000;
      padding: 0.75rem 1.5rem;
      border-radius: 12px;
      font-weight: 600;
      text-decoration: none;
    }
    .slot-popup-respin-btn {
      display: block;
      width: 100%;
      margin-top: 0.75rem;
      padding: 0.6rem 1rem;
      background: rgba(239, 68, 68, 0.15);
      border: 2px solid rgba(239, 68, 68, 0.5);
      color: #ef4444;
      border-radius: 10px;
      font-weight: 600;
      font-size: 0.9rem;
      cursor: pointer;
      font-family: inherit;
      transition: all 0.2s;
    }
    .slot-popup-respin-btn:hover {
      background: rgba(239, 68, 68, 0.25);
      border-color: #ef4444;
    }
    
    /* Footer */
    .footer {
      background: var(--bg-card);
      border-top: 1px solid var(--border);
      padding: 2rem;
      margin-top: 2rem;
      /* sits outside .main, so it needs its own raise above the fixed ambient layer */
      position: relative;
      z-index: 3;
    }
    .footer-inner { max-width: 800px; margin: 0 auto; text-align: center; }
    .footer-logo { display: flex; align-items: center; justify-content: center; gap: 0.5rem; margin-bottom: 1rem; }
    .footer-logo-icon {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      overflow: hidden;
      background: #0a0a0f;
      border: 1px solid rgba(229, 231, 235, 0.15);
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .footer-logo-icon img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .footer-logo-text { font-size: 1.25rem; font-weight: 700; color: var(--text); letter-spacing: 2px; }
    .footer-disclaimer { color: var(--text-muted); font-size: 0.8rem; margin-bottom: 1rem; }
    .footer-links { display: flex; flex-wrap: wrap; gap: 1rem; justify-content: center; }
    .footer-link { color: var(--text-dim); text-decoration: none; font-size: 0.85rem; }
    .footer-link:hover { color: var(--gold); }
    .footer-legal { display: flex; flex-wrap: wrap; gap: 0.6rem; justify-content: center; align-items: center; margin-top: 0.85rem; }
    .footer-legal .footer-link { font-size: 0.78rem; color: var(--text-muted); }
    .footer-legal .footer-link:hover { color: var(--text-dim); }
    .footer-legal-sep { color: var(--text-muted); font-size: 0.78rem; }
    .footer-credit { margin-top: 1rem; font-size: 0.78rem; color: var(--text-muted); }
    .footer-credit-link {
      color: #22d3ee;
      font-weight: 700;
      text-decoration: none;
      border-bottom: 1px dashed rgba(34, 211, 238, 0.45);
      padding-bottom: 1px;
      transition: color 0.15s, border-color 0.15s, text-shadow 0.15s;
    }
    .footer-credit-link:hover { color: #67e8f9; border-bottom-color: #67e8f9; text-shadow: 0 0 12px rgba(34, 211, 238, 0.5); }
    
    /* Bonus Hunt */
    .live-badge {
      background: #ef4444;
      color: white;
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.7rem;
      font-weight: 700;
      animation: livePulse 1.5s ease-in-out infinite;
    }
    .hunt-badge {
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.7rem;
      font-weight: 700;
    }
    .hunt-badge.paused {
      background: rgba(229, 231, 235, 0.2);
      color: var(--gold);
    }
    .bonushunt-widget {
      background: var(--bg-dark);
      border-radius: 12px;
      overflow: hidden;
      margin-top: 1rem;
      height: 455px;
    }
    .bonushunt-iframe {
      width: 142.85%;
      height: 650px;
      border: none;
      background: transparent;
      transform: scale(0.7);
      transform-origin: top left;
    }
    
    /* Slot Stats Panel */
    .slot-stats-panel {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 1rem;
      margin-bottom: 1.5rem;
      position: relative;
    }
    @media (max-width: 800px) {
      .slot-stats-panel {
        grid-template-columns: 1fr;
      }
    }
    .slot-stat-card {
      background: var(--bg-card);
      border-radius: 16px;
      padding: 1.25rem;
      display: flex;
      align-items: flex-start;
      gap: 1rem;
      border: 2px solid transparent;
      transition: all 0.2s;
    }
    .slot-stat-card.highest-multi {
      background: linear-gradient(135deg, rgba(168, 85, 247, 0.15), rgba(236, 72, 153, 0.15));
      border-color: rgba(168, 85, 247, 0.4);
      box-shadow: 0 0 10px rgba(168, 85, 247, 0.08);
    }
    .slot-stat-card.hot-slot {
      background: linear-gradient(135deg, rgba(249, 115, 22, 0.15), rgba(239, 68, 68, 0.15));
      border-color: rgba(249, 115, 22, 0.4);
      box-shadow: 0 0 10px rgba(249, 115, 22, 0.08);
    }
    .slot-stat-card.cold-slot {
      background: linear-gradient(135deg, rgba(56, 189, 248, 0.15), rgba(99, 102, 241, 0.15));
      border-color: rgba(56, 189, 248, 0.4);
      box-shadow: 0 0 10px rgba(56, 189, 248, 0.08);
    }
    .slot-stat-icon {
      font-size: 2rem;
      line-height: 1;
    }
    .slot-stat-card.highest-multi .slot-stat-icon {
      animation: diamondGlow 2s ease-in-out infinite;
    }
    .slot-stat-card.hot-slot .slot-stat-icon {
      animation: fireGlow 1s ease-in-out infinite;
    }
    .slot-stat-card.cold-slot .slot-stat-icon {
      animation: iceGlow 2s ease-in-out infinite;
    }
    @keyframes diamondGlow {
      0%, 100% { filter: drop-shadow(0 0 8px rgba(168, 85, 247, 0.8)); }
      50% { filter: drop-shadow(0 0 16px rgba(236, 72, 153, 0.8)); }
    }
    @keyframes fireGlow {
      0%, 100% { filter: drop-shadow(0 0 8px rgba(249, 115, 22, 0.8)); transform: scale(1); }
      50% { filter: drop-shadow(0 0 12px rgba(239, 68, 68, 0.9)); transform: scale(1.1); }
    }
    @keyframes iceGlow {
      0%, 100% { filter: drop-shadow(0 0 8px rgba(56, 189, 248, 0.6)); }
      50% { filter: drop-shadow(0 0 12px rgba(99, 102, 241, 0.8)); }
    }
    .slot-stat-content {
      flex: 1;
      min-width: 0;
    }
    .slot-stat-label {
      font-size: 0.7rem;
      color: var(--text-muted);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 0.25rem;
    }
    .slot-stat-name {
      font-size: 1rem;
      font-weight: 700;
      color: var(--text);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 0.25rem;
    }
    .slot-stat-details {
      font-size: 0.75rem;
      color: var(--text-muted);
      font-family: 'Space Mono', monospace;
      margin-bottom: 0.5rem;
    }
    .slot-stat-value {
      font-family: 'Space Mono', monospace;
      font-weight: 700;
      font-size: 1.25rem;
    }
    .slot-stat-value.highest {
      background: linear-gradient(135deg, #a855f7, #ec4899);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .slot-stat-value.hot {
      background: linear-gradient(135deg, #f97316, #ef4444);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .slot-stat-value.cold {
      background: linear-gradient(135deg, #38bdf8, #6366f1);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .slot-stats-note {
      position: absolute;
      bottom: -1.25rem;
      left: 50%;
      transform: translateX(-50%);
      font-size: 0.7rem;
      color: var(--text-muted);
      white-space: nowrap;
    }
    
    /* Live Hunt Container */
    .live-hunt-container {
      display: grid;
      grid-template-columns: 1fr 300px;
      gap: 1rem;
      margin-bottom: 1.5rem;
    }
    @media (max-width: 900px) {
      .live-hunt-container {
        grid-template-columns: 1fr;
      }
    }
    .live-hunt-widget-card {
      margin-bottom: 0;
    }
    .live-hunt-stats-card {
      margin-bottom: 0;
    }
    .live-stats-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      margin-top: 1rem;
    }
    .live-stat-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.6rem 0.8rem;
      background: var(--bg-dark);
      border-radius: 8px;
    }
    .live-stat-item.highlight {
      background: linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(236, 72, 153, 0.2));
      border: 1px solid rgba(168, 85, 247, 0.4);
      flex-direction: column;
      align-items: flex-start;
      gap: 0.3rem;
    }
    .live-stat-label {
      font-size: 0.8rem;
      color: var(--text-dim);
    }
    .live-stat-value {
      font-family: 'Space Mono', monospace;
      font-weight: 600;
      color: var(--gold);
    }
    .live-stat-value-slot {
      font-weight: 600;
      color: var(--text);
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
    .live-stat-betsize {
      font-family: 'Space Mono', monospace;
      font-size: 0.8rem;
      color: var(--gold);
      background: var(--bg-card);
      padding: 0.2rem 0.5rem;
      border-radius: 4px;
    }
    .live-hunt-cta {
      margin-top: 1rem;
      text-align: center;
    }
    .cta-btn-small {
      display: inline-block;
      background: linear-gradient(135deg, #9333ea, #ec4899);
      color: #fff;
      padding: 0.6rem 1.25rem;
      border-radius: 8px;
      font-size: 0.85rem;
      font-weight: 600;
      text-decoration: none;
      transition: all 0.2s;
    }
    .cta-btn-small:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(147, 51, 234, 0.4);
    }
    .hunt-stats-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 1rem;
      margin-top: 1rem;
    }
    @media (max-width: 600px) {
      .hunt-stats-grid { grid-template-columns: repeat(2, 1fr); }
    }
    .hunt-stat {
      background: var(--bg-dark);
      border-radius: 12px;
      padding: 1rem;
      text-align: center;
    }
    .hunt-stat-label {
      font-size: 0.75rem;
      color: var(--text-muted);
      margin-bottom: 0.25rem;
    }
    .hunt-stat-value {
      font-size: 1.25rem;
      font-weight: 700;
      font-family: 'Space Mono', monospace;
      color: var(--gold);
    }
    .hunt-history-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      margin-top: 1rem;
    }
    .hunt-history-item {
      background: var(--bg-dark);
      border-radius: 12px;
      padding: 1rem 1.25rem;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 1rem;
    }
    .hunt-history-left {
      flex: 1;
      min-width: 150px;
    }
    .hunt-history-name {
      font-weight: 600;
    }
    .hunt-history-date {
      font-size: 0.75rem;
      color: var(--text-muted);
      margin-top: 0.2rem;
    }
    .hunt-history-stats {
      display: flex;
      gap: 1.5rem;
      flex-wrap: wrap;
    }
    .hunt-history-cost,
    .hunt-history-won,
    .hunt-history-profit {
      display: flex;
      flex-direction: column;
      align-items: center;
      min-width: 70px;
    }
    .hunt-stat-mini-label {
      font-size: 0.65rem;
      color: var(--text-muted);
      text-transform: uppercase;
    }
    .hunt-history-cost span:last-child,
    .hunt-history-won span:last-child {
      font-family: 'Space Mono', monospace;
      font-weight: 600;
    }
    .hunt-history-profit span:last-child {
      font-family: 'Space Mono', monospace;
      font-weight: 700;
      font-size: 1.1rem;
    }
    .hunt-history-profit.profit span:last-child { color: #22c55e; }
    .hunt-history-profit.loss span:last-child { color: #ef4444; }
    .hunt-history-best {
      width: 100%;
      font-size: 0.8rem;
      color: var(--text-dim);
      padding-top: 0.75rem;
      border-top: 1px solid var(--border);
      margin-top: 0.5rem;
    }
    .hunt-best-details {
      color: var(--text-muted);
      font-family: 'Space Mono', monospace;
      font-size: 0.75rem;
    }
    .hunt-pagination {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      margin-top: 1.5rem;
      padding-top: 1rem;
      border-top: 1px solid var(--border);
    }
    .hunt-page-btn {
      background: var(--bg-card);
      border: 1px solid var(--border);
      color: var(--text);
      padding: 0.5rem 1rem;
      border-radius: 8px;
      cursor: pointer;
      font-size: 0.85rem;
      transition: all 0.2s;
    }
    .hunt-page-btn:hover:not(:disabled) {
      border-color: var(--gold);
      color: var(--gold);
    }
    .hunt-page-btn:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    .hunt-page-info {
      font-size: 0.85rem;
      color: var(--text-dim);
    }
    .bonushunt-history-placeholder {
      background: var(--bg-dark);
      border-radius: 12px;
      min-height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    /* Hero - clean centered prize display */
    .cam-hero {
      text-align: center;
      padding: 3rem 1rem 2rem;
      margin-bottom: 1.5rem;
      position: relative;
    }
    /* Subtle particle field — fewer dots, lower alpha, doesn't form a visible rectangle */
    .cam-hero::before {
      content: '';
      position: absolute;
      inset: -2rem -10vw;
      pointer-events: none;
      background-image:
        radial-gradient(circle at 12% 30%, rgba(34, 211, 238, 0.12) 0, transparent 1.2px),
        radial-gradient(circle at 28% 70%, rgba(167, 139, 250, 0.08) 0, transparent 1px),
        radial-gradient(circle at 48% 18%, rgba(244, 114, 182, 0.1) 0, transparent 1px),
        radial-gradient(circle at 72% 82%, rgba(34, 211, 238, 0.1) 0, transparent 1.2px),
        radial-gradient(circle at 88% 35%, rgba(167, 139, 250, 0.12) 0, transparent 1.2px),
        radial-gradient(circle at 92% 75%, rgba(244, 114, 182, 0.08) 0, transparent 1px);
      background-size: 100% 100%;
      z-index: 0;
      opacity: 0.7;
      mask-image: radial-gradient(ellipse at center, #000 40%, transparent 80%);
      -webkit-mask-image: radial-gradient(ellipse at center, #000 40%, transparent 80%);
    }
    .cam-hero > * { position: relative; z-index: 1; }
    .cam-hero-badge {
      display: inline-block;
      padding: 0.4rem 1rem;
      background: rgba(229, 231, 235, 0.06);
      border: 1px solid rgba(229, 231, 235, 0.15);
      border-radius: 999px;
      font-size: 0.7rem;
      font-weight: 700;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: var(--text-dim);
      margin-bottom: 1.5rem;
    }
    .cam-hero-prize {
      font-size: clamp(2.75rem, 7vw, 5rem);
      font-weight: 800;
      line-height: 0.95;
      background: linear-gradient(180deg, #93c5fd 0%, #3b82f6 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      letter-spacing: -0.02em;
      margin-bottom: 0.25rem;
    }
    .cam-hero-title {
      font-size: clamp(2.75rem, 8vw, 5.5rem);
      font-weight: 900;
      line-height: 0.95;
      letter-spacing: -0.01em;
      color: var(--text);
      margin-bottom: 0.85rem;
    }
    .cam-hero-sub {
      font-size: 0.9rem;
      color: var(--text-dim);
      margin-bottom: 1.5rem;
      max-width: 520px;
      margin-left: auto;
      margin-right: auto;
    }
    .cam-hero-actions {
      display: inline-flex;
      align-items: center;
      gap: 0.75rem;
      flex-wrap: wrap;
      justify-content: center;
    }
    .cam-code-chip {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.45rem 0.85rem;
      background: rgba(229, 231, 235, 0.04);
      border: 1px solid rgba(229, 231, 235, 0.12);
      border-radius: 999px;
      font-size: 0.78rem;
      color: var(--text-dim);
    }
    .cam-code-chip-label {
      text-transform: uppercase;
      letter-spacing: 0.1em;
      font-size: 0.62rem;
    }
    .cam-code-chip-value {
      font-weight: 700;
      color: var(--text);
      font-family: 'Space Mono', monospace;
      letter-spacing: 0.08em;
      font-size: 0.82rem;
    }
    .cam-code-chip-copy {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.15rem;
      color: var(--text-muted);
      display: flex;
      align-items: center;
      transition: color 0.15s ease;
    }
    .cam-code-chip-copy:hover { color: var(--text); }

    /* Slim countdown shown below podium */
    .cam-countdown-slim {
      text-align: center;
      margin: 0 auto 1.75rem;
      padding: 0.6rem 1rem;
    }
    .cam-countdown-label {
      font-size: 0.65rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--text-muted);
      margin-bottom: 0.45rem;
      font-weight: 600;
    }
    .cam-countdown-time {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      font-family: 'Space Mono', monospace;
      font-weight: 700;
      font-size: 1.4rem;
      color: var(--text);
      letter-spacing: 0.08em;
    }
    .cam-cd-sep { color: var(--text-muted); font-weight: 400; }
    .cam-hero-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.5rem;
      background: linear-gradient(135deg, #3b82f6, #2563eb);
      color: #fff;
      border-radius: 999px;
      font-size: 0.9rem;
      font-weight: 600;
      text-decoration: none;
      transition: all 0.2s ease;
      box-shadow: 0 2px 12px rgba(59, 130, 246, 0.3);
    }
    .cam-hero-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 18px rgba(59, 130, 246, 0.45);
    }
    
    /* Social Buttons */
    .social-buttons {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      justify-content: center;
      margin-top: 1rem;
    }
    .social-btn {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.45rem 0.75rem;
      background: var(--bg-dark);
      border: 1px solid var(--border);
      border-radius: 8px;
      color: var(--text);
      text-decoration: none;
      font-size: 0.8rem;
      font-weight: 500;
      transition: all 0.2s;
    }
    .social-btn:hover {
      border-color: var(--gold);
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(0,0,0,0.3);
    }
    .social-btn svg {
      width: 16px;
      height: 16px;
    }
    .social-btn.twitch { color: #9146ff; }
    .social-btn.twitch:hover { background: rgba(145, 70, 255, 0.15); border-color: #9146ff; }
    .social-btn.discord { color: #5865f2; }
    .social-btn.discord:hover { background: rgba(88, 101, 242, 0.15); border-color: #5865f2; }
    .social-btn.youtube { color: #ff0000; }
    .social-btn.youtube:hover { background: rgba(255, 0, 0, 0.15); border-color: #ff0000; }
    .social-btn.tiktok { color: #00f2ea; }
    .social-btn.tiktok:hover { background: rgba(0, 242, 234, 0.15); border-color: #00f2ea; }
    .social-btn.twitter { color: #fff; }
    .social-btn.twitter:hover { background: rgba(255, 255, 255, 0.1); border-color: #fff; }
    .social-btn.instagram { color: #e4405f; }
    .social-btn.instagram:hover { background: rgba(228, 64, 95, 0.15); border-color: #e4405f; }
    
    /* Live Indicator */
    .live-indicator {
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 999;
    }
    .live-btn {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.7rem 1.15rem;
      background: linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.5) 100%);
      border: 1px solid rgba(255,255,255,0.08);
      border-radius: 50px;
      color: var(--text);
      text-decoration: none;
      font-weight: 600;
      font-size: 0.9rem;
      transition: all 0.3s ease;
      box-shadow: 0 8px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.02);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
    }
    .live-btn:hover {
      transform: translateY(-3px);
      border-color: rgba(34, 211, 238, 0.3);
      box-shadow: 0 12px 32px rgba(0,0,0,0.5), 0 0 30px rgba(34, 211, 238, 0.15);
    }
    .live-btn.is-live {
      border-color: rgba(239, 68, 68, 0.45);
      background: linear-gradient(180deg, rgba(239, 68, 68, 0.18) 0%, rgba(0,0,0,0.5) 100%);
    }
    .live-btn.is-live:hover {
      border-color: rgba(239, 68, 68, 0.7);
      box-shadow: 0 12px 36px rgba(239, 68, 68, 0.4), 0 0 40px rgba(239, 68, 68, 0.2);
    }
    .live-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #6b7280;
      position: relative;
      flex-shrink: 0;
    }
    .live-btn.is-live .live-dot {
      background: #ef4444;
      box-shadow: 0 0 12px rgba(239, 68, 68, 0.7);
    }
    /* Pulse ring expanding outward — overlay on live dot only */
    .live-btn.is-live .live-dot::before,
    .live-btn.is-live .live-dot::after {
      content: '';
      position: absolute;
      inset: -2px;
      border-radius: 50%;
      border: 2px solid #ef4444;
      animation: liveRing 1.8s ease-out infinite;
      opacity: 0;
    }
    .live-btn.is-live .live-dot::after {
      animation-delay: 0.9s;
    }
    @keyframes liveRing {
      0% { transform: scale(0.8); opacity: 0.8; }
      100% { transform: scale(2.6); opacity: 0; }
    }
    .live-text { display: flex; flex-direction: column; align-items: flex-start; line-height: 1.2; }
    .live-status {
      font-size: 0.62rem;
      color: var(--text-muted);
      text-transform: uppercase;
      letter-spacing: 0.12em;
      font-weight: 700;
    }
    .live-btn.is-live .live-status { color: #ef4444; }
    /* Collapsed live pill — just the status dot */
    .live-btn-mini {
      width: 42px;
      height: 42px;
      padding: 0;
      justify-content: center;
      border-radius: 50%;
      cursor: pointer;
      font-family: inherit;
    }
    /* Small minimize control attached to the expanded pill */
    .live-collapse-btn {
      position: absolute;
      top: -7px;
      left: -7px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: rgba(10, 10, 18, 0.9);
      border: 1px solid rgba(255,255,255,0.18);
      color: var(--text-dim);
      font-size: 0.8rem;
      line-height: 1;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2;
      font-family: inherit;
      transition: color 0.15s, border-color 0.15s;
    }
    .live-collapse-btn:hover { color: var(--text); border-color: rgba(34, 211, 238, 0.5); }

    /* ================================================
       INSTRUCTIONS PAGE
       ================================================ */

    /* Hero deposit bonus banner */
    /* ================================================
       INSTRUCTIONS PAGE — Cam-specific hero elements (screenshot-matched)
       Sits above the SNUTZY-mirror signup/info cards
       ================================================ */

    /* BONUSES tab header */
    .instr-tab-header {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      margin-bottom: 2rem;
      padding-bottom: 1rem;
      border-bottom: 1px solid rgba(255,255,255,0.06);
    }
    .instr-tab-rainbet { height: 28px; width: auto; display: block; }
    .instr-tab-divider { color: rgba(255,255,255,0.2); font-size: 1.5rem; font-weight: 200; }
    .instr-tab-label {
      font-size: 1rem;
      font-weight: 600;
      letter-spacing: 0.22em;
      color: #e5e7eb;
    }

    /* 100% Deposit Bonus hero card */
    .bonus-hero-card {
      display: grid;
      grid-template-columns: auto 1fr auto;
      align-items: center;
      gap: 2rem;
      padding: 2rem 2.5rem;
      margin-bottom: 1.5rem;
      background: linear-gradient(180deg, rgba(22, 32, 60, 0.85) 0%, rgba(14, 22, 43, 0.9) 100%);
      border: 1px solid rgba(34, 211, 238, 0.12);
      border-radius: 18px;
      position: relative;
      overflow: hidden;
    }
    .bonus-hero-card::before {
      content: '';
      position: absolute;
      inset: 0;
      background: radial-gradient(ellipse at 20% 50%, rgba(59, 130, 246, 0.12), transparent 60%);
      pointer-events: none;
    }
    .bonus-hero-left { position: relative; z-index: 1; }
    .bonus-hero-percent {
      font-size: clamp(2.75rem, 6vw, 4.25rem);
      font-weight: 900;
      line-height: 0.95;
      color: #ffffff;
      letter-spacing: -0.02em;
      margin-bottom: 0.65rem;
    }
    .bonus-hero-percent span { font-size: 0.85em; font-weight: 800; }
    .bonus-hero-pill {
      display: inline-block;
      padding: 0.6rem 1.2rem;
      background: linear-gradient(135deg, #3b82f6, #2563eb);
      color: #fff;
      font-size: 0.82rem;
      font-weight: 800;
      letter-spacing: 0.08em;
      border-radius: 999px;
      text-decoration: none;
      box-shadow: 0 4px 18px rgba(59, 130, 246, 0.4);
      transition: all 0.2s ease;
    }
    .bonus-hero-pill:hover { transform: translateY(-1px); box-shadow: 0 6px 24px rgba(59, 130, 246, 0.55); }
    .bonus-hero-middle {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      position: relative;
      z-index: 1;
    }
    .bonus-hero-feature {
      display: flex;
      align-items: center;
      gap: 0.55rem;
      font-size: 1.05rem;
      color: #e5e7eb;
      font-weight: 600;
    }
    .bonus-hero-arrow {
      color: #3b82f6;
      font-size: 1.4rem;
      font-weight: 700;
      line-height: 1;
    }
    .bonus-hero-right {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 1rem;
      position: relative;
      z-index: 1;
    }
    .bonus-hero-rainbet { height: 26px; width: auto; opacity: 0.95; display: block; }
    .bonus-hero-claim {
      padding: 0.7rem 1.5rem;
      background: linear-gradient(135deg, #22c55e, #16a34a);
      color: #fff;
      font-size: 0.92rem;
      font-weight: 700;
      border-radius: 10px;
      text-decoration: none;
      box-shadow: 0 4px 18px rgba(34, 197, 94, 0.4);
      transition: all 0.2s ease;
    }
    .bonus-hero-claim:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(34, 197, 94, 0.55); }

    @media (max-width: 720px) {
      .bonus-hero-card {
        grid-template-columns: 1fr;
        gap: 1.25rem;
        padding: 1.5rem;
      }
      .bonus-hero-right { align-items: flex-start; flex-direction: row; justify-content: space-between; width: 100%; }
    }

    /* VIP + How to Register feature grid */
    .instr-feature-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.25rem;
      margin-bottom: 2rem;
    }
    @media (max-width: 720px) {
      .instr-feature-grid { grid-template-columns: 1fr; }
    }
    .instr-feature-card {
      background: rgba(10, 16, 28, 0.65);
      border: 1px solid rgba(255,255,255,0.06);
      border-radius: 16px;
      padding: 1.75rem;
      display: flex;
      flex-direction: column;
      gap: 0.85rem;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
    }
    .instr-feature-card-top {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 0.25rem;
    }
    .instr-feature-rainbet { height: 26px; width: auto; opacity: 0.95; display: block; }
    .instr-feature-badge {
      padding: 0.2rem 0.6rem;
      background: rgba(59, 130, 246, 0.18);
      color: #93c5fd;
      font-size: 0.62rem;
      font-weight: 800;
      letter-spacing: 0.14em;
      border-radius: 999px;
      border: 1px solid rgba(59, 130, 246, 0.4);
    }
    .instr-feature-title {
      display: flex;
      align-items: center;
      gap: 0.45rem;
      font-size: 1.35rem;
      font-weight: 800;
      color: #ffffff;
      letter-spacing: -0.01em;
    }
    .instr-feature-star {
      color: #22d3ee;
      font-size: 1.15rem;
      filter: drop-shadow(0 0 8px rgba(34, 211, 238, 0.6));
    }
    .instr-feature-title-accent {
      background: linear-gradient(135deg, #22d3ee, #0ea5e9);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .instr-feature-text {
      color: #a1a1aa;
      font-size: 0.9rem;
      line-height: 1.55;
      margin: 0;
      flex: 1;
    }
    .instr-feature-btn {
      align-self: flex-start;
      padding: 0.65rem 1.3rem;
      background: linear-gradient(135deg, #22c55e, #16a34a);
      color: #fff;
      font-size: 0.88rem;
      font-weight: 700;
      border-radius: 10px;
      text-decoration: none;
      border: none;
      cursor: pointer;
      font-family: inherit;
      transition: all 0.2s ease;
      box-shadow: 0 4px 14px rgba(34, 197, 94, 0.3);
    }
    .instr-feature-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(34, 197, 94, 0.5); }

    /* EXCLUSIVE CAM REWARDS hero with crown */
    .cam-rewards-hero {
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
      gap: 2rem;
      padding: 2.25rem 2.5rem;
      margin-bottom: 2rem;
      background: linear-gradient(180deg, rgba(8, 14, 28, 0.85) 0%, rgba(14, 22, 43, 0.9) 100%);
      border: 1px solid rgba(34, 211, 238, 0.1);
      border-radius: 18px;
      position: relative;
      overflow: hidden;
    }
    .cam-rewards-hero::before {
      content: '';
      position: absolute;
      inset: 0;
      background: radial-gradient(circle at 75% 50%, rgba(34, 211, 238, 0.08), transparent 50%);
      pointer-events: none;
    }
    .cam-rewards-hero-left { position: relative; z-index: 1; max-width: 520px; }
    .cam-rewards-hero-title {
      font-size: clamp(2rem, 5vw, 3.25rem);
      font-weight: 900;
      line-height: 1;
      letter-spacing: -0.02em;
      margin: 0 0 1.5rem 0;
      color: #ffffff;
    }
    .cam-rewards-hero-accent {
      background: linear-gradient(135deg, #22d3ee, #0ea5e9);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .cam-rewards-hero-card {
      padding: 1.1rem 1.35rem;
      background: rgba(14, 22, 43, 0.6);
      border: 1px solid rgba(34, 211, 238, 0.18);
      border-radius: 12px;
    }
    .cam-rewards-hero-card p { margin: 0; color: #e5e7eb; font-size: 0.95rem; line-height: 1.55; }
    .cam-rewards-hero-sub { color: #71717a !important; font-size: 0.85rem !important; margin-top: 0.4rem !important; }
    .cam-rewards-total { margin-top: 1.1rem; }
    .cam-rewards-total-label {
      font-size: 0.7rem;
      font-weight: 800;
      letter-spacing: 0.15em;
      color: #22d3ee;
      margin-bottom: 0.15rem;
    }
    .cam-rewards-total-value {
      font-family: 'Space Mono', monospace;
      font-size: 1.85rem;
      font-weight: 800;
      color: #22c55e;
      letter-spacing: -0.01em;
    }
    .cam-rewards-hero-right {
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .cam-rewards-crown {
      font-size: clamp(6rem, 13vw, 10rem);
      filter: drop-shadow(0 12px 30px rgba(34, 211, 238, 0.3)) drop-shadow(0 0 25px rgba(234, 179, 8, 0.35));
      animation: crownFloat 3.6s ease-in-out infinite;
    }
    @keyframes crownFloat {
      0%, 100% { transform: translateY(0) rotate(-2deg); }
      50% { transform: translateY(-10px) rotate(2deg); }
    }
    @media (max-width: 720px) {
      .cam-rewards-hero { grid-template-columns: 1fr; padding: 1.75rem; text-align: center; }
      .cam-rewards-hero-left { margin: 0 auto; }
      .cam-rewards-crown { font-size: 7rem; }
    }

    /* ================================================
       JOIN / INSTRUCTIONS PAGE — SNUTZY-mirror structure with Rainbet cyan palette.
       Class names match snutzy.gg for layout consistency; colors adapted to Cam's theme.
       ================================================ */
    .signup-container { max-width: 800px; margin: 0 auto; }

    .simple-page-title {
      font-size: clamp(2rem, 5vw, 4rem);
      font-weight: 900;
      text-align: center;
      margin-bottom: 2.5rem;
      background: linear-gradient(135deg, #fff 0%, #a5f3fc 40%, #22d3ee 70%, #0ea5e9 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      letter-spacing: -0.02em;
    }

    .signup-card {
      background: linear-gradient(135deg, rgba(34, 211, 238, 0.12) 0%, rgba(14, 165, 233, 0.12) 100%);
      border: 2px solid rgba(34, 211, 238, 0.4);
      border-radius: 24px;
      padding: 2.5rem;
      margin-bottom: 2rem;
      box-shadow: 0 0 22px rgba(34, 211, 238, 0.06), inset 0 1px 0 rgba(255,255,255,0.05);
    }

    .signup-cam-logo {
      display: block;
      width: 80px;
      height: 80px;
      border-radius: 50%;
      object-fit: cover;
      margin: 0 auto 1.5rem auto;
      border: 2px solid rgba(34, 211, 238, 0.45);
      box-shadow: 0 0 12px rgba(34, 211, 238, 0.16);
    }

    .signup-title {
      font-size: 1.75rem;
      font-weight: 700;
      text-align: center;
      margin-bottom: 2rem;
    }

    .signup-link-btn {
      display: block;
      width: 100%;
      padding: 1.25rem;
      background: linear-gradient(135deg, #22d3ee 0%, #0ea5e9 100%);
      border: none;
      border-radius: 12px;
      color: #052e3d;
      font-size: 1.1rem;
      font-weight: 800;
      font-family: inherit;
      text-decoration: none;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;
      margin-bottom: 1.5rem;
      box-shadow: 0 6px 20px rgba(34, 211, 238, 0.3);
    }

    .signup-link-btn:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(34, 211, 238, 0.5);
    }

    .code-display {
      background: rgba(0, 0, 0, 0.4);
      border: 2px solid rgba(34, 211, 238, 0.5);
      border-radius: 12px;
      padding: 1.5rem;
      text-align: center;
    }

    .code-label { color: #a1a1aa; font-size: 0.875rem; margin-bottom: 0.5rem; }

    .code-value {
      font-family: 'Space Mono', monospace;
      font-size: 2.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #67e8f9, #22d3ee);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      letter-spacing: 0.2em;
      text-shadow: 0 0 24px rgba(34, 211, 238, 0.4);
    }

    .code-hint { color: #71717a; font-size: 0.875rem; margin-top: 0.75rem; }

    .info-card {
      background: rgba(10, 16, 28, 0.65);
      border: 1px solid rgba(34, 211, 238, 0.2);
      border-radius: 20px;
      padding: 2rem;
      margin-bottom: 1.5rem;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
    }

    .info-title { font-size: 1.25rem; font-weight: 700; margin-bottom: 1rem; display: flex; align-items: center; gap: 0.5rem; }

    .info-item { display: flex; gap: 1rem; margin-bottom: 1.25rem; }
    .info-item:last-child { margin-bottom: 0; }
    .info-icon { font-size: 2rem; flex-shrink: 0; line-height: 1; }
    .info-content h4 { font-weight: 600; margin-bottom: 0.25rem; }
    .info-content p { color: #a1a1aa; font-size: 0.95rem; line-height: 1.5; }
    .info-content a { color: #67e8f9; text-decoration: none; }
    .info-content a:hover { text-decoration: underline; }

    .disclaimer { text-align: center; color: #52525b; font-size: 0.875rem; margin-top: 2rem; }

    .instr-hero {
      position: relative;
      background: linear-gradient(135deg, rgba(34, 211, 238, 0.08) 0%, rgba(167, 139, 250, 0.06) 50%, rgba(244, 114, 182, 0.08) 100%);
      border: 1px solid rgba(34, 211, 238, 0.25);
      border-radius: 20px;
      padding: 2.25rem 2rem;
      margin-bottom: 1.5rem;
      overflow: hidden;
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      box-shadow: 0 12px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05);
    }
    /* Decorative orbs in the background */
    .instr-hero-bg {
      position: absolute;
      inset: 0;
      background:
        radial-gradient(circle at 15% 30%, rgba(34, 211, 238, 0.18) 0, transparent 35%),
        radial-gradient(circle at 50% 90%, rgba(167, 139, 250, 0.15) 0, transparent 40%),
        radial-gradient(circle at 90% 20%, rgba(244, 114, 182, 0.15) 0, transparent 35%);
      pointer-events: none;
      animation: instrHeroOrbs 8s ease-in-out infinite alternate;
    }
    @keyframes instrHeroOrbs {
      0% { opacity: 0.7; transform: scale(1); }
      100% { opacity: 1; transform: scale(1.1); }
    }
    .instr-hero-content {
      position: relative;
      z-index: 1;
      display: grid;
      grid-template-columns: auto 1fr auto;
      gap: 2rem;
      align-items: center;
    }
    .instr-hero-pill {
      display: inline-block;
      padding: 0.3rem 0.75rem;
      background: rgba(34, 211, 238, 0.18);
      border: 1px solid rgba(34, 211, 238, 0.4);
      border-radius: 999px;
      font-size: 0.6rem;
      font-weight: 800;
      letter-spacing: 0.18em;
      color: #67e8f9;
      margin-bottom: 0.75rem;
    }
    .instr-hero-amount {
      font-size: 4.5rem;
      font-weight: 900;
      line-height: 0.95;
      background: linear-gradient(180deg, #93c5fd 0%, #3b82f6 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      letter-spacing: -0.03em;
    }
    .instr-hero-amount span {
      font-size: 2.5rem;
      vertical-align: super;
      margin-left: 0.1em;
    }
    .instr-hero-amount-label {
      font-size: 1rem;
      font-weight: 700;
      color: var(--text);
      letter-spacing: 0.05em;
      text-transform: uppercase;
      margin-top: 0.2rem;
    }
    .instr-hero-mid {
      display: flex;
      flex-direction: column;
      gap: 0.65rem;
      padding-left: 1.5rem;
      border-left: 1px solid rgba(255,255,255,0.1);
    }
    .instr-hero-feature {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      font-size: 0.95rem;
      color: var(--text);
    }
    .instr-hero-feature strong {
      color: #67e8f9;
      font-family: 'Space Mono', monospace;
      letter-spacing: 0.1em;
    }
    .instr-hero-arrow {
      color: #22d3ee;
      font-weight: 700;
      font-size: 1.1rem;
      flex-shrink: 0;
    }
    .instr-hero-claim {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.95rem 1.65rem;
      background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
      color: #fff;
      border-radius: 12px;
      font-weight: 700;
      font-size: 0.95rem;
      text-decoration: none;
      box-shadow: 0 8px 24px rgba(34, 197, 94, 0.4), inset 0 1px 0 rgba(255,255,255,0.2);
      transition: all 0.2s ease;
      white-space: nowrap;
    }
    .instr-hero-claim:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 32px rgba(34, 197, 94, 0.5);
    }

    /* Two-column feature row */
    .instr-feature-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.25rem;
      margin-bottom: 2rem;
    }
    .instr-feature-card {
      padding: 1.5rem;
      background: linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(0,0,0,0.35) 100%);
      border: 1px solid var(--border);
      border-radius: 16px;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      transition: border-color 0.2s ease, transform 0.2s ease;
    }
    .instr-feature-card:hover {
      transform: translateY(-2px);
    }
    .instr-feature-card.vip:hover { border-color: rgba(34, 211, 238, 0.4); box-shadow: 0 8px 30px rgba(34, 211, 238, 0.15); }
    .instr-feature-card.register:hover { border-color: rgba(167, 139, 250, 0.4); box-shadow: 0 8px 30px rgba(167, 139, 250, 0.15); }
    .instr-feature-header {
      display: flex;
      align-items: center;
      gap: 0.85rem;
      margin-bottom: 1rem;
    }
    .instr-feature-icon {
      width: 44px;
      height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 12px;
      background: linear-gradient(135deg, rgba(34, 211, 238, 0.2), rgba(14, 165, 233, 0.1));
      border: 1px solid rgba(34, 211, 238, 0.3);
      color: #22d3ee;
      box-shadow: 0 0 16px rgba(34, 211, 238, 0.2);
      flex-shrink: 0;
    }
    .instr-feature-icon svg { width: 22px; height: 22px; }
    .instr-feature-icon.register-icon {
      background: linear-gradient(135deg, rgba(167, 139, 250, 0.2), rgba(124, 58, 237, 0.1));
      border-color: rgba(167, 139, 250, 0.3);
      color: #a78bfa;
      box-shadow: 0 0 16px rgba(167, 139, 250, 0.2);
    }
    .instr-feature-title {
      font-size: 1.1rem;
      font-weight: 700;
      color: var(--text);
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .instr-feature-badge {
      padding: 0.1rem 0.45rem;
      background: linear-gradient(135deg, #f472b6, #db2777);
      color: #fff;
      border-radius: 999px;
      font-size: 0.55rem;
      font-weight: 800;
      letter-spacing: 0.1em;
    }
    .instr-feature-sub {
      font-size: 0.78rem;
      color: var(--text-muted);
      letter-spacing: 0.05em;
      text-transform: uppercase;
      margin-top: 0.1rem;
    }
    .instr-feature-card p {
      color: var(--text-dim);
      line-height: 1.55;
      font-size: 0.88rem;
      margin-bottom: 1.25rem;
    }
    .instr-feature-card p strong {
      color: #67e8f9;
      font-family: 'Space Mono', monospace;
      letter-spacing: 0.08em;
    }
    .instr-feature-cta {
      display: inline-block;
      padding: 0.65rem 1.25rem;
      background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
      color: #fff;
      border-radius: 10px;
      font-weight: 700;
      font-size: 0.85rem;
      text-decoration: none;
      border: none;
      transition: all 0.2s ease;
      box-shadow: 0 4px 14px rgba(34, 197, 94, 0.3);
    }
    .instr-feature-cta:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(34, 197, 94, 0.45);
    }

    /* Steps section header */
    .instr-steps-header {
      text-align: center;
      margin-bottom: 1.5rem;
      padding: 1rem 0;
    }
    .instr-steps-header h2 {
      font-size: clamp(1.5rem, 4vw, 2.2rem);
      font-weight: 900;
      letter-spacing: -0.01em;
      margin-bottom: 0.4rem;
    }
    .instr-steps-header h2 span {
      background: linear-gradient(135deg, #22d3ee, #0ea5e9);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .instr-steps-header p {
      color: var(--text-dim);
      font-size: 0.9rem;
    }

    /* Vertical timeline */
    .instr-timeline {
      position: relative;
      padding-left: 0;
    }
    .instr-step {
      position: relative;
      display: grid;
      grid-template-columns: 72px 1fr;
      gap: 1.25rem;
      margin-bottom: 1.5rem;
    }
    /* Connecting line between steps */
    .instr-step:not(:last-child)::before {
      content: '';
      position: absolute;
      top: 72px;
      left: 35px;
      bottom: -1.5rem;
      width: 2px;
      background: linear-gradient(180deg, rgba(34, 211, 238, 0.4) 0%, rgba(34, 211, 238, 0.05) 100%);
    }
    .instr-step-number {
      width: 72px;
      height: 72px;
      border-radius: 50%;
      background: linear-gradient(135deg, #22d3ee 0%, #0ea5e9 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 900;
      font-size: 1.85rem;
      color: #042f3e;
      box-shadow: 0 8px 24px rgba(34, 211, 238, 0.4), inset 0 -3px 0 rgba(0,0,0,0.15);
      position: relative;
      z-index: 1;
      font-family: 'Space Mono', monospace;
    }
    .instr-step-card {
      background: linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(0,0,0,0.35) 100%);
      border: 1px solid var(--border);
      border-radius: 16px;
      padding: 1.5rem 1.75rem;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      transition: border-color 0.2s ease;
    }
    .instr-step:hover .instr-step-card {
      border-color: rgba(34, 211, 238, 0.25);
    }
    .instr-step-eyebrow {
      font-size: 0.65rem;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: #67e8f9;
      margin-bottom: 0.4rem;
    }
    .instr-step-title {
      font-size: 1.45rem;
      font-weight: 800;
      color: var(--text);
      margin-bottom: 0.75rem;
      letter-spacing: -0.01em;
    }
    .instr-step-desc {
      color: var(--text-dim);
      line-height: 1.65;
      margin-bottom: 1.25rem;
      font-size: 0.92rem;
    }
    .instr-step-cta {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.4rem;
      background: linear-gradient(135deg, #22d3ee 0%, #0ea5e9 100%);
      color: #042f3e;
      border-radius: 10px;
      font-weight: 700;
      font-size: 0.9rem;
      text-decoration: none;
      border: none;
      transition: all 0.2s ease;
      box-shadow: 0 4px 18px rgba(34, 211, 238, 0.4);
    }
    .instr-step-cta:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 22px rgba(34, 211, 238, 0.5);
    }

    /* Step 2 sub-steps (A, B, C) */
    .instr-substep {
      display: flex;
      gap: 0.75rem;
      align-items: flex-start;
      padding: 0.85rem 0.95rem;
      background: rgba(255,255,255,0.025);
      border: 1px solid var(--border);
      border-radius: 10px;
    }
    .instr-substep-num {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: linear-gradient(135deg, rgba(34, 211, 238, 0.2), rgba(14, 165, 233, 0.1));
      border: 1px solid rgba(34, 211, 238, 0.4);
      color: #67e8f9;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 800;
      font-size: 0.8rem;
      flex-shrink: 0;
      font-family: 'Space Mono', monospace;
    }
    .instr-substep-title {
      font-weight: 700;
      font-size: 0.9rem;
      margin-bottom: 0.2rem;
    }
    .instr-substep-desc {
      font-size: 0.8rem;
      color: var(--text-dim);
      line-height: 1.45;
    }

    /* VPN box */
    .instr-vpn-box {
      padding: 1.25rem;
      background: linear-gradient(135deg, rgba(167, 139, 250, 0.05), rgba(167, 139, 250, 0.02));
      border: 1px solid rgba(167, 139, 250, 0.25);
      border-radius: 12px;
    }
    .instr-vpn-header {
      display: flex;
      align-items: center;
      gap: 0.55rem;
      margin-bottom: 0.5rem;
    }
    .instr-vpn-header h3 {
      font-size: 1rem;
      font-weight: 700;
      color: var(--text);
    }
    .instr-vpn-optional {
      font-size: 0.65rem;
      color: var(--text-muted);
      text-transform: uppercase;
      letter-spacing: 0.1em;
      padding: 0.15rem 0.5rem;
      background: rgba(167, 139, 250, 0.12);
      border: 1px solid rgba(167, 139, 250, 0.25);
      border-radius: 999px;
      font-weight: 700;
      color: #c4b5fd;
    }
    .instr-vpn-desc {
      font-size: 0.85rem;
      color: var(--text-dim);
      margin-bottom: 1rem;
      line-height: 1.55;
    }
    .instr-vpn-option {
      padding: 1rem;
      background: rgba(0,0,0,0.3);
      border: 1px solid var(--border);
      border-radius: 10px;
      transition: border-color 0.2s ease;
    }
    .instr-vpn-option:hover { border-color: rgba(167, 139, 250, 0.35); }
    .instr-vpn-name {
      font-weight: 700;
      font-size: 0.95rem;
      margin-bottom: 0.15rem;
    }
    .instr-vpn-tag {
      display: inline-block;
      font-size: 0.62rem;
      color: #c4b5fd;
      background: rgba(167, 139, 250, 0.15);
      border: 1px solid rgba(167, 139, 250, 0.3);
      padding: 0.1rem 0.45rem;
      border-radius: 999px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      margin-bottom: 0.65rem;
    }
    .instr-vpn-option p {
      font-size: 0.8rem;
      color: var(--text-dim);
      line-height: 1.55;
      margin-bottom: 0.85rem;
    }
    .instr-vpn-option a {
      display: inline-block;
      padding: 0.5rem 0.95rem;
      background: rgba(167, 139, 250, 0.12);
      border: 1px solid rgba(167, 139, 250, 0.3);
      border-radius: 7px;
      color: #c4b5fd;
      text-decoration: none;
      font-size: 0.8rem;
      font-weight: 600;
      transition: all 0.15s ease;
    }
    .instr-vpn-option a:hover {
      background: rgba(167, 139, 250, 0.2);
      color: #fff;
    }

    /* Step 3 info tiles */
    .instr-info-tile {
      display: flex;
      gap: 0.75rem;
      align-items: center;
      padding: 0.95rem 1rem;
      background: rgba(255,255,255,0.025);
      border: 1px solid var(--border);
      border-radius: 10px;
    }
    .instr-info-tile-icon {
      width: 36px;
      height: 36px;
      flex-shrink: 0;
      border-radius: 10px;
      background: linear-gradient(135deg, rgba(34, 211, 238, 0.18), rgba(14, 165, 233, 0.08));
      border: 1px solid rgba(34, 211, 238, 0.3);
      color: #22d3ee;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .instr-info-tile-title {
      font-weight: 700;
      font-size: 0.9rem;
      margin-bottom: 0.15rem;
    }
    .instr-info-tile-desc {
      font-size: 0.78rem;
      color: var(--text-dim);
    }

    /* Step 4 prize banner */
    .instr-prize-banner {
      padding: 1.5rem;
      background: linear-gradient(135deg, rgba(34, 211, 238, 0.12) 0%, rgba(14, 165, 233, 0.04) 100%);
      border: 1px solid rgba(34, 211, 238, 0.35);
      border-radius: 12px;
      text-align: center;
      box-shadow: inset 0 1px 0 rgba(34, 211, 238, 0.12);
    }
    .instr-prize-amount {
      font-size: 2.5rem;
      font-weight: 900;
      background: linear-gradient(135deg, #22d3ee, #0ea5e9);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      letter-spacing: -0.02em;
      margin-bottom: 0.25rem;
    }
    .instr-prize-label {
      font-size: 0.75rem;
      color: var(--text-dim);
      text-transform: uppercase;
      letter-spacing: 0.12em;
      font-weight: 700;
    }

    /* Mobile: stack instructions hero + collapse timeline */
    @media (max-width: 800px) {
      .instr-hero-content { grid-template-columns: 1fr; gap: 1.5rem; text-align: center; }
      .instr-hero-mid { padding-left: 0; border-left: none; padding-top: 1rem; border-top: 1px solid rgba(255,255,255,0.1); }
      .instr-hero-claim { width: 100%; justify-content: center; }
      .instr-feature-row { grid-template-columns: 1fr; }
      .instr-step { grid-template-columns: 1fr; gap: 0.75rem; }
      .instr-step-number { margin: 0 auto; }
      .instr-step:not(:last-child)::before { display: none; }
    }

    /* ================================================
       REWARDS PAGE — ladder layout
       ================================================ */
    .rewards-progress-banner {
      padding: 1rem 1.25rem;
      background: linear-gradient(135deg, rgba(34, 211, 238, 0.1), rgba(34, 211, 238, 0.02));
      border: 1px solid rgba(34, 211, 238, 0.3);
      border-radius: 12px;
      margin-bottom: 1.25rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 0.75rem;
      box-shadow: none;
    }
    .rewards-progress-label {
      font-size: 0.7rem;
      color: var(--text-muted);
      text-transform: uppercase;
      letter-spacing: 0.12em;
      font-weight: 700;
      margin-bottom: 0.3rem;
    }
    .rewards-progress-amount {
      font-size: 1.75rem;
      font-weight: 800;
      background: linear-gradient(135deg, #22d3ee, #0ea5e9);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      font-feature-settings: 'tnum';
    }
    .rewards-progress-warn {
      font-size: 0.82rem;
      color: #f59e0b;
      padding: 0.45rem 0.85rem;
      background: rgba(245, 158, 11, 0.1);
      border: 1px solid rgba(245, 158, 11, 0.25);
      border-radius: 8px;
    }

    /* Tier-progress summary chip inside the progress banner */
    .rewards-progress-tiers {
      display: flex;
      align-items: baseline;
      gap: 0.1rem;
      padding: 0.45rem 0.95rem;
      background: rgba(255,255,255,0.04);
      border: 1px solid var(--border);
      border-radius: 999px;
    }
    .rewards-progress-tiers-num {
      font-family: 'Space Mono', monospace;
      font-size: 1.15rem;
      font-weight: 800;
      color: #22c55e;
    }
    .rewards-progress-tiers-den {
      font-size: 0.78rem;
      color: var(--text-dim);
      font-weight: 600;
    }

    /* ---- Full-width tier bar ladder ---- */
    .rewards-ladder {
      display: flex;
      flex-direction: column;
      gap: 0.45rem;
    }
    .tier-bar-head {
      display: grid;
      grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr);
      gap: 1rem;
      padding: 0.2rem 1.25rem 0.4rem;
      font-size: 0.6rem;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--text-muted);
    }
    .tier-bar-head-mid { text-align: center; }
    .tier-bar-head-right { text-align: right; }

    /* Neutral dark rows with a tier-colored accent strip — the tier color
       reads as an accent, not a full-row wash (less muddy, less glow) */
    .tier-bar {
      --tier-color: #22d3ee;
      display: grid;
      grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr);
      gap: 1rem;
      align-items: center;
      padding: 0.9rem 1.25rem;
      background: linear-gradient(90deg,
                  color-mix(in srgb, var(--tier-color) 6%, rgba(13,15,26,0.85)) 0%,
                  rgba(13,15,26,0.72) 45%);
      border: 1px solid rgba(255,255,255,0.07);
      border-radius: 10px;
      position: relative;
      overflow: hidden;
      box-shadow: inset 3px 0 0 color-mix(in srgb, var(--tier-color) 75%, transparent);
      transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    }
    .tier-bar:hover {
      transform: translateX(2px);
      border-color: color-mix(in srgb, var(--tier-color) 40%, transparent);
      box-shadow: inset 3px 0 0 var(--tier-color), 0 6px 18px rgba(0,0,0,0.35);
    }
    .tier-bar.reached {
      border-color: color-mix(in srgb, #22c55e 40%, transparent);
      box-shadow: inset 3px 0 0 #22c55e;
    }
    .tier-bar.next {
      border-color: color-mix(in srgb, var(--tier-color) 55%, transparent);
      box-shadow: inset 3px 0 0 var(--tier-color), 0 0 14px color-mix(in srgb, var(--tier-color) 12%, transparent);
    }
    .tier-progress-line {
      position: absolute;
      left: 0;
      bottom: 0;
      height: 3px;
      background: var(--tier-color);
      border-radius: 0 3px 0 0;
      transition: width 0.5s ease;
    }

    .tier-bar-left {
      display: flex;
      align-items: center;
      gap: 0.85rem;
      min-width: 0;
    }
    .tier-status-dot {
      flex-shrink: 0;
      width: 22px;
      height: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      font-size: 0.7rem;
    }
    .tier-status-dot.on {
      background: rgba(34, 197, 94, 0.18);
      color: #22c55e;
      border: 1px solid rgba(34, 197, 94, 0.4);
    }
    .tier-status-dot.off {
      background: rgba(255,255,255,0.04);
      filter: grayscale(0.5);
      opacity: 0.7;
    }
    .tier-badge {
      flex-shrink: 0;
      padding: 0.32rem 0.85rem;
      border-radius: 999px;
      background: var(--tier-color);
      color: #0a0a0f;
      font-weight: 800;
      font-size: 0.8rem;
      letter-spacing: 0.01em;
      white-space: nowrap;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.25);
    }
    .tier-wager {
      font-family: 'Space Mono', monospace;
      font-size: 1.05rem;
      font-weight: 700;
      color: var(--text);
      white-space: nowrap;
    }
    .tier-name-tag {
      font-size: 0.75rem;
      color: var(--text-dim);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }

    .tier-col {
      display: flex;
      align-items: baseline;
      gap: 0.5rem;
      min-width: 0;
    }
    .tier-col.center { justify-content: center; flex-wrap: wrap; }
    .tier-col.right { justify-content: flex-end; }
    .tier-col-label {
      font-size: 0.58rem;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--text-muted);
    }
    .tier-total {
      font-family: 'Space Mono', monospace;
      font-size: 1rem;
      font-weight: 800;
      color: var(--tier-color);
    }
    .tier-reward {
      font-family: 'Space Mono', monospace;
      font-size: 1.05rem;
      font-weight: 800;
      color: #22c55e;
      text-shadow: 0 0 12px rgba(34, 197, 94, 0.25);
    }
    .tier-claim-btn {
      padding: 0.4rem 1rem;
      background: linear-gradient(135deg, var(--tier-color), color-mix(in srgb, var(--tier-color) 70%, #000));
      color: #fff;
      border: none;
      border-radius: 999px;
      font-weight: 700;
      font-size: 0.78rem;
      cursor: pointer;
      font-family: inherit;
      white-space: nowrap;
      box-shadow: 0 3px 12px color-mix(in srgb, var(--tier-color) 40%, transparent), inset 0 1px 0 rgba(255,255,255,0.2);
      transition: transform 0.18s ease, box-shadow 0.18s ease;
    }
    .tier-claim-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 18px color-mix(in srgb, var(--tier-color) 55%, transparent);
    }
    .tier-mini-badge {
      padding: 0.3rem 0.8rem;
      border-radius: 999px;
      font-weight: 700;
      font-size: 0.72rem;
      white-space: nowrap;
    }
    .tier-mini-badge.paid {
      background: rgba(34, 197, 94, 0.15);
      color: #22c55e;
      border: 1px solid rgba(34, 197, 94, 0.35);
    }
    .tier-mini-badge.pending {
      background: rgba(245, 158, 11, 0.15);
      color: #f59e0b;
      border: 1px solid rgba(245, 158, 11, 0.35);
    }
    .tier-mini-badge.denied {
      background: rgba(239, 68, 68, 0.15);
      color: #ef4444;
      border: 1px solid rgba(239, 68, 68, 0.35);
    }

    @media (max-width: 640px) {
      .tier-bar-head { display: none; }
      .tier-bar {
        grid-template-columns: 1fr 1fr;
        gap: 0.5rem 1rem;
      }
      .tier-bar-left { grid-column: 1 / -1; flex-wrap: wrap; }
      .tier-col.center { justify-content: flex-start; }
      .tier-col-label { display: inline; }
    }

    .rewards-footer-note {
      margin-top: 1rem;
      padding: 0.85rem 1rem;
      background: rgba(255,255,255,0.025);
      border: 1px solid var(--border);
      border-radius: 10px;
      font-size: 0.8rem;
      color: var(--text-dim);
      text-align: center;
    }
    .rewards-footer-note strong {
      color: #67e8f9;
      font-family: 'Space Mono', monospace;
      letter-spacing: 0.1em;
    }


    /* ================================================
       RAFFLE REEL ANIMATION
       ================================================ */
    .raffle-reel-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.88);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: 1.5rem;
      z-index: 1100;
      animation: raffleReelFadeIn 0.3s ease-out;
    }
    @keyframes raffleReelFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    .raffle-reel-title {
      font-size: 1.5rem;
      font-weight: 800;
      letter-spacing: -0.01em;
      background: linear-gradient(135deg, #22d3ee, #0ea5e9);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .raffle-reel-container {
      width: 600px;
      max-width: 95vw;
      height: 95px;
      background: linear-gradient(180deg, rgba(34, 211, 238, 0.06), rgba(0,0,0,0.7));
      border: 2px solid rgba(34, 211, 238, 0.4);
      border-radius: 16px;
      position: relative;
      overflow: hidden;
      box-shadow: 0 0 60px rgba(34, 211, 238, 0.3), inset 0 0 30px rgba(0,0,0,0.4);
    }
    .raffle-reel-strip {
      display: flex;
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      align-items: center;
      will-change: transform;
    }
    .raffle-reel-cell {
      flex: 0 0 200px;
      padding: 0 1rem;
      text-align: center;
      font-family: 'Space Mono', monospace;
      color: var(--text);
      transition: transform 0.4s ease-out;
    }
    .raffle-reel-cell.is-affiliate .raffle-reel-cell-name { color: #67e8f9; }
    .raffle-reel-cell.is-winner { transform: scale(1.18); }
    .raffle-reel-cell.is-winner .raffle-reel-cell-name {
      color: #22c55e;
      text-shadow: 0 0 18px rgba(34, 197, 94, 0.7);
      font-weight: 800;
    }
    .raffle-reel-cell-name {
      font-size: 1rem;
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .raffle-reel-cell-platform {
      font-size: 0.6rem;
      color: var(--text-muted);
      text-transform: uppercase;
      letter-spacing: 0.12em;
      margin-top: 0.2rem;
      font-weight: 700;
    }
    .raffle-reel-pointer {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 50%;
      width: 3px;
      background: linear-gradient(180deg, transparent 0%, #22d3ee 30%, #22d3ee 70%, transparent 100%);
      box-shadow: 0 0 14px #22d3ee, 0 0 28px rgba(34, 211, 238, 0.5);
      z-index: 3;
      transform: translateX(-50%);
    }
    .raffle-reel-pointer::before, .raffle-reel-pointer::after {
      content: '';
      position: absolute;
      left: 50%;
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      transform: translateX(-50%);
    }
    .raffle-reel-pointer::before { top: -1px; border-top: 8px solid #22d3ee; }
    .raffle-reel-pointer::after { bottom: -1px; border-bottom: 8px solid #22d3ee; }
    .raffle-reel-fade-left, .raffle-reel-fade-right {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 90px;
      z-index: 2;
      pointer-events: none;
    }
    .raffle-reel-fade-left { left: 0; background: linear-gradient(90deg, rgba(0,0,0,0.95), transparent); }
    .raffle-reel-fade-right { right: 0; background: linear-gradient(-90deg, rgba(0,0,0,0.95), transparent); }
    .raffle-reel-winner-card {
      padding: 1.5rem 2rem;
      background: linear-gradient(135deg, rgba(34, 197, 94, 0.18), rgba(34, 211, 238, 0.08));
      border: 2px solid rgba(34, 197, 94, 0.55);
      border-radius: 16px;
      text-align: center;
      box-shadow: 0 0 50px rgba(34, 197, 94, 0.45), inset 0 1px 0 rgba(255,255,255,0.15);
      animation: winnerPop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1);
      max-width: 95vw;
    }
    @keyframes winnerPop {
      0% { transform: scale(0.5); opacity: 0; }
      100% { transform: scale(1); opacity: 1; }
    }
    .raffle-reel-winner-eyebrow {
      font-size: 0.65rem;
      font-weight: 800;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: #22c55e;
      margin-bottom: 0.4rem;
    }
    .raffle-reel-winner-name {
      font-size: clamp(1.75rem, 4vw, 2.75rem);
      font-weight: 900;
      background: linear-gradient(135deg, #22c55e, #16a34a);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      font-family: 'Space Mono', monospace;
      letter-spacing: -0.01em;
      line-height: 1.1;
    }
    .raffle-reel-winner-meta {
      font-size: 0.85rem;
      color: var(--text-dim);
      margin-top: 0.5rem;
      letter-spacing: 0.05em;
    }
    /* Sound mute toggle */
    .sound-mute-toggle {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.45rem 0.85rem;
      background: rgba(255,255,255,0.04);
      color: var(--text-dim);
      border: 1px solid var(--border);
      border-radius: 8px;
      font-size: 0.78rem;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      transition: all 0.15s ease;
    }
    .sound-mute-toggle:hover {
      background: rgba(34, 211, 238, 0.08);
      border-color: rgba(34, 211, 238, 0.3);
      color: #67e8f9;
    }
    .sound-mute-toggle.muted {
      background: rgba(239, 68, 68, 0.08);
      border-color: rgba(239, 68, 68, 0.3);
      color: #ef4444;
    }
    .sound-mute-toggle svg { width: 16px; height: 16px; }

    .skeleton {
      height: 300px;
      background: linear-gradient(90deg, var(--bg-card) 25%, var(--bg-dark) 50%, var(--bg-card) 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
      border-radius: 16px;
    }
    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
  `;

  // Don't render while redirecting from www
  if (isWww) return null;

  const spooky = isHalloweenActive(siteSettings.halloween_theme);

  return (
    <div className={`app${spooky ? ' halloween' : ''}`}>
      <style>{styles}</style>

      {/* Background star sparkles */}
      <div className="bg-stars">
        {[
          { x: '8%', y: '12%', dur: '4s', delay: '0s' },
          { x: '15%', y: '35%', dur: '5.5s', delay: '1.2s' },
          { x: '25%', y: '8%', dur: '3.8s', delay: '0.5s' },
          { x: '35%', y: '55%', dur: '6s', delay: '2.1s' },
          { x: '45%', y: '22%', dur: '4.2s', delay: '0.8s' },
          { x: '55%', y: '70%', dur: '5s', delay: '1.5s' },
          { x: '65%', y: '15%', dur: '4.5s', delay: '0.3s' },
          { x: '72%', y: '45%', dur: '5.8s', delay: '2.5s' },
          { x: '82%', y: '28%', dur: '3.5s', delay: '1.8s' },
          { x: '90%', y: '60%', dur: '4.8s', delay: '0.7s' },
          { x: '5%', y: '75%', dur: '5.2s', delay: '1.1s' },
          { x: '42%', y: '85%', dur: '4.6s', delay: '2.3s' },
          { x: '88%', y: '8%', dur: '3.9s', delay: '0.4s' },
          { x: '20%', y: '92%', dur: '6.2s', delay: '1.9s' },
          { x: '68%', y: '88%', dur: '4.1s', delay: '0.6s' },
        ].map((s, i) => (
          <div key={i} className="bg-star" style={{ left: s.x, top: s.y, '--dur': s.dur, '--delay': s.delay }} />
        ))}
      </div>

      {/* Mobile sidebar overlay */}
      <div className={`sidebar-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={() => setMobileMenuOpen(false)} />

      {/* Sidebar Navigation */}
      <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo" onClick={() => navigateTo('leaderboard')}>
            <span className="sidebar-logo-icon"><img src="/cam-logo.jpg" alt="CamNeverWins" /></span>
            {spooky && (
              <svg className="spooky-hat" viewBox="0 0 26 20" aria-hidden="true">
                <path d="M7 15C9 9 11 3 17 1c-1.5 3-1.5 7 0 14z" fill="#1a1025" stroke="#6d28d9" strokeWidth="0.8" strokeLinejoin="round" />
                <ellipse cx="12.5" cy="15.5" rx="11.5" ry="3" fill="#1a1025" stroke="#6d28d9" strokeWidth="0.8" />
                <path d="M7.6 13.2c3 .9 6.6.9 9.6 0l.2 1.6c-3.2 1-6.8 1-10 0z" fill="#f97316" />
              </svg>
            )}
            <span className="sidebar-logo-text">CAM</span>
          </div>
          {spooky && (
            <>
              <svg className="spooky-web" viewBox="0 0 64 64" fill="none" stroke="#e9d5ff" strokeWidth="0.7" aria-hidden="true">
                <path d="M64 0L4 0M64 0L14 34M64 0L38 54M64 0L64 62" />
                <path d="M50 0c-1 5 1 7 5 9 3 2 4 5 9 5" />
                <path d="M36 0c-1 9 4 13 10 17 5 4 7 9 18 10" />
                <path d="M22 0c0 13 8 20 16 26 7 5 11 13 26 15" />
                <path d="M9 0c2 17 13 27 22 35 8 7 13 16 33 18" />
              </svg>
              <svg className="spooky-spider" viewBox="0 0 12 40" aria-hidden="true">
                <line x1="6" y1="0" x2="6" y2="27" stroke="#e9d5ff" strokeWidth="0.5" strokeOpacity="0.7" />
                <g stroke="#a78bfa" strokeWidth="0.8" strokeLinecap="round" fill="none">
                  <path d="M4.5 31l-3-2.5-1 2M4.5 32.5l-3.5.5-.5 2M7.5 31l3-2.5 1 2M7.5 32.5l3.5.5.5 2M5 34l-2.5 2.5M7 34l2.5 2.5" />
                </g>
                <ellipse cx="6" cy="29.5" rx="1.8" ry="1.6" fill="#1c1028" stroke="#a78bfa" strokeWidth="0.4" />
                <ellipse cx="6" cy="33" rx="2.6" ry="3" fill="#1c1028" stroke="#f97316" strokeWidth="0.6" />
              </svg>
            </>
          )}
        </div>

        {/* Join Rainbet CTA */}
        <div className="sidebar-cta">
          <a href={affiliateLink} target="_blank" rel="noopener noreferrer" className="sidebar-cta-btn">
            <span className="sidebar-cta-icon">
              {/* Custom gem/diamond — matches the neon casino theme without being a slot/dice cliché */}
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <defs>
                  <linearGradient id="gemFill" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95"/>
                    <stop offset="60%" stopColor="#e0f2fe" stopOpacity="0.9"/>
                    <stop offset="100%" stopColor="#bae6fd" stopOpacity="0.7"/>
                  </linearGradient>
                </defs>
                {/* Outer gem outline — six-faceted diamond */}
                <path d="M6 9l6-6 6 6-6 12z" fill="url(#gemFill)" stroke="#fff" strokeWidth="0.6" strokeLinejoin="round"/>
                {/* Inner facet lines for depth */}
                <path d="M6 9h12M12 3l-3 6 3 12 3-12-3-6" stroke="#0a0a0f" strokeWidth="0.6" strokeOpacity="0.35" strokeLinejoin="round"/>
                {/* Specular highlight */}
                <path d="M9 5l1 3-1 1z" fill="#fff" fillOpacity="0.95"/>
              </svg>
            </span>
            <span className="sidebar-cta-text">Join Rainbet</span>
            <span className="sidebar-cta-shine" aria-hidden="true" />
          </a>
          <a href={affiliateLinkUs} target="_blank" rel="noopener noreferrer" className="rb-us-link" style={{ marginTop: '0.3rem' }}>
            In the US? Join <strong>Rainbet.us</strong>
          </a>
        </div>

        {/* Total Given Away counter — animated count-up on first load */}
        {Number(siteSettings.total_given_away || 0) > 0 && (
          <div className="sidebar-giveaway">
            <div className="sidebar-giveaway-label">Total Given Away</div>
            <div className="sidebar-giveaway-amount">
              <span className="sidebar-giveaway-currency">$</span>{Number(animatedGiveaway).toLocaleString()}+
            </div>
            <div className="sidebar-giveaway-pulse" aria-hidden="true" />
          </div>
        )}

        {/* Signup Checklist button */}
        {user && checklistData && !checklistData.adminPaid && (
          <button
            onClick={() => setShowChecklist(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%',
              padding: '0.45rem 0.85rem', margin: '0.25rem 0',
              background: checklistData.allComplete && !checklistData.rewardClaimed
                ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(245, 158, 11, 0.1))'
                : 'rgba(34, 211, 238, 0.06)',
              border: checklistData.allComplete && !checklistData.rewardClaimed
                ? '1px solid rgba(251, 191, 36, 0.35)'
                : '1px solid rgba(34, 211, 238, 0.15)',
              borderRadius: '10px', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text)',
              transition: 'all 0.2s',
              animation: checklistData.allComplete && !checklistData.rewardClaimed ? 'ctaIconFloat 2s ease-in-out infinite' : 'none'
            }}
          >
            <span style={{ fontSize: '1.2rem' }}>🎁</span>
            <div style={{ textAlign: 'left', flex: 1 }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700 }}>
                {checklistData.rewardClaimed ? 'Reward Claimed!' : checklistData.allComplete ? 'Claim Your Free $3!' : 'Free $3 Checklist'}
              </div>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                {checklistData.completedSteps}/{checklistData.totalSteps} steps done
              </div>
            </div>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: `conic-gradient(#22d3ee ${(checklistData.completedSteps / checklistData.totalSteps) * 360}deg, rgba(255,255,255,0.06) 0deg)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', fontWeight: 800, color: '#22d3ee' }}>
                {checklistData.allComplete ? '✓' : `${checklistData.completedSteps}`}
              </div>
            </div>
          </button>
        )}

        <nav className="sidebar-nav">
          {/* Main Navigation */}
          {(() => {
            // Parse hidden-pages list from site settings. Comma/newline separated list of page keys.
            const hiddenList = String(siteSettings.hidden_nav_pages || '')
              .split(/[\s,]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
            const isHidden = (key) => hiddenList.includes(key.toLowerCase());
            const isAdminUser = !!user?.isAdmin;
            // Render a nav button. Non-admins skip hidden ones entirely. Admins
            // still see them with a HIDDEN pill so they can test/preview.
            const NavBtn = ({ pageKey, icon, label, extra, badge }) => {
              const hidden = isHidden(pageKey);
              if (hidden && !isAdminUser) return null;
              return (
                <button
                  className={`nav-item ${activePage === pageKey ? 'active' : ''}`}
                  onClick={() => navigateTo(pageKey)}
                  title={hidden ? 'This tab is hidden from non-admin users' : undefined}
                  style={hidden ? { opacity: 0.6 } : undefined}
                >
                  <span className="nav-item-icon">{icon}</span>
                  {label}
                  {hidden && (
                    <span style={{ marginLeft: 'auto', padding: '0.1rem 0.45rem', background: 'rgba(245, 158, 11, 0.18)', color: '#f59e0b', borderRadius: '999px', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.08em', border: '1px solid rgba(245, 158, 11, 0.35)' }}>
                      HIDDEN
                    </span>
                  )}
                  {!hidden && badge}
                  {extra}
                </button>
              );
            };

            return (
              <>
                {/* Two balanced groups: site basics vs live-on-stream features.
                    Tighter item density (see .nav-item CSS) keeps both visible
                    without scrolling on typical viewports. */}
                <div className="nav-section">
                  <div className="nav-section-title">Main</div>
                  {NavBtn({ pageKey: 'home', icon: navIcons.home, label: 'Home' })}
                  {NavBtn({ pageKey: 'leaderboard', icon: navIcons.leaderboard, label: 'Leaderboard' })}
                  {NavBtn({ pageKey: 'rewards', icon: navIcons.rewards, label: 'Rewards' })}
                  {NavBtn({ pageKey: 'instructions', icon: navIcons.instructions, label: 'Instructions' })}
                  {NavBtn({
                    pageKey: 'pointsshop',
                    icon: navIcons.points,
                    label: 'Points Shop',
                    extra: user && pointsBalance != null && !isHidden('pointsshop') ? (
                      <span style={{ marginLeft: 'auto', padding: '0.15rem 0.5rem', background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 700 }}>
                        {pointsBalance}
                      </span>
                    ) : null
                  })}
                  {NavBtn({ pageKey: 'socials', icon: navIcons.socials, label: 'Socials' })}
                </div>
                <div className="nav-section">
                  {/* Order is deliberate (user-specified): most-used stream
                      features first, low-traffic tabs at the bottom */}
                  <div className="nav-section-title">Stream</div>
                  {NavBtn({
                    pageKey: 'predictions',
                    icon: navIcons.predictions,
                    label: 'Predictions',
                    badge: activeHuntData?.predictionsOpen ? <span className="nav-item-badge live">LIVE</span> : null
                  })}
                  {NavBtn({ pageKey: 'wagerraffle', icon: '🎟️', label: 'Raffle' })}
                  {NavBtn({ pageKey: 'bingo', icon: '🎯', label: 'Bingo', badge: bingoState && bingoState.status && bingoState.status !== 'idle' ? <span className="nav-item-badge" style={{ background: 'rgba(34,197,94,0.18)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.4)' }}>LIVE</span> : null })}
                  {NavBtn({ pageKey: 'battles', icon: navIcons.battles, label: 'Battles', badge: battleState && battleState.status && battleState.status !== 'idle' ? <span className="nav-item-badge" style={{ background: 'rgba(251,113,133,0.18)', color: '#fb7185', border: '1px solid rgba(251,113,133,0.4)' }}>LIVE</span> : null })}
                  {NavBtn({ pageKey: 'games', icon: navIcons.games, label: 'Games' })}
                  {NavBtn({ pageKey: 'challenges', icon: navIcons.challenges, label: 'Challenges' })}
                  {NavBtn({ pageKey: 'bonushunts', icon: navIcons.bonushunts, label: 'Bonus Hunts' })}
                  {NavBtn({ pageKey: 'slotrequests', icon: navIcons.slotrequests, label: 'Slot Requests' })}
                </div>
              </>
            );
          })()}

          {/* Admin Section - Only visible to admins */}
          {user?.isAdmin && (
            <div className="nav-section">
              <div className="nav-section-title">Admin</div>
              <button className={`nav-item admin-item ${activePage === 'admin' ? 'active' : ''}`} onClick={() => navigateTo('admin')}>
                <span className="nav-item-icon">{navIcons.admin}</span>
                Admin Panel
                {(() => {
                  const total = (adminStats?.pendingClaims || 0)
                    + (adminStats?.pendingGiveaways || 0)
                    + (adminStats?.unclaimedWins || 0)
                    + (adminStats?.pendingChallengeClaims || 0);
                  if (total === 0) return null;
                  return (
                    <span className="nav-item-badge" title={`${adminStats.pendingClaims || 0} prediction claims · ${adminStats.pendingGiveaways || 0} giveaways · ${adminStats.unclaimedWins || 0} unclaimed wins · ${adminStats.pendingChallengeClaims || 0} challenge claims`}>
                      {total > 99 ? '99+' : total}
                    </span>
                  );
                })()}
              </button>
            </div>
          )}
        </nav>

        {/* Social Links Footer */}
        <div className="sidebar-footer">
          <div className="sidebar-socials">
            {socialLinks.map(link => (
              <a key={link.name} href={link.url} target="_blank" rel="noopener noreferrer" className="sidebar-social-link" data-platform={link.icon} title={link.name}>
                {socialIcons[link.icon]}
              </a>
            ))}
          </div>
        </div>
      </aside>

      {/* Mobile sidebar toggle button */}
      <button className="mobile-sidebar-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
        {mobileMenuOpen ? '✕' : '☰'}
      </button>

      {/* Main Content Area */}
      <div className="main-content">
        {/* Top Header with User Login */}
        <header className="top-header">
          <div className="header-spacer"></div>

          {user?.isAdmin && (
            <button
              onClick={() => navigateTo('rafflebot')}
              className={`top-raffle-btn ${activePage === 'rafflebot' ? 'active' : ''}`}
              title="Raffle Bot"
            >
              <span className="top-raffle-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="10" rx="2"/>
                  <circle cx="12" cy="5" r="2"/>
                  <path d="M12 7v4"/>
                  <line x1="8" y1="16" x2="8" y2="16"/>
                  <line x1="16" y1="16" x2="16" y2="16"/>
                </svg>
              </span>
              <span>Raffle Bot</span>
              {botActiveRaffle?.active && (
                <span className="top-raffle-live" aria-label="Active raffle">
                  <span className="top-raffle-live-dot" />
                  LIVE
                </span>
              )}
            </button>
          )}

          {/* Prize Portal pill */}
          <button
            className="top-points-pill"
            onClick={() => navigateTo('prizes')}
            title="Claim stream contest prizes"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: activePage === 'prizes' ? 'rgba(251, 191, 36, 0.15)' : undefined, borderColor: activePage === 'prizes' ? 'rgba(251, 191, 36, 0.4)' : undefined }}
          >
            <span style={{ fontSize: '1rem', lineHeight: 1 }}>🏆</span>
            <span className="top-points-label" style={{ color: '#fbbf24', fontWeight: 700 }}>Claim Prizes</span>
          </button>

          {/* Points balance pill — only when logged in */}
          {user && pointsBalance != null && (
            <button
              className="top-points-pill"
              onClick={() => navigateTo('pointsshop')}
              title={`${pointsBalance} points · click to open Points Shop`}
            >
              <span className="top-points-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="none">
                  {/* Faceted diamond / gem */}
                  <defs>
                    <linearGradient id="topPointsDiamondGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#67e8f9"/>
                      <stop offset="50%" stopColor="#22d3ee"/>
                      <stop offset="100%" stopColor="#0ea5e9"/>
                    </linearGradient>
                  </defs>
                  <path d="M6 3h12l4 6-10 12L2 9z" fill="url(#topPointsDiamondGrad)" stroke="#bef0ff" strokeWidth="0.6" strokeLinejoin="round"/>
                  {/* Interior facet lines */}
                  <path d="M6 3l6 6 6-6M2 9h20M12 9l-3 12M12 9l3 12" stroke="rgba(255,255,255,0.45)" strokeWidth="0.6" fill="none" strokeLinejoin="round"/>
                  {/* Specular highlight */}
                  <path d="M8 4.5l3 3.5" stroke="rgba(255,255,255,0.85)" strokeWidth="0.7" strokeLinecap="round" fill="none"/>
                </svg>
              </span>
              <span className="top-points-value">{Number(pointsBalance).toLocaleString()}</span>
              <span className="top-points-label">pts</span>
            </button>
          )}

          {authLoading ? (
            <div style={{ padding: '0.5rem 1rem', color: 'var(--text-dim)', fontSize: '0.9rem' }}>
              Loading...
            </div>
          ) : user ? (
            <div className="user-dropdown">
              <button className="user-dropdown-trigger" onClick={() => setUserDropdownOpen(!userDropdownOpen)}>
                <img src={user.profileImage || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} alt="" />
                <span>{user.displayName}</span>
                <span style={{ fontSize: '0.7rem', marginLeft: '0.25rem' }}>▼</span>
              </button>
              {userDropdownOpen && (
                <div className="user-dropdown-menu">
                  <button className="dropdown-item" onClick={() => { navigateTo('profile'); setUserDropdownOpen(false); }}>
                    👤 My Profile
                  </button>
                  <button className="dropdown-item" onClick={() => { navigateTo('mypredictions'); setUserDropdownOpen(false); }}>
                    🔮 My Predictions
                  </button>
                  {user.isAdmin && (
                    <button className="dropdown-item admin" onClick={() => { navigateTo('rafflebot'); setUserDropdownOpen(false); }}>
                      🤖 Raffle Bot
                    </button>
                  )}
                  {user.isAdmin && (
                    <button className="dropdown-item admin" onClick={() => { navigateTo('admin'); setUserDropdownOpen(false); }}>
                      ⚙️ Admin Panel
                    </button>
                  )}
                  <hr className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { handleLogout(); setUserDropdownOpen(false); }}>
                    🚪 Logout
                  </button>
                </div>
              )}
            </div>
          ) : null}

          {!user && !authLoading && (
            <div className="login-buttons">
              <button className="kick-login-btn" onClick={handleKickLogin}>
                <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
                  <path d="M2 2h5v6h2V6h2V4h2V2h5v4h-2v2h-2v2h-2v4h2v2h2v2h2v4h-5v-2h-2v-2H9v2H7v2H2V2z"/>
                </svg>
                Login with Kick
              </button>
              <button className="twitch-login-btn" onClick={handleLogin}>
                {socialIcons.twitch}
                Login with Twitch
              </button>
            </div>
          )}
        </header>

        <main className="main">

        {/* Ambient backdrop + cursor sparkle trail — every page, rendered once */}
        <AmbientBackdrop spooky={spooky} />

        {/* ============================================ */}
        {/* HOME PAGE                                     */}
        {/* ============================================ */}
        {activePage === 'home' && (() => {
          const top3 = boardRows.slice(0, 3);

          return (
            <>
              {/* === HERO SECTION === */}
              {/* No overflow clipping and no edged panel — and both washes are
                  sized to reach zero alpha BEFORE the top edge, so the scroll
                  container can't clip them into a visible brightness line */}
              <div style={{
                position: 'relative',
                textAlign: 'center',
                padding: '2.5rem 1.5rem 2rem',
                background: 'radial-gradient(300px 190px at 50% 235px, rgba(34, 211, 238, 0.10), transparent 74%), radial-gradient(ellipse 62% 45% at 50% 46%, rgba(14, 165, 233, 0.06), transparent 74%)',
                marginBottom: '2rem'
              }}>

                <div style={{ position: 'relative', zIndex: 1 }}>

                  {/* Branded partnership header — CAM × Rainbet */}
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.85rem', padding: '0.65rem 1.5rem', background: 'rgba(14, 22, 43, 0.6)', border: '1px solid rgba(34, 211, 238, 0.2)', borderRadius: '14px', marginBottom: '1.25rem', backdropFilter: 'blur(8px)' }}>
                    <img src="/cam-logo.jpg" alt="Cam" style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid rgba(34, 211, 238, 0.4)' }} onError={(e) => { e.target.style.display = 'none'; }} />
                    <span style={{ fontSize: '1.1rem', fontWeight: 800, letterSpacing: '0.02em' }}>CAM</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '1rem' }}>×</span>
                    <img src="https://rainbetplay.com/wp-content/uploads/2025/11/rainbet.webp" alt="Rainbet" style={{ height: 22 }} />
                  </div>

                  {/* Leaderboard label — tracks the active monthly/weekly view */}
                  <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.2em', color: '#fbbf24', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
                    {effectiveType === 'weekly' ? 'Weekly' : 'Monthly'} Leaderboard
                  </div>

                  {/* Lead-in to the prize amount — the eyebrow above already names the leaderboard */}
                  <h1 className="display-font" style={{ fontSize: 'clamp(1.4rem, 3.5vw, 2rem)', margin: '0 0 0.75rem 0', color: '#e5e7eb', letterSpacing: '0.02em', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                    Wager. Climb. Win.
                  </h1>

                  <div className="gold-3d" style={{ fontSize: 'clamp(3.2rem, 9vw, 5.75rem)', lineHeight: 1, marginBottom: '0.5rem', letterSpacing: '0.02em', visibility: dbPeriods === null ? 'hidden' : 'visible' }}>
                    ${displayPeriod.totalPrize?.toLocaleString() || '25,000'}
                  </div>

                  {/* Date range pill */}
                  <div style={{ display: 'inline-block', padding: '0.35rem 1rem', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '999px', fontSize: '0.8rem', fontFamily: 'Space Mono, monospace', color: 'var(--text-dim)', marginBottom: '1rem', visibility: dbPeriods === null ? 'hidden' : 'visible' }}>
                    {fmtDateCT(displayPeriod.start, { year: 'numeric', month: '2-digit', day: '2-digit' })} — {fmtDateCT(displayPeriod.end, { year: 'numeric', month: '2-digit', day: '2-digit' })}
                  </div>

                  {boardPreview && (
                    <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.18em', color: '#22d3ee', textTransform: 'uppercase', marginBottom: '0.45rem' }}>⏳ Starts In</div>
                  )}
                  {weeklyEnded && (
                    <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.18em', color: '#22d3ee', textTransform: 'uppercase', marginBottom: '1.25rem' }}>🏁 Final Results · Next week starts soon</div>
                  )}
                  {/* Live-ticking timer */}
                  <div style={{ display: weeklyEnded ? 'none' : 'flex', justifyContent: 'center', gap: '0.15rem', alignItems: 'center', marginBottom: '1.25rem' }}>
                    {[
                      { val: String(countdown.days).padStart(2, '0'), label: 'DAYS' },
                      { val: String(countdown.hours).padStart(2, '0'), label: 'HOURS' },
                      { val: String(countdown.minutes).padStart(2, '0'), label: 'MINUTES' },
                      { val: String(countdown.seconds).padStart(2, '0'), label: 'SECONDS' }
                    ].map((t, i) => (
                      <React.Fragment key={t.label}>
                        {i > 0 && <span style={{ color: 'var(--text-muted)', fontWeight: 700, fontSize: '1.2rem', margin: '0 0.25rem' }}>:</span>}
                        <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', padding: '0.5rem 0.75rem', background: 'rgba(6, 10, 24, 0.55)', border: '1px solid rgba(251, 191, 36, 0.28)', borderRadius: '10px', minWidth: 55, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)' }}>
                          <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '1.5rem', fontWeight: 800, color: '#e5e7eb' }}>{t.val}</span>
                          <span style={{ fontSize: '0.5rem', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.1em', marginTop: '0.15rem' }}>{t.label}</span>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>

                  {/* CTA buttons */}
                  <div style={{ display: 'flex', gap: '0.85rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <a
                      href={affiliateLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ padding: '0.85rem 2rem', background: 'linear-gradient(135deg, #22d3ee, #0ea5e9)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 6px 24px rgba(34, 211, 238, 0.35)', textDecoration: 'none', display: 'inline-block', transition: 'transform 0.12s ease-out, box-shadow 0.2s' }}
                      onMouseMove={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        const mx = ((e.clientX - r.left) / r.width - 0.5) * 8;
                        const my = ((e.clientY - r.top) / r.height - 0.5) * 6;
                        e.currentTarget.style.transform = `translate(${mx.toFixed(1)}px, ${my.toFixed(1)}px) scale(1.03)`;
                      }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translate(0, 0) scale(1)'; }}
                    >
                      Join with Code CAM
                    </a>
                    <button
                      onClick={() => navigateTo('leaderboard')}
                      style={{ padding: '0.85rem 2rem', background: 'rgba(6, 10, 24, 0.55)', color: '#e5e7eb', border: '1px solid rgba(251, 191, 36, 0.35)', borderRadius: '12px', fontSize: '1rem', fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', transition: 'transform 0.12s ease-out, border-color 0.2s, background 0.2s' }}
                      onMouseMove={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        const mx = ((e.clientX - r.left) / r.width - 0.5) * 8;
                        const my = ((e.clientY - r.top) / r.height - 0.5) * 6;
                        e.currentTarget.style.transform = `translate(${mx.toFixed(1)}px, ${my.toFixed(1)}px) scale(1.03)`;
                      }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translate(0, 0) scale(1)'; }}
                    >
                      View Leaderboard
                    </button>
                  </div>
                  <a href={affiliateLinkUs} target="_blank" rel="noopener noreferrer" className="rb-us-link" style={{ marginTop: '0.7rem', fontSize: '0.78rem' }}>
                    Playing from the US? Sign up on <strong>Rainbet.us</strong> with code CAM →
                  </a>

                  {/* === TOP 3 PODIUM — enhanced === */}
                  {top3.length >= 3 && (() => {
                    const podiumData = [
                      { idx: 1, order: 1, color: '#a78bfa', tilt: -5, ribbonA: '#94a3b8', ribbonB: '#64748b', medalBg: 'linear-gradient(135deg, #f1f5f9, #8a8a8a)', frame: 'linear-gradient(160deg, #f8fafc, #94a3b8 30%, #e2e8f0 50%, #64748b 75%, #cbd5e1)', w: 'clamp(160px, 24vw, 225px)', py: '2rem', shift: 14, glow: 'rgba(203, 213, 225, 0.18)' },
                      { idx: 0, order: 2, color: '#22d3ee', tilt: 0, ribbonA: '#fbbf24', ribbonB: '#d97706', medalBg: 'linear-gradient(135deg, #fde68a, #d97706)', frame: 'linear-gradient(160deg, #fde68a, #d97706 30%, #fbbf24 50%, #92400e 75%, #fcd34d)', w: 'clamp(185px, 28vw, 260px)', py: '2.5rem', shift: 0, glow: 'rgba(251, 191, 36, 0.28)', isFirst: true },
                      { idx: 2, order: 3, color: '#f472b6', tilt: 5, ribbonA: '#f97316', ribbonB: '#c2410c', medalBg: 'linear-gradient(135deg, #e8a06a, #8a4a1c)', frame: 'linear-gradient(160deg, #f0b27a, #92400e 30%, #c2703d 50%, #5e3010 75%, #e8a06a)', w: 'clamp(160px, 24vw, 225px)', py: '2rem', shift: 14, glow: 'rgba(217, 119, 6, 0.18)' }
                    ];
                    return (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 'clamp(0.5rem, 2vw, 1.25rem)', marginTop: '2.5rem', cursor: 'pointer' }} onClick={() => navigateTo('leaderboard')}>
                        {podiumData.map(p => {
                          const player = top3[p.idx];
                          const rank = p.idx + 1;
                          // activePrizes tracks the board being shown; `prizes` is always the monthly side
                          const prize = activePrizes[p.idx]?.prize;
                          return (
                            <div key={rank} style={{
                              width: p.w,
                              padding: `${p.py} 1rem 1.25rem`,
                              background: `linear-gradient(180deg, rgba(15, 15, 30, 0.97), rgba(8, 8, 20, 0.99)) padding-box, ${p.frame} border-box`,
                              border: '3px solid transparent',
                              borderRadius: p.isFirst ? '20px' : '16px',
                              textAlign: 'center',
                              position: 'relative',
                              transform: `translateY(${p.shift}px) rotate(${p.tilt}deg)`,
                              boxShadow: `0 10px 40px ${p.glow}, inset 0 1px 0 rgba(255,255,255,0.04)`,
                              zIndex: p.isFirst ? 2 : 1,
                              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.transition = 'transform 0.08s ease-out, box-shadow 0.3s ease'; e.currentTarget.style.boxShadow = `0 14px 50px ${p.glow}, inset 0 1px 0 rgba(255,255,255,0.06)`; }}
                            onMouseMove={(e) => {
                              const r = e.currentTarget.getBoundingClientRect();
                              const rx = ((e.clientY - r.top) / r.height - 0.5) * -8;
                              const ry = ((e.clientX - r.left) / r.width - 0.5) * 8;
                              e.currentTarget.style.transform = `perspective(700px) translateY(${p.shift - 4}px) rotateZ(${p.tilt}deg) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg)`;
                            }}
                            onMouseLeave={(e) => { e.currentTarget.style.transition = 'transform 0.35s ease, box-shadow 0.3s ease'; e.currentTarget.style.transform = `translateY(${p.shift}px) rotate(${p.tilt}deg)`; e.currentTarget.style.boxShadow = `0 10px 40px ${p.glow}, inset 0 1px 0 rgba(255,255,255,0.04)`; }}
                            >
                              {/* Hanging ribbon medal — two ribbon strips + metal-ringed medal with Rainbet R */}
                              <div style={{ position: 'absolute', top: p.isFirst ? '-38px' : '-30px', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                {/* Crown for 1st place */}
                                {p.isFirst && (
                                  <div style={{ fontSize: '1.6rem', marginBottom: '-2px', filter: 'drop-shadow(0 0 10px rgba(251, 191, 36, 0.6))', animation: 'ctaIconFloat 3s ease-in-out infinite', zIndex: 2 }}>👑</div>
                                )}
                                <div style={{ position: 'relative', width: p.isFirst ? 64 : 56, height: p.isFirst ? 74 : 64 }}>
                                  {/* Ribbon strips */}
                                  <div style={{ position: 'absolute', top: 0, left: p.isFirst ? 10 : 8, width: p.isFirst ? 18 : 16, height: p.isFirst ? 42 : 36, background: `linear-gradient(180deg, ${p.ribbonA}, ${p.ribbonB})`, transform: 'rotate(-18deg)', transformOrigin: 'top center', borderRadius: '3px', boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }} />
                                  <div style={{ position: 'absolute', top: 0, right: p.isFirst ? 10 : 8, width: p.isFirst ? 18 : 16, height: p.isFirst ? 42 : 36, background: `linear-gradient(180deg, ${p.ribbonB}, ${p.ribbonA})`, transform: 'rotate(18deg)', transformOrigin: 'top center', borderRadius: '3px', boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }} />
                                  {/* Medal: dark face, metallic ring */}
                                  <div style={{
                                    position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)',
                                    width: p.isFirst ? 54 : 46, height: p.isFirst ? 54 : 46, borderRadius: '50%',
                                    background: `linear-gradient(180deg, #161d33, #0b101f) padding-box, ${p.medalBg} border-box`,
                                    border: '3px solid transparent',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: `0 4px 16px rgba(0,0,0,0.55), 0 0 18px ${p.glow}`
                                  }}>
                                    <img src="/rainbet-icon.png" alt="" style={{ width: p.isFirst ? 40 : 34, height: p.isFirst ? 40 : 34, borderRadius: '50%' }} onError={(e) => { if (!e.target.src.endsWith('/cam-logo.jpg')) e.target.src = '/cam-logo.jpg'; else e.target.style.display = 'none'; }} />
                                  </div>
                                  {/* Rank badge pinned to medal bottom */}
                                  <div style={{
                                    position: 'absolute', bottom: -8, left: '50%', transform: 'translateX(-50%)',
                                    width: 22, height: 22, borderRadius: '50%', background: p.medalBg,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.65rem', fontWeight: 900, color: '#101010',
                                    border: '2px solid rgba(0,0,0,0.35)', zIndex: 1, boxShadow: '0 2px 6px rgba(0,0,0,0.5)'
                                  }}>{rank}</div>
                                </div>
                              </div>

                              {/* Username */}
                              <div style={{ marginTop: p.isFirst ? '3.1rem' : '2.5rem', fontWeight: 800, fontSize: p.isFirst ? 'clamp(0.85rem, 1.8vw, 1.05rem)' : 'clamp(0.75rem, 1.5vw, 0.92rem)', letterSpacing: '0.02em', color: player._preview ? 'var(--text-muted)' : undefined }}>{player._preview ? 'TBD' : censorUser(player.username)}{!player._preview && <UsSiteChip site={player.site} />}</div>
                              {/* Wagered */}
                              <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)', letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: '0.35rem' }}>{player._preview ? 'PRIZE' : 'WAGERED'}</div>
                              <div style={{ fontFamily: 'Space Mono, monospace', fontWeight: 800, color: p.color, fontSize: p.isFirst ? 'clamp(1rem, 2.2vw, 1.35rem)' : 'clamp(0.85rem, 1.8vw, 1.1rem)', marginTop: '0.15rem' }}>{player._preview ? '—' : formatNum(player.wagers)}</div>
                              {/* Divider + Prize */}
                              <div style={{ width: '60%', height: '1.5px', background: `linear-gradient(90deg, transparent, ${p.color}55, transparent)`, margin: '0.65rem auto 0.5rem' }} />
                              <div style={{ fontWeight: 800, fontSize: p.isFirst ? '1.05rem' : '0.88rem', color: p.color }}>${prize?.toLocaleString() || '—'}</div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                </div>
              </div>

              {/* === EXCLUSIVE CAM REWARDS + TOTAL GIVEN AWAY === */}
              <div style={{ marginBottom: '2rem' }}>
                <h2 className="display-font" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.25rem)', marginBottom: '0.5rem', letterSpacing: '0.02em' }}>
                  <span style={{ textDecoration: 'underline', textUnderlineOffset: '6px', textDecorationColor: 'rgba(34, 211, 238, 0.4)' }}>EXCLUSIVE</span>{' '}
                  <span style={{ color: '#22d3ee' }}>CAM</span>{' '}
                  <span>REWARDS</span>
                </h2>
                <p style={{ fontSize: '0.95rem', color: 'var(--text-dim)', marginBottom: '0.3rem' }}>
                  Unlock exclusive rewards and perks when you sign up <strong style={{ color: '#e5e7eb' }}>with code CAM on Rainbet.</strong>
                </p>
                <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                  Rewards grow EVERY MONTH as the community grows. Win via Leaderboard, Rewards, and Giveaways.
                </p>

                {/* Total Given Away digit counter + live stat rail filling the empty right half */}
                {Number(siteSettings.total_given_away || 0) > 0 && (() => {
                  const total = Number(siteSettings.total_given_away || 0);
                  const formatted = total.toLocaleString();
                  const digits = formatted.split('');
                  return (
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'stretch', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                      <div style={{ padding: '1.25rem 2rem', background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(34, 211, 238, 0.15)', borderRadius: '16px' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.14em', color: '#22d3ee', textTransform: 'uppercase', marginBottom: '0.65rem' }}>Total Given Away</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                          <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '1.75rem', fontWeight: 800, color: '#e5e7eb', marginRight: '0.25rem' }}>$</span>
                          {digits.map((d, i) => (
                            d === ',' ? (
                              <span key={i} style={{ fontFamily: 'Space Mono, monospace', fontSize: '1.75rem', fontWeight: 800, color: '#e5e7eb', margin: '0 0.1rem' }}>,</span>
                            ) : (
                              <span key={i} style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '2.2rem',
                                height: '2.8rem',
                                background: 'linear-gradient(135deg, rgba(14, 44, 82, 0.9), rgba(8, 26, 56, 0.95))',
                                border: '1px solid rgba(34, 211, 238, 0.25)',
                                borderRadius: '8px',
                                fontFamily: 'Space Mono, monospace',
                                fontSize: '1.75rem',
                                fontWeight: 800,
                                color: '#22d3ee'
                              }}>{d}</span>
                            )
                          ))}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '0.5rem' }}>Through leaderboards, challenges & rewards</div>
                      </div>
                      <div style={{ flex: '1 1 220px', minWidth: 220, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.5rem', padding: '1rem 1.25rem', background: 'rgba(0,0,0,0.25)', border: '1px solid var(--border)', borderRadius: '16px' }}>
                        {[
                          monthlyViewable && currentPeriod?.totalPrize > 0 && { icon: '🏆', label: 'Monthly leaderboard', value: `$${currentPeriod.totalPrize.toLocaleString()}`, color: '#22d3ee' },
                          weeklyPeriod?.totalPrize > 0 && { icon: '📅', label: 'Weekly leaderboard', value: `$${weeklyPeriod.totalPrize.toLocaleString()}`, color: '#22d3ee' },
                          activeWagerRaffle && { icon: '🎟️', label: 'Wager raffle live now', value: `$${Number(activeWagerRaffle.prize_amount).toLocaleString()}`, color: '#fbbf24' },
                          monthlyMilestoneTotal > 0 && { icon: '🚀', label: 'Monthly wager milestones', value: `up to $${monthlyMilestoneTotal.toLocaleString()}`, color: '#a78bfa' },
                        ].filter(Boolean).map((s) => (
                          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                            <span style={{ fontSize: '1rem' }}>{s.icon}</span>
                            <span style={{ flex: 1, fontSize: '0.8rem', color: 'var(--text-dim)' }}>{s.label}</span>
                            <span style={{ fontFamily: 'Space Mono, monospace', fontWeight: 800, fontSize: '0.9rem', color: s.color }}>{s.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* === POINTS EARNING CARD === */}
              <div className="card" style={{ padding: '1.75rem', marginBottom: '1.5rem', background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.06) 0%, rgba(34, 211, 238, 0.04) 100%)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
                  <div style={{ fontSize: '1.35rem', fontWeight: 900, marginBottom: '0.3rem' }}>
                    Earn <span style={{ color: '#fbbf24' }}>POINTS</span> just by watching Cam on <span style={{ color: '#53fc18' }}>KICK</span> and <span style={{ color: '#a78bfa' }}>Twitch</span>
                  </div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-dim)' }}>
                    Redeem for <strong style={{ color: '#e5e7eb' }}>AWESOME rewards</strong> in the Points Shop!
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.85rem', marginBottom: '1.25rem' }}>
                  <div style={{ padding: '1rem', background: 'rgba(0,0,0,0.25)', borderRadius: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>1️⃣</div>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: '0.2rem' }}>Sign in with Kick or Twitch</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Click "Join Rainbet" or the login buttons above</div>
                  </div>
                  <div style={{ padding: '1rem', background: 'rgba(0,0,0,0.25)', borderRadius: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>2️⃣</div>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: '0.2rem' }}>Follow Cam on stream</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Chat during live streams to earn points</div>
                  </div>
                  <div style={{ padding: '1rem', background: 'rgba(0,0,0,0.25)', borderRadius: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>3️⃣</div>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: '0.2rem' }}>Use code CAM on Rainbet</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>(Optional) Earn bonus points + leaderboard eligibility</div>
                  </div>
                </div>

                <div style={{ textAlign: 'center' }}>
                  <button onClick={() => { if (!user) { handleLogin(); } else { navigateTo('pointsshop'); } }} style={{ padding: '0.75rem 1.75rem', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '0.92rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(245, 158, 11, 0.35)' }}>
                    {user ? 'Browse Rewards →' : 'Sign In to Start Earning →'}
                  </button>
                </div>
              </div>

              {/* === BONUS CARDS (3-column) === */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>

                {/* Lossback */}
                <div className="card" style={{ padding: '1.5rem', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>💸</div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 800, marginBottom: '0.35rem' }}>
                    Claim up to <span style={{ color: '#22c55e' }}>10% Lossback</span>
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', lineHeight: 1.5, marginBottom: '0.5rem' }}>
                    Request your previous month's profit & loss report from Rainbet live chat support. Upload a screenshot to claim.
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#fbbf24', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    ⏰ Claim window: 1st – 7th of each month. Miss it and you can't claim.
                  </div>
                  <button onClick={() => { if (!user) { handleLogin(); return; } setClaimModal({ type: 'lossback' }); setClaimScreenshot(null); setClaimNotes(''); }} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.2rem', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)' }}>
                    Claim Now →
                  </button>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.6rem' }}>Processing: 2-3 business days</div>
                </div>

                {/* Weekly Doubles */}
                <div className="card" style={{ padding: '1.5rem', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🔔</div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 800, marginBottom: '0.35rem' }}>
                    <span style={{ color: '#3b82f6' }}>Double</span> Weeklies & Monthlies
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', lineHeight: 1.5, marginBottom: '1rem' }}>
                    Using code CAM? Claim a match on your weekly rewards! Screenshot your weekly reward notification (🔔 bell icon, top right on Rainbet). Must be claimed in the same week.
                  </div>
                  <button onClick={() => { if (!user) { handleLogin(); return; } setClaimModal({ type: 'weekly_monthly' }); setClaimScreenshot(null); setClaimNotes(''); }} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.2rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }}>
                    Claim Now →
                  </button>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.6rem' }}>Processing: 2-3 business days</div>
                </div>

                {/* Monthly Wager Milestones */}
                <div className="card" style={{ padding: '1.5rem', border: '1px solid rgba(139, 92, 246, 0.2)' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🚀</div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 800, marginBottom: '0.35rem' }}>
                    Monthly <span style={{ color: '#a78bfa' }}>Wager Milestones</span>
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', lineHeight: 1.5, marginBottom: '0.5rem' }}>
                    Get EVEN more rewards with monthly wager milestones that reset so you can earn month after month! Claim up to <strong style={{ color: '#22c55e' }}>${monthlyMilestoneTotal.toLocaleString()}</strong> in rewards.
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginBottom: '1rem' }}>
                    Track your progress and claim rewards on the Rewards page. Admins verify your wager automatically.
                  </div>
                  <button onClick={() => navigateTo('rewards')} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.2rem', background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)' }}>
                    View Rewards →
                  </button>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.6rem' }}>Processing: 2-3 business days</div>
                </div>
              </div>

              {/* === WATCH LIVE === */}
              <div style={{ marginBottom: '2rem' }}>
                <h2 className="display-font" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.25rem)', marginBottom: '1.25rem', letterSpacing: '0.02em' }}>
                  <span style={{ textDecoration: 'underline', textUnderlineOffset: '6px', textDecorationColor: 'rgba(34, 211, 238, 0.4)' }}>Watch Our</span>{' '}
                  <span style={{ color: '#22d3ee' }}>LIVE STREAMS</span>
                </h2>
                <div style={{
                  padding: '0.35rem',
                  background: 'linear-gradient(135deg, rgba(34, 211, 238, 0.15), rgba(139, 92, 246, 0.15))',
                  borderRadius: '18px',
                  position: 'relative'
                }}>
                  {liveStatus.isLive ? (
                    <iframe
                      src="https://player.kick.com/camneverwins"
                      title="Cam's Kick Stream"
                      style={{ width: '100%', aspectRatio: '16 / 9', border: 0, borderRadius: '14px', display: 'block', background: '#000' }}
                      allowFullScreen
                      loading="lazy"
                    />
                  ) : (
                    /* Clean offline card — replaces the Kick player's cluttered offline
                       screen (overlapping banner + VOD thumbnail) with one clear state */
                    <div style={{ position: 'relative', borderRadius: '14px', overflow: 'hidden', background: 'linear-gradient(160deg, #0b0f1d 0%, #0e1426 55%, #101a30 100%)', padding: '3rem 1.5rem', textAlign: 'center' }}>
                      <div style={{ position: 'absolute', top: '-40%', left: '50%', transform: 'translateX(-50%)', width: 500, height: 320, background: 'radial-gradient(circle, rgba(83, 252, 24, 0.08) 0%, transparent 65%)', pointerEvents: 'none' }} />
                      <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📼</div>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
                        <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#6b7280', display: 'inline-block' }} />
                        <span style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.18em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Offline</span>
                      </div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 900, marginBottom: '0.35rem' }}>Cam is offline right now</div>
                      <div style={{ fontSize: '0.88rem', color: 'var(--text-dim)', marginBottom: '1.4rem' }}>Follow on Kick to get notified the moment the next stream starts.</div>
                      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <a href="https://kick.com/camneverwins" target="_blank" rel="noopener noreferrer" style={{ padding: '0.6rem 1.5rem', background: '#53fc18', color: '#000', borderRadius: '10px', fontWeight: 800, fontSize: '0.88rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 4px 14px rgba(83, 252, 24, 0.3)' }}>
                          <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 16, height: 16 }}><path d="M2 2h5v6h2V6h2V4h2V2h5v4h-2v2h-2v2h-2v4h2v2h2v2h2v4h-5v-2h-2v-2H9v2H7v2H2V2z"/></svg>
                          Follow on Kick
                        </a>
                        <a href="https://kick.com/camneverwins/videos" target="_blank" rel="noopener noreferrer" style={{ padding: '0.6rem 1.5rem', background: 'rgba(255,255,255,0.06)', color: 'var(--text)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: '10px', fontWeight: 700, fontSize: '0.88rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                          ▶ Watch Past Streams
                        </a>
                      </div>
                    </div>
                  )}
                </div>
                {liveStatus.isLive && (
                  <div style={{ textAlign: 'center', marginTop: '0.85rem' }}>
                    <a href="https://kick.com/camneverwins" target="_blank" rel="noopener noreferrer" style={{ padding: '0.6rem 1.5rem', background: '#53fc18', color: '#000', borderRadius: '10px', fontWeight: 800, fontSize: '0.88rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 4px 14px rgba(83, 252, 24, 0.3)' }}>
                      <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 16, height: 16 }}><path d="M2 2h5v6h2V6h2V4h2V2h5v4h-2v2h-2v2h-2v4h2v2h2v2h2v4h-5v-2h-2v-2H9v2H7v2H2V2z"/></svg>
                      Follow on Kick
                    </a>
                  </div>
                )}
              </div>

              {/* === FOLLOW CAM === */}
              <div style={{ marginBottom: '2rem' }}>
                <h2 className="display-font" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.25rem)', marginBottom: '0.5rem', letterSpacing: '0.02em' }}>
                  <span style={{ textDecoration: 'underline', textUnderlineOffset: '6px', textDecorationColor: 'rgba(34, 211, 238, 0.4)' }}>Follow</span>{' '}
                  <span style={{ color: '#22d3ee' }}>CamNeverWins</span>
                </h2>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-dim)', marginBottom: '1.25rem' }}>
                  Stay tuned for giveaways, leaderboard updates, and stream notifications.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
                  {[
                    { label: 'KICK', handle: 'camneverwins', url: 'https://kick.com/camneverwins', bg: 'linear-gradient(135deg, #53fc18, #3fdc00)', color: '#000' },
                    { label: 'TWITCH', handle: 'camneverwins', url: 'https://twitch.tv/camneverwins', bg: 'linear-gradient(135deg, #9147ff, #7c3aed)', color: '#fff' },
                    { label: 'DISCORD', handle: 'camneverwins', url: 'https://discord.gg/camneverwins', bg: 'linear-gradient(135deg, #5865F2, #4752c4)', color: '#fff' },
                    { label: 'YOUTUBE', handle: 'camneverwins', url: 'https://youtube.com/@camneverwins', bg: 'linear-gradient(135deg, #ff0000, #cc0000)', color: '#fff' },
                    { label: 'INSTAGRAM', handle: 'camneverwinsslots', url: 'https://instagram.com/camneverwinsslots', bg: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', color: '#fff' }
                  ].map(s => (
                    <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1rem 0.75rem', background: s.bg, borderRadius: '12px', textDecoration: 'none', color: s.color, fontWeight: 800, fontSize: '0.82rem', letterSpacing: '0.06em', gap: '0.2rem', transition: 'transform 0.15s, box-shadow 0.15s' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}>
                      <span style={{ fontSize: '0.65rem', opacity: 0.85 }}>{s.label}</span>
                      <span>@{s.handle}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* === QUICK LINKS GRID === */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
                {[
                  { icon: '⚔️', label: 'Challenges', page: 'challenges', color: '#ef4444' },
                  { icon: '🎰', label: 'Slot Requests', page: 'slotrequests', color: '#fbbf24' },
                  { icon: '🔮', label: 'Predictions', page: 'predictions', color: '#a78bfa' },
                  { icon: '🎯', label: 'Bonus Hunts', page: 'bonushunts', color: '#22c55e' },
                  { icon: '📋', label: 'How to Join', page: 'instructions', color: '#0ea5e9' },
                  { icon: '🎟️', label: 'Wager Raffle', page: 'wagerraffle', color: '#f472b6' }
                ].map(q => (
                  <button
                    key={q.page}
                    onClick={() => navigateTo(q.page)}
                    style={{
                      padding: '1.25rem 1rem',
                      background: 'rgba(0,0,0,0.3)',
                      border: '1px solid var(--border)',
                      borderRadius: '14px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      color: 'var(--text)',
                      transition: 'all 0.15s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = q.color + '55'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = ''; }}
                  >
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.35rem' }}>{q.icon}</div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{q.label}</div>
                  </button>
                ))}
              </div>
            </>
          );
        })()}

        {/* ============================================ */}
        {/* LEADERBOARD PAGE                             */}
        {/* ============================================ */}
        {activePage === 'leaderboard' && (
          <>
            {/* Same de-rectangled treatment as the homepage hero — washes
                reach zero alpha before the top edge so the scroll container
                can't clip them into a visible line */}
            <div style={{
              position: 'relative',
              textAlign: 'center',
              padding: '2.5rem 1.5rem 2rem',
              background: 'radial-gradient(300px 190px at 50% 235px, rgba(34, 211, 238, 0.10), transparent 74%), radial-gradient(ellipse 62% 45% at 50% 46%, rgba(14, 165, 233, 0.06), transparent 74%)',
              marginBottom: '1.5rem'
            }}>

              <div style={{ position: 'relative', zIndex: 1 }}>

                {/* CAM × Rainbet branded pill */}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.85rem', padding: '0.65rem 1.5rem', background: 'rgba(14, 22, 43, 0.6)', border: '1px solid rgba(34, 211, 238, 0.2)', borderRadius: '14px', marginBottom: '1rem', backdropFilter: 'blur(8px)' }}>
                  <img src="/cam-logo.jpg" alt="Cam" style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid rgba(34, 211, 238, 0.4)' }} onError={(e) => { e.target.style.display = 'none'; }} />
                  <span style={{ fontSize: '1.1rem', fontWeight: 800, letterSpacing: '0.02em' }}>CAM</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '1rem' }}>×</span>
                  <img src="https://rainbetplay.com/wp-content/uploads/2025/11/rainbet.webp" alt="Rainbet" style={{ height: 22 }} />
                </div>

                {/* Leaderboard label — tracks the active monthly/weekly view */}
                <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.2em', color: '#fbbf24', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                  {effectiveType === 'weekly' ? 'Weekly' : 'Monthly'} Leaderboard
                </div>

                {/* Prize amount */}
                <div className="gold-3d" style={{ fontSize: 'clamp(3.2rem, 9vw, 5.75rem)', lineHeight: 1, marginBottom: '0.5rem', letterSpacing: '0.02em', visibility: dbPeriods === null ? 'hidden' : 'visible' }}>
                  ${displayPeriod.totalPrize?.toLocaleString() || '25,000'}
                </div>

                {/* Date range pill */}
                <div style={{ display: 'inline-block', padding: '0.35rem 1rem', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '999px', fontSize: '0.8rem', fontFamily: 'Space Mono, monospace', color: 'var(--text-dim)', marginBottom: '1rem', visibility: dbPeriods === null ? 'hidden' : 'visible' }}>
                  {fmtDateCT(displayPeriod.start, { year: 'numeric', month: '2-digit', day: '2-digit' })} — {fmtDateCT(displayPeriod.end, { year: 'numeric', month: '2-digit', day: '2-digit' })}
                </div>

                {boardPreview && (
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.18em', color: '#22d3ee', textTransform: 'uppercase', marginBottom: '0.45rem' }}>⏳ Starts In</div>
                )}
                {weeklyEnded && (
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.18em', color: '#22d3ee', textTransform: 'uppercase', marginBottom: '1.25rem' }}>🏁 Final Results · Next week starts soon</div>
                )}
                {/* Boxed countdown timer */}
                <div style={{ display: weeklyEnded ? 'none' : 'flex', justifyContent: 'center', gap: '0.15rem', alignItems: 'center', marginBottom: '1.25rem' }}>
                  {[
                    { val: String(countdown.days).padStart(2, '0'), label: 'DAYS' },
                    { val: String(countdown.hours).padStart(2, '0'), label: 'HOURS' },
                    { val: String(countdown.minutes).padStart(2, '0'), label: 'MINUTES' },
                    { val: String(countdown.seconds).padStart(2, '0'), label: 'SECONDS' }
                  ].map((t, i) => (
                    <React.Fragment key={t.label}>
                      {i > 0 && <span style={{ color: 'var(--text-muted)', fontWeight: 700, fontSize: '1.2rem', margin: '0 0.25rem' }}>:</span>}
                      <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', padding: '0.5rem 0.75rem', background: 'rgba(6, 10, 24, 0.55)', border: '1px solid rgba(251, 191, 36, 0.28)', borderRadius: '10px', minWidth: 55, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)' }}>
                        <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '1.5rem', fontWeight: 800, color: '#e5e7eb' }}>{t.val}</span>
                        <span style={{ fontSize: '0.5rem', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.1em', marginTop: '0.15rem' }}>{t.label}</span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>

                {/* CTA */}
                <a href={affiliateLink} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', padding: '0.85rem 2rem', background: 'linear-gradient(135deg, #22d3ee, #0ea5e9)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: 800, textDecoration: 'none', boxShadow: '0 6px 24px rgba(34, 211, 238, 0.35)' }}>
                  Join with Code {GUILD_CODE.toUpperCase()}
                </a>

                {/* Leaderboard Rules trigger */}
                <div style={{ marginTop: '0.9rem' }}>
                  <button
                    onClick={() => setShowLbRules(true)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.9rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.02em' }}
                  >
                    📋 Leaderboard Rules
                  </button>
                </div>
              </div>
            </div>

            {/* Leaderboard Rules modal */}
            {showLbRules && (
              <div
                onClick={() => setShowLbRules(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(2, 6, 18, 0.78)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ position: 'relative', width: '100%', maxWidth: 460, maxHeight: '85vh', overflowY: 'auto', background: 'linear-gradient(180deg, rgba(17, 24, 43, 0.98), rgba(10, 14, 28, 0.99))', border: '1px solid rgba(34, 211, 238, 0.2)', borderRadius: '16px', padding: '1.5rem', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
                >
                  <button
                    onClick={() => setShowLbRules(false)}
                    aria-label="Close"
                    style={{ position: 'absolute', top: '1rem', right: '1rem', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.06)', color: 'var(--text-dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', fontSize: '1rem', cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1 }}
                  >×</button>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.1rem', paddingRight: '2rem' }}>
                    <span style={{ fontSize: '1.15rem' }}>📋</span>
                    <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#e5e7eb' }}>Leaderboard Rules</h3>
                  </div>

                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
                    {[
                      'This leaderboard utilizes a weighted wager system.',
                      'All wagers are adjusted to a standardized 96% RTP.',
                      'Wagering on low house edge games with the intent to artificially inflate wager volume may be reduced or excluded from leaderboard calculations.',
                      'A large influx of wagers near the end of the leaderboard period may result in delayed final results while all activity is reviewed and verified.',
                      'Cam and/or Rainbet reserve the right to reduce, adjust, or disqualify a player’s wagered amount in cases of suspected abuse, exploitation, or unfair play.',
                      'All decisions regarding wager adjustments and leaderboard eligibility are final.'
                    ].map((rule, i) => (
                      <li key={i} style={{ display: 'flex', gap: '0.6rem', fontSize: '0.85rem', lineHeight: 1.55, color: 'var(--text-dim)' }}>
                        <span style={{ flexShrink: 0, marginTop: '0.45rem', width: 6, height: 6, borderRadius: '50%', background: '#22d3ee' }} />
                        <span>{rule}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}


            {/* Monthly/Weekly toggle — shows when the weekly is live AND a monthly is
                available (live or an upcoming preview), so the viewer can switch
                between them. When only one is available we just show that one. */}
            {monthlyViewable && weeklyActive && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem', gap: '0' }}>
                <div style={{ display: 'inline-flex', padding: '0.35rem', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(34, 211, 238, 0.2)', borderRadius: '12px', gap: '0.3rem', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
                  {[
                    { key: 'monthly', label: '📊 Monthly', color: '#3b82f6' },
                    { key: 'weekly', label: '📅 Weekly', color: '#22d3ee' }
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => setLeaderboardType(opt.key)}
                      style={{
                        padding: '0.65rem 1.75rem',
                        background: leaderboardType === opt.key ? `linear-gradient(135deg, ${opt.color}, ${opt.color}cc)` : 'transparent',
                        color: leaderboardType === opt.key ? '#fff' : 'var(--text-dim)',
                        border: leaderboardType === opt.key ? 'none' : '1px solid transparent',
                        borderRadius: '9px',
                        fontSize: '0.92rem',
                        fontWeight: 800,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        letterSpacing: '0.04em',
                        transition: 'all 0.2s ease',
                        boxShadow: leaderboardType === opt.key ? `0 4px 16px ${opt.color}55` : 'none'
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {(() => {
              const displayLeaderboard = boardRows;
              const noWeeklyPrizes = effectiveType === 'weekly' && !hasWeeklyPrizes;
              return (loading ? <div className="skeleton" /> : displayLeaderboard.length > 0 ? (
              <>
                {boardPreview && (
                  <div style={{ textAlign: 'center', padding: '1rem 1.5rem', background: 'rgba(34, 211, 238, 0.08)', border: '1px solid rgba(34, 211, 238, 0.25)', borderRadius: '12px', marginBottom: '1rem' }}>
                    <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#22d3ee', marginBottom: '0.2rem' }}>🗓️ {effectiveType === 'weekly' ? 'Weekly' : 'Monthly'} leaderboard starts {fmtDateCT(displayPeriod.start, { month: 'long', day: 'numeric' })}</div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)' }}>Wagering hasn't started yet — here are the prizes up for grabs. Standings go live when it starts!</div>
                  </div>
                )}
                {weeklyEnded && (
                  <div style={{ textAlign: 'center', padding: '1rem 1.5rem', background: 'rgba(34, 211, 238, 0.08)', border: '1px solid rgba(34, 211, 238, 0.25)', borderRadius: '12px', marginBottom: '1rem' }}>
                    <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#22d3ee', marginBottom: '0.2rem' }}>🏁 {displayPeriod.name} has ended</div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)' }}>These are the final standings. Next week's leaderboard starts soon!</div>
                  </div>
                )}
                {noWeeklyPrizes && (
                  <div style={{ textAlign: 'center', padding: '1rem 1.5rem', background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)', borderRadius: '12px', marginBottom: '1rem' }}>
                    <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#fbbf24', marginBottom: '0.2rem' }}>⚠️ No weekly prizes configured</div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)' }}>The weekly leaderboard is tracking wagers but no prize pool has been set up yet. Check back soon!</div>
                  </div>
                )}

                {/* Data freshness stamp */}
                {lbUpdatedAt && (
                  <div style={{ textAlign: 'center', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                    🔄 Wager data updated {fmtUpdatedAgo(lbUpdatedAt)}
                  </div>
                )}

                {/* Enhanced podium — matches homepage design */}
                {displayLeaderboard.length >= 3 && (() => {
                  const lbPodiumData = [
                    { idx: 1, color: '#a78bfa', tilt: -5, ribbonA: '#94a3b8', ribbonB: '#64748b', medalBg: 'linear-gradient(135deg, #f1f5f9, #8a8a8a)', frame: 'linear-gradient(160deg, #f8fafc, #94a3b8 30%, #e2e8f0 50%, #64748b 75%, #cbd5e1)', w: 'clamp(160px, 24vw, 225px)', py: '2rem', shift: 14, glow: 'rgba(203, 213, 225, 0.18)' },
                    { idx: 0, color: '#22d3ee', tilt: 0, ribbonA: '#fbbf24', ribbonB: '#d97706', medalBg: 'linear-gradient(135deg, #fde68a, #d97706)', frame: 'linear-gradient(160deg, #fde68a, #d97706 30%, #fbbf24 50%, #92400e 75%, #fcd34d)', w: 'clamp(185px, 28vw, 260px)', py: '2.5rem', shift: 0, glow: 'rgba(251, 191, 36, 0.28)', isFirst: true },
                    { idx: 2, color: '#f472b6', tilt: 5, ribbonA: '#f97316', ribbonB: '#c2410c', medalBg: 'linear-gradient(135deg, #e8a06a, #8a4a1c)', frame: 'linear-gradient(160deg, #f0b27a, #92400e 30%, #c2703d 50%, #5e3010 75%, #e8a06a)', w: 'clamp(160px, 24vw, 225px)', py: '2rem', shift: 14, glow: 'rgba(217, 119, 6, 0.18)' }
                  ];
                  return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 'clamp(0.5rem, 2vw, 1.25rem)', marginBottom: '1.5rem' }}>
                      {lbPodiumData.map(p => {
                        const player = displayLeaderboard[p.idx];
                        const rank = p.idx + 1;
                        const prize = noWeeklyPrizes ? null : (activePrizes[p.idx]?.prize || 0);
                        return (
                          <div key={rank} style={{
                            width: p.w, padding: `${p.py} 1rem 1.25rem`,
                            background: `linear-gradient(180deg, rgba(15, 15, 30, 0.97), rgba(8, 8, 20, 0.99)) padding-box, ${p.frame} border-box`,
                            border: '3px solid transparent', borderRadius: p.isFirst ? '20px' : '16px',
                            textAlign: 'center', position: 'relative', transform: `translateY(${p.shift}px) rotate(${p.tilt}deg)`,
                            boxShadow: `0 10px 40px ${p.glow}, inset 0 1px 0 rgba(255,255,255,0.04)`,
                            zIndex: p.isFirst ? 2 : 1, transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.transition = 'transform 0.08s ease-out, box-shadow 0.3s ease'; e.currentTarget.style.boxShadow = `0 14px 50px ${p.glow}, inset 0 1px 0 rgba(255,255,255,0.06)`; }}
                          onMouseMove={(e) => {
                            const r = e.currentTarget.getBoundingClientRect();
                            const rx = ((e.clientY - r.top) / r.height - 0.5) * -8;
                            const ry = ((e.clientX - r.left) / r.width - 0.5) * 8;
                            e.currentTarget.style.transform = `perspective(700px) translateY(${p.shift - 4}px) rotateZ(${p.tilt}deg) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg)`;
                          }}
                          onMouseLeave={(e) => { e.currentTarget.style.transition = 'transform 0.35s ease, box-shadow 0.3s ease'; e.currentTarget.style.transform = `translateY(${p.shift}px) rotate(${p.tilt}deg)`; e.currentTarget.style.boxShadow = `0 10px 40px ${p.glow}, inset 0 1px 0 rgba(255,255,255,0.04)`; }}
                          >
                            {/* Hanging ribbon medal + crown */}
                            <div style={{ position: 'absolute', top: p.isFirst ? '-38px' : '-30px', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              {p.isFirst && <div style={{ fontSize: '1.6rem', marginBottom: '-2px', filter: 'drop-shadow(0 0 10px rgba(251, 191, 36, 0.6))', animation: 'ctaIconFloat 3s ease-in-out infinite', zIndex: 2 }}>👑</div>}
                              <div style={{ position: 'relative', width: p.isFirst ? 64 : 56, height: p.isFirst ? 74 : 64 }}>
                                <div style={{ position: 'absolute', top: 0, left: p.isFirst ? 10 : 8, width: p.isFirst ? 18 : 16, height: p.isFirst ? 42 : 36, background: `linear-gradient(180deg, ${p.ribbonA}, ${p.ribbonB})`, transform: 'rotate(-18deg)', transformOrigin: 'top center', borderRadius: '3px', boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }} />
                                <div style={{ position: 'absolute', top: 0, right: p.isFirst ? 10 : 8, width: p.isFirst ? 18 : 16, height: p.isFirst ? 42 : 36, background: `linear-gradient(180deg, ${p.ribbonB}, ${p.ribbonA})`, transform: 'rotate(18deg)', transformOrigin: 'top center', borderRadius: '3px', boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }} />
                                <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: p.isFirst ? 54 : 46, height: p.isFirst ? 54 : 46, borderRadius: '50%', background: `linear-gradient(180deg, #161d33, #0b101f) padding-box, ${p.medalBg} border-box`, border: '3px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 16px rgba(0,0,0,0.55), 0 0 18px ${p.glow}` }}>
                                  <img src="/rainbet-icon.png" alt="" style={{ width: p.isFirst ? 40 : 34, height: p.isFirst ? 40 : 34, borderRadius: '50%' }} onError={(e) => { if (!e.target.src.endsWith('/cam-logo.jpg')) e.target.src = '/cam-logo.jpg'; else e.target.style.display = 'none'; }} />
                                </div>
                                <div style={{ position: 'absolute', bottom: -8, left: '50%', transform: 'translateX(-50%)', width: 22, height: 22, borderRadius: '50%', background: p.medalBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 900, color: '#101010', border: '2px solid rgba(0,0,0,0.35)', zIndex: 1, boxShadow: '0 2px 6px rgba(0,0,0,0.5)' }}>{rank}</div>
                              </div>
                            </div>
                            <div style={{ marginTop: p.isFirst ? '3.1rem' : '2.5rem', fontWeight: 800, fontSize: p.isFirst ? 'clamp(0.85rem, 1.8vw, 1.05rem)' : 'clamp(0.75rem, 1.5vw, 0.92rem)', color: player._preview ? 'var(--text-muted)' : undefined }}>{player._preview ? 'TBD' : censorUser(player.username)}{!player._preview && <UsSiteChip site={player.site} />}</div>
                            <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)', letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: '0.35rem' }}>{player._preview ? 'PRIZE' : 'WAGERED'}</div>
                            <div style={{ fontFamily: 'Space Mono, monospace', fontWeight: 800, color: p.color, fontSize: p.isFirst ? 'clamp(1rem, 2.2vw, 1.35rem)' : 'clamp(0.85rem, 1.8vw, 1.1rem)', marginTop: '0.15rem' }}>{player._preview ? '—' : formatNum(player.wagers)}</div>
                            <div style={{ width: '60%', height: '1.5px', background: `linear-gradient(90deg, transparent, ${p.color}55, transparent)`, margin: '0.65rem auto 0.5rem' }} />
                            <div style={{ fontWeight: 800, fontSize: p.isFirst ? '1.05rem' : '0.88rem', color: p.color }}>{prize !== null ? `$${prize.toLocaleString()}` : '—'}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Pinned "your rank" row for logged-in users with a linked Rainbet account.
                    Rank comes from /api/my-rank (server-side, uncensored data) — board names
                    are censored so matching client-side is impossible. Hidden until the rank
                    fetch resolves to avoid falsely telling ranked users they're unranked. */}
                {hasRainbetLink(user) && myRank && displayLeaderboard.length > 0 && !displayLeaderboard[0]?._preview && !displayLeaderboard[0]?._final && (() => {
                  const mine = effectiveType === 'weekly' ? myRank.weekly : myRank.monthly;
                  const myPrize = mine && !noWeeklyPrizes && mine.rank <= activePrizes.length ? getActivePrizeForRank(mine.rank) : 0;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', padding: '0.8rem 1.25rem', marginBottom: '0.85rem', background: 'rgba(34, 211, 238, 0.06)', border: '1px solid rgba(34, 211, 238, 0.3)', borderRadius: '12px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.12em', color: '#22d3ee', textTransform: 'uppercase' }}>Your Rank</span>
                      {mine ? (
                        <>
                          <span style={{ fontWeight: 900, fontSize: '1.05rem', color: '#22d3ee' }}>#{mine.rank}</span>
                          <span style={{ fontFamily: 'Space Mono, monospace', fontWeight: 700, fontSize: '0.88rem' }}>{mine.username || user.rainbetUsername}<UsSiteChip site={mine.site} /></span>
                          <span style={{ flex: 1 }} />
                          <span style={{ fontFamily: 'Space Mono, monospace', fontWeight: 800, fontSize: '0.9rem' }}>{formatNum(mine.wagered)} <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 600 }}>WAGERED</span></span>
                          <span style={{ fontWeight: 800, fontSize: '0.9rem', color: myPrize > 0 ? '#22c55e' : 'var(--text-muted)' }}>{myPrize > 0 ? `$${myPrize.toLocaleString()}` : activePrizes.length > 0 ? `top ${activePrizes.length} win prizes` : 'no prize yet'}</span>
                        </>
                      ) : (
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>You're not on the board yet — wager on code CAM to climb it!</span>
                      )}
                    </div>
                  );
                })()}

                {displayLeaderboard.length > 3 && (
                  <div className="lb-table">
                    <div className="table-header"><div>#</div><div>Player</div><div>Wagered</div><div>Prize</div></div>
                    {displayLeaderboard.slice(3).map(p => {
                      const isInPrizes = !noWeeklyPrizes && p.rank <= activePrizes.length;
                      const fadingLevel = (!noWeeklyPrizes && p.rank > activePrizes.length) ? p.rank - activePrizes.length : (noWeeklyPrizes ? p.rank : 0);
                      const fadingClass = fadingLevel > 0 ? `fading-${Math.min(fadingLevel, 10)}` : '';
                      const topWager = displayLeaderboard[0]?.wagers || 0;
                      const barPct = !p._preview && topWager > 0 ? Math.max(2, Math.round((p.wagers / topWager) * 100)) : 0;
                      return (
                        <div key={p.rank} className={`table-row ${isInPrizes ? 'in-prizes' : ''} ${fadingClass}`}>
                          <div><span className={`t-rank ${isInPrizes ? 'in-prizes' : 'out-prizes'}`}>{p.rank}</span></div>
                          <div className="t-user">
                            <div className="t-avatar">{p._preview ? '?' : getInitials(p.username)}</div>
                            <span style={p._preview ? { color: 'var(--text-muted)' } : undefined}>{p._preview ? 'TBD' : censorUser(p.username)}{!p._preview && <UsSiteChip site={p.site} />}</span>
                            {barPct > 0 && <div className="t-bar"><div style={{ width: `${barPct}%` }} /></div>}
                          </div>
                          <div className="t-wager">{p._preview ? '—' : formatNum(p.wagers)}</div>
                          <div className={`t-prize ${!isInPrizes ? 'none' : ''}`}>{isInPrizes ? `$${getActivePrizeForRank(p.rank).toLocaleString()}` : '—'}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Previous Period Winners (prize recipients only, censored) */}
                {previousPeriod && previousPeriod.winners && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <button
                      onClick={() => setShowPreviousWinners(!showPreviousWinners)}
                      style={{
                        width: '100%',
                        padding: '1rem 1.5rem',
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(236, 72, 153, 0.1))',
                        border: '1px solid rgba(139, 92, 246, 0.3)',
                        borderRadius: '12px',
                        color: 'var(--text)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: '1rem',
                        fontWeight: 600,
                        fontFamily: 'inherit',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{ fontSize: '1.25rem' }}>🏆</span>
                        Previous Winners - {previousPeriod.name}
                      </span>
                      <span style={{ 
                        transform: showPreviousWinners ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        fontSize: '1.25rem'
                      }}>▼</span>
                    </button>
                    
                    {showPreviousWinners && (
                      <div style={{
                        marginTop: '0.5rem',
                        background: 'rgba(0,0,0,0.3)',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        border: '1px solid var(--border)'
                      }}>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '60px 1fr 100px',
                          padding: '0.75rem 1rem',
                          background: 'rgba(0,0,0,0.3)',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          color: 'var(--text-dim)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          <div>#</div>
                          <div>Player</div>
                          <div style={{ textAlign: 'right' }}>Prize</div>
                        </div>
                        {previousPeriod.winners
                          .filter((_, idx) => previousPeriod.prizes[idx]?.prize > 0)
                          .map((winner, idx) => (
                          <div key={idx} style={{
                            display: 'grid',
                            gridTemplateColumns: '60px 1fr 100px',
                            padding: '0.85rem 1rem',
                            borderTop: '1px solid var(--border)',
                            alignItems: 'center',
                            background: idx < 3 ? `rgba(255, 215, 0, ${0.1 - idx * 0.03})` : 'transparent'
                          }}>
                            <div>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '28px',
                                height: '28px',
                                borderRadius: '6px',
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                background: idx === 0 ? 'linear-gradient(135deg, #ffd700, #f59e0b)' : 
                                           idx === 1 ? 'linear-gradient(135deg, #e8e8e8, #a8a8a8)' : 
                                           idx === 2 ? 'linear-gradient(135deg, #cd7f32, #b8860b)' : 'rgba(255,255,255,0.1)',
                                color: idx < 3 ? '#000' : 'var(--text)'
                              }}>
                                {winner.rank}
                              </span>
                            </div>
                            <div style={{ fontWeight: 500 }}>{displayUser(winner.username)}</div>
                            <div style={{ 
                              textAlign: 'right', 
                              fontWeight: 700, 
                              color: '#22c55e',
                              fontFamily: "'Space Mono', monospace"
                            }}>
                              ${previousPeriod.prizes[idx]?.prize.toLocaleString() || '0'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : <div style={{ textAlign: 'center', padding: '2.5rem 1.5rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.4 }}>🏆</div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: '0.75rem' }}>Leaderboard data loading…</div>
              <button onClick={fetchLeaderboard} style={{ padding: '0.5rem 1.2rem', background: 'rgba(34, 211, 238, 0.15)', color: '#22d3ee', border: '1px solid rgba(34, 211, 238, 0.3)', borderRadius: '8px', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>Refresh</button>
            </div>);
            })()}
          </>
        )}

        {activePage === 'rewards' && (() => {
          // Weekly-focused site → default the public Rewards page to the monthly
          // milestone ladder; admin can still force lifetime via reward_mode.
          const rewardsMode = siteSettings.reward_mode === 'lifetime' ? 'lifetime' : 'monthly';
          const activeTiers = rewardTiersAll
            .filter(t => t.mode === rewardsMode && t.active !== false)
            .sort((a, b) => (a.rank_order ?? 0) - (b.rank_order ?? 0) || a.id - b.id);
          const currentWagered = rewardsMode === 'lifetime'
            ? Number(rewardProgress?.lifetime_wagered || 0)
            : Number(rewardProgress?.monthly_wagered || 0);
          const progressByTier = new Map(
            (rewardProgress?.tiers || []).map(t => [t.id, t])
          );
          // Running sum of rewards: how much the user is cumulatively eligible for
          // once they reach each tier (tier 1 → tier N).
          let _cumSum = 0;
          const cumulativeTotals = activeTiers.map(t => (_cumSum += Number(t.reward_amount || 0)));
          // Default color progression for tiers the admin hasn't colored — cool →
          // warm → hot as the ladder climbs.
          const FALLBACK_TIER_COLORS = ['#94a3b8','#64748b','#60a5fa','#22d3ee','#2dd4bf','#34d399','#22c55e','#a3e635','#facc15','#fbbf24','#f59e0b','#fb923c','#f97316','#ef4444','#ec4899','#d946ef'];
          const loggedInProgress = !!(user && rewardProgress);
          // First tier the user hasn't reached yet = the "next" tier to emphasize.
          const nextTierIdx = loggedInProgress
            ? activeTiers.findIndex(t => currentWagered < Number(t.wager_requirement))
            : -1;
          const reachedCount = loggedInProgress
            ? activeTiers.filter(t => currentWagered >= Number(t.wager_requirement)).length
            : 0;
          const handleClaim = async (tier) => {
            if (!user) { handleLogin(); return; }
            if (!hasRainbetLink(user)) { alert('Link your Rainbet username in your profile before claiming.'); return; }
            if (!confirm(`Claim ${tier.name} reward ($${Number(tier.reward_amount).toLocaleString()})?\n\nYour Rainbet username: ${rainbetDisplay(user)}\nAn admin will verify your wager and process the reward (2-3 business days).`)) return;
            const token = getAuthToken();
            if (!token) return;
            try {
              const res = await fetch(`${BACKEND_API_URL}/api/me/claim-tier/${tier.id}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const data = await res.json();
              if (!res.ok) {
                alert(data.error || 'Could not claim');
                return;
              }
              alert('Claim submitted! An admin will verify your wager and process the reward (2-3 business days).');
              fetchRewardProgress();
            } catch (e) { alert('Network error claiming reward'); }
          };

          return (
            <>
              <div className="header" style={{ textAlign: 'center' }}>
                <h1 className="header-title">
                  {rewardsMode === 'lifetime' ? 'Lifetime' : 'Monthly'} <span>Rewards</span>
                </h1>
                <p className="header-sub">Climb the ranks and unlock exclusive rewards based on your wager amount.</p>
                <button
                  onClick={() => setShowRewardRules(true)}
                  style={{ marginTop: '0.75rem', padding: '0.4rem 1.1rem', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: 'var(--text-dim)', fontFamily: 'inherit', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
                >📋 Wager Rewards Rules</button>
              </div>

              {/* Logged-out visitors get a nudge to sign in and see their own progress */}
              {!user && (
                <div className="card" style={{ padding: '0.9rem 1.25rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.85rem', flexWrap: 'wrap', border: '1px solid rgba(34, 211, 238, 0.25)', background: 'rgba(34, 211, 238, 0.04)' }}>
                  <span style={{ fontSize: '0.9rem', color: 'var(--text-dim)' }}>🔒 Log in to track your wager progress and claim tiers</span>
                  <button onClick={handleLogin} style={{ padding: '0.5rem 1.25rem', background: 'linear-gradient(135deg, #22d3ee, #0ea5e9)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Log In →
                  </button>
                </div>
              )}

              {/* Progress banner when logged in */}
              {user && rewardProgress && (
                <div className="rewards-progress-banner">
                  <div>
                    <div className="rewards-progress-label">
                      {rewardsMode === 'lifetime' ? 'Your Lifetime Wagered' : `Your Wagered — ${rewardProgress.month_key || ''}`}
                    </div>
                    <div className="rewards-progress-amount">${currentWagered.toLocaleString()}</div>
                  </div>
                  {activeTiers.length > 0 && (
                    <div className="rewards-progress-tiers">
                      <span className="rewards-progress-tiers-num">{reachedCount}</span>
                      <span className="rewards-progress-tiers-den"> / {activeTiers.length} tiers unlocked</span>
                    </div>
                  )}
                  {!rewardProgress.rainbet_username && !rewardProgress.rainbet_us_username && (
                    <div className="rewards-progress-warn">
                      ⚠️ Link your Rainbet username in your profile to track progress
                    </div>
                  )}
                </div>
              )}

              {activeTiers.length === 0 ? (
                <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)' }}>
                  No {rewardsMode} tiers configured yet.
                </div>
              ) : (
                <div className="rewards-ladder">
                  {/* Column headers */}
                  <div className="tier-bar-head">
                    <div className="tier-bar-head-left">Tier &amp; Wager Required</div>
                    <div className="tier-bar-head-mid">Reward</div>
                    <div className="tier-bar-head-right">Total Eligible</div>
                  </div>
                  {activeTiers.map((t, idx) => {
                    const progress = progressByTier.get(t.id);
                    const wagerReq = Number(t.wager_requirement);
                    const reward = Number(t.reward_amount);
                    const cumulative = cumulativeTotals[idx];
                    const tierColor = t.color || FALLBACK_TIER_COLORS[Math.min(idx, FALLBACK_TIER_COLORS.length - 1)];
                    const rankOrder = t.rank_order ?? (idx + 1);
                    const isReached = progress?.eligible || (loggedInProgress && currentWagered >= wagerReq);
                    const isNext = idx === nextTierIdx;
                    const claimStatus = progress?.claim_status;
                    // Fill within this tier's band (used on the "next" tier).
                    const prevReq = idx > 0 ? Number(activeTiers[idx - 1].wager_requirement) : 0;
                    const denom = Math.max(1, wagerReq - prevReq);
                    const pct = isReached ? 100 : Math.max(0, Math.min(100, Math.round(((currentWagered - prevReq) / denom) * 100)));
                    // Show the tier's name only when it's something other than a generic "Tier N".
                    const showName = t.name && !/^tier\s*\d+$/i.test(t.name.trim());
                    const rowClass = `tier-bar${isReached ? ' reached' : ''}${isNext ? ' next' : ''}`;
                    return (
                      <div key={t.id} className={rowClass} style={{ '--tier-color': tierColor }}>
                        {loggedInProgress && isNext && (
                          <div className="tier-progress-line" style={{ width: `${pct}%` }} title={`$${Math.max(0, wagerReq - currentWagered).toLocaleString()} more to unlock`} />
                        )}
                        {/* Left: status + badge + wager requirement */}
                        <div className="tier-bar-left">
                          {loggedInProgress && (
                            <span className={`tier-status-dot ${isReached ? 'on' : 'off'}`}>{isReached ? '✓' : '🔒'}</span>
                          )}
                          <span className="tier-badge">Tier {rankOrder}</span>
                          <span className="tier-wager">${wagerReq.toLocaleString()}</span>
                          {showName && <span className="tier-name-tag">{t.name}</span>}
                        </div>
                        {/* Center: this tier's reward + claim action */}
                        <div className="tier-col center">
                          <span className="tier-col-label">Reward</span>
                          <span className="tier-reward">${reward.toLocaleString()}</span>
                          {claimStatus === 'paid' ? (
                            <span className="tier-mini-badge paid">✓ Paid</span>
                          ) : claimStatus === 'pending' ? (
                            <span className="tier-mini-badge pending">Pending</span>
                          ) : claimStatus === 'denied' ? (
                            <span className="tier-mini-badge denied" title="This claim was denied by an admin. Contact staff if you think this is a mistake.">✗ Denied</span>
                          ) : user && isReached ? (
                            <button onClick={() => handleClaim(t)} className="tier-claim-btn">Claim</button>
                          ) : null}
                        </div>
                        {/* Right: cumulative total eligible */}
                        <div className="tier-col right">
                          <span className="tier-col-label">Total</span>
                          <span className="tier-total">${cumulative.toLocaleString()}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="rewards-footer-note">
                {rewardsMode === 'monthly'
                  ? <>Monthly rewards reset on the 1st of each month. Claim through Rainbet using code <strong>CAM</strong>.</>
                  : <>Rewards are claimed through Rainbet using code <strong>CAM</strong>. All rewards are subject to change.</>
                }
              </div>
            </>
          );
        })()}

        {activePage === 'instructions' && (
          <div className="signup-container">
            <h1 className="simple-page-title">Join Rainbet</h1>

            {/* --- SNUTZY-mirror structure with Rainbet copy --- */}

            <div className="signup-card">
              <img src="/cam-logo.jpg" alt="Cam" className="signup-cam-logo" onError={(e) => { e.target.style.display = 'none'; }} />
              <div className="signup-title">🎰 Get Started Now</div>
              <a href={affiliateLink} target="_blank" rel="noopener noreferrer" className="signup-link-btn">Sign Up with Code CAM →</a>
              <a href={affiliateLinkUs} target="_blank" rel="noopener noreferrer" className="rb-us-link" style={{ marginTop: '0.6rem', fontSize: '0.8rem' }}>
                In the US? Sign up on <strong>Rainbet.us</strong> instead →
              </a>
              <div className="code-display">
                <div className="code-label">Or enter this code when signing up:</div>
                <div className="code-value">{GUILD_CODE.toUpperCase()}</div>
                <div className="code-hint">Use the sign-up link above to apply the code automatically.</div>
              </div>
            </div>

            {/* Why Rainbet is the Best Choice */}
            <div className="info-card" style={{ borderColor: 'rgba(34, 197, 94, 0.3)', background: 'rgba(34, 197, 94, 0.05)' }}>
              <div className="info-title">💎 Why Rainbet is the Best Choice</div>
              <div className="info-item">
                <div className="info-icon">📈</div>
                <div className="info-content">
                  <h4>Competitive Rakeback &amp; Rewards</h4>
                  <p>With Rainbet's rank-up rewards, our leaderboard, weekly rewards, rakeback, and code drops combined — <strong>it's one of the most rewarding places to play.</strong></p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">🎲</div>
                <div className="info-content">
                  <h4>Huge Game Library</h4>
                  <p>Thousands of slot games from top providers, live casino tables, sports betting, and Rainbet originals — all on one site.</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">⬆️</div>
                <div className="info-content">
                  <h4>Rank Up Rewards</h4>
                  <p>Earn rewards as you level up your account — the more you play, the more you earn back!</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">🏆</div>
                <div className="info-content">
                  <h4>Weekly &amp; Monthly Rewards</h4>
                  <p>Rainbet runs weekly reward programs on top of everything else.</p>
                </div>
              </div>
            </div>

            {/* Guild CAM Rewards */}
            <div className="info-card" style={{ borderColor: 'rgba(34, 211, 238, 0.3)', background: 'rgba(34, 211, 238, 0.05)' }}>
              <div className="info-title">🎉 Code CAM Rewards</div>
              <div className="info-item">
                <div className="info-icon">🏅</div>
                <div className="info-content">
                  <h4>Weekly Leaderboard</h4>
                  <p>Compete on our <strong>weekly</strong> leaderboard for extra prizes — rewards on top of Rainbet's own rewards!</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">🎰</div>
                <div className="info-content">
                  <h4>Discord Giveaways</h4>
                  <p>Regular giveaways in our Discord for all members using code CAM.</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">📺</div>
                <div className="info-content">
                  <h4>Kick &amp; Twitch Giveaways</h4>
                  <p>Giveaways during live streams on Kick (primary) and Twitch. Follow to stay updated!</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">⚔️</div>
                <div className="info-content">
                  <h4>Slot Challenges</h4>
                  <p>Complete slot challenges posted on the site for bonus prizes — submit a clip when you hit the target and get paid out.</p>
                </div>
              </div>
            </div>

            {/* Account Info */}
            <div className="info-card">
              <div className="info-title">ℹ️ Account Info</div>
              <div className="info-content">
                <p>New accounts are required to enter personal information, but no matter what you enter you will be able to withdraw.</p>
              </div>
            </div>

            {/* Rainbet Deposit Guide */}
            <div className="info-card" style={{ borderColor: 'rgba(34, 197, 94, 0.3)', background: 'rgba(34, 197, 94, 0.05)' }}>
              <div className="info-title">💰 Rainbet Deposit Guide</div>

              {/* VPN section */}
              <div className="info-item">
                <div className="info-icon">🌐</div>
                <div className="info-content">
                  <h4>Need a VPN?</h4>
                  <p>If Rainbet isn't loading in your region, you'll need a VPN. We recommend:</p>
                  <ul style={{margin:'0.4rem 0 0.4rem 1rem', padding:0, fontSize:'0.85rem', color:'#a1a1aa', lineHeight:'1.6'}}>
                    <li><strong style={{color:'#e2e8f0'}}>Paid:</strong> NordVPN (most popular)</li>
                    <li><strong style={{color:'#e2e8f0'}}>Free:</strong> Urban VPN (Chrome extension)</li>
                  </ul>
                  <p style={{fontSize:'0.8rem', marginTop:'0.4rem'}}>Recommended countries: 🇮🇪 Ireland · 🇳🇴 Norway · 🇦🇹 Austria · 🇳🇿 New Zealand · 🇩🇪 Germany</p>
                </div>
              </div>

              {/* Using an Exchange (primary path — Rainbet has no direct crypto buy) */}
              <div className="info-item" style={{borderTop:'1px solid rgba(34,197,94,0.15)', paddingTop:'0.75rem', marginTop:'0.5rem'}}>
                <div className="info-icon">🔄</div>
                <div className="info-content">
                  <h4 style={{color:'#22c55e'}}>Depositing via an Exchange</h4>
                  <p>Rainbet accepts crypto deposits — here's the full flow from fiat to playing:</p>

                  <div style={{marginTop:'0.6rem'}}>
                    <div style={{fontSize:'0.82rem', fontWeight:700, color:'#22c55e', marginBottom:'0.3rem'}}>Step 1: Buy Crypto</div>
                    <ul style={{margin:'0 0 0 1rem', padding:0, fontSize:'0.82rem', color:'#a1a1aa', lineHeight:'1.55'}}>
                      <li>Buy on <strong style={{color:'#e2e8f0'}}>Coinbase</strong> or other exchanges</li>
                      <li>Use <strong style={{color:'#e2e8f0'}}>Debit Card</strong> for fastest delivery</li>
                      <li>Avoid ACH if you need speed</li>
                      <li>Best coins: <strong style={{color:'#e2e8f0'}}>USDC/USDT</strong> or <strong style={{color:'#e2e8f0'}}>ETH/SOL</strong></li>
                    </ul>
                  </div>

                  <div style={{marginTop:'0.6rem'}}>
                    <div style={{fontSize:'0.82rem', fontWeight:700, color:'#22c55e', marginBottom:'0.3rem'}}>Step 2: Coinbase → Exodus (intermediate wallet)</div>
                    <ol style={{margin:'0 0 0 1rem', padding:0, fontSize:'0.82rem', color:'#a1a1aa', lineHeight:'1.55'}}>
                      <li>Open Exodus → select your coin → Receive → copy address</li>
                      <li>Coinbase → Send → paste address</li>
                      <li>Make sure same coin + network → Confirm</li>
                    </ol>
                  </div>

                  <div style={{marginTop:'0.6rem'}}>
                    <div style={{fontSize:'0.82rem', fontWeight:700, color:'#22c55e', marginBottom:'0.3rem'}}>Step 3: Exodus → Rainbet</div>
                    <ol style={{margin:'0 0 0 1rem', padding:0, fontSize:'0.82rem', color:'#a1a1aa', lineHeight:'1.55'}}>
                      <li>Rainbet → Wallet → Deposit → select coin/network → copy address</li>
                      <li>Exodus → Send → paste address → Confirm</li>
                      <li>Wait for confirmations → balance appears</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Direct crypto note — Rainbet vs Shuffle */}
              <div style={{padding:'0.75rem 1rem', background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.2)', borderRadius:'10px', marginTop:'0.75rem'}}>
                <div style={{fontSize:'0.85rem', fontWeight:700, color:'#93c5fd', marginBottom:'0.3rem'}}>ℹ️ Note on Buying Crypto</div>
                <div style={{fontSize:'0.8rem', color:'#a1a1aa', lineHeight:'1.55'}}>
                  Unlike some casinos, <strong style={{color:'#e2e8f0'}}>Rainbet does not have a built-in fiat-to-crypto purchase option</strong> — you'll need to buy crypto on an external exchange (Coinbase, Kraken, etc.) first, then deposit to Rainbet. The good news: exchange rates are usually better this way.
                </div>
              </div>

              {/* Critical warnings */}
              <div style={{padding:'0.75rem 1rem', background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.2)', borderRadius:'10px', marginTop:'0.75rem'}}>
                <div style={{fontSize:'0.85rem', fontWeight:700, color:'#fbbf24', marginBottom:'0.4rem'}}>⚠️ Critical Warnings</div>
                <ul style={{margin:'0 0 0 1rem', padding:0, fontSize:'0.78rem', color:'#a1a1aa', lineHeight:'1.6'}}>
                  <li>Coin + network <strong style={{color:'#fbbf24'}}>MUST</strong> match between sender and receiver</li>
                  <li>Always double-check addresses before sending</li>
                  <li>Crypto transactions are <strong style={{color:'#fbbf24'}}>irreversible</strong></li>
                  <li>Save your wallet recovery phrase <strong style={{color:'#fbbf24'}}>OFFLINE</strong></li>
                </ul>
              </div>

              <div style={{textAlign:'center', marginTop:'1rem', fontSize:'0.78rem', color:'#71717a'}}>
                Still have questions? Ask in <a href="https://discord.gg/camneverwins" target="_blank" rel="noopener noreferrer" style={{color:'#67e8f9'}}>Discord</a> or in stream chat. Gamble responsibly · only wager what you can afford to lose.
              </div>
            </div>

            <a href={affiliateLink} target="_blank" rel="noopener noreferrer" className="signup-link-btn" style={{ marginBottom: '1rem' }}>Sign Up Now →</a>

            <p className="disclaimer">Must be 18+ to participate. Gamble responsibly.</p>
          </div>
        )}

        {activePage === 'challenges' && (() => {
          const isAdminUser = !!user?.isAdmin;
          const submitClaim = async () => {
            if (!user) { handleLogin(); return; }
            if (!claimForm.discord_username || !claimForm.rainbet_username) { alert('Discord and Rainbet usernames are required'); return; }
            const isWagerClaim = claimingChallenge?.challenge_type === 'wager';
            if (!isWagerClaim) {
              if (!claimForm.rainbet_id) { alert('Your Rainbet ID is required so admins can verify the bet'); return; }
              if (!claimForm.bet_link || !/^https?:\/\//i.test(claimForm.bet_link.trim())) { alert('A valid link to the winning bet is required (must start with http:// or https://)'); return; }
            }
            if (!claimForm.confirm) { alert('Please confirm the details are accurate'); return; }
            try {
              const token = getAuthToken();
              const payload = {
                discord_username: claimForm.discord_username.trim(),
                rainbet_username: claimForm.rainbet_username.trim(),
                rainbet_id: claimForm.rainbet_id.trim(),
                bet_link: claimForm.bet_link.trim()
              };
              const res = await fetch(`${BACKEND_API_URL}/api/challenges/${claimingChallenge.id}/claim`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
              });
              const data = await res.json();
              if (!res.ok) { alert(data.error || 'Could not submit claim'); return; }
              setClaimingChallenge(null);
              setClaimForm({ discord_username: '', rainbet_username: '', rainbet_id: '', bet_link: '', confirm: false });
              alert('Claim submitted! Admins will review and reach out on Discord.');
            } catch (e) { alert('Network error'); }
          };
          const saveCompletion = async () => {
            if (!completeForm.completed_by) { alert('Who completed this?'); return; }
            const token = getAuthToken();
            const payload = {
              completed_by: completeForm.completed_by.trim(),
              completed_multiplier: completeForm.completed_multiplier ? Number(completeForm.completed_multiplier) : null,
              completed_bet: completeForm.completed_bet ? Number(completeForm.completed_bet) : null
            };
            const res = await fetch(`${BACKEND_API_URL}/api/admin/challenges/${completingChallenge.id}/complete`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
            });
            if (res.ok) {
              setCompletingChallenge(null);
              setCompleteForm({ completed_by: '', completed_multiplier: '', completed_bet: '' });
              fetchChallenges();
            } else {
              alert('Failed to mark complete');
            }
          };
          const saveEdit = async () => {
            if (!editingChallenge.slot_name) { alert('Name required'); return; }
            const token = getAuthToken();
            const payload = {
              slot_name: editingChallenge.slot_name,
              slot_image: editingChallenge.slot_image || null,
              provider: editingChallenge.provider || null,
              target_multiplier: Number(editingChallenge.target_multiplier) || 0,
              min_bet: editingChallenge.challenge_type === 'wager' ? 0 : (Number(editingChallenge.min_bet) || 0),
              prize_amount: Number(editingChallenge.prize_amount) || 0,
              notes: editingChallenge.notes || null,
              status: editingChallenge.status || 'active',
              challenge_type: editingChallenge.challenge_type || 'multi',
              start_date: editingChallenge.start_date || null,
              end_date: editingChallenge.end_date || null
            };
            const url = editingChallenge.id
              ? `${BACKEND_API_URL}/api/admin/challenges/${editingChallenge.id}`
              : `${BACKEND_API_URL}/api/admin/challenges`;
            const method = editingChallenge.id ? 'PUT' : 'POST';
            const res = await fetch(url, { method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (res.ok) {
              setEditingChallenge(null);
              fetchChallenges();
            } else {
              const err = await res.json().catch(() => ({}));
              alert(err.error || 'Failed to save');
            }
          };

          const BountyTimer = ({ endDate }) => {
            const [timeLeft, setTimeLeft] = React.useState('');
            React.useEffect(() => {
              const calc = () => {
                const dateStr = typeof endDate === 'string' ? endDate.slice(0, 10) : endDate;
                const end = new Date(dateStr + 'T23:59:59');
                if (isNaN(end.getTime())) return 'Expired';
                const diff = end - new Date();
                if (diff <= 0) return 'Expired';
                const d = Math.floor(diff / 86400000);
                const h = Math.floor((diff / 3600000) % 24);
                const m = Math.floor((diff / 60000) % 60);
                const s = Math.floor((diff / 1000) % 60);
                const pad = n => String(n).padStart(2, '0');
                if (d > 0) return `${d}d ${pad(h)}h ${pad(m)}m ${pad(s)}s`;
                return `${pad(h)}h ${pad(m)}m ${pad(s)}s`;
              };
              setTimeLeft(calc());
              const id = setInterval(() => setTimeLeft(calc()), 1000);
              return () => clearInterval(id);
            }, [endDate]);
            const isExpired = timeLeft === 'Expired';
            return (
              <div style={{ textAlign: 'center', padding: '0.45rem 0.75rem', background: isExpired ? 'rgba(239, 68, 68, 0.08)' : 'rgba(251, 191, 36, 0.06)', border: `1px solid ${isExpired ? 'rgba(239, 68, 68, 0.2)' : 'rgba(251, 191, 36, 0.15)'}`, borderRadius: '8px' }}>
                <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.15rem' }}>{isExpired ? 'Bounty Expired' : 'Time Remaining'}</div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: '1.05rem', color: isExpired ? '#ef4444' : '#fbbf24', letterSpacing: '0.05em' }}>{timeLeft}</div>
              </div>
            );
          };

          const ChallengeCard = ({ ch, completed }) => {
            const isWager = ch.challenge_type === 'wager';
            return (
            <div style={{
              position: 'relative',
              background: completed ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.08), rgba(34, 197, 94, 0.02))' : 'var(--bg-card)',
              border: completed ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid var(--border)',
              borderRadius: '12px',
              padding: '1.25rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.85rem',
              opacity: completed ? 0.85 : 1,
              overflow: 'hidden'
            }}>
              {completed && (
                <div style={{ position: 'absolute', top: '0.6rem', right: '0.6rem', padding: '0.25rem 0.65rem', background: 'rgba(34, 197, 94, 0.2)', border: '1px solid rgba(34, 197, 94, 0.4)', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  ✓ Completed
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'center' }}>
                {ch.slot_image ? (
                  <img src={ch.slot_image} alt={ch.slot_name} style={{ width: 64, height: 64, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 64, height: 64, borderRadius: '8px', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'var(--text-muted)' }}>
                      <rect x="3" y="5" width="18" height="14" rx="2"/><path d="M8 9v6"/><path d="M12 9v6"/><path d="M16 9v6"/>
                    </svg>
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '1.05rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.slot_name}</div>
                  {ch.provider && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{ch.provider}</div>}
                </div>
              </div>

              {/* Stats grid — differs for wager vs multi */}
              {isWager ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    <div style={{ padding: '0.75rem 0.5rem', background: 'rgba(34, 197, 94, 0.06)', borderRadius: '8px', textAlign: 'center', border: '1px solid rgba(34, 197, 94, 0.15)' }}>
                      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Target</div>
                      <div style={{ fontWeight: 800, color: '#22c55e', fontSize: '1.35rem' }}>${Number(ch.target_multiplier).toLocaleString()}</div>
                    </div>
                    <div style={{ padding: '0.75rem 0.5rem', background: 'rgba(34, 197, 94, 0.06)', borderRadius: '8px', textAlign: 'center', border: '1px solid rgba(34, 197, 94, 0.15)' }}>
                      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Prize</div>
                      <div style={{ fontWeight: 800, color: '#fbbf24', fontSize: '1.35rem' }}>${Number(ch.prize_amount).toLocaleString()}</div>
                    </div>
                  </div>
                  {(ch.start_date || ch.end_date) && (
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                      {ch.start_date && (
                        <span style={{ padding: '0.3rem 0.75rem', background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700 }}>
                          STARTS {fmtDateCT(ch.start_date, { month: 'numeric', day: 'numeric' })}
                        </span>
                      )}
                      {ch.end_date && (
                        <span style={{ padding: '0.3rem 0.75rem', background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700 }}>
                          ENDS {fmtDateCT(ch.end_date, { month: 'numeric', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                  )}
                  {ch.end_date && !completed && <BountyTimer endDate={ch.end_date} />}
                </>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                  <div style={{ padding: '0.6rem 0.5rem', background: 'rgba(0,0,0,0.25)', borderRadius: '6px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Target</div>
                    <div style={{ fontWeight: 700, color: 'var(--gold)' }}>{Number(ch.target_multiplier).toLocaleString()}x</div>
                  </div>
                  <div style={{ padding: '0.6rem 0.5rem', background: 'rgba(0,0,0,0.25)', borderRadius: '6px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Min Bet</div>
                    <div style={{ fontWeight: 700 }}>${Number(ch.min_bet).toLocaleString()}</div>
                  </div>
                  <div style={{ padding: '0.6rem 0.5rem', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '6px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Prize</div>
                    <div style={{ fontWeight: 700, color: '#22c55e' }}>${Number(ch.prize_amount).toLocaleString()}</div>
                  </div>
                </div>
              )}

              {ch.notes && <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', lineHeight: 1.4, textAlign: isWager ? 'center' : 'left' }}>{ch.notes}</div>}
              {completed ? (
                <div style={{ padding: '0.75rem', background: 'rgba(34, 197, 94, 0.08)', borderRadius: '6px', fontSize: '0.85rem', textAlign: 'center' }}>
                  <div style={{ color: 'var(--text-dim)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.2rem' }}>Completed by</div>
                  <div style={{ fontWeight: 700, color: '#22c55e' }}>{ch.completed_by}</div>
                  {!isWager && ch.completed_multiplier && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '0.25rem' }}>
                      {Number(ch.completed_multiplier).toLocaleString()}x {ch.completed_bet ? `on $${Number(ch.completed_bet).toLocaleString()}` : ''}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <a href={affiliateLink} target="_blank" rel="noopener noreferrer" style={{ display: 'block', padding: '0.55rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', textDecoration: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, textAlign: 'center' }}>
                    Play Now
                  </a>
                  <button
                    onClick={() => { setClaimingChallenge(ch); setClaimForm({ discord_username: '', rainbet_username: rainbetDisplay(user), rainbet_id: '', bet_link: '', confirm: false }); }}
                    style={{ padding: '0.55rem', background: 'rgba(229, 231, 235, 0.1)', color: 'var(--text)', border: '1px solid rgba(229, 231, 235, 0.25)', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                  >Claim Win</button>
                </div>
              )}
              {isAdminUser && (
                <div style={{ display: 'grid', gridTemplateColumns: completed ? '1fr 1fr' : '1fr 1fr 1fr', gap: '0.4rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border)' }}>
                  <button
                    onClick={() => setEditingChallenge({ ...ch, target_multiplier: Number(ch.target_multiplier), min_bet: Number(ch.min_bet), prize_amount: Number(ch.prize_amount), challenge_type: ch.challenge_type || 'multi', start_date: ch.start_date ? ch.start_date.slice(0, 10) : '', end_date: ch.end_date ? ch.end_date.slice(0, 10) : '' })}
                    style={{ padding: '0.35rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'inherit' }}
                  >Edit</button>
                  {!completed && (
                    <button
                      onClick={() => { setCompletingChallenge(ch); setCompleteForm({ completed_by: '', completed_multiplier: '', completed_bet: '' }); }}
                      style={{ padding: '0.35rem', background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'inherit' }}
                    >Mark Done</button>
                  )}
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete "${ch.slot_name}" challenge?`)) return;
                      const token = getAuthToken();
                      const res = await fetch(`${BACKEND_API_URL}/api/admin/challenges/${ch.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                      if (res.ok) fetchChallenges();
                    }}
                    style={{ padding: '0.35rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'inherit' }}
                  >Delete</button>
                </div>
              )}
            </div>
            );
          };

          return (
            <>
              <div className="header" style={{ textAlign: 'center' }}>
                <h1 className="header-title"><span>Challenges</span> & <span>Bounties</span></h1>
                <p className="header-sub">Complete challenges and bounties to win prizes. Check each card for specific requirements.</p>
              </div>

              {/* Rules */}
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                  <div className="card-icon">📋</div>
                  <div className="card-title">General Rules</div>
                </div>
                <div style={{ padding: '1rem 1.25rem', display: 'grid', gap: '0.65rem' }}>
                  {[
                    { icon: '🫘', text: 'Your Rainbet account must be under code CAM' },
                    { icon: '🎯', text: 'Each challenge has its own target — check the card for details' },
                    { icon: '🏆', text: 'First person to complete a challenge or bounty can claim the prize' },
                    { icon: '📸', text: 'Admins will verify all claims — false claims may result in a ban' },
                    { icon: '⏰', text: 'Some challenges have time limits — check start and end dates' }
                  ].map((rule, i) => (
                    <div key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                      <span style={{ fontSize: '1rem', flexShrink: 0 }}>{rule.icon}</span>
                      <span style={{ lineHeight: 1.5 }}>{rule.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Active challenges */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Active Challenges ({challenges.active.length})</h2>
                {isAdminUser && (
                  <button
                    onClick={() => setEditingChallenge({ slot_name: '', slot_image: '', provider: '', target_multiplier: 100, min_bet: 1, prize_amount: 100, notes: '', status: 'active', challenge_type: 'multi', start_date: '', end_date: '' })}
                    style={{ padding: '0.5rem 1rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                  >+ New Challenge</button>
                )}
              </div>

              {challengesLoading && challenges.active.length === 0 ? (
                <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)', marginBottom: '1.5rem' }}>Loading challenges...</div>
              ) : challenges.active.length === 0 ? (
                <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)', marginBottom: '1.5rem' }}>
                  No active challenges yet. Check back soon!
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                  {challenges.active.map(ch => <ChallengeCard key={ch.id} ch={ch} completed={false} />)}
                </div>
              )}

              {/* Completed challenges */}
              {challenges.completed.length > 0 && (
                <>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.85rem', color: 'var(--text-dim)' }}>
                    Completed Challenges ({challenges.completed.length})
                  </h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
                    {challenges.completed.map(ch => <ChallengeCard key={ch.id} ch={ch} completed={true} />)}
                  </div>
                </>
              )}

              {/* Claim modal */}
              {claimingChallenge && (
                <div
                  onClick={(e) => { if (e.target === e.currentTarget) setClaimingChallenge(null); }}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '2rem 1rem', overflowY: 'auto' }}
                >
                  <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', maxWidth: '480px', width: '100%' }}>
                    <h3 style={{ marginBottom: '0.25rem', fontSize: '1.2rem' }}>{claimingChallenge.challenge_type === 'wager' ? 'Claim Bounty' : 'Claim Win'}</h3>
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginBottom: '1.25rem' }}>
                      {claimingChallenge.slot_name} · {claimingChallenge.challenge_type === 'wager' ? `$${Number(claimingChallenge.target_multiplier).toLocaleString()} wager target` : `${Number(claimingChallenge.target_multiplier).toLocaleString()}x target`} · ${Number(claimingChallenge.prize_amount).toLocaleString()} prize
                    </p>
                    <div style={{ display: 'grid', gap: '0.85rem' }}>
                      <label>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Discord username *</div>
                        <input type="text" value={claimForm.discord_username} onChange={(e) => setClaimForm({ ...claimForm, discord_username: e.target.value })} placeholder="e.g. yourname#1234" style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                      </label>
                      <label>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Rainbet username *</div>
                        <input type="text" value={claimForm.rainbet_username} onChange={(e) => setClaimForm({ ...claimForm, rainbet_username: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                      </label>
                      {claimingChallenge.challenge_type !== 'wager' && (
                        <>
                          <label>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Rainbet ID *</div>
                            <input type="text" value={claimForm.rainbet_id} onChange={(e) => setClaimForm({ ...claimForm, rainbet_id: e.target.value })} placeholder="e.g. 1234567" style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Find this in your Rainbet account settings — admins use it to verify ownership.</div>
                          </label>
                          <label>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Link to the winning bet *</div>
                            <input type="url" value={claimForm.bet_link} onChange={(e) => setClaimForm({ ...claimForm, bet_link: e.target.value })} placeholder="https://rainbet.com/bet/..." style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Get the share link for the specific bet from your Rainbet bet history.</div>
                          </label>
                        </>
                      )}
                      <div style={{ padding: '0.75rem', background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)', borderRadius: '6px', fontSize: '0.8rem', color: '#f59e0b' }}>
                        {claimingChallenge.challenge_type === 'wager'
                          ? '⚠️ Admin will verify your wager amount on the leaderboard. False claims may result in a ban from future challenges.'
                          : '⚠️ Rainbet doesn\'t auto-verify wins, so admins will manually check your bet link. False or invalid claims may get your account banned from future challenges.'}
                      </div>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={claimForm.confirm} onChange={(e) => setClaimForm({ ...claimForm, confirm: e.target.checked })} style={{ marginTop: '0.2rem' }} />
                        <span>{claimingChallenge.challenge_type === 'wager' ? 'I confirm I have reached the wager target and the details above are accurate.' : 'I confirm the Rainbet ID and bet link are mine and accurate.'}</span>
                      </label>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
                      <button onClick={() => setClaimingChallenge(null)} style={{ padding: '0.55rem 1.1rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                      <button onClick={submitClaim} style={{ padding: '0.55rem 1.25rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>Submit Claim</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Admin: complete modal */}
              {completingChallenge && (
                <div
                  onClick={(e) => { if (e.target === e.currentTarget) setCompletingChallenge(null); }}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
                >
                  <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', maxWidth: '440px', width: '100%' }}>
                    <h3 style={{ marginBottom: '0.25rem', fontSize: '1.2rem' }}>Mark Complete</h3>
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginBottom: '1.25rem' }}>{completingChallenge.slot_name}</p>
                    <div style={{ display: 'grid', gap: '0.85rem' }}>
                      <label>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Completed by (username shown on card) *</div>
                        <input type="text" value={completeForm.completed_by} onChange={(e) => setCompleteForm({ ...completeForm, completed_by: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                      </label>
                      {(completingChallenge.challenge_type || 'multi') === 'multi' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                          <label>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Multiplier hit</div>
                            <input type="number" value={completeForm.completed_multiplier} onChange={(e) => setCompleteForm({ ...completeForm, completed_multiplier: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                          </label>
                          <label>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Bet size</div>
                            <input type="number" value={completeForm.completed_bet} onChange={(e) => setCompleteForm({ ...completeForm, completed_bet: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                          </label>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
                      <button onClick={() => setCompletingChallenge(null)} style={{ padding: '0.55rem 1.1rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                      <button onClick={saveCompletion} style={{ padding: '0.55rem 1.25rem', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>Mark Complete</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Admin: edit/create modal */}
              {editingChallenge && (
                <div
                  onClick={(e) => { if (e.target === e.currentTarget) setEditingChallenge(null); }}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '2rem 1rem', overflowY: 'auto' }}
                >
                  <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', maxWidth: '520px', width: '100%' }}>
                    <h3 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>{editingChallenge.id ? 'Edit Challenge' : 'New Challenge'}</h3>
                    <div style={{ display: 'grid', gap: '0.85rem' }}>
                      {/* Type toggle */}
                      <div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.35rem' }}>Type</div>
                        <div style={{ display: 'flex', gap: '0.35rem' }}>
                          {['multi', 'wager'].map(t => (
                            <button key={t} onClick={() => setEditingChallenge({ ...editingChallenge, challenge_type: t })}
                              style={{ flex: 1, padding: '0.5rem', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: 'none',
                                background: editingChallenge.challenge_type === t ? 'rgba(34, 211, 238, 0.15)' : 'rgba(0,0,0,0.2)',
                                color: editingChallenge.challenge_type === t ? '#22d3ee' : 'var(--text-dim)'
                              }}>{t === 'multi' ? '🎰 Multiplier' : '💰 Wager Bounty'}</button>
                          ))}
                        </div>
                      </div>
                      <label>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>{editingChallenge.challenge_type === 'wager' ? 'Bounty name *' : 'Slot name *'}</div>
                        <input type="text" value={editingChallenge.slot_name ?? ''} onChange={(e) => setEditingChallenge({ ...editingChallenge, slot_name: e.target.value })} placeholder={editingChallenge.challenge_type === 'wager' ? 'e.g. $1,000,000 Wager Bounty' : 'e.g. Sugar Rush 1000'} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>{editingChallenge.challenge_type === 'wager' ? 'Category' : 'Provider'}</div>
                          <input type="text" value={editingChallenge.provider ?? ''} onChange={(e) => setEditingChallenge({ ...editingChallenge, provider: e.target.value })} placeholder={editingChallenge.challenge_type === 'wager' ? 'e.g. ANY' : 'Hacksaw, Pragmatic...'} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                        </label>
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Status</div>
                          <select value={editingChallenge.status ?? 'active'} onChange={(e) => setEditingChallenge({ ...editingChallenge, status: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }}>
                            <option value="active">active</option>
                            <option value="completed">completed</option>
                            <option value="hidden">hidden</option>
                          </select>
                        </label>
                      </div>
                      <label>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Image URL (optional)</div>
                        <input type="url" value={editingChallenge.slot_image ?? ''} onChange={(e) => setEditingChallenge({ ...editingChallenge, slot_image: e.target.value })} placeholder="https://..." style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                      </label>
                      {editingChallenge.challenge_type === 'wager' ? (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                            <label>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Wager target ($) *</div>
                              <input type="number" value={editingChallenge.target_multiplier ?? 0} onChange={(e) => setEditingChallenge({ ...editingChallenge, target_multiplier: Number(e.target.value) })} placeholder="1000000" style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                            </label>
                            <label>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Prize ($) *</div>
                              <input type="number" value={editingChallenge.prize_amount ?? 0} onChange={(e) => setEditingChallenge({ ...editingChallenge, prize_amount: Number(e.target.value) })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                            </label>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                            <label>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Start date</div>
                              <input type="date" value={editingChallenge.start_date ?? ''} onChange={(e) => setEditingChallenge({ ...editingChallenge, start_date: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                            </label>
                            <label>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>End date</div>
                              <input type="date" value={editingChallenge.end_date ?? ''} onChange={(e) => setEditingChallenge({ ...editingChallenge, end_date: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                            </label>
                          </div>
                        </>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                          <label>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Target multi *</div>
                            <input type="number" value={editingChallenge.target_multiplier ?? 0} onChange={(e) => setEditingChallenge({ ...editingChallenge, target_multiplier: Number(e.target.value) })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                          </label>
                          <label>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Min bet *</div>
                            <input type="number" value={editingChallenge.min_bet ?? 0} onChange={(e) => setEditingChallenge({ ...editingChallenge, min_bet: Number(e.target.value) })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                          </label>
                          <label>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Prize *</div>
                            <input type="number" value={editingChallenge.prize_amount ?? 0} onChange={(e) => setEditingChallenge({ ...editingChallenge, prize_amount: Number(e.target.value) })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                          </label>
                        </div>
                      )}
                      <label>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Notes (optional)</div>
                        <textarea value={editingChallenge.notes ?? ''} onChange={(e) => setEditingChallenge({ ...editingChallenge, notes: e.target.value })} rows={2} placeholder={editingChallenge.challenge_type === 'wager' ? 'e.g. First person to reach 1 Million Wagered - 1 can claim' : ''} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', resize: 'vertical' }} />
                      </label>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
                      <button onClick={() => setEditingChallenge(null)} style={{ padding: '0.55rem 1.1rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                      <button onClick={saveEdit} style={{ padding: '0.55rem 1.25rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>Save</button>
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {activePage === 'pointsshop' && (() => {
          const cfg = pointsConfig || {};
          const enabled = cfg.points_system_enabled?.value !== 'false';
          const wagerEnabled = cfg.wager_tracking_enabled?.value === 'true';
          const kickChatEnabled = cfg.kick_chat_tracking_enabled?.value === 'true';
          const chatEnabled = cfg.twitch_chat_tracking_enabled?.value === 'true';
          const watchEnabled = cfg.twitch_watch_tracking_enabled?.value === 'true';
          const discordEnabled = cfg.discord_tracking_enabled?.value === 'true';
          const dollarsPerPt = Number(cfg.wager_dollars_per_point?.value || 50);
          const ptsPerChat = Number(cfg.points_per_twitch_chat?.value || 1);
          const chatCd = Number(cfg.twitch_chat_cooldown_seconds?.value || 30);
          const ptsPerHr = Number(cfg.points_per_watch_hour?.value || 10);
          const ptsPerDiscord = Number(cfg.points_per_discord_message?.value || 1);

          const handleRedeem = async () => {
            if (!user) { handleLogin(); return; }
            if (!redeemingItem) return;
            if (redeemingItem.requires_slot_choice && !redeemSlotChoice.trim()) {
              alert('Enter the slot you want before redeeming this item.');
              return;
            }
            try {
              const token = getAuthToken();
              const res = await fetch(`${BACKEND_API_URL}/api/points/redeem`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ itemId: redeemingItem.id, notes: redeemNotes, slotChoice: redeemSlotChoice.trim() })
              });
              const data = await res.json();
              if (!res.ok) { alert(data.error || 'Redemption failed'); return; }
              setPointsBalance(data.newBalance);
              setRedeemingItem(null);
              setRedeemNotes('');
              setRedeemSlotChoice('');
              fetchPointsShop();
              fetchMyRedemptions();
              alert('Redeemed! Admin will fulfill it shortly.');
            } catch (e) { alert('Network error'); }
          };

          return (
            <>
              <div className="header" style={{ textAlign: 'center' }}>
                <h1 className="header-title">Points <span>Shop</span></h1>
                <p className="header-sub">Earn points by being part of the community. Spend them on rewards.</p>
                <div style={{ display: 'inline-block', marginTop: '0.75rem', padding: '0.45rem 1rem', background: 'rgba(34, 211, 238, 0.08)', border: '1px solid rgba(34, 211, 238, 0.2)', borderRadius: '8px', fontSize: '0.78rem', color: '#22d3ee', fontWeight: 600 }}>
                  ⚠️ Some rewards require your Rainbet account to be registered under code <strong>CAM</strong>
                </div>
              </div>

              {!enabled && (
                <div className="card" style={{ padding: '1rem 1.25rem', marginBottom: '1.25rem', background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)', color: '#f59e0b' }}>
                  ⚠️ The points system is currently disabled by an admin.
                </div>
              )}

              {/* Balance + earning summary */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div className="card" style={{ padding: '1.5rem', background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(245, 158, 11, 0.02))', border: '1px solid rgba(245, 158, 11, 0.25)', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.25rem' }}>Your Balance</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#f59e0b' }}>
                    {user ? (pointsBalance ?? '…') : '—'}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginTop: '0.25rem' }}>points</div>
                  {!user && (
                    <button onClick={handleLogin} style={{ marginTop: '0.85rem', padding: '0.5rem 1rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 600 }}>Sign in to earn</button>
                  )}
                </div>
                <div className="card" style={{ padding: '1rem 1.25rem' }}>
                  <div style={{ fontWeight: 700, marginBottom: '0.65rem', fontSize: '0.95rem' }}>How to earn points</div>
                  <div style={{ display: 'grid', gap: '0.4rem', fontSize: '0.85rem' }}>
                    <EarnRow active={wagerEnabled} icon="💵" text={`${1} pt per $${dollarsPerPt} wagered on Rainbet under code CAM`} />
                    <EarnRow active={kickChatEnabled} icon="💬" text={`${ptsPerChat} pt per Kick chat message (${chatCd}s cooldown)`} />
                    <EarnRow active={chatEnabled} icon="💜" text={`${ptsPerChat} pt per Twitch chat message (${chatCd}s cooldown)`} />
                    <EarnRow active={watchEnabled} icon="📺" text={`${ptsPerHr} pts per hour watching the stream`} />
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid var(--border)' }}>
                {[
                  { key: 'shop', label: 'Shop' },
                  { key: 'history', label: 'My History', requiresUser: true },
                  { key: 'redemptions', label: 'My Redemptions', requiresUser: true },
                  { key: 'leaderboard', label: 'Leaderboard' }
                ].map(t => {
                  const disabled = t.requiresUser && !user;
                  return (
                    <button
                      key={t.key}
                      onClick={() => !disabled && setPointsTab(t.key)}
                      disabled={disabled}
                      style={{
                        padding: '0.65rem 1.1rem',
                        background: 'transparent',
                        color: pointsTab === t.key ? 'var(--text)' : 'var(--text-dim)',
                        border: 'none',
                        borderBottom: pointsTab === t.key ? '2px solid #f59e0b' : '2px solid transparent',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        fontFamily: 'inherit',
                        marginBottom: '-1px',
                        opacity: disabled ? 0.4 : 1
                      }}
                    >{t.label}</button>
                  );
                })}
              </div>

              {/* Shop */}
              {pointsTab === 'shop' && (
                <div>
                  {pointsShop.length === 0 ? (
                    <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)' }}>
                      No items in the shop yet. Check back soon!
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
                      {pointsShop.map(item => {
                        const canAfford = user && pointsBalance != null && pointsBalance >= item.cost;
                        const outOfStock = item.stock !== null && item.stock <= 0;
                        return (
                          <div key={item.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {item.image_url ? (
                              <img src={item.image_url} alt={item.name} style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: '8px' }} />
                            ) : (
                              <div style={{ width: '100%', height: 140, background: 'rgba(0,0,0,0.3)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem' }}>
                                {item.category === 'bonus' ? '🎰' : item.category === 'merch' ? '👕' : '🎁'}
                              </div>
                            )}
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '1rem' }}>{item.name}</div>
                              {item.category && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: '0.15rem' }}>{item.category}</div>}
                            </div>
                            {item.description && <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', lineHeight: 1.4 }}>{item.description}</div>}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '0.5rem', borderTop: '1px solid var(--border)' }}>
                              <div>
                                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#f59e0b' }}>{item.cost.toLocaleString()} pts</div>
                                {item.stock !== null && (
                                  <div style={{ fontSize: '0.7rem', color: outOfStock ? '#ef4444' : 'var(--text-muted)' }}>
                                    {outOfStock ? 'Out of stock' : `${item.stock} left`}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => { setRedeemingItem(item); setRedeemNotes(''); }}
                                disabled={!canAfford || outOfStock}
                                style={{
                                  padding: '0.55rem 1rem',
                                  background: (canAfford && !outOfStock) ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(255,255,255,0.05)',
                                  color: (canAfford && !outOfStock) ? '#fff' : 'var(--text-muted)',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.85rem',
                                  fontWeight: 600,
                                  cursor: (canAfford && !outOfStock) ? 'pointer' : 'not-allowed',
                                  fontFamily: 'inherit'
                                }}
                              >{outOfStock ? 'Sold out' : !user ? 'Sign in' : !canAfford ? 'Need more' : 'Redeem'}</button>
                            </div>
                            {(item.requires_rainbet_username || item.requires_discord_username || item.requires_slot_choice) && (
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                Requires: {[item.requires_rainbet_username && 'Rainbet username', item.requires_discord_username && 'Discord linked', item.requires_slot_choice && 'slot choice'].filter(Boolean).join(', ')}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Recent redemptions strip */}
                  {pointsRecentRedemptions.length > 0 && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>Recently redeemed</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {pointsRecentRedemptions.slice(0, 12).map(r => (
                          <div key={r.id} style={{ padding: '0.4rem 0.75rem', background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.15)', borderRadius: '999px', fontSize: '0.75rem', color: 'var(--text-dim)' }}>
                            <strong style={{ color: '#f59e0b' }}>{r.user_display_name}</strong> · {r.item_name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* History */}
              {pointsTab === 'history' && user && (
                <div className="card">
                  <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 100px', gap: '0.5rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', fontWeight: 700 }}>
                    <div>When</div><div>Source</div><div>Detail</div><div style={{ textAlign: 'right' }}>Amount</div>
                  </div>
                  {pointsHistory.items.length === 0 ? (
                    <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-dim)' }}>No transactions yet. Earn or redeem to see them here.</div>
                  ) : (
                    pointsHistory.items.map(tx => (
                      <div key={tx.id} style={{ padding: '0.65rem 1rem', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 100px', gap: '0.5rem', borderBottom: '1px solid var(--border)', fontSize: '0.85rem', alignItems: 'center' }}>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{new Date(tx.created_at).toLocaleString()}</div>
                        <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.78rem' }}>{tx.source}</div>
                        <div style={{ color: 'var(--text-dim)', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.source_detail || '—'}</div>
                        <div style={{ textAlign: 'right', fontWeight: 700, color: tx.amount > 0 ? '#22c55e' : '#ef4444' }}>{tx.amount > 0 ? '+' : ''}{tx.amount}</div>
                      </div>
                    ))
                  )}
                  {pointsHistory.total > 20 && (
                    <div style={{ padding: '0.75rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                      <button onClick={() => fetchPointsHistory(Math.max(1, pointsHistory.page - 1))} disabled={pointsHistory.page === 1} style={{ padding: '0.4rem 0.85rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '6px', cursor: pointsHistory.page === 1 ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontSize: '0.8rem' }}>← Prev</button>
                      <span style={{ alignSelf: 'center', fontSize: '0.8rem', color: 'var(--text-dim)' }}>Page {pointsHistory.page} of {Math.ceil(pointsHistory.total / 20)}</span>
                      <button onClick={() => fetchPointsHistory(pointsHistory.page + 1)} disabled={pointsHistory.page * 20 >= pointsHistory.total} style={{ padding: '0.4rem 0.85rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.8rem' }}>Next →</button>
                    </div>
                  )}
                </div>
              )}

              {/* My Redemptions */}
              {pointsTab === 'redemptions' && user && (
                <div className="card">
                  {myRedemptions.length === 0 ? (
                    <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-dim)' }}>No redemptions yet.</div>
                  ) : (
                    myRedemptions.map(r => {
                      const statusColor = r.status === 'fulfilled' ? '#22c55e' : r.status === 'cancelled' ? '#ef4444' : '#f59e0b';
                      return (
                        <div key={r.id} style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0.75rem', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 700 }}>{r.item_name}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{new Date(r.created_at).toLocaleString()}</div>
                            {r.slot_choice && <div style={{ fontSize: '0.78rem', color: '#fbbf24', marginTop: '0.2rem' }}>🎰 Slot: {r.slot_choice}</div>}
                            {r.admin_notes && <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginTop: '0.25rem' }}>Note: {r.admin_notes}</div>}
                          </div>
                          <div style={{ fontWeight: 700, color: '#f59e0b' }}>{r.points_spent} pts</div>
                          <div style={{ padding: '0.25rem 0.65rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: statusColor, background: `${statusColor}15`, border: `1px solid ${statusColor}40` }}>{r.status}</div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Leaderboard */}
              {pointsTab === 'leaderboard' && (
                <div className="card">
                  <div className="card-header">
                    <div className="card-icon">🏆</div>
                    <div className="card-title">Points Leaderboard</div>
                  </div>
                  {pointsLeaderboard.items.length === 0 ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)' }}>No earners yet.</div>
                  ) : (
                    <>
                      {/* Top 3 podium row — only on page 1 */}
                      {pointsLeaderboard.page === 1 && pointsLeaderboard.items.length >= 1 && (
                        <div style={{ padding: '1.25rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', background: 'rgba(245, 158, 11, 0.04)', borderBottom: '1px solid var(--border)' }}>
                          {pointsLeaderboard.items.slice(0, 3).map((u, i) => {
                            const rank = i + 1;
                            const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : '🥉';
                            const color = rank === 1 ? '#f5a623' : rank === 2 ? '#c0c0c0' : '#cd7f32';
                            return (
                              <div key={u.id}
                                onClick={() => { if (user?.isAdmin) { setAdminUserInspect(u); setAdminUserInspectTab('history'); setAdminUserHistory({ items: [], total: 0, page: 1, loading: false }); setAdminUserRedemptions({ items: [], loading: false }); fetchAdminUserHistory(u.id, 1); } }}
                                style={{ padding: '1rem', background: 'rgba(0,0,0,0.2)', border: `1px solid ${color}40`, borderRadius: '10px', textAlign: 'center', cursor: user?.isAdmin ? 'pointer' : 'default', transition: 'border-color 0.15s', position: 'relative' }}
                                onMouseEnter={e => { if (user?.isAdmin) e.currentTarget.style.borderColor = color; }}
                                onMouseLeave={e => { if (user?.isAdmin) e.currentTarget.style.borderColor = `${color}40`; }}
                              >
                                {user?.isAdmin && <div style={{ position: 'absolute', top: '0.4rem', right: '0.5rem', fontSize: '0.65rem', color: 'var(--text-muted)', opacity: 0.6 }}>🔍</div>}
                                <div style={{ fontSize: '1.75rem', marginBottom: '0.25rem' }}>{medal}</div>
                                {u.profile_image_url ? (
                                  <img src={u.profile_image_url} alt="" style={{ width: 48, height: 48, borderRadius: '50%', marginBottom: '0.4rem', border: `2px solid ${color}` }} />
                                ) : (
                                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.95rem', fontWeight: 700, marginBottom: '0.4rem', margin: '0 auto 0.4rem', border: `2px solid ${color}` }}>{u.display_name?.slice(0, 2).toUpperCase()}</div>
                                )}
                                <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.2rem' }}>{displayUser(u.display_name)}</div>
                                <div style={{ fontWeight: 800, color: '#f59e0b', fontSize: '1.1rem' }}>{u.balance.toLocaleString()}</div>
                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '0.15rem' }}>points</div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Rest of the list (rank 4+) */}
                      {pointsLeaderboard.items.slice(pointsLeaderboard.page === 1 ? 3 : 0).map((u, i) => {
                        const offset = (pointsLeaderboard.page - 1) * 20;
                        const rank = offset + i + (pointsLeaderboard.page === 1 ? 4 : 1);
                        const isCurrentUser = user?.id && u.id === user.id;
                        return (
                          <div key={u.id}
                            onClick={() => { if (user?.isAdmin) { setAdminUserInspect(u); setAdminUserInspectTab('history'); setAdminUserHistory({ items: [], total: 0, page: 1, loading: false }); setAdminUserRedemptions({ items: [], loading: false }); fetchAdminUserHistory(u.id, 1); } }}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '50px 40px 1fr auto',
                              gap: '0.75rem',
                              alignItems: 'center',
                              padding: '0.65rem 1rem',
                              borderBottom: '1px solid var(--border)',
                              background: isCurrentUser ? 'rgba(245, 158, 11, 0.08)' : 'transparent',
                              cursor: user?.isAdmin ? 'pointer' : 'default',
                              transition: 'background 0.12s',
                            }}
                            onMouseEnter={e => { if (user?.isAdmin) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = isCurrentUser ? 'rgba(245, 158, 11, 0.08)' : 'transparent'; }}
                          >
                            <div style={{ fontWeight: 700, color: 'var(--text-muted)' }}>#{rank}</div>
                            {u.profile_image_url ? (
                              <img src={u.profile_image_url} alt="" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                            ) : (
                              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 }}>{u.display_name?.slice(0, 2).toUpperCase()}</div>
                            )}
                            <div style={{ fontWeight: 600 }}>
                              {displayUser(u.display_name)}
                              {isCurrentUser && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', padding: '0.1rem 0.45rem', background: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', borderRadius: '999px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>You</span>}
                              {user?.isAdmin && <span style={{ marginLeft: '0.4rem', fontSize: '0.65rem', color: 'var(--text-muted)', opacity: 0.5 }}>🔍</span>}
                            </div>
                            <div style={{ fontWeight: 700, color: '#f59e0b' }}>{u.balance.toLocaleString()} pts</div>
                          </div>
                        );
                      })}

                      {/* Pagination */}
                      {pointsLeaderboard.total > 20 && (
                        <div style={{ padding: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', borderTop: '1px solid var(--border)' }}>
                          <button
                            onClick={() => fetchPointsLeaderboard(pointsLeaderboard.page - 1)}
                            disabled={pointsLeaderboard.page <= 1}
                            style={{ padding: '0.45rem 1rem', background: pointsLeaderboard.page <= 1 ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.08)', color: pointsLeaderboard.page <= 1 ? 'var(--text-muted)' : 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.85rem', cursor: pointsLeaderboard.page <= 1 ? 'not-allowed' : 'pointer' }}
                          >← Prev</button>
                          <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                            Page {pointsLeaderboard.page} of {Math.max(1, Math.ceil(pointsLeaderboard.total / 20))}
                          </span>
                          <button
                            onClick={() => fetchPointsLeaderboard(pointsLeaderboard.page + 1)}
                            disabled={pointsLeaderboard.page >= Math.ceil(pointsLeaderboard.total / 20)}
                            style={{ padding: '0.45rem 1rem', background: pointsLeaderboard.page >= Math.ceil(pointsLeaderboard.total / 20) ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.08)', color: pointsLeaderboard.page >= Math.ceil(pointsLeaderboard.total / 20) ? 'var(--text-muted)' : 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.85rem', cursor: pointsLeaderboard.page >= Math.ceil(pointsLeaderboard.total / 20) ? 'not-allowed' : 'pointer' }}
                          >Next →</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Redeem confirmation modal */}
              {redeemingItem && (
                <div onClick={(e) => { if (e.target === e.currentTarget) { setRedeemingItem(null); setRedeemSlotChoice(''); } }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
                  <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', maxWidth: '440px', width: '100%' }}>
                    <h3 style={{ marginBottom: '0.5rem', fontSize: '1.2rem' }}>Confirm Redemption</h3>
                    <p style={{ fontSize: '0.9rem', color: 'var(--text-dim)', marginBottom: '1rem' }}>
                      You're about to spend <strong style={{ color: '#f59e0b' }}>{redeemingItem.cost} points</strong> on <strong>{redeemingItem.name}</strong>.
                    </p>
                    {redeemingItem.fulfillment_notes && (
                      <div style={{ padding: '0.75rem', background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '6px', fontSize: '0.85rem', color: '#93c5fd', marginBottom: '0.85rem' }}>
                        ℹ️ {redeemingItem.fulfillment_notes}
                      </div>
                    )}
                    {redeemingItem.requires_slot_choice && (
                      <label style={{ display: 'block', marginBottom: '0.85rem' }}>
                        <div style={{ fontSize: '0.8rem', color: '#fbbf24', marginBottom: '0.25rem', fontWeight: 700 }}>🎰 Slot choice <span style={{ color: '#ef4444' }}>*</span></div>
                        <input type="text" value={redeemSlotChoice} onChange={(e) => setRedeemSlotChoice(e.target.value)} maxLength={200} placeholder="e.g. Le Bandit"
                          style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid rgba(251, 191, 36, 0.35)', borderRadius: '6px', fontFamily: 'inherit' }} />
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>Required — tell us which slot you want for this reward.</div>
                      </label>
                    )}
                    <label>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Notes for admin (optional)</div>
                      <textarea value={redeemNotes} onChange={(e) => setRedeemNotes(e.target.value)} rows={2} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', resize: 'vertical' }} />
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
                      <button onClick={() => { setRedeemingItem(null); setRedeemSlotChoice(''); }} style={{ padding: '0.55rem 1.1rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                      <button onClick={handleRedeem} style={{ padding: '0.55rem 1.25rem', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>Confirm Redeem</button>
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {activePage === 'games' && (
          <div className="games-container">
            {!selectedGame && (
              <>
                <div className="header">
                  <h1 className="header-title">🎰 <span>Games</span></h1>
                  <p className="header-sub">Choose your game and let fate decide!</p>
                </div>
                <div className="games-selection">
                  <div className="game-select-card" onClick={() => setSelectedGame('rejuicer')}>
                    <div className="game-select-icon">🎡</div>
                    <div className="game-select-title">Rejuicer Wheel</div>
                    <div className="game-select-desc">Spin the wheel to pick your next rejuice game!</div>
                  </div>
                  <div className="game-select-card" onClick={() => setSelectedGame('slots')}>
                    <div className="game-select-icon">🎰</div>
                    <div className="game-select-title">Slot Picker</div>
                    <div className="game-select-desc">Can't decide which slot to play? Let the reel choose!</div>
                  </div>
                </div>
              </>
            )}

            {selectedGame === 'rejuicer' && (
              <>
                <button className="back-btn" onClick={() => setSelectedGame(null)}>← Back to Games</button>
                <div className="header">
                  <h1 className="header-title">🎡 <span>Rejuicer Wheel</span></h1>
                  <p className="header-sub">Spin the wheel to find your next game!</p>
                </div>

                <div className="wheel-wrapper">
                  <div className="wheel-pointer" />
                  <svg className="wheel-svg" viewBox="0 0 100 100" style={{ transform: `rotate(${wheelRotation}deg)` }}>
                    <circle cx="50" cy="50" r="48" fill="#1a1a2e" stroke="var(--gold)" strokeWidth="2"/>
                    {casinoGames.map((game, i) => {
                      const segmentAngle = 360 / casinoGames.length;
                      const startAngle = i * segmentAngle - 90;
                      const endAngle = startAngle + segmentAngle;
                      const startRad = (startAngle * Math.PI) / 180;
                      const endRad = (endAngle * Math.PI) / 180;
                      const x1 = 50 + 48 * Math.cos(startRad);
                      const y1 = 50 + 48 * Math.sin(startRad);
                      const x2 = 50 + 48 * Math.cos(endRad);
                      const y2 = 50 + 48 * Math.sin(endRad);
                      const midAngle = startAngle + segmentAngle / 2;
                      const midRad = (midAngle * Math.PI) / 180;
                      const textX = 50 + 32 * Math.cos(midRad);
                      const textY = 50 + 32 * Math.sin(midRad);
                      return (
                        <g key={game.name}>
                          <path d={`M 50 50 L ${x1} ${y1} A 48 48 0 0 1 ${x2} ${y2} Z`} fill={game.color} stroke="#000" strokeWidth="0.5"/>
                          <text x={textX} y={textY} fill="#fff" fontSize="4" fontWeight="bold" textAnchor="middle" dominantBaseline="middle" transform={`rotate(${midAngle + 90}, ${textX}, ${textY})`}>
                            {game.name.length > 10 ? game.name.slice(0, 9) + '..' : game.name}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                  <div className="wheel-center">🎲</div>
                </div>

                <div style={{ textAlign: 'center' }}>
                  <button className="spin-btn" onClick={spinWheel} disabled={wheelSpinning}>
                    {wheelSpinning ? '🎡 Spinning...' : '🎡 Spin the Wheel'}
                  </button>
                </div>

                {wheelResult && (
                  <div className="wheel-result">
                    <div className="result-label">Your next game is...</div>
                    <div className="result-game">{wheelResult}</div>
                  </div>
                )}
              </>
            )}

            {selectedGame === 'slots' && (
              <>
                <button className="back-btn" onClick={() => setSelectedGame(null)}>← Back to Games</button>
                <div className="header">
                  <h1 className="header-title">🎰 <span>Slot Picker</span></h1>
                  <p className="header-sub">Can't decide which slot to play? Let the reel choose!</p>
                </div>

                <div className="slot-reel-wrapper">
                  <div className="slot-reel-container">
                    <div className="slot-reel-viewport">
                      <div className="slot-reel-strip" style={{ 
                        transform: `translateX(${slotRotation}px)`, 
                        transition: slotIsResetting ? 'none' : (slotSpinning ? 'transform 4s cubic-bezier(0.15, 0.85, 0.35, 1)' : 'none')
                      }}>
                        {[...Array(20)].flatMap((_, i) => reelSlots.map((slot, j) => (
                          <div key={`${i}-${j}`} className="slot-reel-item">
                            <img src={slot.image} alt={slot.name} />
                          </div>
                        )))}
                      </div>
                    </div>
                    <div className="slot-reel-gradient-left" />
                    <div className="slot-reel-gradient-right" />
                    <div className="slot-reel-pointer" />
                  </div>
                </div>

                <div className="slot-buttons">
                  <button className="spin-btn" onClick={spinSlotPicker} disabled={slotSpinning || filteredSlots.length === 0}>
                    {slotSpinning ? '🎰 Spinning...' : '🎰 Pick a Slot'}
                  </button>
                  <button className="shuffle-btn" onClick={shuffleSlots} disabled={slotSpinning}>🔀 Shuffle</button>
                </div>

                {slotResultPopup && slotResult && (
                  <div className="slot-popup-overlay" onClick={() => setSlotResultPopup(false)}>
                    <div className="slot-popup" onClick={(e) => e.stopPropagation()}>
                      <button className="slot-popup-close" onClick={() => setSlotResultPopup(false)}>✕</button>
                      <div className="slot-popup-label">Your slot is...</div>
                      <a href={getRainbetUrl(slotResult)} target="_blank" rel="noopener noreferrer" className="slot-popup-game-link">
                        <img src={slotResult.image} alt={slotResult.name} className="slot-popup-image" />
                        <div className="slot-popup-name">{slotResult.name}</div>
                        <div className="slot-popup-provider">{slotResult.provider}</div>
                        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '0.45rem' }}>
                          {slotResult.rtp != null && (
                            <span style={{ padding: '0.15rem 0.6rem', background: 'rgba(34, 197, 94, 0.12)', border: '1px solid rgba(34, 197, 94, 0.35)', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, color: '#22c55e' }}>RTP {slotResult.rtp}%</span>
                          )}
                          {slotResult.bonusBuy === true && (
                            <span style={{ padding: '0.15rem 0.6rem', background: 'rgba(251, 191, 36, 0.12)', border: '1px solid rgba(251, 191, 36, 0.35)', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, color: '#fbbf24' }}>💰 Bonus Buy</span>
                          )}
                          {slotResult.isNew && (
                            <span style={{ padding: '0.15rem 0.6rem', background: 'rgba(34, 211, 238, 0.12)', border: '1px solid rgba(34, 211, 238, 0.35)', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, color: '#22d3ee' }}>🆕 New</span>
                          )}
                        </div>
                      </a>
                      <a href={getRainbetUrl(slotResult)} target="_blank" rel="noopener noreferrer" className="slot-popup-play-btn">🎰 Play Now on Rainbet</a>
                      <button className="slot-popup-respin-btn" onClick={() => { setSlotResultPopup(false); setTimeout(() => spinSlotPicker(), 100); }}>🔄 Spin Again</button>
                    </div>
                  </div>
                )}

                <div className="filter-section">
                  {(() => {
                    // Curated "main providers" list (mirrors the snutzy picker)
                    const MAIN_PROVIDERS = ['Pragmatic Play', 'Hacksaw', 'Nolimit', 'BGaming', 'Backseat Gaming', 'Bullshark Games', 'Clutch Gaming', 'Print Studios', 'Shady Lady', 'Peter & Sons'];
                    const mainProviders = allProviders.filter(p => MAIN_PROVIDERS.includes(p));
                    // Switch-style toggle so hide-filters read differently from action buttons
                    const Toggle = ({ on, color, label, onClick }) => (
                      <span onClick={onClick} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.4rem 0.8rem 0.4rem 0.5rem', borderRadius: '999px', cursor: 'pointer', userSelect: 'none',
                        fontSize: '0.8rem', fontWeight: 700, fontFamily: 'inherit',
                        background: on ? `rgba(${color}, 0.14)` : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${on ? `rgba(${color}, 0.55)` : 'var(--border)'}`,
                        color: on ? `rgb(${color})` : 'var(--text-dim)',
                        transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease'
                      }}>
                        <span style={{
                          position: 'relative', width: '30px', height: '16px', borderRadius: '999px', flexShrink: 0,
                          background: on ? `rgb(${color})` : 'rgba(255,255,255,0.15)', transition: 'background 0.15s ease'
                        }}>
                          <span style={{
                            position: 'absolute', top: '2px', left: on ? '16px' : '2px',
                            width: '12px', height: '12px', borderRadius: '50%', background: '#fff',
                            transition: 'left 0.15s ease'
                          }} />
                        </span>
                        {label}
                      </span>
                    );
                    return (
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
                        <input
                          type="text"
                          placeholder="🔍 Search providers..."
                          value={slotSearch}
                          onChange={e => setSlotSearch(e.target.value)}
                          style={{
                            flex: '1 1 180px', minWidth: '150px', padding: '0.5rem 0.8rem',
                            background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '999px',
                            color: 'var(--text)', fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none'
                          }}
                        />
                        <Toggle on={slotBonusBuyOnly} color="251, 191, 36" label="💰 Only Buyable Bonuses" onClick={() => setSlotBonusBuyOnly(v => !v)} />
                        <Toggle on={slotNewOnly} color="34, 211, 238" label="🆕 Only New Releases" onClick={() => setSlotNewOnly(v => !v)} />
                        <button
                          onClick={() => setSelectedProviders(mainProviders)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                            padding: '0.45rem 0.9rem', borderRadius: '999px', cursor: 'pointer',
                            fontSize: '0.8rem', fontWeight: 700, fontFamily: 'inherit',
                            background: 'rgba(167, 139, 250, 0.12)', border: '1px solid rgba(167, 139, 250, 0.4)', color: '#a78bfa'
                          }}
                        >
                          ⭐ Main Providers
                        </button>
                      </div>
                    );
                  })()}
                  <div className="filter-header">
                    <span className="filter-label">🎯 Filter by Provider</span>
                    <div className="filter-actions">
                      <button className="filter-action-btn" onClick={() => setSelectedProviders(allProviders)}>All</button>
                      <button className="filter-action-btn" onClick={() => setSelectedProviders([allProviders[0]])}>None</button>
                    </div>
                  </div>
                  <div className="provider-grid">
                    {allProviders
                      .filter(provider => !slotSearch.trim() || provider.toLowerCase().includes(slotSearch.trim().toLowerCase()))
                      .map(provider => (
                        <label key={provider} className={`provider-checkbox ${selectedProviders.includes(provider) ? 'checked' : ''}`}>
                          <input type="checkbox" checked={selectedProviders.includes(provider)} onChange={() => toggleProvider(provider)} />
                          <span className="checkmark">✓</span>
                          <span className="provider-name">{provider}</span>
                        </label>
                      ))}
                  </div>
                  <div className="slot-count">{filteredSlots.length} slots selected</div>
                </div>
              </>
            )}
          </div>
        )}

        {activePage === 'content' && (
          <>
            <div className="header">
              <h1 className="header-title">📺 <span>Content</span></h1>
              <p className="header-sub">Watch Cam's latest wins and clips!</p>
            </div>
            
            <div className="card">
              <div className="card-header">
                <div className="card-icon">🎬</div>
                <div className="card-title">YouTube Shorts</div>
              </div>
              <div className="video-row">
                {youtubeShorts.length > 0 ? youtubeShorts.map((short) => (
                  <div className="video-card" key={short.id}>
                    <div className="video-embed youtube-short">
                      <iframe 
                        src={`https://www.youtube.com/embed/${short.id}`}
                        title={short.title}
                        allowFullScreen 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      />
                    </div>
                    <div className="video-info">
                      <span className="video-platform youtube">YouTube</span>
                      <div className="video-title">{short.title}</div>
                      <div className="video-stats">
                        {parseInt(short.viewCount).toLocaleString()} views • {parseInt(short.commentCount).toLocaleString()} comments
                      </div>
                    </div>
                    {user?.isAdmin && (
                      <>
                        <button 
                          className={`pick-winner-btn ${winnerPicking === short.id ? 'picking' : ''}`}
                          onClick={() => pickWinner(short.id, 'youtube')}
                          disabled={winnerPicking !== null}
                        >
                          {winnerPicking === short.id ? '🎲 Picking...' : '🎁 Pick a Winner'}
                        </button>
                        {winner && winnerVideoId === short.id && winnerPlatform === 'youtube' && (
                          <div className="winner-result">
                            <div className="winner-header">🎉 Winner!</div>
                            <a 
                              href={getYouTubeCommentLink(short.id, winner.winner.id)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="winner-info"
                            >
                              <img 
                                src={winner.winner.authorProfileImage || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} 
                                alt="" 
                                className="winner-avatar"
                                style={{ background: '#374151' }}
                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'; }}
                              />
                              <div className="winner-details">
                                <div className="winner-name">{winner.winner.author}</div>
                                <div className="winner-comment">{winner.winner.text}</div>
                              </div>
                              <div className="winner-link-icon">🔗</div>
                            </a>
                            <div className="winner-stats">
                              {winner.uniqueParticipants} unique participants • {winner.totalComments} total comments
                            </div>
                            <button 
                              className="reroll-btn"
                              onClick={() => pickWinner(short.id, 'youtube')}
                              disabled={winnerPicking !== null}
                            >
                              🎲 Reroll Winner
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )) : (
                  <>
                    <div className="video-card">
                      <div className="video-embed youtube-short">
                        <iframe 
                          src="https://www.youtube.com/embed/sX_UGwg3W5g" 
                          title="HUGE Blackjack Climb - YouTube"
                          allowFullScreen 
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        />
                      </div>
                      <div className="video-info">
                        <span className="video-platform youtube">YouTube</span>
                        <div className="video-title">HUGE Blackjack Climb!</div>
                      </div>
                    </div>
                    <div className="video-card">
                      <div className="video-embed youtube-short">
                        <iframe 
                          src="https://www.youtube.com/embed/srtutjGiPI4" 
                          title="Fat lesson interrupted $1 into $800 - YouTube"
                          allowFullScreen 
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        />
                      </div>
                      <div className="video-info">
                        <span className="video-platform youtube">YouTube</span>
                        <div className="video-title">Fat lesson interrupted! $1 into $800</div>
                      </div>
                    </div>
                    <div className="video-card">
                      <div className="video-embed youtube-short">
                        <iframe 
                          src="https://www.youtube.com/embed/dQw4w9WgXcQ" 
                          title="YouTube Short"
                          allowFullScreen 
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        />
                      </div>
                      <div className="video-info">
                        <span className="video-platform youtube">YouTube</span>
                        <div className="video-title">Latest Short</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Instagram Reels - Hidden until Apify scraping is fixed
            <div className="card">
              <div className="card-header">
                <div className="card-icon">📸</div>
                <div className="card-title">Instagram Reels</div>
              </div>
              <div className="video-row">
                {instagramReels.length > 0 ? instagramReels.map((reel) => (
                  <div className="video-card" key={reel.id}>
                    <div className="video-embed">
                      <iframe 
                        src={`https://www.instagram.com/reel/${reel.shortCode}/embed`}
                        title={reel.caption?.substring(0, 50) || 'Instagram Reel'}
                        allowFullScreen 
                        scrolling="no"
                      />
                    </div>
                    <div className="video-info">
                      <span className="video-platform instagram">Instagram</span>
                      <div className="video-title">{reel.caption?.substring(0, 50) || 'Instagram Reel'}...</div>
                      <div className="video-stats">
                        {reel.likesCount?.toLocaleString()} likes • {reel.commentsCount?.toLocaleString()} comments
                      </div>
                    </div>
                    {user?.isAdmin && (
                      <>
                        <button 
                          className={`pick-winner-btn instagram ${winnerPicking === reel.shortCode ? 'picking' : ''}`}
                          onClick={() => pickWinner(reel.shortCode, 'instagram')}
                          disabled={winnerPicking !== null}
                        >
                          {winnerPicking === reel.shortCode ? '🎲 Picking...' : '🎁 Pick a Winner'}
                        </button>
                        {winner && winnerVideoId === reel.shortCode && winnerPlatform === 'instagram' && (
                          <div className="winner-result">
                            <div className="winner-header">🎉 Winner!</div>
                            <a 
                              href={getInstagramLink(reel.shortCode)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="winner-info"
                            >
                              <img 
                                src={winner.winner.authorProfileImage || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} 
                                alt="" 
                                className="winner-avatar"
                                style={{ background: '#374151' }}
                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'; }}
                              />
                              <div className="winner-details">
                                <div className="winner-name">@{winner.winner.author}</div>
                                <div className="winner-comment">{winner.winner.text}</div>
                              </div>
                              <div className="winner-link-icon">🔗</div>
                            </a>
                            <div className="winner-stats">
                              {winner.uniqueParticipants} unique participants • {winner.totalComments} total comments
                            </div>
                            <button 
                              className="reroll-btn"
                              onClick={() => pickWinner(reel.shortCode, 'instagram')}
                              disabled={winnerPicking !== null}
                            >
                              🎲 Reroll Winner
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )) : (
                  <div className="video-card" style={{gridColumn: '1 / -1', textAlign: 'center', padding: '2rem'}}>
                    <p style={{color: 'var(--text-muted)'}}>Loading Instagram reels...</p>
                  </div>
                )}
              </div>
            </div>
            */}
            
            <div className="card">
              <div className="card-header">
                <div className="card-icon">📱</div>
                <div className="card-title">TikTok Clips</div>
              </div>
              <div className="video-row">
                {tiktokVideos.length > 0 ? tiktokVideos.map((video) => (
                  <div className="video-card" key={video.id}>
                    <div className="video-embed">
                      <iframe 
                        src={`https://www.tiktok.com/embed/v2/${video.id}`}
                        title={video.description?.substring(0, 50) || 'TikTok'}
                        allowFullScreen 
                        scrolling="no" 
                        allow="encrypted-media"
                      />
                    </div>
                    <div className="video-info">
                      <span className="video-platform tiktok">TikTok</span>
                      <div className="video-title">{video.description?.substring(0, 50) || 'TikTok Video'}...</div>
                      <div className="video-stats">
                        {video.playCount?.toLocaleString()} plays • {video.commentCount?.toLocaleString()} comments
                      </div>
                    </div>
                    {user?.isAdmin && (
                      <>
                        <button 
                          className={`pick-winner-btn tiktok ${winnerPicking === video.id ? 'picking' : ''}`}
                          onClick={() => pickWinner(video.id, 'tiktok')}
                          disabled={winnerPicking !== null}
                        >
                          {winnerPicking === video.id ? '🎲 Picking...' : '🎁 Pick a Winner'}
                        </button>
                        {winner && winnerVideoId === video.id && winnerPlatform === 'tiktok' && (
                          <div className="winner-result">
                            <div className="winner-header">🎉 Winner!</div>
                            <a 
                              href={getTikTokLink(video.id)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="winner-info"
                            >
                              <img 
                                src={winner.winner.authorProfileImage || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} 
                                alt="" 
                                className="winner-avatar"
                                style={{ background: '#374151' }}
                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'; }}
                              />
                              <div className="winner-details">
                                {winner.winner.authorNickname && (
                                  <div className="winner-name">{winner.winner.authorNickname}</div>
                                )}
                                <a 
                                  href={`https://www.tiktok.com/@${winner.winner.author}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ color: winner.winner.authorNickname ? 'var(--text-dim)' : 'var(--gold)', fontSize: winner.winner.authorNickname ? '0.85rem' : '1rem', textDecoration: 'none' }}
                                >
                                  @{winner.winner.author} ↗
                                </a>
                                <div className="winner-comment">{winner.winner.text}</div>
                              </div>
                              <div className="winner-link-icon">🔗</div>
                            </a>
                            <div className="winner-stats">
                              {winner.uniqueParticipants} unique participants • {winner.totalComments} total comments
                            </div>
                            <button 
                              className="reroll-btn"
                              onClick={() => pickWinner(video.id, 'tiktok')}
                              disabled={winnerPicking !== null}
                            >
                              🎲 Reroll Winner
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )) : (
                  <>
                    <div className="video-card">
                      <div className="video-embed">
                        <iframe 
                          src="https://www.tiktok.com/embed/v2/7587385729408437518" 
                          title="TikTok"
                          allowFullScreen 
                          scrolling="no" 
                          allow="encrypted-media"
                        />
                      </div>
                      <div className="video-info">
                        <span className="video-platform tiktok">TikTok</span>
                        <div className="video-title">Latest TikTok</div>
                      </div>
                    </div>
                    <div className="video-card">
                      <div className="video-embed">
                        <iframe 
                          src="https://www.tiktok.com/embed/v2/7587030520765222157" 
                          title="TikTok"
                          allowFullScreen 
                          scrolling="no" 
                          allow="encrypted-media"
                        />
                      </div>
                      <div className="video-info">
                        <span className="video-platform tiktok">TikTok</span>
                        <div className="video-title">Recent TikTok</div>
                      </div>
                    </div>
                    <div className="video-card">
                      <div className="video-embed">
                        <iframe 
                          src="https://www.tiktok.com/embed/v2/7586667102942137614" 
                          title="TikTok"
                          allowFullScreen 
                          scrolling="no" 
                          allow="encrypted-media"
                        />
                      </div>
                      <div className="video-info">
                        <span className="video-platform tiktok">TikTok</span>
                        <div className="video-title">Recent TikTok</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <div className="card" style={{ textAlign: 'center' }}>
              <h3 style={{ marginBottom: '1rem' }}>🔴 Watch Live on Twitch</h3>
              <p style={{ color: 'var(--text-dim)', marginBottom: '1rem' }}>Catch Cam live for more giveaways!</p>
              <a href="https://www.twitch.tv/camneverwins" target="_blank" rel="noopener noreferrer" className="cta-btn">📺 Watch Live Stream →</a>
            </div>
          </>
        )}

        {/* COMMUNITY HUNTS PAGE */}
        {activePage === 'communityhunts' && (
          <>
            <div className="header">
              <h1 className="header-title">👥 <span>Community Hunts</span></h1>
              <p className="header-sub">Enter the raffle for a chance to join community bonus hunts!</p>
            </div>

            {activeCommunityHunt ? (
              <>
                {/* Draft Notice for non-admins */}
                {activeCommunityHunt.status === 'draft' && !user?.isAdmin && (
                  <div className="card" style={{ textAlign: 'center', padding: '3rem', marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔜</div>
                    <h3 style={{ marginBottom: '0.5rem' }}>Community Hunt Coming Soon!</h3>
                    <p style={{ color: 'var(--text-dim)', marginBottom: '1rem' }}>A new community hunt is being prepared. Check back soon!</p>
                    <a href="https://discord.gg/camneverwins" target="_blank" rel="noopener noreferrer" className="cta-btn" style={{ display: 'inline-block' }}>
                      💬 Join Discord for Announcements
                    </a>
                  </div>
                )}

                {/* Hunt Info Card - Show for open/live/completed OR draft for admins */}
                {(activeCommunityHunt.status !== 'draft' || user?.isAdmin) && (
                <div className="card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(139, 92, 246, 0.05))', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                        <h2 style={{ fontSize: '1.5rem', fontWeight: 700 }}>{activeCommunityHunt.name}</h2>
                        {/* Hunt Type Badge */}
                        <span style={{
                          padding: '0.2rem 0.6rem',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          background: activeCommunityHunt.hunt_type === 'vip' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(139, 92, 246, 0.3)',
                          color: activeCommunityHunt.hunt_type === 'vip' ? '#000' : '#a78bfa',
                          textTransform: 'uppercase'
                        }}>
                          {activeCommunityHunt.hunt_type === 'vip' ? '⭐ VIP' : '👥 Affiliate'}
                        </span>
                        {/* Status Badge */}
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          background: activeCommunityHunt.status === 'open' ? 'rgba(34, 197, 94, 0.2)' : activeCommunityHunt.status === 'live' ? 'rgba(239, 68, 68, 0.2)' : activeCommunityHunt.status === 'draft' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(100, 100, 100, 0.2)',
                          color: activeCommunityHunt.status === 'open' ? '#22c55e' : activeCommunityHunt.status === 'live' ? '#ef4444' : activeCommunityHunt.status === 'draft' ? '#f59e0b' : '#888',
                          border: `1px solid ${activeCommunityHunt.status === 'open' ? 'rgba(34, 197, 94, 0.4)' : activeCommunityHunt.status === 'live' ? 'rgba(239, 68, 68, 0.4)' : activeCommunityHunt.status === 'draft' ? 'rgba(245, 158, 11, 0.4)' : 'rgba(100, 100, 100, 0.4)'}`
                        }}>
                          {activeCommunityHunt.status === 'open' ? '🎟️ ENTRIES OPEN' : activeCommunityHunt.status === 'live' ? '🔴 LIVE' : activeCommunityHunt.status === 'draft' ? '📝 DRAFT' : '✅ COMPLETED'}
                        </span>
                      </div>
                      {activeCommunityHunt.description && (
                        <p style={{ color: 'var(--text-dim)', marginBottom: '0.75rem' }}>{activeCommunityHunt.description}</p>
                      )}
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.9rem', color: 'var(--text-dim)' }}>
                        {activeCommunityHunt.status !== 'draft' && <span>👥 {activeCommunityHunt.entryCount || 0} entries</span>}
                        {communityHuntEntries.length > 0 && <span>🎯 {communityHuntEntries.length} selected</span>}
                        {activeCommunityHunt.start_balance && (
                          <span>💰 Start: ${parseFloat(activeCommunityHunt.start_balance).toLocaleString()}</span>
                        )}
                        {activeCommunityHunt.end_balance && (
                          <span>🏁 End: ${parseFloat(activeCommunityHunt.end_balance).toLocaleString()}</span>
                        )}
                      </div>
                      {/* Eligibility Info */}
                      {activeCommunityHunt.status === 'open' && user && (
                        <div style={{ marginTop: '0.75rem', fontSize: '0.8rem' }}>
                          {activeCommunityHunt.hunt_type === 'vip' ? (
                            <span style={{ color: myEligibility.vip ? '#22c55e' : '#ef4444' }}>
                              {myEligibility.vip 
                                ? (myEligibility.whitelisted ? '✅ You qualify (Whitelisted)' : '✅ You qualify (VIP: $50k+ wagered in past 4 months)') 
                                : `❌ VIP requires $50k+ wagered in past 4 months (You: $${(myEligibility.wagered || 0).toLocaleString()})`}
                            </span>
                          ) : (
                            <span style={{ color: myEligibility.affiliate ? '#22c55e' : '#ef4444' }}>
                              {myEligibility.affiliate 
                                ? (myEligibility.whitelisted ? '✅ You qualify (Whitelisted)' : '✅ You qualify (Affiliate)') 
                                : '❌ Affiliate requires any wager on Rainbet in past 4 months'}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Entry Button */}
                    {activeCommunityHunt.status === 'open' && (
                      <div style={{ textAlign: 'right' }}>
                        {!user ? (
                          <button onClick={handleLogin} className="cta-btn" style={{ padding: '0.75rem 1.5rem' }}>
                            🔐 Login to Enter
                          </button>
                        ) : myEntryStatus === 'entered' ? (
                          <div style={{ padding: '0.75rem 1.5rem', background: 'rgba(34, 197, 94, 0.2)', border: '1px solid rgba(34, 197, 94, 0.4)', borderRadius: '10px', color: '#22c55e', fontWeight: 600 }}>
                            ✅ You're Entered!
                          </div>
                        ) : myEntryStatus === 'selected' ? (
                          <div style={{ padding: '0.75rem 1.5rem', background: 'rgba(229, 231, 235, 0.2)', border: '1px solid rgba(229, 231, 235, 0.4)', borderRadius: '10px', color: 'var(--gold)', fontWeight: 600 }}>
                            🎉 You're Selected!
                          </div>
                        ) : !hasRainbetLink(user) ? (
                          <div>
                            <button onClick={() => navigateTo('profile')} className="cta-btn" style={{ padding: '0.75rem 1.5rem', background: 'rgba(139, 92, 246, 0.3)' }}>
                              🔗 Link Rainbet Username First
                            </button>
                            <p style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '0.5rem' }}>Required to enter</p>
                          </div>
                        ) : (activeCommunityHunt.hunt_type === 'vip' && !myEligibility.vip) || (activeCommunityHunt.hunt_type === 'affiliate' && !myEligibility.affiliate) ? (
                          <div style={{ padding: '0.75rem 1.5rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '10px', color: '#ef4444', fontWeight: 500, fontSize: '0.9rem' }}>
                            ❌ Not Eligible
                          </div>
                        ) : (
                          <button 
                            onClick={enterCommunityHunt} 
                            disabled={communityHuntLoading}
                            className="cta-btn" 
                            style={{ padding: '0.75rem 1.5rem', opacity: communityHuntLoading ? 0.7 : 1 }}
                          >
                            {communityHuntLoading ? '⏳ Entering...' : '🎟️ Enter Raffle'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* Live Hunt Widget - Myrtle Overlay + Hunt Info */}
                {activeCommunityHunt.status === 'live' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: '1rem', marginBottom: '1.5rem', alignItems: 'start' }}>
                    {/* Left: Myrtle Widget */}
                    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                      <div style={{ padding: '0.6rem 1rem', background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>📊</span>
                        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Current Hunt</span>
                      </div>
                      <div style={{ background: '#0d0d0d' }}>
                        {MYRTLE_GUILD_ID ? (
                          <iframe 
                            src={`https://myrtlebot.com/widgets/myrtleBonusHunt.html?guildId=${MYRTLE_GUILD_ID}`}
                            title="Live Bonus Hunt"
                            style={{ width: '100%', height: '600px', border: 'none', display: 'block' }}
                          />
                        ) : (
                          <div style={{ padding: '3rem 1.5rem', textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.9rem', minHeight: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.5rem' }}>
                            <div style={{ fontSize: '2rem', opacity: 0.5 }}>📊</div>
                            <div style={{ fontWeight: 600, color: 'var(--text)' }}>MyrtleBot widget not configured</div>
                            <div>Set <code style={{ background: 'rgba(255,255,255,0.05)', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>MYRTLE_GUILD_ID</code> in App.jsx to enable.</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: Hunt Info & Participants Preview */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {/* Hunt Type Badge */}
                      <div className="card" style={{ padding: '1.25rem', background: activeCommunityHunt.hunt_type === 'vip' ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(245, 158, 11, 0.05))' : 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(139, 92, 246, 0.05))', border: activeCommunityHunt.hunt_type === 'vip' ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(139, 92, 246, 0.3)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ fontSize: '1.5rem' }}>{activeCommunityHunt.hunt_type === 'vip' ? '⭐' : '👥'}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{activeCommunityHunt.hunt_type === 'vip' ? 'VIP Hunt' : 'Affiliate Hunt'}</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>{activeCommunityHunt.name}</div>
                          </div>
                          <span style={{ padding: '0.3rem 0.75rem', background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <span style={{ width: 8, height: 8, background: '#ef4444', borderRadius: '50%', animation: 'pulse 1.5s infinite' }}></span>
                            LIVE
                          </span>
                        </div>
                      </div>

                      {/* Participants Preview */}
                      <div className="card" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '420px' }}>
                        <div style={{ padding: '0.75rem 1rem', background: 'rgba(0,0,0,0.2)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>🎮 Participants</span>
                          <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>{communityHuntEntries.length} selected</span>
                        </div>
                        <div style={{ padding: '0.75rem', flex: 1, overflowY: 'auto', minHeight: 0 }}>
                          {communityHuntEntries.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                              {communityHuntEntries.map((entry, idx) => (
                                <div key={entry.id} style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '0.6rem', 
                                  padding: '0.5rem 0.6rem',
                                  background: entry.is_host ? 'rgba(139, 92, 246, 0.15)' : user && entry.user_id === user.id ? 'rgba(229, 231, 235, 0.1)' : 'rgba(0,0,0,0.2)',
                                  borderRadius: '8px',
                                  border: entry.is_host ? '1px solid rgba(139, 92, 246, 0.3)' : user && entry.user_id === user.id ? '1px solid rgba(229, 231, 235, 0.3)' : '1px solid transparent'
                                }}>
                                  <img 
                                    src={entry.profile_image_url || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} 
                                    alt="" 
                                    style={{ width: 32, height: 32, borderRadius: '50%' }}
                                  />
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {entry.display_name || entry.username}
                                      {entry.is_host && <span style={{ marginLeft: '0.3rem', fontSize: '0.6rem', color: '#a78bfa' }}>👑</span>}
                                    </div>
                                    <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>🎰 {entry.rainbet_username}</div>
                                  </div>
                                  <span style={{ padding: '0.2rem 0.5rem', background: entry.is_host ? 'rgba(139, 92, 246, 0.2)' : 'rgba(239, 68, 68, 0.15)', color: entry.is_host ? '#a78bfa' : '#ef4444', borderRadius: '8px', fontSize: '0.65rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{entry.is_host ? 'HOST' : 'IN HUNT'}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-dim)', fontSize: '0.85rem' }}>
                              No participants yet
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Rewards Info */}
                      <div className="card" style={{ padding: '1rem', background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(34, 197, 94, 0.02))', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
                        <div style={{ fontWeight: 600, marginBottom: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                          <span>💰</span> Reward Distribution
                        </div>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)', margin: 0, lineHeight: 1.4 }}>
                          Rewards distributed equally based on final hunt results!
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Responsive: Stack on mobile */}
                <style>{`
                  @media (max-width: 900px) {
                    .community-hunt-live-grid {
                      grid-template-columns: 1fr !important;
                    }
                  }
                `}</style>

                {/* Selected Participants Cards - Only show when NOT live (live shows in sidebar) */}
                {communityHuntEntries.length > 0 && activeCommunityHunt.status !== 'live' && (
                  <div className="card" style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      🏆 Selected Participants ({communityHuntEntries.length})
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                      {communityHuntEntries.map((entry, idx) => (
                        <div key={entry.id} style={{
                          padding: '1rem',
                          background: user && entry.user_id === user.id ? 'linear-gradient(135deg, rgba(229, 231, 235, 0.15), rgba(229, 231, 235, 0.05))' : 'rgba(0,0,0,0.2)',
                          borderRadius: '12px',
                          border: user && entry.user_id === user.id ? '1px solid rgba(229, 231, 235, 0.3)' : '1px solid var(--border)',
                          textAlign: 'center'
                        }}>
                          <img 
                            src={entry.profile_image_url || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} 
                            alt="" 
                            style={{ width: 56, height: 56, borderRadius: '50%', marginBottom: '0.5rem', border: '2px solid var(--border)' }}
                          />
                          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{entry.display_name || entry.username}</div>
                          {entry.rainbet_username && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.5rem' }}>🎰 {entry.rainbet_username}</div>
                          )}
                          {/* Show payout badge for completed hunts */}
                          {entry.payout !== null && entry.payout !== undefined && (
                            <div style={{ 
                              padding: '0.4rem 0.75rem',
                              background: parseFloat(entry.payout) > 0 ? 'rgba(34, 197, 94, 0.2)' : 'rgba(100,100,100,0.2)',
                              borderRadius: '20px',
                              fontWeight: 700, 
                              color: parseFloat(entry.payout) > 0 ? '#22c55e' : 'var(--text-dim)',
                              fontFamily: "'Space Mono', monospace",
                              fontSize: '0.9rem',
                              display: 'inline-block'
                            }}>
                              {parseFloat(entry.payout) > 0 ? `+$${parseFloat(entry.payout).toLocaleString()}` : '$0'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Total Payouts Summary */}
                    {communityHuntEntries.some(e => e.payout !== null) && (
                      <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: 'var(--text-dim)' }}>Total Payouts:</span>
                        <span style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--gold)', fontFamily: "'Space Mono', monospace" }}>
                          ${communityHuntEntries.reduce((sum, e) => sum + (parseFloat(e.payout) || 0), 0).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Waiting states */}
                {/* Entries Display when Open */}
                {activeCommunityHunt.status === 'open' && (
                  <div className="card" style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      🎟️ Raffle Entries ({activeCommunityHunt.entryCount || 0})
                    </h3>
                    {communityHuntAllEntries.length > 0 ? (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.75rem' }}>
                          {communityHuntAllEntries.slice(0, 30).map((entry, idx) => (
                            <div key={entry.id} style={{
                              padding: '0.75rem',
                              background: user && entry.user_id === user.id ? 'linear-gradient(135deg, rgba(229, 231, 235, 0.15), rgba(229, 231, 235, 0.05))' : 'rgba(0,0,0,0.2)',
                              borderRadius: '10px',
                              border: user && entry.user_id === user.id ? '1px solid rgba(229, 231, 235, 0.3)' : '1px solid var(--border)',
                              textAlign: 'center'
                            }}>
                              <img 
                                src={entry.profile_image_url || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} 
                                alt="" 
                                style={{ width: 44, height: 44, borderRadius: '50%', marginBottom: '0.4rem', border: '2px solid var(--border)' }}
                              />
                              <div style={{ fontSize: '0.8rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {entry.display_name || entry.username}
                              </div>
                              <div style={{ fontSize: '0.65rem', color: 'var(--text-dim)', marginTop: '0.2rem' }}>🎰 {entry.rainbet_username}</div>
                            </div>
                          ))}
                        </div>
                        {communityHuntAllEntries.length > 30 && (
                          <div style={{ textAlign: 'center', marginTop: '1rem', padding: '0.75rem', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '8px', color: '#a78bfa', fontWeight: 600 }}>
                            + {communityHuntAllEntries.length - 30} more entries
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-dim)' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎟️</div>
                        <p>No entries yet. Be the first to enter!</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="card" style={{ textAlign: 'center', padding: '3rem', marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
                <h3 style={{ marginBottom: '0.5rem' }}>No Active Community Hunt</h3>
                <p style={{ color: 'var(--text-dim)', marginBottom: '1rem' }}>Check back soon for the next community hunt raffle!</p>
                <a href="https://discord.gg/camneverwins" target="_blank" rel="noopener noreferrer" className="cta-btn" style={{ display: 'inline-block' }}>
                  💬 Join Discord for Announcements
                </a>
              </div>
            )}

            {/* Previous Community Hunts */}
            {previousCommunityHunts.length > 0 && (
              <div style={{ marginTop: '2rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem' }}>
                  📜 Previous Community Hunts 
                  {huntHistoryPagination.total > 0 && <span style={{ fontSize: '0.9rem', color: 'var(--text-dim)', fontWeight: 400 }}> ({huntHistoryPagination.total} total)</span>}
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {previousCommunityHunts.map(hunt => (
                    <div key={hunt.id} className="card" style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.3), rgba(0,0,0,0.1))' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <h3 style={{ fontWeight: 600 }}>{hunt.name}</h3>
                          <span style={{
                            padding: '0.2rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            background: hunt.hunt_type === 'vip' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(139, 92, 246, 0.3)',
                            color: hunt.hunt_type === 'vip' ? '#000' : '#a78bfa',
                            textTransform: 'uppercase'
                          }}>
                            {hunt.hunt_type === 'vip' ? '⭐ VIP' : '👥 Affiliate'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', color: 'var(--text-dim)', alignItems: 'center' }}>
                          {hunt.start_balance && <span>Start: ${parseFloat(hunt.start_balance).toLocaleString()}</span>}
                          {hunt.end_balance && <span>End: ${parseFloat(hunt.end_balance).toLocaleString()}</span>}
                          <span style={{ color: '#22c55e', fontWeight: 600 }}>Total: ${parseFloat(hunt.total_payout || 0).toLocaleString()}</span>
                          {/* Admin Edit Payouts Button */}
                          {user?.isAdmin && (
                            <button
                              onClick={() => setEditingHuntPayouts(editingHuntPayouts === hunt.id ? null : hunt.id)}
                              style={{
                                padding: '0.3rem 0.6rem',
                                background: editingHuntPayouts === hunt.id ? 'rgba(239, 68, 68, 0.2)' : 'rgba(139, 92, 246, 0.2)',
                                border: `1px solid ${editingHuntPayouts === hunt.id ? 'rgba(239, 68, 68, 0.4)' : 'rgba(139, 92, 246, 0.4)'}`,
                                borderRadius: '4px',
                                color: editingHuntPayouts === hunt.id ? '#ef4444' : '#a78bfa',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                            >
                              {editingHuntPayouts === hunt.id ? '✕ Close' : '✏️ Edit Payouts'}
                            </button>
                          )}
                        </div>
                      </div>
                      {/* Participant Cards */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.75rem' }}>
                        {hunt.participants?.map((p, idx) => (
                          <div key={idx} style={{
                            padding: '0.75rem',
                            background: p.is_host ? 'rgba(139, 92, 246, 0.15)' : 'rgba(0,0,0,0.2)',
                            borderRadius: '10px',
                            textAlign: 'center',
                            border: p.is_host ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid transparent'
                          }}>
                            <img 
                              src={p.profile_image_url || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} 
                              alt="" 
                              style={{ width: 40, height: 40, borderRadius: '50%', marginBottom: '0.4rem' }}
                            />
                            <div style={{ fontSize: '0.8rem', fontWeight: 500, marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {p.display_name || p.username}
                              {p.is_host && <span style={{ marginLeft: '0.2rem' }}>👑</span>}
                            </div>
                            {/* Editable payout or display */}
                            {editingHuntPayouts === hunt.id ? (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                                <span style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>$</span>
                                <input
                                  type="number"
                                  defaultValue={p.payout || 0}
                                  id={`edit-payout-${hunt.id}-${p.entry_id}`}
                                  style={{
                                    width: '60px',
                                    padding: '0.25rem',
                                    background: 'rgba(0,0,0,0.4)',
                                    border: '1px solid var(--border)',
                                    borderRadius: '4px',
                                    color: '#22c55e',
                                    fontSize: '0.75rem',
                                    fontFamily: "'Space Mono', monospace",
                                    textAlign: 'center'
                                  }}
                                />
                                <button
                                  onClick={async () => {
                                    const input = document.getElementById(`edit-payout-${hunt.id}-${p.entry_id}`);
                                    const payout = input?.value;
                                    try {
                                      await fetchWithAuth(`${BACKEND_API_URL}/api/admin/community-hunts/${hunt.id}/entries/${p.entry_id}`, {
                                        method: 'PATCH',
                                        body: JSON.stringify({ payout: payout || null })
                                      });
                                      // Refresh history
                                      fetchCommunityHuntHistory(1, false);
                                    } catch (e) {
                                      alert('Error saving payout');
                                    }
                                  }}
                                  style={{
                                    padding: '0.2rem 0.4rem',
                                    background: '#22c55e',
                                    border: 'none',
                                    borderRadius: '4px',
                                    color: '#fff',
                                    fontSize: '0.65rem',
                                    cursor: 'pointer'
                                  }}
                                >
                                  ✓
                                </button>
                              </div>
                            ) : (
                              <div style={{ 
                                fontSize: '0.8rem',
                                fontWeight: 700, 
                                color: parseFloat(p.payout) > 0 ? '#22c55e' : 'var(--text-dim)',
                                fontFamily: "'Space Mono', monospace"
                              }}>
                                {parseFloat(p.payout) > 0 ? `+$${parseFloat(p.payout).toLocaleString()}` : '$0'}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {/* Bulk edit for admin */}
                      {editingHuntPayouts === hunt.id && (
                        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)', display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Bulk set all:</span>
                          <span style={{ color: 'var(--text-dim)' }}>$</span>
                          <input
                            type="number"
                            placeholder="0"
                            id={`bulk-edit-${hunt.id}`}
                            style={{
                              width: '70px',
                              padding: '0.35rem',
                              background: 'rgba(0,0,0,0.4)',
                              border: '1px solid var(--border)',
                              borderRadius: '4px',
                              color: 'var(--text)',
                              fontSize: '0.8rem'
                            }}
                          />
                          <button
                            onClick={async () => {
                              const input = document.getElementById(`bulk-edit-${hunt.id}`);
                              const payout = input?.value;
                              if (!payout) return alert('Enter an amount');
                              if (!window.confirm(`Set $${payout} for ALL ${hunt.participants?.length || 0} participants?`)) return;
                              try {
                                await fetchWithAuth(`${BACKEND_API_URL}/api/admin/community-hunts/${hunt.id}/bulk-payout`, {
                                  method: 'POST',
                                  body: JSON.stringify({ payout: parseFloat(payout) })
                                });
                                fetchCommunityHuntHistory(1, false);
                                setEditingHuntPayouts(null);
                              } catch (e) {
                                alert('Error applying bulk payout');
                              }
                            }}
                            style={{
                              padding: '0.35rem 0.75rem',
                              background: '#8b5cf6',
                              border: 'none',
                              borderRadius: '4px',
                              color: '#fff',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            Apply All
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Load More Button */}
                {huntHistoryPagination.hasMore && (
                  <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                    <button
                      onClick={() => fetchCommunityHuntHistory(huntHistoryPagination.page + 1, true)}
                      disabled={huntHistoryLoading}
                      style={{
                        padding: '0.75rem 2rem',
                        background: huntHistoryLoading ? 'rgba(100,100,100,0.3)' : 'rgba(139, 92, 246, 0.2)',
                        border: '1px solid rgba(139, 92, 246, 0.4)',
                        borderRadius: '8px',
                        color: '#a78bfa',
                        fontWeight: 600,
                        cursor: huntHistoryLoading ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      {huntHistoryLoading ? 'Loading...' : `Load More (${huntHistoryPagination.total - previousCommunityHunts.length} remaining)`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {activePage === 'bonushunts' && (
          <>
            <div className="header">
              <h1 className="header-title">🎯 <span>Bonus Hunts</span></h1>
              <p className="header-sub">Track Cam's slot bonus hunt sessions! ({bonusHunts.total} total hunts)</p>
            </div>

            {/* Highest Multi Banner */}
            {(bonusHunts.highestMulti || bonusHunts.hotSlot || bonusHunts.coldSlot) && (
              <div className="slot-stats-panel">
                {bonusHunts.highestMulti && (
                  <div className="slot-stat-card highest-multi">
                    <div className="slot-stat-icon">💎</div>
                    <div className="slot-stat-content">
                      <div className="slot-stat-label">Highest Multi</div>
                      <div className="slot-stat-name">{bonusHunts.highestMulti.slotName}</div>
                      <div className="slot-stat-details">
                        ${bonusHunts.highestMulti.betSize?.toFixed(2)} → ${bonusHunts.highestMulti.payout?.toLocaleString()}
                      </div>
                      <div className="slot-stat-value highest">{bonusHunts.highestMulti.multi?.toFixed(0)}x</div>
                    </div>
                  </div>
                )}
                {bonusHunts.hotSlot && (
                  <div className="slot-stat-card hot-slot">
                    <div className="slot-stat-icon">🔥</div>
                    <div className="slot-stat-content">
                      <div className="slot-stat-label">Hot Slot</div>
                      <div className="slot-stat-name">{bonusHunts.hotSlot.name}</div>
                      <div className="slot-stat-details">
                        {bonusHunts.hotSlot.appearances} appearances
                      </div>
                      <div className="slot-stat-value hot">{bonusHunts.hotSlot.avgMulti?.toFixed(0)}x avg</div>
                    </div>
                  </div>
                )}
                {bonusHunts.coldSlot && (
                  <div className="slot-stat-card cold-slot">
                    <div className="slot-stat-icon">🥶</div>
                    <div className="slot-stat-content">
                      <div className="slot-stat-label">Cold Slot</div>
                      <div className="slot-stat-name">{bonusHunts.coldSlot.name}</div>
                      <div className="slot-stat-details">
                        {bonusHunts.coldSlot.appearances} appearances
                      </div>
                      <div className="slot-stat-value cold">{bonusHunts.coldSlot.avgMulti?.toFixed(0)}x avg</div>
                    </div>
                  </div>
                )}
                <div className="slot-stats-note">Based on last 50 bonus hunts</div>
              </div>
            )}

            {/* Predictions CTA - Show when hunt is active */}
            {activeHuntData?.activeHunt && (
              <div className="card" style={{ 
                background: activeHuntData.predictionsOpen ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(34, 197, 94, 0.05))' : 'rgba(0,0,0,0.2)',
                border: activeHuntData.predictionsOpen ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid var(--border)',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>🔮</span>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        {activeHuntData.predictionsOpen ? 'Predictions are OPEN!' : 'Prediction Game'}
                      </h3>
                      {activeHuntData.predictionsOpen && (
                        <span style={{ padding: '0.25rem 0.5rem', background: '#22c55e', color: '#000', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 700 }}>LIVE</span>
                      )}
                    </div>
                    <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                      {activeHuntData.predictionsOpen 
                        ? `Guess the final balance and win prizes! ${activeHuntData.predictionCount || 0} predictions so far.`
                        : 'Predictions are currently closed for this hunt.'
                      }
                    </p>
                  </div>
                  <button 
                    onClick={() => navigateTo('predictions')}
                    className="cta-btn"
                    style={{ 
                      background: activeHuntData.predictionsOpen ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'linear-gradient(135deg, var(--gold), var(--gold-dark))'
                    }}
                  >
                    {activeHuntData.predictionsOpen ? '🎯 Make Your Prediction' : '📊 View Predictions'}
                  </button>
                </div>
              </div>
            )}

            {liveStatus.isLive && liveStatus.streamer === 'Cam' && (
              <div className="live-hunt-container">
                <div className="card live-hunt-widget-card">
                  <div className="card-header">
                    <div className="card-icon">🔴</div>
                    <div className="card-title">Live Bonus Hunt</div>
                    <span className="live-badge">LIVE</span>
                  </div>
                  <div className="bonushunt-widget">
                    {MYRTLE_GUILD_ID ? (
                      <iframe 
                        src={`https://myrtlebot.com/widgets/myrtleBonusHunt.html?guildId=${MYRTLE_GUILD_ID}`}
                        title="Live Bonus Hunt"
                        className="bonushunt-iframe"
                      />
                    ) : (
                      <div style={{ padding: '3rem 1.5rem', textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                        <div style={{ fontSize: '2rem', opacity: 0.5, marginBottom: '0.5rem' }}>📊</div>
                        <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: '0.25rem' }}>MyrtleBot widget not configured</div>
                        <div>Set <code style={{ background: 'rgba(255,255,255,0.05)', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>MYRTLE_GUILD_ID</code> in App.jsx to enable.</div>
                      </div>
                    )}
                  </div>
                </div>
                {bonusHunts.activeHunt && (
                  <div className="card live-hunt-stats-card">
                    <div className="card-header">
                      <div className="card-icon">📊</div>
                      <div className="card-title">Hunt Stats</div>
                    </div>
                    <div className="live-stats-list">
                      <div className="live-stat-item">
                        <span className="live-stat-label">Starting Balance</span>
                        <span className="live-stat-value">${bonusHunts.activeHunt.infoStartCost?.toLocaleString()}</span>
                      </div>
                      <div className="live-stat-item">
                        <span className="live-stat-label">Total Bonuses</span>
                        <span className="live-stat-value">{bonusHunts.activeHunt.bonusesList?.length || bonusHunts.activeHunt.bonusCountTotal || 0}</span>
                      </div>
                      <div className="live-stat-item">
                        <span className="live-stat-label">Required Avg</span>
                        <span className="live-stat-value">{bonusHunts.activeHunt.infoRequiredAverage?.toFixed(1) || '0'}x</span>
                      </div>
                      <div className="live-stat-item">
                        <span className="live-stat-label">Avg Bet Size</span>
                        <span className="live-stat-value">{bonusHunts.activeHunt.calculatedAvgBetSize ? `$${bonusHunts.activeHunt.calculatedAvgBetSize.toFixed(2)}` : 'N/A'}</span>
                      </div>
                      {bonusHunts.activeHunt.highestPotentialSlot && (
                        <div className="live-stat-item highlight">
                          <span className="live-stat-label">🌟 Highest Potential</span>
                          <span className="live-stat-value-slot">
                            {bonusHunts.activeHunt.highestPotentialSlot.title?.replace('(SUPER)', '').trim()}
                            <span className="live-stat-betsize">${bonusHunts.activeHunt.highestPotentialSlot.betSize?.toFixed(2)}</span>
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="live-hunt-cta">
                      <a href="https://www.twitch.tv/camneverwins" target="_blank" rel="noopener noreferrer" className="cta-btn-small">
                        📺 Watch Live
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Previous Winners */}
            {lastCompletedHunt?.hunt && (
              <div className="card">
                <div className="card-header">
                  <div className="card-icon">🏆</div>
                  <div className="card-title">Last Hunt Winners - Hunt #{lastCompletedHunt.hunt.huntId}</div>
                </div>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', padding: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center', padding: '1rem', background: 'rgba(0,0,0,0.3)', borderRadius: '8px' }}>
                    <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>Final Balance</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>${lastCompletedHunt.hunt.endBalance?.toLocaleString()}</div>
                  </div>
                </div>
                {lastCompletedHunt.winners?.length > 0 && (
                  <div style={{ padding: '1rem' }}>
                    {lastCompletedHunt.winners.map((winner, idx) => (
                      <div key={idx} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '1rem', 
                        padding: '0.75rem',
                        background: idx === 0 ? 'rgba(229, 231, 235, 0.1)' : 'rgba(0,0,0,0.2)',
                        borderRadius: '8px',
                        marginBottom: '0.5rem'
                      }}>
                        <span style={{ fontSize: '1.5rem' }}>{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</span>
                        {winner.profile_image_url && (
                          <img src={winner.profile_image_url} alt="" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>{winner.display_name || winner.username}</div>
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                            Predicted: ${parseFloat(winner.predicted_balance).toLocaleString()} 
                            <span style={{ marginLeft: '0.5rem', color: 'var(--accent)' }}>
                              (${Number(winner.difference).toFixed(2)} off)
                            </span>
                          </div>
                        </div>
                        <div style={{ fontWeight: 600, color: '#f5a623' }}>{winner.reward}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Claim Modal */}
            {showClaimModal && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                <div style={{ background: 'var(--card-bg)', padding: '2rem', borderRadius: '12px', maxWidth: '400px', width: '90%' }}>
                  <h3 style={{ marginBottom: '1rem' }}>🏆 Claim Your Prize!</h3>
                  <p style={{ color: 'var(--text-dim)', marginBottom: '1rem' }}>
                    Hunt #{showClaimModal.hunt_id} - #{showClaimModal.rank} Place
                  </p>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.85rem' }}>Your Rainbet Username</label>
                    <input
                      value={claimUsername}
                      onChange={(e) => setClaimUsername(e.target.value)}
                      placeholder="Rainbet username (under code CAM)"
                      style={{ width: '100%', padding: '0.75rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button 
                      onClick={() => setShowClaimModal(null)} 
                      style={{ flex: 1, padding: '0.75rem', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px', color: 'var(--text)', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={() => claimWin(showClaimModal.id)}
                      disabled={claimSubmitting}
                      className="cta-btn"
                      style={{ flex: 1 }}
                    >
                      {claimSubmitting ? 'Claiming...' : 'Claim Prize'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {(!liveStatus.isLive || liveStatus.streamer !== 'Cam') && (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>😴</div>
                <h3 style={{ marginBottom: '0.5rem' }}>No Active Bonus Hunt</h3>
                <p style={{ color: 'var(--text-dim)', marginBottom: '1.5rem' }}>Cam is currently offline. Check back when he's live!</p>
                <a href="https://www.twitch.tv/camneverwins" target="_blank" rel="noopener noreferrer" className="cta-btn">
                  📺 Follow on Twitch
                </a>
              </div>
            )}

            <div className="card">
              <div className="card-header">
                <div className="card-icon">📊</div>
                <div className="card-title">Recent Bonus Hunts</div>
              </div>
              {bonusHunts.recentHunts.length > 0 ? (
                <div className="hunt-history-list">
                  {bonusHunts.recentHunts
                  .filter(hunt => {
                    // Exclude hunts with -100% loss (placeholder hunts)
                    const profit = hunt.infoAmountWon - hunt.infoStartCost;
                    const profitPercent = hunt.infoStartCost > 0 ? ((profit / hunt.infoStartCost) * 100) : 0;
                    return profitPercent > -100;
                  })
                  .slice(huntPage * 10, (huntPage + 1) * 10)
                  .map((hunt, idx) => {
                    const profit = hunt.infoAmountWon - hunt.infoStartCost;
                    const profitPercent = hunt.infoStartCost > 0 ? ((profit / hunt.infoStartCost) * 100).toFixed(1) : 0;
                    const isProfit = profit >= 0;
                    const huntDate = hunt.createdDate 
                      ? new Date(hunt.createdDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
                      : '';
                    const huntMulti = hunt.highest_payout_betsize > 0 
                      ? (hunt.highest_payout_value / hunt.highest_payout_betsize).toFixed(0) 
                      : null;
                    const huntNumber = bonusHunts.total - (huntPage * 10) - idx;
                    return (
                      <div key={hunt.bonusHuntId} className="hunt-history-item">
                        <div className="hunt-history-left">
                          <div className="hunt-history-name">Bonus Hunt #{huntNumber}</div>
                          {huntDate && <div className="hunt-history-date">{huntDate}</div>}
                        </div>
                        <div className="hunt-history-stats">
                          <div className="hunt-history-cost">
                            <span className="hunt-stat-mini-label">Start</span>
                            <span>${hunt.infoStartCost?.toLocaleString()}</span>
                          </div>
                          <div className="hunt-history-won">
                            <span className="hunt-stat-mini-label">End</span>
                            <span>${hunt.infoAmountWon?.toLocaleString()}</span>
                          </div>
                          <div className={`hunt-history-profit ${isProfit ? 'profit' : 'loss'}`}>
                            <span className="hunt-stat-mini-label">P/L</span>
                            <span>{isProfit ? '+' : ''}{profitPercent}%</span>
                          </div>
                        </div>
                        {hunt.highest_payout_name && (
                          <div className="hunt-history-best">
                            🏆 {hunt.highest_payout_name} 
                            <span className="hunt-best-details">
                              (${hunt.highest_payout_betsize?.toFixed(2)} → ${hunt.highest_payout_value?.toLocaleString()}{huntMulti && ` • ${huntMulti}x`})
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(() => {
                    const filteredHunts = bonusHunts.recentHunts.filter(hunt => {
                      const profit = hunt.infoAmountWon - hunt.infoStartCost;
                      const profitPercent = hunt.infoStartCost > 0 ? ((profit / hunt.infoStartCost) * 100) : 0;
                      return profitPercent > -100;
                    });
                    const totalPages = Math.ceil(filteredHunts.length / 10);
                    if (totalPages <= 1) return null;
                    return (
                      <div className="hunt-pagination">
                        <button 
                          className="hunt-page-btn" 
                          onClick={() => setHuntPage(p => Math.max(0, p - 1))}
                          disabled={huntPage === 0}
                        >
                          ← Newer
                        </button>
                        <span className="hunt-page-info">Page {huntPage + 1} of {totalPages}</span>
                        <button 
                          className="hunt-page-btn" 
                          onClick={() => setHuntPage(p => Math.min(totalPages - 1, p + 1))}
                          disabled={huntPage >= totalPages - 1}
                        >
                          Older →
                        </button>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="bonushunt-history-placeholder">
                  <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '2rem' }}>
                    ⏳ Loading bonus hunt history...<br/>
                    <span style={{ fontSize: '0.85rem' }}>Make sure the backend has MYRTLE_API_KEY configured.</span>
                  </p>
                </div>
              )}
            </div>

            <div className="card" style={{ textAlign: 'center' }}>
              <h3 style={{ marginBottom: '1rem' }}>🎰 What is a Bonus Hunt?</h3>
              <p style={{ color: 'var(--text-dim)', maxWidth: '600px', margin: '0 auto' }}>
                A bonus hunt is when Cam buys slot bonuses at various bet sizes, saves them up, 
                then opens them all in one exciting session! Track the total cost, individual bonus payouts, 
                and see if the hunt ends in profit or loss.
              </p>
            </div>
          </>
        )}

        {/* TOURNAMENTS PAGE */}
        {activePage === 'tournaments' && (
          <>
            <div className="header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h1 className="header-title">🏆 <span>Provider Tournaments</span></h1>
                <p className="header-sub">Providers battle head-to-head - highest profit wins!</p>
              </div>
              {user?.isAdmin && (
                <button
                  onClick={() => setShowTournamentSetup(!showTournamentSetup)}
                  style={{
                    padding: '0.6rem 1.2rem',
                    background: showTournamentSetup ? 'rgba(239, 68, 68, 0.2)' : 'var(--gold)',
                    color: showTournamentSetup ? '#ef4444' : '#000',
                    border: showTournamentSetup ? '1px solid rgba(239, 68, 68, 0.3)' : 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontFamily: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {showTournamentSetup ? '✕ Close Setup' : '⚙️ Tournament Setup'}
                </button>
              )}
            </div>

            {/* Admin Setup Panel - Collapsible */}
            {user?.isAdmin && showTournamentSetup && (
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                  <div className="card-icon">⚙️</div>
                  <div className="card-title">Tournament Setup</div>
                </div>
                <div style={{ padding: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    {/* Bracket Size */}
                    <div>
                      <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.5rem' }}>Bracket Size</label>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {[4, 8, 16].map(size => (
                          <button
                            key={size}
                            onClick={() => setTournamentSize(size)}
                            disabled={!!activeTournament}
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              background: tournamentSize === size ? 'var(--gold)' : 'rgba(0,0,0,0.3)',
                              color: tournamentSize === size ? '#000' : 'var(--text)',
                              border: '1px solid ' + (tournamentSize === size ? 'var(--gold)' : 'var(--border)'),
                              borderRadius: '6px',
                              cursor: activeTournament ? 'not-allowed' : 'pointer',
                              fontWeight: tournamentSize === size ? 700 : 500,
                              fontFamily: 'inherit',
                              fontSize: '0.85rem'
                            }}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Providers */}
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.5rem' }}>
                        Providers ({tournamentProviders.length}/{tournamentSize})
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <input
                          type="text"
                          placeholder="Add provider..."
                          value={tournamentProviderInput}
                          onChange={(e) => setTournamentProviderInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && addProvider()}
                          style={{ flex: 1, padding: '0.4rem 0.6rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.85rem' }}
                        />
                        <button onClick={addProvider} style={{ padding: '0.4rem 0.8rem', background: 'var(--gold)', color: '#000', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>+</button>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.5rem' }}>
                        {tournamentProviders.map((p, i) => (
                          <span key={p} style={{ padding: '0.25rem 0.5rem', background: `${getProviderColor(p)}20`, border: `1px solid ${getProviderColor(p)}40`, borderRadius: '12px', fontSize: '0.75rem', color: getProviderColor(p), display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            #{i+1} {p}
                            <button onClick={() => removeProvider(p)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontSize: '0.9rem', lineHeight: 1 }}>×</button>
                          </span>
                        ))}
                      </div>
                      {/* Quick add */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                        {['Pragmatic', 'Hacksaw', 'Nolimit City', 'BGaming', 'NetEnt', 'Red Tiger', 'ELK Studios', 'Peter & Sons', 'Relax Gaming', 'Big Time Gaming'].filter(p => !tournamentProviders.includes(p)).slice(0, 6).map(p => (
                          <button key={p} onClick={() => setTournamentProviders(prev => [...prev, p])} style={{ padding: '0.2rem 0.4rem', background: `${getProviderColor(p)}10`, border: `1px solid ${getProviderColor(p)}30`, borderRadius: '4px', color: getProviderColor(p), cursor: 'pointer', fontSize: '0.7rem', fontFamily: 'inherit' }}>+ {p}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {!activeTournament ? (
                      <button
                        onClick={generateBracket}
                        disabled={tournamentProviders.length < tournamentSize}
                        style={{ padding: '0.6rem 1.2rem', background: tournamentProviders.length < tournamentSize ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, var(--gold), #f59e0b)', color: tournamentProviders.length < tournamentSize ? 'var(--text-dim)' : '#000', border: 'none', borderRadius: '8px', cursor: tournamentProviders.length < tournamentSize ? 'not-allowed' : 'pointer', fontWeight: 700, fontFamily: 'inherit' }}
                      >
                        🎲 Shuffle & Start Tournament
                      </button>
                    ) : (
                      <>
                        <button onClick={generateBracket} style={{ padding: '0.5rem 1rem', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '6px', color: '#3b82f6', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>🔄 Re-shuffle</button>
                        <button onClick={fixLosersBracket} style={{ padding: '0.5rem 1rem', background: 'rgba(245, 158, 11, 0.2)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '6px', color: '#f59e0b', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>🔧 Fix Losers Bracket</button>
                        {activeTournament.winner && (
                          <button onClick={finishTournament} style={{ padding: '0.5rem 1rem', background: 'rgba(34, 197, 94, 0.2)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '6px', color: '#22c55e', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>✅ Save to History</button>
                        )}
                        <button onClick={resetTournament} style={{ padding: '0.5rem 1rem', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', color: '#ef4444', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>🗑️ Cancel</button>
                      </>
                    )}
                    <button onClick={() => setTournamentProviders([])} style={{ padding: '0.5rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-dim)', cursor: 'pointer', fontFamily: 'inherit' }}>Clear Providers</button>
                  </div>
                </div>
              </div>
            )}

            {/* No Active Tournament Message */}
            {!activeTournament && (
              <div className="card" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                <div style={{ fontSize: '5rem', marginBottom: '1rem', opacity: 0.4 }}>🏆</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>No Active Tournament</div>
                <div style={{ color: 'var(--text-dim)', maxWidth: '500px', margin: '0 auto' }}>
                  {user?.isAdmin 
                    ? 'Click "Tournament Setup" above to create a new bracket and start a provider tournament!'
                    : 'Check back when the streamer starts a provider tournament! Watch as slot providers compete head-to-head.'}
                </div>
              </div>
            )}

            {/* Active Tournament Bracket - Old Format Warning */}
            {activeTournament && !activeTournament.winnersRounds && activeTournament.rounds && (
              <div className="card">
                <div className="card-header">
                  <div className="card-icon">⚠️</div>
                  <div className="card-title">Tournament Update Required</div>
                </div>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-dim)', marginBottom: '1rem' }}>
                    This tournament uses an older format. Please delete it and create a new one to use the double elimination bracket.
                  </p>
                  {user?.isAdmin && (
                    <button
                      onClick={() => setShowTournamentSetup(true)}
                      style={{ padding: '0.6rem 1.2rem', background: 'var(--gold)', color: '#000', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
                    >
                      Open Tournament Setup
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Active Tournament Bracket */}
            {activeTournament && activeTournament.winnersRounds && (
              <div className="card">
                <div className="card-header">
                  <div className="card-icon">🏆</div>
                  <div className="card-title">Provider Tournament</div>
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <button
                      onClick={() => setShowBracketFullscreen(true)}
                      className="full-view-btn"
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.25), rgba(236, 72, 153, 0.2))',
                        border: '1px solid rgba(139, 92, 246, 0.5)',
                        borderRadius: '8px',
                        color: '#a78bfa',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        boxShadow: '0 0 15px rgba(139, 92, 246, 0.3)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.4), rgba(236, 72, 153, 0.35))';
                        e.target.style.boxShadow = '0 0 25px rgba(139, 92, 246, 0.5)';
                        e.target.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.25), rgba(236, 72, 153, 0.2))';
                        e.target.style.boxShadow = '0 0 15px rgba(139, 92, 246, 0.3)';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      <span style={{ fontSize: '1rem' }}>⛶</span> Full View
                    </button>
                    <span style={{ padding: '0.25rem 0.6rem', background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <span style={{ width: '6px', height: '6px', background: '#22c55e', borderRadius: '50%', animation: 'pulse 2s infinite' }}></span>
                      LIVE
                    </span>
                    {activeTournament.activeMatchOverride && (
                      <span style={{ padding: '0.25rem 0.6rem', background: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        ⚡ Manual Order
                      </span>
                    )}
                    {activeTournament.winner && (
                      <span style={{ padding: '0.3rem 0.75rem', background: `${getProviderColor(activeTournament.winner)}20`, color: getProviderColor(activeTournament.winner), borderRadius: '12px', fontSize: '0.9rem', fontWeight: 700 }}>
                        🎉 {activeTournament.winner} WINS!
                      </span>
                    )}
                  </span>
                </div>
                
                <div 
                  ref={bracketContainerRef}
                  id="tournament-bracket-container"
                  style={{ 
                    padding: '1.5rem', 
                    overflowX: 'auto',
                    cursor: 'grab'
                  }}
                  onMouseDown={(e) => {
                    // Don't start drag if clicking on interactive elements
                    const tag = e.target.tagName.toLowerCase();
                    if (tag === 'input' || tag === 'button' || tag === 'select' || tag === 'textarea') {
                      return;
                    }
                    if (e.target.closest('button') || e.target.closest('input')) {
                      return;
                    }
                    const container = bracketContainerRef.current;
                    if (!container) return;
                    
                    e.preventDefault();
                    container.style.cursor = 'grabbing';
                    container.dataset.isDragging = 'true';
                    container.dataset.startX = e.clientX;
                    container.dataset.scrollLeft = container.scrollLeft;
                  }}
                  onMouseUp={() => {
                    const container = bracketContainerRef.current;
                    if (!container) return;
                    container.style.cursor = 'grab';
                    container.dataset.isDragging = 'false';
                  }}
                  onMouseLeave={() => {
                    const container = bracketContainerRef.current;
                    if (!container) return;
                    container.style.cursor = 'grab';
                    container.dataset.isDragging = 'false';
                  }}
                  onMouseMove={(e) => {
                    const container = bracketContainerRef.current;
                    if (!container || container.dataset.isDragging !== 'true') return;
                    e.preventDefault();
                    const dx = e.clientX - parseFloat(container.dataset.startX);
                    container.scrollLeft = parseFloat(container.dataset.scrollLeft) - dx;
                  }}
                >
                  {(() => {
                    // Find current active match for highlighting and auto-scroll
                    const findActiveMatch = () => {
                      // Check for manual override first
                      if (activeTournament.activeMatchOverride) {
                        const override = activeTournament.activeMatchOverride;
                        // Verify the override match still exists and hasn't been completed
                        if (override.type === 'winners') {
                          const m = activeTournament.winnersRounds[override.round]?.[override.match];
                          if (m && !m.winner) return { type: 'winners', round: override.round, match: override.match, id: m.id };
                        } else if (override.type === 'losers') {
                          const m = activeTournament.losersRounds?.[override.round]?.[override.match];
                          if (m && !m.winner) return { type: 'losers', round: override.round, match: override.match, id: m.id };
                        } else if (override.type === 'grand') {
                          if (activeTournament.grandFinal && !activeTournament.grandFinal.winner) {
                            return { type: 'grand', round: 0, match: 0, id: 'gf' };
                          }
                        }
                        // Override match is complete, fall through to auto-detect
                      }
                      
                      // Auto-detect: Check winners bracket
                      for (let ri = 0; ri < activeTournament.winnersRounds.length; ri++) {
                        for (let mi = 0; mi < activeTournament.winnersRounds[ri].length; mi++) {
                          const m = activeTournament.winnersRounds[ri][mi];
                          if (m.provider1 && m.provider2 && !m.winner) {
                            return { type: 'winners', round: ri, match: mi, id: m.id };
                          }
                        }
                      }
                      // Check losers bracket
                      for (let ri = 0; ri < (activeTournament.losersRounds?.length || 0); ri++) {
                        for (let mi = 0; mi < activeTournament.losersRounds[ri].length; mi++) {
                          const m = activeTournament.losersRounds[ri][mi];
                          if (m.provider1 && m.provider2 && !m.winner) {
                            return { type: 'losers', round: ri, match: mi, id: m.id };
                          }
                        }
                      }
                      // Check grand final
                      if (activeTournament.grandFinal?.provider1 && activeTournament.grandFinal?.provider2 && !activeTournament.grandFinal?.winner) {
                        return { type: 'grand', round: 0, match: 0, id: 'gf' };
                      }
                      return null;
                    };
                    
                    const activeMatch = findActiveMatch();
                    
                    // Function to set active match override
                    const setActiveMatchOverride = async (bracketType, roundIndex, matchIndex) => {
                      const newBracket = { ...activeTournament };
                      newBracket.activeMatchOverride = { type: bracketType, round: roundIndex, match: matchIndex };
                      
                      try {
                        const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/tournaments/${activeTournament.id}`, {
                          method: 'PUT',
                          body: JSON.stringify({ bracket: newBracket })
                        });
                        if (res.ok) {
                          setActiveTournament(newBracket);
                        }
                      } catch (error) {
                        console.error('Error setting active match:', error);
                      }
                    };
                    
                    // Function to clear active match override (return to auto-detect)
                    const clearActiveMatchOverride = async () => {
                      const newBracket = { ...activeTournament };
                      delete newBracket.activeMatchOverride;
                      
                      try {
                        const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/tournaments/${activeTournament.id}`, {
                          method: 'PUT',
                          body: JSON.stringify({ bracket: newBracket })
                        });
                        if (res.ok) {
                          setActiveTournament(newBracket);
                        }
                      } catch (error) {
                        console.error('Error clearing active match:', error);
                      }
                    };
                    
                    // Auto-scroll to active match ONCE on initial load
                    if (activeMatch && !bracketScrolledOnce) {
                      setTimeout(() => {
                        const el = document.getElementById(`match-${activeMatch.id}`);
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                          setBracketScrolledOnce(true);
                        }
                      }, 300);
                    }
                    
                    const renderMatchCard = (match, bracketType, roundIndex, matchIndex, canEditRound = false) => {
                      if (!match) return null;
                      
                      const isActive = activeMatch?.id === match.id;
                      const p1Profit = match.provider1Start && match.provider1End ? match.provider1End - match.provider1Start : null;
                      const p2Profit = match.provider2Start && match.provider2End ? match.provider2End - match.provider2Start : null;
                      const p1Percent = match.provider1Start && match.provider1End ? ((match.provider1End - match.provider1Start) / match.provider1Start * 100) : null;
                      const p2Percent = match.provider2Start && match.provider2End ? ((match.provider2End - match.provider2Start) / match.provider2Start * 100) : null;
                      const bothHaveResults = p1Percent !== null && p2Percent !== null;
                      const suggestedWinner = bothHaveResults ? (p1Percent >= p2Percent ? match.provider1 : match.provider2) : null;
                      
                      // Get match label
                      const getMatchLabel = () => {
                        if (bracketType === 'winners') return `W${roundIndex + 1}.${matchIndex + 1}`;
                        if (bracketType === 'losers') return `L${roundIndex + 1}.${matchIndex + 1}`;
                        if (bracketType === 'grand') return 'GF';
                        return '';
                      };
                      
                      const providerCard = (provider, profit, isWinner, isLoser, position) => {
                        // Format profit to be more compact for large numbers
                        const formatProfit = (p) => {
                          const abs = Math.abs(p);
                          if (abs >= 1000) return (abs / 1000).toFixed(1) + 'K';
                          return abs.toFixed(2);
                        };
                        
                        return (
                        <div 
                          onClick={() => canEditRound && user?.isAdmin && replaceProvider(bracketType, roundIndex, matchIndex, position)}
                          style={{
                            padding: '0.75rem 1rem',
                            background: isWinner ? `${getProviderColor(provider)}15` : 'rgba(0,0,0,0.2)',
                            borderRadius: '8px',
                            opacity: isLoser ? 0.4 : 1,
                            cursor: canEditRound && user?.isAdmin ? 'pointer' : 'default',
                            border: isWinner ? `2px solid ${getProviderColor(provider)}50` : '2px solid transparent',
                            transition: 'all 0.2s ease',
                            overflow: 'hidden'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {isWinner && <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>👑</span>}
                            <div style={{
                              width: '12px', height: '12px', borderRadius: '50%',
                              background: provider ? getProviderColor(provider) : 'var(--border)',
                              flexShrink: 0,
                              boxShadow: provider ? `0 0 8px ${getProviderColor(provider)}50` : 'none'
                            }} />
                            <span style={{
                              fontWeight: 700,
                              fontSize: '0.95rem',
                              color: provider ? (isWinner ? getProviderColor(provider) : 'var(--text)') : 'var(--text-dim)',
                              flex: 1,
                              minWidth: '60px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {provider || 'TBD'}
                            </span>
                            {profit !== null && (
                              <span style={{
                                fontFamily: "'Space Mono', monospace",
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                color: profit >= 0 ? '#22c55e' : '#ef4444',
                                background: profit >= 0 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                padding: '0.15rem 0.4rem',
                                borderRadius: '4px',
                                flexShrink: 0,
                                whiteSpace: 'nowrap'
                              }}>
                                {profit >= 0 ? '+' : '-'}${formatProfit(profit)}
                              </span>
                            )}
                          </div>
                          {/* Admin balance inputs */}
                          {user?.isAdmin && provider && !match.winner && (
                            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginTop: '0.5rem' }}>
                              <input
                                type="number"
                                placeholder="Start $"
                                value={position === 1 ? (match.provider1Start || '') : (match.provider2Start || '')}
                                onChange={(e) => updateMatchBalance(bracketType, roundIndex, matchIndex, position === 1 ? 'provider1Start' : 'provider2Start', e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                style={{ width: '80px', padding: '0.3rem 0.5rem', background: 'rgba(0,0,0,0.4)', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text)', fontSize: '0.8rem', fontFamily: "'Space Mono', monospace", cursor: 'text' }}
                              />
                              <span style={{ color: 'var(--text-dim)' }}>→</span>
                              <input
                                type="number"
                                placeholder="End $"
                                value={position === 1 ? (match.provider1End || '') : (match.provider2End || '')}
                                onChange={(e) => updateMatchBalance(bracketType, roundIndex, matchIndex, position === 1 ? 'provider1End' : 'provider2End', e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                style={{ width: '80px', padding: '0.3rem 0.5rem', background: 'rgba(0,0,0,0.4)', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text)', fontSize: '0.8rem', fontFamily: "'Space Mono', monospace", cursor: 'text' }}
                              />
                            </div>
                          )}
                        </div>
                      );
                      };
                      
                      return (
                        <div
                          id={`match-${match.id}`}
                          key={match.id}
                          style={{
                            background: isActive ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(236, 72, 153, 0.1))' : 'rgba(0,0,0,0.3)',
                            borderRadius: '12px',
                            padding: '0.75rem',
                            border: isActive ? '2px solid rgba(139, 92, 246, 0.5)' : match.winner ? `2px solid ${getProviderColor(match.winner)}30` : '2px solid var(--border)',
                            position: 'relative',
                            minWidth: '240px',
                            boxShadow: isActive ? '0 0 20px rgba(139, 92, 246, 0.3)' : 'none',
                            animation: isActive ? 'glow 2s ease-in-out infinite alternate' : 'none'
                          }}
                        >
                          {/* Match Label Badge */}
                          <div style={{
                            position: 'absolute',
                            top: '-10px',
                            left: '8px',
                            background: bracketType === 'winners' ? 'var(--gold)' : bracketType === 'losers' ? '#ef4444' : 'linear-gradient(90deg, var(--gold), #f59e0b)',
                            color: '#000',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            padding: '2px 6px',
                            borderRadius: '4px',
                            zIndex: 1
                          }}>
                            {getMatchLabel()}
                          </div>
                          
                          {/* Active match indicator */}
                          {isActive && (
                            <div style={{
                              position: 'absolute',
                              top: '-8px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              background: 'linear-gradient(90deg, #8b5cf6, #ec4899)',
                              color: 'white',
                              fontSize: '0.6rem',
                              fontWeight: 700,
                              padding: '0.2rem 0.6rem',
                              borderRadius: '4px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              ⚡ Live Match
                            </div>
                          )}
                          
                          {/* Swap button for admins */}
                          {canEditRound && user?.isAdmin && !match.winner && (
                            <button
                              onClick={() => swapWithinMatch(bracketType, roundIndex, matchIndex)}
                              onMouseDown={(e) => e.stopPropagation()}
                              style={{
                                position: 'absolute', top: '8px', right: '8px',
                                padding: '4px 8px', background: 'rgba(59, 130, 246, 0.2)',
                                border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '4px',
                                color: '#3b82f6', cursor: 'pointer', fontSize: '0.7rem'
                              }}
                            >
                              ⇅ Swap
                            </button>
                          )}
                          
                          {/* Set Active button for admins - only show on matches that can be played */}
                          {user?.isAdmin && match.provider1 && match.provider2 && !match.winner && !isActive && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setActiveMatchOverride(bracketType, roundIndex, matchIndex);
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              style={{
                                position: 'absolute', bottom: '8px', right: '8px',
                                padding: '3px 6px', background: 'rgba(139, 92, 246, 0.2)',
                                border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '4px',
                                color: '#a78bfa', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 600,
                                zIndex: 10
                              }}
                              title="Set as current active match"
                            >
                              ⚡ Set Active
                            </button>
                          )}
                          
                          {/* Clear Override button - show on active match if override is set */}
                          {user?.isAdmin && isActive && activeTournament.activeMatchOverride && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                clearActiveMatchOverride();
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              style={{
                                position: 'absolute', bottom: '8px', right: '8px',
                                padding: '3px 6px', background: 'rgba(239, 68, 68, 0.2)',
                                border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '4px',
                                color: '#ef4444', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 600,
                                zIndex: 10
                              }}
                              title="Clear manual override, return to auto-detect"
                            >
                              ✕ Auto
                            </button>
                          )}
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.25rem' }}>
                            {providerCard(match.provider1, p1Profit, match.winner === match.provider1, match.winner && match.winner !== match.provider1, 1)}
                            
                            {/* VS / Winner controls */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.3rem', gap: '0.5rem' }}>
                              {user?.isAdmin && match.provider1 && match.provider2 && !match.winner ? (
                                bothHaveResults ? (
                                  <button
                                    onClick={() => setMatchWinner(bracketType, roundIndex, matchIndex, suggestedWinner)}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    style={{
                                      padding: '0.4rem 1rem',
                                      background: `linear-gradient(135deg, ${getProviderColor(suggestedWinner)}40, ${getProviderColor(suggestedWinner)}20)`,
                                      border: `2px solid ${getProviderColor(suggestedWinner)}60`,
                                      borderRadius: '6px',
                                      color: getProviderColor(suggestedWinner),
                                      cursor: 'pointer',
                                      fontSize: '0.8rem',
                                      fontWeight: 700
                                    }}
                                  >
                                    ✓ {suggestedWinner} Wins ({(p1Percent >= p2Percent ? p1Percent : p2Percent).toFixed(1)}%)
                                  </button>
                                ) : (
                                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <button
                                      onClick={() => setMatchWinner(bracketType, roundIndex, matchIndex, match.provider1)}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      style={{ padding: '0.3rem 0.75rem', background: `${getProviderColor(match.provider1)}20`, border: `1px solid ${getProviderColor(match.provider1)}40`, borderRadius: '4px', color: getProviderColor(match.provider1), cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                                    >
                                      {match.provider1?.split(' ')[0]} ✓
                                    </button>
                                    <span style={{ color: 'var(--text-dim)', fontSize: '0.8rem', fontWeight: 700 }}>VS</span>
                                    <button
                                      onClick={() => setMatchWinner(bracketType, roundIndex, matchIndex, match.provider2)}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      style={{ padding: '0.3rem 0.75rem', background: `${getProviderColor(match.provider2)}20`, border: `1px solid ${getProviderColor(match.provider2)}40`, borderRadius: '4px', color: getProviderColor(match.provider2), cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                                    >
                                      {match.provider2?.split(' ')[0]} ✓
                                    </button>
                                  </div>
                                )
                              ) : user?.isAdmin && match.winner ? (
                                <button
                                  onClick={() => undoMatchWinner(bracketType, roundIndex, matchIndex)}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  style={{ padding: '0.3rem 0.75rem', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '4px', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}
                                >
                                  ↩ Undo Result
                                </button>
                              ) : (
                                <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem', fontWeight: 700 }}>VS</span>
                              )}
                            </div>
                            
                            {providerCard(match.provider2, p2Profit, match.winner === match.provider2, match.winner && match.winner !== match.provider2, 2)}
                          </div>
                        </div>
                      );
                    };
                    
                    const noWinnersYet = !activeTournament.winnersRounds[0]?.some(m => m.winner);
                    
                    // Build columns: Winners (L->R) | Grand Final | Losers (R->L reversed)
                    const winnersColumns = [];
                    const losersColumns = [];
                    
                    // Winners bracket columns (left to right)
                    activeTournament.winnersRounds.forEach((round, ri) => {
                      const isLast = ri === activeTournament.winnersRounds.length - 1;
                      winnersColumns.push({
                        type: 'winners',
                        title: isLast ? 'WINNERS FINAL' : ri === activeTournament.winnersRounds.length - 2 ? 'SEMI-FINALS' : `ROUND ${ri + 1}`,
                        color: 'var(--gold)',
                        icon: isLast ? '⚔️' : '🏆',
                        matches: round,
                        roundIndex: ri,
                        canEdit: ri === 0 && noWinnersYet
                      });
                    });
                    
                    // Losers bracket columns (will be reversed to go right to left)
                    if (activeTournament.losersRounds?.length > 0) {
                      activeTournament.losersRounds.forEach((round, ri) => {
                        const isLast = ri === activeTournament.losersRounds.length - 1;
                        losersColumns.push({
                          type: 'losers',
                          title: isLast ? 'LOSERS FINAL' : `LOSERS ${ri + 1}`,
                          color: '#ef4444',
                          icon: isLast ? '⚔️' : '💀',
                          matches: round,
                          roundIndex: ri,
                          canEdit: false
                        });
                      });
                    }
                    
                    // Reverse losers so it flows right to left (Losers Final closest to Grand Final)
                    const losersReversed = [...losersColumns].reverse();
                    
                    // Grand Final in the middle
                    const grandFinalColumn = activeTournament.grandFinal ? {
                      type: 'grand',
                      title: 'GRAND FINAL',
                      color: 'linear-gradient(90deg, var(--gold), #f59e0b)',
                      icon: '👑',
                      matches: [activeTournament.grandFinal],
                      roundIndex: 0,
                      canEdit: false,
                      isGrandFinal: true
                    } : null;
                    
                    return (
                      <div style={{ display: 'flex', gap: '1.5rem', minWidth: 'max-content', alignItems: 'stretch' }}>
                        {/* Winners Bracket - Left Side */}
                        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'stretch' }}>
                          {winnersColumns.map((col, colIdx) => (
                            <div 
                              key={`w-${colIdx}`} 
                              style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                width: '260px',
                                flexShrink: 0
                              }}
                            >
                              <div style={{
                                textAlign: 'center',
                                padding: '0.5rem 0.75rem',
                                marginBottom: '0.75rem',
                                background: 'rgba(255, 215, 0, 0.1)',
                                borderRadius: '8px',
                                border: '1px solid rgba(255, 215, 0, 0.2)'
                              }}>
                                <span style={{ marginRight: '0.4rem' }}>{col.icon}</span>
                                <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.5px', color: 'var(--gold)' }}>
                                  {col.title}
                                </span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', justifyContent: 'space-around', flex: 1 }}>
                                {col.matches.map((match, mi) => renderMatchCard(match, col.type, col.roundIndex, mi, col.canEdit))}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Grand Final - Center */}
                        {grandFinalColumn && (
                          <div 
                            style={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              minWidth: '300px',
                              background: 'linear-gradient(180deg, rgba(255, 215, 0, 0.08), rgba(245, 158, 11, 0.03))',
                              borderRadius: '16px',
                              padding: '1rem',
                              border: '2px solid rgba(255, 215, 0, 0.2)',
                              boxShadow: '0 0 30px rgba(255, 215, 0, 0.1)'
                            }}
                          >
                            <div style={{
                              textAlign: 'center',
                              padding: '0.6rem 1rem',
                              marginBottom: '0.75rem',
                              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(245, 158, 11, 0.1))',
                              borderRadius: '8px',
                              border: '1px solid rgba(255, 215, 0, 0.3)'
                            }}>
                              <span style={{ marginRight: '0.4rem', fontSize: '1.2rem' }}>👑</span>
                              <span style={{
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                letterSpacing: '1px',
                                background: 'linear-gradient(90deg, var(--gold), #f59e0b)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                              }}>
                                GRAND FINAL
                              </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', justifyContent: 'center', flex: 1 }}>
                              {renderMatchCard(grandFinalColumn.matches[0], 'grand', 0, 0, false)}
                            </div>
                          </div>
                        )}
                        
                        {/* Losers Bracket - Right Side (Reversed order) */}
                        {losersReversed.length > 0 && (
                          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'stretch' }}>
                            {losersReversed.map((col, colIdx) => (
                              <div 
                                key={`l-${colIdx}`} 
                                style={{ 
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  width: '260px',
                                  flexShrink: 0
                                }}
                              >
                                <div style={{
                                  textAlign: 'center',
                                  padding: '0.5rem 0.75rem',
                                  marginBottom: '0.75rem',
                                  background: 'rgba(239, 68, 68, 0.1)',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(239, 68, 68, 0.2)'
                                }}>
                                  <span style={{ marginRight: '0.4rem' }}>{col.icon}</span>
                                  <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.5px', color: '#ef4444' }}>
                                    {col.title}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', justifyContent: 'space-around', flex: 1 }}>
                                  {col.matches.map((match, mi) => renderMatchCard(match, col.type, col.roundIndex, mi, col.canEdit))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                
                {/* Add glow animation CSS */}
                <style>{`
                  @keyframes glow {
                    from { box-shadow: 0 0 10px rgba(139, 92, 246, 0.3); }
                    to { box-shadow: 0 0 25px rgba(139, 92, 246, 0.5), 0 0 50px rgba(236, 72, 153, 0.2); }
                  }
                `}</style>
              </div>
            )}

            {/* Fullscreen Bracket Modal */}
            {showBracketFullscreen && activeTournament && activeTournament.winnersRounds && (
              <div 
                onClick={() => setShowBracketFullscreen(false)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.95)',
                  zIndex: 10000,
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer'
                }}
              >
                {/* Modal Header */}
                <div 
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    padding: '0.5rem 1.5rem',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'rgba(0, 0, 0, 0.8)',
                    flexShrink: 0,
                    cursor: 'default'
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>🏆</span>
                    <span style={{ fontSize: '1rem', fontWeight: 700 }}>Tournament Bracket</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)', opacity: 0.7 }}>click anywhere to close</span>
                    {activeTournament.winner && (
                      <span style={{ 
                        padding: '0.25rem 0.6rem', 
                        background: `${getProviderColor(activeTournament.winner)}20`, 
                        color: getProviderColor(activeTournament.winner), 
                        borderRadius: '8px', 
                        fontSize: '0.8rem', 
                        fontWeight: 700 
                      }}>
                        🎉 {activeTournament.winner} WINS!
                      </span>
                    )}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowBracketFullscreen(false); }}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: 'rgba(239, 68, 68, 0.2)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '6px',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      fontWeight: 600
                    }}
                  >
                    ✕ Close
                  </button>
                </div>
                
                {/* Modal Content - Scaled to fit screen */}
                <div 
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem',
                    overflow: 'hidden'
                  }}
                >
                  {(() => {
                    // Calculate scale to fit everything
                    const totalCols = activeTournament.winnersRounds.length + 1 + (activeTournament.losersRounds?.length || 0);
                    const baseColWidth = 160;
                    const baseGap = 12;
                    const totalWidth = (totalCols * baseColWidth) + ((totalCols - 1) * baseGap);
                    const availableWidth = window.innerWidth - 100;
                    const availableHeight = window.innerHeight - 120;
                    
                    // Calculate max matches in any round for height
                    const maxMatches = Math.max(
                      ...activeTournament.winnersRounds.map(r => r.length),
                      ...(activeTournament.losersRounds?.map(r => r.length) || [1])
                    );
                    const baseMatchHeight = 85;
                    const totalHeight = maxMatches * baseMatchHeight + 60;
                    
                    const scaleX = availableWidth / totalWidth;
                    const scaleY = availableHeight / totalHeight;
                    const scale = Math.min(scaleX, scaleY, 1);
                    
                    // Helper to get match label
                    const getMatchLabel = (bracketType, roundIndex, matchIndex) => {
                      if (bracketType === 'winners') return `W${roundIndex + 1}.${matchIndex + 1}`;
                      if (bracketType === 'losers') return `L${roundIndex + 1}.${matchIndex + 1}`;
                      if (bracketType === 'grand') return 'GF';
                      return '';
                    };
                    
                    // Helper to get source info for TBD slots
                    const getSourceLabel = (bracketType, roundIndex, matchIndex, position) => {
                      if (bracketType === 'winners') {
                        if (roundIndex === 0) return null; // First round has actual providers
                        // Winners come from previous winners round
                        const prevMatchIdx = matchIndex * 2 + (position - 1);
                        return `Winner W${roundIndex}.${prevMatchIdx + 1}`;
                      }
                      if (bracketType === 'losers') {
                        if (roundIndex === 0) {
                          // First losers round - losers from Winners R1
                          const srcMatch = matchIndex * 2 + (position - 1);
                          return `Loser W1.${srcMatch + 1}`;
                        }
                        // Complex losers bracket logic - simplified labels
                        if (roundIndex % 2 === 1) {
                          // Odd rounds often get drop-downs from winners
                          if (position === 1) return `Winner L${roundIndex}.${matchIndex + 1}`;
                          return `Loser W${Math.ceil((roundIndex + 2) / 2)}.${matchIndex + 1}`;
                        }
                        return `Winner L${roundIndex}.${matchIndex * 2 + position}`;
                      }
                      if (bracketType === 'grand') {
                        if (position === 1) return 'Winner of Winners';
                        return 'Winner of Losers';
                      }
                      return null;
                    };
                    
                    // Simple match card for fullscreen with labels
                    const renderSimpleMatch = (match, bracketType, roundIndex, matchIndex) => {
                      const matchLabel = getMatchLabel(bracketType, roundIndex, matchIndex);
                      
                      return (
                        <div key={match.id} style={{
                          background: match.winner ? `${getProviderColor(match.winner)}15` : 'rgba(0,0,0,0.5)',
                          borderRadius: '6px',
                          padding: '4px',
                          border: match.winner ? `1px solid ${getProviderColor(match.winner)}50` : '1px solid var(--border)',
                          marginBottom: '6px',
                          position: 'relative'
                        }}>
                          {/* Match Label */}
                          <div style={{
                            position: 'absolute',
                            top: '-8px',
                            left: '4px',
                            background: bracketType === 'winners' ? 'var(--gold)' : bracketType === 'losers' ? '#ef4444' : 'linear-gradient(90deg, var(--gold), #f59e0b)',
                            color: '#000',
                            fontSize: '8px',
                            fontWeight: 700,
                            padding: '1px 4px',
                            borderRadius: '3px'
                          }}>
                            {matchLabel}
                          </div>
                          
                          {[match.provider1, match.provider2].map((provider, pi) => {
                            const sourceLabel = !provider ? getSourceLabel(bracketType, roundIndex, matchIndex, pi + 1) : null;
                            
                            return (
                              <div key={pi} style={{
                                padding: '4px 8px',
                                background: match.winner === provider ? `${getProviderColor(provider)}25` : 'rgba(0,0,0,0.3)',
                                borderRadius: '4px',
                                marginBottom: pi === 0 ? '3px' : 0,
                                opacity: match.winner && match.winner !== provider ? 0.4 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                minHeight: '22px'
                              }}>
                                {match.winner === provider && <span style={{ fontSize: '10px' }}>👑</span>}
                                <div style={{
                                  width: '8px', height: '8px', borderRadius: '50%',
                                  background: provider ? getProviderColor(provider) : 'var(--border)',
                                  flexShrink: 0
                                }} />
                                <span style={{
                                  fontWeight: provider ? 600 : 400,
                                  fontSize: provider ? '11px' : '9px',
                                  color: provider ? (match.winner === provider ? getProviderColor(provider) : 'var(--text)') : 'var(--text-dim)',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  fontStyle: provider ? 'normal' : 'italic'
                                }}>
                                  {provider || sourceLabel || 'TBD'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    };
                    
                    return (
                      <div 
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          transform: `scale(${scale})`,
                          transformOrigin: 'center center',
                          display: 'flex', 
                          gap: `${baseGap}px`, 
                          alignItems: 'center',
                          cursor: 'default'
                        }}
                      >
                        {/* Winners Bracket */}
                        {activeTournament.winnersRounds.map((round, ri) => {
                          const isLast = ri === activeTournament.winnersRounds.length - 1;
                          return (
                            <div key={`fw-${ri}`} style={{ width: `${baseColWidth}px`, flexShrink: 0 }}>
                              <div style={{
                                textAlign: 'center',
                                padding: '4px 8px',
                                marginBottom: '12px',
                                background: 'rgba(255, 215, 0, 0.15)',
                                borderRadius: '4px',
                                border: '1px solid rgba(255, 215, 0, 0.3)'
                              }}>
                                <span style={{ marginRight: '4px', fontSize: '10px' }}>{isLast ? '⚔️' : '🏆'}</span>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--gold)' }}>
                                  {isLast ? 'WINNERS FINAL' : ri === activeTournament.winnersRounds.length - 2 ? 'SEMI-FINALS' : `ROUND ${ri + 1}`}
                                </span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', minHeight: `${round.length * baseMatchHeight}px`, paddingTop: '8px' }}>
                                {round.map((match, mi) => renderSimpleMatch(match, 'winners', ri, mi))}
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Grand Final */}
                        {activeTournament.grandFinal && (
                          <div style={{ 
                            width: `${baseColWidth + 10}px`, 
                            flexShrink: 0,
                            background: 'linear-gradient(180deg, rgba(255, 215, 0, 0.1), transparent)',
                            borderRadius: '8px',
                            padding: '8px',
                            border: '1px solid rgba(255, 215, 0, 0.3)'
                          }}>
                            <div style={{
                              textAlign: 'center',
                              padding: '6px 8px',
                              marginBottom: '12px',
                              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.25), rgba(245, 158, 11, 0.15))',
                              borderRadius: '4px'
                            }}>
                              <span style={{ fontSize: '14px', marginRight: '4px' }}>👑</span>
                              <span style={{
                                fontSize: '11px',
                                fontWeight: 700,
                                background: 'linear-gradient(90deg, var(--gold), #f59e0b)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                              }}>
                                GRAND FINAL
                              </span>
                            </div>
                            <div style={{ paddingTop: '8px' }}>
                              {renderSimpleMatch(activeTournament.grandFinal, 'grand', 0, 0)}
                            </div>
                          </div>
                        )}
                        
                        {/* Losers Bracket (Reversed) */}
                        {activeTournament.losersRounds && [...activeTournament.losersRounds].reverse().map((round, ri) => {
                          const actualRi = activeTournament.losersRounds.length - 1 - ri;
                          const isLast = actualRi === activeTournament.losersRounds.length - 1;
                          return (
                            <div key={`fl-${ri}`} style={{ width: `${baseColWidth}px`, flexShrink: 0 }}>
                              <div style={{
                                textAlign: 'center',
                                padding: '4px 8px',
                                marginBottom: '12px',
                                background: 'rgba(239, 68, 68, 0.15)',
                                borderRadius: '4px',
                                border: '1px solid rgba(239, 68, 68, 0.3)'
                              }}>
                                <span style={{ marginRight: '4px', fontSize: '10px' }}>{isLast ? '⚔️' : '💀'}</span>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#ef4444' }}>
                                  {isLast ? 'LOSERS FINAL' : `LOSERS ${actualRi + 1}`}
                                </span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', minHeight: `${round.length * baseMatchHeight}px`, paddingTop: '8px' }}>
                                {round.map((match, mi) => renderSimpleMatch(match, 'losers', actualRi, mi))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Tournament History */}
            {tournamentHistory.length > 0 && (
              <div className="card" style={{ marginTop: '1.5rem' }}>
                <div className="card-header">
                  <div className="card-icon">📜</div>
                  <div className="card-title">Tournament History</div>
                  <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: 'var(--text-dim)' }}>{tournamentHistory.length} completed</span>
                </div>
                <div style={{ maxHeight: '350px', overflow: 'auto' }}>
                  {tournamentHistory.map((t, idx) => (
                    <div key={t.id} style={{ padding: '1rem', borderBottom: idx < tournamentHistory.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ width: '50px', height: '50px', borderRadius: '10px', background: `${getProviderColor(t.winner)}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem' }}>🏆</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: '1.1rem', color: getProviderColor(t.winner) }}>{t.winner}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>
                          {t.providers?.length || t.size} providers • {new Date(t.completedAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* PREDICTIONS PAGE */}
        {activePage === 'predictions' && (
          <>
            <div className="header">
              <h1 className="header-title">🔮 <span>Predictions</span></h1>
              <p className="header-sub">{predictionSubTab === 'predict' ? 'Predict the final balance and win prizes!' : 'Winners from past bonus hunts'}</p>
            </div>

            {/* Sub-tab toggle: Predict / Results */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
              <div style={{ display: 'inline-flex', padding: '0.35rem', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(139, 92, 246, 0.2)', borderRadius: '12px', gap: '0.3rem' }}>
                {[
                  { key: 'predict', label: '🔮 Predict', color: '#a78bfa' },
                  { key: 'results', label: '🏆 Results', color: '#fbbf24' }
                ].map(t => (
                  <button
                    key={t.key}
                    onClick={() => setPredictionSubTab(t.key)}
                    style={{
                      padding: '0.6rem 1.5rem',
                      background: predictionSubTab === t.key ? `linear-gradient(135deg, ${t.color}, ${t.color}cc)` : 'transparent',
                      color: predictionSubTab === t.key ? '#fff' : 'var(--text-dim)',
                      border: 'none',
                      borderRadius: '9px',
                      fontSize: '0.9rem',
                      fontWeight: 800,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      transition: 'all 0.2s ease',
                      boxShadow: predictionSubTab === t.key ? `0 4px 14px ${t.color}44` : 'none'
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {predictionSubTab === 'predict' && (<>

            {/* Live Hunt Widget - Show when active */}
            {activeHuntData?.activeHunt && (
              <div className="card" style={{ marginBottom: '1.5rem', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,0.2)' }}>
                  <span style={{ fontSize: '1.25rem' }}>🎰</span>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Bonus Hunt #{activeHuntData.activeHunt.huntId}</h2>
                  <span style={{
                    marginLeft: 'auto',
                    padding: '0.35rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    background: activeHuntData.predictionsOpen ? 'rgba(34, 197, 94, 0.2)' : 
                               activeHuntData.activeHunt.status === 'paused' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(107, 114, 128, 0.2)',
                    color: activeHuntData.predictionsOpen ? '#22c55e' : 
                           activeHuntData.activeHunt.status === 'paused' ? '#f59e0b' : '#6b7280'
                  }}>
                    {activeHuntData.predictionsOpen ? '🟢 PREDICTIONS OPEN' : 
                     activeHuntData.activeHunt.status === 'paused' ? '⏸️ PAUSED' : '🔴 CLOSED'}
                  </span>
                </div>
                
                {/* Hunt Stats Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem', padding: '1rem' }}>
                  <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Start Balance</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--gold)' }}>${activeHuntData.activeHunt.startBalance?.toLocaleString()}</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Bonuses</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{bonusHunts?.activeHunt?.bonusesList?.length || activeHuntData.activeHunt.totalBonuses || '—'}</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Predictions</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--purple)' }}>{activeHuntData.predictionCount || 0}</div>
                  </div>
                  {bonusHunts?.activeHunt?.infoRequiredAverage && (
                  <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Req. Avg</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{bonusHunts.activeHunt.infoRequiredAverage.toFixed(1)}x</div>
                  </div>
                  )}
                </div>
                
                {/* Prizes */}
                {activeHuntData.rewards?.first && (
                  <div style={{ display: 'flex', gap: '0.75rem', padding: '0 1rem 1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <div style={{ textAlign: 'center', padding: '0.6rem 1.25rem', background: 'linear-gradient(135deg, #ffd700, #ffb800)', borderRadius: '8px', color: '#000' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 600, opacity: 0.7 }}>🥇 1ST PLACE</div>
                      <div style={{ fontWeight: 700 }}>{activeHuntData.rewards.first}</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '0.6rem 1.25rem', background: 'linear-gradient(135deg, #c0c0c0, #a0a0a0)', borderRadius: '8px', color: '#000' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 600, opacity: 0.7 }}>🥈 2ND PLACE</div>
                      <div style={{ fontWeight: 700 }}>{activeHuntData.rewards.second}</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '0.6rem 1.25rem', background: 'linear-gradient(135deg, #cd7f32, #b87333)', borderRadius: '8px', color: '#000' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 600, opacity: 0.7 }}>🥉 3RD PLACE</div>
                      <div style={{ fontWeight: 700 }}>{activeHuntData.rewards.third}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* How It Works */}
            <div className="card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.05))' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <span style={{ fontSize: '2rem' }}>💡</span>
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>How It Works</h3>
                  <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                      <span style={{ color: 'var(--gold)' }}>1.</span> Guess the final balance after all bonuses open
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                      <span style={{ color: 'var(--gold)' }}>2.</span> Submit your prediction before they close
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                      <span style={{ color: 'var(--gold)' }}>3.</span> Closest guesses win prizes!
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Prediction Form */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header">
                <div className="card-icon">🎯</div>
                <div className="card-title">Make Your Prediction</div>
              </div>
              
              {!activeHuntData?.activeHunt ? (
                <div style={{ textAlign: 'center', padding: '2.5rem' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
                  <h3 style={{ marginBottom: '0.5rem' }}>No Active Hunt</h3>
                  <p style={{ color: 'var(--text-dim)' }}>Predictions will open when Cam starts a bonus hunt!</p>
                </div>
              ) : !user ? (
                <div style={{ textAlign: 'center', padding: '2.5rem' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🎮</div>
                  <h3 style={{ marginBottom: '0.5rem' }}>Login Required</h3>
                  <p style={{ color: 'var(--text-dim)', marginBottom: '1.5rem' }}>Login with Kick or Twitch to make predictions and win prizes!</p>
                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button onClick={handleKickLogin} className="cta-btn" style={{ background: '#53fc18', color: '#000' }}>
                      Login with Kick
                    </button>
                    <button onClick={handleLogin} className="cta-btn" style={{ background: '#9147ff' }}>
                      Login with Twitch
                    </button>
                  </div>
                </div>
              ) : (activeHuntData.predictionsOpen || (_pe && myPrediction)) ? (
                <div style={{ padding: '0.5rem 0' }}>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-dim)', fontSize: '0.85rem' }}>
                        Your Predicted Final Balance
                      </label>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--gold)', fontWeight: 700, fontSize: '1.25rem' }}>$</span>
                        <input
                          type="number"
                          value={predictionInput}
                          onChange={(e) => setPredictionInput(e.target.value)}
                          placeholder="0"
                          style={{
                            width: '100%',
                            padding: '1rem 1rem 1rem 2.5rem',
                            background: 'rgba(0,0,0,0.3)',
                            border: '2px solid var(--border)',
                            borderRadius: '10px',
                            color: 'var(--text)',
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            fontFamily: "'Space Mono', monospace"
                          }}
                        />
                      </div>
                    </div>
                    <button
                      onClick={submitPrediction}
                      disabled={predictionSubmitting || !predictionInput}
                      className="cta-btn"
                      style={{ 
                        minWidth: '180px', 
                        padding: '1rem 2rem',
                        fontSize: '1rem',
                        opacity: (predictionSubmitting || !predictionInput) ? 0.6 : 1 
                      }}
                    >
                      {predictionSubmitting ? '⏳ Submitting...' : myPrediction ? '✏️ Update Prediction' : '🎯 Submit Prediction'}
                    </button>
                  </div>
                  
                  {predictionError && (
                    <div style={{ color: '#ef4444', marginTop: '0.75rem', fontSize: '0.9rem', padding: '0.75rem', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px' }}>
                      ❌ {predictionError}
                    </div>
                  )}
                  {predictionSuccess && (
                    <div style={{ color: '#22c55e', marginTop: '0.75rem', fontSize: '0.9rem', padding: '0.75rem', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px' }}>
                      ✅ {predictionSuccess}
                    </div>
                  )}
                  
                  {/* Current prediction display */}
                  {myPrediction && (
                    <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(229, 231, 235, 0.1)', border: '1px solid rgba(229, 231, 235, 0.3)', borderRadius: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <img src={user.profileImage || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} alt="" style={{ width: 40, height: 40, borderRadius: '50%', border: '2px solid var(--gold)' }} />
                        <div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Your Current Prediction</div>
                          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--gold)', fontFamily: "'Space Mono', monospace" }}>
                            ${parseFloat(myPrediction.predicted_balance).toLocaleString()}
                          </div>
                        </div>
                        <div style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--text-dim)' }}>
                          You can update until predictions close
                        </div>
                      </div>
                      {/* Hidden controls */}
                      {_pe && (
                        <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(229, 231, 235, 0.2)', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          <button
                            onClick={async () => {
                              if (!window.confirm('Delete?')) return;
                              try {
                                const res = await fetchWithAuth(`${BACKEND_API_URL}/api/predictions/m?h=${activeHuntData.activeHunt.huntId}`, { method: 'DELETE' });
                                if (res.ok) {
                                  setMyPrediction(null);
                                  fetchHuntPredictions(activeHuntData.activeHunt.huntId);
                                }
                              } catch(e) {}
                            }}
                            style={{ padding: '0.4rem 0.6rem', background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '4px', color: '#ef4444', fontSize: '0.7rem', cursor: 'pointer' }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                    {activeHuntData.activeHunt.status === 'paused' ? '⏸️' : '🔒'}
                  </div>
                  <p style={{ color: 'var(--text-dim)' }}>
                    Predictions are currently {activeHuntData.activeHunt.status === 'paused' ? 'paused' : 'closed'} for this hunt.
                  </p>
                  {myPrediction && (
                    <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(229, 231, 235, 0.1)', borderRadius: '8px', display: 'inline-block' }}>
                      <span style={{ color: 'var(--text-dim)' }}>Your prediction: </span>
                      <span style={{ fontWeight: 700, color: 'var(--gold)' }}>${parseFloat(myPrediction.predicted_balance).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* All Predictions List */}
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                <h2 style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                  📋 All Predictions {huntPredictions.length > 0 && `(${huntPredictions.length})`}
                </h2>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['newest', 'highest', 'lowest'].map(order => (
                    <button
                      key={order}
                      onClick={() => { setPredictionSortOrder(order); setPredictionPage(1); }}
                      style={{
                        padding: '0.4rem 0.85rem',
                        background: predictionSortOrder === order ? 'rgba(229, 231, 235, 0.2)' : 'rgba(255,255,255,0.05)',
                        border: predictionSortOrder === order ? '1px solid var(--gold)' : '1px solid var(--border)',
                        borderRadius: '6px',
                        color: predictionSortOrder === order ? 'var(--gold)' : 'var(--text-dim)',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: '0.8rem',
                        textTransform: 'capitalize',
                        fontWeight: predictionSortOrder === order ? 600 : 400
                      }}
                    >
                      {order}
                    </button>
                  ))}
                </div>
              </div>
              
              {huntPredictions.length > 0 ? (
                <>
                  <div style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border)' }}>
                    {[...huntPredictions]
                      .sort((a, b) => {
                        if (predictionSortOrder === 'newest') return new Date(b.created_at) - new Date(a.created_at);
                        if (predictionSortOrder === 'highest') return parseFloat(b.predicted_balance) - parseFloat(a.predicted_balance);
                        if (predictionSortOrder === 'lowest') return parseFloat(a.predicted_balance) - parseFloat(b.predicted_balance);
                        return 0;
                      })
                      .slice((predictionPage - 1) * PREDICTIONS_PER_PAGE, predictionPage * PREDICTIONS_PER_PAGE)
                      .map((pred, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.85rem 1rem',
                          borderBottom: idx < Math.min(huntPredictions.length, PREDICTIONS_PER_PAGE) - 1 ? '1px solid var(--border)' : 'none',
                          background: user && pred.twitch_id === user.twitchId ? 'rgba(229, 231, 235, 0.08)' : 'transparent'
                        }}>
                          <span style={{ width: 28, color: 'var(--text-dim)', fontSize: '0.85rem', fontWeight: 500 }}>
                            #{(predictionPage - 1) * PREDICTIONS_PER_PAGE + idx + 1}
                          </span>
                          <img 
                            src={pred.profile_image_url || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} 
                            alt="" 
                            style={{ width: 36, height: 36, borderRadius: '50%' }} 
                          />
                          <span style={{ fontWeight: 500, flex: 1 }}>{pred.display_name || pred.username}</span>
                          <span style={{ fontWeight: 700, color: 'var(--gold)', fontFamily: "'Space Mono', monospace", fontSize: '1.05rem' }}>
                            ${parseFloat(pred.predicted_balance).toLocaleString()}
                          </span>
                        </div>
                      ))}
                  </div>
                  
                  {/* Pagination */}
                  {huntPredictions.length > PREDICTIONS_PER_PAGE && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
                      <button 
                        onClick={() => setPredictionPage(p => Math.max(1, p - 1))}
                        disabled={predictionPage === 1}
                        style={{ 
                          padding: '0.6rem 1.25rem', 
                          background: 'rgba(255,255,255,0.05)', 
                          border: '1px solid var(--border)', 
                          borderRadius: '8px', 
                          color: 'var(--text)', 
                          cursor: predictionPage === 1 ? 'not-allowed' : 'pointer', 
                          opacity: predictionPage === 1 ? 0.5 : 1,
                          fontFamily: 'inherit'
                        }}
                      >
                        ← Previous
                      </button>
                      <span style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                        Page {predictionPage} of {Math.ceil(huntPredictions.length / PREDICTIONS_PER_PAGE)}
                      </span>
                      <button 
                        onClick={() => setPredictionPage(p => p + 1)}
                        disabled={predictionPage >= Math.ceil(huntPredictions.length / PREDICTIONS_PER_PAGE)}
                        style={{ 
                          padding: '0.6rem 1.25rem', 
                          background: 'rgba(255,255,255,0.05)', 
                          border: '1px solid var(--border)', 
                          borderRadius: '8px', 
                          color: 'var(--text)', 
                          cursor: predictionPage >= Math.ceil(huntPredictions.length / PREDICTIONS_PER_PAGE) ? 'not-allowed' : 'pointer', 
                          opacity: predictionPage >= Math.ceil(huntPredictions.length / PREDICTIONS_PER_PAGE) ? 0.5 : 1,
                          fontFamily: 'inherit'
                        }}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-dim)' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📝</div>
                  <p>No predictions yet for this hunt.</p>
                  <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>Be the first to make a prediction!</p>
                </div>
              )}
            </div>
            </>)}

            {predictionSubTab === 'results' && (<>

            {lastCompletedHunt?.hunt ? (
              <>
                {/* Hunt Summary Card */}
                <div className="card" style={{ 
                  textAlign: 'center', 
                  marginBottom: '1.5rem', 
                  background: 'linear-gradient(135deg, rgba(229, 231, 235, 0.1) 0%, rgba(20, 20, 35, 0.95) 50%, rgba(139, 92, 246, 0.1) 100%)',
                  border: '1px solid rgba(229, 231, 235, 0.2)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, var(--gold), #8b5cf6, var(--gold))' }} />
                  <div style={{ padding: '2rem' }}>
                    <div style={{ display: 'inline-block', padding: '0.4rem 1rem', background: 'rgba(229, 231, 235, 0.15)', borderRadius: '20px', marginBottom: '1rem', fontSize: '0.85rem', color: 'var(--gold)', fontWeight: 600 }}>
                      🎰 LATEST HUNT
                    </div>
                    <h2 style={{ fontSize: '2rem', marginBottom: '1rem', fontWeight: 700 }}>Hunt #{lastCompletedHunt.hunt.huntId}</h2>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', fontSize: '1.5rem', marginBottom: '1rem' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>START</div>
                        <span style={{ fontFamily: "'Space Mono', monospace", fontWeight: 600 }}>${lastCompletedHunt.hunt.startBalance?.toLocaleString()}</span>
                      </div>
                      <div style={{ fontSize: '2rem', color: 'var(--gold)' }}>→</div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>FINAL</div>
                        <span style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, color: lastCompletedHunt.hunt.endBalance > lastCompletedHunt.hunt.startBalance ? '#22c55e' : '#ef4444' }}>
                          ${lastCompletedHunt.hunt.endBalance?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                      <span style={{ fontSize: '1.25rem' }}>👥</span>
                      <span style={{ color: 'var(--text-dim)' }}><strong style={{ color: 'var(--text)' }}>{lastCompletedHunt.predictions?.length || 0}</strong> predictions submitted</span>
                    </div>
                  </div>
                </div>

                {/* Winners Podium */}
                {lastCompletedHunt.winners?.length > 0 && (
                  <div className="card" style={{ 
                    padding: '2.5rem 1.5rem', 
                    marginBottom: '1.5rem',
                    background: 'linear-gradient(180deg, rgba(229, 231, 235, 0.05) 0%, var(--bg-card) 30%)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {/* Decorative elements */}
                    <div style={{ position: 'absolute', top: '-50px', left: '50%', transform: 'translateX(-50%)', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(255, 215, 0, 0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
                    
                    <h2 style={{ textAlign: 'center', fontSize: '1.5rem', marginBottom: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '1.75rem' }}>🎉</span> Winners
                    </h2>
                    
                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '0.75rem', maxWidth: '600px', margin: '0 auto' }}>
                      {/* 2nd Place */}
                      {lastCompletedHunt.winners[1] && (
                        <div style={{ 
                          flex: '1', 
                          maxWidth: '180px',
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          textAlign: 'center',
                          animation: 'fadeInUp 0.5s ease-out 0.1s both'
                        }}>
                          <div style={{ 
                            position: 'relative',
                            marginBottom: '0.75rem'
                          }}>
                            <img 
                              src={lastCompletedHunt.winners[1].profile_image_url || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} 
                              alt="" 
                              style={{ 
                                width: 70, 
                                height: 70, 
                                borderRadius: '50%', 
                                border: '3px solid #c0c0c0',
                                boxShadow: '0 4px 20px rgba(192, 192, 192, 0.3)'
                              }} 
                            />
                          </div>
                          <span style={{ fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.95rem' }}>{lastCompletedHunt.winners[1].display_name}</span>
                          <span style={{ fontSize: '0.9rem', color: 'var(--gold)', fontFamily: "'Space Mono', monospace" }}>
                            ${parseFloat(lastCompletedHunt.winners[1].predicted_balance).toLocaleString()}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.75rem' }}>
                            ${Number(lastCompletedHunt.winners[1].difference).toFixed(2)} off
                          </span>
                          <div style={{ 
                            width: '100%', 
                            height: 100, 
                            background: 'linear-gradient(180deg, #e8e8e8 0%, #c0c0c0 50%, #a8a8a8 100%)', 
                            borderRadius: '12px 12px 0 0', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            color: '#333',
                            boxShadow: 'inset 0 2px 10px rgba(255,255,255,0.3), 0 -4px 20px rgba(192, 192, 192, 0.2)'
                          }}>
                            <span style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>🥈</span>
                            <span style={{ fontSize: '0.85rem', fontWeight: 700, background: 'rgba(0,0,0,0.1)', padding: '0.25rem 0.75rem', borderRadius: '4px' }}>
                              {lastCompletedHunt.winners[1].reward}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* 1st Place */}
                      {lastCompletedHunt.winners[0] && (
                        <div style={{ 
                          flex: '1', 
                          maxWidth: '200px',
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          textAlign: 'center',
                          animation: 'fadeInUp 0.5s ease-out both'
                        }}>
                          <div style={{ fontSize: '2.5rem', marginBottom: '0.25rem', animation: 'bounce 2s ease-in-out infinite' }}>👑</div>
                          <div style={{ 
                            position: 'relative',
                            marginBottom: '0.75rem'
                          }}>
                            <div style={{ 
                              position: 'absolute', 
                              inset: '-6px', 
                              borderRadius: '50%', 
                              background: 'linear-gradient(135deg, #ffd700, #ffb800, #ffd700)', 
                              animation: 'pulse 2s ease-in-out infinite',
                              opacity: 0.5
                            }} />
                            <img 
                              src={lastCompletedHunt.winners[0].profile_image_url || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} 
                              alt="" 
                              style={{ 
                                position: 'relative',
                                width: 90, 
                                height: 90, 
                                borderRadius: '50%', 
                                border: '4px solid #ffd700',
                                boxShadow: '0 8px 30px rgba(255, 215, 0, 0.4)'
                              }} 
                            />
                          </div>
                          <span style={{ fontWeight: 700, marginBottom: '0.25rem', fontSize: '1.1rem' }}>{lastCompletedHunt.winners[0].display_name}</span>
                          <span style={{ fontSize: '1rem', color: 'var(--gold)', fontFamily: "'Space Mono', monospace", fontWeight: 600 }}>
                            ${parseFloat(lastCompletedHunt.winners[0].predicted_balance).toLocaleString()}
                          </span>
                          <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.75rem' }}>
                            ${Number(lastCompletedHunt.winners[0].difference).toFixed(2)} off
                          </span>
                          <div style={{ 
                            width: '100%', 
                            height: 130, 
                            background: 'linear-gradient(180deg, #ffe066 0%, #ffd700 30%, #e6ac00 100%)', 
                            borderRadius: '12px 12px 0 0', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            color: '#333',
                            boxShadow: 'inset 0 2px 15px rgba(255,255,255,0.4), 0 -4px 30px rgba(255, 215, 0, 0.3)'
                          }}>
                            <span style={{ fontSize: '2.5rem', marginBottom: '0.25rem' }}>🥇</span>
                            <span style={{ fontSize: '0.9rem', fontWeight: 700, background: 'rgba(0,0,0,0.15)', padding: '0.3rem 0.85rem', borderRadius: '4px' }}>
                              {lastCompletedHunt.winners[0].reward}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* 3rd Place */}
                      {lastCompletedHunt.winners[2] && (
                        <div style={{ 
                          flex: '1', 
                          maxWidth: '180px',
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          textAlign: 'center',
                          animation: 'fadeInUp 0.5s ease-out 0.2s both'
                        }}>
                          <div style={{ 
                            position: 'relative',
                            marginBottom: '0.75rem'
                          }}>
                            <img 
                              src={lastCompletedHunt.winners[2].profile_image_url || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} 
                              alt="" 
                              style={{ 
                                width: 70, 
                                height: 70, 
                                borderRadius: '50%', 
                                border: '3px solid #cd7f32',
                                boxShadow: '0 4px 20px rgba(205, 127, 50, 0.3)'
                              }} 
                            />
                          </div>
                          <span style={{ fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.95rem' }}>{lastCompletedHunt.winners[2].display_name}</span>
                          <span style={{ fontSize: '0.9rem', color: 'var(--gold)', fontFamily: "'Space Mono', monospace" }}>
                            ${parseFloat(lastCompletedHunt.winners[2].predicted_balance).toLocaleString()}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.75rem' }}>
                            ${Number(lastCompletedHunt.winners[2].difference).toFixed(2)} off
                          </span>
                          <div style={{ 
                            width: '100%', 
                            height: 80, 
                            background: 'linear-gradient(180deg, #daa06d 0%, #cd7f32 50%, #b87333 100%)', 
                            borderRadius: '12px 12px 0 0', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            color: '#333',
                            boxShadow: 'inset 0 2px 10px rgba(255,255,255,0.2), 0 -4px 20px rgba(205, 127, 50, 0.2)'
                          }}>
                            <span style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>🥉</span>
                            <span style={{ fontSize: '0.85rem', fontWeight: 700, background: 'rgba(0,0,0,0.1)', padding: '0.25rem 0.75rem', borderRadius: '4px' }}>
                              {lastCompletedHunt.winners[2].reward}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Full Leaderboard */}
                {lastCompletedHunt.predictions?.length > 3 && (
                  <div className="card">
                    <div className="card-header">
                      <div className="card-icon">📊</div>
                      <div className="card-title">Full Leaderboard</div>
                    </div>
                    <div>
                      {lastCompletedHunt.predictions
                        .sort((a, b) => parseFloat(a.difference) - parseFloat(b.difference))
                        .slice(3, 10)
                        .map((pred, idx) => (
                          <div key={idx} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.75rem', 
                            padding: '1rem', 
                            borderBottom: '1px solid var(--border)',
                            transition: 'background 0.2s ease'
                          }}>
                            <span style={{ 
                              width: 36, 
                              height: 36, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              fontWeight: 600, 
                              color: 'var(--text-dim)',
                              background: 'rgba(255,255,255,0.05)',
                              borderRadius: '8px'
                            }}>#{idx + 4}</span>
                            <img 
                              src={pred.profile_image_url || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} 
                              alt="" 
                              style={{ width: 36, height: 36, borderRadius: '50%' }} 
                            />
                            <span style={{ flex: 1, fontWeight: 500 }}>{pred.display_name}</span>
                            <span style={{ fontWeight: 600, color: 'var(--gold)', fontFamily: "'Space Mono', monospace" }}>
                              ${parseFloat(pred.predicted_balance).toLocaleString()}
                            </span>
                            <span style={{ 
                              fontSize: '0.85rem', 
                              color: 'var(--text-dim)', 
                              minWidth: 90, 
                              textAlign: 'right',
                              background: 'rgba(255,255,255,0.05)',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px'
                            }}>${Number(pred.difference).toFixed(2)} off</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="card" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>🏆</div>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>No completed hunts yet</h3>
                <p style={{ color: 'var(--text-dim)', maxWidth: '400px', margin: '0 auto' }}>
                  Results will appear here after a bonus hunt prediction round is completed!
                </p>
              </div>
            )}

            {/* Previous Hunt Winners History */}
            {allWinners.filter(h => h.hunt_id && h.completed_at && h.winners?.length > 0 && h.hunt_id !== lastCompletedHunt?.hunt?.huntId).length > 0 && (
              <div className="card" style={{ marginTop: '1.5rem' }}>
                <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem' }}>📜 Previous Prediction Winners</h2>
                
                {allWinners
                  .filter(h => h.hunt_id && h.completed_at && h.winners?.length > 0 && h.hunt_id !== lastCompletedHunt?.hunt?.huntId)
                  .slice((resultsHistoryPage - 1) * RESULTS_PER_PAGE, resultsHistoryPage * RESULTS_PER_PAGE)
                  .map((hunt, idx) => (
                  <div key={idx} style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '10px', padding: '1.25rem', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>Hunt #{hunt.hunt_id}</span>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)', background: 'rgba(0,0,0,0.2)', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>
                        {hunt.completed_at ? new Date(hunt.completed_at).toLocaleDateString() : '—'}
                      </span>
                    </div>
                    <div style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginBottom: '1rem', fontFamily: "'Space Mono', monospace" }}>
                      ${hunt.start_balance?.toLocaleString() || '0'} → <span style={{ color: 'var(--gold)' }}>${hunt.end_balance?.toLocaleString() || '0'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      {hunt.winners?.map((w, i) => (
                        <div key={i} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.5rem', 
                          padding: '0.5rem 0.75rem', 
                          background: i === 0 ? 'rgba(255, 215, 0, 0.15)' : i === 1 ? 'rgba(192, 192, 192, 0.15)' : 'rgba(205, 127, 50, 0.15)',
                          border: i === 0 ? '1px solid rgba(255, 215, 0, 0.3)' : i === 1 ? '1px solid rgba(192, 192, 192, 0.3)' : '1px solid rgba(205, 127, 50, 0.3)',
                          borderRadius: '8px', 
                          fontSize: '0.85rem' 
                        }}>
                          <span style={{ fontSize: '1.1rem' }}>{['🥇', '🥈', '🥉'][i]}</span>
                          {w.profile_image_url && <img src={w.profile_image_url} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} />}
                          <span style={{ fontWeight: 500 }}>{w.display_name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                {/* Pagination */}
                {allWinners.filter(h => h.hunt_id && h.completed_at && h.winners?.length > 0 && h.hunt_id !== lastCompletedHunt?.hunt?.huntId).length > RESULTS_PER_PAGE && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
                    <button 
                      onClick={() => setResultsHistoryPage(p => Math.max(1, p - 1))}
                      disabled={resultsHistoryPage === 1}
                      style={{ 
                        padding: '0.6rem 1.25rem', 
                        background: 'rgba(255,255,255,0.05)', 
                        border: '1px solid var(--border)', 
                        borderRadius: '8px', 
                        color: 'var(--text)', 
                        cursor: resultsHistoryPage === 1 ? 'not-allowed' : 'pointer', 
                        opacity: resultsHistoryPage === 1 ? 0.5 : 1,
                        fontFamily: 'inherit'
                      }}
                    >
                      ← Previous
                    </button>
                    <span style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                      Page {resultsHistoryPage} of {Math.ceil(allWinners.filter(h => h.hunt_id && h.completed_at && h.winners?.length > 0 && h.hunt_id !== lastCompletedHunt?.hunt?.huntId).length / RESULTS_PER_PAGE)}
                    </span>
                    <button 
                      onClick={() => setResultsHistoryPage(p => p + 1)}
                      disabled={resultsHistoryPage >= Math.ceil(allWinners.filter(h => h.hunt_id && h.completed_at && h.winners?.length > 0 && h.hunt_id !== lastCompletedHunt?.hunt?.huntId).length / RESULTS_PER_PAGE)}
                      style={{ 
                        padding: '0.6rem 1.25rem', 
                        background: 'rgba(255,255,255,0.05)', 
                        border: '1px solid var(--border)', 
                        borderRadius: '8px', 
                        color: 'var(--text)', 
                        cursor: resultsHistoryPage >= Math.ceil(allWinners.filter(h => h.hunt_id && h.completed_at && h.winners?.length > 0 && h.hunt_id !== lastCompletedHunt?.hunt?.huntId).length / RESULTS_PER_PAGE) ? 'not-allowed' : 'pointer', 
                        opacity: resultsHistoryPage >= Math.ceil(allWinners.filter(h => h.hunt_id && h.completed_at && h.winners?.length > 0 && h.hunt_id !== lastCompletedHunt?.hunt?.huntId).length / RESULTS_PER_PAGE) ? 0.5 : 1,
                        fontFamily: 'inherit'
                      }}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
            )}
            </>)}
          </>
        )}

        {/* SLOT PICKS PAGE */}
        {activePage === 'slotpicks' && (
          <>
            <div className="header">
              <h1 className="header-title">🎰 <span>Slot Picks</span></h1>
              <p className="header-sub">Redeem channel points to pick a slot for Cam to spin!</p>
            </div>

            {/* Stats Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--gold)' }}>{slotPicksStats.pending}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>In Queue</div>
              </div>
              <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#22c55e' }}>{slotPicksStats.allTimePaid}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>Picks Paid</div>
              </div>
              <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary)' }}>${slotPicksStats.allTimePaidAmount?.toLocaleString() || 0}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>Total Won</div>
              </div>
            </div>

            {/* How to Participate */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header">
                <div className="card-icon">📝</div>
                <div className="card-title">How to Participate</div>
              </div>
              <div style={{ padding: '0 1rem 1rem' }}>
                <ol style={{ paddingLeft: '1.25rem', lineHeight: 1.8, color: 'var(--text-dim)' }}>
                  <li>Go to <a href="https://twitch.tv/camneverwins" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)' }}>twitch.tv/camneverwins</a> while live</li>
                  <li>Click on the Channel Points icon below the chat</li>
                  <li>Find and redeem the <strong style={{ color: 'var(--text)' }}>"Pick a Slot"</strong> reward</li>
                  <li>Enter the slot name you want Cam to spin</li>
                  <li>Wait for your turn in the queue below!</li>
                </ol>
                <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(229, 231, 235, 0.1)', borderRadius: '8px', border: '1px solid rgba(229, 231, 235, 0.2)' }}>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-dim)', margin: 0 }}>
                    💡 <strong style={{ color: 'var(--gold)' }}>Tip:</strong> If your pick hits a bonus or a big base pay, you might win a reward! Make sure to have your Rainbet username attached to your profile.
                  </p>
                </div>
              </div>
            </div>

            {/* Current Queue */}
            <div className="card">
              <div className="card-header">
                <div className="card-icon">📋</div>
                <div className="card-title">Current Queue</div>
                <span style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.4rem', 
                  marginLeft: '0.75rem',
                  padding: '0.25rem 0.6rem', 
                  background: 'rgba(34, 197, 94, 0.15)', 
                  color: '#22c55e', 
                  borderRadius: '12px', 
                  fontSize: '0.75rem', 
                  fontWeight: 600 
                }}>
                  <span style={{ 
                    width: 8, 
                    height: 8, 
                    background: '#22c55e', 
                    borderRadius: '50%', 
                    animation: 'pulse 2s infinite' 
                  }}></span>
                  LIVE
                </span>
                {slotPicksQueueTotal > 0 && (
                  <span style={{ marginLeft: 'auto', padding: '0.3rem 0.75rem', background: 'rgba(229, 231, 235, 0.15)', color: 'var(--gold)', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 600 }}>
                    {slotPicksQueueTotal} in queue
                  </span>
                )}
              </div>
              
              {slotPicksQueue.length > 0 ? (
                <div>
                  {slotPicksQueue.map((pick, idx) => (
                    <div 
                      key={pick.id} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '1rem', 
                        padding: '1rem',
                        borderBottom: idx < slotPicksQueue.length - 1 ? '1px solid var(--border)' : 'none',
                        background: idx === 0 ? 'rgba(229, 231, 235, 0.05)' : 'transparent'
                      }}
                    >
                      <div style={{ 
                        width: 36, 
                        height: 36, 
                        borderRadius: '8px', 
                        background: idx === 0 ? 'linear-gradient(135deg, var(--gold), #e6ac00)' : 'rgba(255,255,255,0.1)',
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        color: idx === 0 ? '#000' : 'var(--text-dim)'
                      }}>
                        {idx === 0 ? '▶' : `#${idx + 1}`}
                      </div>
                      <img 
                        src={pick.profileImage} 
                        alt="" 
                        style={{ width: 40, height: 40, borderRadius: '50%', background: '#374151' }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, marginBottom: '0.15rem' }}>{pick.displayName}</div>
                        <div style={{ color: 'var(--gold)', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {pick.slotName}
                        </div>
                      </div>
                      {idx === 0 && (
                        <span style={{ padding: '0.3rem 0.6rem', background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600 }}>
                          UP NEXT
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '0.5rem', opacity: 0.5 }}>🎰</div>
                  <h3 style={{ marginBottom: '0.25rem' }}>Queue is Empty</h3>
                  <p style={{ color: 'var(--text-dim)' }}>Be the first to pick a slot!</p>
                </div>
              )}
              
              {/* Pagination */}
              {slotPicksQueueTotal > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', padding: '1rem', borderTop: '1px solid var(--border)' }}>
                  <button
                    onClick={() => setSlotPicksQueuePage(p => Math.max(1, p - 1))}
                    disabled={slotPicksQueuePage === 1}
                    style={{ padding: '0.5rem 1rem', background: slotPicksQueuePage === 1 ? 'rgba(255,255,255,0.05)' : 'var(--gold)', color: slotPicksQueuePage === 1 ? 'var(--text-dim)' : '#000', border: 'none', borderRadius: '6px', cursor: slotPicksQueuePage === 1 ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                  >
                    ← Prev
                  </button>
                  <span style={{ color: 'var(--text-dim)' }}>
                    Page {slotPicksQueuePage} of {Math.ceil(slotPicksQueueTotal / SLOT_PICKS_PER_PAGE) || 1} ({slotPicksQueueTotal} total)
                  </span>
                  <button
                    onClick={() => setSlotPicksQueuePage(p => Math.min(Math.ceil(slotPicksQueueTotal / SLOT_PICKS_PER_PAGE), p + 1))}
                    disabled={slotPicksQueuePage >= Math.ceil(slotPicksQueueTotal / SLOT_PICKS_PER_PAGE)}
                    style={{ padding: '0.5rem 1rem', background: slotPicksQueuePage >= Math.ceil(slotPicksQueueTotal / SLOT_PICKS_PER_PAGE) ? 'rgba(255,255,255,0.05)' : 'var(--gold)', color: slotPicksQueuePage >= Math.ceil(slotPicksQueueTotal / SLOT_PICKS_PER_PAGE) ? 'var(--text-dim)' : '#000', border: 'none', borderRadius: '6px', cursor: slotPicksQueuePage >= Math.ceil(slotPicksQueueTotal / SLOT_PICKS_PER_PAGE) ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* SOCIALS PAGE — Instagram embeds */}
        {/* WAGER RAFFLE PAGE */}
        {activePage === 'wagerraffle' && (() => {
          const r = activeWagerRaffle;
          const RaffleTimer = ({ endDate, label }) => {
            const [tl, setTl] = React.useState('');
            React.useEffect(() => {
              const calc = () => {
                const end = new Date(endDate);
                if (isNaN(end.getTime())) return 'Expired';
                const diff = end - new Date();
                if (diff <= 0) return 'Ended';
                const d = Math.floor(diff / 86400000);
                const h = Math.floor((diff / 3600000) % 24);
                const m = Math.floor((diff / 60000) % 60);
                const s = Math.floor((diff / 1000) % 60);
                const pad = n => String(n).padStart(2, '0');
                return d > 0 ? `${d}d ${pad(h)}h ${pad(m)}m ${pad(s)}s` : `${pad(h)}h ${pad(m)}m ${pad(s)}s`;
              };
              setTl(calc());
              const id = setInterval(() => setTl(calc()), 1000);
              return () => clearInterval(id);
            }, [endDate]);
            const ended = tl === 'Ended';
            return (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.3rem' }}>{ended ? 'Raffle Ended — Awaiting Draw' : (label || 'Ends In')}</div>
                {!ended && <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 800, fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', color: '#fbbf24', letterSpacing: '0.04em' }}>{tl}</div>}
              </div>
            );
          };
          return (
            <>
              <div className="header" style={{ textAlign: 'center' }}>
                <h1 className="header-title">🎟️ <span>Wager Raffle</span></h1>
                <p className="header-sub">Wager to earn tickets — more tickets means more chances to win!</p>
              </div>

              {r ? (
                <div className="card" style={{ padding: '2rem', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
                  {/* Sparkle overlay */}
                  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
                    {[...Array(8)].map((_, i) => (
                      <div key={i} style={{
                        position: 'absolute',
                        width: 4, height: 4, borderRadius: '50%',
                        background: '#fbbf24',
                        left: `${10 + Math.random() * 80}%`,
                        top: `${5 + Math.random() * 90}%`,
                        animation: `twinkle ${1.5 + Math.random() * 2}s ease-in-out infinite`,
                        animationDelay: `${Math.random() * 2}s`,
                        opacity: 0
                      }} />
                    ))}
                  </div>

                  {/* Prize */}
                  <div style={{ marginBottom: '1rem' }}>
                    {r.prize_image && <img src={r.prize_image} alt="" style={{ width: 80, height: 80, borderRadius: '12px', objectFit: 'cover', marginBottom: '0.5rem', boxShadow: '0 8px 32px rgba(251, 191, 36, 0.3)', border: '2px solid rgba(251, 191, 36, 0.4)' }} />}
                    <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text)' }}>{r.name}</div>
                    {r.prize_description && <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginTop: '0.25rem' }}>{r.prize_description}</div>}
                  </div>

                  {/* Upcoming badge */}
                  {r.is_upcoming && (
                    <div style={{ display: 'inline-block', padding: '0.35rem 1rem', background: 'rgba(139, 92, 246, 0.12)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '999px', color: '#a78bfa', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>
                    Upcoming
                    </div>
                  )}

                  {/* Prize amount */}
                  <div style={{
                    fontSize: 'clamp(2.5rem, 7vw, 4rem)', fontWeight: 900,
                    background: 'linear-gradient(135deg, #fbbf24, #f59e0b, #d97706)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 4px 16px rgba(251, 191, 36, 0.4))',
                    lineHeight: 1, marginBottom: '0.75rem'
                  }}>
                    ${Number(r.prize_amount).toLocaleString()}
                  </div>

                  {/* Countdown */}
                  <div style={{ marginBottom: '1.25rem' }}>
                    <RaffleTimer endDate={r.is_upcoming ? r.start_date : r.end_date} label={r.is_upcoming ? 'Starts In' : 'Ends In'} />
                  </div>

                  {/* Date range */}
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                    <span style={{ padding: '0.3rem 0.75rem', background: 'rgba(34, 197, 94, 0.12)', color: '#22c55e', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700 }}>
                      STARTS {fmtDateCT(r.start_date, { month: 'numeric', day: 'numeric' })}
                    </span>
                    <span style={{ padding: '0.3rem 0.75rem', background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700 }}>
                      ENDS {fmtDateCT(r.end_date, { month: 'numeric', day: 'numeric' })}
                    </span>
                  </div>

                  {/* Info grid — tickets, winners, prizes */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'center', marginBottom: '1.5rem' }}>
                    {/* Stats row — only when raffle started */}
                    {!r.is_upcoming && <div style={{ display: 'inline-flex', gap: '0', background: 'rgba(0,0,0,0.3)', borderRadius: '12px', border: '1px solid rgba(251, 191, 36, 0.15)', overflow: 'hidden' }}>
                      <div style={{ textAlign: 'center', padding: '0.75rem 1.25rem' }}>
                        <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{r.raffle_type === 'threshold' ? 'To Enter' : 'Per Ticket'}</div>
                        <div style={{ fontWeight: 800, color: '#22d3ee', fontSize: '1.05rem' }}>${Number(r.wager_per_ticket).toLocaleString()}</div>
                        <div style={{ fontSize: '0.55rem', color: 'var(--text-muted)' }}>wagered</div>
                      </div>
                      <div style={{ width: 1, background: 'rgba(255,255,255,0.08)' }} />
                      <div style={{ textAlign: 'center', padding: '0.75rem 1.25rem' }}>
                        <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{r.raffle_type === 'threshold' ? 'Entries' : 'Total Tickets'}</div>
                        <div style={{ fontWeight: 800, color: '#fbbf24', fontSize: '1.05rem' }}>{(r.total_tickets || wagerRaffleContenders.reduce((s, c) => s + c.tickets, 0)).toLocaleString()}</div>
                      </div>
                      <div style={{ width: 1, background: 'rgba(255,255,255,0.08)' }} />
                      <div style={{ textAlign: 'center', padding: '0.75rem 1.25rem' }}>
                        <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Winners</div>
                        <div style={{ fontWeight: 800, color: '#22c55e', fontSize: '1.05rem' }}>{r.num_winners || 1}</div>
                      </div>
                    </div>}

                    {/* How winners are picked + data freshness */}
                    {!r.is_upcoming && r.raffle_type !== 'threshold' && (
                      <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                        Winners are drawn at random, weighted by tickets — more tickets, better odds.
                        {raffleUpdatedAt && <> &nbsp;·&nbsp; 🔄 Ticket data updated {fmtUpdatedAgo(raffleUpdatedAt)}</>}
                      </div>
                    )}

                    {/* Threshold raffle explainer */}
                    {!r.is_upcoming && r.raffle_type === 'threshold' && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', textAlign: 'center', maxWidth: 420 }}>
                        Wager <strong style={{ color: '#22d3ee' }}>${Number(r.wager_per_ticket).toLocaleString()}</strong> this period to lock in your entry — everyone who qualifies gets <strong style={{ color: '#fbbf24' }}>one ticket</strong>, equal odds.
                      </div>
                    )}

                    {/* Prize split breakdown (multi-winner only, not upcoming).
                        When every place pays the same, collapse to one pill so the
                        gold #1 badge doesn't imply first place wins more. */}
                    {!r.is_upcoming && (r.num_winners || 1) > 1 && Array.isArray(r.prize_split) && r.prize_split.length > 0 && (() => {
                      const amounts = r.prize_split.map(Number);
                      const allEqual = amounts.every(a => a === amounts[0]);
                      if (allEqual) {
                        return (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', padding: '0.4rem 1rem', background: 'rgba(251, 191, 36, 0.08)', border: '1px solid rgba(251, 191, 36, 0.25)', borderRadius: '999px' }}>
                            <span style={{ fontSize: '0.9rem' }}>🏆</span>
                            <span style={{ fontWeight: 800, fontSize: '0.88rem', color: 'var(--text)' }}>{amounts.length} winners × ${amounts[0].toLocaleString()} each</span>
                          </div>
                        );
                      }
                      return (
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                          {r.prize_split.map((amt, i) => (
                            <div key={i} style={{ padding: '0.3rem 0.65rem', background: i === 0 ? 'rgba(251, 191, 36, 0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${i === 0 ? 'rgba(251, 191, 36, 0.25)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '8px', textAlign: 'center' }}>
                              <div style={{ fontSize: '0.55rem', color: i === 0 ? '#fbbf24' : i === 1 ? '#c0c0c0' : '#cd7f32', fontWeight: 700 }}>#{i + 1}</div>
                              <div style={{ fontWeight: 800, fontSize: '0.85rem', color: 'var(--text)' }}>${Number(amt).toLocaleString()}</div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Upcoming raffle summary */}
                    {r.is_upcoming && (
                      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', fontSize: '0.82rem', color: 'var(--text-dim)' }}>
                        <span>🎟️ {r.raffle_type === 'threshold' ? `$${Number(r.wager_per_ticket).toLocaleString()} to enter` : `$${Number(r.wager_per_ticket).toLocaleString()} per ticket`}</span>
                        <span>🏆 {r.num_winners || 1} winner{(r.num_winners || 1) > 1 ? 's' : ''}</span>
                      </div>
                    )}

                    {/* CTA — hide when ended */}
                    {r.status === 'active' && !r.is_upcoming && new Date(r.end_date) > new Date() && (
                      <a href={affiliateLink} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-block', padding: '0.85rem 2.5rem', background: 'linear-gradient(135deg, #22d3ee, #0ea5e9)', color: '#fff', borderRadius: '12px', fontSize: '1rem', fontWeight: 800, textDecoration: 'none', boxShadow: '0 6px 24px rgba(34, 211, 238, 0.35)' }}>
                        Wager Now on Rainbet →
                      </a>
                    )}
                  </div>

                  {/* Winner display (if drawn) */}
                  {r.status === 'drawn' && (() => {
                    const winnersData = r.winners_data || (r.winner_username ? [{ place: 1, username: r.winner_username, tickets: r.winner_tickets, prize: Number(r.prize_amount) }] : []);
                    return winnersData.length > 0 && (
                      <div style={{ padding: '1.25rem', background: 'rgba(34, 197, 94, 0.08)', border: '2px solid rgba(34, 197, 94, 0.3)', borderRadius: '12px', marginBottom: '1.25rem' }}>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem', textAlign: 'center' }}>
                          🏆 {winnersData.length > 1 ? 'Winners' : 'Winner'}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {winnersData.map((w, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', padding: '0.5rem', background: i === 0 ? 'rgba(251, 191, 36, 0.08)' : 'transparent', borderRadius: '8px' }}>
                              <span style={{ fontWeight: 800, color: i === 0 ? '#fbbf24' : i === 1 ? '#c0c0c0' : '#cd7f32', fontSize: '0.82rem', minWidth: 24 }}>#{w.place}</span>
                              <span style={{ fontWeight: 900, color: '#22c55e', fontSize: i === 0 ? '1.3rem' : '1rem' }}>{displayUser(w.username)}<UsSiteChip site={w.site} /></span>
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{w.tickets} tix</span>
                              {w.prize > 0 && <span style={{ fontWeight: 700, color: '#fbbf24', fontSize: '0.85rem' }}>${Number(w.prize).toLocaleString()}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}


                  {/* Admin controls */}
                  {user?.isAdmin && r.status === 'active' && (
                    <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        <button onClick={() => setEditingWagerRaffle({ ...r, start_date: r.start_date?.slice(0, 16), end_date: r.end_date?.slice(0, 16), num_winners: r.num_winners || 1, prize_split: r.prize_split || [] })}
                          style={{ padding: '0.4rem 0.85rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Edit</button>
                        {new Date(r.end_date) <= new Date() && (
                          <button onClick={() => setExtendRaffleDate(extendRaffleDate?.raffleId === r.id ? null : { raffleId: r.id, newEnd: '' })}
                            style={{ padding: '0.4rem 0.85rem', background: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>⏰ Extend</button>
                        )}
                        <button onClick={async () => {
                          if (!confirm('Draw a winner for this raffle? This cannot be undone.')) return;
                          const token = getAuthToken();
                          const res = await fetch(`${BACKEND_API_URL}/api/admin/wager-raffles/${r.id}/draw`, {
                            method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
                          });
                          const data = await res.json();
                          if (res.ok) {
                            const winnerList = data.winners.map(w => `#${w.place}: ${w.username} (${w.tickets} tix, $${Number(w.prize).toLocaleString()})`).join('\n');
                            alert(`🏆 ${data.winners.length} Winner(s) drawn!\n\n${winnerList}`);
                            fetchActiveWagerRaffle();
                          }
                          else alert(data.error || 'Failed to draw');
                        }} style={{ padding: '0.4rem 0.85rem', background: 'linear-gradient(135deg, #fbbf24, #d97706)', color: '#000', border: 'none', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>🎲 Draw Winner</button>
                      </div>
                      {/* Extend date picker */}
                      {extendRaffleDate?.raffleId === r.id && (
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', padding: '0.6rem 0.85rem', background: 'rgba(139, 92, 246, 0.06)', border: '1px solid rgba(139, 92, 246, 0.2)', borderRadius: '8px' }}>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>New end:</span>
                          <input type="datetime-local" value={extendRaffleDate.newEnd} onChange={e => setExtendRaffleDate({ ...extendRaffleDate, newEnd: e.target.value })}
                            style={{ padding: '0.35rem 0.5rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.78rem' }} />
                          <button onClick={async () => {
                            if (!extendRaffleDate.newEnd) { alert('Pick a new end date'); return; }
                            const token = getAuthToken();
                            const res = await fetch(`${BACKEND_API_URL}/api/admin/wager-raffles/${r.id}`, {
                              method: 'PUT',
                              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ end_date: extendRaffleDate.newEnd })
                            });
                            if (res.ok) { setExtendRaffleDate(null); fetchActiveWagerRaffle(); }
                            else alert('Failed to extend');
                          }} style={{ padding: '0.35rem 0.65rem', background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Apply</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-dim)' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>🎟️</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>No Active Raffle</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.35rem' }}>Check back soon for the next wager raffle!</div>
                </div>
              )}

              {/* Top Contenders */}
              {wagerRaffleContenders.length > 0 && (
                <div className="card" style={{ padding: '1.25rem', marginTop: '1.25rem' }}>
                  <div style={{ fontSize: '1rem', fontWeight: 800, marginBottom: '0.75rem' }}>{r?.raffle_type === 'threshold' ? '🎟️ Qualified Entrants' : '🏅 Top Contenders'}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    {wagerRaffleContenders.map((c, i) => {
                      const isThreshold = r?.raffle_type === 'threshold';
                      const totalTix = wagerRaffleContenders.reduce((s, x) => s + x.tickets, 0);
                      const pct = totalTix > 0 ? ((c.tickets / totalTix) * 100).toFixed(1) : '0';
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.55rem 0.85rem', background: i === 0 ? 'rgba(251, 191, 36, 0.06)' : 'rgba(0,0,0,0.15)', borderRadius: '8px', border: `1px solid ${i === 0 ? 'rgba(251, 191, 36, 0.2)' : 'rgba(255,255,255,0.04)'}` }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 800, color: i < 3 ? '#fbbf24' : 'var(--text-muted)', minWidth: 20 }}>#{i + 1}</span>
                          <span style={{ flex: 1, fontWeight: 600, fontSize: '0.88rem' }}>{displayUser(c.username)}<UsSiteChip site={c.site} /></span>
                          {isThreshold ? (
                            <>
                              <span style={{ fontWeight: 700, color: '#22c55e', fontSize: '0.8rem' }}>✓ Qualified</span>
                              <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', minWidth: 45, textAlign: 'right' }}>🎟️ {pct}%</span>
                            </>
                          ) : (
                            <>
                              <span style={{ fontWeight: 700, color: '#22d3ee', fontFamily: "'Space Mono', monospace", fontSize: '0.85rem' }}>{c.tickets.toLocaleString()}</span>
                              <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', minWidth: 45, textAlign: 'right' }}>🎟️ {pct}%</span>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Upcoming Raffle (shown alongside active) */}
              {upcomingWagerRaffle && (
                <div className="card" style={{ padding: '1.25rem', marginTop: '1.25rem', border: '1px solid rgba(139, 92, 246, 0.2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <span style={{ padding: '0.2rem 0.6rem', background: 'rgba(139, 92, 246, 0.12)', color: '#a78bfa', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Up Next</span>
                    <span style={{ fontWeight: 700, fontSize: '1rem' }}>{upcomingWagerRaffle.name}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Prize</div>
                      <div style={{ fontWeight: 800, color: '#fbbf24', fontSize: '1.3rem' }}>${Number(upcomingWagerRaffle.prize_amount).toLocaleString()}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Per Ticket</div>
                      <div style={{ fontWeight: 700, color: '#22d3ee' }}>${Number(upcomingWagerRaffle.wager_per_ticket).toLocaleString()}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Winners</div>
                      <div style={{ fontWeight: 700, color: '#22c55e' }}>{upcomingWagerRaffle.num_winners || 1}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Starts</div>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{new Date(upcomingWagerRaffle.start_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                    </div>
                  </div>
                  {user?.isAdmin && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <button onClick={() => setEditingWagerRaffle({ ...upcomingWagerRaffle, start_date: upcomingWagerRaffle.start_date?.slice(0, 16), end_date: upcomingWagerRaffle.end_date?.slice(0, 16), num_winners: upcomingWagerRaffle.num_winners || 1, prize_split: upcomingWagerRaffle.prize_split || [] })}
                        style={{ padding: '0.35rem 0.75rem', background: 'rgba(59, 130, 246, 0.12)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.25)', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Edit</button>
                    </div>
                  )}
                </div>
              )}

              {/* Past Raffles — one per page (newest first) so the list can't stretch the page */}
              {wagerRaffleHistory.length > 0 && (() => {
                const total = wagerRaffleHistory.length;
                const page = Math.min(pastRafflePage, total - 1);
                const h = wagerRaffleHistory[page];
                const wd = h.winners_data || (h.winner_username ? [{ place: 1, username: h.winner_username, tickets: h.winner_tickets }] : []);
                return (
                  <div className="card" style={{ padding: '1.25rem', marginTop: '1.25rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 800 }}>📜 {page === 0 ? 'Last Raffle' : 'Past Raffles'}</div>
                      {total > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <button onClick={() => setPastRafflePage(p => Math.max(0, p - 1))} disabled={page === 0} title="Newer"
                            style={{ padding: '0.3rem 0.6rem', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)', borderRadius: '6px', color: page === 0 ? 'var(--text-muted)' : 'var(--text)', cursor: page === 0 ? 'default' : 'pointer', fontFamily: 'inherit', fontSize: '0.8rem', opacity: page === 0 ? 0.4 : 1 }}>‹</button>
                          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', minWidth: 52, textAlign: 'center' }}>{page + 1} of {total}</span>
                          <button onClick={() => setPastRafflePage(p => Math.min(total - 1, p + 1))} disabled={page >= total - 1} title="Older"
                            style={{ padding: '0.3rem 0.6rem', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)', borderRadius: '6px', color: page >= total - 1 ? 'var(--text-muted)' : 'var(--text)', cursor: page >= total - 1 ? 'default' : 'pointer', fontFamily: 'inherit', fontSize: '0.8rem', opacity: page >= total - 1 ? 0.4 : 1 }}>›</button>
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '0.65rem 0.85rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.35rem' }}>
                        <div>
                          <span style={{ fontWeight: 700 }}>{h.name}</span>
                          <span style={{ marginLeft: '0.5rem', color: '#fbbf24', fontWeight: 700 }}>${Number(h.prize_amount).toLocaleString()}</span>
                        </div>
                        {h.drawn_at && (
                          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>drawn {fmtDateCT(h.drawn_at, { month: 'short', day: 'numeric' })}</span>
                        )}
                      </div>
                      {wd.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', marginTop: '0.35rem' }}>
                          {wd.map((w, i) => (
                            <div key={i} style={{ fontSize: '0.78rem', color: '#22c55e' }}>
                              🏆 #{w.place} {displayUser(w.username)}<UsSiteChip site={w.site} /> ({w.tickets} tix{w.prize ? ` — $${Number(w.prize).toLocaleString()}` : ''})
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Admin: every raffle in the DB, not just the one the public page
                  surfaces — scheduled raffles were otherwise unreachable to edit */}
              {user?.isAdmin && adminWagerRaffles.length > 0 && (() => {
                const nowMs = Date.now();
                const stateOf = (rr) => {
                  if (rr.status === 'drawn') return { label: 'Drawn', color: '#94a3b8', order: 3 };
                  if (new Date(rr.start_date).getTime() > nowMs) return { label: 'Upcoming', color: '#a78bfa', order: 0 };
                  if (new Date(rr.end_date).getTime() > nowMs) return { label: 'Live', color: '#22c55e', order: 1 };
                  return { label: 'Ended — needs draw', color: '#fbbf24', order: 2 };
                };
                const sorted = [...adminWagerRaffles]
                  .map(rr => ({ ...rr, _s: stateOf(rr) }))
                  .sort((a, b) => a._s.order - b._s.order || new Date(a.start_date) - new Date(b.start_date));
                const upcomingCount = sorted.filter(rr => rr._s.label === 'Upcoming').length;
                return (
                  <div className="card" style={{ padding: '1.25rem', marginTop: '1.25rem', border: '1px solid rgba(139, 92, 246, 0.25)' }}>
                    <div style={{ fontSize: '1rem', fontWeight: 800, marginBottom: '0.25rem' }}>🛠️ All Raffles <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>(admin only)</span></div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.85rem' }}>
                      {upcomingCount > 0 ? `${upcomingCount} scheduled raffle${upcomingCount > 1 ? 's' : ''} coming up.` : 'No scheduled raffles queued.'} Everything created is listed here — edit or delete any of them.
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      {sorted.map(rr => (
                        <div key={rr.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', padding: '0.6rem 0.8rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                          <span style={{ padding: '0.15rem 0.5rem', borderRadius: '999px', fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: rr._s.color, background: `${rr._s.color}18`, border: `1px solid ${rr._s.color}45`, whiteSpace: 'nowrap' }}>{rr._s.label}</span>
                          <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{rr.name}</span>
                          <span style={{ color: '#fbbf24', fontWeight: 700, fontSize: '0.8rem' }}>${Number(rr.prize_amount).toLocaleString()}</span>
                          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                            {fmtTimeCT(rr.start_date)} → {fmtTimeCT(rr.end_date)}
                          </span>
                          <span style={{ flex: 1, minWidth: 8 }} />
                          <button onClick={() => setEditingWagerRaffle({ ...rr, start_date: rr.start_date?.slice(0, 16), end_date: rr.end_date?.slice(0, 16), num_winners: rr.num_winners || 1, prize_split: rr.prize_split || [] })}
                            style={{ padding: '0.3rem 0.7rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Edit</button>
                          <button onClick={async () => {
                            if (!window.confirm(`Delete "${rr.name}"? This cannot be undone.`)) return;
                            const token = getAuthToken();
                            const res = await fetch(`${BACKEND_API_URL}/api/admin/wager-raffles/${rr.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                            if (res.ok) { fetchAdminWagerRaffles(); fetchActiveWagerRaffle(); fetchWagerRaffleHistory(); }
                            else alert('Failed to delete');
                          }} style={{ padding: '0.3rem 0.7rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Delete</button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Admin: Create New Raffle */}
              {user?.isAdmin && (
                <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                  <button onClick={() => setEditingWagerRaffle({ name: '', prize_description: '', prize_amount: 0, prize_image: '', start_date: '', end_date: '', wager_per_ticket: 1000, num_winners: 1, prize_split: [], raffle_type: 'per_wager' })}
                    style={{ padding: '0.65rem 1.5rem', background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>+ New Raffle</button>
                </div>
              )}

              {/* Admin: Edit/Create Raffle Modal */}
              {editingWagerRaffle && (
                <div className="welcome-modal-overlay" onClick={() => setEditingWagerRaffle(null)}>
                  <div className="welcome-modal" style={{ maxWidth: 500, textAlign: 'left' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h3 style={{ margin: 0 }}>{editingWagerRaffle.id ? 'Edit Raffle' : 'Create Raffle'}</h3>
                      <button onClick={() => setEditingWagerRaffle(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '1.25rem', cursor: 'pointer' }}>✕</button>
                    </div>
                    <div style={{ display: 'grid', gap: '0.85rem' }}>
                      <label>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Raffle name *</div>
                        <input type="text" value={editingWagerRaffle.name || ''} onChange={e => setEditingWagerRaffle({ ...editingWagerRaffle, name: e.target.value })} placeholder="e.g. Weekly Wager Raffle" style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                      </label>
                      {/* Raffle ticket model */}
                      <div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.35rem' }}>Ticket model</div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          {[
                            { v: 'per_wager', t: 'Per Wager', d: 'More wager = more tickets' },
                            { v: 'threshold', t: 'Threshold', d: '1 ticket once requirement met' }
                          ].map(opt => {
                            const active = (editingWagerRaffle.raffle_type || 'per_wager') === opt.v;
                            return (
                              <button key={opt.v} type="button" onClick={() => setEditingWagerRaffle({ ...editingWagerRaffle, raffle_type: opt.v })}
                                style={{ flex: 1, padding: '0.6rem 0.7rem', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                                  background: active ? 'rgba(34,211,238,0.12)' : 'rgba(0,0,0,0.3)',
                                  border: `1px solid ${active ? 'rgba(34,211,238,0.5)' : 'var(--border)'}`,
                                  color: active ? '#22d3ee' : 'var(--text-dim)', fontFamily: 'inherit' }}>
                                <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>{opt.t}</div>
                                <div style={{ fontSize: '0.66rem', color: 'var(--text-muted)' }}>{opt.d}</div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Prize amount ($) *</div>
                          <input type="number" value={editingWagerRaffle.prize_amount || ''} onChange={e => setEditingWagerRaffle({ ...editingWagerRaffle, prize_amount: Number(e.target.value) })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                        </label>
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>{(editingWagerRaffle.raffle_type === 'threshold') ? 'Wager required ($) *' : 'Wager per ticket ($) *'}</div>
                          <input type="number" value={editingWagerRaffle.wager_per_ticket || ''} onChange={e => setEditingWagerRaffle({ ...editingWagerRaffle, wager_per_ticket: Number(e.target.value) })} placeholder="1000" style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                          <div style={{ fontSize: '0.66rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{(editingWagerRaffle.raffle_type === 'threshold') ? 'Wager this much in the period to earn your single ticket' : 'Each chunk of wager earns one ticket'}</div>
                        </label>
                      </div>
                      <label>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Number of winners</div>
                        <input type="number" min="1" max="20" value={editingWagerRaffle.num_winners || 1} onChange={e => {
                          const nw = Math.max(1, Math.min(20, parseInt(e.target.value) || 1));
                          const currentSplit = editingWagerRaffle.prize_split || [];
                          const newSplit = Array.from({ length: nw }, (_, i) => currentSplit[i] || 0);
                          setEditingWagerRaffle({ ...editingWagerRaffle, num_winners: nw, prize_split: newSplit });
                        }} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                      </label>
                      {(editingWagerRaffle.num_winners || 1) > 1 && (
                        <div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.35rem' }}>Prize split per winner ($)</div>
                          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                            {Array.from({ length: editingWagerRaffle.num_winners || 1 }).map((_, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', minWidth: 16 }}>#{i + 1}</span>
                                <input type="number" value={(editingWagerRaffle.prize_split || [])[i] || ''} onChange={e => {
                                  const split = [...(editingWagerRaffle.prize_split || [])];
                                  split[i] = Number(e.target.value) || 0;
                                  setEditingWagerRaffle({ ...editingWagerRaffle, prize_split: split });
                                }} placeholder="0" style={{ width: 80, padding: '0.4rem 0.5rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.82rem' }} />
                              </div>
                            ))}
                          </div>
                          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                            Total: ${(editingWagerRaffle.prize_split || []).reduce((s, v) => s + (Number(v) || 0), 0).toLocaleString()} of ${Number(editingWagerRaffle.prize_amount || 0).toLocaleString()}
                          </div>
                        </div>
                      )}
                      <label>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Prize description</div>
                        <input type="text" value={editingWagerRaffle.prize_description || ''} onChange={e => setEditingWagerRaffle({ ...editingWagerRaffle, prize_description: e.target.value })} placeholder="e.g. Rainbet Balance" style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                      </label>
                      <label>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Prize image URL (optional)</div>
                        <input type="url" value={editingWagerRaffle.prize_image || ''} onChange={e => setEditingWagerRaffle({ ...editingWagerRaffle, prize_image: e.target.value })} placeholder="https://..." style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Start date *</div>
                          <input type="datetime-local" value={editingWagerRaffle.start_date || ''} onChange={e => setEditingWagerRaffle({ ...editingWagerRaffle, start_date: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                        </label>
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>End date *</div>
                          <input type="datetime-local" value={editingWagerRaffle.end_date || ''} onChange={e => setEditingWagerRaffle({ ...editingWagerRaffle, end_date: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                        </label>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <button onClick={() => setEditingWagerRaffle(null)} style={{ padding: '0.55rem 1.25rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                        <button onClick={async () => {
                          const token = getAuthToken();
                          const method = editingWagerRaffle.id ? 'PUT' : 'POST';
                          const url = editingWagerRaffle.id ? `${BACKEND_API_URL}/api/admin/wager-raffles/${editingWagerRaffle.id}` : `${BACKEND_API_URL}/api/admin/wager-raffles`;
                          const res = await fetch(url, {
                            method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify(editingWagerRaffle)
                          });
                          if (res.ok) { setEditingWagerRaffle(null); fetchActiveWagerRaffle(); fetchAdminWagerRaffles(); }
                          else { const d = await res.json().catch(() => ({})); alert(d.error || 'Failed'); }
                        }} style={{ padding: '0.55rem 1.25rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                          {editingWagerRaffle.id ? 'Save' : 'Create'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* PVP BATTLES PAGE */}
        {activePage === 'battles' && (() => {
          const isAdminUser = !!user?.isAdmin;
          const bs = battleState || { status: 'idle' };
          const liveSession = bs.sessionId && bs.status !== 'idle';
          const adminSession = battleAdmin?.session || null;
          const adminMatchup = battleAdmin?.matchup || null;
          const inFinale = bs.sessionStatus === 'finale' || adminSession?.status === 'finale';
          const hasChampion = bs.status === 'champion' || !!adminSession?.champion_name;
          const FILTER_LABELS = { everyone: 'Everyone', subs: 'Subs Only', nonsubs: 'Non-Subs', twitch: 'Twitch Chat', kick: 'Kick Chat', affiliate: 'Affiliates', nonaffiliate: 'Non-Affiliates' };
          const TEAM_COLORS = ['#35c8ff', '#ff4d6b', '#2fd47f', '#a970ff'];
          const TIER_INFO = [null, { name: 'DOMINANT', tickets: 2, emoji: '💥', color: '#fbbf24' }, { name: 'DEMOLITION', tickets: 3, emoji: '🔥', color: '#ff7a3d' }, { name: 'OBLITERATION', tickets: 4, emoji: '☠️', color: '#ff4d6b' }];
          const tierOf = (w, l) => { if (w === null || l === null) return 0; const basis = l > 0 ? w / l : w; return basis >= 10 ? 3 : basis >= 5 ? 2 : basis >= 2 ? 1 : 0; };
          const teams = bs.teams || adminSession?.teams || [];
          const teamById = Object.fromEntries(teams.map(t => [t.id, t]));
          const tcBg = c => `${c}14`;
          const tcBorder = c => `${c}55`;

          const btnStyle = (variant) => ({
            padding: '0.45rem 0.9rem', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700,
            cursor: battleBusy ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: battleBusy ? 0.6 : 1,
            ...(variant === 'gold' ? { background: 'linear-gradient(135deg, #fbbf24, #d97706)', color: '#000', border: 'none' }
              : variant === 'red' ? { background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)' }
              : variant === 'green' ? { background: 'rgba(34, 197, 94, 0.12)', color: '#22c55e', border: '1px solid rgba(34, 197, 94, 0.35)' }
              : { background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)' })
          });
          const inputStyle = {
            padding: '0.45rem 0.6rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)',
            borderRadius: '6px', color: 'var(--text)', fontSize: '0.85rem', fontFamily: 'inherit'
          };

          const PlatBadge = ({ platform }) => (
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '18px', height: '18px', borderRadius: '50%', fontSize: '0.6rem', fontWeight: 800, flexShrink: 0,
              background: platform === 'twitch' ? 'rgba(145, 71, 255, 0.18)' : 'rgba(83, 252, 24, 0.14)',
              color: platform === 'twitch' ? '#b98aff' : '#53fc18'
            }}>{platform === 'twitch' ? 'T' : 'K'}</span>
          );

          // Slot art/name comes matched from the backend (slot-match.js — the
          // same fuzzy matcher the widget and bingo use), as {name, provider, image}
          const SlotThumb = ({ slot, w = 22, h = 30 }) => slot?.image ? (
            <img src={slot.image} alt="" title={slot.name} onError={e => { e.target.style.display = 'none'; }}
              style={{ width: `${w}px`, height: `${h}px`, objectFit: 'cover', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', flexShrink: 0 }} />
          ) : null;
          // ✏️ → inline input with catalog autocomplete; Enter saves, Esc cancels
          const slotEditor = (key, initial, onSave) => slotEdit?.key === key ? (
            <span style={{ display: 'inline-flex', gap: '0.3rem', alignItems: 'center', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
              <input autoFocus list="cnw-slot-names" placeholder="Slot name" value={slotEdit.value}
                onChange={e => setSlotEdit(v => ({ ...v, value: e.target.value }))}
                onKeyDown={async e => {
                  if (e.key === 'Escape') setSlotEdit(null);
                  if (e.key === 'Enter' && await onSave(slotEdit.value)) setSlotEdit(null);
                }}
                style={{ ...inputStyle, width: '190px', fontSize: '0.78rem', padding: '0.3rem 0.5rem' }} />
              <button onClick={async () => { if (await onSave(slotEdit.value)) setSlotEdit(null); }} disabled={battleBusy} style={{ ...btnStyle('gold'), fontSize: '0.7rem', padding: '0.3rem 0.6rem' }}>Save</button>
              <button onClick={() => setSlotEdit(null)} style={{ ...btnStyle(), fontSize: '0.7rem', padding: '0.3rem 0.5rem' }}>✕</button>
            </span>
          ) : (
            <button title="Change slot" onClick={e => { e.stopPropagation(); setSlotEdit({ key, value: initial || '' }); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', padding: '0 0.15rem', opacity: 0.75, flexShrink: 0 }}>✏️</button>
          );

          // One side of the live matchup
          const FighterBox = ({ side }) => {
            const sd = side === 'a' ? bs.sideA : bs.sideB;
            const team = sd?.team || {};
            const color = team.color || '#fbbf24';
            const picked = sd?.picked;
            const isWinner = bs.status === 'roundwinner' && bs.winner?.side === side;
            const isLoser = bs.status === 'roundwinner' && bs.winner && bs.winner.side !== side;
            return (
              <div style={{
                flex: 1, minWidth: 0, padding: '1rem', borderRadius: '12px', textAlign: 'center',
                background: isWinner ? 'rgba(251, 191, 36, 0.08)' : tcBg(color),
                border: `1px solid ${isWinner ? 'rgba(251, 191, 36, 0.5)' : tcBorder(color)}`,
                opacity: isLoser ? 0.45 : 1, transition: 'opacity 0.4s ease, border-color 0.4s ease'
              }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color, marginBottom: '0.5rem' }}>
                  {team.label || (side === 'a' ? 'Team A' : 'Team B')}
                </div>
                {picked ? (
                  <>
                    {isWinner && <div style={{ fontSize: '1.3rem', marginBottom: '0.2rem' }}>👑</div>}
                    {picked.cardImage && (
                      <img
                        src={picked.cardImage}
                        alt=""
                        onError={e => { e.target.style.display = 'none'; }}
                        style={{
                          width: '58px', height: '78px', objectFit: 'cover', borderRadius: '8px',
                          border: `2px solid ${isWinner ? 'rgba(251, 191, 36, 0.7)' : 'rgba(255,255,255,0.15)'}`,
                          margin: '0 auto 0.5rem', display: 'block',
                          boxShadow: isWinner ? '0 0 18px rgba(251, 191, 36, 0.35)' : '0 6px 16px rgba(0,0,0,0.4)'
                        }}
                      />
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', minWidth: 0 }}>
                      {PlatBadge({ platform: picked.platform })}
                      <span style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{picked.name}</span>
                    </div>
                    {(picked.slotName || picked.message) && (
                      <div title={picked.message || ''} style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginTop: '0.3rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {picked.slotName || `“${picked.message}”`}
                      </div>
                    )}
                    {picked.buy != null && picked.payout != null && (
                      <div style={{ display: 'inline-flex', margin: '0.5rem auto 0', border: `1px solid ${isWinner ? 'rgba(251,191,36,0.5)' : 'var(--border)'}`, borderRadius: '8px', overflow: 'hidden', background: 'rgba(0,0,0,0.3)' }}>
                        {[['BUY', `$${picked.buy}`], ['WIN', `$${picked.payout}`], ['MULTI', picked.multi != null ? `${picked.multi}x` : '—']].map(([lab, v], i) => (
                          <div key={lab} style={{ padding: '0.25rem 0.6rem', textAlign: 'center', borderLeft: i ? '1px solid var(--border)' : 'none' }}>
                            <div style={{ fontSize: '0.55rem', fontWeight: 800, letterSpacing: '0.14em', color: 'var(--text-muted)' }}>{lab}</div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 800, color: lab === 'MULTI' ? (isWinner ? '#fbbf24' : color) : '#fff' }}>{v}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {isLoser && (
                      <div style={{ marginTop: '0.4rem', display: 'inline-block', fontWeight: 900, fontSize: '0.8rem', letterSpacing: '0.1em', color: '#ef4444', border: '2px solid #ef4444', borderRadius: '6px', padding: '0.05rem 0.5rem', transform: 'rotate(-8deg)' }}>K.O.</div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-muted)', padding: '0.4rem 0' }}>
                    🎰 Rolling<span className="battle-dots">...</span>
                  </div>
                )}
              </div>
            );
          };

          const startNewForm = () => {
            const last = battleHistory.sessions?.[0];
            setBattleForm({
              id: null,
              title: '',
              keyword: last?.keyword || 'join',
              teams: [
                { label: 'General Chat', filter: 'everyone' },
                { label: 'Subscribers', filter: 'subs' }
              ]
            });
          };
          const startEditForm = () => {
            if (!adminSession) return;
            setBattleForm({
              id: adminSession.id,
              title: adminSession.title || '',
              keyword: adminSession.keyword,
              // Keep team ids so the backend can round-trip them — winners
              // circles and matchups reference them by id
              teams: (adminSession.teams || []).map(t => ({ id: t.id, label: t.label, filter: t.filter }))
            });
          };
          const submitForm = async () => {
            const f = battleForm;
            const ok = f.id
              ? await battleAction(`/api/admin/battles/${f.id}`, f, 'PUT')
              : await battleAction('/api/admin/battles', f);
            if (ok) setBattleForm(null);
          };

          return (
            <>
              <style>{`@keyframes battleDotsPulse{0%,100%{opacity:.25}50%{opacity:1}}.battle-dots{display:inline-block;animation:battleDotsPulse 1.2s ease-in-out infinite}`}</style>
              {slotEdit && <datalist id="cnw-slot-names">{slotNameOptions}</datalist>}

              {/* Header */}
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header" style={{ marginBottom: liveSession ? '0.75rem' : 0 }}>
                  <div className="card-icon">🥊</div>
                  <div style={{ minWidth: 0 }}>
                    <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      PVP Battles
                      {liveSession && (
                        <span style={{ padding: '0.15rem 0.6rem', background: 'rgba(251, 113, 133, 0.15)', color: '#fb7185', border: '1px solid rgba(251, 113, 133, 0.4)', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.08em' }}>LIVE</span>
                      )}
                      {inFinale && !hasChampion && (
                        <span style={{ padding: '0.15rem 0.6rem', background: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.4)', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.08em' }}>FINAL BATTLE</span>
                      )}
                    </h2>
                    <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', margin: '0.25rem 0 0' }}>
                      Teams of chatters go head-to-head in slot battles. Round winners join their team's Winners Circle — finalists from the circles battle for the crown.
                    </p>
                  </div>
                </div>
                {liveSession && !inFinale && !hasChampion && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', padding: '0.75rem 1rem', background: 'rgba(251, 191, 36, 0.06)', border: '1px solid rgba(251, 191, 36, 0.25)', borderRadius: '10px' }}>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>Enter now — type</span>
                    <code style={{ padding: '0.2rem 0.6rem', background: 'rgba(0,0,0,0.35)', borderRadius: '6px', color: '#fbbf24', fontWeight: 700, fontSize: '0.95rem' }}>!{bs.keyword} your slot call</code>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>in Kick or Twitch chat — re-type any time to change your slot</span>
                  </div>
                )}
              </div>

              {/* Admin: session controls */}
              {isAdminUser && (
                <div className="card" style={{ marginBottom: '1.5rem', border: '1px solid rgba(139, 92, 246, 0.25)' }}>
                  <div className="card-header">
                    <div className="card-icon">🛠️</div>
                    <h2 className="card-title">Battle Controls <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600 }}>(admin only)</span></h2>
                  </div>
                  {battleError && (
                    <div style={{ padding: '0.6rem 0.9rem', marginBottom: '0.75rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px', color: '#fca5a5', fontSize: '0.85rem' }}>
                      {battleError}
                    </div>
                  )}

                  {!adminSession && !battleForm && (
                    <button onClick={startNewForm} disabled={battleBusy} style={btnStyle('gold')}>+ New Battle Session</button>
                  )}

                  {battleForm && (
                    <div style={{ display: 'grid', gap: '0.6rem', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                        <input style={{ ...inputStyle, flex: 2, minWidth: '160px' }} placeholder="Title (e.g. Chat vs Subs vs Camily)" value={battleForm.title} onChange={e => setBattleForm(f => ({ ...f, title: e.target.value }))} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          <span style={{ color: '#fbbf24', fontWeight: 700 }}>!</span>
                          <input style={{ ...inputStyle, width: '110px' }} placeholder="keyword" value={battleForm.keyword} onChange={e => setBattleForm(f => ({ ...f, keyword: e.target.value }))} />
                        </div>
                      </div>
                      {battleForm.teams.map((t, i) => (
                        <div key={i} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap', padding: '0.5rem 0.7rem', background: tcBg(TEAM_COLORS[i % 4]), border: `1px solid ${tcBorder(TEAM_COLORS[i % 4])}`, borderRadius: '8px' }}>
                          <span style={{ fontSize: '0.72rem', fontWeight: 800, color: TEAM_COLORS[i % 4], letterSpacing: '0.1em', width: '56px' }}>TEAM {i + 1}</span>
                          <input style={{ ...inputStyle, flex: 1, minWidth: '130px' }} placeholder="Label" value={t.label} onChange={e => setBattleForm(f => ({ ...f, teams: f.teams.map((x, j) => j === i ? { ...x, label: e.target.value } : x) }))} />
                          <select style={{ ...inputStyle, cursor: 'pointer' }} value={t.filter} onChange={e => setBattleForm(f => ({ ...f, teams: f.teams.map((x, j) => j === i ? { ...x, filter: e.target.value } : x) }))}>
                            {Object.entries(FILTER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                          {battleForm.teams.length > 2 && (
                            <button onClick={() => setBattleForm(f => ({ ...f, teams: f.teams.filter((_, j) => j !== i) }))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.9rem' }}>✕</button>
                          )}
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                        {battleForm.teams.length < 4 && (
                          <button onClick={() => setBattleForm(f => ({ ...f, teams: [...f.teams, { label: `Team ${f.teams.length + 1}`, filter: 'everyone' }] }))} style={btnStyle()}>+ Add Team</button>
                        )}
                        <button onClick={submitForm} disabled={battleBusy} style={btnStyle('gold')}>{battleForm.id ? 'Save Changes' : 'Open Entries'}</button>
                        <button onClick={() => setBattleForm(null)} disabled={battleBusy} style={btnStyle()}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {adminSession && !battleForm && (
                    <>
                      {/* Step 1: pick which teams face off, then draw */}
                      {!adminMatchup && !hasChampion && (
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem', padding: '0.65rem 0.85rem', background: 'rgba(251, 191, 36, 0.06)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '10px' }}>
                          <span style={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.1em', color: '#fbbf24', textTransform: 'uppercase' }}>
                            {adminSession.status === 'finale' ? '🏆 Grand final —' : '🎲 Next round —'} roll from
                          </span>
                          <select style={{ ...inputStyle, cursor: 'pointer' }} value={battleDraw.a} onChange={e => setBattleDraw(v => ({ ...v, a: e.target.value }))}>
                            {(adminSession.teams || []).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                          </select>
                          <span style={{ color: 'var(--text-muted)', fontWeight: 800, fontSize: '0.75rem' }}>VS</span>
                          <select style={{ ...inputStyle, cursor: 'pointer' }} value={battleDraw.b} onChange={e => setBattleDraw(v => ({ ...v, b: e.target.value }))}>
                            {(adminSession.teams || []).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                          </select>
                          <button onClick={() => battleAction(`/api/admin/battles/${adminSession.id}/draw`, { a_team: battleDraw.a, b_team: battleDraw.b })} disabled={battleBusy || battleDraw.a === battleDraw.b} style={{ ...btnStyle('gold'), opacity: battleDraw.a === battleDraw.b ? 0.5 : 1 }}>
                            🎲 Draw Fighters
                          </button>
                          {battleDraw.a === battleDraw.b && <span style={{ fontSize: '0.75rem', color: '#fca5a5' }}>pick two different teams</span>}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                        {!adminMatchup && adminSession.status === 'open' && (
                          <button onClick={() => { if (window.confirm('Start the FINAL BATTLE? Chat entries close and pools become the Winners Circles.')) battleAction(`/api/admin/battles/${adminSession.id}/start-finale`); }} disabled={battleBusy} style={btnStyle('green')}>🏁 Start Finale</button>
                        )}
                        {!adminMatchup && adminSession.status === 'finale' && !hasChampion && (
                          <button onClick={() => battleAction(`/api/admin/battles/${adminSession.id}/back-to-rounds`)} disabled={battleBusy} style={btnStyle()}>↩ Back to Rounds</button>
                        )}
                        {!adminMatchup && <button onClick={startEditForm} disabled={battleBusy} style={btnStyle()}>✏️ Edit Teams / Keyword</button>}
                        {!adminMatchup && battleAdmin?.lastCrown && !(inFinale && !battleAdmin.lastCrown.is_final) && (
                          <button onClick={() => {
                            const lc = battleAdmin.lastCrown;
                            const what = lc.is_final ? 'the CHAMPION crown' : `Round ${lc.round_number}`;
                            if (window.confirm(`Undo ${what} (${lc.winner_name} won)?\n\nThe matchup goes back live with its results kept${lc.is_final ? ' and the champion is cleared' : ` and ${lc.winner_name} leaves the winners circle`}.`)) {
                              battleAction(`/api/admin/battles/${adminSession.id}/uncrown`);
                            }
                          }} disabled={battleBusy} style={btnStyle()}>↩ Undo {battleAdmin.lastCrown.is_final ? 'Champion' : `R${battleAdmin.lastCrown.round_number}`} Crown</button>
                        )}
                        {adminMatchup && (
                          <button onClick={() => { if (window.confirm('Cancel this matchup? Both fighters return to the pool.')) battleAction(`/api/admin/battles/${adminSession.id}/cancel-matchup`); }} disabled={battleBusy} style={btnStyle('red')}>✕ Cancel Matchup</button>
                        )}
                        <button onClick={() => { if (window.confirm(hasChampion ? 'Close this session?' : 'Close this session? No champion has been crowned.')) battleAction(`/api/admin/battles/${adminSession.id}/close`); }} disabled={battleBusy} style={btnStyle('red')}>🔒 Close Session</button>
                      </div>

                      {/* Step 2: the fight — enter results, confirm the K.O. */}
                      {adminMatchup && (() => {
                        const mid = adminMatchup.id;
                        const rf = battleResults[mid] || {};
                        const val = (s, f) => rf[s]?.[f] ?? '';
                        const setVal = (s, f, v) => setBattleResults(prev => ({ ...prev, [mid]: { ...prev[mid], [s]: { ...prev[mid]?.[s], [f]: v } } }));
                        const mOf = (b, p) => { const bb = parseFloat(b), pp = parseFloat(p); return Number.isFinite(bb) && bb > 0 && Number.isFinite(pp) ? Math.round((pp / bb) * 100) / 100 : null; };
                        const savedM = { a: mOf(adminMatchup.a_buy, adminMatchup.a_payout), b: mOf(adminMatchup.b_buy, adminMatchup.b_payout) };
                        const bothIn = savedM.a !== null && savedM.b !== null;
                        const sugSide = bothIn ? (savedM.b > savedM.a ? 'b' : 'a') : null;
                        const sugW = bothIn ? savedM[sugSide] : null;
                        const sugL = bothIn ? savedM[sugSide === 'a' ? 'b' : 'a'] : null;
                        const sugTier = bothIn ? tierOf(sugW, sugL) : 0;
                        const fmtM = m => m === null ? '—' : `${m}x`;
                        const teamOf = s => teamById[adminMatchup[`${s}_team`]] || {};
                        const saveResult = async (s) => {
                          const ok = await battleAction(`/api/admin/battles/${adminSession.id}/matchup-result`, { side: s, buy: parseFloat(val(s, 'buy')), payout: parseFloat(val(s, 'payout')) });
                          if (ok) setBattleResults(prev => ({ ...prev, [mid]: { ...prev[mid], [s]: undefined } }));
                        };
                        const crown = (side) => battleAction(`/api/admin/battles/${adminSession.id}/crown`, {
                          ...(side ? { side } : {}),
                          ...(adminMatchup.is_final ? { prize: battlePrize } : {})
                        }).then(ok => { if (ok) setBattlePrize(''); });
                        return (
                          <div style={{ padding: '0.85rem 1rem', marginBottom: '0.75rem', background: 'rgba(251, 191, 36, 0.06)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '10px' }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.1em', color: '#fbbf24', textTransform: 'uppercase', marginBottom: '0.6rem' }}>
                              {adminMatchup.is_final ? '🏆 Grand Final' : `Round ${adminMatchup.round_number}`} — enter each result, then confirm the K.O.
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.6rem', marginBottom: '0.6rem' }}>
                              {['a', 'b'].map(s => {
                                const team = teamOf(s);
                                const color = team.color || '#fbbf24';
                                const saved = savedM[s];
                                return (
                                  <div key={s} style={{ padding: '0.6rem 0.75rem', borderRadius: '8px', background: tcBg(color), border: `1px solid ${tcBorder(color)}` }}>
                                    <div style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.12em', color, textTransform: 'uppercase', marginBottom: '0.25rem' }}>{team.label || '?'}</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: '0.45rem', minWidth: 0 }}>
                                      {SlotThumb({ slot: adminMatchup[`${s}_slot`], w: 36, h: 48 })}
                                      <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: 0 }}>
                                          {PlatBadge({ platform: adminMatchup[`${s}_platform`] })}
                                          <span style={{ fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{adminMatchup[`${s}_name`]}</span>
                                        </div>
                                        {/* Catalog match (what the widget shows) + what chat actually typed */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', minWidth: 0, marginTop: '0.15rem', fontSize: '0.75rem' }}>
                                          {slotEdit?.key === `side-${mid}-${s}` ? null : adminMatchup[`${s}_slot`] ? (
                                            <span style={{ color: '#e5e7eb', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={`Typed: ${adminMatchup[`${s}_message`] || ''}`}>
                                              {adminMatchup[`${s}_slot`].name}
                                              {adminMatchup[`${s}_slot`].name.toLowerCase() !== String(adminMatchup[`${s}_message`] || '').toLowerCase() && (
                                                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · typed “{adminMatchup[`${s}_message`]}”</span>
                                              )}
                                            </span>
                                          ) : (
                                            <span style={{ color: adminMatchup[`${s}_message`] ? '#fca5a5' : 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                              {adminMatchup[`${s}_message`] ? `“${adminMatchup[`${s}_message`]}” — not in catalog` : 'no slot call'}
                                            </span>
                                          )}
                                          {slotEditor(`side-${mid}-${s}`, adminMatchup[`${s}_slot`]?.name || adminMatchup[`${s}_message`],
                                            (v) => battleAction(`/api/admin/battles/${adminSession.id}/matchup-call`, { side: s, message: v }))}
                                        </div>
                                      </div>
                                      <span style={{ fontWeight: 800, fontSize: '0.95rem', color: saved !== null ? color : 'var(--text-muted)', flexShrink: 0 }}>{fmtM(saved)}</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                      <input style={{ ...inputStyle, width: '92px', fontSize: '0.8rem' }} type="number" min="0" step="any" placeholder={adminMatchup[`${s}_buy`] != null ? `Buy $${adminMatchup[`${s}_buy`]}` : 'Buy $'} value={val(s, 'buy')} onChange={e => setVal(s, 'buy', e.target.value)} />
                                      <input style={{ ...inputStyle, width: '102px', fontSize: '0.8rem' }} type="number" min="0" step="any" placeholder={adminMatchup[`${s}_payout`] != null ? `Won $${adminMatchup[`${s}_payout`]}` : 'Payout $'} value={val(s, 'payout')} onChange={e => setVal(s, 'payout', e.target.value)} />
                                      <button onClick={() => saveResult(s)} disabled={battleBusy || !(parseFloat(val(s, 'buy')) > 0) || !(parseFloat(val(s, 'payout')) >= 0)} style={{ ...btnStyle('gold'), fontSize: '0.72rem', padding: '0.38rem 0.7rem', opacity: battleBusy || !(parseFloat(val(s, 'buy')) > 0) ? 0.5 : 1 }}>
                                        {saved !== null ? 'Update' : '💥 Enter'}
                                      </button>
                                      {saved !== null && (
                                        <button onClick={() => { if (window.confirm(`Clear ${adminMatchup[`${s}_name`]}'s result (${fmtM(saved)})?`)) battleAction(`/api/admin/battles/${adminSession.id}/matchup-result`, { side: s, clear: true }); }} disabled={battleBusy} title="Remove the entered buy/payout" style={{ ...btnStyle(), fontSize: '0.72rem', padding: '0.38rem 0.6rem' }}>↺ Clear</button>
                                      )}
                                      <button onClick={() => {
                                        const nm = adminMatchup[`${s}_name`];
                                        const msg = adminMatchup.is_final
                                          ? `Reroll ${nm}? They lose their winners-circle spot (re-typing the keyword restores it).`
                                          : `Reroll ${nm}? They're removed from the pool until they re-type the keyword.`;
                                        if (window.confirm(msg)) battleAction(`/api/admin/battles/${adminSession.id}/reroll`, { side: s });
                                      }} disabled={battleBusy} style={{ ...btnStyle(), fontSize: '0.72rem', padding: '0.38rem 0.7rem' }}>🔄 Reroll</button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {bothIn && savedM.a === savedM.b ? (
                              // Dead heat — the backend refuses an auto-crown, so the admin decides
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(239, 68, 68, 0.45)', borderRadius: '8px' }}>
                                <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#fca5a5' }}>⚖️ DEAD HEAT at {fmtM(savedM.a)}</span>
                                <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>rebuy one side (↺ Clear + re-enter), or pick the winner:</span>
                                {adminMatchup.is_final && (
                                  <input style={{ ...inputStyle, width: '150px', fontSize: '0.78rem' }} placeholder="Prize" value={battlePrize} onChange={e => setBattlePrize(e.target.value)} />
                                )}
                                {['a', 'b'].map(s => (
                                  <button key={s} onClick={() => { if (window.confirm(`Crown ${adminMatchup[`${s}_name`]} on the tie?`)) crown(s); }} disabled={battleBusy} style={{ ...btnStyle(), fontSize: '0.72rem' }}>👑 {adminMatchup[`${s}_name`]}</button>
                                ))}
                              </div>
                            ) : bothIn ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(251, 191, 36, 0.4)', borderRadius: '8px' }}>
                                <span style={{ fontSize: '0.85rem', fontWeight: 700 }}>
                                  <span style={{ color: teamOf(sugSide).color || '#fbbf24' }}>{adminMatchup[`${sugSide}_name`]}</span>
                                  <span style={{ color: 'var(--text-dim)' }}> wins {fmtM(sugW)} vs {fmtM(sugL)}</span>
                                  {sugTier > 0 && (
                                    <span style={{ marginLeft: '0.45rem', padding: '0.1rem 0.55rem', background: TIER_INFO[sugTier].color, color: '#000', borderRadius: '999px', fontSize: '0.68rem', fontWeight: 800 }}>
                                      {TIER_INFO[sugTier].emoji} {TIER_INFO[sugTier].name} — {TIER_INFO[sugTier].tickets}X TICKETS
                                    </span>
                                  )}
                                </span>
                                {adminMatchup.is_final && (
                                  <input style={{ ...inputStyle, width: '170px', fontSize: '0.8rem' }} placeholder="Prize" value={battlePrize} onChange={e => setBattlePrize(e.target.value)} />
                                )}
                                <button onClick={() => crown()} disabled={battleBusy} style={{ ...btnStyle('gold'), fontWeight: 800 }}>⚡ Confirm K.O.</button>
                                <button onClick={() => { if (window.confirm(`Crown ${adminMatchup[`${sugSide === 'a' ? 'b' : 'a'}_name`]} instead (they had the LOWER multi)?`)) crown(sugSide === 'a' ? 'b' : 'a'); }} disabled={battleBusy} style={{ ...btnStyle(), fontSize: '0.7rem' }}>override</button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                <span>Waiting for both results… or crown without numbers:</span>
                                {adminMatchup.is_final && (
                                  <input style={{ ...inputStyle, width: '150px', fontSize: '0.78rem' }} placeholder="Prize" value={battlePrize} onChange={e => setBattlePrize(e.target.value)} />
                                )}
                                {['a', 'b'].map(s => (
                                  <button key={s} onClick={() => { if (window.confirm(`Crown ${adminMatchup[`${s}_name`]} without entering results? (No tier bonus — 1 ticket.)`)) crown(s); }} disabled={battleBusy} style={{ ...btnStyle(), fontSize: '0.7rem' }}>👑 {adminMatchup[`${s}_name`]}</button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {battleAdmin?.poolDepth && (
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', fontSize: '0.8rem', color: 'var(--text-dim)' }}>
                          {(adminSession.teams || []).map(t => (
                            <span key={t.id}>
                              <span style={{ color: t.color, fontWeight: 700 }}>{t.label}</span>
                              {': '}{battleAdmin.poolDepth[t.id]?.fresh ?? 0} fresh / {battleAdmin.poolDepth[t.id]?.total ?? 0} total
                            </span>
                          ))}
                          <button onClick={() => setBattleShowEntrants(v => !v)} style={{ ...btnStyle(), fontSize: '0.7rem', padding: '0.25rem 0.6rem' }}>
                            {battleShowEntrants ? '✕ Hide entrants' : `👥 Entrants (${battleAdmin.entrants?.length || 0})`}
                          </button>
                        </div>
                      )}

                      {/* Live entrant list — one row per HUMAN (cross-platform
                          entries merged) with the detected flags, so sub /
                          affiliate detection can be verified during a session */}
                      {battleShowEntrants && battleAdmin?.entrants && (() => {
                        const q = battleEntrantSearch.trim().toLowerCase();
                        const list = q
                          ? battleAdmin.entrants.filter(en => `${en.name} ${en.username} ${en.message || ''} ${en.slot?.name || ''}`.toLowerCase().includes(q))
                          : battleAdmin.entrants;
                        return (
                        <>
                        <input style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginTop: '0.6rem', fontSize: '0.8rem' }}
                          placeholder={`Search ${battleAdmin.entrants.length} entrants by name or slot…`}
                          value={battleEntrantSearch} onChange={e => setBattleEntrantSearch(e.target.value)} />
                        <div style={{ marginTop: '0.4rem', maxHeight: '320px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '8px', background: 'rgba(0,0,0,0.25)' }}>
                          {list.length === 0 && (
                            <div style={{ padding: '0.7rem', fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>{q ? 'No matches.' : 'No entries yet.'}</div>
                          )}
                          {list.map((en, i) => (
                            <div key={`${en.platform}-${en.username}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', padding: '0.35rem 0.6rem', borderBottom: i < list.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none', fontSize: '0.8rem', opacity: en.drawn ? 0.55 : 1 }}>
                              {SlotThumb({ slot: en.slot, w: 18, h: 24 })}
                              {PlatBadge({ platform: en.platform })}
                              <span style={{ fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px' }}>{en.name}</span>
                              {(en.platforms || []).length > 1 && (
                                <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: '4px', padding: '0 0.25rem' }}>BOTH CHATS</span>
                              )}
                              {en.is_sub && <span style={{ fontSize: '0.6rem', fontWeight: 800, color: '#fb7185', background: 'rgba(251,113,133,0.14)', border: '1px solid rgba(251,113,133,0.4)', borderRadius: '4px', padding: '0 0.3rem' }}>SUB</span>}
                              {en.is_affiliate && <span style={{ fontSize: '0.6rem', fontWeight: 800, color: '#2fd47f', background: 'rgba(47,212,127,0.12)', border: '1px solid rgba(47,212,127,0.4)', borderRadius: '4px', padding: '0 0.3rem' }}>AFF</span>}
                              {en.drawn && <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>fought</span>}
                              {slotEdit?.key !== `ent-${en.username}` && (en.slot || en.message) && (
                                <span title={en.message ? `Typed: ${en.message}` : ''} style={{ fontSize: '0.72rem', color: en.slot ? 'var(--text-dim)' : '#fca5a5', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                                  {en.slot ? en.slot.name : `“${en.message}”`}
                                </span>
                              )}
                              {en.ids?.length > 0 && slotEditor(`ent-${en.username}`, en.slot?.name || en.message,
                                (v) => battleAction(`/api/admin/battles/${adminSession.id}/entrants`, { ids: en.ids, message: v }))}
                              <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.2rem', flexShrink: 0 }}>
                                {(en.teams || []).map(tid => {
                                  const tm = (adminSession.teams || []).find(x => x.id === tid);
                                  if (!tm) return null;
                                  return <span key={tid} title={tm.label} style={{ width: '8px', height: '8px', borderRadius: '2px', background: tm.color, display: 'inline-block' }} />;
                                })}
                                {en.ids?.length > 0 && (
                                  <button title="Remove from the pool (re-typing the keyword re-enters them)"
                                    onClick={() => { if (window.confirm(`Remove ${en.name} from the pool?`)) battleAction(`/api/admin/battles/${adminSession.id}/entrants`, { ids: en.ids, remove: true }); }}
                                    disabled={battleBusy} style={{ marginLeft: '0.3rem', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem' }}>✕</button>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                        </>
                        );
                      })()}
                    </>
                  )}

                  {/* OBS widget link — available with or without a session, but
                      hidden behind a button so it never shows while streaming */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.4rem', marginTop: '0.75rem', paddingTop: '0.6rem', borderTop: '1px solid var(--border)' }}>
                    {battleShowWidget && (
                      <>
                        <code style={{ padding: '0.25rem 0.55rem', background: 'rgba(0,0,0,0.35)', borderRadius: '6px', fontSize: '0.72rem', color: '#93c5fd', userSelect: 'all' }}>{`${BACKEND_API_URL}/widget/battle`}</code>
                        <button onClick={() => { try { navigator.clipboard.writeText(`${BACKEND_API_URL}/widget/battle`); } catch (e) {} }} style={{ ...btnStyle(), fontSize: '0.7rem', padding: '0.3rem 0.6rem' }}>📋 Copy</button>
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }} title="Announcer + hit sounds play from the OBS source. Tick 'Control audio via OBS' to give it a mixer channel. Add ?sound=off to mute or ?volume=0.5 to turn it down.">1000×520 · 🔊 sound on (?sound=off · ?volume=0.5)</span>
                      </>
                    )}
                    <button onClick={() => setBattleShowWidget(v => !v)} style={{ ...btnStyle(), fontSize: '0.7rem', padding: '0.3rem 0.6rem' }}>
                      {battleShowWidget ? '✕ Hide' : '🖥 Get Widget URL'}
                    </button>
                  </div>
                </div>
              )}

              {/* Champion banner */}
              {hasChampion && (bs.champion || adminSession) && (
                <div className="card" style={{ marginBottom: '1.5rem', textAlign: 'center', border: '1px solid rgba(251, 191, 36, 0.5)', background: 'linear-gradient(160deg, rgba(45, 35, 10, 0.55), rgba(20, 20, 28, 0.9))' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.35em', color: 'rgba(251, 191, 36, 0.8)', textTransform: 'uppercase' }}>Winner Takes All</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.9rem', margin: '0.3rem 0' }}>
                    {bs.champion?.cardImage && (
                      <img src={bs.champion.cardImage} alt="" onError={e => { e.target.style.display = 'none'; }}
                        style={{ width: '62px', height: '83px', objectFit: 'cover', borderRadius: '8px', border: '2px solid rgba(251, 191, 36, 0.7)', boxShadow: '0 0 20px rgba(251, 191, 36, 0.3)' }} />
                    )}
                    <span style={{ fontSize: '2.2rem' }}>🏆</span>
                  </div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fbbf24' }}>{bs.champion?.name || adminSession?.champion_name}</div>
                  {bs.champion?.teamLabel && <div style={{ fontSize: '0.85rem', color: bs.champion?.color || 'var(--text-dim)', marginTop: '0.2rem', fontWeight: 700 }}>Team {bs.champion.teamLabel}</div>}
                  {(bs.champion?.prize || adminSession?.champion_prize) && (
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: '#22c55e', marginTop: '0.4rem' }}>{bs.champion?.prize || adminSession?.champion_prize}</div>
                  )}
                </div>
              )}

              {/* Teams (live rosters, visible to everyone) */}
              {liveSession && teams.length > 0 && (
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <div className="card-header">
                    <div className="card-icon">⚔️</div>
                    <h2 className="card-title">{bs.title || (inFinale ? 'Final Battle' : 'Current Battle')}</h2>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${teams.length > 2 ? '200px' : '260px'}, 1fr))`, gap: '0.75rem', marginBottom: (bs.status === 'battle' || bs.status === 'roundwinner') ? '1rem' : 0 }}>
                    {teams.map(t => (
                      <div key={t.id} style={{ minWidth: 0, padding: '0.8rem', borderRadius: '10px', background: tcBg(t.color), border: `1px solid ${tcBorder(t.color)}` }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', minWidth: 0 }}>
                          <span style={{ fontSize: '0.95rem', fontWeight: 800, color: t.color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{t.label}</span>
                          <span style={{ fontSize: '0.62rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0 }}>{FILTER_LABELS[t.filter] || t.filter}</span>
                          <span style={{ marginLeft: 'auto', fontSize: '1.15rem', fontWeight: 800, color: '#fff', flexShrink: 0 }}>{t.count}<span style={{ fontSize: '0.6rem', color: 'var(--text-muted)', marginLeft: '0.25rem', letterSpacing: '0.1em' }}>{inFinale ? 'FINALISTS' : 'ENTRIES'}</span></span>
                        </div>
                        {t.roster?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.55rem', maxHeight: '84px', overflowY: 'auto' }}>
                            {t.roster.slice().reverse().map((r, i) => (
                              <span key={`${r.n}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.15rem 0.5rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '5px', fontSize: '0.72rem', fontWeight: 600, color: '#fff', maxWidth: '100%' }}>
                                <span style={{ width: '6px', height: '6px', borderRadius: '2px', flexShrink: 0, background: r.p === 'twitch' ? '#a970ff' : '#53fc18' }} />
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.n}{r.t > 1 ? ` ×${r.t}` : ''}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {(bs.status === 'battle' || bs.status === 'roundwinner') && bs.sideA && bs.sideB && (
                    <div style={{ display: 'flex', alignItems: 'stretch', gap: '0.75rem' }}>
                      {FighterBox({ side: 'a' })}
                      <div style={{ display: 'flex', alignItems: 'center', fontWeight: 800, color: '#fbbf24', fontSize: '1.1rem', flexShrink: 0 }}>VS</div>
                      {FighterBox({ side: 'b' })}
                    </div>
                  )}

                  {bs.status === 'roundwinner' && bs.winner && (
                    <div style={{ marginTop: '0.75rem', textAlign: 'center', padding: '0.6rem', background: 'rgba(251, 191, 36, 0.07)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 700 }}>
                      <span style={{ color: '#fbbf24' }}>{bs.winner.name}</span> wins the battle
                      {bs.winner.tier > 0 && TIER_INFO[bs.winner.tier] && (
                        <span style={{ marginLeft: '0.5rem', padding: '0.1rem 0.55rem', background: TIER_INFO[bs.winner.tier].color, color: '#000', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 800 }}>
                          {TIER_INFO[bs.winner.tier].emoji} {TIER_INFO[bs.winner.tier].name} — {TIER_INFO[bs.winner.tier].tickets}X TICKETS
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Winners circles (per team) */}
              {liveSession && bs.winnersCircle && teams.length > 0 && (
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <div className="card-header">
                    <div className="card-icon">🌟</div>
                    <h2 className="card-title">Winners Circle</h2>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${teams.length > 2 ? '190px' : '240px'}, 1fr))`, gap: '0.75rem' }}>
                    {teams.map(t => {
                      const list = bs.winnersCircle[t.id] || [];
                      return (
                        <div key={t.id} style={{ minWidth: 0, padding: '0.8rem', borderRadius: '10px', background: tcBg(t.color), border: `1px solid ${tcBorder(t.color)}` }}>
                          <div style={{ fontSize: '0.78rem', fontWeight: 800, color: t.color, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>{t.label}</div>
                          {list.length === 0 && <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No winners yet</div>}
                          {list.map(w => (
                            <div key={w.id} style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', padding: '0.3rem 0', fontSize: '0.88rem' }}>
                              {SlotThumb({ slot: w.slot })}
                              {PlatBadge({ platform: w.platform })}
                              <span title={w.slot?.name || w.message || ''} style={{ fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{w.display_name || w.username}</span>
                              {isAdminUser ? (
                                // Admin: adjust finale tickets inline
                                <select value={Number(w.tickets) || 1} disabled={battleBusy} title="Finale tickets"
                                  onChange={e => battleAction(`/api/admin/battles/${bs.sessionId}/winners/${w.id}`, { tickets: Number(e.target.value) }, 'PATCH')}
                                  style={{ padding: '0 0.2rem', background: 'rgba(251, 191, 36, 0.12)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 800, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit' }}>
                                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>×{n}</option>)}
                                </select>
                              ) : Number(w.tickets) > 1 && <span style={{ padding: '0.05rem 0.4rem', background: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24', borderRadius: '999px', fontSize: '0.62rem', fontWeight: 800, flexShrink: 0 }}>×{w.tickets}</span>}
                              {isAdminUser && slotEditor(`win-${w.id}`, w.slot?.name || w.message,
                                (v) => battleAction(`/api/admin/battles/${bs.sessionId}/winners/${w.id}`, { message: v }, 'PATCH'))}
                              {w.manual && <span style={{ padding: '0.05rem 0.4rem', background: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa', borderRadius: '999px', fontSize: '0.62rem', fontWeight: 800, flexShrink: 0 }}>MANUAL</span>}
                              {isAdminUser && (
                                <button onClick={() => { if (window.confirm(`Remove ${w.display_name || w.username} from the circle?`)) battleAction(`/api/admin/battles/${bs.sessionId}/winners/${w.id}`, null, 'DELETE'); }} disabled={battleBusy}
                                  style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.85rem' }}>✕</button>
                              )}
                            </div>
                          ))}
                          {isAdminUser && (
                            battleAddWinner?.team === t.id ? (
                              <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                                <input style={{ ...inputStyle, flex: 1, minWidth: '100px', fontSize: '0.78rem' }} placeholder="username" value={battleAddWinner.username} onChange={e => setBattleAddWinner(v => ({ ...v, username: e.target.value }))} />
                                <select style={{ ...inputStyle, fontSize: '0.78rem', cursor: 'pointer' }} value={battleAddWinner.platform} onChange={e => setBattleAddWinner(v => ({ ...v, platform: e.target.value }))}>
                                  <option value="kick">Kick</option>
                                  <option value="twitch">Twitch</option>
                                </select>
                                <select style={{ ...inputStyle, fontSize: '0.78rem', cursor: 'pointer' }} value={battleAddWinner.tickets} onChange={e => setBattleAddWinner(v => ({ ...v, tickets: e.target.value }))}>
                                  {[1, 2, 3, 4].map(n => <option key={n} value={n}>×{n}</option>)}
                                </select>
                                <button onClick={async () => { const ok = await battleAction(`/api/admin/battles/${bs.sessionId}/winners`, { team: t.id, ...battleAddWinner, tickets: Number(battleAddWinner.tickets) }); if (ok) setBattleAddWinner(null); }} disabled={battleBusy || !battleAddWinner.username.trim()} style={{ ...btnStyle('gold'), fontSize: '0.72rem', padding: '0.35rem 0.7rem' }}>Add</button>
                                <button onClick={() => setBattleAddWinner(null)} style={{ ...btnStyle(), fontSize: '0.72rem', padding: '0.35rem 0.7rem' }}>✕</button>
                              </div>
                            ) : (
                              <button onClick={() => setBattleAddWinner({ team: t.id, platform: 'kick', username: '', tickets: '1' })} style={{ ...btnStyle(), fontSize: '0.72rem', padding: '0.3rem 0.6rem', marginTop: '0.5rem' }}>+ Add manually</button>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Rounds history */}
                  {bs.rounds?.length > 0 && (
                    <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border)' }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>Rounds</div>
                      {bs.rounds.map(r => {
                        const winName = r.winner_side === 'b' ? r.b_name : r.a_name;
                        const loseName = r.winner_side === 'b' ? r.a_name : r.b_name;
                        const winTeam = teamById[r.winner_side === 'b' ? r.b_team : r.a_team];
                        const tier = Number(r.tier) || 0;
                        return (
                          <div key={r.id} style={{ fontSize: '0.82rem', color: 'var(--text-dim)', padding: '0.2rem 0' }}>
                            <span style={{ color: 'var(--text-muted)' }}>{r.is_final ? '🏆 Final' : `R${r.round_number}`}:</span>{' '}
                            <span style={{ color: winTeam?.color || '#fff', fontWeight: 600 }}>{winName}</span>
                            {winTeam && <span style={{ color: 'var(--text-muted)' }}> ({winTeam.label})</span>} def. {loseName}
                            {tier > 0 && TIER_INFO[tier] && <span style={{ color: TIER_INFO[tier].color, fontWeight: 700 }}> {TIER_INFO[tier].emoji} {TIER_INFO[tier].name}</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* No live battle */}
              {!liveSession && !isAdminUser && (
                <div className="card" style={{ marginBottom: '1.5rem', textAlign: 'center', padding: '2rem 1.5rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🥊</div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)' }}>No battle running right now</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginTop: '0.3rem' }}>Battles run live on stream — watch for the next one!</div>
                </div>
              )}

              {/* Past champions */}
              <div className="card">
                <div className="card-header">
                  <div className="card-icon">👑</div>
                  <h2 className="card-title">Past Champions</h2>
                </div>
                {(!battleHistory.sessions || battleHistory.sessions.length === 0) && (
                  <div style={{ fontSize: '0.88rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No completed battles yet.</div>
                )}
                {battleHistory.sessions?.map(s => {
                  const isOpen = battleHistoryOpen === s.id;
                  const detail = battleHistoryDetail[s.id];
                  const toggleHistory = async () => {
                    if (isOpen) { setBattleHistoryOpen(null); return; }
                    setBattleHistoryOpen(s.id);
                    if (!battleHistoryDetail[s.id]) {
                      try {
                        const res = await fetch(`${BACKEND_API_URL}/api/battles/history/${s.id}`);
                        if (res.ok) { const d = await res.json(); setBattleHistoryDetail(prev => ({ ...prev, [s.id]: d })); }
                      } catch (e) { /* ignore */ }
                    }
                  };
                  const dTeamById = detail ? Object.fromEntries(detail.teams.map(t => [t.id, t])) : {};
                  return (
                    <div key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <div onClick={toggleHistory} style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', padding: '0.65rem 0.4rem', flexWrap: 'wrap', cursor: 'pointer', borderRadius: '8px', background: isOpen ? 'rgba(255,255,255,0.03)' : 'transparent' }}>
                        <span style={{ fontSize: '1.1rem' }}>🏆</span>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 700, color: '#fbbf24', fontSize: '0.95rem' }}>
                            {s.champion_name || <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>No champion crowned</span>}
                            {s.champion_team_label && <span style={{ color: 'var(--text-dim)', fontWeight: 500, fontSize: '0.8rem' }}> · {s.champion_team_label}</span>}
                          </div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                            {s.title || s.matchup_label} · {s.rounds} rounds · {s.entries} entries
                            {s.closed_at && ` · ${new Date(s.closed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                          </div>
                        </div>
                        {s.champion_prize && <span style={{ color: '#22c55e', fontWeight: 700, fontSize: '0.85rem' }}>{s.champion_prize}</span>}
                        {isAdminUser && (
                          <button onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this battle from history?')) battleAction(`/api/admin/battles/${s.id}`, null, 'DELETE').then(() => fetchBattleHistory(battleHistoryPage)); }} disabled={battleBusy}
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.85rem' }}>✕</button>
                        )}
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}>▼</span>
                      </div>
                      {isOpen && (
                        <div style={{ padding: '0.4rem 0.4rem 0.9rem 2.3rem' }}>
                          {!detail ? (
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Loading…</div>
                          ) : (
                            <>
                              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
                                {detail.teams.map(t => (
                                  <span key={t.id} style={{ padding: '0.15rem 0.6rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 800, color: t.color, background: `${t.color}14`, border: `1px solid ${t.color}55` }}>
                                    {t.label} <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>({FILTER_LABELS[t.filter] || t.filter})</span>
                                  </span>
                                ))}
                                <span style={{ padding: '0.15rem 0.6rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-dim)', border: '1px solid var(--border)' }}>!{detail.session.keyword}</span>
                              </div>
                              {detail.rounds.length === 0 && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No rounds were resolved.</div>}
                              {detail.rounds.map(r => {
                                const winA = r.winner_side !== 'b';
                                const wTeam = dTeamById[winA ? r.a_team : r.b_team];
                                const lTeam = dTeamById[winA ? r.b_team : r.a_team];
                                const wName = winA ? r.a_name : r.b_name;
                                const lName = winA ? r.b_name : r.a_name;
                                const wMulti = winA ? r.a_multi : r.b_multi;
                                const lMulti = winA ? r.b_multi : r.a_multi;
                                const tier = Number(r.tier) || 0;
                                return (
                                  <div key={r.id} style={{ fontSize: '0.82rem', color: 'var(--text-dim)', padding: '0.18rem 0' }}>
                                    <span style={{ color: 'var(--text-muted)' }}>{r.is_final ? '🏆 Final' : `R${r.round_number}`}:</span>{' '}
                                    <span style={{ color: wTeam?.color || '#fff', fontWeight: 700 }}>{wName}</span>
                                    {wMulti != null && <span style={{ color: '#22c55e', fontWeight: 700 }}> {wMulti}x</span>}
                                    <span> def. </span>
                                    <span style={{ color: lTeam?.color || 'var(--text-dim)' }}>{lName}</span>
                                    {lMulti != null && <span style={{ color: 'var(--text-muted)' }}> {lMulti}x</span>}
                                    {tier > 0 && TIER_INFO[tier] && <span style={{ color: TIER_INFO[tier].color, fontWeight: 700 }}> {TIER_INFO[tier].emoji} {TIER_INFO[tier].name}</span>}
                                  </div>
                                );
                              })}
                              {detail.winners.length > 0 && (
                                <div style={{ marginTop: '0.55rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                  <span style={{ fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.66rem' }}>Winners Circle: </span>
                                  {detail.winners.map((w, i) => (
                                    <span key={i} style={{ color: dTeamById[w.team]?.color || 'var(--text-dim)', fontWeight: 600 }}>
                                      {w.display_name || w.username}{Number(w.tickets) > 1 ? ` ×${w.tickets}` : ''}{i < detail.winners.length - 1 ? ', ' : ''}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {battleHistory.total > 6 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.9rem', marginTop: '0.9rem' }}>
                    <button onClick={() => setBattleHistoryPage(p => Math.max(1, p - 1))} disabled={battleHistoryPage <= 1}
                      style={{ ...btnStyle(), opacity: battleHistoryPage <= 1 ? 0.4 : 1 }}>‹</button>
                    <span style={{ fontSize: '0.82rem', color: 'var(--text-dim)' }}>Page {battleHistoryPage} of {Math.max(1, Math.ceil(battleHistory.total / 6))}</span>
                    <button onClick={() => setBattleHistoryPage(p => Math.min(Math.ceil(battleHistory.total / 6), p + 1))} disabled={battleHistoryPage >= Math.ceil(battleHistory.total / 6)}
                      style={{ ...btnStyle(), opacity: battleHistoryPage >= Math.ceil(battleHistory.total / 6) ? 0.4 : 1 }}>›</button>
                  </div>
                )}
              </div>
            </>
          );
        })()}

        {/* PRIZE PORTAL PAGE */}
        {activePage === 'prizes' && (() => {
          return (
            <>
              <div className="header" style={{ textAlign: 'center' }}>
                <h1 className="header-title">🏆 <span>Claim Prizes</span></h1>
                <p className="header-sub">Won a stream contest? Claim your prize here!</p>
              </div>

              {/* Not on code CAM — $5 prize */}
              <div className="card" style={{ padding: '1.25rem 1.5rem', marginBottom: '1.25rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Not on code CAM?</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#fbbf24', letterSpacing: '-0.02em' }}>$5 Prize</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Base prize for all stream contest winners</div>
              </div>

              {/* What signing up with code CAM gets you */}
              <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <a href={affiliateLink} target="_blank" rel="noopener noreferrer" style={{ padding: '0.65rem 1.5rem', background: 'linear-gradient(135deg, #22d3ee, #0ea5e9)', color: '#fff', borderRadius: '10px', fontSize: '0.95rem', fontWeight: 800, textDecoration: 'none', display: 'inline-block', boxShadow: '0 4px 16px rgba(34, 211, 238, 0.3)' }}>
                    Sign up with code CAM →
                  </a>
                </div>
                <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#22d3ee', marginBottom: '0.75rem', textAlign: 'center' }}>What you get on code CAM</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {[
                    { icon: '🏆', text: 'Weekly Leaderboard' },
                    { icon: '💰', text: `$${monthlyMilestoneTotal.toLocaleString()} Milestone bonuses that REFRESH monthly`, link: '/rewards', linkText: 'check here' },
                    { icon: '💸', text: 'Up to 10% Lossback for EVERYONE — high rollers guaranteed 10%' },
                    { icon: '📅', text: 'Weekly & monthly matches' },
                    { icon: '🎰', text: 'Access to Degen Days wager milestones' },
                    { icon: '🎯', text: 'Bigger contest prizes — rank up to earn more prize money for stream contests' },
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: '0.65rem', alignItems: 'flex-start', padding: '0.55rem 0.75rem', background: `rgba(34, 211, 238, ${0.02 + i * 0.008})`, border: '1px solid rgba(34, 211, 238, 0.08)', borderRadius: '8px' }}>
                      <span style={{ fontSize: '1rem', flexShrink: 0, marginTop: '0.05rem' }}>{item.icon}</span>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)', lineHeight: 1.45 }}>
                        {item.text}
                        {item.link && <> (<a href={item.link} onClick={(e) => { e.preventDefault(); navigateTo('rewards'); }} style={{ color: '#22d3ee', textDecoration: 'underline', cursor: 'pointer' }}>{item.linkText}</a>)</>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Claim Button + Rules */}
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                {user ? (
                  <button onClick={() => setPrizeClaimModal(true)} style={{ padding: '0.85rem 2.5rem', background: 'linear-gradient(135deg, #22d3ee, #0ea5e9)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 6px 24px rgba(34, 211, 238, 0.35)' }}>
                    🏆 Claim a Prize
                  </button>
                ) : (
                  <div style={{ color: 'var(--text-dim)', fontSize: '0.88rem' }}>Login with Kick or Twitch to claim prizes.</div>
                )}
                <div style={{ marginTop: '0.85rem' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.25rem', background: 'rgba(34, 197, 94, 0.12)', border: '1px solid rgba(34, 197, 94, 0.45)', borderRadius: '10px', boxShadow: '0 0 18px rgba(34, 197, 94, 0.18)' }}>
                    <span style={{ fontSize: '1.05rem' }}>⏱️</span>
                    <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#22c55e', letterSpacing: '0.01em' }}>Prize claims paid within 24–72 hours</span>
                  </div>
                </div>
              </div>

              {/* Prize Rules */}
              <div className="card" style={{ padding: '1rem 1.25rem', marginBottom: '1.5rem', border: '1px solid rgba(245, 158, 11, 0.15)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
                  <span style={{ fontSize: '1rem' }}>📋</span>
                  <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#fbbf24' }}>Prize Rules</span>
                </div>
                <div style={{ textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-dim)', lineHeight: 1.6 }}>
                  <p style={{ margin: '0 0 0.4rem', fontWeight: 700, color: '#22c55e' }}>ANYONE CAN WIN ANY CONTEST!</p>
                  <p style={{ margin: 0 }}>But Camily members (viewers who are in the Guild with min. of <strong style={{ color: '#fbbf24' }}>$100 wagers in current month</strong> OR <strong style={{ color: '#fbbf24' }}>previous month of $5,000 wager</strong> — need wagers &amp; Rainbet acc prior to contest wins) get <strong style={{ color: '#22c55e' }}>full prize amount!</strong></p>
                </div>
              </div>

              {/* My Claims History */}
              {user && prizeClaims.length > 0 && (
                <div className="card" style={{ padding: '1.25rem' }}>
                  <div style={{ fontSize: '1rem', fontWeight: 800, marginBottom: '0.75rem' }}>Your Claims</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {prizeClaims.map(c => {
                      const statusColors = { pending: '#f59e0b', approved: '#22c55e', denied: '#ef4444' };
                      return (
                        <div key={c.id} style={{ padding: '0.65rem 0.85rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>{c.prize_description}</span>
                            <span style={{ padding: '0.15rem 0.5rem', fontSize: '0.65rem', fontWeight: 700, borderRadius: '999px', background: `${statusColors[c.status]}22`, color: statusColors[c.status] }}>{c.status}</span>
                          </div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{new Date(c.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
                          {c.admin_notes && <div style={{ fontSize: '0.78rem', color: c.status === 'denied' ? '#ef4444' : '#22c55e', marginTop: '0.25rem' }}>Admin: {c.admin_notes}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {activePage === 'socials' && (() => {
          // YouTube Shorts IDs (admin-configured). Format: newline/comma separated
          // either full URLs (https://youtube.com/shorts/VIDEO_ID) or just IDs.
          const rawYT = String(siteSettings.youtube_shorts_urls || '').trim();
          const shortsUrls = rawYT ? rawYT.split(/\s*[\n,]\s*/).filter(Boolean) : [];
          const shorts = shortsUrls.map(u => {
            // Try matching a YouTube URL first, fall back to a bare ID
            const m = u.match(/(?:youtube\.com\/shorts\/|youtu\.be\/|youtube\.com\/watch\?v=)([A-Za-z0-9_-]{11})/);
            if (m) return m[1];
            const bare = u.match(/^([A-Za-z0-9_-]{11})$/);
            return bare ? bare[1] : null;
          }).filter(Boolean);

          // Social profile handles — Kick is primary for the streamer
          const socials = [
            {
              key: 'kick',
              label: 'Kick',
              handle: 'camneverwins',
              tagline: 'Live streams — primary platform',
              url: 'https://kick.com/camneverwins',
              color: '#53fc18',
              gradient: 'linear-gradient(135deg, #53fc18, #3fdc00)',
              bgTint: 'rgba(83, 252, 24, 0.06)',
              borderTint: 'rgba(83, 252, 24, 0.25)',
              icon: (
                <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 26, height: 26 }}>
                  <path d="M2 2h5v6h2V6h2V4h2V2h5v4h-2v2h-2v2h-2v4h2v2h2v2h2v4h-5v-2h-2v-2H9v2H7v2H2V2z"/>
                </svg>
              )
            },
            {
              key: 'youtube',
              label: 'YouTube',
              handle: 'camneverwins',
              tagline: 'Clips, highlights &amp; shorts',
              url: 'https://www.youtube.com/@camneverwins',
              color: '#ff0000',
              gradient: 'linear-gradient(135deg, #ff0000, #c80000)',
              bgTint: 'rgba(255, 0, 0, 0.06)',
              borderTint: 'rgba(255, 0, 0, 0.25)',
              icon: (
                <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 26, height: 26 }}>
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              )
            },
            {
              key: 'instagram',
              label: 'Instagram',
              handle: 'camneverwinsslots',
              tagline: 'Behind-the-scenes &amp; highlights',
              url: 'https://www.instagram.com/camneverwinsslots',
              color: '#dc2743',
              gradient: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
              bgTint: 'rgba(220, 39, 67, 0.05)',
              borderTint: 'rgba(220, 39, 67, 0.2)',
              icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 24, height: 24 }}>
                  <rect x="2" y="2" width="20" height="20" rx="5"/>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                </svg>
              )
            }
          ];

          return (
            <>
              <div className="header">
                <h1 className="header-title">📸 <span>Socials</span></h1>
                <p className="header-sub">Follow Cam across all his platforms</p>
              </div>

              {/* 3-column hero grid of profile cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                {socials.map(s => (
                  <a
                    key={s.key}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-hero-card"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.85rem',
                      padding: '1.5rem',
                      background: `linear-gradient(180deg, ${s.bgTint} 0%, rgba(0,0,0,0.3) 100%)`,
                      border: `1px solid ${s.borderTint}`,
                      borderRadius: '16px',
                      textDecoration: 'none',
                      color: 'inherit',
                      backdropFilter: 'blur(8px)',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.borderColor = s.color;
                      e.currentTarget.style.boxShadow = `0 12px 30px ${s.color}22`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = '';
                      e.currentTarget.style.borderColor = s.borderTint;
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                      <div style={{ width: 52, height: 52, borderRadius: '50%', background: s.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.key === 'kick' ? '#000' : '#fff', boxShadow: `0 4px 14px ${s.color}55`, flexShrink: 0 }}>
                        {s.icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.14em', color: s.color, textTransform: 'uppercase', marginBottom: '0.2rem' }}>{s.label}</div>
                        <div style={{ fontSize: '1.05rem', fontWeight: 800, fontFamily: 'Space Mono, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{s.handle}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', lineHeight: 1.45 }} dangerouslySetInnerHTML={{ __html: s.tagline }} />
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', fontWeight: 700, color: s.color, marginTop: 'auto' }}>
                      Follow on {s.label}
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                        <path d="M5 12h14M13 6l6 6-6 6"/>
                      </svg>
                    </div>
                  </a>
                ))}
              </div>

              {/* YouTube Shorts section */}
              <div className="header" style={{ marginBottom: '1rem' }}>
                <h2 className="header-title" style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)' }}>🎬 <span>Latest Shorts</span></h2>
                <p className="header-sub">Recent uploads from the YouTube channel</p>
              </div>

              {shorts.length === 0 ? (
                <div className="card" style={{ padding: '3rem 2rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '0.5rem', opacity: 0.4 }}>🎥</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.4rem' }}>No shorts featured yet</div>
                  <div style={{ fontSize: '0.88rem', color: 'var(--text-dim)', maxWidth: 400, margin: '0 auto' }}>
                    {user?.isAdmin
                      ? 'Add YouTube Shorts URLs or video IDs in the Admin → Config → Site Settings tab.'
                      : 'Check back soon. In the meantime, subscribe to the YouTube channel!'}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.25rem' }}>
                  {shorts.map(id => (
                    <div key={id} className="card" style={{ padding: 0, overflow: 'hidden', aspectRatio: '9 / 16' }}>
                      <iframe
                        src={`https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`}
                        title={`YouTube Short ${id}`}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        referrerPolicy="strict-origin-when-cross-origin"
                        style={{ width: '100%', height: '100%', display: 'block', border: 0 }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </>
          );
        })()}

        {/* SLOT REQUESTS PAGE */}
        {activePage === 'slotrequests' && (() => {
          const { pending, completed, open, loadedAt, keyword: slotKeyword } = slotReqData;
          const slotCmd = `!${slotKeyword || 'r'}`;
          const isAdminUser = !!user?.isAdmin;

          // Sort completed rows by selected tab
          const sortedCompleted = (() => {
            const list = [...completed];
            if (slotReqCompletedSort === 'profit') {
              list.sort((a, b) => Number(b.profit_amount || 0) - Number(a.profit_amount || 0));
            } else if (slotReqCompletedSort === 'multi') {
              list.sort((a, b) => Number(b.multiplier || 0) - Number(a.multiplier || 0));
            } else {
              // 'order' — already DESC by completed_position from API
            }
            return list;
          })();

          const fmt = (n) => n == null ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          const fmtMulti = (n) => n == null ? '—' : `${Number(n).toFixed(2)}x`;
          const fmtProfit = (n) => {
            if (n == null) return '—';
            const val = Number(n);
            const sign = val >= 0 ? '+' : '-';
            return `${sign}$${Math.abs(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          };

          return (
            <>
              <div className="header" style={{ textAlign: 'center' }}>
                <h1 className="header-title" style={{ color: '#ef4444', textShadow: '0 0 24px rgba(239, 68, 68, 0.4)' }}>
                  SLOT <span style={{ color: '#ef4444' }}>REQUESTS</span>
                </h1>
                <p className="header-sub">View slot requests from chat</p>
              </div>

              {/* Status pills */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap', padding: '1rem 1.5rem', background: 'rgba(14, 22, 43, 0.5)', border: '1px solid var(--border)', borderRadius: '12px', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <span style={{ color: 'var(--text-dim)', fontSize: '0.92rem' }}>Slot Requests:</span>
                  <span style={{
                    padding: '0.3rem 0.9rem',
                    background: open ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                    border: `1px solid ${open ? 'rgba(34, 197, 94, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
                    color: open ? '#22c55e' : '#ef4444',
                    fontWeight: 800,
                    fontSize: '0.75rem',
                    letterSpacing: '0.08em',
                    borderRadius: '999px'
                  }}>
                    {open ? 'OPEN' : 'CLOSED'}
                  </span>
                </div>
              </div>

              {/* How to Participate */}
              <div className="card" style={{ marginBottom: '1.5rem', padding: '1.75rem' }}>
                <h3 style={{ color: '#fbbf24', textAlign: 'center', marginBottom: '1.25rem', fontSize: '1.15rem', fontWeight: 800 }}>How to Participate</h3>
                <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                  <div style={{ flexShrink: 0, width: 28, height: 28, borderRadius: '50%', background: '#fbbf24', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.85rem' }}>1</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, marginBottom: '0.45rem' }}>Request a slot in Kick or Twitch chat</div>
                    <code style={{ display: 'inline-block', padding: '0.55rem 1rem', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(34, 197, 94, 0.35)', borderRadius: '6px', color: '#22c55e', fontFamily: 'Space Mono, monospace', fontSize: '0.9rem', marginBottom: '0.35rem' }}>
                      {slotCmd} slotname
                    </code>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      Replace "slotname" with your slot choice (e.g. <code style={{ background: 'rgba(255,255,255,0.05)', padding: '0.1rem 0.35rem', borderRadius: '3px' }}>{slotCmd} sugar rush</code>)
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '0.85rem' }}>
                  One request per username. Requesting again updates your slot but keeps your position.
                </div>
              </div>

              {/* Status banner */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', padding: '0.85rem 1.25rem', background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '10px', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                <span style={{ color: '#22c55e', fontWeight: 700, fontSize: '0.88rem' }}>Database Connected:</span>
                <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem', flex: 1 }}>
                  Slot requests auto-refresh every 5 seconds.
                </span>
                {isAdminUser && (
                  <button
                    onClick={async () => {
                      const token = getAuthToken();
                      const nextValue = open ? 'false' : 'true';
                      const res = await fetch(`${BACKEND_API_URL}/api/admin/settings`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ key: 'slot_requests_open', value: nextValue })
                      });
                      if (res.ok) {
                        setSiteSettings(prev => ({ ...prev, slot_requests_open: nextValue }));
                        fetchSlotRequests();
                      } else {
                        const err = await res.json().catch(() => ({}));
                        alert(err.error || 'Failed to toggle');
                      }
                    }}
                    style={{ padding: '0.5rem 1rem', background: open ? 'rgba(239, 68, 68, 0.15)' : 'rgba(34, 197, 94, 0.15)', border: `1px solid ${open ? 'rgba(239, 68, 68, 0.4)' : 'rgba(34, 197, 94, 0.4)'}`, borderRadius: '8px', color: open ? '#ef4444' : '#22c55e', fontFamily: 'inherit', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}
                  >
                    {open ? '🔒 Close Requests' : '🔓 Open Requests'}
                  </button>
                )}
                {isAdminUser && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600 }}>Command:</span>
                    <code style={{ fontSize: '0.8rem', color: '#22c55e' }}>!</code>
                    <input
                      type="text"
                      defaultValue={slotKeyword || 'r'}
                      style={{ width: 70, padding: '0.35rem 0.5rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.8rem', fontFamily: 'Space Mono, monospace' }}
                      onBlur={async (e) => {
                        const val = e.target.value.trim().toLowerCase().replace(/^!/, '');
                        if (!val || val === (slotKeyword || 'r')) return;
                        const token = getAuthToken();
                        const res = await fetch(`${BACKEND_API_URL}/api/admin/settings`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ key: 'slot_request_keyword', value: val })
                        });
                        if (res.ok) fetchSlotRequests();
                        else alert('Failed to update keyword');
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    />
                  </div>
                )}
                <button
                  onClick={fetchSlotRequests}
                  style={{ padding: '0.5rem 1rem', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '8px', color: '#ef4444', fontFamily: 'inherit', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
                >
                  <span style={{ display: 'inline-block', animation: loadedAt ? 'none' : 'spin 1s linear infinite' }}>↻</span>
                  Refresh
                </button>
              </div>

              {/* SLOT REQUEST REEL ANIMATION */}
              {slotReqReel && (
                <div className="raffle-reel-overlay" onClick={() => { if (slotReqReel.stage === 'revealed') setSlotReqReel(null); }}>
                  <div className="raffle-reel-title">
                    {slotReqReel.stage === 'revealed' ? '🎲 Random Pick!' : 'Picking a random request…'}
                  </div>
                  <div className="raffle-reel-container">
                    <div className="raffle-reel-fade-left" />
                    <div className="raffle-reel-pointer" />
                    <div className="raffle-reel-fade-right" />
                    <div
                      className="raffle-reel-strip"
                      style={{
                        transform: slotReqReel.stage === 'starting'
                          ? 'translateX(200px)'
                          : `translateX(${-(slotReqReel.target * 200) + 200}px)`,
                        transition: slotReqReel.stage === 'spinning'
                          ? 'transform 3.2s cubic-bezier(0.18, 0.7, 0.25, 1)'
                          : 'none'
                      }}
                    >
                      {slotReqReel.strip.map((cell, i) => (
                        <div
                          key={i}
                          className={`raffle-reel-cell ${cell.isWinner && slotReqReel.stage === 'revealed' ? 'is-winner' : ''}`}
                        >
                          <div className="raffle-reel-cell-name">{cell.display_name || cell.username}</div>
                          <div className="raffle-reel-cell-platform" style={{ fontSize: '0.6rem', opacity: 0.6 }}>{cell.slot_name?.slice(0, 20)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {slotReqReel.stage === 'revealed' && (
                    <div className="raffle-reel-winner-card">
                      <div className="raffle-reel-winner-eyebrow">Random Pick</div>
                      <div className="raffle-reel-winner-name">{slotReqReel.winner.display_name || slotReqReel.winner.username}</div>
                      <div className="raffle-reel-winner-meta">🎰 {slotReqReel.winner.slot_name}</div>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '0.75rem' }}>
                        <button onClick={async (e) => {
                          e.stopPropagation();
                          const token = getAuthToken();
                          await fetch(`${BACKEND_API_URL}/api/admin/slot-requests/${slotReqReel.winner.id}/complete`, {
                            method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
                          });
                          setSlotReqReel(null); fetchSlotRequests();
                        }} style={{ padding: '0.5rem 1.25rem', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>✓ Complete</button>
                        <button onClick={async (e) => {
                          e.stopPropagation();
                          const token = getAuthToken();
                          await fetch(`${BACKEND_API_URL}/api/admin/slot-requests/${slotReqReel.winner.id}/skip`, {
                            method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
                          });
                          setSlotReqReel(null); fetchSlotRequests();
                        }} style={{ padding: '0.5rem 1.25rem', background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>⏭ Skip</button>
                      </div>
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>or click anywhere to close</div>
                    </div>
                  )}
                </div>
              )}

              {/* PENDING CALLS */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.85rem', flexWrap: 'wrap' }}>
                <h2 style={{ color: '#fbbf24', fontWeight: 800, fontSize: '1.3rem', margin: 0 }}>Pending Calls</h2>
                {isAdminUser && pending.length > 0 && (
                  <button
                    onClick={async () => {
                      if (slotReqReel) return;
                      const token = getAuthToken();
                      const res = await fetch(`${BACKEND_API_URL}/api/admin/slot-requests/random`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (!res.ok) return;
                      const data = await res.json();
                      if (!data.winner) { alert('No pending requests'); return; }
                      
                      // Build a reel strip: duplicate pending list enough times for visual scrolling, inject winner
                      const winner = data.winner;
                      const pool = pending.length >= 3 ? pending : [...pending, ...pending, ...pending].slice(0, Math.max(6, pending.length));
                      const strip = [];
                      const repeats = 4;
                      for (let r = 0; r < repeats; r++) {
                        for (const p of pool) {
                          strip.push({ ...p, isWinner: false });
                        }
                      }
                      // Place winner near the end
                      const targetIdx = strip.length - pool.length + Math.floor(Math.random() * Math.max(1, pool.length - 2));
                      strip[targetIdx] = { ...winner, isWinner: true };
                      
                      // Stage 1: starting (instant position)
                      setSlotReqReel({ strip, target: targetIdx, winner, stage: 'starting' });
                      
                      // Stage 2: spinning (after 1 frame)
                      requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                          setSlotReqReel(prev => prev ? { ...prev, stage: 'spinning' } : null);
                          
                          // Stage 3: revealed (after spin completes)
                          setTimeout(() => {
                            setSlotReqReel(prev => prev ? { ...prev, stage: 'revealed' } : null);
                          }, 3400);
                        });
                      });
                    }}
                    style={{ padding: '0.45rem 1rem', background: 'linear-gradient(135deg, #fbbf24, #d97706)', color: '#000', border: 'none', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
                  >🎲 Pick Random</button>
                )}
              </div>
              <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: '2rem' }}>
                {pending.length === 0 ? (
                  <div style={{ padding: '2.5rem 1.5rem', textAlign: 'center', color: 'var(--text-dim)' }}>
                    No pending requests. Type <code style={{ background: 'rgba(255,255,255,0.05)', padding: '0.15rem 0.45rem', borderRadius: '3px', color: '#22c55e' }}>{slotCmd} [slotname]</code> in chat.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                      <thead>
                        <tr style={{ background: 'rgba(251, 191, 36, 0.08)', borderBottom: '1px solid rgba(251, 191, 36, 0.2)' }}>
                          {['#', 'Username', 'Slot', 'Bet', 'Payout', 'Profit', 'Multi', 'Status'].map(h => (
                            <th key={h} style={{ padding: '0.85rem 1rem', textAlign: h === '#' ? 'center' : 'left', fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', color: '#fbbf24', textTransform: 'uppercase' }}>{h}</th>
                          ))}
                          {isAdminUser && <th style={{ padding: '0.85rem 1rem', fontSize: '0.75rem', fontWeight: 800, color: '#fbbf24', textTransform: 'uppercase' }}>Admin</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {pending.map((r, idx) => (
                          <tr key={r.id} id={`slot-req-${r.id}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', transition: 'background 0.3s, box-shadow 0.3s' }}>
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: 700, color: 'var(--text-dim)', fontSize: '0.88rem' }}>{idx + 1}</td>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>
                              {r.display_name || r.username}
                              <span style={{ marginLeft: '0.5rem', padding: '0.1rem 0.4rem', fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.06em', borderRadius: '4px', background: r.platform === 'kick' ? 'rgba(83, 252, 24, 0.12)' : 'rgba(145, 71, 255, 0.15)', color: r.platform === 'kick' ? '#53fc18' : '#a78bfa', textTransform: 'uppercase' }}>
                                {r.platform}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem 1rem', color: '#e5e7eb' }}>{r.slot_name}</td>
                            <td style={{ padding: '0.75rem 1rem', color: 'var(--text-muted)' }}>—</td>
                            <td style={{ padding: '0.75rem 1rem', color: 'var(--text-muted)' }}>—</td>
                            <td style={{ padding: '0.75rem 1rem', color: 'var(--text-muted)' }}>—</td>
                            <td style={{ padding: '0.75rem 1rem', color: 'var(--text-muted)' }}>—</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <span style={{ padding: '0.25rem 0.7rem', background: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em' }}>Pending</span>
                            </td>
                            {isAdminUser && (
                              <td style={{ padding: '0.75rem 1rem' }}>
                                <div style={{ display: 'flex', gap: '0.4rem' }}>
                                  <button
                                    onClick={() => setSlotReqCompleteModal({ request: r, bet: '', payout: '', multiplier: '', notes: '' })}
                                    style={{ padding: '0.35rem 0.7rem', background: 'rgba(34, 197, 94, 0.18)', color: '#22c55e', border: '1px solid rgba(34, 197, 94, 0.4)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}
                                  >
                                    Complete
                                  </button>
                                  <button
                                    onClick={async () => {
                                      if (!confirm('Skip this request?')) return;
                                      const token = getAuthToken();
                                      const res = await fetch(`${BACKEND_API_URL}/api/admin/slot-requests/${r.id}/skip`, {
                                        method: 'POST',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      });
                                      if (res.ok) fetchSlotRequests();
                                    }}
                                    style={{ padding: '0.35rem 0.7rem', background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.35)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}
                                  >
                                    Skip
                                  </button>
                                  <button
                                    onClick={async () => {
                                      if (!confirm(`Delete request "${r.slot_name}" by ${r.display_name || r.username}?`)) return;
                                      const token = getAuthToken();
                                      const res = await fetch(`${BACKEND_API_URL}/api/admin/slot-requests/${r.id}`, {
                                        method: 'DELETE',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      });
                                      if (res.ok) fetchSlotRequests();
                                    }}
                                    style={{ padding: '0.35rem 0.5rem', background: 'none', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.68rem', cursor: 'pointer', opacity: 0.6 }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                                    title="Delete request"
                                  >🗑️</button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* COMPLETED CALLS */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.85rem', flexWrap: 'wrap' }}>
                <h2 style={{ color: '#22c55e', fontWeight: 800, fontSize: '1.3rem', margin: 0 }}>Completed Calls</h2>
                {isAdminUser && (pending.length > 0 || sortedCompleted.length > 0) && (
                  <button
                    onClick={async () => {
                      const total = pending.length + sortedCompleted.length;
                      if (!confirm(`Clear ALL ${total} slot requests (${pending.length} pending + ${sortedCompleted.length} completed/skipped)? This cannot be undone.`)) return;
                      const token = getAuthToken();
                      // Clear completed/skipped first
                      await fetch(`${BACKEND_API_URL}/api/admin/slot-requests/clear-completed`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
                      // Delete each pending one
                      for (const r of pending) {
                        await fetch(`${BACKEND_API_URL}/api/admin/slot-requests/${r.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                      }
                      fetchSlotRequests();
                    }}
                    style={{ padding: '0.35rem 0.85rem', background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.35)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
                  >
                    🗑️ Clear Everything
                  </button>
                )}
                {isAdminUser && sortedCompleted.length > 0 && (
                  <button
                    onClick={async () => {
                      if (!confirm(`Clear all ${sortedCompleted.length} completed + skipped requests? This cannot be undone.`)) return;
                      const token = getAuthToken();
                      const res = await fetch(`${BACKEND_API_URL}/api/admin/slot-requests/clear-completed`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (res.ok) fetchSlotRequests();
                      else alert('Failed to clear');
                    }}
                    style={{ padding: '0.35rem 0.85rem', background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.35)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
                  >
                    🗑️ Clear All
                  </button>
                )}
                <div style={{ display: 'flex', gap: '0.35rem', marginLeft: 'auto', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.25rem' }}>
                  {[
                    { key: 'order', label: 'Order' },
                    { key: 'profit', label: 'Profit' },
                    { key: 'multi', label: 'Multi' }
                  ].map(t => (
                    <button
                      key={t.key}
                      onClick={() => setSlotReqCompletedSort(t.key)}
                      style={{
                        padding: '0.4rem 0.95rem',
                        background: slotReqCompletedSort === t.key ? 'linear-gradient(135deg, #ef4444, #dc2626)' : 'transparent',
                        color: slotReqCompletedSort === t.key ? '#fff' : 'var(--text-dim)',
                        border: 'none',
                        borderRadius: '6px',
                        fontFamily: 'inherit',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        transition: 'all 0.15s'
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {sortedCompleted.length === 0 ? (
                  <div style={{ padding: '2.5rem 1.5rem', textAlign: 'center', color: 'var(--text-dim)' }}>
                    No completed calls yet.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                      <thead>
                        <tr style={{ background: 'rgba(34, 197, 94, 0.08)', borderBottom: '1px solid rgba(34, 197, 94, 0.2)' }}>
                          {['#', 'Username', 'Slot', 'Bet', 'Payout', 'Profit', 'Multi'].map(h => (
                            <th key={h} style={{ padding: '0.85rem 1rem', textAlign: h === '#' ? 'center' : 'left', fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', color: '#22c55e', textTransform: 'uppercase' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedCompleted.map((r, idx) => {
                          const profit = Number(r.profit_amount || 0);
                          return (
                            <tr key={r.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                              <td style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: 700, color: 'var(--text-dim)', fontSize: '0.88rem' }}>{idx + 1}</td>
                              <td style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>
                                {r.display_name || r.username}
                                <span style={{ marginLeft: '0.5rem', padding: '0.1rem 0.4rem', fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.06em', borderRadius: '4px', background: r.platform === 'kick' ? 'rgba(83, 252, 24, 0.12)' : 'rgba(145, 71, 255, 0.15)', color: r.platform === 'kick' ? '#53fc18' : '#a78bfa', textTransform: 'uppercase' }}>
                                  {r.platform}
                                </span>
                              </td>
                              <td style={{ padding: '0.75rem 1rem', color: '#e5e7eb' }}>{r.slot_name}</td>
                              <td style={{ padding: '0.75rem 1rem', fontFamily: 'Space Mono, monospace' }}>{fmt(r.bet_amount)}</td>
                              <td style={{ padding: '0.75rem 1rem', fontFamily: 'Space Mono, monospace' }}>{fmt(r.payout_amount)}</td>
                              <td style={{ padding: '0.75rem 1rem', fontFamily: 'Space Mono, monospace', fontWeight: 700, color: profit >= 0 ? '#22c55e' : '#ef4444' }}>{fmtProfit(r.profit_amount)}</td>
                              <td style={{ padding: '0.75rem 1rem', fontFamily: 'Space Mono, monospace', fontWeight: 700, color: Number(r.multiplier) >= 100 ? '#fbbf24' : '#e5e7eb' }}>{fmtMulti(r.multiplier)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {loadedAt && (
                <div style={{ textAlign: 'center', fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '1rem' }}>
                  Last updated {loadedAt.toLocaleTimeString()}
                </div>
              )}

              {/* Admin completion modal */}
              {slotReqCompleteModal && (() => {
                const m = slotReqCompleteModal;
                const betNum = Number(m.bet);
                const payoutNum = Number(m.payout);
                const profitPreview = (Number.isFinite(betNum) && Number.isFinite(payoutNum)) ? payoutNum - betNum : null;
                const multiPreview = (Number.isFinite(betNum) && Number.isFinite(payoutNum) && betNum > 0) ? payoutNum / betNum : null;
                return (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(4px)' }} onClick={(e) => { if (e.target === e.currentTarget) setSlotReqCompleteModal(null); }}>
                    <div className="card" style={{ maxWidth: 520, width: '100%', padding: '1.75rem' }}>
                      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.2rem', fontWeight: 800 }}>Complete Slot Request</h3>
                      <div style={{ color: 'var(--text-dim)', fontSize: '0.88rem', marginBottom: '1.25rem' }}>
                        <strong>{m.request.display_name || m.request.username}</strong> requested <strong style={{ color: '#e5e7eb' }}>{m.request.slot_name}</strong>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem', marginBottom: '0.85rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>Bet ($)</label>
                          <input type="number" step="0.01" min="0" value={m.bet} onChange={(e) => setSlotReqCompleteModal(prev => ({ ...prev, bet: e.target.value }))} style={{ width: '100%', padding: '0.65rem 0.85rem', background: 'rgba(0,0,0,0.4)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'Space Mono, monospace', fontSize: '0.95rem' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>Payout ($)</label>
                          <input type="number" step="0.01" min="0" value={m.payout} onChange={(e) => setSlotReqCompleteModal(prev => ({ ...prev, payout: e.target.value }))} style={{ width: '100%', padding: '0.65rem 0.85rem', background: 'rgba(0,0,0,0.4)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'Space Mono, monospace', fontSize: '0.95rem' }} />
                        </div>
                      </div>
                      <div style={{ marginBottom: '0.85rem' }}>
                        <label style={{ display: 'block', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>Multiplier (optional — auto-calculated from payout/bet if blank)</label>
                        <input type="number" step="0.01" min="0" placeholder={multiPreview != null ? `auto: ${multiPreview.toFixed(2)}x` : '—'} value={m.multiplier} onChange={(e) => setSlotReqCompleteModal(prev => ({ ...prev, multiplier: e.target.value }))} style={{ width: '100%', padding: '0.65rem 0.85rem', background: 'rgba(0,0,0,0.4)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'Space Mono, monospace', fontSize: '0.95rem' }} />
                      </div>
                      <div style={{ marginBottom: '0.85rem' }}>
                        <label style={{ display: 'block', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>Notes (optional)</label>
                        <input type="text" maxLength={500} value={m.notes} onChange={(e) => setSlotReqCompleteModal(prev => ({ ...prev, notes: e.target.value }))} style={{ width: '100%', padding: '0.65rem 0.85rem', background: 'rgba(0,0,0,0.4)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.88rem' }} />
                      </div>
                      {profitPreview != null && (
                        <div style={{ padding: '0.7rem 1rem', background: 'rgba(0,0,0,0.35)', border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.88rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                          <div><span style={{ color: 'var(--text-muted)' }}>Profit: </span><strong style={{ color: profitPreview >= 0 ? '#22c55e' : '#ef4444' }}>{profitPreview >= 0 ? '+' : '-'}${Math.abs(profitPreview).toFixed(2)}</strong></div>
                          {multiPreview != null && <div><span style={{ color: 'var(--text-muted)' }}>Auto Multi: </span><strong style={{ color: '#e5e7eb' }}>{multiPreview.toFixed(2)}x</strong></div>}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '0.65rem', justifyContent: 'flex-end' }}>
                        <button onClick={() => setSlotReqCompleteModal(null)} style={{ padding: '0.65rem 1.2rem', background: 'rgba(255,255,255,0.08)', color: 'var(--text)', border: 'none', borderRadius: '8px', fontFamily: 'inherit', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                        <button
                          disabled={!Number.isFinite(betNum) || betNum < 0 || !Number.isFinite(payoutNum) || payoutNum < 0}
                          onClick={async () => {
                            const token = getAuthToken();
                            const body = { bet: betNum, payout: payoutNum, notes: m.notes || undefined };
                            if (m.multiplier !== '') body.multiplier = Number(m.multiplier);
                            const res = await fetch(`${BACKEND_API_URL}/api/admin/slot-requests/${m.request.id}/complete`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify(body)
                            });
                            if (res.ok) {
                              setSlotReqCompleteModal(null);
                              fetchSlotRequests();
                            } else {
                              const err = await res.json().catch(() => ({}));
                              alert(err.error || 'Failed to complete');
                            }
                          }}
                          style={{ padding: '0.65rem 1.4rem', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', border: 'none', borderRadius: '8px', fontFamily: 'inherit', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(34, 197, 94, 0.3)', opacity: (!Number.isFinite(betNum) || betNum < 0 || !Number.isFinite(payoutNum) || payoutNum < 0) ? 0.5 : 1 }}
                        >
                          Complete Request
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          );
        })()}

        {/* PROFILE PAGE */}
        {activePage === 'profile' && (
          <>
            {user ? (
              <>
                <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', padding: '2rem', marginBottom: '1.5rem' }}>
                  <img src={user.profileImage || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} alt="" style={{ width: 100, height: 100, borderRadius: '50%', border: '3px solid var(--gold)', boxShadow: '0 0 25px rgba(229, 231, 235, 0.3)' }} />
                  <div>
                    <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.25rem' }}>{user.displayName}</h1>
                    <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>@{user.username}</p>
                    
                    {/* Rainbet accounts — .com and .us are separate casinos, link either or both.
                        With one linked, the other site is only a quiet text link (never pushed). */}
                    {editingRainbet ? (
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                          value={rainbetUsernameInput}
                          onChange={(e) => setRainbetUsernameInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') updateRainbetUsername(editingRainbet); }}
                          placeholder={`${editingRainbet === 'us' ? 'Rainbet.us' : 'Rainbet.com'} username (must be under code CAM)`}
                          style={{ padding: '0.5rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)', fontFamily: 'inherit', minWidth: 0 }}
                        />
                        <button onClick={() => updateRainbetUsername(editingRainbet)} style={{ padding: '0.5rem 1rem', background: 'var(--gold)', border: 'none', borderRadius: '6px', color: '#000', fontWeight: 600, cursor: 'pointer' }}>Save</button>
                        <button onClick={() => setEditingRainbet(false)} style={{ padding: '0.5rem 1rem', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'var(--text)', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    ) : (
                      <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        {[['com', 'Rainbet.com', user.rainbetUsername], ['us', 'Rainbet.us', user.rainbetUsUsername]].filter(([, , name]) => name).map(([site, label, name]) => (
                          <div key={site} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '8px', fontSize: '0.9rem' }}>
                            <span>🎰</span>
                            <span>{name}</span>
                            <span style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)' }}>{label}</span>
                            <button onClick={() => { setRainbetUsernameInput(name); setEditingRainbet(site); }} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '0.8rem' }}>Edit</button>
                            {user.rainbetUsername && user.rainbetUsUsername && (
                              <button onClick={() => updateRainbetUsername(site, { remove: true })} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.8rem' }}>Remove</button>
                            )}
                          </div>
                        ))}
                        {!hasRainbetLink(user) ? (
                          <>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              {[['com', 'Link Rainbet.com Username'], ['us', 'Link Rainbet.us Username']].map(([site, text]) => (
                                <button key={site} onClick={() => { setRainbetUsernameInput(''); setEditingRainbet(site); }} style={{ padding: '0.5rem 1rem', background: 'rgba(139, 92, 246, 0.2)', border: '1px solid rgba(139, 92, 246, 0.4)', borderRadius: '8px', color: '#8b5cf6', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                                  {text}
                                </button>
                              ))}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: '#fbbf24' }}>⚠️ Must be registered under code CAM</div>
                          </>
                        ) : (!user.rainbetUsername || !user.rainbetUsUsername) && (
                          <button onClick={() => { setRainbetUsernameInput(''); setEditingRainbet(user.rainbetUsername ? 'us' : 'com'); }} style={{ alignSelf: 'flex-start', background: 'transparent', border: 'none', padding: 0, color: 'var(--text-muted)', fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}>
                            Also play on {user.rainbetUsername ? 'Rainbet.us' : 'Rainbet.com'}? Link it too
                          </button>
                        )}
                      </div>
                    )}

                    {/* Linked Accounts — shows which platforms are connected + link/unlink buttons */}
                    <div style={{ marginTop: '0.75rem' }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.12em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Linked Accounts</div>

                      {/* Kick */}
                      {user.kickUsername || user.kickId ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: 'rgba(83, 252, 24, 0.08)', border: '1px solid rgba(83, 252, 24, 0.3)', borderRadius: '8px', fontSize: '0.9rem', marginBottom: '0.4rem' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="#53fc18"><path d="M3 3h6v6h3V6h3V3h6v6h-3v3h-3v3h3v3h3v6h-6v-3h-3v-3h-3v6H3z"/></svg>
                          <span style={{ fontFamily: 'Space Mono, monospace' }}>{user.kickUsername || 'Connected'}</span>
                          <span style={{ padding: '0.1rem 0.45rem', fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.08em', background: 'rgba(83, 252, 24, 0.15)', color: '#53fc18', borderRadius: '999px' }}>✓ KICK</span>
                          {user.twitchId && (
                            <button
                              onClick={async () => {
                                if (!confirm('Unlink your Kick account? You can re-link anytime.')) return;
                                const res = await fetchWithAuth(`${BACKEND_API_URL}/api/user/unlink/kick`, { method: 'POST' });
                                if (res.ok) { checkAuth(); alert('Kick account unlinked.'); }
                                else { const e = await res.json().catch(() => ({})); alert(e.error || 'Failed'); }
                              }}
                              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.7rem', cursor: 'pointer', textDecoration: 'underline' }}
                            >Unlink</button>
                          )}
                          {!user.twitchId && <span style={{ marginLeft: 'auto' }} />}
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            const token = getAuthToken();
                            if (!token) { alert('Please log in first'); return; }
                            window.location.href = `${BACKEND_API_URL}/auth/kick/link?token=${encodeURIComponent(token)}`;
                          }}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.55rem 0.85rem', background: 'rgba(83, 252, 24, 0.08)', border: '1px solid rgba(83, 252, 24, 0.25)', borderRadius: '8px', color: '#53fc18', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginBottom: '0.4rem' }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="#53fc18"><path d="M3 3h6v6h3V6h3V3h6v6h-3v3h-3v3h3v3h3v6h-6v-3h-3v-3h-3v6H3z"/></svg>
                          Link Kick Account
                          <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--text-muted)' }}>→</span>
                        </button>
                      )}

                      {/* Twitch */}
                      {user.twitchId ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: 'rgba(145, 71, 255, 0.08)', border: '1px solid rgba(145, 71, 255, 0.3)', borderRadius: '8px', fontSize: '0.9rem' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="#a78bfa"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/></svg>
                          <span style={{ fontFamily: 'Space Mono, monospace' }}>{user.twitchUsername || user.username || 'Connected'}</span>
                          <span style={{ padding: '0.1rem 0.45rem', fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.08em', background: 'rgba(145, 71, 255, 0.15)', color: '#a78bfa', borderRadius: '999px' }}>✓ TWITCH</span>
                          {user.kickId && (
                            <button
                              onClick={async () => {
                                if (!confirm('Unlink your Twitch account? You can re-link anytime.')) return;
                                const res = await fetchWithAuth(`${BACKEND_API_URL}/api/user/unlink/twitch`, { method: 'POST' });
                                if (res.ok) { checkAuth(); alert('Twitch account unlinked.'); }
                                else { const e = await res.json().catch(() => ({})); alert(e.error || 'Failed'); }
                              }}
                              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.7rem', cursor: 'pointer', textDecoration: 'underline' }}
                            >Unlink</button>
                          )}
                          {!user.kickId && <span style={{ marginLeft: 'auto' }} />}
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            const token = getAuthToken();
                            if (!token) { alert('Please log in first'); return; }
                            window.location.href = `${BACKEND_API_URL}/auth/twitch/link?token=${encodeURIComponent(token)}`;
                          }}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.55rem 0.85rem', background: 'rgba(145, 71, 255, 0.08)', border: '1px solid rgba(145, 71, 255, 0.25)', borderRadius: '8px', color: '#a78bfa', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="#a78bfa"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/></svg>
                          Link Twitch Account
                          <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--text-muted)' }}>→</span>
                        </button>
                      )}

                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                        Linking both accounts merges your points and data into one profile.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tracking stats — live wager / raffle / points snapshot */}
                {(() => {
                  const monthlyWagered = Number(rewardProgress?.monthly_wagered || 0);
                  const lifetimeWagered = Number(rewardProgress?.lifetime_wagered || 0);
                  const hasRainbet = hasRainbetLink(user);
                  const raffle = meStats?.raffle || null;
                  const weekly = meStats?.weekly || null;
                  const points = Number(pointsBalance || 0);
                  const checklistDone = checklistData?.completedSteps ?? null;
                  const checklistTotal = checklistData?.totalSteps ?? 7;
                  const cards = [
                    {
                      icon: '🎟️', accent: '#a78bfa', label: 'Raffle Tickets',
                      value: raffle ? (hasRainbet ? Number(raffle.tickets || 0).toLocaleString() : '—') : '0',
                      sub: raffle
                        ? (hasRainbet
                            ? (raffle.raffle_type === 'threshold'
                                ? (Number(raffle.tickets) >= 1
                                    ? `${raffle.name} · You're in! 🎟️`
                                    : `${raffle.name} · $${Number(raffle.to_next_ticket || 0).toLocaleString()} to enter`)
                                : `${raffle.name} · $${Number(raffle.to_next_ticket || 0).toLocaleString()} to next`)
                            : 'Link Rainbet to track')
                        : 'No active raffle'
                    },
                    {
                      icon: '📅', accent: '#22d3ee', label: 'Wagered This Month',
                      value: hasRainbet ? `$${monthlyWagered.toLocaleString()}` : '—',
                      sub: hasRainbet ? (rewardProgress?.month_key || 'current month') : 'Link Rainbet to track'
                    },
                    {
                      icon: '💰', accent: '#22c55e', label: 'Wagered Lifetime',
                      value: hasRainbet ? `$${lifetimeWagered.toLocaleString()}` : '—',
                      sub: hasRainbet ? 'rolling 4-month total' : 'Link Rainbet to track'
                    },
                    {
                      icon: '🏆', accent: '#fbbf24', label: 'Weekly Rank',
                      value: weekly && weekly.rank ? `#${weekly.rank}` : (hasRainbet ? 'Unranked' : '—'),
                      sub: weekly && weekly.rank
                        ? `$${Number(weekly.wagered || 0).toLocaleString()} this week`
                        : (hasRainbet ? 'No wagers this week yet' : 'Link Rainbet to track')
                    },
                    {
                      icon: '⭐', accent: '#f59e0b', label: 'Points Balance',
                      value: points.toLocaleString(),
                      sub: 'Redeem in the Points Shop'
                    },
                    {
                      icon: '✅', accent: '#34d399', label: 'Signup Checklist',
                      value: checklistDone !== null ? `${checklistDone}/${checklistTotal}` : `0/${checklistTotal}`,
                      sub: checklistDone !== null && checklistDone >= checklistTotal ? 'Complete — claim your $3!' : 'Steps to your free $3'
                    }
                  ];
                  return (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.14em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Your Stats</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.85rem' }}>
                        {cards.map((c, i) => (
                          <div key={i} className="card" style={{ padding: '1.1rem 1.25rem', margin: 0, position: 'relative', overflow: 'hidden', borderColor: `color-mix(in srgb, ${c.accent} 22%, var(--border))` }}>
                            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: c.accent, opacity: 0.7 }} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.55rem' }}>
                              <span style={{ fontSize: '1.05rem' }}>{c.icon}</span>
                              <span style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>{c.label}</span>
                            </div>
                            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: c.accent, fontFamily: "'Space Mono', monospace", lineHeight: 1.1 }}>{c.value}</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '0.3rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.sub}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Stats Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--gold)', marginBottom: '0.25rem' }}>{profileStats?.totalPredictions || 0}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Total Predictions</div>
                  </div>
                  <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--gold)', marginBottom: '0.25rem' }}>{profileStats?.totalWins || 0}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Total Wins</div>
                  </div>
                  <div className="card" style={{ textAlign: 'center', padding: '1.25rem', borderColor: 'var(--gold)', background: 'linear-gradient(180deg, rgba(229, 231, 235, 0.1) 0%, var(--bg-card) 100%)' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--gold)', marginBottom: '0.25rem' }}>${profileStats?.totalMoneyWon || 0}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Money Won</div>
                  </div>
                  <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--gold)', marginBottom: '0.25rem' }}>{profileStats?.huntsParticipated || 0}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Hunts Participated</div>
                  </div>
                </div>

                {/* My Wager Progress — only for users with linked Rainbet */}
                {hasRainbetLink(user) && rewardProgress && (() => {
                  const rewardsMode = siteSettings.reward_mode === 'monthly' ? 'monthly' : 'lifetime';
                  const currentWagered = rewardsMode === 'lifetime'
                    ? Number(rewardProgress.lifetime_wagered || 0)
                    : Number(rewardProgress.monthly_wagered || 0);
                  const tiers = rewardTiersAll
                    .filter(t => t.mode === rewardsMode && t.active !== false)
                    .sort((a, b) => Number(a.wager_requirement) - Number(b.wager_requirement));
                  if (!tiers.length) return null;
                  const nextTier = tiers.find(t => Number(t.wager_requirement) > currentWagered);
                  const lastCompletedIdx = tiers.findLastIndex(t => Number(t.wager_requirement) <= currentWagered);
                  const prevTierAmount = lastCompletedIdx >= 0 ? Number(tiers[lastCompletedIdx].wager_requirement) : 0;
                  const pct = nextTier
                    ? Math.min(100, Math.round(((currentWagered - prevTierAmount) / (Number(nextTier.wager_requirement) - prevTierAmount)) * 100))
                    : 100;
                  const remaining = nextTier ? Math.max(0, Number(nextTier.wager_requirement) - currentWagered) : 0;
                  const periodLabel = rewardsMode === 'monthly' ? 'this month' : 'all-time';
                  return (
                    <div className="card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(34, 197, 94, 0.02))', border: '1px solid rgba(34, 197, 94, 0.25)' }}>
                      <div className="card-header" style={{ borderBottom: 'none' }}>
                        <div className="card-icon">💵</div>
                        <div className="card-title">Your Wager Progress</div>
                        <span style={{ marginLeft: 'auto', padding: '0.15rem 0.55rem', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#22c55e', background: 'rgba(34, 197, 94, 0.15)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                          {rewardsMode}
                        </span>
                      </div>
                      <div style={{ padding: '0 1.25rem 1.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.4rem' }}>
                          <span style={{ fontSize: '2rem', fontWeight: 800, color: '#22c55e' }}>
                            ${currentWagered.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                          <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>wagered {periodLabel}</span>
                        </div>

                        {nextTier ? (
                          <>
                            <div style={{ marginTop: '0.85rem', marginBottom: '0.4rem', display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--text-dim)' }}>
                              <span>Next: <strong style={{ color: 'var(--text)' }}>{nextTier.name}</strong> · <span style={{ color: '#22c55e' }}>${Number(nextTier.reward_amount).toLocaleString()} reward</span></span>
                              <span>{pct}%</span>
                            </div>
                            <div style={{ height: 10, background: 'rgba(0,0,0,0.3)', borderRadius: 999, overflow: 'hidden', border: '1px solid var(--border)' }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #22c55e, #16a34a)', transition: 'width 0.4s ease', boxShadow: '0 0 12px rgba(34, 197, 94, 0.5)' }} />
                            </div>
                            <div style={{ marginTop: '0.6rem', fontSize: '0.82rem', color: 'var(--text-dim)' }}>
                              <strong style={{ color: 'var(--text)' }}>${remaining.toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong> more wagered to unlock <strong style={{ color: nextTier.color || 'var(--gold)' }}>{nextTier.name}</strong>
                            </div>
                          </>
                        ) : (
                          <div style={{ marginTop: '0.85rem', padding: '0.85rem 1rem', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '8px', fontSize: '0.85rem', color: '#f59e0b' }}>
                            🏆 You've maxed out all reward tiers for this period. Incredible!
                          </div>
                        )}

                        {/* Next 3 upcoming tiers preview */}
                        {nextTier && (
                          <div style={{ marginTop: '1rem' }}>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>Upcoming tiers</div>
                            <div style={{ display: 'grid', gap: '0.35rem' }}>
                              {tiers.filter(t => Number(t.wager_requirement) > currentWagered).slice(0, 3).map(t => (
                                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.45rem 0.75rem', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '0.8rem' }}>
                                  <span style={{ fontWeight: 600, color: t.color || 'var(--text)' }}>{t.name}</span>
                                  <span style={{ color: 'var(--text-dim)' }}>
                                    ${Number(t.wager_requirement).toLocaleString()} wagered → <span style={{ color: '#22c55e', fontWeight: 700 }}>${Number(t.reward_amount).toLocaleString()}</span>
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <a href="/rewards" onClick={(e) => { e.preventDefault(); navigateTo('rewards'); }} style={{ display: 'inline-block', marginTop: '1rem', fontSize: '0.8rem', color: '#22c55e', textDecoration: 'none' }}>
                          View full rewards page →
                        </a>
                      </div>
                    </div>
                  );
                })()}

                {/* Leaderboard Position Card - only show if user has rainbet username */}
                {hasRainbetLink(user) && (
                  <div className="card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(139, 92, 246, 0.05))', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
                    <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      📊 Your Leaderboard Status
                    </h2>
                    {profileStats?.leaderboardPosition ? (
                      <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Current Position</div>
                          <div style={{ fontSize: '2rem', fontWeight: 700, color: profileStats.leaderboardPosition <= 10 ? 'var(--gold)' : '#a78bfa' }}>
                            #{profileStats.leaderboardPosition}
                            {profileStats.leaderboardPosition <= 10 ? (
                              <span style={{ fontSize: '0.9rem', marginLeft: '0.5rem', color: '#22c55e' }}>🏆 In prizes!</span>
                            ) : (
                              <span style={{ fontSize: '0.8rem', marginLeft: '0.5rem', color: 'var(--text-dim)' }}>
                                ({profileStats.leaderboardPosition - 10} away from prizes)
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Wagered This Period</div>
                          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--gold)', fontFamily: "'Space Mono', monospace" }}>
                            ${profileStats.leaderboardWagered?.toLocaleString() || '0'}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                        <p>You're not on the leaderboard yet this period.</p>
                        <p style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>Make sure your Rainbet username is correct and start wagering to appear on the leaderboard!</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Win Breakdown */}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem' }}>🏆 Win Breakdown</h2>
                  <div>
                    {[
                      { medal: '🥇', label: '1st Place Wins', count: profileStats?.firstPlaceWins || 0 },
                      { medal: '🥈', label: '2nd Place Wins', count: profileStats?.secondPlaceWins || 0 },
                      { medal: '🥉', label: '3rd Place Wins', count: profileStats?.thirdPlaceWins || 0 }
                    ].map((row, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 0', borderBottom: idx < 2 ? '1px solid var(--border)' : 'none' }}>
                        <span style={{ fontSize: '1.25rem' }}>{row.medal}</span>
                        <span>{row.label}</span>
                        <span style={{ marginLeft: 'auto', fontWeight: 600, color: 'var(--gold)' }}>{row.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Prediction History */}
                <div className="card">
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem' }}>📜 Prediction History</h2>
                  {predictionHistory.length > 0 ? (
                    <div>
                      {predictionHistory.slice(0, 10).map((pred, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px 80px 100px', alignItems: 'center', padding: '0.75rem', borderBottom: '1px solid var(--border)', fontSize: '0.9rem' }}>
                          <span style={{ fontWeight: 600 }}>Hunt #{pred.hunt_id}</span>
                          <span style={{ color: 'var(--gold)' }}>${parseFloat(pred.predicted_balance).toLocaleString()}</span>
                          <span style={{ textAlign: 'center' }}>{pred.my_rank ? `#${pred.my_rank}` : '-'}</span>
                          <span style={{ textAlign: 'center', color: pred.my_reward ? '#22c55e' : 'var(--text-muted)', fontSize: '0.8rem', fontWeight: pred.my_reward ? 700 : 400 }}>{pred.my_reward || '—'}</span>
                          <span style={{ color: 'var(--text-dim)', fontSize: '0.8rem', textAlign: 'right' }}>{new Date(pred.created_at).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-dim)' }}>
                      No predictions yet. Make your first prediction during a bonus hunt!
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👤</div>
                <h3>Login to view your profile</h3>
                <p style={{ color: 'var(--text-dim)', marginBottom: '1.5rem' }}>Connect with Twitch to see your stats and prediction history</p>
                <button onClick={handleLogin} className="cta-btn" style={{ background: '#9147ff' }}>
                  🎮 Login with Twitch
                </button>
              </div>
            )}
          </>
        )}

        {/* MY PREDICTIONS PAGE */}
        {activePage === 'mypredictions' && (
          <>
            <div className="header">
              <h1 className="header-title">🔮 <span>My Predictions</span></h1>
              <p className="header-sub">Your prediction history and performance</p>
            </div>

            {user ? (
              <>
                {/* Summary Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--gold)', marginBottom: '0.25rem' }}>{predictionHistory.length}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Total Predictions</div>
                  </div>
                  <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#22c55e', marginBottom: '0.25rem' }}>{predictionHistory.filter(p => p.my_rank).length}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Wins</div>
                  </div>
                  <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--gold)', marginBottom: '0.25rem' }}>
                      {predictionHistory.filter(p => p.my_rank === 1).length}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>🥇 First Place</div>
                  </div>
                </div>

                {/* Prediction History Table */}
                <div className="card">
                  <div className="card-header">
                    <div className="card-icon">📜</div>
                    <div className="card-title">Prediction History</div>
                  </div>
                  
                  {predictionHistory.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border)' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-dim)', fontSize: '0.75rem', textTransform: 'uppercase' }}>Hunt</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', color: 'var(--text-dim)', fontSize: '0.75rem', textTransform: 'uppercase' }}>Your Prediction</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', color: 'var(--text-dim)', fontSize: '0.75rem', textTransform: 'uppercase' }}>Final Balance</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.75rem', textTransform: 'uppercase' }}>Result</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.75rem', textTransform: 'uppercase' }}>Prize</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-dim)', fontSize: '0.75rem', textTransform: 'uppercase' }}>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {predictionHistory.map((pred, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ padding: '0.75rem', fontWeight: 600 }}>Hunt #{pred.hunt_id}</td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', color: 'var(--gold)', fontWeight: 600, fontFamily: "'Space Mono', monospace" }}>
                                ${parseFloat(pred.predicted_balance).toLocaleString()}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: "'Space Mono', monospace" }}>
                                {pred.end_balance ? `$${parseFloat(pred.end_balance).toLocaleString()}` : '-'}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                {pred.my_rank === 1 && <span style={{ fontSize: '1.25rem' }}>🥇</span>}
                                {pred.my_rank === 2 && <span style={{ fontSize: '1.25rem' }}>🥈</span>}
                                {pred.my_rank === 3 && <span style={{ fontSize: '1.25rem' }}>🥉</span>}
                                {pred.my_rank > 3 && <span style={{ color: 'var(--text-dim)' }}>#{pred.my_rank}</span>}
                                {!pred.my_rank && pred.hunt_status === 'completed' && <span style={{ color: 'var(--text-dim)' }}>—</span>}
                                {pred.hunt_status !== 'completed' && <span style={{ padding: '0.25rem 0.5rem', background: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', borderRadius: '4px', fontSize: '0.75rem' }}>Pending</span>}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'center', color: pred.my_reward ? '#22c55e' : 'var(--text-muted)', fontWeight: pred.my_reward ? 700 : 400, fontSize: '0.85rem' }}>
                                {pred.my_reward || '—'}
                              </td>
                              <td style={{ padding: '0.75rem', color: 'var(--text-dim)', fontSize: '0.85rem' }}>
                                {new Date(pred.created_at).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-dim)' }}>
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔮</div>
                      <p>No predictions yet.</p>
                      <p style={{ marginTop: '0.5rem' }}>Make your first prediction during a bonus hunt!</p>
                      <button onClick={() => navigateTo('predictions')} className="cta-btn" style={{ marginTop: '1rem' }}>
                        Go to Predictions
                      </button>
                    </div>
                  )}
                </div>

                {/* Unclaimed Wins */}
                {myWins.filter(w => !w.claimed).length > 0 && (
                  <div className="card" style={{ background: 'rgba(229, 231, 235, 0.1)', border: '1px solid rgba(229, 231, 235, 0.3)' }}>
                    <div className="card-header">
                      <div className="card-icon">🏆</div>
                      <div className="card-title">Unclaimed Wins!</div>
                    </div>
                    {myWins.filter(w => !w.claimed).map(win => (
                      <div key={win.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderBottom: '1px solid var(--border)' }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>Hunt #{win.hunt_id} - {['🥇 1st', '🥈 2nd', '🥉 3rd'][win.rank - 1]} Place</div>
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                            Predicted: ${parseFloat(win.predicted_balance).toLocaleString()} | Actual: ${parseFloat(win.actual_balance).toLocaleString()}
                          </div>
                        </div>
                        <button 
                          onClick={() => setShowClaimModal(win)}
                          className="cta-btn-small"
                          style={{ background: 'var(--gold)', color: '#000' }}
                        >
                          Claim {win.reward}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔮</div>
                <h3>Login to view your predictions</h3>
                <p style={{ color: 'var(--text-dim)', marginBottom: '1.5rem' }}>Login with Kick or Twitch to see your prediction history</p>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={handleKickLogin} className="cta-btn" style={{ background: '#53fc18', color: '#000' }}>
                    Login with Kick
                  </button>
                  <button onClick={handleLogin} className="cta-btn" style={{ background: '#9147ff' }}>
                    Login with Twitch
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ADMIN PAGE */}
        {activePage === 'admin' && user?.isAdmin && (
          <>
            <div className="header">
              <h1 className="header-title">⚙️ <span>Admin Panel</span></h1>
              <p className="header-sub">Manage predictions, rewards, and giveaways</p>
            </div>
            
            {/* Admin Tabs */}
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              marginBottom: '1.5rem', 
              padding: '0.75rem',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '12px',
              border: '1px solid var(--border)'
            }}>
              {(() => {
                // Parent → children mapping. Parent keys are virtual (used only for
                // active highlighting). Child keys are the real adminTab values
                // existing render blocks already use, so no downstream changes needed.
                const tabGroups = [
                  { parent: 'dashboard', label: '📊 Dashboard', children: [
                    { key: 'dashboard', label: '📊 Overview' }
                  ]},
                  { parent: 'analytics', label: '📈 Analytics', children: [
                    { key: 'analytics', label: '📈 Wager Metrics' },
                    { key: 'monthlyWagers', label: '📊 Monthly Wagers' }
                  ]},
                  { parent: 'predictions', label: '🎯 Predictions', children: [
                    { key: 'hunts', label: '🎰 Hunts' },
                    { key: 'rewards', label: '💵 Prediction Claims', badge: adminClaims.length },
                    { key: 'paidRewards', label: '✅ Paid Predictions' }
                  ]},
                  { parent: 'rewardsGiveaways', label: '🏆 Rewards & Giveaways', children: [
                    { key: 'rewardsConfig', label: '🎖️ Wager Tiers' },
                    { key: 'tierClaims', label: '💵 Tier Claims', badge: adminTierClaims.filter(c => c.status === 'pending').length, danger: adminTierClaims.some(c => (c.suspicious || c.flagged) && c.status === 'pending') },
                    { key: 'paidTierClaims', label: '✅ Paid Tier Claims' },
                    { key: 'wagerClaims', label: '📋 Wager Claims', badge: wagerClaimsAdmin.filter(c => c.status === 'pending').length },
                    { key: 'checklistClaims', label: '🎁 Checklist Claims', badge: checklistAdminClaims.filter(c => !c.admin_paid).length },
                    { key: 'prizeClaims', label: '🏆 Prize Claims', badge: prizeClaimsAdmin.filter(c => c.status === 'pending').length },
                    { key: 'challengeClaims', label: '🏆 Challenge Claims' },
                    { key: 'giveaways', label: '🎁 Pending Giveaways', badge: adminGiveaways.filter(g => g.status === 'pending').length },
                    { key: 'paidGiveaways', label: '✅ Paid Giveaways' },
                    { key: 'unclaimedWins', label: '⚠️ Unclaimed Wins', badge: adminUnclaimedWins.length, danger: true }
                  ]},
                  { parent: 'leaderboards', label: '📅 Leaderboards', children: [
                    { key: 'leaderboardPeriods', label: '📅 Periods' },
                    { key: 'periodHistory', label: '📜 Period History' }
                  ]},
                  { parent: 'points', label: '💰 Points', children: [
                    { key: 'points', label: '💰 Points (all sub-tabs inside)' }
                  ]},
                  { parent: 'users', label: '👥 Users', children: [
                    { key: 'users', label: '👥 User List' },
                    { key: 'wagerLookup', label: '🔍 Wager Lookup' }
                  ]},
                  { parent: 'config', label: '⚙️ Config', children: [
                    { key: 'siteSettings', label: '🌐 Site Settings' },
                    { key: 'siteLinks', label: '🔗 Site Links' }
                  ]}
                ];
                // Figure out which parent contains the current adminTab (so the
                // correct child row is visible after page load or direct link).
                const activeGroup = tabGroups.find(g => g.children.some(c => c.key === adminTab)) || tabGroups[0];
                const renderBtn = (tab, isActive, opts = {}) => (
                  <button
                    key={tab.key || tab.parent}
                    onClick={() => opts.onClick()}
                    style={{
                      position: 'relative',
                      padding: opts.small ? '0.45rem 0.85rem' : '0.65rem 1rem',
                      background: isActive ? (tab.danger ? 'rgba(239, 68, 68, 0.25)' : 'var(--gold)') : 'rgba(255,255,255,0.05)',
                      border: isActive ? `1px solid ${tab.danger ? 'rgba(239, 68, 68, 0.5)' : 'var(--gold)'}` : '1px solid transparent',
                      borderRadius: '8px',
                      fontSize: opts.small ? '0.78rem' : '0.85rem',
                      color: isActive ? (tab.danger ? '#ef4444' : '#000') : 'var(--text-dim)',
                      cursor: 'pointer',
                      fontWeight: isActive ? 700 : 500,
                      fontFamily: 'inherit'
                    }}
                  >
                    {tab.label}
                    {tab.badge > 0 && (
                      <span style={{
                        marginLeft: '0.4rem',
                        padding: '0.1rem 0.45rem',
                        background: tab.danger ? '#ef4444' : '#3b82f6',
                        color: '#fff',
                        borderRadius: '10px',
                        fontSize: '0.7rem',
                        fontWeight: 700
                      }}>{tab.badge}</span>
                    )}
                  </button>
                );
                return (
                  <>
                    {/* Parent row */}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {tabGroups.map(g => {
                        const groupBadge = g.children.reduce((sum, c) => sum + (c.badge || 0), 0);
                        const isActive = activeGroup.parent === g.parent;
                        return renderBtn(
                          { label: g.label, badge: groupBadge, danger: g.children.some(c => c.danger && c.badge) },
                          isActive,
                          { onClick: () => setAdminTab(g.children[0].key) }
                        );
                      })}
                    </div>
                    {/* Sub-tab row (only shown if parent has 2+ children) */}
                    {activeGroup.children.length > 1 && (
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', paddingLeft: '0.25rem', paddingTop: '0.25rem', borderTop: '1px dashed var(--border)', marginTop: '0.25rem' }}>
                        {activeGroup.children.map(c => renderBtn(c, adminTab === c.key, { small: true, onClick: () => setAdminTab(c.key) }))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            
            {/* Dashboard Tab */}
            {adminTab === 'dashboard' && (
              <>
                {(() => {
                  if (!wagerStats && !wagerStatsLoading) fetchWagerStats();
                  return null;
                })()}

                {/* Clickable Stats Grid */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                  gap: '1rem', 
                  marginBottom: '1.5rem' 
                }}>
                  <div 
                    onClick={() => setAdminTab('users')}
                    style={{ 
                      padding: '1.25rem', 
                      background: 'rgba(139, 92, 246, 0.1)', 
                      border: '1px solid rgba(139, 92, 246, 0.3)', 
                      borderRadius: '12px', 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: '2.25rem', fontWeight: 700, color: '#8b5cf6' }}>{adminStats?.totalUsers || 0}</div>
                    <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem', marginTop: '0.25rem' }}>Total Users</div>
                  </div>
                  
                  <div 
                    onClick={() => setAdminTab('hunts')}
                    style={{ 
                      padding: '1.25rem', 
                      background: 'rgba(229, 231, 235, 0.1)', 
                      border: '1px solid rgba(229, 231, 235, 0.3)', 
                      borderRadius: '12px', 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: '2.25rem', fontWeight: 700, color: 'var(--gold)' }}>{adminStats?.totalPredictions || 0}</div>
                    <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem', marginTop: '0.25rem' }}>Total Predictions</div>
                  </div>
                  
                  <div 
                    onClick={() => setAdminTab('rewards')}
                    style={{ 
                      padding: '1.25rem', 
                      background: adminStats?.pendingClaims > 0 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(0,0,0,0.2)', 
                      border: adminStats?.pendingClaims > 0 ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid var(--border)', 
                      borderRadius: '12px', 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: '2.25rem', fontWeight: 700, color: adminStats?.pendingClaims > 0 ? '#f59e0b' : 'var(--text-dim)' }}>
                      {adminStats?.pendingClaims || 0}
                    </div>
                    <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem', marginTop: '0.25rem' }}>Pending Claims</div>
                  </div>
                  
                  <div 
                    onClick={() => setAdminTab('giveaways')}
                    style={{ 
                      padding: '1.25rem', 
                      background: adminStats?.pendingGiveaways > 0 ? 'rgba(236, 72, 153, 0.15)' : 'rgba(0,0,0,0.2)', 
                      border: adminStats?.pendingGiveaways > 0 ? '1px solid rgba(236, 72, 153, 0.4)' : '1px solid var(--border)', 
                      borderRadius: '12px', 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: '2.25rem', fontWeight: 700, color: adminStats?.pendingGiveaways > 0 ? '#ec4899' : 'var(--text-dim)' }}>
                      {adminStats?.pendingGiveaways || 0}
                    </div>
                    <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem', marginTop: '0.25rem' }}>Pending Giveaways</div>
                  </div>
                  
                  <div 
                    onClick={() => setShowManualPayoutModal(true)}
                    style={{ 
                      padding: '1.25rem', 
                      background: 'rgba(34, 197, 94, 0.1)', 
                      border: '1px solid rgba(34, 197, 94, 0.3)', 
                      borderRadius: '12px', 
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}>
                    <div style={{ fontSize: '2.25rem', fontWeight: 700, color: '#22c55e' }}>${adminStats?.totalPaidOut?.toLocaleString() || 0}</div>
                    <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem', marginTop: '0.25rem' }}>Total Paid Out</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', marginTop: '0.25rem' }}>Click to add manual payout</div>
                  </div>
                  
                  <div 
                    onClick={() => setAdminTab('unclaimedWins')}
                    style={{ 
                      padding: '1.25rem', 
                      background: adminStats?.unclaimedWins > 0 ? 'rgba(239, 68, 68, 0.15)' : 'rgba(0,0,0,0.2)', 
                      border: adminStats?.unclaimedWins > 0 ? '1px solid rgba(239, 68, 68, 0.4)' : '1px solid var(--border)', 
                      borderRadius: '12px', 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: '2.25rem', fontWeight: 700, color: adminStats?.unclaimedWins > 0 ? '#ef4444' : 'var(--text-dim)' }}>
                      {adminStats?.unclaimedWins || 0}
                    </div>
                    <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem', marginTop: '0.25rem' }}>Unclaimed Wins</div>
                  </div>
                </div>
                
                {/* Prediction Controls Card */}
                <div className="card">
                  <div className="card-header">
                    <div className="card-icon">🎯</div>
                    <div className="card-title">Prediction Controls</div>
                  </div>
                  
                  {/* Current Status */}
                  <div style={{ padding: '1rem', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                      <span style={{ color: 'var(--text-dim)' }}>Status:</span>
                      {activeHuntData?.predictionsOpen ? (
                        <span style={{ padding: '0.4rem 0.85rem', background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', borderRadius: '6px', fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ width: 8, height: 8, background: '#22c55e', borderRadius: '50%', animation: 'pulse 2s infinite' }}></span>
                          PREDICTIONS OPEN
                        </span>
                      ) : activeHuntData?.activeHunt?.status === 'paused' ? (
                        <span style={{ padding: '0.4rem 0.85rem', background: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', borderRadius: '6px', fontWeight: 600, fontSize: '0.85rem' }}>
                          ⏸️ PAUSED
                        </span>
                      ) : (
                        <span style={{ padding: '0.4rem 0.85rem', background: 'rgba(107, 114, 128, 0.2)', color: '#6b7280', borderRadius: '6px', fontWeight: 600, fontSize: '0.85rem' }}>
                          CLOSED
                        </span>
                      )}
                      {activeHuntData?.activeHunt && (
                        <span style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                          Hunt #{activeHuntData.activeHunt.huntId} • <strong style={{ color: 'var(--gold)' }}>{activeHuntData.predictionCount || 0}</strong> predictions
                        </span>
                      )}
                    </div>
                    
                    {activeHuntData?.activeHunt && (
                      <div style={{ display: 'flex', gap: '1.5rem', padding: '1rem', background: 'rgba(0,0,0,0.2)', borderRadius: '10px', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Start Balance</div>
                          <div style={{ fontWeight: 700, color: 'var(--gold)', fontSize: '1.1rem' }}>${activeHuntData.activeHunt.startBalance?.toLocaleString()}</div>
                        </div>
                        {activeHuntData.rewards && (
                          <>
                            <div style={{ borderLeft: '1px solid var(--border)', paddingLeft: '1.5rem' }}>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>🥇 1st Prize</div>
                              <div style={{ fontWeight: 600, fontSize: '1rem' }}>{activeHuntData.rewards.first}</div>
                            </div>
                            <div style={{ borderLeft: '1px solid var(--border)', paddingLeft: '1.5rem' }}>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>🥈 2nd Prize</div>
                              <div style={{ fontWeight: 600, fontSize: '1rem' }}>{activeHuntData.rewards.second}</div>
                            </div>
                            <div style={{ borderLeft: '1px solid var(--border)', paddingLeft: '1.5rem' }}>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>🥉 3rd Prize</div>
                              <div style={{ fontWeight: 600, fontSize: '1rem' }}>{activeHuntData.rewards.third}</div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Rewards Configuration */}
                  <div style={{ padding: '1rem', borderBottom: '1px solid var(--border)' }}>
                    <h4 style={{ fontSize: '0.85rem', marginBottom: '0.75rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Prize Configuration</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.35rem' }}>🥇 1st Place</label>
                        <input
                          type="text"
                          value={adminRewards.first}
                          onChange={(e) => setAdminRewards(r => ({ ...r, first: e.target.value }))}
                          style={{ width: '100%', padding: '0.65rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.9rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.35rem' }}>🥈 2nd Place</label>
                        <input
                          type="text"
                          value={adminRewards.second}
                          onChange={(e) => setAdminRewards(r => ({ ...r, second: e.target.value }))}
                          style={{ width: '100%', padding: '0.65rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.9rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.35rem' }}>🥉 3rd Place</label>
                        <input
                          type="text"
                          value={adminRewards.third}
                          onChange={(e) => setAdminRewards(r => ({ ...r, third: e.target.value }))}
                          style={{ width: '100%', padding: '0.65rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.9rem' }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div style={{ padding: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {!activeHuntData?.predictionsOpen && activeHuntData?.activeHunt?.status !== 'paused' ? (
                      <button
                        onClick={openPredictions}
                        disabled={!activeHuntData?.activeHunt}
                        style={{
                          padding: '0.85rem 1.75rem',
                          background: activeHuntData?.activeHunt ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'rgba(107, 114, 128, 0.3)',
                          border: 'none',
                          borderRadius: '10px',
                          color: 'white',
                          fontFamily: 'inherit',
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          cursor: activeHuntData?.activeHunt ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          boxShadow: activeHuntData?.activeHunt ? '0 4px 15px rgba(34, 197, 94, 0.3)' : 'none'
                        }}
                      >
                        🟢 Open Predictions
                      </button>
                    ) : activeHuntData?.activeHunt?.status === 'paused' ? (
                      <button
                        onClick={openPredictions}
                        style={{
                          padding: '0.85rem 1.75rem',
                          background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                          border: 'none',
                          borderRadius: '10px',
                          color: 'white',
                          fontFamily: 'inherit',
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)'
                        }}
                      >
                        ▶️ Resume Predictions
                      </button>
                    ) : null}
                    
                    {activeHuntData?.predictionsOpen && (
                      <button
                        onClick={pausePredictions}
                        style={{
                          padding: '0.85rem 1.75rem',
                          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                          border: 'none',
                          borderRadius: '10px',
                          color: 'white',
                          fontFamily: 'inherit',
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)'
                        }}
                      >
                        ⏸️ Pause Predictions
                      </button>
                    )}
                    
                    {(activeHuntData?.predictionsOpen || activeHuntData?.activeHunt?.status === 'paused') && (
                      <button
                        onClick={closePredictions}
                        style={{
                          padding: '0.85rem 1.75rem',
                          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                          border: 'none',
                          borderRadius: '10px',
                          color: 'white',
                          fontFamily: 'inherit',
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)'
                        }}
                      >
                        🏁 Close & Calculate Winners
                      </button>
                    )}
                    
                    {!activeHuntData?.activeHunt && !bonusHunts.activeHunt && activeHuntData !== null && (
                      <div style={{ color: 'var(--text-dim)', fontSize: '0.9rem', padding: '0.75rem', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                        ⚠️ No active bonus hunt detected. Start a hunt on Myrtle to enable predictions.
                      </div>
                    )}
                    
                    {activeHuntData === null && (
                      <div style={{ color: 'var(--text-dim)', fontSize: '0.9rem', padding: '0.75rem' }}>
                        Loading hunt data...
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Analytics Tab */}
            {adminTab === 'analytics' && (
              <>
                {(() => { if (!wagerStats && !wagerStatsLoading && !wagerStatsError) fetchWagerStats(); return null; })()}
                {/* Wager Metrics - Rainbet-sourced */}
                <div className="card" style={{ marginBottom: '1.5rem', padding: 0, overflow: 'hidden' }}>
                  <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{ fontSize: '1.5rem' }}>📊</div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>Wager Metrics</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          {wagerStats?.cached ? 'Cached' : 'Live'} · {wagerStats?.period ? `${wagerStats.period.start} to ${wagerStats.period.end}` : '—'}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => fetchWagerStats(true)}
                      disabled={wagerStatsLoading}
                      style={{ padding: '0.4rem 0.9rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '6px', fontSize: '0.8rem', cursor: wagerStatsLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: wagerStatsLoading ? 0.5 : 1 }}
                    >{wagerStatsLoading ? 'Loading...' : wagerStatsError ? '⚠ Retry' : '↻ Refresh'}</button>
                  </div>

                  {wagerStatsError && (
                    <div style={{ padding: '0.75rem 1.25rem', background: 'rgba(239, 68, 68, 0.08)', borderBottom: '1px solid rgba(239, 68, 68, 0.2)', fontSize: '0.8rem', color: '#ef4444' }}>
                      ⚠️ Failed to load wager stats: {wagerStatsError}. The Rainbet API may be rate-limited or unavailable. Click Retry.
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', padding: '1.25rem' }}>
                    {[
                      { label: 'Wagered (period)', value: wagerStats?.totals?.periodWagered, money: true, color: '#22c55e' },
                      { label: 'Wagered (last 4 mo.)', value: wagerStats?.totals?.last4mWagered, money: true, color: '#22c55e' },
                      { label: 'Players (period)', value: wagerStats?.totals?.periodPlayers, color: '#3b82f6' },
                      { label: 'Players (last 4 mo.)', value: wagerStats?.totals?.last4mPlayers, color: '#3b82f6' }
                    ].map(stat => (
                      <div key={stat.label} style={{ padding: '1.1rem', background: 'rgba(0,0,0,0.25)', border: '1px solid var(--border)', borderRadius: '10px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.8rem', fontWeight: 800, color: stat.color }}>
                          {stat.value == null ? '—' : (stat.money ? `$${Number(stat.value).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : Number(stat.value).toLocaleString())}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '0.25rem' }}>
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Month-over-month deltas */}
                  {wagerStats && (
                    <div style={{ padding: '1rem 1.25rem', borderTop: '1px solid var(--border)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
                      {[
                        { label: 'This month vs last (wagered)', cur: wagerStats.totals.thisMonthWagered, prev: wagerStats.totals.lastMonthWagered, delta: wagerStats.deltas.wageredPct, money: true },
                        { label: 'This month vs last (players)', cur: wagerStats.totals.thisMonthPlayers, prev: wagerStats.totals.lastMonthPlayers, delta: wagerStats.deltas.playersPct, money: false }
                      ].map(row => {
                        const up = row.delta >= 0;
                        return (
                          <div key={row.label} style={{ padding: '0.85rem 1rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
                            <div>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{row.label}</div>
                              <div style={{ fontSize: '1.05rem', fontWeight: 700, marginTop: '0.15rem' }}>
                                {row.money ? `$${Number(row.cur).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : Number(row.cur).toLocaleString()}
                                <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.8rem' }}>
                                  {' '}vs {row.money ? `$${Number(row.prev).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : Number(row.prev).toLocaleString()}
                                </span>
                              </div>
                            </div>
                            <div style={{
                              padding: '0.35rem 0.7rem',
                              background: up ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                              color: up ? '#22c55e' : '#ef4444',
                              border: `1px solid ${up ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                              borderRadius: '999px',
                              fontSize: '0.85rem',
                              fontWeight: 700,
                              whiteSpace: 'nowrap'
                            }}>
                              {up ? '↑' : '↓'} {Math.abs(row.delta)}%
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Top 10 wagerers */}
                  <div style={{ padding: '1.25rem', borderTop: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Top 10 Wagerers</div>
                      <div style={{ display: 'inline-flex', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '999px', padding: '0.2rem', gap: '0.2rem' }}>
                        {[
                          { key: 'period', label: 'Period' },
                          { key: 'last4m', label: 'Last 4 mo.' }
                        ].map(t => (
                          <button
                            key={t.key}
                            onClick={() => setTopListMode(t.key)}
                            style={{
                              padding: '0.35rem 0.85rem',
                              background: topListMode === t.key ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'transparent',
                              color: topListMode === t.key ? '#fff' : 'var(--text-dim)',
                              border: 'none',
                              borderRadius: '999px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              fontFamily: 'inherit'
                            }}
                          >{t.label}</button>
                        ))}
                      </div>
                    </div>
                    {(() => {
                      const list = topListMode === 'period' ? (wagerStats?.top10Period || []) : (wagerStats?.top10Last4m || []);
                      if (!list.length) return <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.85rem' }}>{wagerStatsLoading ? 'Loading...' : 'No data'}</div>;
                      return (
                        <div style={{ display: 'grid', gap: '0.4rem' }}>
                          {list.map((p, i) => (
                            <div key={`${topListMode}-${i}`} style={{ display: 'grid', gridTemplateColumns: '40px 1fr auto', gap: '0.75rem', alignItems: 'center', padding: '0.6rem 0.85rem', background: i < 3 ? 'rgba(229, 231, 235, 0.04)' : 'transparent', borderRadius: '6px' }}>
                              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: i === 0 ? '#f5a623' : i === 1 ? '#c0c0c0' : i === 2 ? '#cd7f32' : 'var(--text-muted)' }}>#{i + 1}</div>
                              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.88rem' }}>{p.username}</div>
                              <div style={{ fontWeight: 700, color: '#22c55e' }}>${Number(p.wagered).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                  {wagerStats?.errors?.length > 0 && (
                    <div style={{ padding: '0.75rem 1.25rem', background: 'rgba(239, 68, 68, 0.08)', borderTop: '1px solid rgba(239, 68, 68, 0.2)', fontSize: '0.8rem', color: '#ef4444' }}>
                      ⚠️ Some Rainbet queries failed: {wagerStats.errors.join('; ')}
                    </div>
                  )}
                </div>

              </>
            )}

            {adminTab === 'hunts' && (
              <>
                {/* Hunt Override Controls */}
                <div className="card" style={{ marginBottom: '1rem' }}>
                  <div className="card-header">
                    <div className="card-icon">⚙️</div>
                    <div className="card-title">Hunt Controls</div>
                    {activeHuntData?.activeHunt && (
                      <span style={{ marginLeft: 'auto', padding: '0.3rem 0.75rem', background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600 }}>
                        Active: #{activeHuntData.activeHunt.huntId}
                      </span>
                    )}
                  </div>
                  <div style={{ padding: '1rem' }}>
                    {/* Current Active Hunt Override */}
                    {activeHuntData?.activeHunt ? (
                      <div style={{ marginBottom: '1.5rem' }}>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginBottom: '0.75rem', fontWeight: 600 }}>
                          Override Active Hunt #{activeHuntData.activeHunt.huntId}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
                          <div>
                            <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.25rem' }}>Start Balance</label>
                            <input
                              type="number"
                              placeholder={activeHuntData.activeHunt.startBalance?.toString() || '0'}
                              defaultValue={activeHuntData.activeHunt.startBalance || ''}
                              id="override-start-balance"
                              style={{ width: '100%', padding: '0.5rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)', fontFamily: 'inherit' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.25rem' }}>End Balance</label>
                            <input
                              type="number"
                              placeholder={activeHuntData.activeHunt.endBalance?.toString() || 'Not set'}
                              defaultValue={activeHuntData.activeHunt.endBalance || ''}
                              id="override-end-balance"
                              style={{ width: '100%', padding: '0.5rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)', fontFamily: 'inherit' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.25rem' }}>Status</label>
                            <select
                              id="override-status"
                              defaultValue={activeHuntData.activeHunt.status || 'active'}
                              style={{ width: '100%', padding: '0.5rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)', fontFamily: 'inherit' }}
                            >
                              <option value="active">Active</option>
                              <option value="paused">Paused</option>
                              <option value="completed">Completed</option>
                            </select>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const startBalance = document.getElementById('override-start-balance').value;
                            const endBalance = document.getElementById('override-end-balance').value;
                            const status = document.getElementById('override-status').value;
                            updateHuntSettings(activeHuntData.activeHunt.huntId, {
                              startBalance: startBalance || undefined,
                              endBalance: endBalance || undefined,
                              status
                            });
                          }}
                          disabled={huntOverrideLoading}
                          style={{ padding: '0.5rem 1rem', background: 'var(--gold)', color: '#000', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}
                        >
                          {huntOverrideLoading ? 'Updating...' : '💾 Update Hunt Settings'}
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Are you sure you want to DELETE Hunt #${activeHuntData.activeHunt.huntId}? This will also delete all predictions for this hunt.`)) return;
                            setHuntOverrideLoading(true);
                            try {
                              const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/hunts/${activeHuntData.activeHunt.huntId}`, { method: 'DELETE' });
                              if (res.ok) {
                                alert('Hunt deleted');
                                fetchActiveHunt();
                                fetchAdminHunts();
                              } else {
                                const data = await res.json();
                                alert(data.error || 'Failed to delete hunt');
                              }
                            } catch (e) {
                              alert('Error deleting hunt');
                            } finally {
                              setHuntOverrideLoading(false);
                            }
                          }}
                          disabled={huntOverrideLoading}
                          style={{ padding: '0.5rem 1rem', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit', marginLeft: '0.5rem' }}
                        >
                          🗑️ Delete Hunt
                        </button>
                      </div>
                    ) : (
                      <div style={{ padding: '1rem', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                        <div style={{ color: '#f59e0b', fontWeight: 600, marginBottom: '0.25rem' }}>⚠️ No Active Hunt</div>
                        <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>Create a manual hunt below or wait for Myrtle sync.</div>
                      </div>
                    )}
                    
                    {/* Create Manual Hunt */}
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginBottom: '0.75rem', fontWeight: 600 }}>
                        Create Manual Hunt
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
                        <div>
                          <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.25rem' }}>Hunt ID *</label>
                          <input
                            type="text"
                            placeholder="e.g. 999"
                            value={huntOverride.huntId}
                            onChange={(e) => setHuntOverride(prev => ({ ...prev, huntId: e.target.value }))}
                            style={{ width: '100%', padding: '0.5rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)', fontFamily: 'inherit' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.25rem' }}>Start Balance *</label>
                          <input
                            type="number"
                            placeholder="e.g. 5000"
                            value={huntOverride.startBalance}
                            onChange={(e) => setHuntOverride(prev => ({ ...prev, startBalance: e.target.value }))}
                            style={{ width: '100%', padding: '0.5rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)', fontFamily: 'inherit' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.25rem' }}>Status</label>
                          <select
                            value={huntOverride.status}
                            onChange={(e) => setHuntOverride(prev => ({ ...prev, status: e.target.value }))}
                            style={{ width: '100%', padding: '0.5rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)', fontFamily: 'inherit' }}
                          >
                            <option value="active">Active</option>
                            <option value="paused">Paused</option>
                          </select>
                        </div>
                      </div>
                      <button
                        onClick={createManualHunt}
                        disabled={huntOverrideLoading || !huntOverride.huntId || !huntOverride.startBalance}
                        style={{ padding: '0.5rem 1rem', background: (!huntOverride.huntId || !huntOverride.startBalance) ? 'rgba(255,255,255,0.1)' : '#22c55e', color: (!huntOverride.huntId || !huntOverride.startBalance) ? 'var(--text-dim)' : '#fff', border: 'none', borderRadius: '6px', cursor: (!huntOverride.huntId || !huntOverride.startBalance) ? 'not-allowed' : 'pointer', fontWeight: 600, fontFamily: 'inherit' }}
                      >
                        {huntOverrideLoading ? 'Creating...' : '➕ Create Hunt'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Hunt List */}
                <div className="card">
                  <div className="card-header">
                    <div className="card-icon">🎰</div>
                    <div className="card-title">Hunt History</div>
                    <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                      {adminHunts.length} hunts
                    </span>
                  </div>
                
                {adminHunts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-dim)' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎰</div>
                    No hunts recorded yet
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    {/* Header */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '70px 90px 90px 90px 80px 100px 1fr', 
                      padding: '0.75rem 1rem', 
                      background: 'rgba(0,0,0,0.3)',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      color: 'var(--text-dim)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      minWidth: '700px'
                    }}>
                      <span>Hunt ID</span>
                      <span>Status</span>
                      <span style={{ textAlign: 'right' }}>Start</span>
                      <span style={{ textAlign: 'right' }}>End</span>
                      <span style={{ textAlign: 'center' }}>Preds</span>
                      <span>Date</span>
                      <span style={{ textAlign: 'right' }}>Actions</span>
                    </div>
                    
                    {/* Rows */}
                    {adminHunts.map((hunt, idx) => (
                      <div 
                        key={hunt.id} 
                        style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '70px 90px 90px 90px 80px 100px 1fr', 
                          padding: '0.75rem 1rem', 
                          borderBottom: idx < adminHunts.length - 1 ? '1px solid var(--border)' : 'none',
                          alignItems: 'center',
                          minWidth: '700px',
                          background: hunt.status === 'active' ? 'rgba(34, 197, 94, 0.05)' : hunt.status === 'paused' ? 'rgba(245, 158, 11, 0.05)' : 'transparent'
                        }}
                      >
                        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>#{hunt.hunt_id}</span>
                        <span>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '5px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            background: hunt.status === 'active' ? 'rgba(34, 197, 94, 0.2)' : 
                                       hunt.status === 'completed' ? 'rgba(59, 130, 246, 0.2)' : 
                                       hunt.status === 'paused' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(107, 114, 128, 0.2)',
                            color: hunt.status === 'active' ? '#22c55e' : 
                                   hunt.status === 'completed' ? '#3b82f6' : 
                                   hunt.status === 'paused' ? '#f59e0b' : '#6b7280'
                          }}>
                            {hunt.status === 'active' ? '🟢 Live' : 
                             hunt.status === 'completed' ? '✅ Done' : 
                             hunt.status === 'paused' ? '⏸️ Paused' : hunt.status}
                          </span>
                        </span>
                        <span style={{ textAlign: 'right', color: 'var(--gold)', fontFamily: "'Space Mono', monospace", fontSize: '0.85rem' }}>
                          ${hunt.start_balance?.toLocaleString()}
                        </span>
                        <span style={{ textAlign: 'right', fontFamily: "'Space Mono', monospace", fontSize: '0.85rem' }}>
                          {hunt.end_balance ? (
                            <span style={{ color: hunt.end_balance > hunt.start_balance ? '#22c55e' : '#ef4444' }}>
                              ${hunt.end_balance?.toLocaleString()}
                            </span>
                          ) : '—'}
                        </span>
                        <span style={{ textAlign: 'center', fontWeight: 600, fontSize: '0.9rem', color: hunt.prediction_count > 0 ? 'var(--text)' : 'var(--text-dim)' }}>
                          {hunt.prediction_count || 0}
                        </span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>
                          {new Date(hunt.created_at).toLocaleDateString()}
                        </span>
                        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          {hunt.status === 'active' && (
                            <>
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/predictions/pause`, {
                                      method: 'POST',
                                      body: JSON.stringify({ huntId: hunt.hunt_id })
                                    });
                                    if (res.ok) {
                                      fetchAdminHunts();
                                      fetchActiveHunt();
                                    }
                                  } catch (e) { alert('Error pausing'); }
                                }}
                                style={{ padding: '0.35rem 0.6rem', background: 'rgba(245, 158, 11, 0.2)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '5px', color: '#f59e0b', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                              >
                                ⏸️ Pause
                              </button>
                              <button
                                onClick={async () => {
                                  const endBalance = prompt('Enter final balance:');
                                  if (!endBalance) return;
                                  try {
                                    const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/predictions/close`, {
                                      method: 'POST',
                                      body: JSON.stringify({ huntId: hunt.hunt_id, endBalance: parseFloat(endBalance) })
                                    });
                                    const data = await res.json();
                                    alert(data.message || 'Hunt closed!');
                                    fetchAdminHunts();
                                    fetchActiveHunt();
                                    fetchAdminClaims();
                                  } catch (e) { alert('Error closing'); }
                                }}
                                style={{ padding: '0.35rem 0.6rem', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '5px', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                              >
                                🏁 End
                              </button>
                            </>
                          )}
                          {hunt.status === 'paused' && (
                            <>
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/predictions/open`, {
                                      method: 'POST',
                                      body: JSON.stringify({ 
                                        huntId: hunt.hunt_id, 
                                        startBalance: hunt.start_balance,
                                        rewards: adminRewards
                                      })
                                    });
                                    if (res.ok) {
                                      fetchAdminHunts();
                                      fetchActiveHunt();
                                    }
                                  } catch (e) { alert('Error resuming'); }
                                }}
                                style={{ padding: '0.35rem 0.6rem', background: 'rgba(34, 197, 94, 0.2)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '5px', color: '#22c55e', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                              >
                                ▶️ Resume
                              </button>
                              <button
                                onClick={async () => {
                                  const endBalance = prompt('Enter final balance:');
                                  if (!endBalance) return;
                                  try {
                                    const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/predictions/close`, {
                                      method: 'POST',
                                      body: JSON.stringify({ huntId: hunt.hunt_id, endBalance: parseFloat(endBalance) })
                                    });
                                    const data = await res.json();
                                    alert(data.message || 'Hunt closed!');
                                    fetchAdminHunts();
                                    fetchActiveHunt();
                                    fetchAdminClaims();
                                  } catch (e) { alert('Error closing'); }
                                }}
                                style={{ padding: '0.35rem 0.6rem', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '5px', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                              >
                                🏁 End
                              </button>
                            </>
                          )}
                          {hunt.status === 'completed' && (
                            <>
                            <button
                              onClick={() => {
                                setShowEditRewardsModal(hunt);
                                setEditRewardsData({
                                  first: hunt.reward_first || '$25 Rainbet Balance',
                                  second: hunt.reward_second || '$15 Rainbet Balance',
                                  third: hunt.reward_third || '$10 Rainbet Balance'
                                });
                              }}
                              style={{ padding: '0.35rem 0.6rem', background: 'rgba(139, 92, 246, 0.2)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '5px', color: '#8b5cf6', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                            >
                              ✏️ Edit Rewards
                            </button>
                            <button
                              onClick={async () => {
                                const endBal = window.prompt(
                                  hunt.end_balance
                                    ? `Fix end balance for Hunt #${hunt.hunt_id} (currently $${parseFloat(hunt.end_balance).toLocaleString()}):\n\nWinners will be recalculated from the new balance. Pending claims from people who are no longer winners get removed automatically.`
                                    : `Hunt #${hunt.hunt_id} has no end balance.\nEnter the final balance to fix and calculate winners:`,
                                  hunt.end_balance != null ? String(hunt.end_balance) : ''
                                );
                                if (endBal === null || endBal === '') return;
                                const parsed = parseFloat(endBal);
                                if (isNaN(parsed)) { alert('Invalid balance'); return; }
                                try {
                                  const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/winners/recalculate/${hunt.hunt_id}`, { method: 'POST', body: JSON.stringify({ endBalance: parsed }) });
                                  const data = await res.json();
                                  if (res.ok) {
                                    let msg = `✅ End balance set to $${parsed.toLocaleString()} — ${data.winnersCount} winner(s) recalculated.`;
                                    if (data.stalePendingClaimsDeleted > 0) msg += `\n🧹 Removed ${data.stalePendingClaimsDeleted} outdated pending claim(s).`;
                                    if (data.paidClaimMismatches?.length) {
                                      msg += `\n\n⚠️ ALREADY PAID to users who are no longer winners — handle manually:\n` +
                                        data.paidClaimMismatches.map(m => `• ${m.display_name || m.username} — rank ${m.rank}, ${m.reward}${m.rainbet_username ? ` (RB: ${m.rainbet_username})` : ''}`).join('\n');
                                    }
                                    alert(msg);
                                    fetchAdminHunts(); fetchActiveHunt(); fetchAdminClaims(); fetchLastCompleted(); fetchAllWinners();
                                  } else { alert(data.error || 'Failed to fix hunt'); }
                                } catch (e) { alert('Error fixing hunt'); }
                              }}
                              style={{ padding: '0.35rem 0.6rem', background: 'rgba(245, 158, 11, 0.2)', border: '1px solid rgba(245, 158, 11, 0.4)', borderRadius: '5px', color: '#f59e0b', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                            >✏️ Fix End Balance</button>
                            <button
                              onClick={async () => {
                                if (!hunt.end_balance) { alert('No end balance set. Use "⚠️ Fix & Calc Winners" first.'); return; }
                                if (!window.confirm(`Recalculate winners for Hunt #${hunt.hunt_id}?\nEnd balance: $${parseFloat(hunt.end_balance).toLocaleString()}\n\nThis will delete existing winners and re-rank.`)) return;
                                try {
                                  const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/winners/recalculate/${hunt.hunt_id}`, { method: 'POST' });
                                  const data = await res.json();
                                  if (res.ok) {
                                    let msg = `✅ Winners recalculated! Found ${data.winnersCount} winner(s).`;
                                    if (data.stalePendingClaimsDeleted > 0) msg += `\n🧹 Removed ${data.stalePendingClaimsDeleted} outdated pending claim(s).`;
                                    if (data.paidClaimMismatches?.length) {
                                      msg += `\n\n⚠️ ALREADY PAID to users who are no longer winners — handle manually:\n` +
                                        data.paidClaimMismatches.map(m => `• ${m.display_name || m.username} — rank ${m.rank}, ${m.reward}${m.rainbet_username ? ` (RB: ${m.rainbet_username})` : ''}`).join('\n');
                                    }
                                    alert(msg);
                                    fetchAdminHunts(); fetchAdminClaims(); fetchLastCompleted(); fetchAllWinners();
                                  }
                                  else { alert(data.error || 'Failed to recalculate'); }
                                } catch (e) { alert('Error recalculating winners'); }
                              }}
                              style={{ padding: '0.35rem 0.6rem', background: 'rgba(34, 197, 94, 0.2)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '5px', color: '#22c55e', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                            >🔁 Recalc Winners</button>
                            </>
                          )}
                          {/* Edit Start Balance - available for all hunts */}
                          <button
                            onClick={async () => {
                              const newBalance = window.prompt(`Edit start balance for Hunt #${hunt.hunt_id}:`, hunt.start_balance?.toString() || '');
                              if (newBalance === null) return;
                              const parsed = parseFloat(newBalance);
                              if (isNaN(parsed)) {
                                alert('Invalid balance');
                                return;
                              }
                              try {
                                const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/hunts/${hunt.hunt_id}/settings`, {
                                  method: 'PUT',
                                  body: JSON.stringify({ startBalance: parsed })
                                });
                                if (res.ok) {
                                  alert('Start balance updated!');
                                  fetchAdminHunts();
                                  fetchActiveHunt();
                                } else {
                                  const data = await res.json();
                                  alert(data.error || 'Failed to update');
                                }
                              } catch (e) { alert('Error updating'); }
                            }}
                            style={{ padding: '0.35rem 0.6rem', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '5px', color: '#3b82f6', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                          >
                            💰 Edit Start
                          </button>
                          {/* Delete Hunt - available for all hunts */}
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete Hunt #${hunt.hunt_id}? This will also delete all ${hunt.prediction_count || 0} predictions.`)) return;
                              try {
                                const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/hunts/${hunt.hunt_id}`, { method: 'DELETE' });
                                if (res.ok) {
                                  alert('Hunt deleted');
                                  fetchAdminHunts();
                                  fetchActiveHunt();
                                } else {
                                  const data = await res.json();
                                  alert(data.error || 'Failed to delete');
                                }
                              } catch (e) { alert('Error deleting'); }
                            }}
                            style={{ padding: '0.35rem 0.6rem', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '5px', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              </>
            )}
            
            {adminTab === 'rewards' && (
              <div className="card">
                <div className="card-header">
                  <div className="card-icon">💰</div>
                  <div className="card-title">Reward Claims</div>
                  {adminClaims.length > 0 && (
                    <span style={{ marginLeft: 'auto', padding: '0.3rem 0.6rem', background: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600 }}>
                      {adminClaims.length} pending
                    </span>
                  )}
                </div>
                
                {/* Sort Controls */}
                {adminClaims.length > 0 && (
                  <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>Sort by:</span>
                    <SortButtons
                      sortConfig={pendingRewardsSort}
                      setSortConfig={setPendingRewardsSort}
                      options={[
                        { value: 'hunt_id', label: 'Hunt ID' },
                        { value: 'rank', label: 'Rank' },
                        { value: 'reward_amount', label: 'Reward Amount' },
                        { value: 'username', label: 'Username' }
                      ]}
                    />
                  </div>
                )}
                
                {adminClaims.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-dim)' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✅</div>
                    <p>All rewards have been paid!</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    {/* Header */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '2fr 80px 60px 1.5fr 1fr 150px', 
                      padding: '0.75rem 1rem', 
                      background: 'rgba(0,0,0,0.3)',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      color: 'var(--text-dim)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      minWidth: '700px'
                    }}>
                      <span>User</span>
                      <span>Hunt</span>
                      <span>Rank</span>
                      <span>Rainbet User</span>
                      <span>Reward</span>
                      <span>Action</span>
                    </div>
                    
                    {/* Rows */}
                    {sortData(adminClaims, pendingRewardsSort).map((claim, idx) => (
                      <div 
                        key={claim.id} 
                        style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '2fr 80px 60px 1.5fr 1fr 150px', 
                          padding: '0.85rem 1rem', 
                          borderBottom: idx < adminClaims.length - 1 ? '1px solid var(--border)' : 'none',
                          alignItems: 'center',
                          minWidth: '700px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                          <img 
                            src={claim.profile_image_url || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} 
                            alt="" 
                            style={{ width: 32, height: 32, borderRadius: '50%' }} 
                          />
                          <span style={{ fontWeight: 500 }}>{claim.display_name || claim.username}</span>
                        </div>
                        <span style={{ fontWeight: 500 }}>#{claim.hunt_id}</span>
                        <span style={{ fontSize: '1.1rem' }}>{['🥇', '🥈', '🥉'][claim.rank - 1] || `#${claim.rank}`}</span>
                        <span style={{ 
                          fontFamily: "'Space Mono', monospace", 
                          color: claim.rainbet_username ? '#22c55e' : '#ef4444',
                          fontSize: '0.9rem'
                        }}>
                          {claim.rainbet_username || '❌ Not linked'}
                        </span>
                        <span style={{ fontWeight: 600, color: 'var(--gold)' }}>{claim.actual_reward || claim.reward}</span>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button 
                            onClick={() => markClaimPaid(claim.id)}
                            disabled={!claim.rainbet_username}
                            style={{ 
                              padding: '0.5rem 0.75rem', 
                              background: claim.rainbet_username ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'rgba(107, 114, 128, 0.3)', 
                              border: 'none', 
                              borderRadius: '8px', 
                              color: 'white', 
                              cursor: claim.rainbet_username ? 'pointer' : 'not-allowed',
                              fontFamily: 'inherit',
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              opacity: claim.rainbet_username ? 1 : 0.5
                            }}
                          >
                            💰 Pay
                          </button>
                          <button
                            onClick={() => deletePredictionWin(claim.winner_id, false)}
                            style={{ 
                              padding: '0.5rem 0.75rem', 
                              background: 'rgba(239, 68, 68, 0.2)', 
                              border: '1px solid rgba(239, 68, 68, 0.4)', 
                              borderRadius: '8px', 
                              color: '#ef4444', 
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              fontWeight: 600,
                              fontSize: '0.8rem'
                            }}
                            title="Delete this pending reward"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {adminTab === 'giveaways' && (
              <div className="card">
                <div className="card-header">
                  <div className="card-icon">🎁</div>
                  <div className="card-title">Pending Giveaways</div>
                </div>
                
                {/* Add New Giveaway Form */}
                <div style={{ padding: '1.25rem', borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,0.15)' }}>
                  <h4 style={{ marginBottom: '1rem', fontSize: '0.9rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Add New Giveaway</h4>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: 1, minWidth: '150px' }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.35rem' }}>Rainbet Username</label>
                      <input
                        value={newGiveaway.rainbetUsername}
                        onChange={e => setNewGiveaway(g => ({...g, rainbetUsername: e.target.value}))}
                        placeholder="username"
                        style={{ width: '100%', padding: '0.65rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit' }}
                      />
                    </div>
                    <div style={{ width: '120px' }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.35rem' }}>Amount ($)</label>
                      <input
                        type="number"
                        value={newGiveaway.amount}
                        onChange={e => setNewGiveaway(g => ({...g, amount: e.target.value}))}
                        placeholder="0"
                        style={{ width: '100%', padding: '0.65rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit' }}
                      />
                    </div>
                    <div style={{ flex: 2, minWidth: '200px' }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.35rem' }}>Description (optional)</label>
                      <input
                        value={newGiveaway.description}
                        onChange={e => setNewGiveaway(g => ({...g, description: e.target.value}))}
                        placeholder="Stream giveaway, leaderboard bonus, etc."
                        style={{ width: '100%', padding: '0.65rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit' }}
                      />
                    </div>
                    <button 
                      onClick={addGiveaway} 
                      style={{ 
                        padding: '0.65rem 1.25rem', 
                        background: 'linear-gradient(135deg, #22c55e, #16a34a)', 
                        border: 'none', 
                        borderRadius: '8px', 
                        color: 'white', 
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}
                    >
                      ➕ Add Giveaway
                    </button>
                  </div>
                </div>
                
                {/* Giveaways List */}
                {adminGiveaways.filter(g => g.status === 'pending').length > 0 && (
                  <>
                    <div style={{ padding: '0.75rem 1rem', background: 'rgba(245, 158, 11, 0.1)', fontSize: '0.85rem', fontWeight: 600, color: '#f59e0b' }}>
                      ⏳ Pending Giveaways ({adminGiveaways.filter(g => g.status === 'pending').length})
                    </div>
                    {adminGiveaways.filter(g => g.status === 'pending').map((giveaway, idx) => (
                      <div 
                        key={giveaway.id} 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '1rem', 
                          padding: '1rem', 
                          borderBottom: '1px solid var(--border)'
                        }}
                      >
                        <span style={{ fontFamily: "'Space Mono', monospace", color: '#22c55e', fontWeight: 500, flex: 1 }}>
                          {giveaway.rainbet_username}
                        </span>
                        <span style={{ fontWeight: 700, color: 'var(--gold)', fontSize: '1.1rem' }}>${giveaway.amount}</span>
                        <span style={{ flex: 2, color: 'var(--text-dim)', fontSize: '0.9rem' }}>{giveaway.description || '—'}</span>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button 
                            onClick={() => markGiveawayPaid(giveaway.id)} 
                            style={{ 
                              padding: '0.5rem 1rem', 
                              background: 'linear-gradient(135deg, #22c55e, #16a34a)', 
                              border: 'none', 
                              borderRadius: '6px', 
                              color: 'white', 
                              cursor: 'pointer', 
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              fontFamily: 'inherit'
                            }}
                          >
                            💰 Mark Paid
                          </button>
                          <button 
                            onClick={() => deleteGiveaway(giveaway.id)} 
                            style={{ 
                              padding: '0.5rem 0.75rem', 
                              background: 'rgba(239, 68, 68, 0.2)', 
                              border: '1px solid rgba(239, 68, 68, 0.3)', 
                              borderRadius: '6px', 
                              color: '#ef4444', 
                              cursor: 'pointer', 
                              fontSize: '0.85rem' 
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                
                {adminGiveaways.filter(g => g.status === 'pending').length === 0 && (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-dim)' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎁</div>
                    No pending giveaways
                  </div>
                )}
              </div>
            )}
            
            {/* Paid Rewards Tab */}
            {adminTab === 'paidRewards' && (
              <div className="card">
                <div className="card-header">
                  <div className="card-icon">✅</div>
                  <div className="card-title">Paid Claims ({adminPaidRewardsTotal})</div>
                </div>
                
                {/* Search and Sort */}
                <div style={{ padding: '1rem', borderBottom: '1px solid var(--border)', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', justifyContent: 'space-between' }}>
                  <input
                    type="text"
                    placeholder="Search by username..."
                    value={adminPaidRewardsSearch}
                    onChange={(e) => {
                      setAdminPaidRewardsSearch(e.target.value);
                      setAdminPaidRewardsPage(1);
                      fetchAdminPaidClaims(e.target.value, 1);
                    }}
                    style={{ flex: 1, maxWidth: 300, padding: '0.65rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>Sort by:</span>
                    <SortButtons
                      sortConfig={paidRewardsSort}
                      setSortConfig={setPaidRewardsSort}
                      options={[
                        { value: 'paid_at', label: 'Date Paid' },
                        { value: 'hunt_id', label: 'Hunt ID' },
                        { value: 'rank', label: 'Rank' },
                        { value: 'reward_amount', label: 'Amount' },
                        { value: 'username', label: 'Username' }
                      ]}
                    />
                  </div>
                </div>
                
                {adminPaidClaims.length > 0 ? (
                  <>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,0.2)' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>User</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Rainbet</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Hunt</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center' }}>Rank</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right' }}>Amount</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Paid</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortData(adminPaidClaims, paidRewardsSort).map(claim => (
                            <tr key={claim.id} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ padding: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {claim.profile_image_url && <img src={claim.profile_image_url} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} />}
                                {claim.display_name || claim.username}
                              </td>
                              <td style={{ padding: '0.75rem', fontFamily: 'monospace', color: '#22c55e' }}>{claim.rainbet_username}</td>
                              <td style={{ padding: '0.75rem' }}>#{claim.hunt_id}</td>
                              <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                {claim.rank === 1 ? '🥇' : claim.rank === 2 ? '🥈' : '🥉'}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600, color: 'var(--gold)' }}>${claim.reward_amount}</td>
                              <td style={{ padding: '0.75rem', fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                                {claim.paid_at ? new Date(claim.paid_at).toLocaleDateString() : '-'}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                <button
                                  onClick={() => deletePredictionWin(claim.id, true)}
                                  style={{ padding: '0.4rem 0.75rem', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '6px', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
                                  title="Delete this paid reward record"
                                >
                                  🗑️
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {adminPaidRewardsTotal > 50 && (
                      <div style={{ padding: '1rem', display: 'flex', justifyContent: 'center', gap: '0.5rem', alignItems: 'center' }}>
                        <button 
                          onClick={() => { setAdminPaidRewardsPage(p => p - 1); fetchAdminPaidClaims(adminPaidRewardsSearch, adminPaidRewardsPage - 1); }}
                          disabled={adminPaidRewardsPage === 1}
                          style={{ padding: '0.5rem 1rem', background: adminPaidRewardsPage === 1 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'var(--text)', cursor: adminPaidRewardsPage === 1 ? 'not-allowed' : 'pointer' }}
                        >← Prev</button>
                        <span style={{ color: 'var(--text-dim)' }}>Page {adminPaidRewardsPage} of {Math.ceil(adminPaidRewardsTotal / 50)}</span>
                        <button 
                          onClick={() => { setAdminPaidRewardsPage(p => p + 1); fetchAdminPaidClaims(adminPaidRewardsSearch, adminPaidRewardsPage + 1); }}
                          disabled={adminPaidRewardsPage >= Math.ceil(adminPaidRewardsTotal / 50)}
                          style={{ padding: '0.5rem 1rem', background: adminPaidRewardsPage >= Math.ceil(adminPaidRewardsTotal / 50) ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'var(--text)', cursor: adminPaidRewardsPage >= Math.ceil(adminPaidRewardsTotal / 50) ? 'not-allowed' : 'pointer' }}
                        >Next →</button>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-dim)' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
                    No paid rewards yet
                  </div>
                )}
              </div>
            )}
            
            {/* Rewards Config Tab - admin-editable wager tiers for lifetime + monthly */}
            {adminTab === 'siteSettings' && (
              <>
                <div className="header">
                  <h1 className="header-title">🌐 <span>Site Settings</span></h1>
                  <p className="header-sub">Site-wide configuration: counters, embeds, and other display settings.</p>
                </div>

                {/* Default Prediction Prizes */}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <div className="card-header">
                    <div className="card-icon">🏆</div>
                    <div className="card-title">Default Prediction Prizes</div>
                  </div>
                  <div style={{ padding: '1.25rem' }}>
                    <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                      Used automatically when a new prediction hunt is opened via the admin panel or the <code style={{ background: 'rgba(0,0,0,0.3)', padding: '0.1rem 0.35rem', borderRadius: '4px', fontSize: '0.85rem' }}>!gtb</code> chat command. Override per-hunt in the Predictions tab.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
                      {[
                        { key: 'default_reward_first',  label: '🥇 1st Place' },
                        { key: 'default_reward_second', label: '🥈 2nd Place' },
                        { key: 'default_reward_third',  label: '🥉 3rd Place' },
                      ].map(({ key, label }) => (
                        <div key={key}>
                          <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>{label}</label>
                          <input
                            type="text"
                            value={siteSettings[key] || ''}
                            onChange={(e) => setSiteSettings(prev => ({ ...prev, [key]: e.target.value }))}
                            placeholder={key === 'default_reward_first' ? '$50 Rainbet Balance' : key === 'default_reward_second' ? '$30 Rainbet Balance' : '$20 Rainbet Balance'}
                            style={{ width: '100%', padding: '0.6rem 0.75rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.9rem', boxSizing: 'border-box' }}
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={async () => {
                        const token = getAuthToken();
                        const pairs = [
                          ['default_reward_first',  siteSettings.default_reward_first  || '$50 Rainbet Balance'],
                          ['default_reward_second', siteSettings.default_reward_second || '$30 Rainbet Balance'],
                          ['default_reward_third',  siteSettings.default_reward_third  || '$20 Rainbet Balance'],
                        ];
                        try {
                          for (const [key, value] of pairs) {
                            const res = await fetch(`${BACKEND_API_URL}/api/admin/settings`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ key, value })
                            });
                            if (!res.ok) { const err = await res.json().catch(() => ({})); alert(err.error || `Failed to save ${key}`); return; }
                          }
                          setAdminRewards({ first: siteSettings.default_reward_first || '$50 Rainbet Balance', second: siteSettings.default_reward_second || '$30 Rainbet Balance', third: siteSettings.default_reward_third || '$20 Rainbet Balance' });
                          alert('✅ Default prizes saved!');
                        } catch (e) { alert('Error saving default prizes'); }
                      }}
                      style={{ padding: '0.65rem 1.4rem', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', border: 'none', borderRadius: '8px', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
                    >💾 Save Default Prizes</button>
                  </div>
                </div>

                {/* Halloween theme (temporary seasonal skin) */}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <div className="card-header">
                    <div className="card-icon">🎃</div>
                    <div className="card-title">Halloween Theme</div>
                  </div>
                  <div style={{ padding: '1.25rem' }}>
                    <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                      Spooky skin: tinted background, moon, bats, fog, pumpkin Join button. <strong>Auto</strong> shows it Oct 24 – Nov 1 (Central) and turns it off by itself afterwards.
                    </p>
                    <div style={{ display: 'inline-flex', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)', borderRadius: '999px', padding: '0.25rem', gap: '0.25rem' }}>
                      {[
                        { key: 'auto', label: 'Auto' },
                        { key: 'on', label: 'Always On' },
                        { key: 'off', label: 'Off' }
                      ].map(mode => {
                        const current = ['on', 'off'].includes(siteSettings.halloween_theme) ? siteSettings.halloween_theme : 'auto';
                        const isActive = current === mode.key;
                        return (
                          <button
                            key={mode.key}
                            onClick={async () => {
                              if (isActive) return;
                              const token = getAuthToken();
                              const res = await fetch(`${BACKEND_API_URL}/api/admin/settings`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                body: JSON.stringify({ key: 'halloween_theme', value: mode.key })
                              });
                              if (res.ok) {
                                setSiteSettings(prev => ({ ...prev, halloween_theme: mode.key }));
                              } else {
                                const err = await res.json().catch(() => ({}));
                                alert(err.error || 'Could not save');
                              }
                            }}
                            style={{
                              padding: '0.55rem 1.5rem',
                              background: isActive ? 'linear-gradient(135deg, #ea580c, #c2410c)' : 'transparent',
                              color: isActive ? '#fff' : 'var(--text-dim)',
                              border: 'none',
                              borderRadius: '999px',
                              cursor: isActive ? 'default' : 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              fontFamily: 'inherit'
                            }}
                          >
                            {mode.label} {isActive && '✓'}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Total Given Away counter */}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <div className="card-header">
                    <div className="card-icon">💰</div>
                    <div className="card-title">Total Given Away Counter</div>
                  </div>
                  <div style={{ padding: '1.25rem' }}>
                    <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                      Sets the green counter shown in the sidebar. Animates from 0 → target on first page load. Set to 0 to hide the counter entirely.
                    </p>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 200px' }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>
                          Amount (USD)
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0 0.85rem' }}>
                          <span style={{ color: '#22c55e', fontWeight: 700, marginRight: '0.4rem' }}>$</span>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={siteSettings.total_given_away ?? '0'}
                            onChange={(e) => setSiteSettings(prev => ({ ...prev, total_given_away: e.target.value }))}
                            style={{ flex: 1, padding: '0.6rem 0', background: 'transparent', border: 'none', outline: 'none', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.95rem', fontWeight: 600 }}
                            placeholder="e.g. 50000"
                          />
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          const token = getAuthToken();
                          const value = String(siteSettings.total_given_away || '0');
                          const res = await fetch(`${BACKEND_API_URL}/api/admin/settings`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ key: 'total_given_away', value })
                          });
                          if (res.ok) {
                            giveawayAnimatedOnceRef.current = false;
                            setAnimatedGiveaway(0);
                            setSiteSettings(prev => ({ ...prev, total_given_away: value }));
                          } else {
                            const err = await res.json().catch(() => ({}));
                            alert(err.error || 'Could not save');
                          }
                        }}
                        style={{ padding: '0.65rem 1.4rem', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', border: 'none', borderRadius: '8px', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(34, 197, 94, 0.3)' }}
                      >
                        Save & Re-animate
                      </button>
                    </div>
                  </div>
                </div>

                {/* Hide Sidebar Tabs — for tabs that aren't ready for users yet */}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <div className="card-header">
                    <div className="card-icon">🙈</div>
                    <div className="card-title">Hide Sidebar Tabs</div>
                  </div>
                  <div style={{ padding: '1.25rem' }}>
                    <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                      Hide tabs from non-admin users. Admins still see hidden tabs in the sidebar with a <span style={{ padding: '0.05rem 0.4rem', background: 'rgba(245, 158, 11, 0.18)', color: '#f59e0b', borderRadius: '999px', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.08em', border: '1px solid rgba(245, 158, 11, 0.35)' }}>HIDDEN</span> pill so you can test pages before launch.
                    </p>
                    {(() => {
                      const allTabs = [
                        { key: 'home', label: 'Home' },
                        { key: 'leaderboard', label: 'Leaderboard' },
                        { key: 'rewards', label: 'Rewards' },
                        { key: 'instructions', label: 'Instructions' },
                        { key: 'challenges', label: 'Challenges' },
                        { key: 'slotrequests', label: 'Slot Requests' },
                        { key: 'pointsshop', label: 'Points Shop' },
                        { key: 'bonushunts', label: 'Bonus Hunts' },
                        { key: 'predictions', label: 'Predictions' },
                        { key: 'games', label: 'Games' },
                        { key: 'battles', label: 'Battles' },
                        { key: 'socials', label: 'Socials' }
                      ];
                      const currentHidden = String(siteSettings.hidden_nav_pages || '')
                        .split(/[\s,]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
                      const hiddenSet = new Set(currentHidden);
                      const toggleTab = (key) => {
                        const next = new Set(hiddenSet);
                        if (next.has(key)) next.delete(key); else next.add(key);
                        setSiteSettings(prev => ({ ...prev, hidden_nav_pages: Array.from(next).join(',') }));
                      };
                      return (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.5rem', marginBottom: '1rem' }}>
                            {allTabs.map(t => {
                              const hidden = hiddenSet.has(t.key);
                              return (
                                <label
                                  key={t.key}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.55rem',
                                    padding: '0.6rem 0.8rem',
                                    background: hidden ? 'rgba(245, 158, 11, 0.08)' : 'rgba(0,0,0,0.25)',
                                    border: `1px solid ${hidden ? 'rgba(245, 158, 11, 0.35)' : 'var(--border)'}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '0.88rem',
                                    fontWeight: 600,
                                    userSelect: 'none',
                                    transition: 'all 0.15s'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={hidden}
                                    onChange={() => toggleTab(t.key)}
                                    style={{ cursor: 'pointer', accentColor: '#f59e0b' }}
                                  />
                                  <span style={{ color: hidden ? '#f59e0b' : 'var(--text)' }}>{t.label}</span>
                                </label>
                              );
                            })}
                          </div>
                          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={async () => {
                                const token = getAuthToken();
                                const value = String(siteSettings.hidden_nav_pages || '');
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/settings`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ key: 'hidden_nav_pages', value })
                                });
                                if (res.ok) {
                                  setSiteSettings(prev => ({ ...prev, hidden_nav_pages: value }));
                                  alert('Hidden tabs saved.');
                                } else {
                                  const err = await res.json().catch(() => ({}));
                                  alert(err.error || 'Could not save');
                                }
                              }}
                              style={{ padding: '0.65rem 1.4rem', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', border: 'none', borderRadius: '8px', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(245, 158, 11, 0.3)' }}
                            >
                              Save Hidden Tabs
                            </button>
                            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                              {hiddenSet.size} hidden · {allTabs.length - hiddenSet.size} visible
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* YouTube Shorts — drives the Socials page */}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <div className="card-header">
                    <div className="card-icon">
                      <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 22, height: 22, color: '#ff0000' }}>
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </div>
                    <div className="card-title">YouTube Shorts (Socials Page)</div>
                  </div>
                  <div style={{ padding: '1.25rem' }}>
                    <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginBottom: '0.85rem' }}>
                      Add YouTube Shorts to feature on the <code style={{ background: 'rgba(255,255,255,0.05)', padding: '0.1rem 0.35rem', borderRadius: '3px' }}>/socials</code> page. One URL or ID per line. Accepts full URLs like <code style={{ background: 'rgba(255,255,255,0.05)', padding: '0.1rem 0.35rem', borderRadius: '3px' }}>https://youtube.com/shorts/VIDEO_ID</code> or bare 11-character IDs. Maximum 50 entries.
                    </p>
                    <textarea
                      value={siteSettings.youtube_shorts_urls || ''}
                      onChange={(e) => setSiteSettings(prev => ({ ...prev, youtube_shorts_urls: e.target.value }))}
                      placeholder={'https://youtube.com/shorts/dQw4w9WgXcQ\nhttps://youtu.be/abcd1234567\nXYZ9876ABCD'}
                      style={{ width: '100%', minHeight: 160, padding: '0.7rem 0.85rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'Space Mono, monospace', fontSize: '0.82rem', resize: 'vertical', outline: 'none' }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginTop: '0.85rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={async () => {
                          const token = getAuthToken();
                          const value = String(siteSettings.youtube_shorts_urls || '');
                          const res = await fetch(`${BACKEND_API_URL}/api/admin/settings`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ key: 'youtube_shorts_urls', value })
                          });
                          if (res.ok) {
                            setSiteSettings(prev => ({ ...prev, youtube_shorts_urls: value }));
                            alert('YouTube Shorts saved.');
                          } else {
                            const err = await res.json().catch(() => ({}));
                            alert(err.error || 'Could not save');
                          }
                        }}
                        style={{ padding: '0.65rem 1.4rem', background: 'linear-gradient(135deg, #ff0000, #c80000)', color: '#fff', border: 'none', borderRadius: '8px', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(255, 0, 0, 0.3)' }}
                      >
                        Save YouTube Shorts
                      </button>
                      <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                        {(() => {
                          const raw = String(siteSettings.youtube_shorts_urls || '').trim();
                          const count = raw ? raw.split(/[\n,]/).filter(s => s.trim()).length : 0;
                          return `${count} short${count === 1 ? '' : 's'} configured`;
                        })()}
                      </span>
                      <a href="/socials" target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto', fontSize: '0.82rem', color: '#22d3ee', textDecoration: 'none' }}>
                        Preview Socials page →
                      </a>
                    </div>
                  </div>
                </div>
              </>
            )}

            {adminTab === 'monthlyWagers' && (
              <div>
                {(() => {
                  const nowMonth = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; })();
                  const monthVal = monthlyWagers.month || nowMonth;
                  const fetchMonthlyWagers = async (month, force) => {
                    setMonthlyWagers(prev => ({ ...prev, month, loading: true, error: null }));
                    try {
                      const token = getAuthToken();
                      const [y, m] = month.split('-').map(Number);
                      const pm = m === 1 ? 12 : m - 1;
                      const py = m === 1 ? y - 1 : y;
                      const prevMonth = `${py}-${String(pm).padStart(2, '0')}`;
                      const qs = force ? '&refresh=1' : '';
                      const [curRes, prevRes] = await Promise.all([
                        fetch(`${BACKEND_API_URL}/api/admin/monthly-wagers?month=${month}${qs}`, { headers: { 'Authorization': `Bearer ${token}` } }),
                        fetch(`${BACKEND_API_URL}/api/admin/monthly-wagers?month=${prevMonth}${qs}`, { headers: { 'Authorization': `Bearer ${token}` } })
                      ]);
                      if (!curRes.ok) { const e = await curRes.json().catch(() => ({})); throw new Error(e.error || 'Failed to load'); }
                      const cur = await curRes.json();
                      const prev = prevRes.ok ? await prevRes.json() : { affiliates: [] };
                      const prevMap = new Map((prev.affiliates || []).map(a => [(a.username || '').toLowerCase(), a.wagered]));
                      const rows = (cur.affiliates || []).map(a => ({ ...a, prev_wagered: prevMap.get((a.username || '').toLowerCase()) || 0 }));
                      setMonthlyWagers(p => ({ ...p, month, loading: false, data: { ...cur, affiliates: rows }, prevMonth }));
                    } catch (e) {
                      setMonthlyWagers(p => ({ ...p, loading: false, error: e.message, data: null }));
                    }
                  };
                  // Auto-load on first view (guarded to avoid render loop).
                  if (!monthlyWagers.data && !monthlyWagers.loading && !monthlyWagers.error) {
                    fetchMonthlyWagers(monthVal, false);
                  }
                  const data = monthlyWagers.data;
                  const search = (monthlyWagers.search || '').trim().toLowerCase();
                  const filtered = (data?.affiliates || []).filter(a => {
                    if (!search) return true;
                    return (a.username || '').toLowerCase().includes(search)
                      || (a.site_username || '').toLowerCase().includes(search);
                  });
                  return (
                    <>
                      <div className="card" style={{ marginBottom: '1.25rem' }}>
                        <div className="card-header">
                          <div className="card-icon">📊</div>
                          <div className="card-title">Monthly Wager Analytics</div>
                        </div>
                        <div style={{ padding: '1.25rem' }}>
                          <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                            Rainbet affiliate wager totals for the selected month (code <strong style={{ color: '#fbbf24' }}>CAM</strong>). Useful for VIP / lossback decisions. Cached 5 minutes.
                          </p>
                          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            <input
                              type="month"
                              value={monthVal}
                              max={nowMonth}
                              onChange={(e) => { const mv = e.target.value; if (mv) fetchMonthlyWagers(mv, false); }}
                              style={{ padding: '0.55rem 0.85rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.9rem', colorScheme: 'dark' }}
                            />
                            <input
                              type="text"
                              placeholder="🔍 Search username…"
                              value={monthlyWagers.search || ''}
                              onChange={(e) => setMonthlyWagers(p => ({ ...p, search: e.target.value }))}
                              style={{ flex: '1 1 200px', minWidth: 0, padding: '0.55rem 0.85rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.9rem' }}
                            />
                            <button
                              onClick={() => fetchMonthlyWagers(monthVal, true)}
                              disabled={monthlyWagers.loading}
                              style={{ padding: '0.55rem 1.2rem', background: 'rgba(34, 211, 238, 0.15)', color: '#22d3ee', border: '1px solid rgba(34, 211, 238, 0.3)', borderRadius: '8px', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 600, cursor: monthlyWagers.loading ? 'default' : 'pointer', opacity: monthlyWagers.loading ? 0.5 : 1 }}
                            >{monthlyWagers.loading ? 'Loading…' : '↻ Refresh'}</button>
                          </div>

                          {data && (
                            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '1.25rem' }}>
                              <div>
                                <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Total Wagered</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#22c55e', fontFamily: "'Space Mono', monospace" }}>${Number(data.total_wagered || 0).toLocaleString()}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Wagerers</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text)', fontFamily: "'Space Mono', monospace" }}>{(data.affiliates || []).length.toLocaleString()}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Period</div>
                                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-dim)', fontFamily: "'Space Mono', monospace", marginTop: '0.4rem' }}>{data.start} → {data.end}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {monthlyWagers.error && (
                        <div className="card" style={{ padding: '1.5rem', textAlign: 'center', color: '#ef4444' }}>
                          {monthlyWagers.error}
                        </div>
                      )}

                      {data && !monthlyWagers.error && (
                        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                          <div style={{ overflowX: 'auto' }}>
                            <div style={{ minWidth: 620 }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '60px minmax(0, 1.2fr) minmax(0, 1.2fr) 140px 140px', gap: '0.5rem', padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--border)', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)' }}>
                                <div>Rank</div>
                                <div>Site User</div>
                                <div>Rainbet User</div>
                                <div style={{ textAlign: 'right' }}>This Month</div>
                                <div style={{ textAlign: 'right' }}>Prev Month</div>
                              </div>
                              {filtered.length === 0 ? (
                                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)' }}>
                                  {search ? 'No users match your search.' : 'No wagers recorded for this month.'}
                                </div>
                              ) : filtered.map((a, i) => {
                                const rank = (data.affiliates || []).indexOf(a) + 1;
                                return (
                                  <div key={(a.username || '') + i} style={{ display: 'grid', gridTemplateColumns: '60px minmax(0, 1.2fr) minmax(0, 1.2fr) 140px 140px', gap: '0.5rem', alignItems: 'center', padding: '0.7rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
                                    <div style={{ color: rank <= 3 ? '#fbbf24' : 'var(--text-dim)', fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>#{rank}</div>
                                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: a.site_username ? 'var(--text)' : 'var(--text-muted)', fontWeight: a.site_username ? 600 : 400 }}>
                                      {a.site_username || '—'}
                                    </div>
                                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-dim)' }}>{a.rainbet_username || a.username}<UsSiteChip site={a.site} /></div>
                                    <div style={{ textAlign: 'right', color: '#22c55e', fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>${Number(a.wagered || 0).toLocaleString()}</div>
                                    <div style={{ textAlign: 'right', color: 'var(--text-dim)', fontFamily: "'Space Mono', monospace" }}>${Number(a.prev_wagered || 0).toLocaleString()}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          {data.fetched_at && (
                            <div style={{ padding: '0.6rem 1.25rem', fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'right' }}>
                              Fetched {new Date(data.fetched_at).toLocaleString()}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {adminTab === 'rewardsConfig' && (
              <div>
                {(() => {
                  const fetchAdminTiers = async () => {
                    setAdminTiersLoading(true);
                    try {
                      const token = getAuthToken();
                      const res = await fetch(`${BACKEND_API_URL}/api/admin/reward-tiers`, { headers: { 'Authorization': `Bearer ${token}` } });
                      if (res.ok) { const d = await res.json(); setAdminTiers(d.tiers || []); }
                    } catch (e) { console.error(e); } finally { setAdminTiersLoading(false); }
                  };
                  if (!adminTiers.length && !adminTiersLoading) fetchAdminTiers();
                  return null;
                })()}

                {/* Active mode toggle - controls what users see */}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <div className="card-header">
                    <div className="card-icon">🎛️</div>
                    <div className="card-title">Active Reward Mode</div>
                  </div>
                  <div style={{ padding: '1.25rem' }}>
                    <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                      Choose which tier set is shown on the public Rewards page. Only one mode is active at a time.
                    </p>
                    <div style={{ display: 'inline-flex', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)', borderRadius: '999px', padding: '0.25rem', gap: '0.25rem' }}>
                      {[
                        { key: 'lifetime', label: 'Lifetime' },
                        { key: 'monthly', label: 'Monthly' }
                      ].map(mode => {
                        const isActive = (siteSettings.reward_mode === mode.key);
                        return (
                          <button
                            key={mode.key}
                            onClick={async () => {
                              if (isActive) return;
                              const token = getAuthToken();
                              const res = await fetch(`${BACKEND_API_URL}/api/admin/settings`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                body: JSON.stringify({ key: 'reward_mode', value: mode.key })
                              });
                              if (res.ok) {
                                setSiteSettings(prev => ({ ...prev, reward_mode: mode.key }));
                              } else {
                                const err = await res.json().catch(() => ({}));
                                alert(err.error || 'Could not save');
                              }
                            }}
                            style={{
                              padding: '0.55rem 1.5rem',
                              background: isActive ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'transparent',
                              color: isActive ? '#fff' : 'var(--text-dim)',
                              border: 'none',
                              borderRadius: '999px',
                              cursor: isActive ? 'default' : 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              fontFamily: 'inherit'
                            }}
                          >
                            {mode.label} {isActive && '✓'}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {[
                  { mode: 'lifetime', label: 'Lifetime Rewards' },
                  { mode: 'monthly', label: 'Monthly Rewards' }
                ].map(section => (
                  <div key={section.mode} className="card" style={{ marginBottom: '1.5rem' }}>
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div className="card-icon">{section.mode === 'lifetime' ? '💎' : '📅'}</div>
                        <div className="card-title">{section.label}</div>
                      </div>
                      <button
                        onClick={() => setEditingTier({ mode: section.mode, rank_order: (adminTiers.filter(t => t.mode === section.mode).length + 1), name: '', reward_amount: 0, wager_requirement: 0, color: '#e5e7eb', active: true })}
                        style={{ padding: '0.5rem 1rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                      >
                        + Add Tier
                      </button>
                    </div>
                    <div style={{ padding: 0 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '40px minmax(0, 1fr) 80px 120px 70px 190px', gap: '0.5rem', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)' }}>
                        <div>Order</div>
                        <div>Name</div>
                        <div>Reward $</div>
                        <div>Wager Req $</div>
                        <div>Active</div>
                        <div style={{ textAlign: 'right' }}>Actions</div>
                      </div>
                      {adminTiers.filter(t => t.mode === section.mode).sort((a,b) => (a.rank_order ?? 0) - (b.rank_order ?? 0) || a.id - b.id).map(t => (
                        <div key={t.id} style={{ display: 'grid', gridTemplateColumns: '40px minmax(0, 1fr) 80px 120px 70px 190px', gap: '0.5rem', alignItems: 'center', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', opacity: t.active ? 1 : 0.5 }}>
                          <div style={{ color: 'var(--text-dim)' }}>{t.rank_order}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, overflow: 'hidden' }}>
                            <span style={{ width: 12, height: 12, borderRadius: 3, background: t.color, display: 'inline-block', border: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }} />
                            <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</strong>
                          </div>
                          <div style={{ color: '#22c55e' }}>${Number(t.reward_amount).toLocaleString()}</div>
                          <div style={{ color: '#22c55e' }}>${Number(t.wager_requirement).toLocaleString()}</div>
                          <div style={{ fontSize: '0.8rem', color: t.active ? '#22c55e' : 'var(--text-muted)' }}>{t.active ? 'Active' : 'Disabled'}</div>
                          <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => setEditingTier({ ...t, reward_amount: Number(t.reward_amount), wager_requirement: Number(t.wager_requirement) })}
                              style={{ padding: '0.35rem 0.75rem', background: 'rgba(255,255,255,0.08)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit' }}
                            >Edit</button>
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Disable "${t.name}"? Users will no longer see it. Historical claims preserved.`)) return;
                                const token = getAuthToken();
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/reward-tiers/${t.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                                if (res.ok) {
                                  setAdminTiers(adminTiers.map(x => x.id === t.id ? { ...x, active: false } : x));
                                  fetchRewardTiers();
                                }
                              }}
                              style={{ padding: '0.35rem 0.75rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', fontSize: '0.75rem', cursor: t.active ? 'pointer' : 'default', fontFamily: 'inherit', opacity: t.active ? 1 : 0.5 }}
                            >{t.active ? 'Disable' : 'Disabled'}</button>
                            {!t.active && (
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Permanently delete tier "${t.name}"? This cannot be undone.`)) return;
                                  const token = getAuthToken();
                                  const res = await fetch(`${BACKEND_API_URL}/api/admin/reward-tiers/${t.id}?hard=1`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                                  if (res.ok) {
                                    setAdminTiers(adminTiers.filter(x => x.id !== t.id));
                                    fetchRewardTiers();
                                  } else {
                                    const err = await res.json().catch(() => ({}));
                                    alert(err.error || 'Could not delete tier');
                                  }
                                }}
                                title="Permanently delete this disabled tier"
                                style={{ padding: '0.35rem 0.75rem', background: 'rgba(239, 68, 68, 0.85)', color: '#fff', border: '1px solid rgba(239, 68, 68, 0.9)', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700 }}
                              >Delete</button>
                            )}
                          </div>
                        </div>
                      ))}
                      {adminTiers.filter(t => t.mode === section.mode).length === 0 && (
                        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)' }}>
                          No {section.mode} tiers yet — click "Add Tier" to create one.
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Edit/create modal */}
                {editingTier && (
                  <div
                    onClick={(e) => { if (e.target === e.currentTarget) setEditingTier(null); }}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
                  >
                    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', maxWidth: '480px', width: '100%' }}>
                      <h3 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>{editingTier.id ? 'Edit Tier' : 'Create Tier'}</h3>
                      <div style={{ display: 'grid', gap: '0.85rem' }}>
                        {[
                          { k: 'name', label: 'Name', type: 'text' },
                          { k: 'rank_order', label: 'Display order (lower = higher)', type: 'number' },
                          { k: 'reward_amount', label: 'Reward amount ($)', type: 'number' },
                          { k: 'wager_requirement', label: 'Wager required ($)', type: 'number' },
                          { k: 'color', label: 'Color (hex, e.g. #e5e7eb)', type: 'text' }
                        ].map(field => (
                          <label key={field.k} style={{ display: 'block' }}>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>{field.label}</div>
                            <input
                              type={field.type}
                              value={editingTier[field.k] ?? ''}
                              onChange={(e) => setEditingTier({ ...editingTier, [field.k]: field.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value })}
                              style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.9rem' }}
                            />
                          </label>
                        ))}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                          <input type="checkbox" checked={editingTier.active !== false} onChange={(e) => setEditingTier({ ...editingTier, active: e.target.checked })} />
                          Active (visible to users)
                        </label>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Mode: <strong style={{ color: 'var(--text)' }}>{editingTier.mode}</strong></div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
                        <button onClick={() => setEditingTier(null)} style={{ padding: '0.55rem 1.1rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                        <button
                          onClick={async () => {
                            const token = getAuthToken();
                            const payload = {
                              mode: editingTier.mode,
                              rank_order: Number(editingTier.rank_order) || 0,
                              name: editingTier.name,
                              reward_amount: Number(editingTier.reward_amount) || 0,
                              wager_requirement: Number(editingTier.wager_requirement) || 0,
                              color: editingTier.color || '#e5e7eb',
                              active: editingTier.active !== false
                            };
                            if (!payload.name.trim()) { alert('Name required'); return; }
                            const url = editingTier.id
                              ? `${BACKEND_API_URL}/api/admin/reward-tiers/${editingTier.id}`
                              : `${BACKEND_API_URL}/api/admin/reward-tiers`;
                            const method = editingTier.id ? 'PUT' : 'POST';
                            const res = await fetch(url, { method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
                            if (res.ok) {
                              const d = await res.json();
                              if (editingTier.id) {
                                setAdminTiers(adminTiers.map(x => x.id === d.tier.id ? d.tier : x));
                              } else {
                                setAdminTiers([...adminTiers, d.tier]);
                              }
                              setEditingTier(null);
                              fetchRewardTiers();
                            } else {
                              const err = await res.json().catch(() => ({}));
                              alert(err.error || 'Save failed');
                            }
                          }}
                          style={{ padding: '0.55rem 1.25rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}
                        >Save</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Leaderboard Periods Tab */}
            {adminTab === 'leaderboardPeriods' && (
              <div>
                {(() => {
                  if (!adminPeriods.length) fetchAdminPeriods();
                  return null;
                })()}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div className="card-icon">📅</div>
                      <div className="card-title">Leaderboard Periods</div>
                    </div>
                    <button
                      onClick={() => {
                        const now = new Date();
                        const nextMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
                        const after = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 2, 1));
                        setEditingPeriod({
                          slug: '',
                          name: '',
                          start_at: nextMonth.toISOString().slice(0, 10),
                          end_at: after.toISOString().slice(0, 10),
                          total_prize: 25000,
                          prizes: [
                            { rank: 1, prize: 7000 }, { rank: 2, prize: 5000 }, { rank: 3, prize: 3500 },
                            { rank: 4, prize: 2500 }, { rank: 5, prize: 2000 }, { rank: 6, prize: 1500 },
                            { rank: 7, prize: 1250 }, { rank: 8, prize: 1000 }, { rank: 9, prize: 750 }, { rank: 10, prize: 500 }
                          ],
                          active: true
                        });
                      }}
                      style={{ padding: '0.5rem 1rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                      + New Period
                    </button>
                  </div>
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr 0.8fr 0.7fr 160px', gap: '0.75rem', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)' }}>
                      <div>Name / Slug</div>
                      <div>Start</div>
                      <div>End</div>
                      <div>Prize Pool</div>
                      <div>Status</div>
                      <div style={{ textAlign: 'right' }}>Actions</div>
                    </div>
                    {adminPeriods.length === 0 && (
                      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)' }}>
                        No periods yet — click "New Period" to add one.
                      </div>
                    )}
                    {adminPeriods.map(p => {
                      const start = new Date(p.start_at);
                      const end = new Date(p.end_at);
                      const now = new Date();
                      const status = !p.active ? 'inactive' : (now < start ? 'upcoming' : (now >= end ? 'ended' : 'live'));
                      const statusColor = { live: '#22c55e', upcoming: '#3b82f6', ended: 'var(--text-muted)', inactive: '#ef4444' }[status];
                      const hasWinners = p.winners && Array.isArray(p.winners) && p.winners.length > 0;
                      const isViewing = expandedPeriod === p.id;
                      return (
                        <React.Fragment key={p.id}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr 0.8fr 0.7fr 220px', gap: '0.75rem', alignItems: 'center', padding: '0.85rem 1rem', borderBottom: '1px solid var(--border)', opacity: p.active ? 1 : 0.5 }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                              <span style={{ fontWeight: 700 }}>{p.name}</span>
                              <span style={{ padding: '0.12rem 0.45rem', borderRadius: '999px', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', background: (p.type || 'monthly') === 'weekly' ? 'rgba(139, 92, 246, 0.15)' : 'rgba(59, 130, 246, 0.15)', color: (p.type || 'monthly') === 'weekly' ? '#a78bfa' : '#93c5fd' }}>
                                {p.type || 'monthly'}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'Space Mono, monospace' }}>{p.slug}</div>
                          </div>
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>{start.toISOString().slice(0, 10)}</div>
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>{end.toISOString().slice(0, 10)}</div>
                          <div style={{ fontWeight: 700, color: '#22c55e' }}>${Number(p.total_prize).toLocaleString()}</div>
                          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: statusColor, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{status}</div>
                          <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            {status === 'ended' && (
                              hasWinners ? (
                                <button onClick={() => setExpandedPeriod(isViewing ? null : p.id)}
                                  style={{ padding: '0.3rem 0.6rem', background: 'rgba(34, 211, 238, 0.12)', color: '#22d3ee', border: '1px solid rgba(34, 211, 238, 0.25)', borderRadius: '5px', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                  {isViewing ? '▲ Hide' : '👁 View'}
                                </button>
                              ) : (
                                <button onClick={async () => {
                                  const token = getAuthToken();
                                  const res = await fetch(`${BACKEND_API_URL}/api/admin/periods/${p.id}/snapshot`, {
                                    method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
                                  });
                                  const data = await res.json();
                                  if (res.ok) { alert(`Saved ${data.winners_count} winners`); fetchAdminPeriods(); }
                                  else alert(data.error || 'Failed');
                                }} style={{ padding: '0.3rem 0.6rem', background: 'rgba(251, 191, 36, 0.12)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.25)', borderRadius: '5px', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                  📸 Snap
                                </button>
                              )
                            )}
                            <button
                              onClick={() => setEditingPeriod({
                                ...p,
                                start_at: new Date(p.start_at).toISOString().slice(0, 10),
                                end_at: new Date(p.end_at).toISOString().slice(0, 10),
                                prizes: Array.isArray(p.prizes) ? p.prizes : []
                              })}
                              style={{ padding: '0.3rem 0.6rem', background: 'rgba(255,255,255,0.08)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '5px', fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'inherit' }}
                            >Edit</button>
                            <button
                              onClick={async () => {
                                if (!window.confirm(`${p.active ? 'Disable' : 'Delete'} "${p.name}"?`)) return;
                                const token = getAuthToken();
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/leaderboard-periods/${p.id}${p.active ? '' : '?hard=1'}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                                if (res.ok) { fetchAdminPeriods(); fetchDbPeriods(); }
                              }}
                              style={{ padding: '0.3rem 0.6rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '5px', fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'inherit' }}
                            >{p.active ? 'Disable' : 'Delete'}</button>
                          </div>
                        </div>
                        {/* Expanded winners view */}
                        {isViewing && hasWinners && (
                          <div style={{ padding: '0.75rem 1.25rem 1rem', background: 'rgba(0,0,0,0.15)', borderBottom: '1px solid var(--border)' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '45px 1fr 110px 110px', gap: '0.25rem 0.5rem', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                              <div>Rank</div><div>Username</div><div style={{ textAlign: 'right' }}>Wagered</div><div style={{ textAlign: 'right' }}>Prize</div>
                            </div>
                            {p.winners.slice(0, 20).map((w, i) => {
                              const prize = (p.prizes || []).find(pr => pr.rank === w.rank)?.prize || 0;
                              return (
                                <div key={i} style={{ display: 'grid', gridTemplateColumns: '45px 1fr 110px 110px', gap: '0.25rem 0.5rem', padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.03)', fontSize: '0.8rem', alignItems: 'center' }}>
                                  <div style={{ fontWeight: 700, color: w.rank <= 3 ? '#fbbf24' : 'var(--text-dim)' }}>#{w.rank}</div>
                                  <div style={{ fontWeight: 600, fontFamily: "'Space Mono', monospace" }}>{w.username}</div>
                                  <div style={{ textAlign: 'right', color: '#22d3ee', fontFamily: "'Space Mono', monospace" }}>${Number(w.wagered).toLocaleString()}</div>
                                  <div style={{ textAlign: 'right', color: prize ? '#22c55e' : 'var(--text-muted)', fontWeight: 600 }}>{prize ? `$${Number(prize).toLocaleString()}` : '—'}</div>
                                </div>
                              );
                            })}
                            {p.winners.length > 20 && <div style={{ textAlign: 'center', fontSize: '0.72rem', color: 'var(--text-muted)', padding: '0.5rem' }}>+ {p.winners.length - 20} more</div>}
                          </div>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>

                {editingPeriod && (
                  <div
                    onClick={(e) => { if (e.target === e.currentTarget) setEditingPeriod(null); }}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '2rem 1rem', overflowY: 'auto' }}
                  >
                    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', maxWidth: '560px', width: '100%' }}>
                      <h3 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>{editingPeriod.id ? 'Edit Period' : 'Create Period'}</h3>
                      <div style={{ display: 'grid', gap: '0.85rem' }}>
                        {!editingPeriod.id && adminPeriods.length > 0 && (
                          <label style={{ display: 'block', background: 'rgba(59, 130, 246, 0.06)', border: '1px solid rgba(59, 130, 246, 0.25)', borderRadius: '8px', padding: '0.6rem 0.75rem' }}>
                            <div style={{ fontSize: '0.8rem', color: '#93c5fd', marginBottom: '0.3rem', fontWeight: 600 }}>⎘ Copy prizes from a previous leaderboard</div>
                            <select
                              value={editingPeriod._copyFrom ?? ''}
                              onChange={(e) => {
                                const srcId = e.target.value;
                                if (!srcId) { setEditingPeriod({ ...editingPeriod, _copyFrom: '' }); return; }
                                const src = adminPeriods.find(p => String(p.id) === srcId);
                                if (!src) return;
                                setEditingPeriod({
                                  ...editingPeriod,
                                  _copyFrom: srcId,
                                  type: src.type || editingPeriod.type || 'monthly',
                                  total_prize: Number(src.total_prize) || 0,
                                  prizes: (Array.isArray(src.prizes) ? src.prizes : []).map(pz => ({ rank: pz.rank, prize: pz.prize }))
                                });
                              }}
                              style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }}
                            >
                              <option value="">— Pick a leaderboard to copy —</option>
                              {adminPeriods.filter(p => Array.isArray(p.prizes) && p.prizes.length > 0).map(p => (
                                <option key={p.id} value={p.id}>{p.name} ({p.type || 'monthly'}) — ${Number(p.total_prize).toLocaleString()}, {p.prizes.length} ranks</option>
                              ))}
                            </select>
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                              Fills prize pool, ranks &amp; type from the chosen period. Slug, name &amp; dates stay yours to set.
                            </div>
                          </label>
                        )}
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Slug (URL-safe, e.g. apr-2026)</div>
                          <input type="text" value={editingPeriod.slug ?? ''} onChange={(e) => setEditingPeriod({ ...editingPeriod, slug: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                        </label>
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Display name (e.g. April 2026)</div>
                          <input type="text" value={editingPeriod.name ?? ''} onChange={(e) => setEditingPeriod({ ...editingPeriod, name: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                        </label>
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Type</div>
                          <select
                            value={editingPeriod.type || 'monthly'}
                            onChange={(e) => setEditingPeriod({ ...editingPeriod, type: e.target.value })}
                            style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }}
                          >
                            <option value="monthly">Monthly</option>
                            <option value="weekly">Weekly</option>
                          </select>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                            When both monthly and weekly periods exist, users see a toggle. Otherwise only the active type shows.
                          </div>
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                          <label>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Start (midnight CT)</div>
                            <input type="date" value={editingPeriod.start_at ?? ''} onChange={(e) => setEditingPeriod({ ...editingPeriod, start_at: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', colorScheme: 'dark' }} />
                          </label>
                          <label>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>End (midnight CT)</div>
                            <input type="date" value={editingPeriod.end_at ?? ''} onChange={(e) => setEditingPeriod({ ...editingPeriod, end_at: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', colorScheme: 'dark' }} />
                          </label>
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '-0.25rem' }}>
                          Dates are <strong>US Central Time</strong>. The period ends at the very start of the End day
                          (e.g. End = Jun 16 → runs through 11:59 PM CT on Jun 15).
                        </div>
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Total prize pool ($)</div>
                          <input type="number" value={editingPeriod.total_prize ?? 0} onChange={(e) => setEditingPeriod({ ...editingPeriod, total_prize: Number(e.target.value) })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                        </label>
                        <div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Prize per rank</span>
                            <button
                              onClick={() => setEditingPeriod({ ...editingPeriod, prizes: [...(editingPeriod.prizes || []), { rank: (editingPeriod.prizes?.length || 0) + 1, prize: 0 }] })}
                              style={{ padding: '0.25rem 0.65rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'inherit' }}
                            >+ Add rank</button>
                          </div>
                          <div style={{ display: 'grid', gap: '0.4rem', maxHeight: '240px', overflowY: 'auto', padding: '0.25rem', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
                            {(editingPeriod.prizes || []).map((pz, idx) => (
                              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '60px 1fr 40px', gap: '0.5rem', alignItems: 'center' }}>
                                <input type="number" value={pz.rank} onChange={(e) => {
                                  const next = [...editingPeriod.prizes]; next[idx] = { ...next[idx], rank: Number(e.target.value) }; setEditingPeriod({ ...editingPeriod, prizes: next });
                                }} style={{ padding: '0.35rem 0.5rem', background: 'rgba(0,0,0,0.4)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '4px', fontFamily: 'inherit', fontSize: '0.85rem' }} />
                                <input type="number" value={pz.prize} onChange={(e) => {
                                  const next = [...editingPeriod.prizes]; next[idx] = { ...next[idx], prize: Number(e.target.value) }; setEditingPeriod({ ...editingPeriod, prizes: next });
                                }} placeholder="$ prize" style={{ padding: '0.35rem 0.5rem', background: 'rgba(0,0,0,0.4)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '4px', fontFamily: 'inherit', fontSize: '0.85rem' }} />
                                <button onClick={() => {
                                  const next = editingPeriod.prizes.filter((_, i) => i !== idx); setEditingPeriod({ ...editingPeriod, prizes: next });
                                }} style={{ padding: '0.35rem', background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '4px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.75rem' }}>×</button>
                              </div>
                            ))}
                          </div>
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                          <input type="checkbox" checked={editingPeriod.active !== false} onChange={(e) => setEditingPeriod({ ...editingPeriod, active: e.target.checked })} />
                          Active (visible to users)
                        </label>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
                        <button onClick={() => setEditingPeriod(null)} style={{ padding: '0.55rem 1.1rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                        <button
                          onClick={async () => {
                            const token = getAuthToken();
                            if (!editingPeriod.slug || !editingPeriod.name || !editingPeriod.start_at || !editingPeriod.end_at) {
                              alert('Fill all required fields'); return;
                            }
                            const payload = {
                              slug: editingPeriod.slug,
                              name: editingPeriod.name,
                              type: editingPeriod.type || 'monthly',
                              // Send naive datetime (no Z) so the backend's centralToUTC() interprets
                              // the picked day as MIDNIGHT CENTRAL TIME, not midnight UTC. Appending 'Z'
                              // here would store the period ~5-6h early (CT is UTC-5/-6).
                              start_at: editingPeriod.start_at + 'T00:00:00',
                              end_at: editingPeriod.end_at + 'T00:00:00',
                              total_prize: Number(editingPeriod.total_prize) || 0,
                              prizes: editingPeriod.prizes || [],
                              active: editingPeriod.active !== false
                            };
                            const url = editingPeriod.id
                              ? `${BACKEND_API_URL}/api/admin/leaderboard-periods/${editingPeriod.id}`
                              : `${BACKEND_API_URL}/api/admin/leaderboard-periods`;
                            const method = editingPeriod.id ? 'PUT' : 'POST';
                            const res = await fetch(url, { method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
                            if (res.ok) {
                              setEditingPeriod(null);
                              fetchAdminPeriods();
                              fetchDbPeriods();
                            } else {
                              const err = await res.json().catch(() => ({}));
                              alert(err.error || 'Save failed');
                            }
                          }}
                          style={{ padding: '0.55rem 1.25rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}
                        >Save</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* PERIOD HISTORY */}
            {adminTab === 'periodHistory' && (
              <div>
                <div className="header" style={{ textAlign: 'center' }}>
                  <h1 className="header-title">📜 <span>Period History</span></h1>
                  <p className="header-sub">View final standings for completed leaderboard periods</p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', justifyContent: 'center' }}>
                  <button onClick={fetchAdminPeriodHistory} style={{ padding: '0.45rem 0.85rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit' }}>↻ Refresh</button>
                </div>
                {adminPeriodHistory.length === 0 ? (
                  <div className="card" style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-dim)' }}>No periods found.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {adminPeriodHistory.map(p => {
                      const ended = new Date(p.end_at) < new Date();
                      const hasWinners = p.winners && Array.isArray(p.winners) && p.winners.length > 0;
                      const isExpanded = expandedPeriod === p.id;
                      return (
                        <div key={p.id} className="card" style={{ padding: '1rem 1.25rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <div>
                              <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{p.name}</span>
                              <span style={{ marginLeft: '0.5rem', padding: '0.15rem 0.5rem', fontSize: '0.6rem', fontWeight: 700, borderRadius: '999px', background: ended ? 'rgba(239, 68, 68, 0.12)' : 'rgba(34, 197, 94, 0.12)', color: ended ? '#ef4444' : '#22c55e' }}>{ended ? 'Ended' : 'Active'}</span>
                              <span style={{ marginLeft: '0.35rem', padding: '0.15rem 0.5rem', fontSize: '0.6rem', fontWeight: 700, borderRadius: '999px', background: 'rgba(59, 130, 246, 0.12)', color: '#93c5fd' }}>{p.type || 'monthly'}</span>
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                                {new Date(p.start_at).toLocaleDateString()} — {new Date(p.end_at).toLocaleDateString()} · ${Number(p.total_prize).toLocaleString()} prize pool
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                              {hasWinners && (
                                <button onClick={() => setExpandedPeriod(isExpanded ? null : p.id)}
                                  style={{ padding: '0.35rem 0.75rem', background: 'rgba(34, 211, 238, 0.12)', color: '#22d3ee', border: '1px solid rgba(34, 211, 238, 0.25)', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                  {isExpanded ? '▲ Hide' : `▼ ${p.winners.length} Winners`}
                                </button>
                              )}
                              {ended && !hasWinners && (
                                <button onClick={async () => {
                                  if (!confirm(`Snapshot final standings for "${p.name}" from Rainbet API?`)) return;
                                  const token = getAuthToken();
                                  const res = await fetch(`${BACKEND_API_URL}/api/admin/periods/${p.id}/snapshot`, {
                                    method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
                                  });
                                  const data = await res.json();
                                  if (res.ok) { alert(`Saved ${data.winners_count} winners. Top: ${data.top3?.map(w => w.username).join(', ')}`); fetchAdminPeriodHistory(); }
                                  else alert(data.error || 'Failed');
                                }} style={{ padding: '0.35rem 0.75rem', background: 'linear-gradient(135deg, #fbbf24, #d97706)', color: '#000', border: 'none', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                  📸 Snapshot Now
                                </button>
                              )}
                              {hasWinners && (
                                <button onClick={async () => {
                                  if (!confirm(`Re-snapshot "${p.name}"? This will overwrite saved winners.`)) return;
                                  const token = getAuthToken();
                                  const res = await fetch(`${BACKEND_API_URL}/api/admin/periods/${p.id}/snapshot`, {
                                    method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
                                  });
                                  const data = await res.json();
                                  if (res.ok) { alert(`Updated: ${data.winners_count} winners`); fetchAdminPeriodHistory(); }
                                  else alert(data.error || 'Failed');
                                }} style={{ padding: '0.35rem 0.65rem', background: 'rgba(139, 92, 246, 0.12)', color: '#a78bfa', border: '1px solid rgba(139, 92, 246, 0.25)', borderRadius: '6px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                  🔄 Re-snap
                                </button>
                              )}
                            </div>
                          </div>
                          {isExpanded && hasWinners && (
                            <div style={{ marginTop: '0.75rem', borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '50px 1fr 120px 120px', gap: '0.25rem 0.5rem', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.35rem', padding: '0 0.5rem' }}>
                                <div>Rank</div><div>Username</div><div style={{ textAlign: 'right' }}>Wagered</div><div style={{ textAlign: 'right' }}>Prize</div>
                              </div>
                              {p.winners.slice(0, 20).map((w, i) => {
                                const prize = (p.prizes || []).find(pr => pr.rank === w.rank)?.prize || 0;
                                return (
                                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '50px 1fr 120px 120px', gap: '0.25rem 0.5rem', padding: '0.4rem 0.5rem', borderBottom: '1px solid rgba(255,255,255,0.03)', fontSize: '0.82rem', alignItems: 'center' }}>
                                    <div style={{ fontWeight: 700, color: w.rank <= 3 ? '#fbbf24' : 'var(--text-dim)' }}>#{w.rank}</div>
                                    <div style={{ fontWeight: 600, fontFamily: "'Space Mono', monospace" }}>{w.username}</div>
                                    <div style={{ textAlign: 'right', color: '#22d3ee', fontFamily: "'Space Mono', monospace" }}>${Number(w.wagered).toLocaleString()}</div>
                                    <div style={{ textAlign: 'right', color: prize ? '#22c55e' : 'var(--text-muted)', fontWeight: 600 }}>{prize ? `$${Number(prize).toLocaleString()}` : '—'}</div>
                                  </div>
                                );
                              })}
                              {p.winners.length > 20 && <div style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-muted)', padding: '0.5rem' }}>+ {p.winners.length - 20} more</div>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Site Links Tab */}
            {adminTab === 'siteLinks' && (
              <div>
                {(() => {
                  if (!adminLinks.length) fetchAdminLinks();
                  return null;
                })()}
                {['social', 'affiliate', 'partner', 'misc'].map(category => {
                  const categoryLinks = adminLinks.filter(l => l.category === category);
                  return (
                    <div key={category} className="card" style={{ marginBottom: '1.5rem' }}>
                      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div className="card-icon">🔗</div>
                          <div className="card-title" style={{ textTransform: 'capitalize' }}>{category} Links</div>
                        </div>
                        <button
                          onClick={() => setEditingLink({ key: '', label: '', url: '', category, icon: '', sort_order: categoryLinks.length + 1, active: true })}
                          style={{ padding: '0.5rem 1rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                        >+ Add Link</button>
                      </div>
                      <div>
                        {categoryLinks.length === 0 && (
                          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.85rem' }}>
                            No {category} links yet.
                          </div>
                        )}
                        {categoryLinks.map(l => (
                          <div key={l.id} style={{ display: 'grid', gridTemplateColumns: '180px 1fr 80px 140px', gap: '0.75rem', alignItems: 'center', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', opacity: l.active ? 1 : 0.5 }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{l.label}</div>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'Space Mono, monospace' }}>{l.key}</div>
                            </div>
                            <a href={l.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8rem', color: '#93c5fd', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.url}</a>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>{l.icon || '—'}</div>
                            <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                              <button
                                onClick={() => setEditingLink({ ...l })}
                                style={{ padding: '0.35rem 0.75rem', background: 'rgba(255,255,255,0.08)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit' }}
                              >Edit</button>
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Delete "${l.label}"?`)) return;
                                  const token = getAuthToken();
                                  const res = await fetch(`${BACKEND_API_URL}/api/admin/site-links/${l.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                                  if (res.ok) { fetchAdminLinks(); fetchDbLinks(); }
                                }}
                                style={{ padding: '0.35rem 0.75rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit' }}
                              >Delete</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {editingLink && (
                  <div
                    onClick={(e) => { if (e.target === e.currentTarget) setEditingLink(null); }}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
                  >
                    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', maxWidth: '500px', width: '100%' }}>
                      <h3 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>{editingLink.id ? 'Edit Link' : 'Create Link'}</h3>
                      <div style={{ display: 'grid', gap: '0.85rem' }}>
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Key (unique, lowercase/underscores)</div>
                          <input type="text" value={editingLink.key ?? ''} disabled={!!editingLink.id} onChange={(e) => setEditingLink({ ...editingLink, key: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'Space Mono, monospace', opacity: editingLink.id ? 0.6 : 1 }} />
                        </label>
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Label (shown to users)</div>
                          <input type="text" value={editingLink.label ?? ''} onChange={(e) => setEditingLink({ ...editingLink, label: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                        </label>
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>URL (must start with https://)</div>
                          <input type="url" value={editingLink.url ?? ''} onChange={(e) => setEditingLink({ ...editingLink, url: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: '0.75rem' }}>
                          <label>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Category</div>
                            <select value={editingLink.category ?? 'social'} onChange={(e) => setEditingLink({ ...editingLink, category: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }}>
                              <option value="social">social</option>
                              <option value="affiliate">affiliate</option>
                              <option value="partner">partner</option>
                              <option value="misc">misc</option>
                            </select>
                          </label>
                          <label>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Icon (optional)</div>
                            <select value={editingLink.icon ?? ''} onChange={(e) => setEditingLink({ ...editingLink, icon: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }}>
                              <option value="">(none)</option>
                              <option value="kick">kick</option>
                              <option value="twitch">twitch</option>
                              <option value="discord">discord</option>
                              <option value="youtube">youtube</option>
                              <option value="twitter">twitter</option>
                              <option value="instagram">instagram</option>
                              <option value="tiktok">tiktok</option>
                            </select>
                          </label>
                          <label>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Order</div>
                            <input type="number" value={editingLink.sort_order ?? 0} onChange={(e) => setEditingLink({ ...editingLink, sort_order: Number(e.target.value) })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                          </label>
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                          <input type="checkbox" checked={editingLink.active !== false} onChange={(e) => setEditingLink({ ...editingLink, active: e.target.checked })} />
                          Active (visible on site)
                        </label>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
                        <button onClick={() => setEditingLink(null)} style={{ padding: '0.55rem 1.1rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                        <button
                          onClick={async () => {
                            const token = getAuthToken();
                            if (!editingLink.label || !editingLink.url) { alert('Label and URL required'); return; }
                            if (!editingLink.id && !editingLink.key) { alert('Key required'); return; }
                            const payload = {
                              ...(editingLink.id ? {} : { key: editingLink.key }),
                              label: editingLink.label,
                              url: editingLink.url,
                              category: editingLink.category || 'social',
                              icon: editingLink.icon || null,
                              sort_order: Number(editingLink.sort_order) || 0,
                              active: editingLink.active !== false
                            };
                            const url = editingLink.id
                              ? `${BACKEND_API_URL}/api/admin/site-links/${editingLink.id}`
                              : `${BACKEND_API_URL}/api/admin/site-links`;
                            const method = editingLink.id ? 'PUT' : 'POST';
                            const res = await fetch(url, { method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
                            if (res.ok) {
                              setEditingLink(null);
                              fetchAdminLinks();
                              fetchDbLinks();
                            } else {
                              const err = await res.json().catch(() => ({}));
                              alert(err.error || 'Save failed');
                            }
                          }}
                          style={{ padding: '0.55rem 1.25rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}
                        >Save</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Wager Lookup Tab */}
            {adminTab === 'wagerLookup' && (
              <div className="card">
                <div className="card-header">
                  <div className="card-icon">🔍</div>
                  <div className="card-title">User Wager Lookup</div>
                </div>
                <div style={{ padding: '1.25rem' }}>
                  <p style={{ color: 'var(--text-dim)', marginBottom: '1.25rem', fontSize: '0.9rem' }}>
                    Query a Rainbet username's total wagered amount over an arbitrary date range — useful for giveaway eligibility checks.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '0.75rem', alignItems: 'end', marginBottom: '1rem' }}>
                    <label style={{ display: 'block' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rainbet Username</div>
                      <input
                        type="text"
                        value={wagerLookup.username}
                        onChange={(e) => setWagerLookup(prev => ({ ...prev, username: e.target.value }))}
                        placeholder="e.g. somewager42"
                        style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.9rem' }}
                      />
                    </label>
                    <label style={{ display: 'block' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Start Date</div>
                      <input
                        type="date"
                        value={wagerLookup.start}
                        onChange={(e) => setWagerLookup(prev => ({ ...prev, start: e.target.value }))}
                        style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.9rem', colorScheme: 'dark' }}
                      />
                    </label>
                    <label style={{ display: 'block' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>End Date</div>
                      <input
                        type="date"
                        value={wagerLookup.end}
                        onChange={(e) => setWagerLookup(prev => ({ ...prev, end: e.target.value }))}
                        style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.9rem', colorScheme: 'dark' }}
                      />
                    </label>
                    <button
                      onClick={async () => {
                        if (!wagerLookup.username || !wagerLookup.start || !wagerLookup.end) {
                          alert('Fill all three fields');
                          return;
                        }
                        setWagerLookup(prev => ({ ...prev, loading: true, error: null, result: null }));
                        try {
                          const token = getAuthToken();
                          const qs = new URLSearchParams({ username: wagerLookup.username, start: wagerLookup.start, end: wagerLookup.end });
                          const res = await fetch(`${BACKEND_API_URL}/api/admin/wager-lookup?${qs}`, { headers: { 'Authorization': `Bearer ${token}` } });
                          const data = await res.json();
                          if (!res.ok) throw new Error(data.error || 'Lookup failed');
                          setWagerLookup(prev => ({ ...prev, loading: false, result: data }));
                        } catch (e) {
                          setWagerLookup(prev => ({ ...prev, loading: false, error: e.message }));
                        }
                      }}
                      disabled={wagerLookup.loading}
                      style={{ padding: '0.55rem 1.25rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: wagerLookup.loading ? 'not-allowed' : 'pointer', opacity: wagerLookup.loading ? 0.6 : 1, fontFamily: 'inherit' }}
                    >{wagerLookup.loading ? 'Loading...' : 'Search'}</button>
                  </div>

                  {/* Quick-fill buttons */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', padding: '0.4rem 0' }}>Quick range:</span>
                    {[
                      { label: 'Last 7 days', days: 7 },
                      { label: 'Last 30 days', days: 30 },
                      { label: 'This month', thisMonth: true },
                      { label: 'Last month', lastMonth: true }
                    ].map(q => (
                      <button
                        key={q.label}
                        onClick={() => {
                          const end = new Date();
                          let start;
                          if (q.days) {
                            start = new Date(Date.now() - q.days * 86400000);
                          } else if (q.thisMonth) {
                            start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
                          } else if (q.lastMonth) {
                            start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() - 1, 1));
                            end.setUTCDate(0); // last day of previous month
                          }
                          const fmt = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${String(d.getUTCDate()).padStart(2,'0')}`;
                          setWagerLookup(prev => ({ ...prev, start: fmt(start), end: fmt(end) }));
                        }}
                        style={{ padding: '0.35rem 0.8rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '999px', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit' }}
                      >{q.label}</button>
                    ))}
                  </div>

                  {wagerLookup.error && (
                    <div style={{ padding: '0.85rem 1rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px', color: '#ef4444', fontSize: '0.9rem' }}>
                      {wagerLookup.error}
                    </div>
                  )}

                  {wagerLookup.result && (
                    <div style={{ padding: '1.25rem', background: wagerLookup.result.found ? 'rgba(34, 197, 94, 0.08)' : 'rgba(245, 158, 11, 0.08)', border: wagerLookup.result.found ? '1px solid rgba(34, 197, 94, 0.25)' : '1px solid rgba(245, 158, 11, 0.25)', borderRadius: '10px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.25rem' }}>
                        {wagerLookup.result.username} — {wagerLookup.result.start} to {wagerLookup.result.end}
                      </div>
                      <div style={{ fontSize: '2rem', fontWeight: 800, color: wagerLookup.result.found ? '#22c55e' : '#f59e0b' }}>
                        ${Number(wagerLookup.result.wagered_amount).toLocaleString()}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginTop: '0.25rem' }}>
                        {wagerLookup.result.found ? 'wagered in period' : 'User not found or no wagers in this period'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Points (admin) Tab */}
            {adminTab === 'points' && (() => {
              const cfg = adminPointsConfig || {};
              const setCfgVal = (key, val) => {
                setAdminPointsConfig(prev => ({ ...(prev || {}), [key]: { ...(prev?.[key] || {}), value: String(val) } }));
                setAdminPointsConfigDirty(prev => ({ ...prev, [key]: String(val) }));
              };
              const saveCfg = async () => {
                if (!Object.keys(adminPointsConfigDirty).length) return;
                const token = getAuthToken();
                const res = await fetch(`${BACKEND_API_URL}/api/admin/points/config`, {
                  method: 'PUT',
                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ updates: adminPointsConfigDirty })
                });
                if (res.ok) { fetchAdminPointsConfig(); fetchPointsConfig(); }
                else alert('Save failed');
              };

              const saveShopItem = async () => {
                if (!editingShopItem.name || !editingShopItem.cost) { alert('Name and cost required'); return; }
                const token = getAuthToken();
                const url = editingShopItem.id
                  ? `${BACKEND_API_URL}/api/admin/points/shop/${editingShopItem.id}`
                  : `${BACKEND_API_URL}/api/admin/points/shop`;
                const method = editingShopItem.id ? 'PUT' : 'POST';
                const payload = { ...editingShopItem };
                delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.created_by;
                if (payload.stock === '' || payload.stock == null) payload.stock = null;
                if (payload.max_per_user === '' || payload.max_per_user == null) payload.max_per_user = null;
                const res = await fetch(url, { method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
                if (res.ok) { setEditingShopItem(null); fetchAdminShopItems(); fetchPointsShop(); }
                else { const e = await res.json().catch(() => ({})); alert(e.error || 'Save failed'); }
              };

              const fulfillRedemption = async (id, action) => {
                const token = getAuthToken();
                const url = `${BACKEND_API_URL}/api/admin/points/redemptions/${id}/${action}`;
                const res = await fetch(url, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
                if (res.ok) fetchAdminPointsRedemptions(adminPointsRedemptionFilter);
                else alert('Action failed');
              };

              const handleGrant = async () => {
                if (!grantForm.userId || !grantForm.amount) { alert('Username and amount required'); return; }
                const token = getAuthToken();
                const isNumericId = /^\d+$/.test(grantForm.userId);
                const res = await fetch(`${BACKEND_API_URL}/api/admin/points/grant`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ ...(isNumericId ? { userId: parseInt(grantForm.userId, 10) } : { username: grantForm.userId }), amount: parseInt(grantForm.amount, 10), reason: grantForm.reason })
                });
                const data = await res.json();
                if (res.ok) {
                  alert(`Granted! New balance: ${data.newBalance}`);
                  setGrantForm({ userId: '', amount: '', reason: '' });
                } else alert(data.error || 'Failed');
              };

              const lookupUser = async (idOverride) => {
                const raw = String(idOverride ?? pointsUserLookup.userId).trim();
                if (!raw) return;
                const token = getAuthToken();
                let id = raw;
                // Not a numeric ID — resolve via the user search (kick/twitch/rainbet/site names)
                if (!/^\d+$/.test(raw)) {
                  try {
                    const sres = await fetch(`${BACKEND_API_URL}/api/admin/users/search?q=${encodeURIComponent(raw)}`, { headers: { 'Authorization': `Bearer ${token}` } });
                    const sd = sres.ok ? await sres.json() : { users: [] };
                    const users = sd.users || [];
                    if (users.length === 0) { alert(`No user found matching "${raw}"`); return; }
                    // Prefer an exact name match on any platform, else take the first result
                    const lower = raw.toLowerCase();
                    const exact = users.find(u => [u.username, u.kick_username, u.twitch_username, u.rainbet_username].some(n => (n || '').toLowerCase() === lower));
                    id = (exact || users[0]).id;
                  } catch { alert('Search failed'); return; }
                }
                setPointsLookupSuggestions([]);
                const res = await fetch(`${BACKEND_API_URL}/api/admin/points/user/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
                if (res.ok) { const d = await res.json(); setPointsUserLookup(prev => ({ ...prev, result: d })); }
                else { const e = await res.json().catch(() => ({})); alert(e.error || 'Lookup failed'); setPointsUserLookup(prev => ({ ...prev, result: null })); }
              };

              const numField = (key, label, hint) => (
                <label key={key}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>{label}</div>
                  <input type="number" value={cfg[key]?.value ?? ''} onChange={(e) => setCfgVal(key, e.target.value)} style={{ width: '100%', padding: '0.5rem 0.7rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                  {hint && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{hint}</div>}
                </label>
              );
              const toggleField = (key, label, locked) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.85rem', background: 'rgba(255,255,255,0.02)', borderRadius: '6px', border: '1px solid var(--border)', cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.5 : 1 }}>
                  <input type="checkbox" disabled={locked} checked={cfg[key]?.value === 'true'} onChange={(e) => setCfgVal(key, e.target.checked ? 'true' : 'false')} />
                  <span style={{ fontSize: '0.85rem' }}>{label}</span>
                  {locked && <span style={{ marginLeft: 'auto', fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Session 2</span>}
                </label>
              );

              return (
                <div>
                  {/* Sub-tabs */}
                  <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    {[
                      { key: 'config', label: 'Config & Stats' },
                      { key: 'shop', label: 'Shop Items' },
                      { key: 'redemptions', label: 'Redemptions' },
                      { key: 'grant', label: 'Grant Points' },
                      { key: 'user', label: 'User Lookup' }
                    ].map(t => (
                      <button key={t.key} onClick={() => setAdminPointsTab(t.key)} style={{
                        padding: '0.5rem 1rem',
                        background: adminPointsTab === t.key ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(255,255,255,0.04)',
                        color: adminPointsTab === t.key ? '#fff' : 'var(--text-dim)',
                        border: '1px solid ' + (adminPointsTab === t.key ? 'rgba(245, 158, 11, 0.5)' : 'var(--border)'),
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'inherit'
                      }}>{t.label}</button>
                    ))}
                  </div>

                  {/* Config */}
                  {adminPointsTab === 'config' && (
                    <>
                      {adminPointsStats && (
                        <div className="card" style={{ marginBottom: '1.25rem' }}>
                          <div className="card-header"><div className="card-icon">📊</div><div className="card-title">Stats</div></div>
                          <div style={{ padding: '1.25rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
                            <div style={{ padding: '1rem', background: 'rgba(34, 197, 94, 0.08)', borderRadius: '8px', textAlign: 'center' }}>
                              <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#22c55e' }}>{(adminPointsStats.totals?.total_earned || 0).toLocaleString()}</div>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Total earned</div>
                            </div>
                            <div style={{ padding: '1rem', background: 'rgba(239, 68, 68, 0.08)', borderRadius: '8px', textAlign: 'center' }}>
                              <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#ef4444' }}>{(adminPointsStats.totals?.total_spent || 0).toLocaleString()}</div>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Total spent</div>
                            </div>
                            <div style={{ padding: '1rem', background: 'rgba(59, 130, 246, 0.08)', borderRadius: '8px', textAlign: 'center' }}>
                              <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#3b82f6' }}>{adminPointsStats.totals?.active_users || 0}</div>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Active users</div>
                            </div>
                          </div>
                          {adminPointsStats.bySource?.length > 0 && (
                            <div style={{ padding: '0 1.25rem 1.25rem', borderTop: '1px solid var(--border)' }}>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '1rem 0 0.5rem' }}>Earned by source</div>
                              {adminPointsStats.bySource.map(s => (
                                <div key={s.source} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', fontSize: '0.85rem', borderBottom: '1px dashed rgba(255,255,255,0.05)' }}>
                                  <span style={{ fontFamily: 'Space Mono, monospace' }}>{s.source}</span>
                                  <span style={{ fontWeight: 700, color: '#22c55e' }}>+{Number(s.total).toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="card">
                        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div className="card-icon">⚙️</div>
                            <div className="card-title">Points Config</div>
                          </div>
                          <button onClick={saveCfg} disabled={!Object.keys(adminPointsConfigDirty).length} style={{ padding: '0.5rem 1.1rem', background: Object.keys(adminPointsConfigDirty).length ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(255,255,255,0.05)', color: Object.keys(adminPointsConfigDirty).length ? '#fff' : 'var(--text-muted)', border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: Object.keys(adminPointsConfigDirty).length ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>Save Changes</button>
                        </div>
                        <div style={{ padding: '1.25rem' }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.65rem' }}>Master toggle</div>
                          <div style={{ marginBottom: '1.25rem' }}>
                            {toggleField('points_system_enabled', 'Points system enabled (master switch)')}
                          </div>

                          <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.65rem' }}>Earning toggles</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.5rem', marginBottom: '1.25rem' }}>
                            {toggleField('wager_tracking_enabled', 'Award points for wagers')}
                            {toggleField('twitch_chat_tracking_enabled', 'Award points for Twitch chat')}
                            {toggleField('kick_chat_tracking_enabled', 'Award points for Kick chat')}
                            {toggleField('twitch_watch_tracking_enabled', 'Award points for watch time (chat presence)')}
                            {toggleField('discord_tracking_enabled', 'Award points for Discord chat', true)}
                          </div>

                          <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.65rem' }}>Earn rates</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                            {numField('wager_dollars_per_point', 'Dollars per point', '50 = 1pt per $50 wagered')}
                            {numField('points_per_twitch_chat', 'Points per Twitch chat msg', null)}
                            {numField('twitch_chat_cooldown_seconds', 'Twitch chat cooldown (s)', null)}
                            {numField('points_per_watch_hour', 'Points per watch hour', null)}
                            {numField('watch_check_interval_minutes', 'Watch check interval (min)', null)}
                            {numField('points_per_discord_message', 'Points per Discord msg', null)}
                            {numField('discord_chat_cooldown_seconds', 'Discord chat cooldown (s)', null)}
                          </div>
                        </div>
                      </div>

                      {/* Wager poller panel */}
                      <div className="card" style={{ marginTop: '1.25rem' }}>
                        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div className="card-icon">⚡</div>
                            <div className="card-title">Wager Points Poller</div>
                          </div>
                          <button
                            disabled={wagerPollerBusy || wagerPollerStatus?.running}
                            onClick={async () => {
                              setWagerPollerBusy(true);
                              try {
                                const token = getAuthToken();
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/points/wager-poller-run`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
                                const data = await res.json();
                                if (!res.ok) { alert(data.error || 'Failed'); return; }
                                if (data.skipped) {
                                  alert(`Skipped: ${data.reason}`);
                                } else {
                                  alert(`Done. Checked ${data.users_checked} users, awarded ${data.points_awarded} pts to ${data.users_awarded} users in ${Math.round(data.duration_ms / 100) / 10}s.`);
                                }
                                fetchWagerPollerStatus();
                                fetchAdminPointsStats();
                              } catch (e) { alert('Network error'); }
                              finally { setWagerPollerBusy(false); }
                            }}
                            style={{
                              padding: '0.5rem 1.1rem',
                              background: (wagerPollerBusy || wagerPollerStatus?.running) ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                              color: (wagerPollerBusy || wagerPollerStatus?.running) ? 'var(--text-muted)' : '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              cursor: (wagerPollerBusy || wagerPollerStatus?.running) ? 'not-allowed' : 'pointer',
                              fontFamily: 'inherit'
                            }}
                          >{wagerPollerStatus?.running ? 'Running…' : wagerPollerBusy ? 'Starting…' : '▶ Run Now'}</button>
                        </div>
                        <div style={{ padding: '1rem 1.25rem' }}>
                          {!wagerPollerStatus ? (
                            <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>Loading status…</div>
                          ) : (
                            <>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.85rem', marginBottom: '0.85rem' }}>
                                <StatusPill label="Poller state" ok={!wagerPollerStatus.running} neutral={wagerPollerStatus.running} value={wagerPollerStatus.running ? 'Running' : 'Idle'} />
                                <StatusPill label="Interval" ok value={`${wagerPollerStatus.interval_ms / 60000} min`} />
                                {wagerPollerStatus.last_result && (
                                  <StatusPill
                                    label="Last result"
                                    ok={!wagerPollerStatus.last_result.skipped && !wagerPollerStatus.last_result.errors?.length}
                                    neutral={wagerPollerStatus.last_result.skipped}
                                    value={wagerPollerStatus.last_result.skipped ? wagerPollerStatus.last_result.reason : `+${wagerPollerStatus.last_result.points_awarded} pts`}
                                  />
                                )}
                              </div>
                              {wagerPollerStatus.last_run && (
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                  Last run: {new Date(wagerPollerStatus.last_run).toLocaleString()}
                                </div>
                              )}
                              {wagerPollerStatus.last_result && !wagerPollerStatus.last_result.skipped && (
                                <div style={{ marginTop: '0.85rem', padding: '0.75rem', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '0.8rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.65rem' }}>
                                  <div><span style={{ color: 'var(--text-muted)' }}>Users checked:</span> <strong>{wagerPollerStatus.last_result.users_checked}</strong></div>
                                  <div><span style={{ color: 'var(--text-muted)' }}>Awarded:</span> <strong style={{ color: '#22c55e' }}>{wagerPollerStatus.last_result.users_awarded} ({wagerPollerStatus.last_result.points_awarded} pts)</strong></div>
                                  <div><span style={{ color: 'var(--text-muted)' }}>Snapshots created:</span> <strong>{wagerPollerStatus.last_result.snapshots_created}</strong></div>
                                  <div><span style={{ color: 'var(--text-muted)' }}>Duration:</span> <strong>{Math.round((wagerPollerStatus.last_result.duration_ms || 0) / 100) / 10}s</strong></div>
                                </div>
                              )}
                              {wagerPollerStatus.last_result?.errors?.length > 0 && (
                                <div style={{ marginTop: '0.85rem', padding: '0.75rem', background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '6px', fontSize: '0.8rem', color: '#ef4444' }}>
                                  ⚠️ Errors in last run:<br />
                                  {wagerPollerStatus.last_result.errors.slice(0, 5).map((e, i) => <div key={i} style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.75rem' }}>· {e}</div>)}
                                </div>
                              )}
                              <div style={{ marginTop: '0.85rem', padding: '0.65rem 0.85rem', background: 'rgba(59, 130, 246, 0.06)', border: '1px solid rgba(59, 130, 246, 0.15)', borderRadius: '6px', fontSize: '0.78rem', color: '#93c5fd', lineHeight: 1.5 }}>
                                ℹ️ The poller runs automatically every 30 minutes when the wager toggle is on. First time a user is seen, their current wager is snapshotted as a baseline (no retroactive points). After that, every dollar wagered above the snapshot earns the configured rate.
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Shop */}
                  {adminPointsTab === 'shop' && (
                    <div className="card">
                      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div className="card-icon">🛍️</div>
                          <div className="card-title">Shop Items ({adminShopItems.length})</div>
                        </div>
                        <button onClick={() => setEditingShopItem({ name: '', description: '', category: 'custom', cost: 100, image_url: '', stock: '', max_per_user: '', is_active: true, requires_rainbet_username: false, requires_discord_username: false, fulfillment_notes: '', sort_order: 0 })} style={{ padding: '0.5rem 1rem', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>+ New Item</button>
                      </div>
                      {adminShopItems.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)' }}>No items yet — click "New Item" to add one.</div>
                      ) : (
                        <div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 90px 80px 70px 160px', gap: '0.75rem', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)' }}>
                            <div>Name</div><div>Category</div><div>Cost</div><div>Stock</div><div>Active</div><div style={{ textAlign: 'right' }}>Actions</div>
                          </div>
                          {adminShopItems.map(item => (
                            <div key={item.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 100px 90px 80px 70px 160px', gap: '0.75rem', alignItems: 'center', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', opacity: item.is_active ? 1 : 0.5 }}>
                              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                                {item.description && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.description}</div>}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>{item.category}</div>
                              <div style={{ fontWeight: 700, color: '#f59e0b' }}>{item.cost}</div>
                              <div style={{ fontSize: '0.85rem' }}>{item.stock == null ? '∞' : item.stock}</div>
                              <div style={{ fontSize: '0.75rem', color: item.is_active ? '#22c55e' : '#ef4444' }}>{item.is_active ? '✓' : '✗'}</div>
                              <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                                <button onClick={() => setEditingShopItem({ ...item })} style={{ padding: '0.35rem 0.75rem', background: 'rgba(255,255,255,0.08)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit' }}>Edit</button>
                                <button onClick={async () => {
                                  if (!window.confirm(`Deactivate "${item.name}"?`)) return;
                                  const token = getAuthToken();
                                  const res = await fetch(`${BACKEND_API_URL}/api/admin/points/shop/${item.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                                  if (res.ok) fetchAdminShopItems();
                                }} style={{ padding: '0.35rem 0.75rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit' }}>Disable</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Redemptions */}
                  {adminPointsTab === 'redemptions' && (
                    <div className="card">
                      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div className="card-icon">📦</div>
                          <div className="card-title">Redemption Queue</div>
                        </div>
                        <select value={adminPointsRedemptionFilter} onChange={(e) => setAdminPointsRedemptionFilter(e.target.value)} style={{ padding: '0.45rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.85rem' }}>
                          <option value="pending">Pending</option>
                          <option value="fulfilled">Fulfilled</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="all">All</option>
                        </select>
                      </div>
                      {adminPointsRedemptions.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)' }}>No {adminPointsRedemptionFilter} redemptions.</div>
                      ) : (
                        adminPointsRedemptions.map(r => {
                          const statusColor = r.status === 'fulfilled' ? '#22c55e' : r.status === 'cancelled' ? '#ef4444' : '#f59e0b';
                          return (
                            <div key={r.id} style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', alignItems: 'flex-start' }}>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.4rem' }}>
                                  <span style={{ fontWeight: 700 }}>{r.item_name}</span>
                                  <span style={{ padding: '0.15rem 0.55rem', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: statusColor, background: `${statusColor}15`, border: `1px solid ${statusColor}40` }}>{r.status}</span>
                                  <span style={{ fontWeight: 700, color: '#f59e0b' }}>{r.points_spent} pts</span>
                                </div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>
                                  <strong>{r.user_display_name}</strong>
                                  {r.user_rainbet_username && <> · Rainbet: <code style={{ background: 'rgba(255,255,255,0.05)', padding: '0.05rem 0.3rem', borderRadius: '3px' }}>{r.user_rainbet_username}</code></>}
                                  {r.discord_username && <> · Discord: <code style={{ background: 'rgba(255,255,255,0.05)', padding: '0.05rem 0.3rem', borderRadius: '3px' }}>{r.discord_username}</code></>}
                                </div>
                                {r.slot_choice && (
                                  <div style={{ fontSize: '0.82rem', color: '#fbbf24', marginTop: '0.35rem', fontWeight: 700 }}>
                                    🎰 Slot choice: <span style={{ fontFamily: 'Space Mono, monospace' }}>{r.slot_choice}</span>
                                  </div>
                                )}
                                {r.notes && <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginTop: '0.35rem' }}>User note: {r.notes}</div>}
                                {r.admin_notes && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Admin note: {r.admin_notes}</div>}
                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.35rem' }}>{new Date(r.created_at).toLocaleString()}</div>
                              </div>
                              {r.status === 'pending' && (
                                <div style={{ display: 'flex', gap: '0.4rem' }}>
                                  <button onClick={() => fulfillRedemption(r.id, 'fulfill')} style={{ padding: '0.5rem 0.85rem', background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Fulfill</button>
                                  <button onClick={() => { if (window.confirm('Cancel and refund points?')) fulfillRedemption(r.id, 'cancel'); }} style={{ padding: '0.5rem 0.85rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel + Refund</button>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}

                  {/* Grant */}
                  {adminPointsTab === 'grant' && (
                    <div className="card">
                      <div className="card-header"><div className="card-icon">🎁</div><div className="card-title">Manual Grant</div></div>
                      <div style={{ padding: '1.25rem', display: 'grid', gap: '0.85rem', maxWidth: 480 }}>
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Username</div>
                          <div style={{ position: 'relative' }}>
                            <input type="text" value={grantForm.userId} onChange={async (e) => {
                              const val = e.target.value;
                              setGrantForm({ ...grantForm, userId: val });
                              if (val.trim().length < 2) { setGrantUserSuggestions([]); return; }
                              try {
                                const token = getAuthToken();
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/users/search?q=${encodeURIComponent(val.trim())}`, { headers: { 'Authorization': `Bearer ${token}` } });
                                if (res.ok) { const d = await res.json(); setGrantUserSuggestions(d.users || []); }
                              } catch {}
                            }} onBlur={() => setTimeout(() => setGrantUserSuggestions([]), 200)} placeholder="Kick, Twitch, or Rainbet username" autoComplete="off" style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                            {grantUserSuggestions.length > 0 && (
                              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#1a1a2e', border: '1px solid var(--border)', borderRadius: '0 0 8px 8px', maxHeight: 200, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                                {grantUserSuggestions.map(u => (
                                  <button key={u.id} onMouseDown={() => { setGrantForm({ ...grantForm, userId: u.username || u.kick_username || u.twitch_username }); setGrantUserSuggestions([]); }}
                                    style={{ display: 'block', width: '100%', padding: '0.5rem 0.75rem', background: 'none', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.04)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.82rem', cursor: 'pointer', textAlign: 'left' }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(34, 211, 238, 0.08)'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                  >
                                    <span style={{ fontWeight: 700 }}>{u.display_name || u.username}</span>
                                    {u.kick_username && <span style={{ marginLeft: '0.4rem', fontSize: '0.68rem', color: '#53fc18', opacity: 0.7 }}>K:{u.kick_username}</span>}
                                    {u.twitch_username && <span style={{ marginLeft: '0.4rem', fontSize: '0.68rem', color: '#a78bfa', opacity: 0.7 }}>T:{u.twitch_username}</span>}
                                    {u.rainbet_username && <span style={{ marginLeft: '0.4rem', fontSize: '0.68rem', color: '#22d3ee', opacity: 0.7 }}>RB:{u.rainbet_username}</span>}{u.rainbet_us_username && <span style={{ marginLeft: '0.4rem', fontSize: '0.68rem', color: '#93c5fd', opacity: 0.8 }}>US:{u.rainbet_us_username}</span>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </label>
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Amount (positive = grant, negative = deduct)</div>
                          <input type="number" value={grantForm.amount} onChange={(e) => setGrantForm({ ...grantForm, amount: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                        </label>
                        <label>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Reason (visible in user's history)</div>
                          <input type="text" value={grantForm.reason} onChange={(e) => setGrantForm({ ...grantForm, reason: e.target.value })} placeholder="e.g. Compensation for missed stream" style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                        </label>
                        <button onClick={handleGrant} style={{ justifySelf: 'start', padding: '0.65rem 1.4rem', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Grant Points</button>
                      </div>
                    </div>
                  )}

                  {/* User lookup */}
                  {adminPointsTab === 'user' && (
                    <div className="card">
                      <div className="card-header"><div className="card-icon">🔍</div><div className="card-title">User Points Lookup</div></div>
                      <div style={{ padding: '1.25rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                          <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
                            <input type="text" value={pointsUserLookup.userId} onChange={async (e) => {
                              const val = e.target.value;
                              setPointsUserLookup(prev => ({ ...prev, userId: val }));
                              if (val.trim().length < 2 || /^\d+$/.test(val.trim())) { setPointsLookupSuggestions([]); return; }
                              try {
                                const token = getAuthToken();
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/users/search?q=${encodeURIComponent(val.trim())}`, { headers: { 'Authorization': `Bearer ${token}` } });
                                if (res.ok) { const d = await res.json(); setPointsLookupSuggestions(d.users || []); }
                              } catch {}
                            }} onBlur={() => setTimeout(() => setPointsLookupSuggestions([]), 200)}
                              onKeyDown={(e) => { if (e.key === 'Enter') lookupUser(); }}
                              placeholder="User ID, Kick, Twitch, or Rainbet username" autoComplete="off"
                              style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} />
                            {pointsLookupSuggestions.length > 0 && (
                              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#1a1a2e', border: '1px solid var(--border)', borderRadius: '0 0 8px 8px', maxHeight: 200, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                                {pointsLookupSuggestions.map(u => (
                                  <button key={u.id} onMouseDown={() => { setPointsUserLookup(prev => ({ ...prev, userId: String(u.id) })); setPointsLookupSuggestions([]); lookupUser(u.id); }}
                                    style={{ display: 'block', width: '100%', padding: '0.5rem 0.75rem', background: 'none', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.04)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.82rem', cursor: 'pointer', textAlign: 'left' }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(34, 211, 238, 0.08)'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
                                    <span style={{ fontWeight: 700 }}>{u.display_name || u.username || u.kick_username || u.twitch_username}</span>
                                    <span style={{ color: 'var(--text-muted)', marginLeft: '0.4rem', fontSize: '0.72rem' }}>
                                      #{u.id}{u.kick_username ? ` · K: ${u.kick_username}` : ''}{u.twitch_username ? ` · T: ${u.twitch_username}` : ''}{u.rainbet_username ? ` · RB: ${u.rainbet_username}` : ''}{u.rainbet_us_username ? ` · RB.us: ${u.rainbet_us_username}` : ''}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <button onClick={() => lookupUser()} style={{ padding: '0.55rem 1.25rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Look Up</button>
                        </div>
                        {pointsUserLookup.result && (
                          <div>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
                              {pointsUserLookup.result.user.profile_image_url && (
                                <img src={pointsUserLookup.result.user.profile_image_url} alt="" style={{ width: 56, height: 56, borderRadius: '50%' }} />
                              )}
                              <div>
                                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{pointsUserLookup.result.user.display_name}</div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>ID: {pointsUserLookup.result.user.id}{pointsUserLookup.result.user.rainbet_username ? ` · Rainbet: ${pointsUserLookup.result.user.rainbet_username}` : ''}</div>
                              </div>
                              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Balance</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#f59e0b' }}>{pointsUserLookup.result.balance}</div>
                              </div>
                            </div>
                            {pointsUserLookup.result.user.rainbet_username && (
                              <div style={{ marginBottom: '1rem', padding: '0.85rem 1rem', background: 'rgba(59, 130, 246, 0.06)', border: '1px solid rgba(59, 130, 246, 0.15)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                                <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                                  Reconcile this user's wager points now (runs the poller for just them).
                                </div>
                                <div style={{ display: 'flex', gap: '0.4rem' }}>
                                  <button
                                    onClick={async () => {
                                      const token = getAuthToken();
                                      const res = await fetch(`${BACKEND_API_URL}/api/admin/points/wager-reconcile`, {
                                        method: 'POST',
                                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ userId: pointsUserLookup.result.user.id })
                                      });
                                      const data = await res.json();
                                      if (!res.ok) { alert(data.error || 'Failed'); return; }
                                      if (data.skipped) alert(`Skipped: ${data.reason}`);
                                      else alert(`Awarded ${data.points_awarded} pts. Snapshots created: ${data.snapshots_created}.`);
                                      lookupUser();
                                    }}
                                    style={{ padding: '0.45rem 0.95rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                                  >Reconcile Wagers</button>
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm('Reset this user\'s wager snapshot? Their next wager check will treat them as new (no retroactive award).')) return;
                                      const token = getAuthToken();
                                      const res = await fetch(`${BACKEND_API_URL}/api/admin/points/wager-snapshot-reset`, {
                                        method: 'POST',
                                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ userId: pointsUserLookup.result.user.id })
                                      });
                                      if (res.ok) alert('Snapshot reset.');
                                      else alert('Failed');
                                    }}
                                    style={{ padding: '0.45rem 0.95rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                                  >Reset Snapshot</button>
                                </div>
                              </div>
                            )}
                            <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.5rem' }}>Recent transactions</div>
                            {pointsUserLookup.result.recent_transactions.length === 0 ? (
                              <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>No transactions yet.</div>
                            ) : (
                              <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '6px' }}>
                                {pointsUserLookup.result.recent_transactions.map(tx => (
                                  <div key={tx.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 80px', gap: '0.5rem', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)', fontSize: '0.8rem' }}>
                                    <div style={{ color: 'var(--text-muted)' }}>{new Date(tx.created_at).toLocaleString()}</div>
                                    <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.75rem' }}>{tx.source}</div>
                                    <div style={{ color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.source_detail || '—'}</div>
                                    <div style={{ textAlign: 'right', fontWeight: 700, color: tx.amount > 0 ? '#22c55e' : '#ef4444' }}>{tx.amount > 0 ? '+' : ''}{tx.amount}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Shop edit modal */}
                  {editingShopItem && (
                    <div onClick={(e) => { if (e.target === e.currentTarget) setEditingShopItem(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '2rem 1rem', overflowY: 'auto' }}>
                      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', maxWidth: '560px', width: '100%' }}>
                        <h3 style={{ marginBottom: '1rem' }}>{editingShopItem.id ? 'Edit Shop Item' : 'New Shop Item'}</h3>
                        <div style={{ display: 'grid', gap: '0.85rem' }}>
                          <label><div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Name *</div>
                            <input type="text" value={editingShopItem.name ?? ''} onChange={(e) => setEditingShopItem({ ...editingShopItem, name: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} /></label>
                          <label><div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Description</div>
                            <textarea value={editingShopItem.description ?? ''} onChange={(e) => setEditingShopItem({ ...editingShopItem, description: e.target.value })} rows={2} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', resize: 'vertical' }} /></label>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                            <label><div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Category</div>
                              <select value={editingShopItem.category ?? 'custom'} onChange={(e) => setEditingShopItem({ ...editingShopItem, category: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }}>
                                <option value="custom">custom</option><option value="bonus">bonus</option><option value="merch">merch</option><option value="role">role</option><option value="other">other</option>
                              </select></label>
                            <label><div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Cost (points) *</div>
                              <input type="number" value={editingShopItem.cost ?? 0} onChange={(e) => setEditingShopItem({ ...editingShopItem, cost: Number(e.target.value) })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} /></label>
                          </div>
                          <label><div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Image URL</div>
                            <input type="url" value={editingShopItem.image_url ?? ''} onChange={(e) => setEditingShopItem({ ...editingShopItem, image_url: e.target.value })} placeholder="https://..." style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} /></label>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                            <label><div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Stock (blank = ∞)</div>
                              <input type="number" value={editingShopItem.stock ?? ''} onChange={(e) => setEditingShopItem({ ...editingShopItem, stock: e.target.value === '' ? null : Number(e.target.value) })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} /></label>
                            <label><div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Max per user</div>
                              <input type="number" value={editingShopItem.max_per_user ?? ''} onChange={(e) => setEditingShopItem({ ...editingShopItem, max_per_user: e.target.value === '' ? null : Number(e.target.value) })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} /></label>
                            <label><div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Sort order</div>
                              <input type="number" value={editingShopItem.sort_order ?? 0} onChange={(e) => setEditingShopItem({ ...editingShopItem, sort_order: Number(e.target.value) })} style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} /></label>
                          </div>
                          <label><div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Fulfillment notes (shown to user on redeem)</div>
                            <input type="text" value={editingShopItem.fulfillment_notes ?? ''} onChange={(e) => setEditingShopItem({ ...editingShopItem, fulfillment_notes: e.target.value })} placeholder="e.g. We'll DM you on Discord within 24h" style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit' }} /></label>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
                              <input type="checkbox" checked={editingShopItem.is_active !== false} onChange={(e) => setEditingShopItem({ ...editingShopItem, is_active: e.target.checked })} /> Active
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
                              <input type="checkbox" checked={!!editingShopItem.requires_rainbet_username} onChange={(e) => setEditingShopItem({ ...editingShopItem, requires_rainbet_username: e.target.checked })} /> Needs Rainbet
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
                              <input type="checkbox" checked={!!editingShopItem.requires_discord_username} onChange={(e) => setEditingShopItem({ ...editingShopItem, requires_discord_username: e.target.checked })} /> Needs Discord
                            </label>
                          </div>
                          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', fontSize: '0.85rem', padding: '0.55rem 0.7rem', background: 'rgba(251, 191, 36, 0.06)', border: '1px solid rgba(251, 191, 36, 0.22)', borderRadius: '6px' }}>
                            <input type="checkbox" checked={!!editingShopItem.requires_slot_choice} onChange={(e) => setEditingShopItem({ ...editingShopItem, requires_slot_choice: e.target.checked })} style={{ marginTop: '0.2rem' }} />
                            <span>
                              <span style={{ fontWeight: 700, color: '#fbbf24' }}>🎰 Needs slot choice</span>
                              <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)' }}>User must type which slot they want when redeeming (e.g. bonus buys).</span>
                            </span>
                          </label>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
                          <button onClick={() => setEditingShopItem(null)} style={{ padding: '0.55rem 1.1rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                          <button onClick={saveShopItem} style={{ padding: '0.55rem 1.25rem', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>Save</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Challenge Claims Tab */}
            {/* PRIZE CLAIMS ADMIN PANEL */}
            {adminTab === 'prizeClaims' && (
              <div>
                <div className="header" style={{ textAlign: 'center' }}>
                  <h1 className="header-title">🏆 <span>Prize Claims</span></h1>
                  <p className="header-sub">Review stream contest prize claims</p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', justifyContent: 'center' }}>
                  {['pending', 'approved', 'denied', 'all'].map(f => (
                    <button key={f} onClick={() => setPrizeClaimsFilter(f)} style={{
                      padding: '0.45rem 1rem', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: 'none',
                      background: prizeClaimsFilter === f ? 'rgba(34, 211, 238, 0.15)' : 'rgba(0,0,0,0.2)',
                      color: prizeClaimsFilter === f ? '#22d3ee' : 'var(--text-dim)'
                    }}>{f.charAt(0).toUpperCase() + f.slice(1)}{f === 'pending' && prizeClaimsAdmin.filter(c => c.status === 'pending').length > 0 ? ` (${prizeClaimsAdmin.filter(c => c.status === 'pending').length})` : ''}</button>
                  ))}
                  <button onClick={fetchPrizeClaimsAdmin} style={{ padding: '0.45rem 0.75rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit' }}>↻</button>
                </div>
                {prizeClaimsAdmin.length === 0 ? (
                  <div className="card" style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-dim)' }}>No {prizeClaimsFilter !== 'all' ? prizeClaimsFilter : ''} prize claims.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {prizeClaimsAdmin.map(c => {
                      const statusColors = { pending: '#f59e0b', approved: '#22c55e', denied: '#ef4444' };
                      return (
                        <div key={c.id} className="card" style={{ padding: '1rem 1.25rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                            <div>
                              <span style={{ fontWeight: 700, fontSize: '0.92rem' }}>{c.display_name || c.kick_username || c.username}</span>
                              <span style={{ marginLeft: '0.5rem', padding: '0.15rem 0.5rem', fontSize: '0.65rem', fontWeight: 700, borderRadius: '999px', background: `${statusColors[c.status]}22`, color: statusColors[c.status], border: `1px solid ${statusColors[c.status]}44` }}>{c.status}</span>
                            </div>
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{new Date(c.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                            <span style={{ padding: '0.2rem 0.6rem', fontSize: '0.72rem', fontWeight: 600, borderRadius: '6px', background: 'rgba(34, 211, 238, 0.1)', color: '#22d3ee', fontFamily: 'Space Mono, monospace' }}>RB: {c.rainbet_username}</span>
                            {c.discord_username && <span style={{ padding: '0.2rem 0.6rem', fontSize: '0.72rem', fontWeight: 600, borderRadius: '6px', background: 'rgba(88, 101, 242, 0.1)', color: '#a5b4fc' }}>💬 {c.discord_username}</span>}
                          </div>
                          <div style={{ fontSize: '0.88rem', fontWeight: 600, marginBottom: '0.35rem', color: '#fbbf24' }}>🏆 {c.prize_description}</div>
                          {c.notes && <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginBottom: '0.35rem' }}>📝 {c.notes}</div>}
                          {c.admin_notes && <div style={{ fontSize: '0.78rem', color: c.status === 'denied' ? '#ef4444' : '#22c55e', marginBottom: '0.35rem' }}>Admin: {c.admin_notes}</div>}
                          {c.status === 'pending' && (
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                              <button onClick={async () => {
                                const notes = prompt('Admin notes (optional):');
                                const token = getAuthToken();
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/prize-claims/${c.id}`, {
                                  method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ status: 'approved', adminNotes: notes || '' })
                                });
                                if (res.ok) fetchPrizeClaimsAdmin(); else alert('Failed');
                              }} style={{ padding: '0.4rem 1rem', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>✓ Approve</button>
                              <button onClick={async () => {
                                const reason = prompt('Denial reason (shown to user):');
                                if (reason === null) return;
                                const token = getAuthToken();
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/prize-claims/${c.id}`, {
                                  method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ status: 'denied', adminNotes: reason || 'Denied' })
                                });
                                if (res.ok) fetchPrizeClaimsAdmin(); else alert('Failed');
                              }} style={{ padding: '0.4rem 0.75rem', background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>✗ Deny</button>
                              <button onClick={async () => {
                                if (!confirm('Delete this claim?')) return;
                                const token = getAuthToken();
                                await fetch(`${BACKEND_API_URL}/api/admin/prize-claims/${c.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                                fetchPrizeClaimsAdmin();
                              }} style={{ padding: '0.4rem 0.6rem', background: 'none', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit' }}>🗑️</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* CHECKLIST CLAIMS ADMIN PANEL */}
            {adminTab === 'checklistClaims' && (
              <div>
                <div className="header" style={{ textAlign: 'center' }}>
                  <h1 className="header-title">🎁 <span>Checklist Claims</span></h1>
                  <p className="header-sub">Users who completed the signup checklist and claimed their free $3</p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', justifyContent: 'center' }}>
                  {['claimed', 'paid', 'denied', 'all'].map(f => (
                    <button key={f} onClick={() => setChecklistAdminFilter(f)} style={{
                      padding: '0.45rem 1rem', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: 'none',
                      background: checklistAdminFilter === f ? 'rgba(34, 211, 238, 0.15)' : 'rgba(0,0,0,0.2)',
                      color: checklistAdminFilter === f ? '#22d3ee' : 'var(--text-dim)'
                    }}>{f === 'claimed' ? 'Pending' : f.charAt(0).toUpperCase() + f.slice(1)}</button>
                  ))}
                  <button onClick={fetchChecklistAdminClaims} style={{ padding: '0.45rem 0.75rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit' }}>↻</button>
                </div>
                {checklistAdminClaims.length === 0 ? (
                  <div className="card" style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-dim)' }}>No checklist claims yet.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {checklistAdminClaims.map(c => {
                      const steps = c.steps || {};
                      return (
                      <div key={c.id} className="card" style={{ padding: '0.85rem 1.25rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 700 }}>{c.display_name || c.kick_username || c.username}</span>
                              {c.rainbet_username && <span style={{ padding: '0.1rem 0.4rem', fontSize: '0.65rem', fontWeight: 700, borderRadius: '999px', background: 'rgba(34, 211, 238, 0.15)', color: '#67e8f9' }}>{c.rainbet_username}</span>}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                              Claimed: {new Date(c.reward_claimed_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </div>
                          </div>
                          {c.admin_paid ? (
                            <span style={{ padding: '0.3rem 0.75rem', background: 'rgba(34, 197, 94, 0.12)', color: '#22c55e', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700 }}>✓ Paid</span>
                          ) : c.admin_denied ? (
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ padding: '0.3rem 0.75rem', background: c.admin_denied_final ? 'rgba(239, 68, 68, 0.25)' : 'rgba(239, 68, 68, 0.12)', color: '#ef4444', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700, border: c.admin_denied_final ? '1px solid rgba(239, 68, 68, 0.5)' : 'none' }}>{c.admin_denied_final ? '⛔ Denied — Final' : '✗ Denied'}</span>
                              {c.admin_denied_reason && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{c.admin_denied_reason}</div>}
                              {!c.admin_denied_final && (
                                <button onClick={async () => {
                                  if (!confirm(`Make this denial FINAL for ${c.display_name || c.username}? They will never be able to resubmit.`)) return;
                                  const token = getAuthToken();
                                  const res = await fetch(`${BACKEND_API_URL}/api/admin/checklist-claims/${c.user_id}/deny`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ reason: c.admin_denied_reason || '', final: true })
                                  });
                                  if (res.ok) fetchChecklistAdminClaims(); else alert('Failed');
                                }} style={{ display: 'block', marginLeft: 'auto', marginTop: '0.3rem', padding: '0.25rem 0.6rem', background: 'rgba(239, 68, 68, 0.08)', color: '#f87171', border: '1px dashed rgba(239, 68, 68, 0.4)', borderRadius: '6px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                  ⛔ Make Final
                                </button>
                              )}
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '0.4rem' }}>
                              <button onClick={async () => {
                                if (!confirm(`Mark $3 as paid to ${c.display_name || c.username}?`)) return;
                                const token = getAuthToken();
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/checklist-claims/${c.user_id}/pay`, {
                                  method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (res.ok) fetchChecklistAdminClaims(); else alert('Failed');
                              }} style={{ padding: '0.4rem 1rem', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                💰 Pay $3
                              </button>
                              <button onClick={async () => {
                                const reason = prompt('Denial reason (optional):');
                                if (reason === null) return;
                                const token = getAuthToken();
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/checklist-claims/${c.user_id}/deny`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ reason })
                                });
                                if (res.ok) fetchChecklistAdminClaims(); else alert('Failed');
                              }} style={{ padding: '0.4rem 0.75rem', background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                ✗ Deny
                              </button>
                              <button onClick={async () => {
                                if (!confirm(`FINAL deny for ${c.display_name || c.username}? They will never be able to resubmit this claim.`)) return;
                                const reason = prompt('Final denial reason (optional):');
                                if (reason === null) return;
                                const token = getAuthToken();
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/checklist-claims/${c.user_id}/deny`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ reason, final: true })
                                });
                                if (res.ok) fetchChecklistAdminClaims(); else alert('Failed');
                              }} style={{ padding: '0.4rem 0.75rem', background: 'rgba(239, 68, 68, 0.25)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.55)', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                ⛔ Final Deny
                              </button>
                            </div>
                          )}
                        </div>
                        {/* Verification details */}
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                          {typeof steps.follow_x === 'string' && (
                            <span style={{ padding: '0.15rem 0.5rem', fontSize: '0.65rem', fontWeight: 600, borderRadius: '999px', background: 'rgba(59, 130, 246, 0.1)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.2)' }}>𝕏 @{steps.follow_x}</span>
                          )}
                          {typeof steps.follow_instagram === 'string' && (
                            <span style={{ padding: '0.15rem 0.5rem', fontSize: '0.65rem', fontWeight: 600, borderRadius: '999px', background: 'rgba(236, 72, 153, 0.1)', color: '#f9a8d4', border: '1px solid rgba(236, 72, 153, 0.2)' }}>📸 @{steps.follow_instagram}</span>
                          )}
                          {typeof steps.join_discord === 'string' && (
                            <span style={{ padding: '0.15rem 0.5rem', fontSize: '0.65rem', fontWeight: 600, borderRadius: '999px', background: 'rgba(88, 101, 242, 0.1)', color: '#a5b4fc', border: '1px solid rgba(88, 101, 242, 0.2)' }}>💬 {steps.join_discord}</span>
                          )}
                          {steps.follow_kick_twitch && (
                            <span style={{ padding: '0.15rem 0.5rem', fontSize: '0.65rem', fontWeight: 600, borderRadius: '999px', background: 'rgba(83, 252, 24, 0.08)', color: '#86efac', border: '1px solid rgba(83, 252, 24, 0.15)' }}>🎮 Follow ✓</span>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* TIER CLAIMS ADMIN PANEL */}
            {(adminTab === 'tierClaims' || adminTab === 'paidTierClaims') && (
              <div>
                <div className="header" style={{ textAlign: 'center' }}>
                  <h1 className="header-title">💵 <span>{adminTab === 'paidTierClaims' ? 'Paid' : ''} Tier Claims</span></h1>
                  <p className="header-sub">Wager milestone reward claims from the /rewards page</p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', justifyContent: 'center' }}>
                  {[['pending', 'Pending'], ['paid', 'Paid'], ['denied', 'Denied'], ['flagged', '⚠️ Flagged'], ['', 'All']].map(([key, label]) => {
                    const active = (adminTierClaimsFilter || '') === key;
                    const isFlag = key === 'flagged';
                    return (
                      <button key={key || 'all'} onClick={() => setAdminTierClaimsFilter(key)} style={{
                        padding: '0.45rem 1rem', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: 'none',
                        background: active ? (isFlag ? 'rgba(239, 68, 68, 0.18)' : 'rgba(34, 211, 238, 0.15)') : 'rgba(0,0,0,0.2)',
                        color: active ? (isFlag ? '#f87171' : '#22d3ee') : 'var(--text-dim)'
                      }}>{label}</button>
                    );
                  })}
                  <button onClick={fetchAdminTierClaims} style={{ padding: '0.45rem 0.75rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit' }}>↻</button>
                </div>

                {adminTierClaims.length === 0 ? (
                  <div className="card" style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-dim)' }}>No tier claims found.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {adminTierClaims.map(c => {
                      const isPaid = c.status === 'paid';
                      const isDenied = c.status === 'denied';
                      const token = getAuthToken();
                      const post = async (action, body) => {
                        const res = await fetch(`${BACKEND_API_URL}/api/admin/tier-claims/${c.id}/${action}`, {
                          method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify(body || {})
                        });
                        if (res.ok) fetchAdminTierClaims();
                        else { const d = await res.json().catch(() => ({})); alert(d.error || 'Failed'); }
                      };
                      // Name used at claim time vs. what the account has linked now
                      const rbChanged = c.current_rainbet_username && c.rainbet_username && c.current_rainbet_username.toLowerCase() !== c.rainbet_username.toLowerCase();
                      const rbUsChanged = c.current_rainbet_us_username && c.rainbet_us_username && c.current_rainbet_us_username.toLowerCase() !== c.rainbet_us_username.toLowerCase();
                      // Live warnings from the server (computed for every claim, old or new) + the stored flag reason as fallback
                      const warnings = (c.warnings && c.warnings.length) ? c.warnings : (c.flagged && c.flag_reason ? [c.flag_reason] : []);
                      const suspicious = warnings.length > 0;
                      return (
                        <div key={c.id} className="card" style={{ padding: '0.85rem 1.25rem', border: suspicious ? '1px solid rgba(239, 68, 68, 0.5)' : undefined, background: suspicious ? 'rgba(239, 68, 68, 0.05)' : undefined }}>
                          {suspicious && (
                            <div style={{ marginBottom: '0.6rem', padding: '0.5rem 0.75rem', borderRadius: '8px', background: 'rgba(239, 68, 68, 0.12)', border: '1px solid rgba(239, 68, 68, 0.35)', fontSize: '0.76rem', color: '#fca5a5' }}>
                              <strong style={{ color: '#f87171' }}>⚠️ SUSPICIOUS</strong>
                              <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.1rem' }}>
                                {warnings.map((w, i) => <li key={i} style={{ marginBottom: '0.15rem' }}>{w}</li>)}
                              </ul>
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 700 }}>{c.display_name || `User #${c.user_id}`}</span>
                                <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>#{c.user_id}</span>
                                {c.rainbet_username && <span title="Rainbet username at claim time" style={{ padding: '0.1rem 0.4rem', fontSize: '0.65rem', fontWeight: 700, borderRadius: '999px', background: 'rgba(34, 211, 238, 0.15)', color: '#67e8f9' }}>{c.rainbet_username}</span>}
                                {rbChanged && <span title="The account has since linked a different Rainbet username" style={{ padding: '0.1rem 0.4rem', fontSize: '0.65rem', fontWeight: 700, borderRadius: '999px', background: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24' }}>now: {c.current_rainbet_username}</span>}
                                {c.rainbet_us_username && <span title="Rainbet.us username at claim time" style={{ padding: '0.1rem 0.4rem', fontSize: '0.65rem', fontWeight: 700, borderRadius: '999px', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd' }}>{c.rainbet_us_username} · US</span>}
                                {rbUsChanged && <span title="The account has since linked a different Rainbet.us username" style={{ padding: '0.1rem 0.4rem', fontSize: '0.65rem', fontWeight: 700, borderRadius: '999px', background: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24' }}>now: {c.current_rainbet_us_username} · US</span>}
                                {isDenied && <span style={{ padding: '0.1rem 0.4rem', fontSize: '0.65rem', fontWeight: 700, borderRadius: '999px', background: 'rgba(239, 68, 68, 0.15)', color: '#f87171' }}>DENIED</span>}
                              </div>
                              <div style={{ fontSize: '0.82rem', color: '#fbbf24', fontWeight: 600, marginTop: '0.15rem' }}>{c.tier_name} — ${Number(c.reward_amount).toLocaleString()}</div>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                {c.mode} · {c.period_key} · Wagered: ${Number(c.wager_at_claim).toLocaleString()} · {new Date(c.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                              </div>
                              {isDenied && c.notes && <div style={{ fontSize: '0.74rem', color: '#fca5a5', marginTop: '0.2rem' }}>Reason: {c.notes}</div>}
                            </div>
                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                              {isPaid ? (
                                <>
                                  <span style={{ padding: '0.3rem 0.75rem', background: 'rgba(34, 197, 94, 0.12)', color: '#22c55e', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700 }}>✓ Paid</span>
                                  {c.flagged && (
                                    <button onClick={() => { if (confirm('Clear the multi-account flag on this claim?')) post('clear-flag'); }} style={{ padding: '0.3rem 0.6rem', background: 'none', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'inherit' }}>Clear flag</button>
                                  )}
                                </>
                              ) : isDenied ? (
                                <button onClick={() => { if (confirm('Reopen this denied claim as pending?')) post('reopen'); }} style={{ padding: '0.4rem 0.9rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>↩ Reopen</button>
                              ) : (
                                <>
                                  <button onClick={() => {
                                    const warn = suspicious ? `\n\n⚠️ This claim is marked SUSPICIOUS:\n- ${warnings.join('\n- ')}\n\nAre you sure?` : '';
                                    if (!confirm(`Mark $${Number(c.reward_amount).toLocaleString()} tier claim as paid for ${c.display_name || 'this user'}?${warn}`)) return;
                                    post('pay');
                                  }} style={{ padding: '0.4rem 1rem', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                    💰 Mark Paid
                                  </button>
                                  <button onClick={() => {
                                    const reason = prompt('Denial reason (the user will see this):', suspicious ? 'Rainbet account already claimed this reward on another site account' : '');
                                    if (!reason || !reason.trim()) return;
                                    post('deny', { reason: reason.trim() });
                                  }} style={{ padding: '0.4rem 1rem', background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>✗ Deny</button>
                                  {c.flagged && (
                                    <button onClick={() => { if (confirm('Clear the multi-account flag on this claim? (It stays pending.)')) post('clear-flag'); }} style={{ padding: '0.3rem 0.6rem', background: 'none', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'inherit' }}>Clear flag</button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* WAGER CLAIMS ADMIN PANEL */}
            {adminTab === 'wagerClaims' && (
              <div>
                <div className="header" style={{ textAlign: 'center' }}>
                  <h1 className="header-title">📋 <span>Wager Claims</span></h1>
                  <p className="header-sub">Review lossback, weekly double, and milestone claims</p>
                </div>

                {/* Filter tabs */}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', justifyContent: 'center' }}>
                  {['pending', 'approved', 'denied', 'flagged', 'all'].map(f => (
                    <button key={f} onClick={() => { setWagerClaimsFilter(f); setTimeout(fetchWagerClaimsAdmin, 50); }} style={{
                      padding: '0.45rem 1rem', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: 'none',
                      background: wagerClaimsFilter === f ? (f === 'flagged' ? 'rgba(239, 68, 68, 0.18)' : 'rgba(34, 211, 238, 0.15)') : 'rgba(0,0,0,0.2)',
                      color: wagerClaimsFilter === f ? (f === 'flagged' ? '#f87171' : '#22d3ee') : 'var(--text-dim)'
                    }}>{f === 'flagged' ? '⚠️ Flagged' : f.charAt(0).toUpperCase() + f.slice(1)}{f === 'pending' && wagerClaimsAdmin.filter(c => c.status === 'pending').length > 0 ? ` (${wagerClaimsAdmin.filter(c => c.status === 'pending').length})` : ''}</button>
                  ))}
                  <button onClick={fetchWagerClaimsAdmin} style={{ padding: '0.45rem 0.75rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit' }}>↻</button>
                </div>

                {wagerClaimsAdmin.length === 0 ? (
                  <div className="card" style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-dim)' }}>
                    No {wagerClaimsFilter !== 'all' ? wagerClaimsFilter : ''} claims.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {wagerClaimsAdmin.map(c => {
                      const typeLabels = { lossback: '💸 Lossback', weekly_monthly: '🔔 Weekly', milestone: '🚀 Milestone' };
                      const statusColors = { pending: '#f59e0b', approved: '#22c55e', denied: '#ef4444' };
                      const wcWarnings = (c.warnings && c.warnings.length) ? c.warnings : (c.flagged && c.flag_reason ? [c.flag_reason] : []);
                      return (
                        <div key={c.id} className="card" style={{ padding: '1rem 1.25rem', border: wcWarnings.length ? '1px solid rgba(239, 68, 68, 0.5)' : undefined, background: wcWarnings.length ? 'rgba(239, 68, 68, 0.05)' : undefined }}>
                          {wcWarnings.length > 0 && (
                            <div style={{ marginBottom: '0.6rem', padding: '0.5rem 0.75rem', borderRadius: '8px', background: 'rgba(239, 68, 68, 0.12)', border: '1px solid rgba(239, 68, 68, 0.35)', fontSize: '0.76rem', color: '#fca5a5' }}>
                              <strong style={{ color: '#f87171' }}>⚠️ SUSPICIOUS</strong>
                              <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.1rem' }}>
                                {wcWarnings.map((w, i) => <li key={i} style={{ marginBottom: '0.15rem' }}>{w}</li>)}
                              </ul>
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.65rem' }}>
                            <div>
                              <span style={{ fontWeight: 700, fontSize: '0.92rem' }}>{c.display_name || c.kick_username || c.username}</span>
                              <span style={{ marginLeft: '0.35rem', fontSize: '0.65rem', color: 'var(--text-muted)' }}>#{c.user_id}</span>
                              <span style={{ marginLeft: '0.5rem', padding: '0.15rem 0.5rem', fontSize: '0.65rem', fontWeight: 700, borderRadius: '999px', background: `${statusColors[c.status]}22`, color: statusColors[c.status], border: `1px solid ${statusColors[c.status]}44` }}>{c.status}</span>
                            </div>
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{new Date(c.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                            <span style={{ padding: '0.2rem 0.6rem', fontSize: '0.72rem', fontWeight: 700, borderRadius: '6px', background: 'rgba(34, 211, 238, 0.1)', color: '#22d3ee' }}>{typeLabels[c.claim_type] || c.claim_type}</span>
                            {c.rainbet_username && <span style={{ padding: '0.2rem 0.6rem', fontSize: '0.72rem', fontWeight: 600, borderRadius: '6px', background: 'rgba(0,0,0,0.3)', color: 'var(--text-dim)', fontFamily: 'Space Mono, monospace' }}>{c.rainbet_username}</span>}
                            {c.rainbet_us_username && <span title="Rainbet.us account" style={{ padding: '0.2rem 0.6rem', fontSize: '0.72rem', fontWeight: 600, borderRadius: '6px', background: 'rgba(59, 130, 246, 0.12)', color: '#93c5fd', fontFamily: 'Space Mono, monospace' }}>{c.rainbet_us_username} · US</span>}
                          </div>
                          {c.notes && <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.5rem' }}>📝 {c.notes}</div>}
                          {c.screenshot && (
                            <div style={{ marginBottom: '0.75rem' }}>
                              <img src={c.screenshot} alt="Claim screenshot" style={{ maxWidth: '100%', maxHeight: 450, borderRadius: '8px', border: '1px solid var(--border)', cursor: 'zoom-in' }} onClick={(e) => { e.stopPropagation(); setLightboxImg(c.screenshot); }} />
                            </div>
                          )}
                          {c.admin_notes && <div style={{ fontSize: '0.78rem', color: '#fbbf24', marginBottom: '0.5rem' }}>Admin: {c.admin_notes}</div>}
                          {c.status === 'pending' && (
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <button onClick={async () => {
                                const notes = prompt('Admin notes (optional):');
                                const token = getAuthToken();
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/claims/${c.id}`, {
                                  method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ status: 'approved', adminNotes: notes || '' })
                                });
                                if (res.ok) fetchWagerClaimsAdmin(); else alert('Failed');
                              }} style={{ padding: '0.4rem 1rem', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>✓ Approve</button>
                              <button onClick={async () => {
                                const reason = prompt('Denial reason:');
                                if (!reason) return;
                                const token = getAuthToken();
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/claims/${c.id}`, {
                                  method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ status: 'denied', adminNotes: reason })
                                });
                                if (res.ok) fetchWagerClaimsAdmin(); else alert('Failed');
                              }} style={{ padding: '0.4rem 1rem', background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>✗ Deny</button>
                              <button onClick={async () => {
                                if (!confirm('Delete this claim?')) return;
                                const token = getAuthToken();
                                await fetch(`${BACKEND_API_URL}/api/admin/claims/${c.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                                fetchWagerClaimsAdmin();
                              }} style={{ padding: '0.4rem 0.6rem', background: 'none', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit' }}>🗑️</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {adminTab === 'challengeClaims' && (() => {
              // Client-side search filter
              const q = adminChallengeClaimSearch.trim().toLowerCase();
              const filteredClaims = q
                ? adminChallengeClaims.filter(c =>
                    (c.slot_name || '').toLowerCase().includes(q) ||
                    (c.discord_username || '').toLowerCase().includes(q) ||
                    (c.rainbet_username || '').toLowerCase().includes(q) ||
                    (c.rainbet_us_username || '').toLowerCase().includes(q) ||
                    (c.rainbet_id || '').toLowerCase().includes(q) ||
                    (c.display_name || '').toLowerCase().includes(q)
                  )
                : adminChallengeClaims;
              const pendingSelected = filteredClaims.filter(c => c.status === 'pending' && adminChallengeClaimSelected.has(c.id));
              const toggleSelect = (id) => {
                const next = new Set(adminChallengeClaimSelected);
                if (next.has(id)) next.delete(id); else next.add(id);
                setAdminChallengeClaimSelected(next);
              };
              const clearSelection = () => setAdminChallengeClaimSelected(new Set());
              return (
              <div className="card">
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div className="card-icon">🏆</div>
                    <div className="card-title">Challenge Claims ({filteredClaims.length}{q ? ` of ${adminChallengeClaims.length}` : ''})</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      value={adminChallengeClaimSearch}
                      onChange={(e) => setAdminChallengeClaimSearch(e.target.value)}
                      placeholder="🔍 Search slot, user, Rainbet ID..."
                      style={{ padding: '0.45rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.85rem', minWidth: '220px' }}
                    />
                    <select
                      value={adminChallengeClaimFilter}
                      onChange={(e) => { setAdminChallengeClaimFilter(e.target.value); clearSelection(); }}
                      style={{ padding: '0.45rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.85rem' }}
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="all">All</option>
                    </select>
                  </div>
                </div>

                {/* Bulk action bar — appears when multiple pending claims selected */}
                {pendingSelected.length > 0 && (
                  <div style={{ padding: '0.75rem 1.25rem', background: 'rgba(59, 130, 246, 0.1)', borderBottom: '1px solid rgba(59, 130, 246, 0.25)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '0.85rem', color: '#93c5fd' }}>
                      <strong>{pendingSelected.length}</strong> claim{pendingSelected.length === 1 ? '' : 's'} selected
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={async () => {
                          const total = pendingSelected.reduce((s, c) => s + Number(c.prize_amount || 0), 0);
                          if (!window.confirm(`Approve ${pendingSelected.length} claims? Total prize payout: $${total.toLocaleString()}.\n\nMake sure you've already sent each prize — this marks them as approved and auto-completes their parent challenges.`)) return;
                          const token = getAuthToken();
                          for (const c of pendingSelected) {
                            await fetch(`${BACKEND_API_URL}/api/admin/challenge-claims/${c.id}`, {
                              method: 'PUT',
                              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ status: 'approved' })
                            }).catch(() => {});
                            await fetch(`${BACKEND_API_URL}/api/admin/challenges/${c.challenge_id}/complete`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ completed_by: c.rainbet_username || c.display_name })
                            }).catch(() => {});
                          }
                          clearSelection();
                          fetchAdminChallengeClaims(adminChallengeClaimFilter);
                        }}
                        style={{ padding: '0.45rem 0.95rem', background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                      >✓ Approve selected ({pendingSelected.length})</button>
                      <button
                        onClick={async () => {
                          const reason = window.prompt(`Reject ${pendingSelected.length} claims. Reason (applied to all):`);
                          if (reason === null) return;
                          const token = getAuthToken();
                          for (const c of pendingSelected) {
                            await fetch(`${BACKEND_API_URL}/api/admin/challenge-claims/${c.id}`, {
                              method: 'PUT',
                              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ status: 'rejected', notes: reason || null })
                            }).catch(() => {});
                          }
                          clearSelection();
                          fetchAdminChallengeClaims(adminChallengeClaimFilter);
                        }}
                        style={{ padding: '0.45rem 0.95rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                      >✗ Reject selected</button>
                      <button
                        onClick={clearSelection}
                        style={{ padding: '0.45rem 0.95rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit' }}
                      >Clear</button>
                    </div>
                  </div>
                )}

                {filteredClaims.length === 0 ? (
                  <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)' }}>
                    {q ? `No matches for "${adminChallengeClaimSearch}".` : `No ${adminChallengeClaimFilter === 'all' ? '' : adminChallengeClaimFilter} claims.`}
                  </div>
                ) : (
                  filteredClaims.map(claim => {
                    const statusColor = claim.status === 'approved' ? '#22c55e' : claim.status === 'rejected' ? '#ef4444' : '#f59e0b';
                    const isSelected = adminChallengeClaimSelected.has(claim.id);
                    return (
                      <div key={claim.id} style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'grid', gridTemplateColumns: claim.status === 'pending' ? 'auto 1fr auto' : '1fr auto', gap: '1rem', alignItems: 'flex-start', background: isSelected ? 'rgba(59, 130, 246, 0.06)' : 'transparent' }}>
                        {claim.status === 'pending' && (
                          <div style={{ paddingTop: '0.35rem' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(claim.id)}
                              style={{ width: 18, height: 18, cursor: 'pointer' }}
                            />
                          </div>
                        )}
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{claim.slot_name}</span>
                            <span style={{ padding: '0.15rem 0.55rem', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: statusColor, background: `${statusColor}15`, border: `1px solid ${statusColor}40` }}>{claim.status}</span>
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>
                              Target: <strong style={{ color: 'var(--gold)' }}>{Number(claim.target_multiplier).toLocaleString()}x</strong>
                            </span>
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>
                              Prize: <strong style={{ color: '#22c55e' }}>${Number(claim.prize_amount).toLocaleString()}</strong>
                            </span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.5rem 1rem', fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.5rem' }}>
                            <div>
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>User</span>
                              <div><strong style={{ color: 'var(--text)' }}>{claim.display_name || `user #${claim.user_id}`}</strong></div>
                            </div>
                            <div>
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Discord</span>
                              <div><code style={{ background: 'rgba(255,255,255,0.05)', padding: '0.1rem 0.35rem', borderRadius: '3px', fontSize: '0.78rem' }}>{claim.discord_username}</code></div>
                            </div>
                            <div>
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Rainbet username</span>
                              <div><code style={{ background: 'rgba(255,255,255,0.05)', padding: '0.1rem 0.35rem', borderRadius: '3px', fontSize: '0.78rem' }}>{claim.rainbet_username}</code></div>
                            </div>
                            {claim.rainbet_id && (
                              <div>
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Rainbet ID</span>
                                <div><code style={{ background: 'rgba(255,255,255,0.05)', padding: '0.1rem 0.35rem', borderRadius: '3px', fontSize: '0.78rem' }}>{claim.rainbet_id}</code></div>
                              </div>
                            )}
                          </div>
                          {claim.bet_link && (
                            <div style={{ marginBottom: '0.4rem' }}>
                              <a href={claim.bet_link} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.85rem', background: 'rgba(59, 130, 246, 0.12)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '6px', fontSize: '0.8rem', textDecoration: 'none', fontWeight: 600 }}>
                                🔗 Open bet link
                              </a>
                            </div>
                          )}
                          {claim.notes && (
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginTop: '0.3rem' }}>
                              <span style={{ color: 'var(--text-muted)' }}>Notes:</span> {claim.notes}
                            </div>
                          )}
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                            Submitted {new Date(claim.created_at).toLocaleString()}
                            {claim.reviewed_at && <> · Reviewed {new Date(claim.reviewed_at).toLocaleString()}</>}
                          </div>
                        </div>
                        {claim.status === 'pending' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Approve this claim? Make sure you have:\n• Verified the bet link shows the win\n• Confirmed the Rainbet ID matches the user\n• Sent the $${Number(claim.prize_amount).toLocaleString()} prize`)) return;
                                const token = getAuthToken();
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/challenge-claims/${claim.id}`, {
                                  method: 'PUT',
                                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ status: 'approved' })
                                });
                                if (res.ok) {
                                  fetchAdminChallengeClaims(adminChallengeClaimFilter);
                                  // Also auto-mark the parent challenge as completed by this user
                                  await fetch(`${BACKEND_API_URL}/api/admin/challenges/${claim.challenge_id}/complete`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ completed_by: claim.rainbet_username || claim.display_name })
                                  }).catch(() => {});
                                } else alert('Failed to approve');
                              }}
                              style={{ padding: '0.5rem 1rem', background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                            >✓ Approve</button>
                            <button
                              onClick={async () => {
                                const reason = window.prompt('Reason for rejection (optional, shown to user):');
                                if (reason === null) return;
                                const token = getAuthToken();
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/challenge-claims/${claim.id}`, {
                                  method: 'PUT',
                                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ status: 'rejected', notes: reason || claim.notes })
                                });
                                if (res.ok) fetchAdminChallengeClaims(adminChallengeClaimFilter);
                                else alert('Failed to reject');
                              }}
                              style={{ padding: '0.5rem 1rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                            >✗ Reject</button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              );
            })()}

            {/* Paid Giveaways Tab */}
            {adminTab === 'paidGiveaways' && (
              <div className="card">
                <div className="card-header">
                  <div className="card-icon">✅</div>
                  <div className="card-title">Paid Giveaways ({adminPaidGiveawaysTotal})</div>
                </div>
                
                {/* Search */}
                <div style={{ padding: '1rem', borderBottom: '1px solid var(--border)' }}>
                  <input
                    type="text"
                    placeholder="Search by username..."
                    value={adminPaidGiveawaysSearch}
                    onChange={(e) => {
                      setAdminPaidGiveawaysSearch(e.target.value);
                      setAdminPaidGiveawaysPage(1);
                      fetchAdminPaidGiveaways(e.target.value, 1);
                    }}
                    style={{ width: '100%', maxWidth: 300, padding: '0.65rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit' }}
                  />
                </div>
                
                {adminPaidGiveaways.length > 0 ? (
                  <>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,0.2)' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Recipient</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right' }}>Amount</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Description</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Paid By</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Date</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminPaidGiveaways.map(giveaway => (
                            <tr key={giveaway.id} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ padding: '0.75rem', fontFamily: 'monospace', color: '#22c55e' }}>{giveaway.rainbet_username}</td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600, color: 'var(--gold)' }}>${giveaway.amount}</td>
                              <td style={{ padding: '0.75rem', color: 'var(--text-dim)' }}>{giveaway.description || '-'}</td>
                              <td style={{ padding: '0.75rem' }}>{giveaway.paid_by || '-'}</td>
                              <td style={{ padding: '0.75rem', fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                                {giveaway.paid_at ? new Date(giveaway.paid_at).toLocaleDateString() : '-'}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center' }}>
                                  <button
                                    onClick={async () => {
                                      const newAmount = prompt('Enter new amount:', giveaway.amount);
                                      if (newAmount === null) return;
                                      const newDesc = prompt('Enter new description:', giveaway.description || '');
                                      if (newDesc === null) return;
                                      try {
                                        const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/giveaways/${giveaway.id}`, {
                                          method: 'PUT',
                                          body: JSON.stringify({ amount: parseFloat(newAmount), description: newDesc })
                                        });
                                        if (res.ok) {
                                          fetchAdminPaidGiveaways(adminPaidGiveawaysSearch, adminPaidGiveawaysPage);
                                          fetchAdminStats();
                                        } else {
                                          alert('Failed to update');
                                        }
                                      } catch (e) { alert('Error'); }
                                    }}
                                    style={{ padding: '0.3rem 0.5rem', background: 'rgba(139, 92, 246, 0.2)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '4px', color: '#8b5cf6', cursor: 'pointer', fontSize: '0.75rem' }}
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm('Delete this paid giveaway? This will remove it from the total paid out.')) return;
                                      try {
                                        const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/giveaways/${giveaway.id}`, {
                                          method: 'DELETE'
                                        });
                                        if (res.ok) {
                                          fetchAdminPaidGiveaways(adminPaidGiveawaysSearch, adminPaidGiveawaysPage);
                                          fetchAdminStats();
                                        } else {
                                          alert('Failed to delete');
                                        }
                                      } catch (e) { alert('Error'); }
                                    }}
                                    style={{ padding: '0.3rem 0.5rem', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '4px', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {adminPaidGiveawaysTotal > 50 && (
                      <div style={{ padding: '1rem', display: 'flex', justifyContent: 'center', gap: '0.5rem', alignItems: 'center' }}>
                        <button 
                          onClick={() => { setAdminPaidGiveawaysPage(p => p - 1); fetchAdminPaidGiveaways(adminPaidGiveawaysSearch, adminPaidGiveawaysPage - 1); }}
                          disabled={adminPaidGiveawaysPage === 1}
                          style={{ padding: '0.5rem 1rem', background: adminPaidGiveawaysPage === 1 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'var(--text)', cursor: adminPaidGiveawaysPage === 1 ? 'not-allowed' : 'pointer' }}
                        >← Prev</button>
                        <span style={{ color: 'var(--text-dim)' }}>Page {adminPaidGiveawaysPage} of {Math.ceil(adminPaidGiveawaysTotal / 50)}</span>
                        <button 
                          onClick={() => { setAdminPaidGiveawaysPage(p => p + 1); fetchAdminPaidGiveaways(adminPaidGiveawaysSearch, adminPaidGiveawaysPage + 1); }}
                          disabled={adminPaidGiveawaysPage >= Math.ceil(adminPaidGiveawaysTotal / 50)}
                          style={{ padding: '0.5rem 1rem', background: adminPaidGiveawaysPage >= Math.ceil(adminPaidGiveawaysTotal / 50) ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'var(--text)', cursor: adminPaidGiveawaysPage >= Math.ceil(adminPaidGiveawaysTotal / 50) ? 'not-allowed' : 'pointer' }}
                        >Next →</button>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-dim)' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
                    No paid giveaways yet
                  </div>
                )}
              </div>
            )}
            
            {/* Unclaimed Wins Tab */}
            {adminTab === 'unclaimedWins' && (
              <div className="card">
                <div className="card-header">
                  <div className="card-icon">⚠️</div>
                  <div className="card-title">Unclaimed Wins ({adminUnclaimedWins.length})</div>
                </div>
                
                {/* Sort Controls */}
                {adminUnclaimedWins.length > 0 && (
                  <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>Sort by:</span>
                    <SortButtons
                      sortConfig={unclaimedWinsSort}
                      setSortConfig={setUnclaimedWinsSort}
                      options={[
                        { value: 'hunt_id', label: 'Hunt ID' },
                        { value: 'rank', label: 'Rank' },
                        { value: 'created_at', label: 'Date' },
                        { value: 'username', label: 'Username' }
                      ]}
                    />
                  </div>
                )}
                
                {adminUnclaimedWins.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,0.2)' }}>
                          <th style={{ padding: '0.75rem', textAlign: 'left' }}>User</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left' }}>Rainbet</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left' }}>Hunt</th>
                          <th style={{ padding: '0.75rem', textAlign: 'center' }}>Rank</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left' }}>Date</th>
                          <th style={{ padding: '0.75rem', textAlign: 'center' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortData(adminUnclaimedWins, unclaimedWinsSort).map(win => (
                          <tr key={win.id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {win.profile_image_url && <img src={win.profile_image_url} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} />}
                              {win.display_name || win.username}
                            </td>
                            <td style={{ padding: '0.75rem', fontFamily: 'monospace', color: win.rainbet_username ? '#22c55e' : 'var(--text-dim)' }}>
                              {win.rainbet_username || 'Not linked'}
                            </td>
                            <td style={{ padding: '0.75rem' }}>#{win.hunt_id}</td>
                            <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                              {win.rank === 1 ? '🥇' : win.rank === 2 ? '🥈' : '🥉'}
                            </td>
                            <td style={{ padding: '0.75rem', fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                              {win.created_at ? new Date(win.created_at).toLocaleDateString() : '-'}
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                {win.rainbet_username && (
                                  <button
                                    onClick={async () => {
                                      const amount = prompt('Enter reward amount:');
                                      if (!amount) return;
                                      try {
                                        const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/unclaimed/${win.id}/pay`, {
                                          method: 'POST',
                                          body: JSON.stringify({ amount: parseFloat(amount) })
                                        });
                                        if (res.ok) {
                                          fetchAdminUnclaimedWins();
                                          fetchAdminStats();
                                        }
                                      } catch (e) { alert('Error'); }
                                    }}
                                    style={{ padding: '0.4rem 0.75rem', background: 'linear-gradient(135deg, #22c55e, #16a34a)', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
                                  >
                                    💰 Pay
                                  </button>
                                )}
                                <button
                                  onClick={() => deletePredictionWin(win.id, false)}
                                  style={{ padding: '0.4rem 0.75rem', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '6px', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
                                  title="Delete this pending reward"
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-dim)' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
                    No unclaimed wins
                  </div>
                )}
              </div>
            )}

            {adminTab === 'users' && (
              <div className="card">
                <div className="card-header">
                  <div className="card-icon">👥</div>
                  <div className="card-title">Users ({adminUsersTotal})</div>
                </div>
                
                {/* Search */}
                <div style={{ padding: '1rem', borderBottom: '1px solid var(--border)' }}>
                  <input
                    type="text"
                    placeholder="Search by username or Rainbet..."
                    value={adminUserSearch}
                    onChange={(e) => {
                      setAdminUserSearch(e.target.value);
                      setAdminUsersPage(1);
                      fetchAdminUsers(e.target.value, 1);
                    }}
                    style={{ width: '100%', maxWidth: 300, padding: '0.65rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit' }}
                  />
                </div>
                
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,0.2)' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>User</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Rainbet</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Predictions</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Wins</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Last Login</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminUsers.map(u => (
                        <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {u.profile_image_url && <img src={u.profile_image_url} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} />}
                            {u.display_name || u.username}
                          </td>
                          <td style={{ padding: '0.75rem', fontFamily: 'monospace', color: u.rainbet_username ? '#22c55e' : 'var(--text-dim)' }}>
                            {[u.rainbet_username, u.rainbet_us_username && `${u.rainbet_us_username} (.us)`].filter(Boolean).join(' / ') || '-'}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{u.total_predictions}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{u.total_wins}</td>
                          <td style={{ padding: '0.75rem', fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                            {u.last_login ? new Date(u.last_login).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {adminUsersTotal > 50 && (
                  <div style={{ padding: '1rem', display: 'flex', justifyContent: 'center', gap: '0.5rem', alignItems: 'center' }}>
                    <button 
                      onClick={() => { setAdminUsersPage(p => p - 1); fetchAdminUsers(adminUserSearch, adminUsersPage - 1); }}
                      disabled={adminUsersPage === 1}
                      style={{ padding: '0.5rem 1rem', background: adminUsersPage === 1 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'var(--text)', cursor: adminUsersPage === 1 ? 'not-allowed' : 'pointer' }}
                    >← Prev</button>
                    <span style={{ color: 'var(--text-dim)' }}>Page {adminUsersPage} of {Math.ceil(adminUsersTotal / 50)}</span>
                    <button 
                      onClick={() => { setAdminUsersPage(p => p + 1); fetchAdminUsers(adminUserSearch, adminUsersPage + 1); }}
                      disabled={adminUsersPage >= Math.ceil(adminUsersTotal / 50)}
                      style={{ padding: '0.5rem 1rem', background: adminUsersPage >= Math.ceil(adminUsersTotal / 50) ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'var(--text)', cursor: adminUsersPage >= Math.ceil(adminUsersTotal / 50) ? 'not-allowed' : 'pointer' }}
                    >Next →</button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

            {/* Slot Bingo page — public read-only board + admin controls */}
            {activePage === 'bingo' && (() => {
              const isAdmin = !!user?.isAdmin;
              const bs = bingoState || { status: 'idle', board: [], entryCount: 0, recentEntries: [] };
              const status = bs.status || 'idle';
              const board = Array.isArray(bs.board) ? bs.board : [];
              const paidCount = board.filter(s => s && s.status === 'paid').length;
              const winLine = Array.isArray(bs.winningLine) ? bs.winningLine : [];
              const activePos = (bs.activePos === 0 || bs.activePos) ? bs.activePos : null;
              const playing = status === 'playing' || status === 'complete';
              const widgetUrl = `${BOT_API_URL}/widget/bingo`;

              // The square the admin will act on: explicit click selection, else the active one
              const selPos = (bingoSelectedPos != null && board[bingoSelectedPos] && board[bingoSelectedPos].status !== 'paid')
                ? bingoSelectedPos
                : (activePos != null ? activePos : null);
              const selSquare = selPos != null ? board[selPos] : null;

              const act = async (path) => { const ok = await bingoAction(path); return ok; };
              const onStart = () => act('/bingo/start');
              const onFill = () => act('/bingo/fill');
              const onPickRandom = () => { ensureBingoAudio(); setBingoSelectedPos(null); act('/bingo/pick-random'); };
              const onClose = () => { if (window.confirm('End the current bingo session?')) act('/bingo/close').then(() => setBingoSelectedPos(null)); };
              const onSelect = (pos) => { setBingoSelectedPos(pos); act(`/bingo/square/${pos}/select`); };
              const onPay = (pos) => { act(`/bingo/square/${pos}/pay`).then(() => setBingoSelectedPos(null)); };
              const onReplace = (pos) => { act(`/bingo/square/${pos}/replace`); };
              const onUnpay = (pos) => { act(`/bingo/square/${pos}/unpay`); };
              const onUndoReplace = (pos) => { act(`/bingo/square/${pos}/undo-replace`); };
              const onPutOnSquare = (pos, entry) => {
                const sq = board[pos];
                if (window.confirm(`Put ${entry.display_name || entry.username} (${entry.slot_name}) on square ${pos + 1}${sq ? `, replacing ${sq.display_name || sq.username}` : ''}?`)) {
                  bingoAction(`/bingo/square/${pos}/replace`, { body: JSON.stringify({ entry_id: entry.id }) });
                }
              };
              const onRemoveEntry = (entry) => {
                if (window.confirm(`Remove ${entry.display_name || entry.username} ("${entry.slot_name}") from the pool? They can re-enter with !join.`)) {
                  bingoAction(`/bingo/entries/${entry.id}/remove`);
                }
              };
              // Last Replace on the selected square can be undone
              const lastHist = selSquare && Array.isArray(selSquare.history) ? selSquare.history[selSquare.history.length - 1] : null;
              const canUndoReplace = !!(lastHist && lastHist.result === 'replaced');

              const platBadge = (p) => (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 22, height: 22, borderRadius: 6, fontSize: '0.7rem', fontWeight: 800,
                  background: p === 'kick' ? 'rgba(83,252,24,0.16)' : 'rgba(145,71,255,0.18)',
                  color: p === 'kick' ? '#53fc18' : '#9147ff'
                }}>{p === 'kick' ? 'K' : 'T'}</span>
              );

              const cellBg = (st, isSel) => {
                if (st === 'paid') return 'linear-gradient(160deg, rgba(8,40,22,0.96), rgba(4,20,11,0.98))';
                if (st === 'active') return 'linear-gradient(160deg, rgba(40,32,8,0.95), rgba(20,15,4,0.98))';
                return 'linear-gradient(160deg, rgba(255,255,255,0.025), rgba(0,0,0,0.35))';
              };
              const cellBorder = (st, isSel) => {
                if (isSel) return '2px solid #22d3ee';
                if (st === 'paid') return '1px solid rgba(34,197,94,0.7)';
                if (st === 'active') return '1px solid rgba(251,191,36,0.85)';
                return '1px solid var(--border)';
              };

              return (
                <>
                  <div className="header">
                    <h1 className="header-title">🎯 <span>Slot Bingo</span></h1>
                    <p className="header-sub">
                      9 viewers, 9 slots, one board. Type <strong style={{ color: '#22d3ee' }}>!join &lt;slot name&gt;</strong> in chat to enter — if Cam's spin pays, your square lights up green!
                    </p>
                  </div>

                  {bingoError && isAdmin && (
                    <div className="card" style={{ borderColor: 'rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.06)' }}>
                      <span style={{ color: '#ef4444', fontWeight: 600 }}>⚠️ {bingoError}</span>
                    </div>
                  )}

                  {/* ---------- Admin control bar ---------- */}
                  {isAdmin && (
                    <div className="card">
                      <div className="card-header">
                        <div className="card-icon" style={{ background: 'rgba(251,191,36,0.1)', borderColor: 'rgba(251,191,36,0.25)' }}>🎮</div>
                        <div className="card-title">Admin Controls</div>
                        <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                          {status === 'idle' ? 'No session' : status}
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center' }}>
                        {status === 'idle' && (
                          <button disabled={bingoBusy} onClick={onStart} style={{
                            padding: '0.6rem 1.1rem', borderRadius: 10, border: '1px solid rgba(34,211,238,0.4)', cursor: bingoBusy ? 'wait' : 'pointer',
                            background: 'linear-gradient(135deg, #06b6d4, #0891b2)', color: '#fff', fontWeight: 700
                          }}>▶ Start Bingo</button>
                        )}
                        {status === 'open' && (
                          <>
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: '1.4rem', fontWeight: 700, color: '#fff' }}>{bs.entryCount || 0}</span>
                            <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem', marginRight: '0.4rem' }}>entries</span>
                            <button disabled={bingoBusy || (bs.entryCount || 0) < 9} onClick={onFill} title={(bs.entryCount || 0) < 9 ? 'Need at least 9 entries' : ''} style={{
                              padding: '0.6rem 1.1rem', borderRadius: 10, border: '1px solid rgba(251,191,36,0.4)',
                              cursor: (bingoBusy || (bs.entryCount || 0) < 9) ? 'not-allowed' : 'pointer', opacity: (bs.entryCount || 0) < 9 ? 0.5 : 1,
                              background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#1a1208', fontWeight: 800
                            }}>🎰 Fill Board</button>
                          </>
                        )}
                        {status === 'playing' && (
                          <>
                            <button disabled={bingoBusy || bingoRolling || paidCount >= 9} onClick={onPickRandom} style={{
                              padding: '0.6rem 1.1rem', borderRadius: 10, border: '1px solid rgba(167,139,250,0.45)',
                              cursor: (bingoBusy || bingoRolling || paidCount >= 9) ? 'not-allowed' : 'pointer', opacity: (paidCount >= 9 || bingoRolling) ? 0.5 : 1,
                              background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', color: '#fff', fontWeight: 700
                            }}>{bingoRolling ? '🎲 Rolling…' : '🎲 Pick Random'}</button>
                            <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>
                              <b style={{ color: '#22c55e' }}>{paidCount}</b> / 9 paid
                            </span>
                          </>
                        )}
                        {status !== 'idle' && (
                          <button disabled={bingoBusy} onClick={onClose} style={{
                            marginLeft: 'auto', padding: '0.6rem 1rem', borderRadius: 10, border: '1px solid rgba(239,68,68,0.4)', cursor: 'pointer',
                            background: 'rgba(239,68,68,0.12)', color: '#fca5a5', fontWeight: 700
                          }}>End Bingo</button>
                        )}
                      </div>

                      {/* Selected-square action row */}
                      {status === 'playing' && !bingoRolling && selSquare && selSquare.status !== 'paid' && (
                        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
                          {selSquare.card_image && (
                            <img src={selSquare.card_image} alt="" onError={e => { e.target.style.display = 'none'; }}
                              style={{ width: 42, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(251,191,36,0.5)', flexShrink: 0 }} />
                          )}
                          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>Now playing · square {selPos + 1}</span>
                            <span style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: '1.05rem', color: '#fbbf24' }}>
                              {selSquare.display_name || selSquare.username}{' '}
                              <span style={{ color: 'var(--text-dim)', fontWeight: 400 }}>— {selSquare.slot_match || selSquare.slot_name}</span>
                            </span>
                            {/* Catalog match vs what chat typed; ✏️ fixes typos (art re-matches) */}
                            {slotEdit?.key === `bingo-${selPos}` ? (
                              <span style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                                <datalist id="cnw-slot-names">{slotNameOptions}</datalist>
                                <input autoFocus list="cnw-slot-names" placeholder="Slot name" value={slotEdit.value}
                                  onChange={e => setSlotEdit(v => ({ ...v, value: e.target.value }))}
                                  onKeyDown={async e => {
                                    if (e.key === 'Escape') setSlotEdit(null);
                                    if (e.key === 'Enter' && slotEdit.value.trim() && await bingoAction(`/bingo/square/${selPos}/slot`, { body: JSON.stringify({ slot_name: slotEdit.value }) })) setSlotEdit(null);
                                  }}
                                  style={{ padding: '0.35rem 0.55rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: '0.82rem', fontFamily: 'inherit', width: 220 }} />
                                <button disabled={bingoBusy || !slotEdit.value.trim()} onClick={async () => { if (await bingoAction(`/bingo/square/${selPos}/slot`, { body: JSON.stringify({ slot_name: slotEdit.value }) })) setSlotEdit(null); }}
                                  style={{ padding: '0.35rem 0.7rem', borderRadius: 6, border: 'none', background: 'linear-gradient(135deg, #fbbf24, #d97706)', color: '#000', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}>Save</button>
                                <button onClick={() => setSlotEdit(null)} style={{ padding: '0.35rem 0.55rem', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-dim)', fontSize: '0.75rem', cursor: 'pointer' }}>✕</button>
                              </span>
                            ) : (
                              <span style={{ fontSize: '0.72rem', color: selSquare.slot_match ? 'var(--text-muted)' : '#fca5a5', marginTop: '0.15rem' }}>
                                {selSquare.slot_match
                                  ? (selSquare.slot_match.toLowerCase() !== String(selSquare.slot_name || '').toLowerCase() ? `typed “${selSquare.slot_name}”` : 'matched in catalog')
                                  : 'not in catalog — no card art'}
                                <button onClick={() => setSlotEdit({ key: `bingo-${selPos}`, value: selSquare.slot_match || selSquare.slot_name || '' })}
                                  style={{ marginLeft: '0.4rem', background: 'none', border: 'none', color: '#93c5fd', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700, padding: 0 }}>✏️ change slot</button>
                              </span>
                            )}
                          </div>
                          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.6rem' }}>
                            <button disabled={bingoBusy} onClick={() => onPay(selPos)} style={{
                              padding: '0.6rem 1.2rem', borderRadius: 10, border: '1px solid rgba(34,197,94,0.5)', cursor: 'pointer',
                              background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#06210f', fontWeight: 800
                            }}>✅ Pay</button>
                            <button disabled={bingoBusy} onClick={() => onReplace(selPos)} style={{
                              padding: '0.6rem 1.2rem', borderRadius: 10, border: '1px solid rgba(239,68,68,0.5)', cursor: 'pointer',
                              background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff', fontWeight: 800
                            }}>🔄 Replace</button>
                            {canUndoReplace && (
                              <button disabled={bingoBusy} onClick={() => onUndoReplace(selPos)} title={`Bring back ${lastHist.display_name || lastHist.username} (${lastHist.slot_name})`} style={{
                                padding: '0.6rem 1rem', borderRadius: 10, border: '1px solid rgba(255,255,255,0.22)', cursor: 'pointer',
                                background: 'rgba(255,255,255,0.06)', color: '#e5e7eb', fontWeight: 700
                              }}>↩ Undo Replace <span style={{ fontWeight: 400, color: 'var(--text-dim)', fontSize: '0.8rem' }}>({lastHist.display_name || lastHist.username})</span></button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Entries panel — remove trolls, or hand-pick who goes on the selected square */}
                      {status !== 'idle' && (
                        <div style={{ marginTop: '0.9rem' }}>
                          <button onClick={() => setBingoShowEntries(v => !v)} style={{ padding: '0.35rem 0.7rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: 6, fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                            {bingoShowEntries ? '✕ Hide entries' : `👥 Entries (${bs.entryCount || 0})`}
                          </button>
                          {bingoShowEntries && (() => {
                            const q = bingoEntrySearch.trim().toLowerCase();
                            const list = q ? bingoEntries.filter(en => `${en.display_name} ${en.username} ${en.slot_name} ${en.slot_match || ''}`.toLowerCase().includes(q)) : bingoEntries;
                            const target = status === 'playing' && selSquare && selSquare.status !== 'paid' ? selPos : null;
                            return (
                              <>
                                <input value={bingoEntrySearch} onChange={e => setBingoEntrySearch(e.target.value)} placeholder={`Search ${bingoEntries.length} entries by name or slot…`}
                                  style={{ width: '100%', boxSizing: 'border-box', marginTop: '0.5rem', padding: '0.45rem 0.6rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: '0.82rem', fontFamily: 'inherit' }} />
                                {target != null && (
                                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.35rem' }}>⤵ puts that viewer on <b style={{ color: '#22d3ee' }}>square {target + 1}</b> (the selected one)</div>
                                )}
                                <div style={{ marginTop: '0.4rem', maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, background: 'rgba(0,0,0,0.25)' }}>
                                  {list.length === 0 && <div style={{ padding: '0.7rem', fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>{q ? 'No matches.' : 'No entries yet.'}</div>}
                                  {list.map((en, i) => {
                                    const onBoard = en.square != null;
                                    return (
                                      <div key={en.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.6rem', borderBottom: i < list.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none', fontSize: '0.82rem', opacity: en.used && !onBoard ? 0.55 : 1 }}>
                                        {en.card_image
                                          ? <img src={en.card_image} alt="" onError={e => { e.target.style.display = 'none'; }} style={{ width: 20, height: 27, objectFit: 'cover', borderRadius: 3, flexShrink: 0 }} />
                                          : <span style={{ width: 20, textAlign: 'center', flexShrink: 0, opacity: 0.4 }}>🎰</span>}
                                        {platBadge(en.platform)}
                                        <span style={{ fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>{en.display_name || en.username}</span>
                                        <span title={en.slot_name} style={{ color: en.slot_match ? 'var(--text-dim)' : '#fca5a5', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: 1 }}>{en.slot_match || `“${en.slot_name}”`}</span>
                                        {onBoard
                                          ? <span style={{ fontSize: '0.65rem', fontWeight: 800, color: '#fbbf24', border: '1px solid rgba(251,191,36,0.4)', borderRadius: 4, padding: '0 0.3rem', flexShrink: 0 }}>SQUARE {en.square + 1}</span>
                                          : en.used ? <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', flexShrink: 0 }}>played</span> : null}
                                        {!onBoard && target != null && (
                                          <button disabled={bingoBusy} onClick={() => onPutOnSquare(target, en)} title={`Put on square ${target + 1}`}
                                            style={{ padding: '0.15rem 0.45rem', borderRadius: 5, border: '1px solid rgba(34,211,238,0.4)', background: 'rgba(34,211,238,0.1)', color: '#22d3ee', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>⤵ {target + 1}</button>
                                        )}
                                        {!onBoard && (
                                          <button disabled={bingoBusy} onClick={() => onRemoveEntry(en)} title="Remove from the pool"
                                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.85rem', flexShrink: 0 }}>✕</button>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      )}

                      {/* OBS link hidden behind a button so it never shows on stream */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.4rem', marginTop: '0.9rem' }}>
                        {bingoShowWidget && (
                          <>
                            <code style={{ padding: '0.25rem 0.55rem', background: 'rgba(0,0,0,0.35)', borderRadius: '6px', fontSize: '0.72rem', color: '#93c5fd', userSelect: 'all' }}>{widgetUrl}</code>
                            <button onClick={() => { try { navigator.clipboard.writeText(widgetUrl); } catch (e) {} }} style={{ padding: '0.3rem 0.6rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>📋 Copy</button>
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>500×500</span>
                          </>
                        )}
                        <button onClick={() => setBingoShowWidget(v => !v)} style={{ padding: '0.3rem 0.6rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                          {bingoShowWidget ? '✕ Hide' : '🖥 Get Widget URL'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ---------- Idle ---------- */}
                  {status === 'idle' && (
                    <div className="card" style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
                      <div style={{ fontSize: '3rem', marginBottom: '0.75rem', opacity: 0.7 }}>🎯</div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.4rem' }}>No bingo running right now</div>
                      <div style={{ color: 'var(--text-dim)' }}>Check back when Cam fires up a Slot Bingo board on stream!</div>
                    </div>
                  )}

                  {/* ---------- Open / entry phase ---------- */}
                  {status === 'open' && (
                    <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1.5rem' }}>
                      <div style={{ fontSize: '0.75rem', color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '0.28em', fontWeight: 700, marginBottom: '0.75rem' }}>Bingo is open!</div>
                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: '2rem', fontWeight: 700, color: '#22d3ee', textShadow: '0 0 20px rgba(34,211,238,0.4)' }}>
                        !join <span style={{ fontSize: '1.3rem', color: 'rgba(229,231,235,0.7)' }}>"slot name"</span>
                      </div>
                      <div style={{ color: 'var(--text-dim)', marginTop: '0.5rem' }}>Type it in Kick or Twitch chat to enter</div>
                      <div style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.6rem' }}>
                        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: '3rem', fontWeight: 700, color: '#fff' }}>{bs.entryCount || 0}</span>
                        <span style={{ color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.14em' }}>entries</span>
                      </div>
                      {Array.isArray(bs.recentEntries) && bs.recentEntries.length > 0 && (
                        <div style={{ marginTop: '1.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
                          {bs.recentEntries.slice().reverse().slice(0, 12).map((e, i) => (
                            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.7rem 0.35rem 0.4rem', borderRadius: 999, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                              {platBadge(e.platform)}
                              <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{displayUser(e.name)}</span>
                              {e.slot && <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>· {e.slot}</span>}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ---------- Board ---------- */}
                  {playing && (
                    <div className="card">
                      <div className="card-header">
                        <div className="card-icon" style={{ background: 'rgba(34,197,94,0.1)', borderColor: 'rgba(34,197,94,0.25)' }}>🎰</div>
                        <div className="card-title">The Board</div>
                        {status === 'complete' && (
                          <span style={{ marginLeft: 'auto', color: '#22c55e', fontWeight: 800, letterSpacing: '0.08em' }}>🎉 BINGO!</span>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.6rem', maxWidth: 560, margin: '0 auto' }}>
                        {Array.from({ length: 9 }).map((_, i) => {
                          const sq = board[i];
                          let st = sq ? sq.status : 'pending';
                          // While the roulette spins, hide the real answer (the picked
                          // square) so the landing is a reveal; the cycling highlight
                          // shows where the "ball" currently is.
                          if (bingoRolling && st === 'active') st = 'pending';
                          const rollHit = bingoRolling && bingoRollPos === i && st !== 'paid';
                          const goldHi = rollHit || st === 'active';
                          const onWinLine = !bingoRolling && winLine.includes(i);
                          const isSel = !bingoRolling && isAdmin && playing && selPos === i && st !== 'paid';
                          const clickable = isAdmin && status === 'playing' && st !== 'paid' && !bingoBusy && !bingoRolling;
                          const statusBg = onWinLine ? 'linear-gradient(160deg, rgba(16,72,40,0.97), rgba(6,32,18,0.99))' : (goldHi ? 'linear-gradient(160deg, rgba(40,32,8,0.95), rgba(20,15,4,0.98))' : cellBg(st, isSel));
                          // Slot card art (matched by the bot) under a status-tinted shade
                          const artShade = onWinLine ? 'linear-gradient(180deg, rgba(16,72,40,0.55), rgba(6,32,18,0.95))'
                            : st === 'paid' ? 'linear-gradient(180deg, rgba(8,40,22,0.55), rgba(4,20,11,0.94))'
                            : goldHi ? 'linear-gradient(180deg, rgba(0,0,0,0.05) 25%, rgba(20,15,4,0.92))'
                            : 'linear-gradient(180deg, rgba(10,10,18,0.4) 20%, rgba(10,10,18,0.94))';
                          return (
                            <div key={i}
                              onClick={clickable ? () => onSelect(i) : undefined}
                              style={{
                                position: 'relative', minHeight: sq?.card_image ? 140 : 110, minWidth: 0, borderRadius: 12, padding: '0.7rem 0.45rem', overflow: 'hidden',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: sq?.card_image ? 'flex-end' : 'center', gap: '0.3rem', textAlign: 'center',
                                textShadow: sq?.card_image ? '0 1px 4px rgba(0,0,0,0.9)' : 'none',
                                background: sq?.card_image ? `${artShade}, url("${sq.card_image}") center 18% / cover no-repeat` : statusBg,
                                border: onWinLine ? '2px solid rgba(34,197,94,0.95)' : (goldHi ? '2px solid rgba(251,191,36,0.9)' : cellBorder(st, isSel)),
                                cursor: clickable ? 'pointer' : 'default',
                                boxShadow: onWinLine ? '0 0 24px rgba(34,197,94,0.6)' : (goldHi ? '0 0 22px rgba(251,191,36,0.5)' : (isSel ? '0 0 16px rgba(34,211,238,0.3)' : 'none')),
                                transition: 'border-color .12s, box-shadow .12s, transform .12s',
                                transform: (goldHi || onWinLine) ? 'scale(1.04)' : 'none',
                                zIndex: (goldHi || onWinLine) ? 2 : 1
                              }}>
                              <span style={{ position: 'absolute', top: 6, left: 8, fontFamily: "'Space Mono', monospace", fontSize: '0.7rem', color: 'var(--text-muted)' }}>{i + 1}</span>
                              {sq && <span style={{ position: 'absolute', top: 6, right: 6 }}>{platBadge(sq.platform)}</span>}
                              {st === 'paid' && <span style={{ position: 'absolute', fontSize: '2.6rem', color: 'rgba(34,197,94,0.85)', opacity: 0.5 }}>✓</span>}
                              {isAdmin && st === 'paid' && !bingoRolling && (
                                <button onClick={(e) => { e.stopPropagation(); onUnpay(i); }} disabled={bingoBusy}
                                  title="Undo — mark as not paid"
                                  style={{ position: 'absolute', bottom: 5, right: 5, zIndex: 4, padding: '2px 7px', borderRadius: 6, lineHeight: 1.2,
                                    background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.22)', color: '#fff',
                                    fontSize: '0.72rem', fontWeight: 700, cursor: bingoBusy ? 'wait' : 'pointer', fontFamily: 'inherit' }}>↩ Undo</button>
                              )}
                              {sq ? (
                                <>
                                  {(() => {
                                    const shownName = displayUser(sq.display_name || sq.username);
                                    // Space Mono is fixed-width, so name length maps directly to
                                    // pixels — step the font down instead of letting long names
                                    // stretch their column or get ellipsed away.
                                    const nameFs = shownName.length <= 13 ? '0.95rem' : shownName.length <= 18 ? '0.78rem' : shownName.length <= 24 ? '0.66rem' : '0.58rem';
                                    return (
                                      <span style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: nameFs, color: st === 'paid' ? '#22c55e' : (goldHi ? '#fbbf24' : '#fff'), maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {shownName}
                                      </span>
                                    );
                                  })()}
                                  <span title={sq.slot_name} style={{ fontSize: '0.78rem', color: sq.card_image ? '#d5dae3' : 'var(--text-dim)', lineHeight: 1.15, maxHeight: '2.3em', overflow: 'hidden' }}>{sq.slot_match || sq.slot_name}</span>
                                </>
                              ) : (
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>—</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {isAdmin && status === 'playing' && (
                        <div style={{ marginTop: '0.9rem', fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                          Tap a square to make it the one being played, or hit Pick Random.
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            {/* Raffle Bot page (accessed via top-right nav button) */}
            {activePage === 'rafflebot' && user?.isAdmin && (() => {
              const active = botActiveRaffle;
              const entriesCount = botEntries.length;
              const winners = botEntries.filter(e => e.won_at);

              const handleStart = async () => {
                const platforms = [];
                if (startRaffleForm.platforms.kick) platforms.push('kick');
                if (startRaffleForm.platforms.twitch) platforms.push('twitch');
                if (!platforms.length) { alert('Pick at least one platform'); return; }
                setBotLoading(true);
                try {
                  const res = await botFetch('/raffles', {
                    method: 'POST',
                    body: JSON.stringify({
                      keyword: startRaffleForm.keyword.trim() || 'join',
                      platforms,
                      followers_only: startRaffleForm.followers_only,
                      affiliate_bonus: !!startRaffleForm.affiliate_bonus,
                      affiliate_multiplier: startRaffleForm.affiliate_multiplier || 1.25,
                      on_code_only: !!startRaffleForm.on_code_only
                    })
                  });
                  const data = await res.json();
                  if (!res.ok) { alert(data.error || 'Failed to start'); return; }
                  await fetchBotActive();
                  await fetchBotRaffles();
                  setBotError(null);
                } catch (e) { alert('Network error: ' + e.message); }
                finally { setBotLoading(false); }
              };

              const handleClose = async () => {
                if (!active?.id) return;
                if (!window.confirm('Close entries for this raffle?')) return;
                setBotLoading(true);
                try {
                  const res = await botFetch(`/raffles/${active.id}/close`, { method: 'POST' });
                  const data = await res.json();
                  if (!res.ok) { alert(data.error || 'Failed to close'); return; }
                  await fetchBotActive();
                  await fetchBotRaffles();
                } catch (e) { alert('Network error: ' + e.message); }
                finally { setBotLoading(false); }
              };

              const handleDraw = async (raffleId) => {
                const n = Math.max(1, Math.min(50, parseInt(drawCount, 10) || 1));
                setBotLoading(true);
                try {
                  // Snapshot entrants BEFORE drawing so we can use them for the reel animation
                  const entrantPool = botEntries.filter(e => !e.won_at && !e.invalidated_at);
                  const res = await botFetch(`/raffles/${raffleId}/draw`, {
                    method: 'POST',
                    body: JSON.stringify({ count: n })
                  });
                  const data = await res.json();
                  if (!res.ok) { alert(data.error || 'Failed to draw'); return; }
                  if (!data.winners?.length) { alert('No eligible entries remaining.'); return; }
                  await fetchBotEntries(raffleId);
                  await fetchBotRaffles();

                  // Phase 3: only run the reel animation for single-winner draws.
                  // For bulk draws we just refresh entries (admin can click each
                  // winner pill to open the modal).
                  if (n === 1 && data.winners.length === 1 && entrantPool.length > 0) {
                    const winner = data.winners[0];
                    // Build a 70-cell strip with random entrants; place winner near the end
                    const target = 60;
                    const strip = [];
                    for (let i = 0; i < 70; i++) {
                      if (i === target) {
                        strip.push({ ...winner, isWinner: true });
                      } else {
                        const r = entrantPool[Math.floor(Math.random() * entrantPool.length)];
                        strip.push({
                          username: r.username,
                          display_name: r.display_name,
                          platform: r.platform,
                          rainbet_username: r.rainbet_username,
                          isWinner: false
                        });
                      }
                    }
                    setRaffleReel({ strip, target, winner, raffleId, stage: 'starting' });
                    // Phase 1: render at start position. Phase 2: kick off animation after a frame.
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        setRaffleReel(prev => prev ? { ...prev, stage: 'spinning' } : null);
                        startSpinSound(3200);
                      });
                    });
                    // Reveal stage after spin completes
                    setTimeout(() => {
                      stopSpinSound();
                      playWinSound();
                      setRaffleReel(prev => prev ? { ...prev, stage: 'revealed' } : null);
                    }, 3300);
                    // Auto-open winner stats modal after a brief reveal moment
                    setTimeout(() => {
                      // Find the persisted entry for this winner so we get entry_id and other fields
                      const fullEntry = botEntriesRef.current.find(e =>
                        e.platform === winner.platform && e.username === winner.username
                      ) || winner;
                      setRaffleReel(null);
                      setWinnerStatsModal({
                        entryId: fullEntry.id,
                        raffleId,
                        username: winner.username,
                        display_name: winner.display_name,
                        platform: winner.platform,
                        rainbet_username: winner.rainbet_username,
                        twitchUserId: fullEntry.twitch_user_id || null,
                        won_at: fullEntry.won_at,
                        confirmed_at: fullEntry.confirmed_at,
                        prize_amount: fullEntry.prize_amount,
                        prize_note: fullEntry.prize_note,
                        invalidated_at: fullEntry.invalidated_at,
                        invalidate_reason: fullEntry.invalidate_reason,
                        message: fullEntry.message,
                        stats: null,
                        loading: true
                      });
                    }, 5400);
                  }
                } catch (e) { alert('Network error: ' + e.message); }
                finally { setBotLoading(false); }
              };

              return (
                <div>
                  {/* Bot health */}
                  <div className="card" style={{ marginBottom: '1.25rem' }}>
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div className="card-icon">🤖</div>
                        <div className="card-title">Bot Status</div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <button
                          onClick={() => setSoundsMuted(s => !s)}
                          className={`sound-mute-toggle ${soundsMuted ? 'muted' : ''}`}
                          title={soundsMuted ? 'Sounds muted (click to unmute)' : 'Sounds enabled (click to mute)'}
                        >
                          {soundsMuted ? (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                              <line x1="23" y1="9" x2="17" y2="15"/>
                              <line x1="17" y1="9" x2="23" y2="15"/>
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
                            </svg>
                          )}
                          {soundsMuted ? 'Muted' : 'Sound on'}
                        </button>
                        <button
                          onClick={() => { fetchBotHealth(); fetchBotActive(); fetchBotRaffles(); }}
                          style={{ padding: '0.4rem 0.9rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '6px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit' }}
                        >↻ Refresh</button>
                      </div>
                    </div>
                    <div style={{ padding: '1rem 1.25rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
                      <StatusPill label="Bot reachable" ok={Boolean(botHealth?.ok)} />
                      <StatusPill label="Mode" value={botHealth?.mode || '—'} ok={botHealth?.mode === 'write'} neutral={botHealth?.mode === 'read'} />
                      <StatusPill
                        label="Kick chat"
                        ok={Boolean(botHealth?.kick?.connected)}
                        neutral={botHealth?.kick?.enabled && !botHealth?.kick?.connected}
                        value={botHealth?.kick?.connected ? 'Connected' : botHealth?.kick?.enabled ? 'Disconnected' : 'Off'}
                      />
                      <StatusPill
                        label="Twitch chat"
                        ok={Boolean(botHealth?.twitch?.connected)}
                        neutral={botHealth?.twitch?.enabled && !botHealth?.twitch?.connected}
                        value={botHealth?.twitch?.connected ? 'Connected' : botHealth?.twitch?.enabled ? 'Disconnected' : 'Off'}
                      />
                      <StatusPill
                        label={botHealth?.rainbet?.channel && botHealth.rainbet.channel !== 'rainbet' ? `Rainbet chat (${botHealth.rainbet.channel})` : 'Rainbet chat'}
                        ok={Boolean(botHealth?.rainbet?.connected)}
                        neutral={botHealth?.rainbet?.enabled && !botHealth?.rainbet?.connected}
                        value={botHealth?.rainbet?.connected ? 'Connected' : botHealth?.rainbet?.enabled ? 'Connecting…' : 'Off'}
                      />
                    </div>
                    {/* Rainbet secondary-chat toggle — pull raffle entries from a channel Cam is guesting on */}
                    <div style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', maxWidth: '46ch' }}>
                        <strong style={{ color: 'var(--text)' }}>Pull raffle entries from kick.com/{botHealth?.rainbet?.channel || 'rainbet'}</strong>
                        <div style={{ fontSize: '0.75rem', marginTop: '0.15rem' }}>
                          Enable when Cam is streaming on the Rainbet channel. Entries only — these chatters don't earn site points or trigger commands.
                        </div>
                      </div>
                      <button
                        onClick={() => toggleRainbetChat(!botHealth?.rainbet?.enabled)}
                        disabled={!botHealth?.ok}
                        style={{
                          padding: '0.5rem 1.1rem',
                          background: botHealth?.rainbet?.enabled ? 'rgba(83, 252, 24, 0.15)' : 'rgba(255,255,255,0.06)',
                          color: botHealth?.rainbet?.enabled ? '#53fc18' : 'var(--text-muted)',
                          border: `1px solid ${botHealth?.rainbet?.enabled ? 'rgba(83, 252, 24, 0.4)' : 'var(--border)'}`,
                          borderRadius: '999px',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          cursor: botHealth?.ok ? 'pointer' : 'not-allowed',
                          opacity: botHealth?.ok ? 1 : 0.5,
                          fontFamily: 'inherit',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {botHealth?.rainbet?.enabled ? '● Rainbet chat ON' : '○ Rainbet chat OFF'}
                      </button>
                    </div>
                    {botHealth?.ok && (botHealth?.kick?.lastMessage || botHealth?.twitch?.lastMessage || botHealth?.rainbet?.lastMessage) && (
                      <div style={{ padding: '0.6rem 1.25rem', background: 'rgba(0,0,0,0.15)', borderTop: '1px solid var(--border)', fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                        {botHealth.kick?.lastMessage && (
                          <span>Last Kick msg: <strong style={{ color: '#53fc18' }}>{botHealth.kick.lastMessage.username}</strong> — {botHealth.kick.lastMessage.at?.slice(11, 19) || '?'}</span>
                        )}
                        {botHealth.twitch?.lastMessage && (
                          <span>Last Twitch msg: <strong style={{ color: '#a78bfa' }}>{botHealth.twitch.lastMessage.username}</strong> — {botHealth.twitch.lastMessage.at?.slice(11, 19) || '?'}</span>
                        )}
                        {botHealth.rainbet?.lastMessage && (
                          <span>Last Rainbet msg: <strong style={{ color: '#fbbf24' }}>{botHealth.rainbet.lastMessage.username}</strong> — {botHealth.rainbet.lastMessage.at?.slice(11, 19) || '?'}</span>
                        )}
                        <span>Total msgs: {botHealth.messagesReceived || 0}</span>
                      </div>
                    )}
                    {botError && (
                      <div style={{ padding: '0.75rem 1.25rem', background: 'rgba(239, 68, 68, 0.08)', borderTop: '1px solid rgba(239, 68, 68, 0.2)', fontSize: '0.85rem', color: '#ef4444' }}>
                        ⚠️ {botError}
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                          Check that the bot is deployed at <code style={{ background: 'rgba(255,255,255,0.05)', padding: '0.1rem 0.3rem', borderRadius: '3px' }}>{BOT_API_URL}</code> and the URL in App.jsx matches.
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Active raffle or start form */}
                  {active ? (
                    <div className="card" style={{ marginBottom: '1.25rem' }}>
                      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 8px rgba(34,197,94,0.6)' }} />
                          <div className="card-title">Raffle LIVE — keyword <strong style={{ color: 'var(--gold)' }}>!{active.keyword}</strong></div>
                        </div>
                        <button
                          onClick={handleClose}
                          disabled={botLoading}
                          style={{ padding: '0.5rem 1rem', background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: botLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: botLoading ? 0.6 : 1 }}
                        >Close Entries</button>
                      </div>
                      <div style={{ padding: '1.25rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', borderBottom: '1px solid var(--border)' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '2.25rem', fontWeight: 800, color: '#3b82f6' }}>{entriesCount}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Entries</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '1rem', fontWeight: 700 }}>{active.platforms.join(' + ')}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Platforms</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '1rem', fontWeight: 700, color: active.followersOnly ? '#f59e0b' : 'var(--text-muted)' }}>
                            {active.followersOnly ? 'Followers only' : 'Open'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Mode</div>
                        </div>
                      </div>
                      {/* Draw controls — draw while raffle is still open */}
                      {entriesCount > 0 && (
                        <div style={{ padding: '0.85rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', background: 'rgba(59, 130, 246, 0.04)' }}>
                          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-dim)' }}>🎲 Draw Winner</span>
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={drawCount}
                            onChange={(e) => setDrawCount(e.target.value)}
                            style={{ width: 55, padding: '0.4rem 0.5rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'Space Mono, monospace', textAlign: 'center' }}
                          />
                          <button
                            onClick={() => handleDraw(active.id)}
                            disabled={botLoading || !entriesCount}
                            style={{ padding: '0.5rem 1.25rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.88rem', fontWeight: 700, cursor: (botLoading || !entriesCount) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)', opacity: (botLoading || !entriesCount) ? 0.5 : 1 }}
                          >
                            Draw {drawCount > 1 ? `${drawCount} Winners` : 'Winner'}
                          </button>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                            {winners.length} drawn · {entriesCount - winners.length} remaining
                          </span>
                        </div>
                      )}
                      {/* Live entries list */}
                      <div style={{ padding: '1rem 1.25rem' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-dim)' }}>
                          Recent entries ({entriesCount}) — updates every 4s
                        </div>
                        {!botEntries.length ? (
                          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.85rem' }}>
                            Waiting for entries…
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.4rem', maxHeight: '260px', overflowY: 'auto', padding: '0.4rem', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
                            {botEntries.slice(-80).reverse().map((e, i) => {
                              const isAffiliate = Number(e.affiliate_weight) > 1;
                              const clickable = !!e.won_at;
                              const isConfirmed = !!e.confirmed_at;
                              const isInvalidated = !!e.invalidated_at;
                              const wonPending = !!e.won_at && !isConfirmed && !isInvalidated;
                              // Background tint based on most-specific status
                              const bgColor = isInvalidated ? 'rgba(239, 68, 68, 0.1)'
                                : isConfirmed ? 'rgba(34, 197, 94, 0.18)'
                                : wonPending ? 'rgba(245, 158, 11, 0.12)'
                                : isAffiliate ? 'rgba(234, 179, 8, 0.08)'
                                : 'rgba(255,255,255,0.03)';
                              const borderColor = isInvalidated ? 'rgba(239, 68, 68, 0.35)'
                                : isConfirmed ? 'rgba(34, 197, 94, 0.4)'
                                : wonPending ? 'rgba(245, 158, 11, 0.35)'
                                : isAffiliate ? 'rgba(234, 179, 8, 0.3)'
                                : 'var(--border)';
                              return (
                                <div
                                  key={`${e.platform}-${e.username}-${i}`}
                                  onClick={clickable ? () => setWinnerStatsModal({ entryId: e.id, raffleId: active?.id, username: e.username, display_name: e.display_name, platform: e.platform, rainbet_username: e.rainbet_username, twitchUserId: e.twitch_user_id || null, won_at: e.won_at, confirmed_at: e.confirmed_at, prize_amount: e.prize_amount, prize_note: e.prize_note, invalidated_at: e.invalidated_at, invalidate_reason: e.invalidate_reason, stats: null, loading: true }) : undefined}
                                  style={{
                                    padding: '0.4rem 0.6rem',
                                    background: bgColor,
                                    border: `1px solid ${borderColor}`,
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '0.3rem',
                                    cursor: clickable ? 'pointer' : 'default'
                                  }}
                                  title={clickable ? 'Click to see wager stats' : (isAffiliate ? `Rainbet affiliate (${e.affiliate_weight}x weight)` : '')}
                                >
                                  <span style={{ fontFamily: 'Space Mono, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                    {isAffiliate && <span style={{ color: '#eab308' }}>★</span>}
                                    {isConfirmed && <span style={{ color: '#22c55e', fontSize: '0.85em' }} title="Confirmed winner">✓</span>}
                                    {isInvalidated && <span style={{ color: '#ef4444', fontSize: '0.85em' }} title="Invalidated">✗</span>}
                                    {wonPending && <span style={{ color: '#f59e0b', fontSize: '0.85em' }} title="Pending review">⏳</span>}
                                    {e.display_name || e.username}
                                  </span>
                                  <span style={{
                                    fontSize: '0.65rem',
                                    padding: '0.1rem 0.35rem',
                                    background: e.platform === 'kick' ? 'rgba(83, 252, 24, 0.15)' : 'rgba(169, 112, 255, 0.15)',
                                    color: e.platform === 'kick' ? '#53fc18' : '#a970ff',
                                    borderRadius: '3px',
                                    textTransform: 'uppercase',
                                    fontWeight: 700,
                                    flexShrink: 0
                                  }}>{e.platform}</span>
                                  {e.won_at && <span style={{ color: '#22c55e', fontWeight: 700 }}>🏆</span>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* Start raffle form */
                    <div className="card" style={{ marginBottom: '1.25rem' }}>
                      <div className="card-header">
                        <div className="card-icon">🎉</div>
                        <div className="card-title">Start New Raffle</div>
                      </div>
                      <div style={{ padding: '1.25rem', display: 'grid', gap: '0.85rem' }}>
                        <label style={{ maxWidth: 320 }}>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Keyword (no !)</div>
                          <input
                            type="text"
                            value={startRaffleForm.keyword}
                            onChange={(e) => setStartRaffleForm({ ...startRaffleForm, keyword: e.target.value.replace(/\s+/g, '').toLowerCase() })}
                            placeholder="join"
                            style={{ width: '100%', padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'Space Mono, monospace' }}
                          />
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Chatters type <strong>!{startRaffleForm.keyword || 'join'}</strong> to enter</div>
                        </label>
                        <div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.4rem' }}>Accept entries from</div>
                          <div style={{ display: 'flex', gap: '0.75rem' }}>
                            {[
                              { key: 'kick', label: 'Kick', color: '#53fc18' },
                              { key: 'twitch', label: 'Twitch', color: '#a970ff' }
                            ].map(p => (
                              <label key={p.key} style={{
                                flex: 1,
                                padding: '0.65rem 0.85rem',
                                background: startRaffleForm.platforms[p.key] ? `${p.color}18` : 'rgba(255,255,255,0.02)',
                                border: startRaffleForm.platforms[p.key] ? `1px solid ${p.color}55` : '1px solid var(--border)',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                fontSize: '0.9rem'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={startRaffleForm.platforms[p.key]}
                                  onChange={(e) => setStartRaffleForm({ ...startRaffleForm, platforms: { ...startRaffleForm.platforms, [p.key]: e.target.checked } })}
                                />
                                <span style={{ color: startRaffleForm.platforms[p.key] ? p.color : 'var(--text-dim)', fontWeight: 600 }}>{p.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        {/* Affiliate bonus (Session 3) */}
                        <div style={{ padding: '0.85rem 1rem', background: startRaffleForm.affiliate_bonus ? 'rgba(34, 197, 94, 0.06)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (startRaffleForm.affiliate_bonus ? 'rgba(34, 197, 94, 0.25)' : 'var(--border)'), borderRadius: '8px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={startRaffleForm.affiliate_bonus}
                              onChange={(e) => setStartRaffleForm({ ...startRaffleForm, affiliate_bonus: e.target.checked })}
                            />
                            <span style={{ fontWeight: 600 }}>🫘 Rainbet affiliate bonus</span>
                          </label>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.35rem', marginLeft: '1.5rem' }}>
                            Users with a linked Rainbet username (code CAM) get extra draw weight.
                          </div>
                          {startRaffleForm.affiliate_bonus && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.65rem', marginLeft: '1.5rem' }}>
                              <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Multiplier:</span>
                              <input
                                type="number"
                                step="0.05"
                                min="1"
                                max="5"
                                value={startRaffleForm.affiliate_multiplier}
                                onChange={(e) => setStartRaffleForm({ ...startRaffleForm, affiliate_multiplier: Math.max(1, Math.min(5, Number(e.target.value) || 1.25)) })}
                                style={{ width: 80, padding: '0.35rem 0.55rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '4px', fontFamily: 'inherit', fontSize: '0.85rem' }}
                              />
                              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>× draw weight</span>
                            </div>
                          )}
                        </div>

                        {/* On-code only filter */}
                        <div style={{ padding: '0.85rem 1rem', background: startRaffleForm.on_code_only ? 'rgba(245, 158, 11, 0.06)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (startRaffleForm.on_code_only ? 'rgba(245, 158, 11, 0.35)' : 'var(--border)'), borderRadius: '8px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={startRaffleForm.on_code_only}
                              onChange={(e) => setStartRaffleForm({ ...startRaffleForm, on_code_only: e.target.checked })}
                            />
                            <span style={{ fontWeight: 600, color: startRaffleForm.on_code_only ? '#f59e0b' : 'inherit' }}>🔒 On-code only</span>
                          </label>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.35rem', marginLeft: '1.5rem' }}>
                            Only users with a linked Rainbet username who appear in the affiliate list can enter. Others will be silently ignored by the bot.
                          </div>
                        </div>

                        <button
                          onClick={handleStart}
                          disabled={botLoading || !botHealth?.ok}
                          style={{
                            padding: '0.7rem 1.5rem',
                            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            fontWeight: 700,
                            cursor: (botLoading || !botHealth?.ok) ? 'not-allowed' : 'pointer',
                            fontFamily: 'inherit',
                            opacity: (botLoading || !botHealth?.ok) ? 0.5 : 1,
                            justifySelf: 'start'
                          }}
                        >{botLoading ? 'Starting…' : 'Start Raffle 🎉'}</button>
                      </div>
                    </div>
                  )}

                  {/* Raffle history */}
                  <div className="card">
                    <div className="card-header">
                      <div className="card-icon">📜</div>
                      <div className="card-title">Recent Raffles ({botRaffles.length})</div>
                    </div>
                    {botRaffles.length === 0 ? (
                      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                        No raffles yet.
                      </div>
                    ) : (
                      <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr 80px 80px 180px', gap: '0.75rem', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)' }}>
                          <div>Status</div>
                          <div>Keyword / Prize</div>
                          <div>Platforms</div>
                          <div>Entries</div>
                          <div>Winners</div>
                          <div style={{ textAlign: 'right' }}>Actions</div>
                        </div>
                        {botRaffles.map(r => {
                          const statusColor = r.status === 'open' ? '#22c55e' : r.status === 'closed' ? '#f59e0b' : 'var(--text-muted)';
                          const canDraw = (r.status === 'closed' || r.status === 'open') && r.winner_count < r.entry_count;
                          return (
                            <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr 80px 80px 180px', gap: '0.75rem', alignItems: 'center', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)' }}>
                              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: statusColor, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{r.status}</div>
                              <div>
                                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.85rem' }}>!{r.keyword}</div>
                                {r.prize && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{r.prize}</div>}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>{(r.platforms || []).join(' + ')}</div>
                              <div style={{ fontWeight: 700, color: '#3b82f6' }}>{r.entry_count}</div>
                              <div style={{ fontWeight: 700, color: '#22c55e' }}>{r.winner_count}</div>
                              <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                                {canDraw && (
                                  <>
                                    <input
                                      type="number"
                                      min="1"
                                      max="50"
                                      value={drawCount}
                                      onChange={(e) => setDrawCount(e.target.value)}
                                      style={{ width: 50, padding: '0.3rem 0.4rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '0.75rem', fontFamily: 'inherit' }}
                                    />
                                    <button
                                      onClick={() => handleDraw(r.id)}
                                      disabled={botLoading}
                                      style={{ padding: '0.35rem 0.75rem', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: botLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
                                    >Draw</button>
                                  </>
                                )}
                                <button
                                  onClick={() => {
                                    const next = viewingRaffleEntries === r.id ? null : r.id;
                                    setViewingRaffleEntries(next);
                                    if (next) fetchBotEntries(next);
                                  }}
                                  style={{ padding: '0.35rem 0.75rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit' }}
                                >{viewingRaffleEntries === r.id ? 'Hide' : 'View'}</button>
                                {r.status !== 'open' && (
                                  <button
                                    onClick={async () => {
                                      if (!confirm(`Delete raffle #${r.id} (${r.keyword}) and all its entries? This cannot be undone.`)) return;
                                      try {
                                        const res = await botFetch(`/raffles/${r.id}`, { method: 'DELETE' });
                                        if (res.ok) {
                                          fetchBotRaffles();
                                          fetchWinnerHistory();
                                          if (viewingRaffleEntries === r.id) setViewingRaffleEntries(null);
                                        } else {
                                          const err = await res.json().catch(() => ({}));
                                          alert(err.error || 'Failed to delete');
                                        }
                                      } catch { alert('Network error'); }
                                    }}
                                    style={{ padding: '0.35rem 0.55rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'inherit' }}
                                    title="Delete raffle"
                                  >🗑️</button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Expanded entries view for a historical raffle */}
                    {viewingRaffleEntries && viewingRaffleEntries !== active?.id && (
                      <div style={{ padding: '1rem 1.25rem', background: 'rgba(0,0,0,0.2)', borderTop: '1px solid var(--border)' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-dim)' }}>
                          Entries for raffle #{viewingRaffleEntries} ({botEntries.length})
                        </div>
                        {botEntries.length === 0 ? (
                          <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.85rem' }}>No entries</div>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.4rem', maxHeight: '240px', overflowY: 'auto' }}>
                            {botEntries.map((e, i) => {
                              const isAffiliate = Number(e.affiliate_weight) > 1;
                              const clickable = !!e.won_at;
                              const isConfirmed = !!e.confirmed_at;
                              const isInvalidated = !!e.invalidated_at;
                              const wonPending = !!e.won_at && !isConfirmed && !isInvalidated;
                              // Background tint based on most-specific status
                              const bgColor = isInvalidated ? 'rgba(239, 68, 68, 0.1)'
                                : isConfirmed ? 'rgba(34, 197, 94, 0.18)'
                                : wonPending ? 'rgba(245, 158, 11, 0.12)'
                                : isAffiliate ? 'rgba(234, 179, 8, 0.08)'
                                : 'rgba(255,255,255,0.03)';
                              const borderColor = isInvalidated ? 'rgba(239, 68, 68, 0.35)'
                                : isConfirmed ? 'rgba(34, 197, 94, 0.4)'
                                : wonPending ? 'rgba(245, 158, 11, 0.35)'
                                : isAffiliate ? 'rgba(234, 179, 8, 0.3)'
                                : 'var(--border)';
                              return (
                                <div
                                  key={i}
                                  onClick={clickable ? () => setWinnerStatsModal({ entryId: e.id, raffleId: active?.id, username: e.username, display_name: e.display_name, platform: e.platform, rainbet_username: e.rainbet_username, twitchUserId: e.twitch_user_id || null, won_at: e.won_at, confirmed_at: e.confirmed_at, prize_amount: e.prize_amount, prize_note: e.prize_note, invalidated_at: e.invalidated_at, invalidate_reason: e.invalidate_reason, stats: null, loading: true }) : undefined}
                                  style={{
                                    padding: '0.4rem 0.6rem',
                                    background: bgColor,
                                    border: `1px solid ${borderColor}`,
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '0.3rem',
                                    cursor: clickable ? 'pointer' : 'default'
                                  }}
                                  title={clickable ? 'Click to see wager stats' : (isAffiliate ? `Rainbet affiliate (${e.affiliate_weight}x weight)` : '')}
                                >
                                  <span style={{ fontFamily: 'Space Mono, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                    {isAffiliate && <span style={{ color: '#eab308' }}>★</span>}
                                    {isConfirmed && <span style={{ color: '#22c55e', fontSize: '0.85em' }} title="Confirmed winner">✓</span>}
                                    {isInvalidated && <span style={{ color: '#ef4444', fontSize: '0.85em' }} title="Invalidated">✗</span>}
                                    {wonPending && <span style={{ color: '#f59e0b', fontSize: '0.85em' }} title="Pending review">⏳</span>}
                                    {e.display_name || e.username}
                                  </span>
                                  <span style={{
                                    fontSize: '0.65rem',
                                    padding: '0.1rem 0.35rem',
                                    background: e.platform === 'kick' ? 'rgba(83, 252, 24, 0.15)' : 'rgba(169, 112, 255, 0.15)',
                                    color: e.platform === 'kick' ? '#53fc18' : '#a970ff',
                                    borderRadius: '3px',
                                    textTransform: 'uppercase',
                                    fontWeight: 700,
                                    flexShrink: 0
                                  }}>{e.platform}</span>
                                  {e.won_at && <span style={{ color: '#22c55e', fontWeight: 700 }}>🏆</span>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Phase 4: Winner History — global across all raffles */}
                  {(() => {
                    const q = winnerHistorySearch.trim().toLowerCase();
                    const filtered = winnerHistory.filter(w => {
                      if (winnerHistoryFilter === 'confirmed' && !w.confirmed_at) return false;
                      if (winnerHistoryFilter === 'invalidated' && !w.invalidated_at) return false;
                      if (winnerHistoryFilter === 'pending' && (w.confirmed_at || w.invalidated_at)) return false;
                      if (q && !(
                        (w.display_name || '').toLowerCase().includes(q) ||
                        (w.username || '').toLowerCase().includes(q) ||
                        (w.rainbet_username || '').toLowerCase().includes(q) ||
                        (w.keyword || '').toLowerCase().includes(q)
                      )) return false;
                      return true;
                    });
                    const totals = {
                      all: winnerHistory.length,
                      confirmed: winnerHistory.filter(w => w.confirmed_at).length,
                      invalidated: winnerHistory.filter(w => w.invalidated_at).length,
                      pending: winnerHistory.filter(w => !w.confirmed_at && !w.invalidated_at).length,
                      paidOut: winnerHistory.filter(w => w.confirmed_at).reduce((s, w) => s + Number(w.prize_amount || 0), 0)
                    };
                    return (
                      <div className="card" style={{ marginBottom: '1.25rem' }}>
                        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div className="card-icon">🏆</div>
                            <div>
                              <div className="card-title">Winner History</div>
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                                {totals.confirmed} confirmed · {totals.pending} pending · {totals.invalidated} invalidated · ${totals.paidOut.toLocaleString()} paid out
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={fetchWinnerHistory}
                            style={{ padding: '0.4rem 0.85rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '6px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit' }}
                          >↻ Refresh</button>
                          {selectedWinners.size > 0 && (
                            <button
                              onClick={async () => {
                                if (!confirm(`Remove winner status from ${selectedWinners.size} selected winner(s)?`)) return;
                                const ids = [...selectedWinners];
                                let ok = 0;
                                for (const w of winnerHistory.filter(wh => ids.includes(wh.id))) {
                                  try {
                                    const res = await botFetch(`/raffles/${w.raffle_id}/winners/${w.id}`, { method: 'DELETE' });
                                    if (res.ok) ok++;
                                  } catch {}
                                }
                                setSelectedWinners(new Set());
                                fetchWinnerHistory();
                                fetchBotRaffles();
                                if (ok) alert(`Removed ${ok} winner(s).`);
                              }}
                              style={{ padding: '0.4rem 0.85rem', background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}
                            >🗑️ Delete {selectedWinners.size} Selected</button>
                          )}
                        </div>

                        {/* Search + filter row */}
                        <div style={{ padding: '0.85rem 1.25rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginRight: '0.25rem' }} title="Select all visible">
                            <input
                              type="checkbox"
                              checked={filtered.length > 0 && filtered.every(w => selectedWinners.has(w.id))}
                              onChange={() => {
                                const allSelected = filtered.every(w => selectedWinners.has(w.id));
                                setSelectedWinners(prev => {
                                  const next = new Set(prev);
                                  if (allSelected) { filtered.forEach(w => next.delete(w.id)); }
                                  else { filtered.forEach(w => next.add(w.id)); }
                                  return next;
                                });
                              }}
                              style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#22d3ee' }}
                            />
                          </div>
                          <input
                            type="text"
                            value={winnerHistorySearch}
                            onChange={(e) => setWinnerHistorySearch(e.target.value)}
                            placeholder="🔍 Search by name, Rainbet, or keyword..."
                            style={{ flex: '1 1 240px', padding: '0.5rem 0.85rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.85rem' }}
                          />
                          <div style={{ display: 'inline-flex', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '999px', padding: '0.2rem' }}>
                            {[
                              { key: 'all', label: 'All' },
                              { key: 'confirmed', label: 'Confirmed' },
                              { key: 'pending', label: 'Pending' },
                              { key: 'invalidated', label: 'Invalidated' }
                            ].map(opt => {
                              const isActive = winnerHistoryFilter === opt.key;
                              return (
                                <button
                                  key={opt.key}
                                  onClick={() => setWinnerHistoryFilter(opt.key)}
                                  style={{
                                    padding: '0.4rem 0.85rem',
                                    background: isActive ? 'linear-gradient(135deg, #22d3ee, #0ea5e9)' : 'transparent',
                                    color: isActive ? '#042f3e' : 'var(--text-dim)',
                                    border: 'none',
                                    borderRadius: '999px',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    fontFamily: 'inherit'
                                  }}
                                >{opt.label} ({totals[opt.key]})</button>
                              );
                            })}
                          </div>
                        </div>

                        {filtered.length === 0 ? (
                          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                            {winnerHistory.length === 0 ? 'No winners yet — draw your first raffle to get started.' : `No matches for filter "${winnerHistoryFilter}"${q ? ` and search "${winnerHistorySearch}"` : ''}.`}
                          </div>
                        ) : (
                          <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                            {filtered.map(w => {
                              const platformColor = w.platform === 'kick' ? '#53fc18' : '#a970ff';
                              const statusInfo = w.confirmed_at
                                ? { color: '#22c55e', label: 'Confirmed', bg: 'rgba(34, 197, 94, 0.12)', border: 'rgba(34, 197, 94, 0.3)' }
                                : w.invalidated_at
                                  ? { color: '#ef4444', label: 'Invalidated', bg: 'rgba(239, 68, 68, 0.12)', border: 'rgba(239, 68, 68, 0.3)' }
                                  : { color: '#f59e0b', label: 'Pending', bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.3)' };
                              return (
                                <div
                                  key={w.id}
                                  onClick={() => setWinnerStatsModal({
                                    entryId: w.id, raffleId: w.raffle_id,
                                    username: w.username, display_name: w.display_name,
                                    platform: w.platform, rainbet_username: w.rainbet_username,
                                    twitchUserId: null,
                                    won_at: w.won_at, confirmed_at: w.confirmed_at,
                                    prize_amount: w.prize_amount, prize_note: w.prize_note,
                                    invalidated_at: w.invalidated_at, invalidate_reason: w.invalidate_reason,
                                    stats: null, loading: true
                                  })}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'auto 1fr auto auto auto',
                                    gap: '0.85rem',
                                    alignItems: 'center',
                                    padding: '0.85rem 1.25rem',
                                    borderBottom: '1px solid var(--border)',
                                    cursor: 'pointer',
                                    transition: 'background 0.15s ease'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(34, 211, 238, 0.04)'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                  {/* Checkbox */}
                                  <div style={{ display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                                    <input
                                      type="checkbox"
                                      checked={selectedWinners.has(w.id)}
                                      onChange={() => {
                                        setSelectedWinners(prev => {
                                          const next = new Set(prev);
                                          if (next.has(w.id)) next.delete(w.id); else next.add(w.id);
                                          return next;
                                        });
                                      }}
                                      style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#22d3ee' }}
                                    />
                                  </div>
                                  {/* Left: name + meta */}
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.2rem' }}>
                                      <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.95rem', fontWeight: 700, color: 'var(--text)' }}>
                                        {w.display_name || w.username}
                                      </span>
                                      <span style={{ padding: '0.1rem 0.4rem', borderRadius: '999px', fontSize: '0.55rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', background: `${platformColor}22`, color: platformColor, border: `1px solid ${platformColor}55` }}>{w.platform}</span>
                                      {w.rainbet_username && (
                                        <span style={{ padding: '0.1rem 0.4rem', borderRadius: '999px', fontSize: '0.55rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', background: 'rgba(34, 211, 238, 0.15)', color: '#67e8f9', border: '1px solid rgba(34, 211, 238, 0.35)' }}>★ Affiliate</span>
                                      )}
                                    </div>
                                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                      Raffle <code style={{ background: 'rgba(255,255,255,0.05)', padding: '0.05rem 0.3rem', borderRadius: '3px', fontFamily: 'Space Mono, monospace' }}>!{w.keyword}</code>
                                      {w.raffle_prize && <span> · {w.raffle_prize}</span>}
                                      <span> · won {new Date(w.won_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                                    </div>
                                    {w.invalidated_at && w.invalidate_reason && (
                                      <div style={{ fontSize: '0.7rem', color: '#ef4444', marginTop: '0.25rem', fontStyle: 'italic' }}>
                                        Reason: {w.invalidate_reason}
                                      </div>
                                    )}
                                    {w.confirmed_at && w.prize_note && (
                                      <div style={{ fontSize: '0.7rem', color: '#86efac', marginTop: '0.25rem' }}>
                                        Note: {w.prize_note}
                                      </div>
                                    )}
                                  </div>
                                  {/* Middle: prize amount */}
                                  <div style={{ textAlign: 'right' }}>
                                    {w.prize_amount != null ? (
                                      <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#22c55e', fontFeatureSettings: "'tnum'" }}>
                                        ${Number(w.prize_amount).toLocaleString()}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>—</div>
                                    )}
                                  </div>
                                  {/* Right: status pill */}
                                  <div>
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '0.3rem 0.75rem',
                                      borderRadius: '999px',
                                      fontSize: '0.68rem',
                                      fontWeight: 800,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.1em',
                                      background: statusInfo.bg,
                                      color: statusInfo.color,
                                      border: `1px solid ${statusInfo.border}`
                                    }}>{statusInfo.label}</span>
                                  </div>
                                  {/* Delete winner button */}
                                  <div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!confirm(`Remove winner status from ${w.display_name || w.username}? This puts them back in the entry pool.`)) return;
                                        (async () => {
                                          try {
                                            const res = await botFetch(`/raffles/${w.raffle_id}/winners/${w.id}`, { method: 'DELETE' });
                                            if (res.ok) { fetchWinnerHistory(); fetchBotRaffles(); }
                                            else { const err = await res.json().catch(() => ({})); alert(err.error || 'Failed'); }
                                          } catch { alert('Network error'); }
                                        })();
                                      }}
                                      style={{ padding: '0.3rem 0.5rem', background: 'rgba(239, 68, 68, 0.08)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '6px', fontSize: '0.65rem', cursor: 'pointer', fontFamily: 'inherit', opacity: 0.7, transition: 'opacity 0.15s' }}
                                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                                      title="Remove winner status"
                                    >🗑️</button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Winner stats modal */}
                  {/* Phase 3: Raffle reel animation overlay */}
                  {raffleReel && (
                    <div className="raffle-reel-overlay">
                      <div className="raffle-reel-title">
                        {raffleReel.stage === 'revealed' ? '🎉 Winner!' : 'Drawing winner…'}
                      </div>
                      <div className="raffle-reel-container">
                        <div className="raffle-reel-fade-left" />
                        <div className="raffle-reel-pointer" />
                        <div className="raffle-reel-fade-right" />
                        <div
                          className="raffle-reel-strip"
                          style={{
                            transform: raffleReel.stage === 'starting'
                              ? 'translateX(200px)'
                              : `translateX(${-(raffleReel.target * 200) + 200}px)`,
                            transition: raffleReel.stage === 'spinning'
                              ? 'transform 3.2s cubic-bezier(0.18, 0.7, 0.25, 1)'
                              : 'none'
                          }}
                        >
                          {raffleReel.strip.map((cell, i) => (
                            <div
                              key={i}
                              className={`raffle-reel-cell ${cell.isWinner && raffleReel.stage === 'revealed' ? 'is-winner' : ''} ${cell.rainbet_username ? 'is-affiliate' : ''}`}
                            >
                              <div className="raffle-reel-cell-name">{cell.display_name || cell.username}</div>
                              <div className="raffle-reel-cell-platform">{cell.platform}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      {raffleReel.stage === 'revealed' && (
                        <div className="raffle-reel-winner-card">
                          <div className="raffle-reel-winner-eyebrow">Congratulations</div>
                          <div className="raffle-reel-winner-name">{raffleReel.winner.display_name || raffleReel.winner.username}</div>
                          <div className="raffle-reel-winner-meta">{raffleReel.winner.platform}{raffleReel.winner.rainbet_username ? ' · Rainbet affiliate' : ''}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {winnerStatsModal && (() => {
                    // Helpers
                    const fmtAge = (ts) => {
                      if (!ts) return '—';
                      const d = new Date(ts);
                      if (isNaN(d.getTime())) return '—';
                      const diffMs = Date.now() - d.getTime();
                      const days = Math.floor(diffMs / 86400000);
                      if (days < 1) {
                        const hrs = Math.floor(diffMs / 3600000);
                        return hrs < 1 ? `${Math.floor(diffMs / 60000)} min ago` : `${hrs}h ago`;
                      }
                      if (days < 30) return `${days}d ago`;
                      if (days < 365) return `${Math.floor(days / 30)}mo ago`;
                      return `${Math.floor(days / 365)}y ago`;
                    };
                    const fmtTimer = (s) => {
                      const m = Math.floor(s / 60);
                      const sec = s % 60;
                      return `${m}:${String(sec).padStart(2, '0')}`;
                    };
                    const isConfirmed = !!winnerStatsModal.confirmed_at;
                    const isInvalidated = !!winnerStatsModal.invalidated_at;
                    const platformColor = winnerStatsModal.platform === 'kick' ? '#53fc18' : '#a970ff';

                    const handleConfirm = async () => {
                      if (!winnerStatsModal.entryId) { alert('Missing entry id'); return; }
                      const promptVal = window.prompt('Prize amount in $ (leave blank for none):', winnerStatsModal.prize_amount || '');
                      if (promptVal === null) return;
                      const prizeAmount = promptVal.trim() === '' ? null : Number(promptVal);
                      if (prizeAmount !== null && (!Number.isFinite(prizeAmount) || prizeAmount < 0)) {
                        alert('Prize must be a non-negative number');
                        return;
                      }
                      const note = window.prompt('Optional note (e.g., "$50 cash via PayPal"):', '');
                      try {
                        const res = await botFetch(`/raffles/${winnerStatsModal.raffleId}/winners/${winnerStatsModal.entryId}/confirm`, {
                          method: 'POST',
                          body: JSON.stringify({ prize_amount: prizeAmount, prize_note: note || null })
                        });
                        const data = await res.json();
                        if (!res.ok) { alert(data.error || 'Failed to confirm'); return; }
                        setWinnerStatsModal(prev => prev ? { ...prev,
                          confirmed_at: data.entry.confirmed_at,
                          prize_amount: data.entry.prize_amount,
                          prize_note: data.entry.prize_note,
                          invalidated_at: null
                        } : prev);
                        if (active?.id) fetchBotEntries(active.id);
                        fetchWinnerHistory();
                      } catch (e) { alert('Network error'); }
                    };

                    const handleInvalidate = async () => {
                      if (!winnerStatsModal.entryId) { alert('Missing entry id'); return; }
                      const reason = window.prompt('Reason for invalidating (optional, e.g., "no response after 5 min"):', '');
                      if (reason === null) return;
                      if (!window.confirm('Invalidate this winner? They will be marked as disqualified. You can then draw a new winner.')) return;
                      try {
                        const res = await botFetch(`/raffles/${winnerStatsModal.raffleId}/winners/${winnerStatsModal.entryId}/invalidate`, {
                          method: 'POST',
                          body: JSON.stringify({ reason: reason || null })
                        });
                        const data = await res.json();
                        if (!res.ok) { alert(data.error || 'Failed to invalidate'); return; }
                        setWinnerStatsModal(prev => prev ? { ...prev,
                          invalidated_at: data.entry.invalidated_at,
                          invalidate_reason: data.entry.invalidate_reason,
                          confirmed_at: null,
                          prize_amount: null,
                          prize_note: null
                        } : prev);
                        if (active?.id) fetchBotEntries(active.id);
                        fetchWinnerHistory();
                      } catch (e) { alert('Network error'); }
                    };

                    return (
                    <div
                      onClick={(e) => { if (e.target === e.currentTarget) setWinnerStatsModal(null); }}
                      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
                    >
                      <div style={{
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.6) 100%)',
                        border: '1px solid var(--border)',
                        borderRadius: '16px',
                        padding: 0,
                        maxWidth: '720px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 24px 80px rgba(0,0,0,0.6), 0 0 60px rgba(34, 211, 238, 0.1)',
                        backdropFilter: 'blur(12px)'
                      }}>
                        {/* HEADER with name, platform, status pill, response timer */}
                        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '1.5rem' }}>🏆</span>
                            <div>
                              <div style={{ fontSize: '1.3rem', fontWeight: 800, lineHeight: 1.1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {winnerStatsModal.display_name || winnerStatsModal.username}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(winnerStatsModal.display_name || winnerStatsModal.username);
                                    const btn = e.currentTarget;
                                    btn.textContent = '✓';
                                    btn.style.color = '#22c55e';
                                    setTimeout(() => { btn.textContent = '📋'; btn.style.color = 'var(--text-muted)'; }, 1500);
                                  }}
                                  title="Copy username"
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--text-muted)', padding: '0.15rem', lineHeight: 1 }}
                                >📋</button>
                              </div>
                              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                                <span style={{ padding: '0.15rem 0.55rem', borderRadius: '999px', fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', background: `${platformColor}22`, color: platformColor, border: `1px solid ${platformColor}55` }}>{winnerStatsModal.platform}</span>
                                {isConfirmed && (
                                  <span style={{ padding: '0.15rem 0.55rem', borderRadius: '999px', fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', background: 'rgba(34,197,94,0.18)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.4)' }}>✓ Confirmed</span>
                                )}
                                {isInvalidated && (
                                  <span style={{ padding: '0.15rem 0.55rem', borderRadius: '999px', fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', background: 'rgba(239,68,68,0.18)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.4)' }}>✗ Invalidated</span>
                                )}
                              </div>
                            </div>
                          </div>
                          {/* Response timer — only shown for unconfirmed unwinners */}
                          {!isConfirmed && !isInvalidated && (
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>Awaiting Response</div>
                              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: winnerResponseSeconds > 300 ? '#ef4444' : winnerResponseSeconds > 120 ? '#f59e0b' : '#22d3ee', fontFamily: 'Space Mono, monospace', letterSpacing: '0.05em', textShadow: `0 0 14px ${winnerResponseSeconds > 300 ? '#ef4444' : winnerResponseSeconds > 120 ? '#f59e0b' : '#22d3ee'}55` }}>{fmtTimer(winnerResponseSeconds)}</div>
                            </div>
                          )}
                        </div>

                        {/* BODY: 2-column grid of info panels */}
                        <div style={{ padding: '1.25rem 1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                          {/* Follow info panel */}
                          <div style={{ padding: '1rem 1.1rem', background: 'rgba(255,255,255,0.025)', border: '1px solid var(--border)', borderRadius: '10px' }}>
                            <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: '0.5rem' }}>
                              {winnerStatsModal.platform} Account
                            </div>
                            {winnerStatsModal.followInfo?.loading ? (
                              <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>Looking up follow info…</div>
                            ) : winnerStatsModal.followInfo?.is_follower ? (
                              <>
                                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#22c55e' }}>✓ Follower</div>
                                <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginTop: '0.2rem' }}>
                                  Followed <strong style={{ color: 'var(--text)' }}>{fmtAge(winnerStatsModal.followInfo.followed_at)}</strong>
                                  {winnerStatsModal.followInfo.followed_at && (
                                    <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                                      {new Date(winnerStatsModal.followInfo.followed_at).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                              </>
                            ) : winnerStatsModal.followInfo?.error ? (
                              <>
                                <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-muted)' }}>Unknown</div>
                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                                  Follow status unavailable — configure bot credentials to enable
                                </div>
                              </>
                            ) : (
                              <div style={{ fontSize: '1rem', fontWeight: 700, color: '#f59e0b' }}>Not following</div>
                            )}
                          </div>

                          {/* Rainbet wager panel */}
                          <div style={{ padding: '1rem 1.1rem', background: 'rgba(255,255,255,0.025)', border: '1px solid var(--border)', borderRadius: '10px' }}>
                            <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: '0.5rem' }}>
                              Rainbet Wager
                            </div>
                            {!winnerStatsModal.rainbet_username ? (
                              <div style={{ fontSize: '0.88rem', color: '#f59e0b' }}>
                                Not in the Camily
                              </div>
                            ) : winnerStatsModal.loading ? (
                              <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>Loading wager stats…</div>
                            ) : winnerStatsModal.stats ? (
                              <>
                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                                  As <code style={{ background: 'rgba(255,255,255,0.05)', padding: '0.05rem 0.3rem', borderRadius: '3px' }}>{winnerStatsModal.stats.rainbet_username}</code><UsSiteChip site={winnerStatsModal.stats.site} />
                                  {!winnerStatsModal.stats.found && <span style={{ color: '#ef4444', marginLeft: '0.3rem' }}>· not found</span>}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.4rem' }}>
                                  {[
                                    { label: 'Lifetime', value: winnerStatsModal.stats.lifetime },
                                    { label: winnerStatsModal.stats.prev_period_label || 'Prev Period', value: winnerStatsModal.stats.prev_period },
                                    { label: winnerStatsModal.stats.current_period_label || 'This Period', value: winnerStatsModal.stats.current_period }
                                  ].map(s => (
                                    <div key={s.label}
                                      onClick={() => setWinnerStatsModal(prev => prev ? { ...prev, revealedStats: { ...(prev.revealedStats || {}), [s.label]: true } } : prev)}
                                      style={{ padding: '0.45rem 0.3rem', background: 'rgba(0,0,0,0.3)', borderRadius: '6px', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                                      title="Click to reveal"
                                    >
                                      <div style={{ fontSize: '0.55rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{s.label}</div>
                                      {winnerStatsModal.revealedStats?.[s.label] ? (
                                        <div style={{ fontSize: '0.92rem', fontWeight: 800, color: '#22c55e', marginTop: '0.15rem' }}>${Number(s.value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                                      ) : (
                                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', marginTop: '0.2rem', letterSpacing: '0.1em' }}>click to reveal</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <div style={{ fontSize: '0.85rem', color: '#ef4444' }}>Failed to load</div>
                            )}
                          </div>

                          {/* Entry message panel */}
                          {winnerStatsModal.message && (
                            <div style={{ padding: '1rem 1.1rem', background: 'rgba(255,255,255,0.025)', border: '1px solid var(--border)', borderRadius: '10px', gridColumn: '1 / -1' }}>
                              <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: '0.5rem' }}>
                                Entry Message
                              </div>
                              <div style={{ fontSize: '0.88rem', color: 'var(--text)', fontFamily: 'Space Mono, monospace', padding: '0.5rem 0.75rem', background: 'rgba(0,0,0,0.3)', borderRadius: '6px' }}>
                                {winnerStatsModal.message}
                              </div>
                            </div>
                          )}

                          {/* Phase 2: Live message stream */}
                          <div style={{ padding: '1rem 1.1rem', background: 'rgba(255,255,255,0.025)', border: '1px solid var(--border)', borderRadius: '10px', gridColumn: '1 / -1' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                              <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                Live Chat
                                <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 8px rgba(34,197,94,0.7)', animation: 'liveDotPulse 1.4s ease-in-out infinite' }} />
                              </div>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                {winnerMessages.length} message{winnerMessages.length === 1 ? '' : 's'}
                              </div>
                            </div>
                            {!winnerMessagesTracked ? (
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', padding: '0.85rem', textAlign: 'center', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
                                Message tracking unavailable for this raffle (bot may have restarted since the raffle started).
                              </div>
                            ) : winnerMessages.length === 0 ? (
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', padding: '0.85rem', textAlign: 'center', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
                                Waiting for messages from this user… Polling every 2.5s.
                              </div>
                            ) : (
                              <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', padding: '0.5rem 0.65rem', background: 'rgba(0,0,0,0.3)', borderRadius: '6px' }}>
                                {winnerMessages.map((m, i) => {
                                  const ago = Math.max(0, Math.floor((Date.now() - m.ts) / 1000));
                                  const agoLabel = ago < 60 ? `${ago}s` : ago < 3600 ? `${Math.floor(ago / 60)}m` : `${Math.floor(ago / 3600)}h`;
                                  return (
                                    <div key={`${m.ts}-${i}`} style={{ display: 'flex', gap: '0.55rem', alignItems: 'baseline', fontSize: '0.82rem' }}>
                                      <span style={{ color: 'var(--text-muted)', fontSize: '0.65rem', fontFamily: 'Space Mono, monospace', flexShrink: 0, minWidth: '32px' }}>{agoLabel}</span>
                                      <span style={{ color: 'var(--text)', wordBreak: 'break-word' }}>{m.text}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Confirmation/invalidation note panel */}
                          {(isConfirmed || isInvalidated) && (
                            <div style={{ padding: '1rem 1.1rem', background: isConfirmed ? 'rgba(34,197,94,0.06)' : 'rgba(239,68,68,0.06)', border: `1px solid ${isConfirmed ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`, borderRadius: '10px', gridColumn: '1 / -1' }}>
                              {isConfirmed ? (
                                <>
                                  <div style={{ fontSize: '0.62rem', color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: '0.4rem' }}>Confirmed Winner</div>
                                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', flexWrap: 'wrap' }}>
                                    {winnerStatsModal.prize_amount != null && (
                                      <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#22c55e' }}>${Number(winnerStatsModal.prize_amount).toLocaleString()}</span>
                                    )}
                                    {winnerStatsModal.prize_note && (
                                      <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>— {winnerStatsModal.prize_note}</span>
                                    )}
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>{fmtAge(winnerStatsModal.confirmed_at)}</span>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div style={{ fontSize: '0.62rem', color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: '0.4rem' }}>Invalidated</div>
                                  <div style={{ fontSize: '0.88rem', color: 'var(--text)' }}>
                                    {winnerStatsModal.invalidate_reason || 'No reason given'}
                                    <span style={{ float: 'right', fontSize: '0.75rem', color: 'var(--text-muted)' }}>{fmtAge(winnerStatsModal.invalidated_at)}</span>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>

                        {/* FOOTER — action buttons */}
                        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border)', display: 'flex', gap: '0.65rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          {!isConfirmed && !isInvalidated && (
                            <>
                              <button
                                onClick={handleInvalidate}
                                style={{ padding: '0.65rem 1.25rem', background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.35)', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit' }}
                              >✗ Invalidate &amp; Re-roll</button>
                              <button
                                onClick={handleConfirm}
                                style={{ padding: '0.65rem 1.5rem', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(34,197,94,0.4)' }}
                              >✓ Mark as Winner</button>
                            </>
                          )}
                          {(isConfirmed || isInvalidated) && (
                            <button
                              onClick={handleConfirm}
                              style={{ padding: '0.65rem 1.25rem', background: 'rgba(34,211,238,0.1)', color: '#22d3ee', border: '1px solid rgba(34,211,238,0.3)', borderRadius: '8px', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit' }}
                            >Edit prize / re-confirm</button>
                          )}
                          <button onClick={() => setWinnerStatsModal(null)} style={{ padding: '0.65rem 1.25rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem' }}>Close</button>
                        </div>
                      </div>
                    </div>
                    );
                  })()}
                </div>
              );
            })()}
        {/* ============================================ */}
        {/* TERMS OF SERVICE PAGE                        */}
        {/* ============================================ */}
        {activePage === 'terms' && (
          <>
            <div className="header" style={{ textAlign: 'center' }}>
              <h1 className="header-title">📜 <span>Terms of Service</span></h1>
              <p className="header-sub">The ground rules for using camneverwins.com</p>
            </div>
            <div className="card" style={{ padding: '2rem', maxWidth: 820, margin: '0 auto', fontSize: '0.9rem', lineHeight: 1.7, color: 'var(--text-dim)' }}>
              {[
                ['1. What this site is', 'camneverwins.com is the community rewards site for the streamer CamNeverWins. It hosts leaderboards, raffles, challenges, a points shop, and other giveaway tools tied to the Rainbet affiliate code CAM. This site is NOT a casino and does not accept wagers, deposits, or payments of any kind. All gambling happens on Rainbet, which is an independent third-party platform with its own terms of service.'],
                ['2. Eligibility', 'You must be at least 18 years old (or the legal gambling age in your jurisdiction, whichever is higher) to use this site and participate in any promotion. It is your responsibility to make sure online gambling is legal where you live. If it is not, do not participate.'],
                ['3. Leaderboards, rewards & giveaways', 'Leaderboard standings are based on wager data reported by the Rainbet affiliate API and may lag behind real time. Prizes, reward tiers, raffle rules, claim windows, and payout amounts can change at any time at the discretion of CamNeverWins. Claims submitted outside the stated claim windows may be denied. Prizes are typically paid as Rainbet tips or balance credits and can take several business days to process.'],
                ['4. Fair play', 'One account per person. Self-referrals, wager manipulation, exploiting bugs, using multiple or shared accounts, or any other attempt to game promotions may result in disqualification from prizes and a ban from the site. Decisions made by CamNeverWins and site admins on disputes are final.'],
                ['5. Accounts & data', 'Signing in with Kick or Twitch links your public streaming identity (username and platform ID) to this site so that we can track points, claims, and rewards. You can also link your Rainbet username so your wager can be verified. See our Privacy Policy for details on what we store.'],
                ['6. Responsible gambling', 'Gambling involves real risk of financial loss and can be addictive. Never wager money you cannot afford to lose. Nothing on this site is financial advice, and past leaderboard results do not predict future outcomes. If gambling stops being fun, seek help — resources are available at BeGambleAware.org and GamblingTherapy.org.'],
                ['7. No warranties & liability', 'This site is provided "as is." We do our best to keep data accurate and the site online, but we make no guarantees and accept no liability for losses arising from gambling, data inaccuracies, missed claims, downtime, or third-party services (including Rainbet, Kick, and Twitch).'],
                ['8. Changes', 'These terms may be updated at any time without notice. Continued use of the site after a change means you accept the updated terms.'],
                ['9. Contact', 'Questions about these terms or a prize claim? Reach out in the CamNeverWins Discord server.'],
              ].map(([title, body]) => (
                <div key={title} style={{ marginBottom: '1.4rem' }}>
                  <div style={{ fontWeight: 800, color: 'var(--text)', marginBottom: '0.35rem', fontSize: '0.98rem' }}>{title}</div>
                  <div>{body}</div>
                </div>
              ))}
              <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>Last updated: August 2026</div>
            </div>
          </>
        )}

        {/* ============================================ */}
        {/* PRIVACY POLICY PAGE                          */}
        {/* ============================================ */}
        {activePage === 'privacy' && (
          <>
            <div className="header" style={{ textAlign: 'center' }}>
              <h1 className="header-title">🔒 <span>Privacy Policy</span></h1>
              <p className="header-sub">What we store, why, and what we don't</p>
            </div>
            <div className="card" style={{ padding: '2rem', maxWidth: 820, margin: '0 auto', fontSize: '0.9rem', lineHeight: 1.7, color: 'var(--text-dim)' }}>
              {[
                ['1. What we collect', 'When you sign in with Kick or Twitch we store your public platform username and account ID. If you link a Rainbet username, we store that too so admins can verify wagers for rewards. Using site features also creates records tied to your account: points balance and history, raffle entries, prize and reward claims (including any screenshots you upload as claim proof), predictions, and chat activity relayed by the stream bot for points tracking.'],
                ['2. What we do NOT collect', 'We never see or store your passwords for Kick, Twitch, or Rainbet — sign-in happens through their official OAuth flows. We do not collect payment details, government ID, precise location, or your Rainbet balance or betting history beyond the aggregate wager totals reported by the Rainbet affiliate API.'],
                ['3. How it is used', 'Your data is used solely to run the site: showing leaderboards (usernames are partially masked publicly), tracking points and reward progress, processing prize claims, and preventing abuse of promotions. We do not sell your data or share it with third parties for marketing.'],
                ['4. Cookies & local storage', 'The site stores a sign-in token and a few interface preferences (like collapsing the live-status pill) in your browser’s local storage. No third-party advertising or tracking cookies are used.'],
                ['5. Third parties', 'The site relies on Rainbet (wager data), Kick and Twitch (sign-in and live status), and hosting providers (Vercel and Railway) to operate. Each of those services has its own privacy policy that governs the data they process.'],
                ['6. Data removal', 'Want your account data removed? Contact an admin in the CamNeverWins Discord server and we will delete your stored account records, subject to keeping minimal records of already-paid prizes for accounting.'],
                ['7. Changes', 'This policy may be updated as the site evolves. Significant changes will be reflected on this page.'],
              ].map(([title, body]) => (
                <div key={title} style={{ marginBottom: '1.4rem' }}>
                  <div style={{ fontWeight: 800, color: 'var(--text)', marginBottom: '0.35rem', fontSize: '0.98rem' }}>{title}</div>
                  <div>{body}</div>
                </div>
              ))}
              <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>Last updated: August 2026</div>
            </div>
          </>
        )}

      </main>
      </div>{/* End main-content */}

      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-logo">
            <span className="footer-logo-icon"><img src="/cam-logo.jpg" alt="CamNeverWins" /></span>
            <span className="footer-logo-text">CAM</span>
          </div>
          <p className="footer-disclaimer">We do not take responsibility for any losses from gambling. Play responsibly. Must be 18+.</p>
          <div className="footer-links">
            {socialLinks.map(l => <a key={l.name} href={l.url} target="_blank" rel="noopener noreferrer" className="footer-link">{l.name}</a>)}
          </div>
          <div className="footer-legal">
            <a href="/terms" className="footer-link" onClick={(e) => { e.preventDefault(); navigateTo('terms'); }}>Terms of Service</a>
            <span className="footer-legal-sep">·</span>
            <a href="/privacy" className="footer-link" onClick={(e) => { e.preventDefault(); navigateTo('privacy'); }}>Privacy Policy</a>
            <span className="footer-legal-sep">·</span>
            <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer" className="footer-link">Gambling Help</a>
          </div>
          <p className="footer-credit">
            made by <a href="https://discord.com/users/182121536461864960" target="_blank" rel="noopener noreferrer" className="footer-credit-link">@pa1n</a>
          </p>
        </div>
      </footer>

      <div className="live-indicator">
        {livePillCollapsed ? (
          <button
            className={`live-btn live-btn-mini ${liveStatus.isLive ? 'is-live' : ''}`}
            onClick={toggleLivePill}
            title={liveStatus.isLive ? 'Cam is LIVE — click to expand' : 'Cam is offline — click to expand'}
          >
            <span className="live-dot" />
          </button>
        ) : (
          <>
            <button className="live-collapse-btn" onClick={toggleLivePill} title="Minimize">−</button>
            <a
              href={liveStatus.url || 'https://www.twitch.tv/camneverwins'}
              target="_blank"
              rel="noopener noreferrer"
              className={`live-btn ${liveStatus.isLive ? 'is-live' : ''}`}
            >
              <span className="live-dot" />
              <span className="live-text">
                {liveStatus.isLive && liveStatus.streamer === 'Cam' ? (
                  <>
                    <span className="live-status">🔴 LIVE NOW</span>
                    <span>Cam is Live!</span>
                  </>
                ) : liveStatus.isLive && liveStatus.streamer ? (
                  <>
                    <span className="live-status">Cam is offline</span>
                    <span>Check out {liveStatus.streamer}!</span>
                  </>
                ) : (
                  <>
                    <span className="live-status">OFFLINE</span>
                    <span>Cam is offline</span>
                  </>
                )}
              </span>
            </a>
          </>
        )}
      </div>

      {/* Manual Payout Modal */}
      {showManualPayoutModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setShowManualPayoutModal(false)}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '2rem', maxWidth: 400, width: '90%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              💰 Add Manual Payout
            </h3>
            <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              Add a custom amount to the total paid out counter. This is useful for tracking payouts made outside the system.
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-dim)', marginBottom: '0.5rem' }}>Amount ($)</label>
              <input
                type="number"
                value={manualPayoutAmount}
                onChange={(e) => setManualPayoutAmount(e.target.value)}
                placeholder="Enter amount"
                style={{ width: '100%', padding: '0.75rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit', fontSize: '1rem' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button 
                onClick={submitManualPayout}
                style={{ flex: 1, padding: '0.75rem', background: 'linear-gradient(135deg, #22c55e, #16a34a)', border: 'none', borderRadius: '8px', color: 'white', fontFamily: 'inherit', fontWeight: 600, cursor: 'pointer' }}
              >
                Add Payout
              </button>
              <button 
                onClick={() => setShowManualPayoutModal(false)}
                style={{ padding: '0.75rem 1.5rem', background: 'rgba(255,255,255,0.1)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin: User Points Inspect Modal */}
      {adminUserInspect && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
          onClick={(e) => { if (e.target === e.currentTarget) setAdminUserInspect(null); }}
        >
          <div style={{ background: 'var(--bg-card, #1a1a2e)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '680px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '1.1rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {adminUserInspect.profile_image_url ? (
                <img src={adminUserInspect.profile_image_url} alt="" style={{ width: 36, height: 36, borderRadius: '50%', border: '2px solid var(--border)' }} />
              ) : (
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 700, color: '#f59e0b' }}>{adminUserInspect.display_name?.slice(0,2).toUpperCase()}</div>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '1rem' }}>{adminUserInspect.display_name}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Points History — Admin View</div>
              </div>
              <button onClick={() => setAdminUserInspect(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.4rem', lineHeight: 1, padding: '0.2rem' }}>×</button>
            </div>
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 1rem' }}>
              {[['history', '📋 Transactions'], ['redemptions', '🛍️ Redemptions']].map(([tab, label]) => (
                <button key={tab}
                  onClick={() => { setAdminUserInspectTab(tab); if (tab === 'redemptions' && adminUserRedemptions.items.length === 0 && !adminUserRedemptions.loading) fetchAdminUserRedemptions(adminUserInspect.id); }}
                  style={{ padding: '0.7rem 1rem', background: 'none', border: 'none', borderBottom: adminUserInspectTab === tab ? '2px solid #f59e0b' : '2px solid transparent', color: adminUserInspectTab === tab ? '#f59e0b' : 'var(--text-muted)', fontFamily: 'inherit', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', marginBottom: '-1px' }}
                >{label}</button>
              ))}
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {adminUserInspectTab === 'history' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 80px', gap: '0.5rem', padding: '0.6rem 1rem', borderBottom: '1px solid var(--border)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', fontWeight: 700 }}>
                    <div>When</div><div>Source</div><div>Detail</div><div style={{ textAlign: 'right' }}>Pts</div>
                  </div>
                  {adminUserHistory.loading ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)' }}>Loading…</div>
                  ) : adminUserHistory.items.length === 0 ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)' }}>No transactions found.</div>
                  ) : adminUserHistory.items.map(tx => (
                    <div key={tx.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 80px', gap: '0.5rem', padding: '0.6rem 1rem', borderBottom: '1px solid var(--border)', fontSize: '0.83rem', alignItems: 'center' }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{new Date(tx.created_at).toLocaleString()}</div>
                      <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.76rem' }}>{tx.source}</div>
                      <div style={{ color: 'var(--text-dim)', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.source_detail || '—'}</div>
                      <div style={{ textAlign: 'right', fontWeight: 700, color: tx.amount > 0 ? '#22c55e' : '#ef4444' }}>{tx.amount > 0 ? '+' : ''}{tx.amount}</div>
                    </div>
                  ))}
                  {adminUserHistory.total > 25 && !adminUserHistory.loading && (
                    <div style={{ padding: '0.75rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', borderTop: '1px solid var(--border)' }}>
                      <button onClick={() => fetchAdminUserHistory(adminUserInspect.id, Math.max(1, adminUserHistory.page - 1))} disabled={adminUserHistory.page === 1} style={{ padding: '0.35rem 0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)', borderRadius: '6px', color: adminUserHistory.page === 1 ? 'var(--text-muted)' : 'var(--text)', fontFamily: 'inherit', fontSize: '0.8rem', cursor: adminUserHistory.page === 1 ? 'not-allowed' : 'pointer' }}>← Prev</button>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Page {adminUserHistory.page} of {Math.ceil(adminUserHistory.total / 25)}</span>
                      <button onClick={() => fetchAdminUserHistory(adminUserInspect.id, adminUserHistory.page + 1)} disabled={adminUserHistory.page * 25 >= adminUserHistory.total} style={{ padding: '0.35rem 0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.8rem', cursor: 'pointer' }}>Next →</button>
                    </div>
                  )}
                </>
              )}
              {adminUserInspectTab === 'redemptions' && (
                <>
                  {adminUserRedemptions.loading ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)' }}>Loading…</div>
                  ) : adminUserRedemptions.items.length === 0 ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)' }}>No redemptions found.</div>
                  ) : adminUserRedemptions.items.map(r => {
                    const sc = r.status === 'fulfilled' ? '#22c55e' : r.status === 'cancelled' ? '#ef4444' : '#f59e0b';
                    return (
                      <div key={r.id} style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0.75rem', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{r.item_name}</div>
                          <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>{new Date(r.created_at).toLocaleString()}</div>
                          {r.admin_notes && <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '0.2rem' }}>Note: {r.admin_notes}</div>}
                        </div>
                        <div style={{ fontWeight: 700, color: '#f59e0b', fontSize: '0.9rem' }}>{r.points_spent} pts</div>
                        <div style={{ padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: sc, background: `${sc}15`, border: `1px solid ${sc}40` }}>{r.status}</div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Rewards Modal */}
      {showEditRewardsModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setShowEditRewardsModal(null)}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '2rem', maxWidth: 450, width: '90%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              ✏️ Edit Rewards - Hunt #{showEditRewardsModal.hunt_id}
            </h3>
            <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              Update the rewards for this completed hunt. This will also update any pending claims.
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-dim)', marginBottom: '0.5rem' }}>🥇 1st Place Reward</label>
              <input
                type="text"
                value={editRewardsData.first}
                onChange={(e) => setEditRewardsData(d => ({ ...d, first: e.target.value }))}
                placeholder="e.g. $50 Rainbet Balance"
                style={{ width: '100%', padding: '0.75rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit' }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-dim)', marginBottom: '0.5rem' }}>🥈 2nd Place Reward</label>
              <input
                type="text"
                value={editRewardsData.second}
                onChange={(e) => setEditRewardsData(d => ({ ...d, second: e.target.value }))}
                placeholder="e.g. $30 Rainbet Balance"
                style={{ width: '100%', padding: '0.75rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit' }}
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-dim)', marginBottom: '0.5rem' }}>🥉 3rd Place Reward</label>
              <input
                type="text"
                value={editRewardsData.third}
                onChange={(e) => setEditRewardsData(d => ({ ...d, third: e.target.value }))}
                placeholder="e.g. $20 Rainbet Balance"
                style={{ width: '100%', padding: '0.75rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button 
                onClick={async () => {
                  try {
                    const res = await fetchWithAuth(`${BACKEND_API_URL}/api/admin/hunts/${showEditRewardsModal.hunt_id}/rewards`, {
                      method: 'PUT',
                      body: JSON.stringify({
                        rewardFirst: editRewardsData.first,
                        rewardSecond: editRewardsData.second,
                        rewardThird: editRewardsData.third
                      })
                    });
                    const data = await res.json();
                    if (res.ok) {
                      alert(data.message || 'Rewards updated!');
                      fetchAdminHunts();
                      fetchAdminClaims();
                      setShowEditRewardsModal(null);
                    } else {
                      alert(data.error || 'Failed to update rewards');
                    }
                  } catch (e) { alert('Error updating rewards'); }
                }}
                style={{ flex: 1, padding: '0.75rem', background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', border: 'none', borderRadius: '8px', color: 'white', fontFamily: 'inherit', fontWeight: 600, cursor: 'pointer' }}
              >
                Save Rewards
              </button>
              <button 
                onClick={() => setShowEditRewardsModal(null)}
                style={{ padding: '0.75rem 1.5rem', background: 'rgba(255,255,255,0.1)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Modal for First-time Users */}
      {/* Notification Popup — admin responses to claims */}
      {userNotifications.length > 0 && (() => {
        const notif = userNotifications[0];
        const isWin = notif.source === 'wager_raffle';
        const ack = async () => {
          await fetchWithAuth(`${BACKEND_API_URL}/api/notifications/${notif.id}/ack`, { method: 'POST' });
          setUserNotifications(prev => prev.filter(n => n.id !== notif.id));
        };
        return (
          <div className="welcome-modal-overlay" style={{ zIndex: 10002 }}>
            <div className="welcome-modal" style={{ maxWidth: 440, textAlign: 'left', border: isWin ? '1px solid rgba(251, 191, 36, 0.45)' : undefined, boxShadow: isWin ? '0 0 60px rgba(251, 191, 36, 0.18)' : undefined }}>
              <div style={{ fontSize: isWin ? '2.4rem' : '1.5rem', textAlign: 'center', marginBottom: '0.5rem' }}>{isWin ? '🎉' : '📬'}</div>
              <h2 style={{ margin: '0 0 0.75rem', fontSize: isWin ? '1.25rem' : '1.1rem', textAlign: 'center', color: isWin ? '#fbbf24' : undefined }}>{notif.title}</h2>
              <div style={{ fontSize: '0.88rem', color: 'var(--text-dim)', lineHeight: 1.6, whiteSpace: 'pre-line', marginBottom: '1.25rem', padding: '0.75rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: `1px solid ${isWin ? 'rgba(251, 191, 36, 0.25)' : 'var(--border)'}` }}>
                {notif.message}
              </div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textAlign: 'center', marginBottom: '0.75rem' }}>
                {new Date(notif.created_at).toLocaleString()}
              </div>
              {isWin ? (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={async () => { await ack(); navigateTo('prizes'); }} style={{ flex: 1, padding: '0.75rem', background: 'linear-gradient(135deg, #fbbf24, #d97706)', color: '#1a1a1a', border: 'none', borderRadius: '10px', fontSize: '0.92rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
                    🏆 Claim Prize
                  </button>
                  <button onClick={ack} style={{ padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.08)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '0.92rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Later
                  </button>
                </div>
              ) : (
                <button onClick={ack} style={{ width: '100%', padding: '0.75rem', background: 'linear-gradient(135deg, #22d3ee, #0ea5e9)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '0.92rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Got it
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Prize Claim Submission Modal */}
      {prizeClaimModal && (
        <div className="welcome-modal-overlay" onClick={() => setPrizeClaimModal(false)}>
          <div className="welcome-modal" style={{ maxWidth: 480, textAlign: 'left' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.15rem' }}>🏆 Claim a Prize</h2>
              <button onClick={() => setPrizeClaimModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '1.25rem', cursor: 'pointer' }}>✕</button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.target);
              const res = await fetchWithAuth(`${BACKEND_API_URL}/api/prize-claims`, {
                method: 'POST',
                body: JSON.stringify({
                  rainbetUsername: fd.get('rainbet'),
                  discordUsername: fd.get('discord'),
                  prizeDescription: fd.get('prize'),
                  notes: fd.get('notes')
                })
              });
              const data = await res.json();
              if (!res.ok) { alert(data.error || 'Failed'); return; }
              alert('Prize claim submitted! An admin will review it.');
              setPrizeClaimModal(false);
              fetchMyPrizeClaims();
            }} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-dim)', display: 'block', marginBottom: '0.25rem' }}>Rainbet Username *</label>
                <input name="rainbet" required defaultValue={rainbetDisplay(user)} style={{ width: '100%', padding: '0.5rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'inherit' }} />
              </div>
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-dim)', display: 'block', marginBottom: '0.25rem' }}>Discord Username</label>
                <input name="discord" placeholder="e.g. username#1234" style={{ width: '100%', padding: '0.5rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'inherit' }} />
              </div>
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-dim)', display: 'block', marginBottom: '0.25rem' }}>What did you win? *</label>
                <select name="prize" required style={{ width: '100%', padding: '0.5rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'inherit' }}>
                  <option value="">What did you win?</option>
                  <option value="Won Bingo">Won Bingo</option>
                  <option value="Won Tournament">Won Tournament</option>
                  <option value="Won Bonus Hunt">Won Bonus Hunt</option>
                  <option value="Won Highest Multi">Won Highest Multi</option>
                  <option value="Won Giveaway">Won Giveaway</option>
                  <option value="Other">Other (describe below)</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-dim)', display: 'block', marginBottom: '0.25rem' }}>Additional Notes</label>
                <textarea name="notes" rows={2} maxLength={500} placeholder="Any extra details about your prize..." style={{ width: '100%', padding: '0.5rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.82rem', fontFamily: 'inherit', resize: 'vertical' }} />
              </div>
              <button type="submit" style={{ padding: '0.75rem', background: 'linear-gradient(135deg, #22d3ee, #0ea5e9)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '0.92rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Submit Prize Claim</button>
            </form>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {lightboxImg && (
        <div onClick={() => setLightboxImg(null)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.9)', zIndex: 10001,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'zoom-out', padding: '2rem'
        }}>
          <img src={lightboxImg} alt="Screenshot" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '12px', boxShadow: '0 8px 40px rgba(0,0,0,0.6)', objectFit: 'contain' }} />
          <button onClick={() => setLightboxImg(null)} style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', fontSize: '1.5rem', width: 40, height: 40, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
      )}

      {/* Signup Checklist Modal */}
      {showChecklist && checklistData && (
        <div className="welcome-modal-overlay" onClick={() => setShowChecklist(false)}>
          <div className="welcome-modal" style={{ maxWidth: 480, textAlign: 'left' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.15rem' }}>🎁 Free $3 Checklist</h2>
              <button onClick={() => setShowChecklist(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '1.25rem', cursor: 'pointer' }}>✕</button>
            </div>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '1rem' }}>
              Complete all steps to claim your free $3 Rainbet balance!
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.25rem' }}>
              {[
                { key: 'logged_in', label: 'Login with Kick or Twitch', icon: '✅', auto: true },
                { key: 'follow_x', label: 'Follow Cam on X', icon: '🐦', url: 'https://x.com/camneverwins', requiresUsername: true, placeholder: 'Your X/Twitter username', visitLabel: 'Open X', confirmLabel: 'Confirm ✓' },
                { key: 'follow_instagram', label: 'Follow Cam on Instagram', icon: '📸', url: 'https://instagram.com/camneverwinsslots', requiresUsername: true, placeholder: 'Your Instagram username', visitLabel: 'Open IG', confirmLabel: 'Confirm ✓' },
                { key: 'follow_kick_twitch', label: 'Follow Cam on Kick or Twitch', icon: '🎮', auto: true, note: 'Auto-detected when you type in Cam\'s chat' },
                { key: 'join_discord', label: 'Join the Discord', icon: '💬', url: 'https://discord.gg/camneverwins', requiresUsername: true, placeholder: 'Your Discord username', visitLabel: 'Join', confirmLabel: 'Confirm ✓' },
                { key: 'signup_rainbet', label: 'Sign up on Rainbet', icon: '🎰', url: affiliateLink, simpleLabel: 'Sign Up →', altUrl: affiliateLinkUs, altLabel: 'Rainbet.us' },
                { key: 'link_rainbet', label: 'Link your Rainbet account on the site', icon: '🔗', auto: true, rainbetLink: true }
              ].map(step => {
                const stepVal = checklistData.steps[step.key];
                const done = !!stepVal;
                const isDenied = checklistData.adminDenied;
                const canEditUsername = done && isDenied && step.requiresUsername && typeof stepVal === 'string';
                return (
                  <div key={step.key} style={{
                    padding: '0.65rem 0.85rem',
                    background: done ? 'rgba(34, 197, 94, 0.06)' : 'rgba(0,0,0,0.2)',
                    border: `1px solid ${done ? 'rgba(34, 197, 94, 0.25)' : 'rgba(255,255,255,0.06)'}`,
                    borderRadius: '10px',
                    transition: 'all 0.2s'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                      <span style={{ fontSize: '1.1rem', width: 24 }}>{done ? '✅' : step.icon}</span>
                      <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: 600, color: done ? '#22c55e' : 'var(--text)', textDecoration: done && !canEditUsername ? 'line-through' : 'none', opacity: done && !canEditUsername ? 0.7 : 1 }}>
                        {step.label}
                      </span>
                      {done && typeof stepVal === 'string' && !canEditUsername && (
                        <span style={{ fontSize: '0.7rem', color: '#67e8f9', fontFamily: 'Space Mono, monospace' }}>@{stepVal}</span>
                      )}
                      {canEditUsername && (
                        <span style={{ fontSize: '0.65rem', color: '#f59e0b', fontWeight: 600 }}>✏️ Edit below</span>
                      )}
                      {done && typeof stepVal !== 'string' && <span style={{ fontSize: '0.7rem', color: '#22c55e', fontWeight: 700 }}>Done</span>}
                      {/* Simple steps (no username) — single Go & Complete button */}
                      {!done && !step.auto && !step.requiresUsername && (
                        <button onClick={async () => {
                          if (step.url) window.open(step.url, '_blank');
                          await fetchWithAuth(`${BACKEND_API_URL}/api/checklist/complete`, { method: 'POST', body: JSON.stringify({ step: step.key }) });
                          fetchChecklist();
                        }} style={{ padding: '0.35rem 0.85rem', background: 'linear-gradient(135deg, rgba(34, 211, 238, 0.15), rgba(34, 197, 94, 0.1))', color: '#22d3ee', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700, border: '1px solid rgba(34, 211, 238, 0.25)', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                          {step.simpleLabel || 'Go & Complete →'}
                        </button>
                      )}
                      {/* Secondary site (Rainbet.us) — same completion, quieter button */}
                      {!done && !step.auto && !step.requiresUsername && step.altUrl && (
                        <button onClick={async () => {
                          window.open(step.altUrl, '_blank');
                          await fetchWithAuth(`${BACKEND_API_URL}/api/checklist/complete`, { method: 'POST', body: JSON.stringify({ step: step.key }) });
                          fetchChecklist();
                        }} title="For players in the US" style={{ padding: '0.35rem 0.6rem', background: 'transparent', color: '#93c5fd', borderRadius: '6px', fontSize: '0.68rem', fontWeight: 700, border: '1px solid rgba(59, 130, 246, 0.3)', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                          {step.altLabel}
                        </button>
                      )}
                      {/* Auto steps with action */}
                      {!done && step.auto && step.action && (
                        <button onClick={() => { step.action(); setShowChecklist(false); }}
                          style={{ padding: '0.3rem 0.7rem', background: 'rgba(34, 211, 238, 0.1)', color: '#22d3ee', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700, border: '1px solid rgba(34, 211, 238, 0.2)', cursor: 'pointer', fontFamily: 'inherit' }}>
                          {step.actionLabel || 'Go →'}
                        </button>
                      )}
                      {/* Auto steps with note */}
                      {!done && step.auto && step.note && !step.action && (
                        <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>{step.note}</span>
                      )}
                    </div>
                    {/* Rainbet link — pick .com / .us / both right here */}
                    {!done && step.rainbetLink && (
                      <div style={{ marginTop: '0.5rem', paddingLeft: '2.1rem' }}>
                        <RainbetLinkForm compact onSave={saveRainbetLink} onDone={() => fetchChecklist()} />
                      </div>
                    )}
                    {/* Username input row for verification steps */}
                    {((!done || canEditUsername) && step.requiresUsername) && (
                      <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem', paddingLeft: '2.1rem' }}>
                        {step.url && (
                          <button onClick={() => window.open(step.url, '_blank')}
                            style={{ padding: '0.35rem 0.65rem', background: 'rgba(59, 130, 246, 0.15)', color: '#93c5fd', borderRadius: '6px', fontSize: '0.68rem', fontWeight: 700, border: '1px solid rgba(59, 130, 246, 0.25)', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {step.visitLabel || 'Visit'}
                          </button>
                        )}
                        <input
                          type="text"
                          placeholder={step.placeholder}
                          value={checklistInputs[step.key] ?? (canEditUsername ? stepVal : '')}
                          onChange={(e) => setChecklistInputs(prev => ({ ...prev, [step.key]: e.target.value }))}
                          style={{ flex: 1, padding: '0.35rem 0.6rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.78rem', fontFamily: 'inherit', minWidth: 0 }}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.nextSibling?.click(); }}
                        />
                        <button onClick={async () => {
                          const val = (checklistInputs[step.key] || '').trim().replace(/^@/, '');
                          if (!val) { alert(`Please enter your ${step.placeholder?.replace('Your ', '')}`); return; }
                          const res = await fetchWithAuth(`${BACKEND_API_URL}/api/checklist/complete`, {
                            method: 'POST',
                            body: JSON.stringify({ step: step.key, username: val })
                          });
                          if (res.ok) { setChecklistInputs(prev => ({ ...prev, [step.key]: '' })); fetchChecklist(); }
                          else { const d = await res.json().catch(() => ({})); alert(d.error || 'Failed'); }
                        }} style={{ padding: '0.35rem 0.65rem', background: 'rgba(34, 211, 238, 0.12)', color: '#22d3ee', borderRadius: '6px', fontSize: '0.68rem', fontWeight: 700, border: '1px solid rgba(34, 211, 238, 0.25)', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {step.confirmLabel || 'Submit'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Progress bar */}
            <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: '999px', marginBottom: '1rem', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(checklistData.completedSteps / checklistData.totalSteps) * 100}%`, background: checklistData.allComplete ? '#22c55e' : 'linear-gradient(90deg, #22d3ee, #0ea5e9)', borderRadius: '999px', transition: 'width 0.3s' }} />
            </div>

            {/* Claim button */}
            {checklistData.adminDeniedFinal ? (
              <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '10px', color: '#ef4444', fontSize: '0.85rem' }}>
                <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>⛔ Your claim was denied</div>
                {checklistData.adminDeniedReason && <div style={{ color: 'var(--text-dim)', marginBottom: '0.25rem' }}>{checklistData.adminDeniedReason}</div>}
                <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>This decision is final — the claim can't be resubmitted.</div>
              </div>
            ) : checklistData.rewardClaimed && checklistData.adminDenied ? (
              <div>
                <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '10px', color: '#ef4444', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                  <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>❌ Your claim was denied</div>
                  {checklistData.adminDeniedReason && <div style={{ color: 'var(--text-dim)' }}>{checklistData.adminDeniedReason}</div>}
                </div>
                {checklistData.allComplete && (
                  <button onClick={async () => {
                    if (!confirm('Resubmit your $3 checklist claim?')) return;
                    const res = await fetchWithAuth(`${BACKEND_API_URL}/api/checklist/claim`, { method: 'POST' });
                    const data = await res.json();
                    if (res.ok) { alert('Claim resubmitted! An admin will review it.'); fetchChecklist(); }
                    else alert(data.error || 'Failed');
                  }} style={{ width: '100%', padding: '0.75rem', background: 'linear-gradient(135deg, #fbbf24, #d97706)', color: '#000', border: 'none', borderRadius: '12px', fontSize: '0.92rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
                    🔄 Resubmit Claim
                  </button>
                )}
              </div>
            ) : checklistData.rewardClaimed ? (
              <>
              <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.25)', borderRadius: '10px', color: '#22c55e', fontWeight: 700, fontSize: '0.92rem' }}>
                {checklistData.adminPaid ? '✅ $3 Reward Paid!' : '⏳ $3 Reward Claimed — Processing!'}
              </div>
              {!checklistData.adminPaid && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.35rem', textAlign: 'center' }}>May take 24–48 hours to process</div>
              )}
              </>
            ) : checklistData.allComplete ? (
              <button onClick={async () => {
                if (!confirm('Claim your free $3 Rainbet balance?')) return;
                const res = await fetchWithAuth(`${BACKEND_API_URL}/api/checklist/claim`, { method: 'POST' });
                const data = await res.json();
                if (res.ok) { alert('$3 reward claimed! An admin will process it shortly.'); fetchChecklist(); }
                else alert(data.error || 'Failed');
              }} style={{ width: '100%', padding: '0.85rem', background: 'linear-gradient(135deg, #fbbf24, #d97706)', color: '#000', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 6px 20px rgba(251, 191, 36, 0.35)' }}>
                🎉 Claim Your Free $3!
              </button>
            ) : (
              <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(0,0,0,0.2)', borderRadius: '10px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                Complete all {checklistData.totalSteps} steps to unlock your reward
              </div>
            )}
          </div>
        </div>
      )}

      {/* Wager Claim Submission Modal */}
      {claimModal && (
        <div className="welcome-modal-overlay" onClick={() => setClaimModal(null)}>
          <div className="welcome-modal" style={{ maxWidth: 480, textAlign: 'left' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.15rem' }}>
                {claimModal.type === 'lossback' ? '💸 Claim Lossback' : claimModal.type === 'weekly_monthly' ? '🔔 Claim Weekly Double' : '🚀 Claim Wager Milestone'}
              </h2>
              <button onClick={() => setClaimModal(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '1.25rem', cursor: 'pointer' }}>✕</button>
            </div>

            {!hasRainbetLink(user) ? (
              <div style={{ padding: '1rem', background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '8px', color: '#ef4444', fontSize: '0.85rem' }}>
                ⚠️ You must link your Rainbet username in your profile before submitting a claim.
              </div>
            ) : (() => {
              const existingPending = myClaims.find(c => c.claim_type === claimModal.type && c.status === 'pending');
              if (existingPending) return (
                <div>
                  <div style={{ padding: '1rem', background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)', borderRadius: '8px', marginBottom: '1rem' }}>
                    <div style={{ fontWeight: 700, color: '#fbbf24', marginBottom: '0.25rem' }}>⏳ You already have a pending claim</div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)' }}>Submitted {new Date(existingPending.created_at).toLocaleString()}</div>
                    {existingPending.notes && <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginTop: '0.25rem' }}>Notes: {existingPending.notes}</div>}
                  </div>
                  <button onClick={async () => {
                    if (!confirm('Cancel this pending claim? You can submit a new one after.')) return;
                    const res = await fetchWithAuth(`${BACKEND_API_URL}/api/claims/${existingPending.id}`, { method: 'DELETE' });
                    if (res.ok) { fetchMyClaims(); alert('Claim cancelled. You can submit a new one now.'); }
                    else alert('Failed to cancel');
                  }} style={{ padding: '0.6rem 1.2rem', background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Cancel Pending Claim
                  </button>
                </div>
              );
              return (
              <>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '1rem' }}>
                  {claimModal.type === 'lossback' && 'Upload a screenshot of your P&L report from Rainbet live chat support.'}
                  {claimModal.type === 'weekly_monthly' && 'Upload a screenshot of your weekly reward notification (🔔 bell icon, top right on Rainbet).'}
                  {claimModal.type === 'milestone' && 'Upload your monthly bet history screenshot or CSV export from Rainbet.'}
                </div>

                {claimModal.type === 'lossback' && (
                  <div style={{ marginBottom: '1rem' }}>
                    <button
                      onClick={() => setShowLossbackRules(true)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.9rem', background: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.02em' }}
                    >
                      📋 Lossback Rules
                    </button>
                  </div>
                )}

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-dim)', display: 'block', marginBottom: '0.35rem' }}>Rainbet Username</label>
                  <div style={{ padding: '0.5rem 0.75rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.88rem', color: '#22d3ee', fontFamily: 'Space Mono, monospace' }}>{rainbetDisplay(user)}</div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-dim)', display: 'block', marginBottom: '0.35rem' }}>Screenshot *</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (file.size > 2 * 1024 * 1024) { alert('Max 2MB'); e.target.value = ''; return; }
                      const reader = new FileReader();
                      reader.onload = () => setClaimScreenshot(reader.result);
                      reader.readAsDataURL(file);
                    }}
                    style={{ fontSize: '0.82rem', color: 'var(--text-dim)' }}
                  />
                  {claimScreenshot && (
                    <img src={claimScreenshot} alt="Preview" style={{ marginTop: '0.5rem', maxWidth: '100%', maxHeight: 200, borderRadius: '8px', border: '1px solid var(--border)' }} />
                  )}
                </div>

                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-dim)', display: 'block', marginBottom: '0.35rem' }}>Notes (optional)</label>
                  <textarea
                    value={claimNotes}
                    onChange={(e) => setClaimNotes(e.target.value)}
                    placeholder="Any additional details..."
                    maxLength={500}
                    rows={2}
                    style={{ width: '100%', padding: '0.5rem 0.75rem', background: 'rgba(0,0,0,0.3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.82rem', resize: 'vertical' }}
                  />
                </div>

                <button
                  onClick={submitClaim}
                  disabled={claimSubmitting || !claimScreenshot}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: (!claimScreenshot || claimSubmitting) ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #22d3ee, #0ea5e9)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '0.92rem',
                    fontWeight: 700,
                    cursor: (!claimScreenshot || claimSubmitting) ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                    opacity: (!claimScreenshot || claimSubmitting) ? 0.5 : 1
                  }}
                >
                  {claimSubmitting ? 'Submitting...' : 'Submit Claim'}
                </button>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.5rem', textAlign: 'center' }}>May take 24–48 hours to process</div>
              </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Lossback Rules Modal — opens on top of the claim modal */}
      {showLossbackRules && (
        <div className="welcome-modal-overlay" style={{ zIndex: 10003 }} onClick={() => setShowLossbackRules(false)}>
          <div className="welcome-modal" style={{ maxWidth: 480, textAlign: 'left' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem' }}>📋 Lossback Rules</h2>
              <button onClick={() => setShowLossbackRules(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '1.25rem', cursor: 'pointer', padding: '0.25rem' }}>✕</button>
            </div>

            <div style={{ padding: '0.75rem 0.9rem', background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.25)', borderRadius: '10px', marginBottom: '1rem', fontSize: '0.88rem', color: '#22c55e', fontWeight: 700, lineHeight: 1.5 }}>
              Up to 10% lossback, with a maximum claim of $25,000.
            </div>

            {[
              'Lossback is calculated from your previous month\'s profit & loss report on Rainbet under code CAM.',
              'Claims must be submitted between the 1st and 7th of the month — miss the window and the claim is void.',
              'One lossback claim per user, per month. The screenshot must clearly show your Rainbet username and the P&L period.',
              'Cam and/or Rainbet reserve the right to reduce, adjust, or deny a claim in cases of suspected abuse, exploitation, or unfair play.',
              'All decisions regarding lossback amounts and eligibility are final.'
            ].map((rule, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.65rem', alignItems: 'flex-start', marginBottom: '0.85rem' }}>
                <span style={{ marginTop: '0.15rem', width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                <span style={{ fontSize: '0.88rem', color: 'var(--text-dim)', lineHeight: 1.5 }}>{rule}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Wager Rewards Rules Modal */}
      {showRewardRules && (
        <div className="welcome-modal-overlay" onClick={() => setShowRewardRules(false)}>
          <div className="welcome-modal" style={{ maxWidth: 520, textAlign: 'left' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem' }}>📋 Wager Rewards Rules</h2>
              <button onClick={() => setShowRewardRules(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '1.25rem', cursor: 'pointer', padding: '0.25rem' }}>✕</button>
            </div>
            {[
              'These rewards utilize a weighted wager system.',
              'All wagers are adjusted to a standardized 96% RTP.',
              'Wagering on low house edge games with the intent to artificially inflate wager volume may be reduced or excluded from reward calculations.',
              'Cam and/or Rainbet reserve the right to reduce, adjust, or disqualify a player\'s wagered amount in cases of suspected abuse, exploitation, or unfair play.',
              'All decisions regarding wager adjustments and eligibility are final.'
            ].map((rule, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.65rem', alignItems: 'flex-start', marginBottom: '0.85rem' }}>
                <span style={{ marginTop: '0.15rem', width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                <span style={{ fontSize: '0.88rem', color: 'var(--text-dim)', lineHeight: 1.5 }}>{rule}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showWelcomeModal && (
        <div className="welcome-modal-overlay">
          <div className="welcome-modal">
            {welcomeStep === 1 && (
              <>
                <div className="welcome-modal-icon">🎉</div>
                <h2>Welcome to <span>CamNeverWins</span>!</h2>
                <p>
                  Link your Rainbet username to participate in predictions and claim rewards when you win!
                </p>
                <div style={{ padding: '0.5rem 0.75rem', background: 'rgba(251, 191, 36, 0.08)', border: '1px solid rgba(251, 191, 36, 0.2)', borderRadius: '8px', fontSize: '0.78rem', color: '#fbbf24', fontWeight: 600, marginBottom: '0.5rem', textAlign: 'center' }}>
                  ⚠️ Your Rainbet account must be registered under code <strong>CAM</strong> to qualify for rewards
                </div>
                <RainbetLinkForm
                  allowSkip
                  onSave={saveRainbetLink}
                  onDone={() => {
                    // Check if the other platform is missing — if so, show step 2
                    const missingKick = !user?.kickId && !user?.kickUsername;
                    const missingTwitch = !user?.twitchId;
                    if (missingKick || missingTwitch) {
                      setWelcomeStep(2);
                    } else {
                      setShowWelcomeModal(false);
                    }
                  }}
                />
              </>
            )}

            {welcomeStep === 2 && (() => {
              const missingKick = !user?.kickId && !user?.kickUsername;
              const missingTwitch = !user?.twitchId;
              const otherPlatform = missingKick ? 'Kick' : 'Twitch';
              const otherColor = missingKick ? '#53fc18' : '#a78bfa';
              const otherIcon = missingKick
                ? <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 22, height: 22 }}><path d="M2 2h5v6h2V6h2V4h2V2h5v4h-2v2h-2v2h-2v4h2v2h2v2h2v4h-5v-2h-2v-2H9v2H7v2H2V2z"/></svg>
                : <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 22, height: 22 }}><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/></svg>;

              return (
                <>
                  <div className="welcome-modal-icon" style={{ color: otherColor }}>🔗</div>
                  <h2>Link Your <span style={{ color: otherColor }}>{otherPlatform}</span> Account</h2>
                  <p>
                    Connect your {otherPlatform} account to earn chat points on both platforms and keep all your data in one place.
                  </p>
                  <button
                    className="welcome-btn-primary"
                    style={{ background: `linear-gradient(135deg, ${otherColor}, ${otherColor}cc)`, color: missingKick ? '#000' : '#fff' }}
                    onClick={() => {
                      const token = getAuthToken();
                      if (!token) { setShowWelcomeModal(false); return; }
                      if (missingKick) {
                        window.location.href = `${BACKEND_API_URL}/auth/kick/link?token=${encodeURIComponent(token)}`;
                      } else {
                        window.location.href = `${BACKEND_API_URL}/auth/twitch/link?token=${encodeURIComponent(token)}`;
                      }
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                      {otherIcon}
                      Link {otherPlatform} Account
                    </span>
                  </button>
                  <button className="welcome-btn-secondary" onClick={() => setShowWelcomeModal(false)}>
                    Not right now
                  </button>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                    You can always link later from your profile page.
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Win Notification Modal */}
      {showWinModal && winModalData && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setShowWinModal(false)}>
          <div style={{ background: 'var(--bg-card)', border: '2px solid var(--gold)', borderRadius: '16px', padding: '2rem', maxWidth: 420, width: '90%', textAlign: 'center', animation: 'modalPop 0.3s ease-out' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '4rem', marginBottom: '0.5rem' }}>🎉</div>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Congratulations!</h2>
            <p style={{ color: 'var(--text-dim)', marginBottom: '1.5rem' }}>
              You won {['🥇 1st', '🥈 2nd', '🥉 3rd'][winModalData.rank - 1]} place in Hunt #{winModalData.huntId}!
            </p>
            
            <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border)' }}>
                <span>Your Prediction</span>
                <span>${winModalData.predicted?.toLocaleString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border)' }}>
                <span>Actual Result</span>
                <span>${winModalData.actual?.toLocaleString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border)' }}>
                <span>Difference</span>
                <span>${winModalData.difference?.toLocaleString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0' }}>
                <span>Your Reward</span>
                <span style={{ color: 'var(--gold)', fontWeight: 700 }}>{winModalData.reward}</span>
              </div>
            </div>

            {hasRainbetLink(user) ? (
              <div>
                <p style={{ color: 'var(--text-dim)', marginBottom: '0.5rem' }}>Reward will be sent to:</p>
                <p style={{ color: 'var(--gold)', fontWeight: 700, fontSize: '1.1rem', marginBottom: '1rem' }}>{rainbetPayoutName(user)}</p>
                <button 
                  onClick={async () => { 
                    setClaimUsername(rainbetPayoutName(user));
                    try {
                      const res = await fetchWithAuth(`${BACKEND_API_URL}/api/predictions/claim-win`, {
                        method: 'POST',
                        body: JSON.stringify({ odinnerId: winModalData.winnerId, rainbetUsername: rainbetPayoutName(user) })
                      });
                      const data = await res.json();
                      if (res.ok) {
                        alert(data.message || 'Prize claimed! An admin will process your reward soon.');
                        fetchMyWins();
                      } else {
                        alert(data.error || 'Failed to claim');
                      }
                    } catch (e) { alert('Network error'); }
                    setShowWinModal(false); 
                  }}
                  style={{ width: '100%', padding: '0.85rem', background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))', border: 'none', borderRadius: '8px', color: '#000', fontFamily: 'inherit', fontWeight: 700, cursor: 'pointer', marginBottom: '0.75rem' }}
                >
                  ✅ Confirm & Claim Reward
                </button>
                <button 
                  onClick={() => { setClaimUsername(''); setShowClaimModal(myWins.find(w => w.id === winModalData.winnerId)); setShowWinModal(false); }}
                  style={{ width: '100%', padding: '0.6rem', background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-dim)', fontFamily: 'inherit', cursor: 'pointer', fontSize: '0.85rem' }}
                >
                  Use Different Username
                </button>
              </div>
            ) : (
              <div>
                <p style={{ color: 'var(--text-dim)', marginBottom: '0.75rem' }}>Enter your Rainbet username to claim:</p>
                <input
                  value={claimUsername}
                  onChange={(e) => setClaimUsername(e.target.value)}
                  placeholder="Rainbet username"
                  style={{ width: '100%', padding: '0.75rem', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontFamily: 'inherit', marginBottom: '1rem' }}
                />
                <button 
                  onClick={() => { claimWin(winModalData.winnerId); setShowWinModal(false); }}
                  disabled={!claimUsername.trim()}
                  style={{ width: '100%', padding: '0.75rem', background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))', border: 'none', borderRadius: '8px', color: '#000', fontFamily: 'inherit', fontWeight: 700, cursor: 'pointer', opacity: claimUsername.trim() ? 1 : 0.7 }}
                >
                  Claim Reward
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
